import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Analytics } from "@vercel/analytics/react";
import { EthereumProvider } from "@walletconnect/ethereum-provider";
import { createClientUPProvider } from "@lukso/up-provider";
import { keccak_256 } from "@noble/hashes/sha3.js";

// --- WALLETCONNECT CONFIG ------------------------------------
const WC_PROJECT_ID = "a400b4f2690a1229f437a68367f3a5a8";
const LUKSO_MAINNET_CHAIN_ID = 42;
const LUKSO_TESTNET_CHAIN_ID = 4201;

// --- KLIPY GIF API (replaces Tenor v1, which Google shuts down June 2026) -----
// Key is path-based and client-safe (same exposure model as the old Tenor public
// key). Stored as a Vercel env var (VITE_KLIPY_API_KEY) so file overwrites never
// wipe it. Vite inlines VITE_-prefixed vars into the client bundle at build time.
// Both GIF pickers route through klipyGifSearch().
const KLIPY_API_KEY = import.meta.env.VITE_KLIPY_API_KEY || "";

// Klipy returns { result, data: { data: [...] } }; each item carries a file/files
// object whose exact size buckets (xs/sm/md/hd) and formats (gif/webp/mp4) aren't
// in their JS-rendered docs. This resolver walks the documented shape and falls
// back deeply, so it survives shape variance and skips ad slots (no gif → null).
function klipyPickUrl(item, prefer = "full") {
  const f = (item && (item.file || item.files)) || {};
  const order = prefer === "preview" ? ["xs", "sm", "md", "hd"] : ["md", "hd", "sm", "xs"];
  for (const sz of order) {
    const bucket = f[sz];
    if (!bucket) continue;
    const u = bucket?.gif?.url || bucket?.webp?.url || bucket?.url;
    if (u) return u;
  }
  // Deep fallback: first url-ish string anywhere under the file object.
  let found = null;
  const walk = (o) => {
    if (found || !o || typeof o !== "object") return;
    for (const k in o) {
      const v = o[k];
      if (typeof v === "string" && /^https?:\/\//.test(v) && (k === "url" || /\.gif/i.test(v))) { found = v; return; }
      if (v && typeof v === "object") walk(v);
    }
  };
  walk(f);
  return found;
}

async function klipyGifSearch(query, limit = 12) {
  if (!query || !query.trim()) return [];
  if (!KLIPY_API_KEY) {
    console.warn("[Klipy] VITE_KLIPY_API_KEY is empty — set it in Vercel and REDEPLOY (VITE_ vars are inlined at build time, so a key added after the last build won't be present).");
    return [];
  }
  try {
    const url = `https://api.klipy.com/api/v1/${KLIPY_API_KEY}/gifs/search?q=${encodeURIComponent(query)}&per_page=${limit}&customer_id=fullsend-web`;
    const res = await fetch(url);
    if (!res.ok) {
      const body = await res.text().catch(() => "");
      console.warn(`[Klipy] search HTTP ${res.status}`, body.slice(0, 300));
      return [];
    }
    const data = await res.json();
    const items = (data && data.data && data.data.data) || [];
    const mapped = items
      .filter(it => it && it.type !== "ad")
      .map(it => ({ id: it.slug || it.id, preview: klipyPickUrl(it, "preview"), full: klipyPickUrl(it, "full") }))
      .filter(it => it.preview || it.full);
    if (mapped.length === 0) console.warn("[Klipy] 0 usable items — raw response:", data);
    return mapped;
  } catch (e) {
    console.warn("[Klipy] search error:", e?.message || e);
    return [];
  }
}

// On-screen debug logger for mobile connect diagnosis. Writes timestamped
// lines to a fixed overlay at the bottom of the screen — visible on iPhone
// (where there's no console), and persists in the DOM across the trip to the
// UP app, so the last step reached is visible on return. Fully guarded.
function wcDebug(msg) {
  return; // debug overlay disabled — mobile WC connect confirmed working
  try {
    if (typeof document === "undefined") return;
    let el = document.getElementById("__fs_wc_debug");
    if (!el) {
      el = document.createElement("div");
      el.id = "__fs_wc_debug";
      el.style.cssText = "position:fixed;left:0;right:0;bottom:0;max-height:45vh;overflow:auto;z-index:2147483647;background:rgba(0,0,0,0.92);color:#3f6;font:11px/1.45 monospace;padding:8px;white-space:pre-wrap;word-break:break-all;";
      const close = document.createElement("div");
      close.textContent = "✕ close debug";
      close.style.cssText = "color:#f66;text-align:right;cursor:pointer;margin-bottom:6px;font-weight:bold;";
      close.onclick = () => el.remove();
      el.appendChild(close);
      document.body.appendChild(el);
    }
    const line = document.createElement("div");
    line.textContent = "[" + new Date().toISOString().slice(11, 23) + "] " + msg;
    el.appendChild(line);
    el.scrollTop = el.scrollHeight;
  } catch (_) {}
}

async function createWCProvider() {
  const provider = await EthereumProvider.init({
    projectId: WC_PROJECT_ID,
    chains: [LUKSO_MAINNET_CHAIN_ID],
    // @walletconnect/ethereum-provider v2.23 only honors optionalChains (the
    // `chains` required entry gets dropped → requiredNamespaces:{}). The user's
    // UP is on mainnet (42), so mainnet MUST be in optionalChains or the wallet
    // settles a session with no matching account (accounts:[]). Mainnet first.
    optionalChains: [LUKSO_MAINNET_CHAIN_ID, LUKSO_TESTNET_CHAIN_ID],
    // Without rpcMap, WalletConnect's EthereumProvider can't build an RPC
    // sub-provider for the chain, so getProvider(chain) returns undefined and
    // request() throws "undefined is not an object (evaluating
    // 'this.getProvider(i).request')". Supplying RPCs fixes mobile connect.
    rpcMap: {
      [LUKSO_MAINNET_CHAIN_ID]: "https://42.rpc.thirdweb.com",
      [LUKSO_TESTNET_CHAIN_ID]: "https://rpc.testnet.lukso.network",
    },
    showQrModal: true,
    qrModalOptions: {
      explorerRecommendedWalletIds: [
        "abcfa7caec0d196ce9a99b48ee6d562884f7e38252fe7520c1fc9f53bf0e2fbc", // UniversalProfiles
        "c57ca95b47569778a828d19178114f4db188b89b763c899ba0be274e97267d96", // MetaMask
      ],
    },
    metadata: {
      name: "FullSend",
      description: "Decentralized tipping for live streamers",
      url: "https://fullsend.live",
      icons: ["https://fullsend.live/fullsend-icon.svg"],
    },
  });
  return provider;
}

// --- LUKSO Grid mini-app (up-provider) ------------------------
// When FullSend runs embedded inside a Universal Profile Grid, it lives in an
// iframe and the host (universaleverything / UP mobile app) injects a one-click
// provider via up-provider. The UP extension / MetaMask / WalletConnect paths
// cannot reach the wallet from inside that iframe, so we detect the embedded
// context and use the up-provider there. Every non-embedded path is untouched.
function isEmbeddedGrid() {
  try { return window.self !== window.top; } catch (e) { return true; } // cross-origin throws = embedded
}
let _gridProvider = null;
function getGridProvider() {
  if (_gridProvider) return _gridProvider;
  try { _gridProvider = createClientUPProvider(); } catch (e) { _gridProvider = null; }
  return _gridProvider;
}
// up-provider is for wallet methods (accounts, sending tx). Read LYX balance
// straight from the mainnet RPC instead of depending on the provider for reads.
async function rpcBalanceLYX(address, decimals = 1) {
  // Try each RPC in turn — if one is rate-limited ("Request limit exceeded")
  // or down, fall through to the next instead of failing the connect.
  for (const rpc of LUKSO_TESTNET.rpcUrls) {
    try {
      const resp = await fetch(rpc, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ jsonrpc: "2.0", id: 1, method: "eth_getBalance", params: [address, "latest"] }),
      });
      const json = await resp.json();
      if (json && json.error) continue; // rate limit / provider error → next RPC
      if (json && json.result) return (Number(BigInt(json.result)) / 1e18).toFixed(decimals);
    } catch (e) { /* network error → next RPC */ }
  }
  return (0).toFixed(decimals);
}

// --- DESIGN SYSTEM -------------------------------------------
const COLORS = {
  bg: "#06060b",
  bgAlt: "#0a0a12",
  surface: "#0f0f18",
  surfaceAlt: "#141420",
  surfaceHover: "#1a1a28",
  border: "#ffffff08",
  borderSubtle: "#ffffff05",
  borderActive: "#00ff8825",
  text: "#eaeaef",
  textSecondary: "#a0a0b8",
  textMuted: "#7a7a95",
  accent: "#00ff88",
  accentDim: "#00ff8820",
  accentGlow: "#00ff8840",
  gold: "#ffd700",
  purple: "#b44aff",
  blue: "#4a9eff",
  green: "#00ff88",
  red: "#ff4a6a",
  orange: "#ff8c00",
  cyan: "#00e5ff",
};

const TIERS = {
  micro: { min: 0, max: 4.99, emoji: "⚡", color: COLORS.orange, label: "Micro", glow: "none", duration: 2450, bg: "#ff8c0008", maxChars: 0 },
  green: { min: 5, max: 19.99, emoji: "💚", color: "#00cc66", label: "Supporter", glow: "0 0 20px #00cc6640", duration: 5000, bg: "#00cc6608", maxChars: 80 },
  blue: { min: 20, max: 49.99, emoji: "💪", color: "#4a9eff", label: "Champion", glow: "0 0 30px #4a9eff40", duration: 10000, bg: "#4a9eff08", maxChars: 120 },
  purple: { min: 50, max: 99.99, emoji: "💎", color: "#b44aff", label: "Legend", glow: "0 0 40px #b44aff40", duration: 15000, bg: "#b44aff08", maxChars: 150 },
  fire: { min: 100, max: Infinity, emoji: "🐋", color: "#ffd700", label: "WHALE", glow: "0 0 60px #ffd70050, 0 0 120px #ff8c0020", duration: 20000, bg: "#ffd70008", maxChars: 200 },
};

// Reusable copy button with "✓ Copied" feedback
function CopyButton({ text, label = "Copy", color = COLORS.cyan, style = {} }) {
  const [copied, setCopied] = useState(false);
  return (
    <button onClick={() => {
      navigator.clipboard?.writeText?.(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }} style={{
      padding: "5px 12px", borderRadius: 5,
      background: copied ? `${COLORS.accent}15` : `${color}10`,
      border: `1px solid ${copied ? COLORS.accent + "30" : color + "20"}`,
      color: copied ? COLORS.accent : color,
      fontSize: 10, fontWeight: 700, cursor: "pointer",
      fontFamily: "'JetBrains Mono', monospace",
      flexShrink: 0, transition: "all 0.2s",
      ...style,
    }}>{copied ? "✓ Copied" : label}</button>
  );
}

function getTier(amount) {
  const ct = window.__fullsend_settings_data?.customTiers;
  const whaleMin = ct?.whale?.min || 100;
  const legendMin = ct?.legend?.min || 50;
  const championMin = ct?.champion?.min || 20;
  const supporterMin = ct?.supporter?.min || 5;
  if (amount >= whaleMin) return TIERS.fire;
  if (amount >= legendMin) return TIERS.purple;
  if (amount >= championMin) return TIERS.blue;
  if (amount >= supporterMin) return TIERS.green;
  return TIERS.micro;
}

// --- SYNC CREATOR TO DB (shared helper) -------------------------
// Always sends the COMPLETE platforms object to prevent partial overwrites
// Option 3: Only UP users can publish creator data. EOAs can browse/customize
// locally, but nothing hits the DB until they upgrade to a UP.
function syncCreatorToDB(overrides = {}) {
  const address = window.__fullsend_profile?.address;
  if (!address) return;
  // EOA guard — require Universal Profile for creator registration
  if (!window.__fullsend_profile?.isUP) return;
  const s = window.__fullsend_settings_data || {};
  const upLinks = window.__fullsend_profile?.links || {};
  
  // Build complete social links: saved settings > UP profile
  const socialLinks = {
    youtube: s.socialLinks?.youtube || upLinks.youtube || "",
    twitch: s.socialLinks?.twitch || upLinks.twitch || "",
    kick: s.socialLinks?.kick || upLinks.kick || "",
    rumble: s.socialLinks?.rumble || upLinks.rumble || "",
    x: s.socialLinks?.x || upLinks.x || "",
    website: s.socialLinks?.website || upLinks.website || "",
  };

  // Build platforms_merge — only includes fields that should be updated
  // Backend will merge with existing DB state, so we don't need to send everything
  const platforms_merge = {
    ...socialLinks,
    ...overrides,
  };

  // Only include _widgetTheme if widget settings exist
  if (s.widgetAccent) {
    platforms_merge._widgetTheme = {
      accent: s.widgetAccent,
      mode: s.widgetMode || "dark",
      radius: s.widgetRadius || 12,
    };
  }

  // Custom uploaded social-link icons (key -> base64). Only send if any exist.
  if (s.socialIcons && Object.values(s.socialIcons).some(Boolean)) {
    platforms_merge._socialIcons = s.socialIcons;
  }

  // Creator-defined extra links. Only send the ones that actually have a URL.
  if (Array.isArray(s.customLinks)) {
    const cl = s.customLinks.filter(l => l && l.url && l.url.trim());
    platforms_merge._customLinks = cl;
  }

  // _activeGoal is owned by dedicated goal handlers (create/update/bump/clear).
  // syncCreatorToDB intentionally does NOT touch it to avoid race conditions where
  // settings-sync overwrites a fresh tip/vote bump with stale data.

  // Only include _overlayState if overrides didn't already set it
  if (!('_overlayState' in overrides) && window.__fullsend_overlay_state) {
    platforms_merge._overlayState = window.__fullsend_overlay_state;
  }

  fetch("/api/creator-register", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      address,
      name: s.channelName || window.__fullsend_profile?.name,
      avatar: window.__fullsend_profile?.avatar,
      bio: s.channelBio || window.__fullsend_profile?.bio || "",
      tags: s.channelTags || [],
      platforms_merge,
      settings: {
        verification: s.verification || {},
        socialLinks,
        minTip: s.minTip,
        autoShowTiers: s.autoShowTiers,
        customTiers: s.customTiers,
        // Sound + TTS settings — needed by standalone OBS overlay
        selectedSound: s.selectedSound || {},
        customSounds: s.customSounds || {},
        masterVolume: window.__fullsend_volume,
        soundToggles: window.__fullsend_sound_toggles || { tips: true, follows: true, polls: true },
        tts: window.__fullsend_tts || { enabled: false },
        // Widget extras — read by standalone /tip/[address] page
        thankYouEnabled: s.thankYouEnabled !== false,
        thankYouMessage: s.thankYouMessage || "Thank you for your support! 💚",
        thankYouImage: s.thankYouImage || "",
        showTopTippers: s.showTopTippers !== false,
        webhookUrl: s.webhookUrl || "",
      },
    }),
  }).catch(() => {});
}

// --- LOAD CREATOR SETTINGS FOR CONNECTED ADDRESS ----------------
// The settings blob (window.__fullsend_settings_data + localStorage
// "fullsend_settings") is a single GLOBAL object, not keyed by address. After an
// account switch it still holds the PREVIOUS creator's channel identity
// (name/bio/socials/verification/tiers/sounds), which then (a) renders in the
// widget preview and (b) gets written into the NEW creator's directory record by
// syncCreatorToDB. This rebuilds the blob for the connected address from DB
// (falling back to the connected UP's own profile for brand-new creators) so the
// previous creator's data never lingers or leaks across accounts.
async function loadCreatorSettingsForAddress(address) {
  const up = window.__fullsend_profile || {};
  const upLinks = up.links || {};
  // Base = the connected UP's own profile (used as-is for unregistered creators)
  const fresh = {
    channelName: up.name || "",
    channelBio: up.bio || "",
    channelTags: [],
    socialLinks: {
      youtube: upLinks.youtube || "",
      twitch: upLinks.twitch || "",
      kick: upLinks.kick || "",
      rumble: upLinks.rumble || "",
      x: upLinks.x || "",
      website: upLinks.website || "",
    },
    socialIcons: {},
    customLinks: [],
    verification: {},
  };
  let blob = { ...fresh };
  try {
    const r = await fetch(`/api/creator-register?address=${address}`);
    if (r.ok) {
      const data = await r.json();
      const creator = data.creators?.[0] || data.creator;
      if (creator) {
        const platforms = typeof creator.platforms === "string" ? JSON.parse(creator.platforms || "{}") : (creator.platforms || {});
        const settings = typeof creator.settings === "string" ? JSON.parse(creator.settings || "{}") : (creator.settings || {});
        const sl = settings.socialLinks || {};
        const wt = platforms._widgetTheme || {};
        blob = {
          channelName: creator.name || fresh.channelName,
          channelBio: creator.bio || fresh.channelBio,
          channelTags: creator.tags || [],
          socialLinks: {
            youtube: sl.youtube || platforms.youtube || fresh.socialLinks.youtube,
            twitch: sl.twitch || platforms.twitch || fresh.socialLinks.twitch,
            kick: sl.kick || platforms.kick || fresh.socialLinks.kick,
            rumble: sl.rumble || platforms.rumble || fresh.socialLinks.rumble,
            x: sl.x || platforms.x || fresh.socialLinks.x,
            website: sl.website || platforms.website || fresh.socialLinks.website,
          },
          verification: settings.verification || {},
          socialIcons: platforms._socialIcons || {},
          customLinks: Array.isArray(platforms._customLinks) ? platforms._customLinks : [],
          // Per-creator preferences persisted in DB (undefined keys dropped below)
          minTip: settings.minTip,
          autoShowTiers: settings.autoShowTiers,
          customTiers: settings.customTiers,
          selectedSound: settings.selectedSound,
          customSounds: settings.customSounds,
          masterVolume: settings.masterVolume,
          soundToggles: settings.soundToggles,
          tts: settings.tts,
          thankYouEnabled: settings.thankYouEnabled,
          thankYouMessage: settings.thankYouMessage,
          thankYouImage: settings.thankYouImage,
          showTopTippers: settings.showTopTippers,
          webhookUrl: settings.webhookUrl,
          widgetAccent: wt.accent,
          widgetMode: wt.mode,
          widgetRadius: wt.radius,
        };
      }
    }
  } catch (e) { /* non-fatal — keep the UP-derived fresh blob */ }
  // Drop undefined keys so they fall through to component defaults
  Object.keys(blob).forEach(k => { if (blob[k] === undefined) delete blob[k]; });
  window.__fullsend_settings_data = blob;
  try { localStorage.setItem("fullsend_settings", JSON.stringify(blob)); } catch {}
  // Re-derive dependent globals (they were seeded from the OLD creator's blob)
  window.__fullsend_tts = blob.tts || { enabled: false, voice: "", minAmount: 5, rate: 1, pitch: 1, tiers: { micro: false, supporter: false, champion: true, legend: true, whale: true } };
  window.__fullsend_volume = (blob.masterVolume ?? 0.7);
  window.__fullsend_sound_toggles = blob.soundToggles || { tips: true, follows: true, polls: true };
  window.__fullsend_selected_sounds = blob.selectedSound || {};
  window.__fullsend_thankyou = { enabled: blob.thankYouEnabled !== false, message: blob.thankYouMessage || "Thank you for your support! 💚", image: blob.thankYouImage || "" };
  window.__fullsend_show_top_tippers = blob.showTopTippers !== false;
  // Force widget/settings readers to re-render with the connected creator's data
  window.dispatchEvent(new CustomEvent("fullsend-settings-changed", { detail: blob }));
}

// --- PUSH OVERLAY COMMAND (lightweight, doesn't overwrite other fields) ---
function pushOverlayCmd(address, overlayState) {
  window.__fullsend_overlay_state = overlayState;
  fetch("/api/creator-register", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      address,
      platforms_merge: { _overlayState: overlayState },
    }),
  }).catch(() => {});
}

// --- TIPROUTER CONTRACT --------------------------------------
const TIPROUTER_ADDRESS = "0xa4CF21e0a2603fcEaf9f69d8B9b5658E66BefEAf";
const TIPROUTER_ABI = [
  { name: "tip", type: "function", stateMutability: "payable", inputs: [{ name: "_creator", type: "address" }, { name: "_messageHash", type: "bytes32" }], outputs: [] },
  { name: "calculateFee", type: "function", stateMutability: "view", inputs: [{ name: "_totalPayment", type: "uint256" }], outputs: [{ name: "fee", type: "uint256" }] },
  { name: "calculateTotalPayment", type: "function", stateMutability: "view", inputs: [{ name: "_tipAmount", type: "uint256" }], outputs: [{ name: "totalPayment", type: "uint256" }] },
  { name: "getFeeConfig", type: "function", stateMutability: "view", inputs: [], outputs: [{ name: "percentage", type: "uint256" }, { name: "cap", type: "uint256" }, { name: "minTip", type: "uint256" }, { name: "isRelayerEnabled", type: "bool" }, { name: "isPaused", type: "bool" }] },
  { name: "getAnalytics", type: "function", stateMutability: "view", inputs: [], outputs: [{ name: "volume", type: "uint256" }, { name: "fees", type: "uint256" }, { name: "count", type: "uint256" }] },
  { name: "feePercentage", type: "function", stateMutability: "view", inputs: [], outputs: [{ name: "", type: "uint256" }] },
  { name: "feeCap", type: "function", stateMutability: "view", inputs: [], outputs: [{ name: "", type: "uint256" }] },
  { name: "paused", type: "function", stateMutability: "view", inputs: [], outputs: [{ name: "", type: "bool" }] },
  { name: "TipSent", type: "event", inputs: [{ name: "from", type: "address", indexed: true }, { name: "to", type: "address", indexed: true }, { name: "amount", type: "uint256" }, { name: "fee", type: "uint256" }, { name: "messageHash", type: "bytes32" }, { name: "timestamp", type: "uint256" }] },
];

// LUKSO Testnet config
const LUKSO_TESTNET = {
  chainId: "0x2a", // 42
  chainName: "LUKSO",
  // Free public RPCs — Thirdweb primary (separate rate-limit pool), LUKSO
  // official as backup. Sigmacore moved to a paid plan, so it's no longer used.
  // Read helpers fall back across this list when one returns "Request limit
  // exceeded" / errors.
  rpcUrls: ["https://42.rpc.thirdweb.com", "https://rpc.mainnet.lukso.network"],
  blockExplorerUrls: ["https://explorer.execution.mainnet.lukso.network"],
  nativeCurrency: { name: "LYX", symbol: "LYX", decimals: 18 },
};

// ============================================================================
// GOAL SYSTEM — DB-only source of truth (Session 20 refactor)
// ============================================================================
// Single shape: platforms._activeGoal = { id, title, target, current, status,
//   showOnWidget, showOnOverlay, createdAt, completedAt? }
// History: platforms._goalHistory = [ { ...goal }, ... ] (append-only)
// ALL reads/writes go through this module. No localStorage. No window globals.
// ============================================================================

const GoalAPI = {
  // Read full goal state for a creator (active + history)
  async fetch(creatorAddress) {
    if (!creatorAddress) return { active: null, history: [] };
    try {
      const r = await fetch(`/api/creator-register?address=${creatorAddress}`);
      if (!r.ok) return { active: null, history: [] };
      const d = await r.json();
      const platforms = typeof d.creator?.platforms === "string"
        ? JSON.parse(d.creator.platforms || "{}")
        : (d.creator?.platforms || {});
      // A lingering _activeGoal object (e.g. a backend merge that didn't honor a
      // null clear on archive/delete) must NOT render as active. Gate strictly on
      // status === "active" + a real target — this is the single source for every
      // goal consumer via useActiveGoal, so it kills ghost goals everywhere.
      const _ag = platforms._activeGoal;
      const active = (_ag && _ag.title && _ag.status === "active" && parseFloat(_ag.target) > 0)
        ? _ag : null;
      const history = Array.isArray(platforms._goalHistory) ? platforms._goalHistory : [];
      return { active, history };
    } catch { return { active: null, history: [] }; }
  },

  // Write active goal directly (used by create / update progress / activate paused)
  async setActive(creatorAddress, goal) {
    if (!creatorAddress) return false;
    try {
      const r = await fetch("/api/creator-register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          address: creatorAddress.toLowerCase(),
          platforms_merge: { _activeGoal: goal },
        }),
      });
      const d = await r.json();
      return !!d?.success;
    } catch { return false; }
  },

  // Bump the active goal's current value by an amount (tips, votes)
  // Reads DB first to avoid stale-overwrite races.
  async bumpProgress(creatorAddress, amount) {
    if (!creatorAddress || !amount || amount <= 0) return null;
    const { active } = await GoalAPI.fetch(creatorAddress);
    if (!active || active.status !== "active") return null;
    const newCurrent = Math.min((parseFloat(active.current) || 0) + parseFloat(amount), parseFloat(active.target) || 0);
    const updated = { ...active, current: newCurrent };
    if (newCurrent >= parseFloat(active.target) && !updated.completedAt) {
      updated.completedAt = new Date().toISOString();
    }
    const ok = await GoalAPI.setActive(creatorAddress, updated);
    return ok ? updated : null;
  },

  // Create a new goal — pushes any currently active goal to history as "paused"
  async create(creatorAddress, { title, target }) {
    if (!creatorAddress || !title || !target) return false;
    const { active, history } = await GoalAPI.fetch(creatorAddress);
    const newHistory = [...history];
    if (active && active.status === "active") {
      newHistory.unshift({ ...active, status: "paused" });
    }
    const newGoal = {
      id: "goal-" + Date.now(),
      title: title.trim(),
      target: parseFloat(target),
      current: 0,
      status: "active",
      showOnWidget: true,
      showOnOverlay: true,
      createdAt: new Date().toISOString(),
    };
    try {
      const r = await fetch("/api/creator-register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          address: creatorAddress.toLowerCase(),
          platforms_merge: { _activeGoal: newGoal, _goalHistory: newHistory },
        }),
      });
      const d = await r.json();
      return d?.success ? newGoal : false;
    } catch { return false; }
  },

  // Activate a goal from history (swaps active <-> paused entry)
  async activate(creatorAddress, goalId) {
    const { active, history } = await GoalAPI.fetch(creatorAddress);
    const target = history.find(g => g.id === goalId);
    if (!target) return false;
    const newHistory = history.filter(g => g.id !== goalId);
    if (active && active.status === "active") {
      newHistory.unshift({ ...active, status: "paused" });
    }
    const newActive = { ...target, status: "active" };
    try {
      const r = await fetch("/api/creator-register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          address: creatorAddress.toLowerCase(),
          platforms_merge: { _activeGoal: newActive, _goalHistory: newHistory },
        }),
      });
      const d = await r.json();
      return !!d?.success;
    } catch { return false; }
  },

  // Archive: move active (or any history goal) to history with status="archived"
  async archive(creatorAddress, goalId) {
    const { active, history } = await GoalAPI.fetch(creatorAddress);
    let newActive = active;
    let newHistory = [...history];
    if (active?.id === goalId) {
      newHistory.unshift({ ...active, status: "archived", archivedAt: new Date().toISOString() });
      newActive = null;
    } else {
      newHistory = newHistory.map(g => g.id === goalId ? { ...g, status: "archived", archivedAt: new Date().toISOString() } : g);
    }
    try {
      const r = await fetch("/api/creator-register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          address: creatorAddress.toLowerCase(),
          platforms_merge: { _activeGoal: newActive, _goalHistory: newHistory },
        }),
      });
      const d = await r.json();
      return !!d?.success;
    } catch { return false; }
  },

  // Delete forever — removes from history (or clears active)
  async remove(creatorAddress, goalId) {
    const { active, history } = await GoalAPI.fetch(creatorAddress);
    let newActive = active;
    if (active?.id === goalId) newActive = null;
    const newHistory = history.filter(g => g.id !== goalId);
    try {
      const r = await fetch("/api/creator-register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          address: creatorAddress.toLowerCase(),
          platforms_merge: { _activeGoal: newActive, _goalHistory: newHistory },
        }),
      });
      const d = await r.json();
      return !!d?.success;
    } catch { return false; }
  },

  // Toggle visibility flags on active goal
  async setVisibility(creatorAddress, { showOnWidget, showOnOverlay }) {
    const { active } = await GoalAPI.fetch(creatorAddress);
    if (!active) return false;
    const updated = { ...active };
    if (typeof showOnWidget === "boolean") updated.showOnWidget = showOnWidget;
    if (typeof showOnOverlay === "boolean") updated.showOnOverlay = showOnOverlay;
    return GoalAPI.setActive(creatorAddress, updated);
  },

  // Reset progress to 0 (keeps goal active)
  async reset(creatorAddress) {
    const { active } = await GoalAPI.fetch(creatorAddress);
    if (!active) return false;
    return GoalAPI.setActive(creatorAddress, { ...active, current: 0, completedAt: null });
  },
};

// React hook: subscribe to goal state with auto-refresh every 5s
function useActiveGoal(creatorAddress, intervalMs = 5000) {
  const [state, setState] = useState({ active: null, history: [], loading: true });
  const refresh = useCallback(async () => {
    if (!creatorAddress) { setState({ active: null, history: [], loading: false }); return; }
    const { active, history } = await GoalAPI.fetch(creatorAddress);
    setState({ active, history, loading: false });
  }, [creatorAddress]);
  useEffect(() => {
    refresh();
    if (!creatorAddress) return;
    const id = setInterval(refresh, intervalMs);
    const onTip = () => refresh();
    window.addEventListener("fullsend-tip", onTip);
    window.addEventListener("fullsend-goal-updated", onTip);
    return () => { clearInterval(id); window.removeEventListener("fullsend-tip", onTip); window.removeEventListener("fullsend-goal-updated", onTip); };
  }, [creatorAddress, refresh, intervalMs]);
  return { ...state, refresh };
}


// --- ADMIN CONFIG --------------------------------------------
// Whitelisted addresses that can access /admin dashboard
const ADMIN_WHITELIST = [
  "0x8eF515A8B0d119fc0A331Cb847D9E52B1892f3aa", // Testnet/dev admin UP
  "0x29d7c7E4571a83B3eF5C867f75c81D736a9D58aa", // Mainnet creator UP (treasury)
  "0x3c34A77c9f751359b78d9d03e024217733cbc9F7", // Deployer EOA (contract owner)
  "0x3C23a8cA4F38AA1a9EAD2b8F38F1515349184504", // Mainnet relayer EOA
];

// Deployer / contract owner EOA. Owns on-chain admin functions on TipRouter +
// PollVoting (setFlatFee, setTreasury, setRelayer, transferOwnership, etc).
// Should NEVER be used for relay calls or tip flow — it's strictly for admin ops.
const TREASURY_ADDRESS = "0x3c34A77c9f751359b78d9d03e024217733cbc9F7";

// Creator UP that receives the 0.50 LYX fee from every tip via TipRouter.
// This is the actual platform treasury — fees flow here, not to the deployer.
const CREATOR_TREASURY_UP = "0x29d7c7E4571a83B3eF5C867f75c81D736a9D58aa";

// Cap a numeric balance string at 5 visible chars (e.g. "36.88", "3.999",
// "0.123", "1234", "12345"). Truncates instead of rounding so displayed
// values never overstate the wallet. Used on admin balance StatCards.
function capBalance(b) {
  if (b === null || b === undefined || b === "") return null;
  const num = parseFloat(b);
  if (isNaN(num)) return null;
  if (num === 0) return "0.000";
  const intDigits = Math.floor(Math.abs(num)).toString().length;
  // 5-char budget: integer part + "." + decimals = 5
  // So decimals = max(0, 4 - intDigits)
  const decimals = Math.max(0, 4 - intDigits);
  const factor = Math.pow(10, decimals);
  return (Math.floor(num * factor) / factor).toFixed(decimals);
}

// Format a LYX amount with auto-scaling units so very small values stay readable.
// Mainnet relayer gas per action ≈ 0.000003 LYX which renders as "0.0000" at 4
// decimals. This helper picks the right unit so the same value reads as "3.00 µLYX".
//   ≥ 0.01 LYX     → "0.0123 LYX"
//   ≥ 0.001 LYX    → "0.00123 LYX"
//   ≥ 0.000001 LYX → "123.45 µLYX"
//   < 0.000001 LYX → "123.45 nLYX"
//   0              → "0"
function formatGas(lyx) {
  if (lyx === null || lyx === undefined || isNaN(lyx)) return "—";
  const n = Number(lyx);
  if (n === 0) return "0";
  const abs = Math.abs(n);
  if (abs >= 0.01)     return `${n.toFixed(4)} LYX`;
  if (abs >= 0.001)    return `${n.toFixed(5)} LYX`;
  if (abs >= 0.000001) return `${(n * 1e6).toFixed(2)} µLYX`;
  return `${(n * 1e9).toFixed(2)} nLYX`;
}

// --- LSP26 FOLLOWER SYSTEM -----------------------------------
// LUKSO native singleton — same address on testnet + mainnet (CREATE2)
const LSP26_ADDRESS = "0xf01103E5a9909Fc0DBe8166dA7085e0285daDDcA";

// Map of stock MP3 sounds (relative URLs in /public/sounds)
const STOCK_MP3_SOUNDS = {
  // Micro tier
  "typewriter": "/sounds/micro/typewriter.mp3",
  "puzzle": "/sounds/micro/puzzle.mp3",
  "mario": "/sounds/micro/mario.mp3",
  "whoosh": "/sounds/micro/whoosh.mp3",
  // Supporter tier
  "doorbell": "/sounds/supporter/doorbell.mp3",
  "cash-register": "/sounds/supporter/cash-register.mp3",
  "woah": "/sounds/supporter/woah.mp3",
  "gameshow": "/sounds/supporter/gameshow.mp3",
  // Champion tier
  "camera": "/sounds/champion/camera.mp3",
  "PA": "/sounds/champion/PA.mp3",
  "gong": "/sounds/champion/gong.mp3",
  "champion": "/sounds/champion/champion.mp3",
  // Legend tier
  "rizz": "/sounds/legend/rizz.mp3",
  "wow": "/sounds/legend/wow.mp3",
  "Wilhelm": "/sounds/legend/Wilhelm.mp3",
  "hello-there": "/sounds/legend/hello-there.mp3",
  // Whale tier
  "royal-fanfare": "/sounds/whale/royal-fanfare.mp3",
  "yeah-boi": "/sounds/whale/yeah-boi.mp3",
  "isnt-that-amazing": "/sounds/whale/isnt-that-amazing.mp3",
  "whale": "/sounds/whale/whale.mp3",
  // Follow tier
  "noti": "/sounds/follow/noti.mp3",
  "8bit": "/sounds/follow/8bit.mp3",
  "upgrade": "/sounds/follow/upgrade.mp3",
  "amp": "/sounds/follow/amp.mp3",
};

// Preload & cache MP3s so the first play is instant (no network/decode delay)
// Eliminates the "SFX fires late" lag when the alert card slides in
const __MP3_CACHE = {};
(function preloadStockSounds() {
  if (typeof window === "undefined") return;
  for (const [name, url] of Object.entries(STOCK_MP3_SOUNDS)) {
    try {
      const a = new Audio();
      a.preload = "auto";
      a.src = url;
      a.load(); // kick browser to fetch+decode now
      __MP3_CACHE[name] = a;
    } catch {}
  }
})();
// Expose so custom uploads can also warm the cache
if (typeof window !== "undefined") {
  window.__fullsend_mp3_cache = __MP3_CACHE;
  window.__fullsend_preload_custom = function(key, url) {
    try {
      if (!url) return;
      const a = new Audio();
      a.preload = "auto";
      a.src = url;
      a.load();
      __MP3_CACHE[`custom:${key}`] = a;
    } catch {}
  };
}

// Global sound engine — always available for overlay, settings, and alerts
function playFullSendSound(soundName, volume) {
  // MP3 file sounds — play via preloaded Audio element (instant, no network wait)
  if (STOCK_MP3_SOUNDS[soundName]) {
    try {
      const cached = __MP3_CACHE[soundName];
      const vol = volume !== undefined ? volume : (window.__fullsend_volume || 0.7);
      if (cached) {
        // Clone so overlapping plays don't cut each other off,
        // but the cloned node reuses the decoded source (fast path).
        const a = cached.cloneNode(true);
        a.volume = vol;
        a.play().catch(() => {});
      } else {
        const a = new Audio(STOCK_MP3_SOUNDS[soundName]);
        a.volume = vol;
        a.play().catch(() => {});
      }
    } catch {}
    return;
  }
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const master = ctx.createGain();
    master.gain.value = volume !== undefined ? volume : (window.__fullsend_volume || 0.7);
    master.connect(ctx.destination);
    const now = ctx.currentTime;
    const dest = master;
    const patterns = {
      "coin-drop": () => { const o = ctx.createOscillator(); const g = ctx.createGain(); o.type = "sine"; o.frequency.setValueAtTime(1200, now); o.frequency.exponentialRampToValueAtTime(600, now + 0.15); g.gain.setValueAtTime(0.3, now); g.gain.exponentialRampToValueAtTime(0.01, now + 0.2); o.connect(g); g.connect(dest); o.start(now); o.stop(now + 0.2); },
      "soft-ping": () => { const o = ctx.createOscillator(); const g = ctx.createGain(); o.type = "sine"; o.frequency.value = 880; g.gain.setValueAtTime(0.2, now); g.gain.exponentialRampToValueAtTime(0.01, now + 0.3); o.connect(g); g.connect(dest); o.start(now); o.stop(now + 0.3); },
      "subtle-chime": () => { [1047, 1319, 1568].forEach((f, i) => { const o = ctx.createOscillator(); const g = ctx.createGain(); o.type = "sine"; o.frequency.value = f; g.gain.setValueAtTime(0.15, now + i * 0.08); g.gain.exponentialRampToValueAtTime(0.01, now + i * 0.08 + 0.25); o.connect(g); g.connect(dest); o.start(now + i * 0.08); o.stop(now + i * 0.08 + 0.25); }); },
      "pop": () => { const o = ctx.createOscillator(); const g = ctx.createGain(); o.type = "sine"; o.frequency.setValueAtTime(400, now); o.frequency.exponentialRampToValueAtTime(150, now + 0.1); g.gain.setValueAtTime(0.4, now); g.gain.exponentialRampToValueAtTime(0.01, now + 0.12); o.connect(g); g.connect(dest); o.start(now); o.stop(now + 0.12); },
      "cash-register": () => { [800, 1000, 1200, 1400].forEach((f, i) => { const o = ctx.createOscillator(); const g = ctx.createGain(); o.type = "square"; o.frequency.value = f; g.gain.setValueAtTime(0.15, now + i * 0.05); g.gain.exponentialRampToValueAtTime(0.01, now + i * 0.05 + 0.1); o.connect(g); g.connect(dest); o.start(now + i * 0.05); o.stop(now + i * 0.05 + 0.15); }); },
      "level-up": () => { [523, 659, 784, 1047].forEach((f, i) => { const o = ctx.createOscillator(); const g = ctx.createGain(); o.type = "square"; o.frequency.value = f; g.gain.setValueAtTime(0.2, now + i * 0.1); g.gain.exponentialRampToValueAtTime(0.01, now + i * 0.1 + 0.2); o.connect(g); g.connect(dest); o.start(now + i * 0.1); o.stop(now + i * 0.1 + 0.25); }); },
      "notification-bell": () => { const o = ctx.createOscillator(); const g = ctx.createGain(); o.type = "sine"; o.frequency.value = 1500; g.gain.setValueAtTime(0.3, now); g.gain.setValueAtTime(0.3, now + 0.1); g.gain.exponentialRampToValueAtTime(0.01, now + 0.5); o.connect(g); g.connect(dest); o.start(now); o.stop(now + 0.5); },
      "success": () => { [523, 659, 784].forEach((f, i) => { const o = ctx.createOscillator(); const g = ctx.createGain(); o.type = "triangle"; o.frequency.value = f; g.gain.setValueAtTime(0.25, now + i * 0.12); g.gain.exponentialRampToValueAtTime(0.01, now + i * 0.12 + 0.3); o.connect(g); g.connect(dest); o.start(now + i * 0.12); o.stop(now + i * 0.12 + 0.35); }); },
      "fanfare": () => { [523, 659, 784, 1047, 784, 1047].forEach((f, i) => { const o = ctx.createOscillator(); const g = ctx.createGain(); o.type = "sawtooth"; o.frequency.value = f; g.gain.setValueAtTime(0.12, now + i * 0.1); g.gain.exponentialRampToValueAtTime(0.01, now + i * 0.1 + 0.25); o.connect(g); g.connect(dest); o.start(now + i * 0.1); o.stop(now + i * 0.1 + 0.3); }); },
      "power-up": () => { const o = ctx.createOscillator(); const g = ctx.createGain(); o.type = "sawtooth"; o.frequency.setValueAtTime(200, now); o.frequency.exponentialRampToValueAtTime(1200, now + 0.4); g.gain.setValueAtTime(0.2, now); g.gain.exponentialRampToValueAtTime(0.01, now + 0.5); o.connect(g); g.connect(dest); o.start(now); o.stop(now + 0.5); },
      "achievement": () => { [784, 988, 1175, 1568].forEach((f, i) => { const o = ctx.createOscillator(); const g = ctx.createGain(); o.type = "triangle"; o.frequency.value = f; g.gain.setValueAtTime(0.2, now + i * 0.08); g.gain.exponentialRampToValueAtTime(0.01, now + i * 0.08 + 0.4); o.connect(g); g.connect(dest); o.start(now + i * 0.08); o.stop(now + i * 0.08 + 0.45); }); },
      "trumpets": () => { [392, 494, 587, 784].forEach((f, i) => { const o = ctx.createOscillator(); const g = ctx.createGain(); o.type = "sawtooth"; o.frequency.value = f; g.gain.setValueAtTime(0.1, now + i * 0.15); g.gain.exponentialRampToValueAtTime(0.01, now + i * 0.15 + 0.35); o.connect(g); g.connect(dest); o.start(now + i * 0.15); o.stop(now + i * 0.15 + 0.4); }); },
      "epic-horn": () => { [262, 330, 392, 523].forEach((f, i) => { const o = ctx.createOscillator(); const o2 = ctx.createOscillator(); const g = ctx.createGain(); o.type = "sawtooth"; o2.type = "square"; o.frequency.value = f; o2.frequency.value = f * 1.005; g.gain.setValueAtTime(0.01, now + i * 0.15); g.gain.linearRampToValueAtTime(0.15, now + i * 0.15 + 0.1); g.gain.setValueAtTime(0.15, now + i * 0.15 + 0.3); g.gain.exponentialRampToValueAtTime(0.01, now + i * 0.15 + 0.8); o.connect(g); o2.connect(g); g.connect(dest); o.start(now + i * 0.15); o2.start(now + i * 0.15); o.stop(now + i * 0.15 + 0.85); o2.stop(now + i * 0.15 + 0.85); }); },
      "dramatic-reveal": () => { const o = ctx.createOscillator(); const g = ctx.createGain(); o.type = "sawtooth"; o.frequency.setValueAtTime(80, now); o.frequency.exponentialRampToValueAtTime(800, now + 0.8); g.gain.setValueAtTime(0.01, now); g.gain.linearRampToValueAtTime(0.2, now + 0.3); g.gain.exponentialRampToValueAtTime(0.01, now + 1.0); o.connect(g); g.connect(dest); o.start(now); o.stop(now + 1.0); },
      "crowd-cheer": () => { for (let i = 0; i < 12; i++) { const o = ctx.createOscillator(); const g = ctx.createGain(); o.type = i % 2 === 0 ? "sawtooth" : "triangle"; o.frequency.value = 150 + Math.random() * 500; g.gain.setValueAtTime(0.01, now); g.gain.linearRampToValueAtTime(0.05, now + 0.15); g.gain.exponentialRampToValueAtTime(0.01, now + 1.0); o.connect(g); g.connect(dest); o.start(now + Math.random() * 0.1); o.stop(now + 1.0); } },
      "orchestral": () => { [262, 330, 392, 523, 659].forEach((f, i) => { const o = ctx.createOscillator(); const g = ctx.createGain(); o.type = "sawtooth"; o.frequency.value = f; g.gain.setValueAtTime(0.01, now); g.gain.linearRampToValueAtTime(0.08, now + 0.15); g.gain.exponentialRampToValueAtTime(0.01, now + 1.2); o.connect(g); g.connect(dest); o.start(now + i * 0.05); o.stop(now + 1.2); }); },
      "air-horn": () => { [110, 138, 165, 220].forEach((f) => { const o = ctx.createOscillator(); const g = ctx.createGain(); o.type = "sawtooth"; o.frequency.value = f; g.gain.setValueAtTime(0.01, now); g.gain.linearRampToValueAtTime(0.18, now + 0.15); g.gain.exponentialRampToValueAtTime(0.01, now + 1.4); o.connect(g); g.connect(dest); o.start(now); o.stop(now + 1.4); }); },
      "explosion": () => { const bufferSize = ctx.sampleRate * 1.2; const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate); const data = buffer.getChannelData(0); for (let i = 0; i < bufferSize; i++) data[i] = (Math.random() * 2 - 1) * Math.exp(-i / (ctx.sampleRate * 0.2)); const src = ctx.createBufferSource(); const g = ctx.createGain(); src.buffer = buffer; g.gain.setValueAtTime(0.5, now); g.gain.exponentialRampToValueAtTime(0.01, now + 1.2); src.connect(g); g.connect(dest); src.start(now); },
      "dubstep-drop": () => { const o = ctx.createOscillator(); const g = ctx.createGain(); o.type = "sawtooth"; o.frequency.setValueAtTime(800, now); o.frequency.exponentialRampToValueAtTime(40, now + 0.25); o.frequency.setValueAtTime(800, now + 0.3); o.frequency.exponentialRampToValueAtTime(40, now + 0.55); g.gain.setValueAtTime(0.3, now); g.gain.exponentialRampToValueAtTime(0.01, now + 1.0); o.connect(g); g.connect(dest); o.start(now); o.stop(now + 1.0); },
      "crowd-roar": () => { for (let i = 0; i < 16; i++) { const o = ctx.createOscillator(); const g = ctx.createGain(); o.type = i % 3 === 0 ? "sawtooth" : "triangle"; o.frequency.value = 80 + Math.random() * 500; g.gain.setValueAtTime(0.01, now); g.gain.linearRampToValueAtTime(0.04, now + 0.2); g.gain.exponentialRampToValueAtTime(0.01, now + 1.3); o.connect(g); g.connect(dest); o.start(now + Math.random() * 0.08); o.stop(now + 1.3); } },
      "notification": () => { [880, 1100].forEach((f, i) => { const o = ctx.createOscillator(); const g = ctx.createGain(); o.type = "sine"; o.frequency.value = f; g.gain.setValueAtTime(0.2, now + i * 0.12); g.gain.exponentialRampToValueAtTime(0.01, now + i * 0.12 + 0.2); o.connect(g); g.connect(dest); o.start(now + i * 0.12); o.stop(now + i * 0.12 + 0.25); }); },
      "welcome-chime": () => { [659, 784, 988, 1319].forEach((f, i) => { const o = ctx.createOscillator(); const g = ctx.createGain(); o.type = "sine"; o.frequency.value = f; g.gain.setValueAtTime(0.15, now + i * 0.1); g.gain.exponentialRampToValueAtTime(0.01, now + i * 0.1 + 0.3); o.connect(g); g.connect(dest); o.start(now + i * 0.1); o.stop(now + i * 0.1 + 0.35); }); },
      "bell": () => { const o = ctx.createOscillator(); const g = ctx.createGain(); o.type = "sine"; o.frequency.value = 2000; g.gain.setValueAtTime(0.25, now); g.gain.exponentialRampToValueAtTime(0.01, now + 0.6); o.connect(g); g.connect(dest); o.start(now); o.stop(now + 0.6); },
      "whoosh": () => { const o = ctx.createOscillator(); const g = ctx.createGain(); o.type = "sine"; o.frequency.setValueAtTime(200, now); o.frequency.exponentialRampToValueAtTime(3000, now + 0.15); o.frequency.exponentialRampToValueAtTime(200, now + 0.3); g.gain.setValueAtTime(0.15, now); g.gain.exponentialRampToValueAtTime(0.01, now + 0.35); o.connect(g); g.connect(dest); o.start(now); o.stop(now + 0.35); },
      // Poll sounds
      "poll-created": () => { [659, 784, 988, 1319, 1568].forEach((f, i) => { const o = ctx.createOscillator(); const g = ctx.createGain(); o.type = "triangle"; o.frequency.value = f; g.gain.setValueAtTime(0.18, now + i * 0.07); g.gain.exponentialRampToValueAtTime(0.01, now + i * 0.07 + 0.3); o.connect(g); g.connect(dest); o.start(now + i * 0.07); o.stop(now + i * 0.07 + 0.35); }); },
      "poll-vote": () => { const o = ctx.createOscillator(); const g = ctx.createGain(); o.type = "sine"; o.frequency.setValueAtTime(600, now); o.frequency.exponentialRampToValueAtTime(900, now + 0.08); g.gain.setValueAtTime(0.15, now); g.gain.exponentialRampToValueAtTime(0.01, now + 0.12); o.connect(g); g.connect(dest); o.start(now); o.stop(now + 0.12); },
      "poll-ended": () => { [988, 784, 659, 523].forEach((f, i) => { const o = ctx.createOscillator(); const g = ctx.createGain(); o.type = "triangle"; o.frequency.value = f; g.gain.setValueAtTime(0.15, now + i * 0.12); g.gain.exponentialRampToValueAtTime(0.01, now + i * 0.12 + 0.3); o.connect(g); g.connect(dest); o.start(now + i * 0.12); o.stop(now + i * 0.12 + 0.35); }); },
    };
    if (patterns[soundName]) patterns[soundName]();
    setTimeout(() => ctx.close(), 2000);
  } catch(e) {}
}
window.__fullsend_playSound = playFullSendSound;

// Play a sound for a tier — checks for custom upload first, then synth
let __lastTierSoundAt = 0;
function playTierSound(tierKey) {
  // Throttle: don't overlap sounds within 600ms of each other
  const now = Date.now();
  if (now - __lastTierSoundAt < 600) return;
  __lastTierSoundAt = now;
  const sounds = window.__fullsend_selected_sounds || {};
  const soundName = sounds[tierKey] || "none";
  if (soundName === "none") return;
  if (soundName === "custom") {
    const customSounds = window.__fullsend_settings_data?.customSounds || {};
    if (customSounds[tierKey]) {
      try {
        const cacheKey = `custom:${tierKey}`;
        let cached = __MP3_CACHE[cacheKey];
        if (!cached || cached.src !== customSounds[tierKey]) {
          // Warm the cache on first use, then reuse
          cached = new Audio();
          cached.preload = "auto";
          cached.src = customSounds[tierKey];
          cached.load();
          __MP3_CACHE[cacheKey] = cached;
        }
        const a = cached.cloneNode(true);
        a.volume = window.__fullsend_volume || 0.7;
        a.play().catch(() => {});
      } catch {}
    }
    return;
  }
  playFullSendSound(soundName);
}
window.__fullsend_playTierSound = playTierSound;

// Helper: encode function call without ethers.js dependency
function encodeFunctionCall(name, types, values) {
  // keccak256 selector
  const sig = `${name}(${types.join(",")})`;
  const encoder = new TextEncoder();
  const data = encoder.encode(sig);
  return crypto.subtle.digest("SHA-256", data).then(() => {
    // Use simple ABI encoding for our known types
    let selector = keccak256(sig).slice(0, 10);
    let encoded = selector;
    for (let i = 0; i < types.length; i++) {
      if (types[i] === "address") {
        encoded += values[i].slice(2).toLowerCase().padStart(64, "0");
      } else if (types[i] === "bytes32") {
        encoded += values[i].slice(2).padStart(64, "0");
      } else if (types[i] === "uint256") {
        encoded += BigInt(values[i]).toString(16).padStart(64, "0");
      }
    }
    return encoded;
  });
}

// Minimal keccak256 for function selectors (4 bytes)
function keccak256(str) {
  // We'll compute the selector using the provider's eth_call with a precompile
  // For now, use precomputed selectors for known functions
  const selectors = {
    "tip(address,bytes32)": "0xfd91fd10",
    "calculateFee(uint256)": "0x99a5d747",
    "calculateTotalPayment(uint256)": "0x8dd8919e",
    "getFeeConfig()": "0x5fbbc0d2",
    "getAnalytics()": "0x0ccb2ec8",
    "feePercentage()": "0xa001ecdd",
    "feeCap()": "0x075b6e33",
    "paused()": "0x5c975abb",
    "follow(address)": "0x4dbf27cc",
    "unfollow(address)": "0x015a4ead",
    "vote(uint256,uint256)": "0xb384abef",
  };
  return selectors[str] || "0x00000000";
}

// Encode a tip() call
function encodeTipCall(creatorAddress, messageHash) {
  const selector = "0xfd91fd10"; // tip(address,bytes32)
  const creator = creatorAddress.slice(2).toLowerCase().padStart(64, "0");
  const msgHash = messageHash.slice(2).padStart(64, "0");
  return selector + creator + msgHash;
}

// Encode LSP26 follow(address) call
function encodeFollowCall(targetAddress) {
  const selector = "0x4dbf27cc"; // follow(address)
  const target = targetAddress.slice(2).toLowerCase().padStart(64, "0");
  return selector + target;
}

// Encode LSP26 unfollow(address) call
function encodeUnfollowCall(targetAddress) {
  const selector = "0x015a4ead"; // unfollow(address)
  const target = targetAddress.slice(2).toLowerCase().padStart(64, "0");
  return selector + target;
}

// Encode PollVoting vote(uint256,uint256) call — for LYX-gated direct wallet votes
function encodeVoteCall(pollId, optionIndex) {
  const selector = "0xb384abef"; // vote(uint256,uint256)
  const id = BigInt(pollId).toString(16).padStart(64, "0");
  const idx = BigInt(optionIndex).toString(16).padStart(64, "0");
  return selector + id + idx;
}

// PollVoting contract address
const POLL_VOTING_ADDRESS = "0xA8A466Ab3096cbe88285d9B712d2562ef7297ACe";

// Encode calculateTotalPayment() call
function encodeCalcTotalPayment(tipAmountWei) {
  const selector = "0x8dd8919e";
  const amount = BigInt(tipAmountWei).toString(16).padStart(64, "0");
  return selector + amount;
}

// Convert LYX to wei
function lyxToWei(lyx) {
  return BigInt(Math.round(parseFloat(lyx) * 1e18)).toString();
}

// Convert wei to LYX
function weiToLyx(wei) {
  return (Number(BigInt(wei)) / 1e18);
}

// Pack a tip message into bytes32 (UTF-8, first 32 bytes), or zeros if empty.
function hashMessage(message) {
  if (!message || message.trim() === "") return "0x" + "0".repeat(64);
  // Proper UTF-8 bytes — charCodeAt would emit UTF-16 surrogates for emoji and
  // any char >255, producing >2 hex digits and corrupting the value.
  const bytes = new TextEncoder().encode(message).slice(0, 32);
  let hex = "0x";
  for (const b of bytes) hex += b.toString(16).padStart(2, "0");
  return hex.padEnd(66, "0");
}

// --- RELAY HELPERS -------------------------------------------
// ERC725X execute operation types
const OPERATION_CALL = 0;

// --- FULLSEND RELAYER PERMISSIONS ---
// FullSend relayer EOA — this address executes relay calls and pays gas.
// IMPORTANT: must match the EOA whose private key is stored in
// RELAYER_PRIVATE_KEY in Vercel env vars. If these drift, gasless follows
// will revert with NoPermissionsSet (UI grants perms to this address, but
// the actual signer is whoever holds RELAYER_PRIVATE_KEY).
const FULLSEND_RELAYER = "0x3C23a8cA4F38AA1a9EAD2b8F38F1515349184504"; // mainnet relayer EOA

// ERC725Y data keys for the relayer
const RELAYER_PERMISSION_KEY = "0x4b80742de2bf82acb3630000" + FULLSEND_RELAYER.slice(2).toLowerCase();
const RELAYER_ALLOWED_CALLS_KEY = "0x4b80742de2bf393a64c70000" + FULLSEND_RELAYER.slice(2).toLowerCase();
const ADDRESS_PERMISSIONS_ARRAY_KEY = "0x4b80742de2bf866dca7fc6b5b2f73d08e8aaee685104a0e35a2b7e4f011a8008";

// Permissions value for the FullSend relayer.
// Only CALL + EXECUTE_RELAY_CALL are ever needed. TRANSFERVALUE is deliberately
// NOT granted — the relayer must never be able to move LYX from any UP.
const PERMS_FOLLOW_VOTE = "0x0000000000000000000000000000000000000000000000000000000000100800"; // CALL + EXECUTE_RELAY_CALL

// AllowedCalls per feature — tightened to specific function selectors
// All use callType 0x00000003 (CALL+TRANSFERVALUE) — KM requires TRANSFERVALUE bit for all
// Follow: follow(address) + unfollow(address) on LSP26
const CALLS_FOLLOW = "002000000003f01103e5a9909fc0dbe8166da7085e0285daddcaffffffff4dbf27cc002000000003f01103e5a9909fc0dbe8166da7085e0285daddcaffffffff015a4ead";
// Polls: voteViaRelayer + createPoll + endPoll on PollVoting
const CALLS_POLLS = "002000000003201046f412aaa13f89994fa77bcea726936ae5c9ffffffff0480912a002000000003201046f412aaa13f89994fa77bcea726936ae5c9ffffffff9de156ad002000000003201046f412aaa13f89994fa77bcea726936ae5c9ffffffff14d2bf63";

// Build AllowedCalls value based on selected toggles
function buildAllowedCalls(enableFollow, enablePolls) {
  let calls = "";
  if (enableFollow) calls += CALLS_FOLLOW;
  if (enablePolls) calls += CALLS_POLLS;
  return "0x" + calls;
}

// Build permissions value. The relayer always gets the same permission set
// (CALL + EXECUTE_RELAY_CALL, no TRANSFERVALUE) — what limits its scope is
// the AllowedCalls list above. By contract, it cannot move LYX from the UP.
function buildPermissions(_enableFollow, _enablePolls) {
  return PERMS_FOLLOW_VOTE;
}

// Check if the FullSend relayer already has permissions on the user's UP
async function checkFullSendPermissions(provider, upAddress) {
  try {
    const result = await provider.request({
      method: "eth_call",
      params: [{ to: upAddress, data: "0x54f6127f" + RELAYER_PERMISSION_KEY.slice(2) }, "latest"],
    });
    // Check if EXECUTE_RELAY_CALL bit (0x100000) is set in the permissions
    if (result && result.length >= 66) {
      const perms = BigInt(result);
      const EXECUTE_RELAY_CALL_BIT = BigInt("0x100000");
      const CALL_BIT = BigInt("0x800");
      // Must have both CALL and EXECUTE_RELAY_CALL
      return (perms & EXECUTE_RELAY_CALL_BIT) !== 0n && (perms & CALL_BIT) !== 0n;
    }
    return false;
  } catch {
    return false;
  }
}

// Grant FullSend relayer permissions on the user's UP
// Single setDataBatch transaction — one confirmation popup
// Toggles control which actions are granted. Tips never need permissions
// (LYX flow is always user-authorized via wallet popup).
async function grantFullSendPermissions(provider, upAddress, enableFollow = true, enablePolls = true) {
  const permVal = buildPermissions(enableFollow, enablePolls);
  const callsVal = buildAllowedCalls(enableFollow, enablePolls);

  if (callsVal === "0x") {
    throw new Error("No permissions selected");
  }

  const permKey = RELAYER_PERMISSION_KEY.slice(2);
  const callsKey = RELAYER_ALLOWED_CALLS_KEY.slice(2);
  const permValHex = permVal.slice(2);
  const callsValHex = callsVal.slice(2);

  // setDataBatch(bytes32[], bytes[]) with 2 keys — permissions + AllowedCalls
  const payload = "0x97902421"
    + "0000000000000000000000000000000000000000000000000000000000000040"
    + "00000000000000000000000000000000000000000000000000000000000000a0"
    + "0000000000000000000000000000000000000000000000000000000000000002"
    + permKey
    + callsKey
    + "0000000000000000000000000000000000000000000000000000000000000002"
    // offset to value[0] = 0x40
    + "0000000000000000000000000000000000000000000000000000000000000040"
    // offset to value[1] = 0x40 + 0x20 (len) + 0x20 (data) = 0x80
    + "0000000000000000000000000000000000000000000000000000000000000080"
    // value[0]: permissions (32 bytes)
    + "0000000000000000000000000000000000000000000000000000000000000020"
    + permValHex
    // value[1]: AllowedCalls (variable length)
    + (callsValHex.length / 2).toString(16).padStart(64, "0")
    + callsValHex + "0".repeat((64 - (callsValHex.length % 64)) % 64);

  console.log("Grant permissions payload length:", payload.length);

  const txHash = await provider.request({
    method: "eth_sendTransaction",
    params: [{ from: upAddress, to: upAddress, data: payload }],
  });

  console.log("Permissions granted, tx:", txHash);
  return txHash;
}


// Encode the UP's execute(operationType, to, value, data) call
// This wraps the TipRouter.tip() call so it comes FROM the UP
function encodeUPExecute(toAddress, valueWei, innerCalldata) {
  // execute(uint256,address,uint256,bytes) selector
  const selector = "0x44c028fe";
  const operation = BigInt(OPERATION_CALL).toString(16).padStart(64, "0");
  const to = toAddress.slice(2).toLowerCase().padStart(64, "0");
  const value = BigInt(valueWei).toString(16).padStart(64, "0");
  // bytes offset (4 * 32 = 128 = 0x80)
  const offset = "0000000000000000000000000000000000000000000000000000000000000080";
  // bytes length
  const dataHex = innerCalldata.startsWith("0x") ? innerCalldata.slice(2) : innerCalldata;
  const dataLength = (dataHex.length / 2).toString(16).padStart(64, "0");
  // pad data to 32-byte boundary
  const paddedData = dataHex + "0".repeat((64 - (dataHex.length % 64)) % 64);
  return selector + operation + to + value + offset + dataLength + paddedData;
}

// Send a value/calldata to a target through the user's UP, choosing the right
// shape for the wallet:
//  - Mobile UP app (walletconnect) and Grid host (upprovider) auto-wrap
//    `from: UP` txs into UP.execute, so we send a DIRECT call and let them wrap.
//  - Desktop UP Browser Extension expects a MANUAL UP.execute wrap.
// Using the wrong shape double-wraps and reverts in the relay's gas estimation.
// Returns the tx hash.
// True when the wallet auto-wraps `from: UP` transactions into UP.execute on its
// own, so we must send a DIRECT call (a manual UP.execute on top would double-wrap
// and revert / drop the value). Covers: the UP mobile app over WalletConnect, the
// Grid host (upprovider), AND the UP mobile app's IN-APP BROWSER — which injects
// window.lukso exactly like the desktop extension (providerType "up") but, unlike
// desktop, auto-wraps. We distinguish the in-app browser from the desktop
// extension by mobile user-agent.
function isAutoWrapProvider(pt) {
  if (pt === "walletconnect" || pt === "upprovider") return true;
  if (pt === "up" && isMobileDevice()) return true; // UP mobile app in-app browser
  return false;
}

async function sendUPCall(provider, profile, target, valueWei, innerCalldata) {
  const pt = profile && profile.providerType;
  const valueHex = "0x" + BigInt(valueWei || 0).toString(16);
  if (isAutoWrapProvider(pt)) {
    return provider.request({
      method: "eth_sendTransaction",
      params: [{ from: profile.address, to: target, value: valueHex, data: innerCalldata }],
    });
  }
  return provider.request({
    method: "eth_sendTransaction",
    params: [{
      from: profile.address,
      to: profile.address,
      data: encodeUPExecute(target, valueWei || "0", innerCalldata),
      value: "0x0",
    }],
  });
}

// --- MOCK DATA -----------------------------------------------
// Stock avatar placeholder SVG (user silhouette)
const STOCK_AVATAR = "data:image/svg+xml," + encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 80 80"><rect width="80" height="80" rx="12" fill="#0f0f18"/><circle cx="40" cy="30" r="14" fill="#00ff8840"/><ellipse cx="40" cy="68" rx="24" ry="20" fill="#00ff8825"/></svg>`);
const DEMO_PFP_SUPPORTER = "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAgGBgcGBQgHBwcJCQgKDBQNDAsLDBkSEw8UHRofHh0aHBwgJC4nICIsIxwcKDcpLDAxNDQ0Hyc5PTgyPC4zNDL/2wBDAQkJCQwLDBgNDRgyIRwhMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjL/wAARCABQAFADASIAAhEBAxEB/8QAHwAAAQUBAQEBAQEAAAAAAAAAAAECAwQFBgcICQoL/8QAtRAAAgEDAwIEAwUFBAQAAAF9AQIDAAQRBRIhMUEGE1FhByJxFDKBkaEII0KxwRVS0fAkM2JyggkKFhcYGRolJicoKSo0NTY3ODk6Q0RFRkdISUpTVFVWV1hZWmNkZWZnaGlqc3R1dnd4eXqDhIWGh4iJipKTlJWWl5iZmqKjpKWmp6ipqrKztLW2t7i5usLDxMXGx8jJytLT1NXW19jZ2uHi4+Tl5ufo6erx8vP09fb3+Pn6/8QAHwEAAwEBAQEBAQEBAQAAAAAAAAECAwQFBgcICQoL/8QAtREAAgECBAQDBAcFBAQAAQJ3AAECAxEEBSExBhJBUQdhcRMiMoEIFEKRobHBCSMzUvAVYnLRChYkNOEl8RcYGRomJygpKjU2Nzg5OkNERUZHSElKU1RVVldYWVpjZGVmZ2hpanN0dXZ3eHl6goOEhYaHiImKkpOUlZaXmJmaoqOkpaanqKmqsrO0tba3uLm6wsPExcbHyMnK0tPU1dbX2Nna4uPk5ebn6Onq8vP09fb3+Pn6/9oADAMBAAIRAxEAPwDtsUYpaK9nlPghMUYrM1u/ewSy8tgrTXIiyehO1iAfYkAVasb6K+hLICkicSxMfmjb0Pt6HoRWKqwdV0b+9a/yNnQmqSq2929iwcKCSQAOpJpcVzPja9exsbFiga1kudk5Y4Ayp259t3P4CtHRdatNStlQXMLXMYCSIJASTj7w9QRz+napVVe2dFrWyfr/AMNp95bw0/YKstVdr0NXFGKWiujlOYTFGKWilygNzRmod9G+urkKsZ/iTSW1vRZbSKURXCsssEh6LIpyP8Pxrihca14cubKHWpFSSchYb6OQNjkFo3JwCB7jHORXdamLuTTZ10+UR3m3dCxAI3DkA57HGD9a898SamnizSIkVZob20LedapCZBvPGDgZHQjnocdeteTmeHpNc7uprZo9jK3Vk/ZJXg3qt+hg6tfz32pyyz3pvMSlVlHCld2BtHYdelVpQAm4naw5DZwR+NdTo3g/T0htLu9Et2ssW4DGYweuNo5/Otq0s9HL7rGzgimxuQtAVP4bh/Kvm6mawjsnK3U+mjBRVkrIq+A/El/cXv8AZV+0jxtGzW8swIJ24yAT14Occ4x6V6DmuSubqBbeRbyUxIjeYjMwDo4HGw/3hnjHXODkZrpIHk+zx+fjzdg344G7HP617+SY54ynJSjZx+7U+YzjCxpVVOO0uhZzRmod9G+vb5DyLFbzPejzPeqvmUeZXZyGvKWvMrjPG+iQyWY1GxhMF6jszzW5EbPlf4m78gfj9a6nzKp6rcRpp0qSBT52IFViACznaBz7mubF0YyoS5nbR69jpwkpwrRcN7mR4f1GS4tLayZw9z9mWYsy4Ur0yAO/t/iKtSSSlpDJFI0Cgs1ytrI8ahThiGAxweCcnFcfpkV9K9rcWlykVzbJsjEjgfL1wc8c5IIOMFfpXR6X4gntYpLG4tZYh5DwHEjFFDc8MM4HU4wep5r87jgqPO3U09D7lRagvduzaR7PT41ujtdWOGmdQ4wRzuPp29K2bqH7Bp0V3HuNuQMx8t5YPTDf3fr7c1kNPFfiS6inu4zNJGscqRLMjIAAWJQ4J6g5XPQ1f/tq5urK5sJLeMbWMBnUlQyjHIQ8g9uuP5V24GjiaddLCSv3s1t5pnNmLw86N8RGyW3r5Mm8yjzKq+ZR5lff8h8NylPzKXzKqeZUN3dfZrOe4xkxRs+PXAzXW4pK7N1C7sM1HxFZ6c5idnlnAyYohkj0yTwPxritQ1C/17UdrJuCcpbDkIO5Hqcd/wClGnWcl9cYkdgOZbibGSO7N7nNdxY6dZxKjCFPKDKY9yfMGGPmJ7nP5V+e5nn06qcbWj2/z/r9D6LC4GFLVas5yyZ9AuoBceYjlYsxM4O8gFGVfVSSCDxjOOccdlDexSCORbWbBBKN5YP4ZBOD9cVRtNPtNU03N1CGkMjlpMYbJPPPcdsHI9qbo+lSJrR0htTnhtAvmxFc7myGyWbkkgj2GR74rwqk1KN57o+kpqUYrqYum3t9oet38MD3CW05aSUQsjY+b746gHJxu45yM8Cuih1C2DJBloXI+WOVShPfjP3uvYnrUtvosfh/Umt/OlmaRSI5pHB4XBwuOApUggdtrDtVK3iddcvbaeIzWG8SCJmHljeMnKnryDg9j+nt5XnMcM3dLla36v5nlY3LIYlc7m1+RpeZR5lUISYRJA0hk8mV4g56sFOAT74xUnmV+g0nGrTjUjs0n958hOnyycX0K2+qWrzrHpF2W/iiKAepPAH5kVPurD8Ryti0iH3SzOfcgAD/ANCp5hVWHws6nZf8A6aFPnqRia3hOBYtKuLgkbnkIJ64Cj/65qwdZsre+gt5ZmEk6DbExGBk8EnoMgZ55+tYXhS9n/tK5t2LeQICwQn5d2Rz9cDFQWqSXfjKUApCBKqAZIX5UBJJ9/xr8j+pupWmm+lz6mmnKyidVpovY7m+WFYHVZ2G1mZcZ5HY+p9P0qXUI9YW5tr6wS18+3Y4USEMVOMjJGOoB7dPeq9tqDxXF1NDZXM/murERqCFBGMkjJB+U8Y7iro1WZSDLpl2EPdEZyPwwKnllpdf1Y9Om1yJMbK+tatdR3VzDaWTIVxj524yeQDjPzMOvQmq1xc/2bqTC4kkne4EKK20KBy+eB0AAJ7/AFrTF48qhrW2aZT/ABO3lD/x4ZP5Vz3iG7uIZ4bmSOKI2k9s52Sb2w0hz2HbP51VGlGpL2c9I/1+oq0Y8urLkpaHU7uJiCJG+0RH1RuD+IYH8xTt9VbuQ/bLMeWsa/vdiquMIQhxjJ44B/Gn7q/TsgqutgIc2ttPu0X4HxePpKniJJH/2Q==";
const DEMO_PFP_CHAMPION = "data:image/jpeg;base64,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";
const DEMO_PFP_LEGEND = "data:image/jpeg;base64,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";
const DEMO_PFP_WHALE = "data:image/jpeg;base64,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";
const DEMO_QR_CODE = "data:image/svg+xml;base64,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";

// Helper: check if avatar is a renderable image (URL or data URI)
const isImageAvatar = (avatar) => avatar && (avatar.startsWith("http") || avatar.startsWith("data:image"));

// --- Token metadata resolver (for poll token-gating) ---
// Reads a token's LSP4 name, type (LSP7 vs LSP8), the owner's balance, and a
// best-effort icon image via the mainnet RPC. Used when a creator pastes a
// collection address — the wallet provider's getData returns nothing on mainnet,
// so we read directly the same way the profile fetcher does.
const _tokenMetaCache = {};
async function fetchTokenMeta(address, ownerAddress) {
  const out = { name: "", image: "", standard: "LSP8", decimals: 0, held: null };
  if (!address || address.length < 42) return out;
  const ck = address.toLowerCase() + "|" + (ownerAddress || "").toLowerCase();
  if (_tokenMetaCache[ck]) return _tokenMetaCache[ck];
  const rpc = LUKSO_TESTNET.rpcUrls[0]; // NB: misnamed const — points at mainnet
  const call = async (to, data) => {
    try {
      const resp = await fetch(rpc, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ jsonrpc: "2.0", id: 1, method: "eth_call", params: [{ to, data }, "latest"] }),
      });
      const j = await resp.json();
      return (j && j.result) || null;
    } catch { return null; }
  };
  const decodeBytes = (hex) => {
    if (!hex || hex === "0x") return "";
    const h = hex.slice(2);
    if (h.length < 128) return h;
    const off = parseInt(h.slice(0, 64), 16) * 2;
    if (!Number.isFinite(off) || off + 64 > h.length) return "";
    const blen = parseInt(h.slice(off, off + 64), 16);
    if (blen > 0 && blen <= 8192 && off + 64 + blen * 2 <= h.length) return h.slice(off + 64, off + 64 + blen * 2);
    return "";
  };
  const hexToStr = (h) => h ? h.match(/.{2}/g).map(x => String.fromCharCode(parseInt(x, 16))).join("").replace(/[^\x20-\x7E]/g, "").trim() : "";
  // Name — LSP4TokenName
  try { out.name = hexToStr(decodeBytes(await call(address, "0x54f6127fdeba1e292f8ba88238e10ab3c7f88bd4be4fac56cad5194b6eccefc1c8558531"))); } catch {}
  // Symbol — LSP4TokenSymbol (many tokens set only this, e.g. "LUDES")
  try { out.symbol = hexToStr(decodeBytes(await call(address, "0x54f6127f2f0a68ab07768e01943a599e73362a0e17a63a72e94dd2e384d2c1d4db932756"))); } catch {}
  if (!out.name && out.symbol) out.name = out.symbol;
  // Type — LSP7 exposes decimals(); LSP8 does not
  try {
    const dec = await call(address, "0x313ce567");
    if (dec && dec !== "0x" && dec.length >= 4) {
      const d = parseInt(dec, 16);
      if (d >= 0 && d <= 36) { out.standard = "LSP7"; out.decimals = d; }
    }
  } catch {}
  // Owner balance — both LSP7 and LSP8 expose balanceOf(address) at 0x70a08231
  if (ownerAddress) {
    try {
      const bal = await call(address, "0x70a08231" + ownerAddress.slice(2).toLowerCase().padStart(64, "0"));
      if (bal && bal !== "0x") {
        const raw = BigInt(bal);
        out.held = out.standard === "LSP7" ? Number(raw / (10n ** BigInt(out.decimals || 0))) : Number(raw);
      }
    } catch {}
  }
  // Total supply — totalSupply() at 0x18160ddd
  try {
    const ts = await call(address, "0x18160ddd");
    if (ts && ts !== "0x") {
      const raw = BigInt(ts);
      out.supply = out.standard === "LSP7" ? Number(raw / (10n ** BigInt(out.decimals || 0))) : Number(raw);
    }
  } catch {}
  // Image — LSP4Metadata VerifiableURI → IPFS JSON → icon (best effort)
  try {
    const mh = decodeBytes(await call(address, "0x54f6127f9afb95cacc9f95858ec44aa8c3b685511002e30ae54415823f406128b85b238e"));
    if (mh) {
      const ascii = mh.match(/.{2}/g).map(x => String.fromCharCode(parseInt(x, 16))).join("");
      const m = ascii.match(/ipfs:\/\/[A-Za-z0-9]+|https?:\/\/[^\s"']+/);
      if (m) {
        let url = m[0];
        if (url.startsWith("ipfs://")) url = "https://api.universalprofile.cloud/ipfs/" + url.slice(7);
        const meta = await fetch(url).then(r => r.json()).catch(() => null);
        // Prefer the rich metadata name ("Lab Credits") over LSP4TokenName ("LABC")
        const metaName = (meta?.LSP4Metadata?.name || meta?.name || "").trim();
        if (metaName) out.name = metaName;
        let icon = meta?.LSP4Metadata?.icon?.[0]?.url || meta?.LSP4Metadata?.images?.[0]?.[0]?.url || "";
        if (icon && icon.startsWith("ipfs://")) icon = "https://api.universalprofile.cloud/ipfs/" + icon.slice(7);
        if (icon) out.image = icon;
      }
    }
  } catch {}
  _tokenMetaCache[ck] = out;
  return out;
}

// Display a token as "Name $SYMBOL"; avoids doubling when name == symbol.
function fmtToken(name, symbol) {
  const n = (name || "").trim(), s = (symbol || "").trim();
  if (n && s && n.toLowerCase() !== s.toLowerCase()) return `${n} $${s}`;
  if (s) return `$${s}`;
  return n || "Unnamed token";
}

// --- LSP3 Profile Cache & Fetcher ---
const _profileCache = {};
async function fetchUPProfile(address) {
  if (!address || address.length < 10) return null;
  const key = address.toLowerCase();
  if (_profileCache[key]) return _profileCache[key];
  try {
    const rpc = LUKSO_TESTNET.rpcUrls[0];
    const LSP3_KEY = "5ef83ad9559033e6e941db7d7c495acdce616347d28e90c7ce47cbfcfcad3bc5";
    const resp = await fetch(rpc, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        jsonrpc: "2.0", id: 1, method: "eth_call",
        params: [{ to: address, data: "0x54f6127f" + LSP3_KEY }, "latest"],
      }),
    });
    const json = await resp.json();
    const result = json.result;
    if (result && result !== "0x" && result.length > 130) {
      const data = result.slice(2);
      const offset = parseInt(data.slice(0, 64), 16) * 2;
      const length = parseInt(data.slice(offset, offset + 64), 16);
      const contentHex = data.slice(offset + 64, offset + 64 + length * 2);
      let raw = "";
      for (let i = 0; i < contentHex.length; i += 2) {
        raw += String.fromCharCode(parseInt(contentHex.slice(i, i + 2), 16));
      }
      const ipfsIdx = raw.indexOf("ipfs://");
      const httpsIdx = raw.indexOf("https://");
      const urlStart = ipfsIdx > -1 ? ipfsIdx : httpsIdx;
      if (urlStart > -1) {
        let url = raw.slice(urlStart);
        if (url.startsWith("ipfs://")) url = "https://api.universalprofile.cloud/ipfs/" + url.slice(7);
        const metaResp = await fetch(url);
        const meta = await metaResp.json();
        const p = meta?.LSP3Profile || meta;
        let avatar = STOCK_AVATAR;
        const img = p.profileImage?.[0]?.url || p.profileImage?.[0]?.src;
        if (img) {
          avatar = img.startsWith("ipfs://")
            ? "https://api.universalprofile.cloud/ipfs/" + img.slice(7)
            : img;
        }
        const profile = {
          name: p.name || `${address.slice(0, 6)}...${address.slice(-4)}`,
          avatar,
          address,
        };
        _profileCache[key] = profile;
        return profile;
      }
    }
  } catch (e) { /* silent */ }
  // Fallback: no UP metadata found (might be an EOA)
  const fallback = { name: `${address.slice(0, 6)}...${address.slice(-4)}`, avatar: STOCK_AVATAR, address };
  _profileCache[key] = fallback;
  return fallback;
}

// React hook: resolve UP profiles for a list of addresses
function useUPProfiles(addresses) {
  const [profiles, setProfiles] = useState({});
  useEffect(() => {
    if (!addresses || addresses.length === 0) return;
    const toFetch = addresses.filter(a => a && !profiles[a.toLowerCase()]);
    if (toFetch.length === 0) return;
    Promise.all(toFetch.map(a => fetchUPProfile(a))).then(results => {
      const newProfiles = { ...profiles };
      results.forEach(p => { if (p) newProfiles[p.address.toLowerCase()] = p; });
      setProfiles(newProfiles);
    });
  }, [addresses.join(",")]);
  return profiles;
}

const MOCK_PROFILES = [
  { address: "0x1234...abcd", name: "Dracos", avatar: DEMO_PFP_SUPPORTER },
  { address: "0x5678...efgh", name: "MKTiaraGirl", avatar: DEMO_PFP_CHAMPION },
  { address: "0x9abc...ijkl", name: "Arion", avatar: DEMO_PFP_LEGEND },
  { address: "0xdef0...mnop", name: "Thalos", avatar: DEMO_PFP_WHALE },
  { address: "0x1357...qrst", name: "ChainWizard", avatar: DEMO_PFP_SUPPORTER },
  { address: "0x2468...uvwx", name: "AltsAnon", avatar: DEMO_PFP_CHAMPION },
  { address: "0xaabb...ccdd", name: "PoolNoodle", avatar: DEMO_PFP_LEGEND },
  { address: "0xeeff...0011", name: "GrillMaster", avatar: DEMO_PFP_WHALE },
];

// --- MOBILE DETECTION ----------------------------------------
function isMobileDevice() {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || (window.innerWidth <= 768);
}

// Detects in-app browser webviews (X/Twitter, Meta, TikTok, etc.) where the
// WalletConnect app-to-app handoff is sandboxed and stalls. Used to nudge the
// user to open the page in their real browser.
function isInAppBrowser() {
  const ua = navigator.userAgent || "";
  if (/FBAN|FBAV|FB_IAB|Instagram|Twitter|\bLine\/|MicroMessenger|Snapchat|Pinterest|TikTok|musical_ly|LinkedInApp/i.test(ua)) return true;
  // iOS webview heuristic: iOS but no real-browser token (Safari/CriOS/FxiOS)
  const isIOS = /iPhone|iPad|iPod/i.test(ua);
  if (isIOS && !/Safari/i.test(ua) && !/CriOS/i.test(ua) && !/FxiOS/i.test(ua)) return true;
  return false;
}

// Reads an uploaded icon image and returns a small data URL via onResult.
// SVGs are kept as-is (crisp vectors, capped at 60KB); raster images (PNG/JPG)
// are cover-fit resized to a 64x64 PNG to keep the stored base64 small.
function readIconFile(file, onResult) {
  if (!file) return;
  const isSvg = file.type === "image/svg+xml" || /\.svg$/i.test(file.name);
  if (isSvg) {
    if (file.size > 60000) { alert("That SVG is a bit large — keep it under ~60KB (try optimizing/simplifying it)."); return; }
    const r = new FileReader();
    r.onload = () => { try { onResult(r.result); } catch {} };
    r.readAsDataURL(file);
    return;
  }
  const reader = new FileReader();
  reader.onload = () => {
    const img = new Image();
    img.onload = () => {
      const size = 64;
      const c = document.createElement("canvas");
      c.width = size; c.height = size;
      const ctx = c.getContext("2d");
      const scale = Math.max(size / img.width, size / img.height);
      const w = img.width * scale, h = img.height * scale;
      ctx.drawImage(img, (size - w) / 2, (size - h) / 2, w, h);
      try { onResult(c.toDataURL("image/png")); } catch {}
    };
    img.src = reader.result;
  };
  reader.readAsDataURL(file);
}

// ---- Moments on Chain (MOC) — Forever Moments read API + FullSend MOC endpoint ----
const FM_API = "https://www.forevermoments.life/api/public/v1";
const IPFS_GW = "https://api.universalprofile.cloud/ipfs/";

function fmResolveIpfs(url) {
  if (!url || typeof url !== "string") return "";
  if (url.startsWith("ipfs://")) return IPFS_GW + url.slice(7);
  return url;
}

// Defensive image extraction. Forever Moments v2 returns an already-resolved
// gateway URL at media.primaryUrl; older/LSP4 shapes are tried as fallbacks.
function fmMomentImage(m) {
  if (!m) return "";
  if (m.media?.primaryUrl) return fmResolveIpfs(m.media.primaryUrl);
  if (m.media?.mediaItems?.[0]?.url) return fmResolveIpfs(m.media.mediaItems[0].url);
  const md = m.metadata || m.lsp4Metadata?.LSP4Metadata || m.lsp4Metadata || m.LSP4Metadata || {};
  const cands = [
    m.image, m.imageUrl, m.thumbnail, m.icon,
    md.image,
    md.images?.[0]?.[0]?.url, md.images?.[0]?.url,
    md.icon?.[0]?.url,
    md.assets?.[0]?.url,
  ];
  for (const c of cands) { if (c && typeof c === "string") return fmResolveIpfs(c); }
  return "";
}

function fmMomentIsVideo(m) {
  return m?.media?.primaryType === "video" || (m?.media?.videos && m.media.videos.length > 0);
}

function fmMomentTitle(m) {
  const md = m?.metadata || m?.lsp4Metadata?.LSP4Metadata || m?.lsp4Metadata || {};
  return m?.title || m?.name || md.name || "Moment";
}

function fmMomentId(m) {
  return String(m?.id ?? m?.momentId ?? m?.tokenId ?? m?.address ?? "");
}

async function fmFetchMoments(address, { limit = 50, timeout = 9000 } = {}) {
  const ctrl = new AbortController();
  const to = setTimeout(() => ctrl.abort(), timeout);
  try {
    const r = await fetch(`${FM_API}/profiles/${address}/moments?limit=${limit}`, { signal: ctrl.signal, headers: { Accept: "application/json" } });
    if (!r.ok) throw new Error("fm moments " + r.status);
    const j = await r.json();
    const data = j.data ?? j;
    return Array.isArray(data) ? data : (data.moments || data.items || []);
  } finally { clearTimeout(to); }
}

async function fmFetchMoment(momentId, { timeout = 9000 } = {}) {
  const ctrl = new AbortController();
  const to = setTimeout(() => ctrl.abort(), timeout);
  try {
    const r = await fetch(`${FM_API}/moments/${momentId}`, { signal: ctrl.signal, headers: { Accept: "application/json" } });
    if (!r.ok) throw new Error("fm moment " + r.status);
    const j = await r.json();
    return j.data?.moment ?? j.data ?? j.moment ?? j;
  } finally { clearTimeout(to); }
}

async function fmFetchProfiles(addresses) {
  const list = (addresses || []).filter(Boolean).join(",");
  if (!list) return {};
  const r = await fetch(`${FM_API}/profiles?addresses=${list}`, { headers: { Accept: "application/json" } });
  if (!r.ok) throw new Error("fm profiles " + r.status);
  const j = await r.json();
  return j.data ?? j.profiles ?? j ?? {};
}

// Quick health probe for the offline indicator
async function fmHealthCheck() {
  try {
    const ctrl = new AbortController();
    const to = setTimeout(() => ctrl.abort(), 6000);
    const r = await fetch(`${FM_API}/likes/stats`, { signal: ctrl.signal });
    clearTimeout(to);
    return r.ok;
  } catch { return false; }
}

const MOCAPI = {
  async getActive(creator) {
    try { const r = await fetch(`/api/moc?creator=${creator}&active=1`); const j = await r.json(); return j.moc || null; } catch { return null; }
  },
  async launch(creator, cfg) {
    const r = await fetch("/api/moc", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "launch", creator, ...cfg }) });
    return r.json();
  },
  async end(creator, mocId) {
    const r = await fetch("/api/moc", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "end", creator, mocId }) });
    return r.json();
  },
  async queue(creator, mocId) {
    try { const r = await fetch(`/api/moc?creator=${creator}${mocId ? `&mocId=${mocId}` : ""}`); const j = await r.json(); return j.submissions || []; } catch { return []; }
  },
  async submit(payload) {
    const r = await fetch("/api/moc", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "submit", ...payload }) });
    return r.json();
  },
  async setShow(id, showOnStream) {
    const r = await fetch("/api/moc", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "setShow", id, showOnStream }) });
    return r.json();
  },
  async reject(id) {
    const r = await fetch("/api/moc", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "reject", id }) });
    return r.json();
  },
  async archive(id) {
    const r = await fetch("/api/moc", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "archive", id }) });
    return r.json();
  },
  async unarchive(id) {
    const r = await fetch("/api/moc", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "unarchive", id }) });
    return r.json();
  },
};

// --- TOP TIPPERS LEADERBOARD WIDGET (stable component to prevent remount flicker) ---
// --- UP REQUIRED MODAL — gate creator registration to Universal Profiles ---
function UpRequiredModal({ onClose, action = "register as a creator" }) {
  const isMobile = isMobileDevice();
  const upLink = isMobile ? getUPMobileLink() : "https://my.universalprofile.cloud/";
  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, zIndex: 10000,
      background: "rgba(0,0,0,0.7)", backdropFilter: "blur(6px)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 20,
      animation: "fadeIn 0.2s ease-out",
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        maxWidth: 420, width: "100%",
        background: `linear-gradient(135deg, ${COLORS.surface}, ${COLORS.cyan}08)`,
        border: `1px solid ${COLORS.cyan}30`,
        borderRadius: 16,
        padding: "28px 24px",
        position: "relative",
        boxShadow: `0 20px 60px rgba(0,0,0,0.5), 0 0 40px ${COLORS.cyan}15`,
        animation: "slideDown 0.3s cubic-bezier(0.16,1,0.3,1)",
      }}>
        <button onClick={onClose} style={{
          position: "absolute", top: 12, right: 12,
          width: 28, height: 28, borderRadius: 6,
          background: COLORS.bg, border: `1px solid ${COLORS.border}`,
          color: COLORS.textMuted, fontSize: 14,
          cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>✕</button>

        <div style={{ fontSize: 36, marginBottom: 10 }}>🆔</div>
        <div style={{
          fontSize: 10, fontWeight: 800, color: COLORS.cyan,
          fontFamily: "'JetBrains Mono', monospace",
          letterSpacing: "2px", textTransform: "uppercase",
          marginBottom: 6,
        }}>Universal Profile Required</div>
        <div style={{
          fontFamily: "'Syne', sans-serif",
          fontSize: 20, fontWeight: 700,
          color: COLORS.text,
          marginBottom: 10,
          lineHeight: 1.3,
        }}>To {action}, you'll need a Universal Profile</div>
        <div style={{ fontSize: 12, color: COLORS.textSecondary, lineHeight: 1.6, marginBottom: 18 }}>
          You can browse and customize everything with your current wallet, but FullSend creator pages are built on LUKSO's Universal Profile standard — it's what powers your on-chain name, avatar, followers, and tip history.
        </div>

        <div style={{
          padding: "12px 14px", borderRadius: 10,
          background: `${COLORS.bg}80`,
          border: `1px solid ${COLORS.border}`,
          marginBottom: 18,
        }}>
          <div style={{
            fontSize: 9, fontWeight: 800, color: COLORS.accent,
            fontFamily: "'JetBrains Mono', monospace",
            letterSpacing: "1.2px", textTransform: "uppercase",
            marginBottom: 6,
          }}>What you unlock</div>
          <ul style={{ margin: 0, padding: "0 0 0 16px", fontSize: 11, color: COLORS.textSecondary, lineHeight: 1.7 }}>
            <li>Your creator tip page at <code style={{ fontSize: 10, color: COLORS.accent, fontFamily: "'JetBrains Mono', monospace" }}>fullsend.live/tip/your-UP</code></li>
            <li>On-chain PFP, name, bio, and social links</li>
            <li>Receive tips, build a follower graph, run polls</li>
            <li>Gasless UX for your own actions</li>
          </ul>
        </div>

        <a href={upLink} target="_blank" rel="noopener noreferrer" style={{
          display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          padding: "13px", borderRadius: 10,
          background: `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.cyan})`,
          color: COLORS.bg,
          fontSize: 14, fontWeight: 700,
          fontFamily: "'Syne', sans-serif",
          textDecoration: "none",
          marginBottom: 10,
          transition: "transform 0.2s",
        }}
        onMouseEnter={e => e.currentTarget.style.transform = "translateY(-1px)"}
        onMouseLeave={e => e.currentTarget.style.transform = "translateY(0)"}>
          {isMobile ? "Get the UP App" : "Create Your Universal Profile"} →
        </a>
        <div onClick={onClose} style={{
          textAlign: "center", fontSize: 10, color: COLORS.textMuted,
          fontFamily: "'JetBrains Mono', monospace",
          letterSpacing: "0.5px", textTransform: "uppercase",
          cursor: "pointer",
          padding: "6px",
        }}>Keep Exploring</div>
      </div>
    </div>
  );
}


function TopTippersWidget({ creatorAddress, WC }) {
  const [period, setPeriod] = useState("today"); // today | week | month | ath
  const [allTimeList, setAllTimeList] = useState([]); // from leaderboard endpoint
  const [activity, setActivity] = useState([]);       // raw tips for period aggregation
  const [profiles, setProfiles] = useState({});
  const [hasLoaded, setHasLoaded] = useState(false);
  const profilesRef = useRef({});

  useEffect(() => {
    if (!creatorAddress) return;
    let cancelled = false;
    const load = async () => {
      try {
        const [lbRes, actRes] = await Promise.all([
          fetch(`/api/data-tips?action=leaderboard&creator=${creatorAddress}&limit=5`),
          fetch(`/api/data-tips?action=activity&creator=${creatorAddress}&limit=500`),
        ]);
        const [lbData, actData] = await Promise.all([lbRes.json(), actRes.json()]);
        if (cancelled) return;
        const lb = lbData.leaderboard || [];
        const act = actData.activity || [];
        setAllTimeList(lb);
        setActivity(act);

        // Resolve UP profiles for every tipper we might display
        const allTippers = new Set([
          ...lb.map(t => t.tipper).filter(Boolean),
          ...act.map(t => t.tipper).filter(Boolean),
        ]);
        const newProfiles = { ...profilesRef.current };
        await Promise.all([...allTippers].map(async addr => {
          const key = addr.toLowerCase();
          if (!newProfiles[key]) {
            try {
              const p = await fetchUPProfile(addr);
              if (p) newProfiles[key] = p;
            } catch {}
          }
        }));
        if (cancelled) return;
        profilesRef.current = newProfiles;
        setProfiles(newProfiles);
        setHasLoaded(true);
      } catch { if (!cancelled) setHasLoaded(true); }
    };
    load();
    const id = setInterval(load, 30000);
    const onTip = () => load();
    window.addEventListener("fullsend-tip", onTip);
    return () => { cancelled = true; clearInterval(id); window.removeEventListener("fullsend-tip", onTip); };
  }, [creatorAddress]);

  // Compute the currently-displayed list based on selected period.
  // ATH uses the server-aggregated leaderboard; others aggregate raw activity client-side.
  const tippers = useMemo(() => {
    if (period === "ath") {
      return allTimeList.slice(0, 5);
    }
    const nowSec = Math.floor(Date.now() / 1000);
    const windowSec = period === "today" ? 86400 : period === "week" ? 7 * 86400 : 30 * 86400;
    const cutoff = nowSec - windowSec;
    const agg = {};
    for (const t of activity) {
      // FullSend-support tips go to this same UP but are tracked separately on the
      // FullSend Supporters board — keep them out of the creator's tipper leaderboard.
      if ((t.message_hash || t.message || "").startsWith("Tip to FullSend")) continue;
      const ts = parseInt(t.timestamp || 0);
      if (ts < cutoff) continue;
      const key = (t.tipper || "").toLowerCase();
      if (!key) continue;
      if (!agg[key]) agg[key] = { tipper: t.tipper, total_amount: 0, tip_count: 0 };
      agg[key].total_amount += parseFloat(t.amount || 0);
      agg[key].tip_count += 1;
    }
    return Object.values(agg).sort((a, b) => b.total_amount - a.total_amount).slice(0, 5);
  }, [period, allTimeList, activity]);

  // Hide the widget entirely if there are no tips at all
  if (!hasLoaded) return null;
  if (allTimeList.length === 0 && activity.length === 0) return null;

  const medals = ["🥇", "🥈", "🥉"];
  const periods = [
    { key: "today", label: "Day" },
    { key: "week",  label: "Week" },
    { key: "month", label: "Month" },
    { key: "ath",   label: "ATH" },
  ];
  const emptyMessage =
    period === "today" ? "No tips yet today — be the first!" :
    period === "week"  ? "No tips this week — be the first!" :
    period === "month" ? "No tips this month" :
    "No tips yet";

  return (
    <div style={{
      padding: "12px 14px", borderRadius: 10,
      background: `linear-gradient(135deg, ${WC.surface}, ${WC.gold}06)`,
      border: `1px solid ${WC.gold}18`,
      marginBottom: 12,
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
        <span style={{
          fontSize: 9, fontWeight: 800, color: WC.gold,
          background: `${WC.gold}18`, padding: "3px 8px", borderRadius: 5,
          fontFamily: "'JetBrains Mono', monospace",
          letterSpacing: "1.2px", textTransform: "uppercase",
          border: `1px solid ${WC.gold}30`,
        }}>🏆 Top Tippers</span>
        <div style={{ display: "flex", gap: 3 }}>
          {periods.map(p => (
            <button
              key={p.key}
              onClick={() => setPeriod(p.key)}
              style={{
                padding: "3px 7px", borderRadius: 4,
                background: period === p.key ? `${WC.gold}20` : "transparent",
                border: `1px solid ${period === p.key ? WC.gold + "40" : WC.border}`,
                color: period === p.key ? WC.gold : WC.textMuted,
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 8, fontWeight: 700,
                letterSpacing: "0.5px", textTransform: "uppercase",
                cursor: "pointer",
                transition: "all 0.15s",
              }}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>
      {tippers.length === 0 ? (
        <div style={{
          fontSize: 10, color: WC.textMuted,
          textAlign: "center", padding: "10px 0",
          fontStyle: "italic",
        }}>
          {emptyMessage}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {tippers.slice(0, 5).map((t, i) => {
            const profile = profiles[(t.tipper || "").toLowerCase()] || {};
            const displayName = profile.name || (t.tipper ? `${t.tipper.slice(0,6)}...${t.tipper.slice(-4)}` : "Anonymous");
            const avatar = profile.avatar;
            return (
              <a key={t.tipper || i}
                href={t.tipper ? `https://universalprofile.cloud/${t.tipper}` : undefined}
                target="_blank" rel="noopener noreferrer"
                title={t.tipper ? `View ${displayName} on Universal Profile` : undefined}
                style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "6px 8px", borderRadius: 6,
                background: i === 0 ? `${WC.gold}08` : `${WC.bg}40`,
                border: i === 0 ? `1px solid ${WC.gold}20` : `1px solid ${WC.border}`,
                textDecoration: "none", cursor: t.tipper ? "pointer" : "default", transition: "background 0.15s",
              }}
              onMouseEnter={e => { if (t.tipper) e.currentTarget.style.background = `${WC.accent}12`; }}
              onMouseLeave={e => { e.currentTarget.style.background = i === 0 ? `${WC.gold}08` : `${WC.bg}40`; }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0, flex: 1 }}>
                  <span style={{ fontSize: 13, width: 20, textAlign: "center", flexShrink: 0 }}>{medals[i] || `#${i+1}`}</span>
                  {avatar ? (
                    <img src={avatar} alt="" style={{ width: 22, height: 22, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} onError={e => { e.target.onerror = null; e.target.style.display = "none"; e.target.nextSibling && (e.target.nextSibling.style.display = "flex"); }} />
                  ) : null}
                  <div style={{ width: 22, height: 22, borderRadius: "50%", background: `${WC.accent}15`, border: `1px solid ${WC.accent}25`, flexShrink: 0, display: avatar ? "none" : "flex", alignItems: "center", justifyContent: "center", fontSize: 10 }}>👤</div>
                  <span style={{ fontSize: 11, fontWeight: 600, color: WC.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
                    {displayName}
                  </span>
                </div>
                <span style={{ fontSize: 11, fontWeight: 700, color: WC.accent, fontFamily: "'JetBrains Mono', monospace", flexShrink: 0 }}>
                  {parseFloat(t.total_amount || 0).toFixed(1)} LYX
                </span>
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
}

// UP mobile app deep link / download URLs
const UP_MOBILE_LINKS = {
  ios: "https://apps.apple.com/us/app/universal-profiles/id6702018631",
  android: "https://play.google.com/store/apps/details?id=io.universaleverything.universalprofiles",
};

function getUPMobileLink() {
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/i.test(ua)) return UP_MOBILE_LINKS.ios;
  if (/Android/i.test(ua)) return UP_MOBILE_LINKS.android;
  return UP_MOBILE_LINKS.ios; // default
}

// --- SHARED ANIMATION STYLES --------------------------------
const GLOBAL_STYLES = `
  input[type=number]::-webkit-inner-spin-button,
  input[type=number]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
  input[type=number] { -moz-appearance: textfield; }
  @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
  @keyframes fadeInUp { 0% { opacity: 0; transform: translateY(20px); } 100% { opacity: 1; transform: translateY(0); } }
  @keyframes fadeIn { 0% { opacity: 0; } 100% { opacity: 1; } }
  @keyframes slideInRight { 0% { opacity: 0; transform: translateX(40px); } 100% { opacity: 1; transform: translateX(0); } }
  @keyframes slideInLeft { 0% { opacity: 0; transform: translateX(-40px); } 100% { opacity: 1; transform: translateX(0); } }
  @keyframes scaleIn { 0% { opacity: 0; transform: scale(0.9); } 100% { opacity: 1; transform: scale(1); } }
  @keyframes slideDown { 0% { opacity: 0; transform: translateY(-12px); } 100% { opacity: 1; transform: translateY(0); } }
  @keyframes float { 0%, 100% { transform: translateY(0px); } 50% { transform: translateY(-8px); } }
  @keyframes shimmer { 0% { background-position: -200% center; } 100% { background-position: 200% center; } }
  @keyframes glowPulse { 0%, 100% { opacity: 0.4; } 50% { opacity: 0.8; } }
  @keyframes gridScroll { 0% { transform: translate(0,0); } 100% { transform: translate(40px, 40px); } }
  @keyframes orbFloat1 { 0%, 100% { transform: translate(0, 0) scale(1); } 33% { transform: translate(30px, -20px) scale(1.1); } 66% { transform: translate(-20px, 15px) scale(0.95); } }
  @keyframes orbFloat2 { 0%, 100% { transform: translate(0, 0) scale(1); } 33% { transform: translate(-25px, 25px) scale(0.9); } 66% { transform: translate(20px, -30px) scale(1.05); } }
  @keyframes ripple { 0% { transform: scale(0); opacity: 0.5; } 100% { transform: scale(4); opacity: 0; } }
  @keyframes confetti { 0% { transform: translateY(0) rotate(0deg); opacity: 1; } 100% { transform: translateY(-100px) rotate(720deg); opacity: 0; } }
  @keyframes slideOverlayIn { 0% { translate: 120% 0; scale: 0.85; opacity: 0; } 60% { translate: -4% 0; scale: 1.02; opacity: 1; } 100% { translate: 0 0; scale: 1; opacity: 1; } }
  @keyframes fadeOverlayOut { 0% { opacity: 1; transform: scale(1); } 100% { opacity: 0; transform: scale(0.92) translateY(-16px); } }
  @keyframes whalePulse { 0%, 100% { box-shadow: 0 0 30px #ffd70025, 0 0 60px #ff8c0015, inset 0 0 30px #ffd70008; } 50% { box-shadow: 0 0 50px #ffd70040, 0 0 100px #ff8c0025, inset 0 0 50px #ffd70010; } }
  @keyframes shrink { 0% { width: 100%; } 100% { width: 0%; } }
  @keyframes barGrow { 0% { width: 0%; } 100% { width: var(--bar-width); } }
  @keyframes numberCount { 0% { opacity: 0; transform: translateY(8px); } 100% { opacity: 1; transform: translateY(0); } }
  @keyframes chatSlideIn { 0% { opacity: 0; transform: translateX(-10px); } 100% { opacity: 1; transform: translateX(0); } }
  @keyframes dotBounce { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-4px); } }
  @keyframes heroGlow { 0%, 100% { filter: brightness(1) blur(0px); } 50% { filter: brightness(1.15) blur(0.5px); } }
  @keyframes subSlideIn { 0% { opacity: 0; transform: translateY(-20px) scale(0.9); } 100% { opacity: 1; transform: translateY(0) scale(1); } }
  @keyframes subFadeOut { 0% { opacity: 1; } 100% { opacity: 0; transform: translateY(-10px) scale(0.95); } }
  @keyframes countUp { 0% { transform: translateY(8px); opacity: 0; } 100% { transform: translateY(0); opacity: 1; } }
  @keyframes navSlideDown { 0% { opacity: 0; transform: translateY(-100%); } 100% { opacity: 1; transform: translateY(0); } }

  * { box-sizing: border-box; }
  html, body, #root { height: auto; overflow: visible; margin: 0; padding: 0; background: #06060b; }
  ::-webkit-scrollbar { width: 4px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: #ffffff10; border-radius: 2px; }
  ::-webkit-scrollbar-thumb:hover { background: #ffffff20; }
`;

// --- UTILITY HOOK -------------------------------------------
function useStaggerReveal(count, baseDelay = 60) {
  const [visible, setVisible] = useState(new Set());
  useEffect(() => {
    const timers = [];
    for (let i = 0; i < count; i++) {
      timers.push(setTimeout(() => setVisible(prev => new Set([...prev, i])), i * baseDelay + 100));
    }
    return () => timers.forEach(clearTimeout);
  }, [count, baseDelay]);
  return visible;
}

// --- MOCK CHANNELS (for tipper profile) ---------------------
const MOCK_CHANNELS = [
  { name: "Alts Anonymous", avatar: "🎭", color: COLORS.accent, viewers: "1.2k" },
  { name: "Dracos Live", avatar: "👑", color: COLORS.gold, viewers: "3.4k" },
  { name: "NFT Degen Hour", avatar: "🦍", color: COLORS.purple, viewers: "890" },
  { name: "ChainWizard Builds", avatar: "🧙", color: COLORS.blue, viewers: "2.1k" },
  { name: "MetaHead Studios", avatar: "🤖", color: COLORS.orange, viewers: "670" },
];

const MOCK_TIPPER_DATA = {
  profile: MOCK_PROFILES[5], // AltsAnon as the connected user
  totalTipped: 247,
  totalChannels: 4,
  totalTips: 38,
  channels: [
    { channel: MOCK_CHANNELS[1], rank: 3, total: 105, tips: 14, nft: "💙 Silver" },
    { channel: MOCK_CHANNELS[0], rank: 7, total: 82, tips: 11, nft: "💚 Bronze" },
    { channel: MOCK_CHANNELS[3], rank: 2, total: 45, tips: 8, nft: null },
    { channel: MOCK_CHANNELS[2], rank: 12, total: 15, tips: 5, nft: null },
  ],
  nfts: [
    { channel: MOCK_CHANNELS[1], tier: "Silver Champion", icon: "🥈", color: "#4a9eff", threshold: 300 },
    { channel: MOCK_CHANNELS[0], tier: "Bronze Supporter", icon: "🥉", color: "#00cc66", threshold: 100 },
  ],
};

// --- STANDALONE TIP PAGE ------------------------------------
function TipPage({ creatorAddress }) {
  const [mounted, setMounted] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [creatorInfo, setCreatorInfo] = useState({
    name: `${creatorAddress.slice(0, 6)}...${creatorAddress.slice(-4)}`,
    avatar: null,
    bio: "",
    links: {},
    tags: [],
    widgetTheme: null,
    activeGoal: null,
    loading: true,
  });

  useEffect(() => { requestAnimationFrame(() => setMounted(true)); }, []);

  // Track page view (silent, non-blocking)
  useEffect(() => {
    fetch("/api/data-stats", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "page-view", address: creatorAddress }),
    }).catch(() => {});
  }, [creatorAddress]);

  // Fetch creator profile — DB is the source of truth (mirrors main site settings)
  // LSP3 on-chain only used for avatar (PFP)
  useEffect(() => {
    // 1. Fetch from creator directory DB (saved settings from main site)
    const fetchFromDB = async () => {
      try {
        const res = await fetch(`/api/creator-register?address=${creatorAddress}`);
        if (res.status === 404) {
          // Creator not registered — still allow tipping via on-chain, just show address
          setCreatorInfo(prev => ({ ...prev, loading: false }));
          return;
        }
        const data = await res.json();
        const creator = data.creators?.[0] || data.creator;
        if (creator) {
          const platforms = typeof creator.platforms === "string"
            ? JSON.parse(creator.platforms || "{}")
            : (creator.platforms || {});
          // Widget theme stored inside platforms as _widgetTheme
          const wt = platforms._widgetTheme || null;
          const _ag = platforms._activeGoal; const ag = (_ag && _ag.title && _ag.status === "active" && parseFloat(_ag.target) > 0) ? _ag : null;
          // Fast poll load from overlay state — pass to TipWidget via creatorInfo
          const overlayState = platforms._overlayState || null;
          const settings = typeof creator.settings === "string" ? JSON.parse(creator.settings || "{}") : (creator.settings || {});
          // Expose creator's widget extras so standalone widget can render them
          window.__fullsend_thankyou = {
            enabled: settings.thankYouEnabled !== false,
            message: settings.thankYouMessage || "Thank you for your support! 💚",
            image: settings.thankYouImage || "",
          };
          window.__fullsend_show_top_tippers = settings.showTopTippers !== false;
          // Creator's custom tier thresholds (used by getTier() + message gating)
          if (settings.customTiers) {
            window.__fullsend_settings_data = { ...(window.__fullsend_settings_data || {}), customTiers: settings.customTiers };
          }
          // LIVE status with heartbeat expiry check (2 min)
          const heartbeat = overlayState?.liveHeartbeat || 0;
          const isLive = overlayState?.isLive === true && (Date.now() - heartbeat) < 120000;
          const primaryStream = overlayState?.primaryStream || "auto";
          setCreatorInfo(prev => ({
            ...prev,
            name: creator.name || prev.name,
            bio: creator.bio || "",
            tags: creator.tags || [],
            isLive,
            primaryStream,
            links: {
              youtube: platforms.youtube || "",
              twitch: platforms.twitch || "",
              kick: platforms.kick || "",
              rumble: platforms.rumble || "",
              x: platforms.x || "",
              website: platforms.website || "",
            },
            verification: settings.verification || {},
            socialIcons: platforms._socialIcons || {},
            customLinks: Array.isArray(platforms._customLinks) ? platforms._customLinks : [],
            widgetTheme: wt,
            activeGoal: ag,
            loading: false,
          }));
        }
      } catch (err) {
        console.log("Creator DB fetch (non-fatal):", err);
        setCreatorInfo(prev => ({ ...prev, loading: false }));
      }
    };

    // 2. Fetch LSP3 for avatar only
    const fetchAvatar = async () => {
      try {
        const rpc = LUKSO_TESTNET.rpcUrls[0];
        const LSP3_KEY = "5ef83ad9559033e6e941db7d7c495acdce616347d28e90c7ce47cbfcfcad3bc5";
        const callData = "0x54f6127f" + LSP3_KEY;
        const resp = await fetch(rpc, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            jsonrpc: "2.0", id: 1, method: "eth_call",
            params: [{ to: creatorAddress, data: callData }, "latest"],
          }),
        });
        const json = await resp.json();
        const result = json.result;
        if (result && result !== "0x" && result.length > 130) {
          const hex = result.slice(2);
          const offset = parseInt(hex.slice(0, 64), 16) * 2;
          const length = parseInt(hex.slice(offset, offset + 64), 16);
          const contentHex = hex.slice(offset + 64, offset + 64 + length * 2);
          let raw = "";
          for (let i = 0; i < contentHex.length; i += 2) {
            raw += String.fromCharCode(parseInt(contentHex.slice(i, i + 2), 16));
          }
          const ipfsIdx = raw.indexOf("ipfs://");
          const httpsIdx = raw.indexOf("https://");
          const urlStart = ipfsIdx > -1 ? ipfsIdx : httpsIdx;
          if (urlStart > -1) {
            let url = raw.slice(urlStart);
            if (url.startsWith("ipfs://")) url = "https://api.universalprofile.cloud/ipfs/" + url.slice(7);
            const metaResp = await fetch(url);
            const meta = await metaResp.json();
            const p = meta?.LSP3Profile || meta;
            const img = p.profileImage?.[0]?.url || p.profileImage?.[0]?.src;
            if (img) {
              const avatar = img.startsWith("ipfs://")
                ? "https://api.universalprofile.cloud/ipfs/" + img.slice(7)
                : img;
              setCreatorInfo(prev => ({ ...prev, avatar }));
            }
            // Also use LSP3 name as fallback ONLY if DB didn't provide one
            if (p.name) {
              setCreatorInfo(prev => ({ ...prev, name: prev.name || p.name }));
            }
          }
        }
        setCreatorInfo(prev => ({ ...prev, loading: false }));
      } catch (err) {
        console.log("LSP3 avatar fetch (non-fatal):", err);
        setCreatorInfo(prev => ({ ...prev, loading: false }));
      }
    };

    // Run both — DB first (fast), then LSP3 for avatar
    fetchFromDB();
    fetchAvatar();

    // Poll the DB every 10 seconds for live status updates (isLive, heartbeat, goal, etc.)
    const pollInterval = setInterval(() => {
      fetchFromDB();
    }, 10000);

    // Background verification recheck — silently verify YouTube/Twitch on every viewer visit
    const recheckVerification = async () => {
      try {
        const res = await fetch(`/api/creator-register?address=${creatorAddress}`);
        const data = await res.json();
        const creator = data.creators?.[0] || data.creator;
        if (!creator) return;
        const settings = typeof creator.settings === "string" ? JSON.parse(creator.settings || "{}") : (creator.settings || {});
        const platforms = typeof creator.platforms === "string" ? JSON.parse(creator.platforms || "{}") : (creator.platforms || {});
        const verification = settings.verification || {};
        const socialLinks = settings.socialLinks || {};

        let changed = false;
        for (const platform of ["youtube", "twitch"]) {
          const v = verification[platform];
          if (v?.status === "verified" && v?.code && socialLinks[platform]) {
            try {
              const checkRes = await fetch("/api/verify-creator", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ platform, url: socialLinks[platform], code: v.code, creator: creatorAddress }),
              });
              const result = await checkRes.json();
              if (!result.verified) {
                verification[platform] = { code: null, status: "none", verifiedAt: null };
                changed = true;
                console.log(`Verification revoked for ${platform} — code not found`);
              }
            } catch (e) { /* non-fatal — keep current status */ }
          }
        }

        if (changed) {
          // Update settings in DB with revoked verification
          settings.verification = verification;
          await fetch("/api/creator-register", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              address: creatorAddress,
              settings,
            }),
          });
        }
      } catch (e) { /* non-fatal */ }
    };
    // Delay recheck so it doesn't slow page load
    setTimeout(recheckVerification, 5000);

    // Cleanup polling interval on unmount
    return () => clearInterval(pollInterval);
  }, [creatorAddress]);

  return (
    <div style={{
      minHeight: "100vh",
      background: COLORS.bg,
      color: COLORS.text,
      fontFamily: "'Outfit', sans-serif",
    }}>
      <style>{GLOBAL_STYLES}</style>

      {/* Header Bar */}
      <div style={{
        padding: "10px 16px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        borderBottom: `1px solid ${COLORS.border}`,
      }}>
        <div style={{
          fontFamily: "'Syne', sans-serif",
          fontSize: 16, fontWeight: 800,
          background: `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.cyan})`,
          WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
          letterSpacing: "-0.5px",
        }}>FullSend</div>
        <div style={{ fontSize: 9, color: COLORS.textMuted }}>
          LUKSO Mainnet
        </div>
      </div>

      {/* Tip Widget */}
      <div style={{
        maxWidth: 420, margin: "0 auto", padding: "12px 12px 0",
        opacity: mounted ? 1 : 0,
        transform: mounted ? "translateY(0)" : "translateY(15px)",
        transition: "all 0.5s cubic-bezier(0.16, 1, 0.3, 1)",
      }}>
        {creatorInfo.loading ? (
          <div style={{ textAlign: "center", padding: "60px 20px" }}>
            <div style={{
              width: 64, height: 64, borderRadius: "50%",
              background: `${COLORS.accent}12`, border: `2px solid ${COLORS.accent}20`,
              margin: "0 auto 16px",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <span style={{ fontFamily: "'Syne', sans-serif", fontSize: 24, fontWeight: 900, background: `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.cyan})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", animation: "pulse 1.5s infinite" }}>F</span>
            </div>
            <div style={{ fontSize: 12, color: COLORS.textMuted }}>Loading creator profile...</div>
          </div>
        ) : (
          <TipWidget creatorAddress={creatorAddress} standalone creatorInfo={creatorInfo} />
        )}
      </div>
    </div>
  );
}

// --- STANDALONE OVERLAY (for OBS Browser Source) ----------------
function StandaloneOverlay({ creatorAddress }) {
  const [alerts, setAlerts] = useState([]);
  const [activePoll, setActivePoll] = useState(null);

  // Independent vote-count refresh — Control push only sets the poll skeleton,
  // this loop keeps vote tallies fresh on the OBS overlay every 5s.
  useEffect(() => {
    if (!activePoll?.id || !creatorAddress) return;
    let cancelled = false;
    const refresh = async () => {
      try {
        const r = await fetch(`/api/data-polls?creator=${creatorAddress}&votes=true`);
        if (!r.ok) return;
        const data = await r.json();
        if (cancelled || !data?.polls) return;
        const livePoll = data.polls.find(p => String(p.id) === String(activePoll.id));
        if (!livePoll || !Array.isArray(livePoll.options)) return;
        setActivePoll(prev => {
          if (!prev || String(prev.id) !== String(activePoll.id) || !Array.isArray(prev.options)) return prev;
          return {
            ...prev,
            totalVotes: livePoll.totalVotes ?? prev.totalVotes,
            options: prev.options.map((o, i) => ({ ...o, votes: livePoll.options[i]?.votes ?? o.votes ?? 0 })),
          };
        });
      } catch {}
    };
    refresh();
    const id = setInterval(refresh, 15000);
    return () => { cancelled = true; clearInterval(id); };
  }, [activePoll?.id, creatorAddress]);

  const [goalVisible, setGoalVisible] = useState(false);
  // DB-only goal — single source of truth, polls every 5s + bumps on tip events
  const { active: tipGoalActive } = useActiveGoal(creatorAddress);
  const tipGoal = tipGoalActive;
  const [positions, setPositions] = useState({
    tips: "bottom-right", follows: "top-left", micro: "bottom-right",
    chat: "bottom-left", polls: "top-right", goal: "bottom-center", qr: "bottom-left",
    leaderboard: "top-right", moc: "bottom-center",
  });
  const [overlayVis, setOverlayVis] = useState({
    tips: true, follows: true, micro: true, chat: false, polls: false, goal: false, qr: false,
  });

  // Moments on Chain — approved submissions to feature on stream (polls /api/moc?shown=1)
  const [mocShown, setMocShown] = useState([]);
  const [mocProfiles, setMocProfiles] = useState({});
  const [mocIdx, setMocIdx] = useState(0);
  useEffect(() => {
    if (!creatorAddress) return;
    let cancelled = false;
    const load = async () => {
      try {
        const r = await fetch(`/api/moc?creator=${creatorAddress}&shown=1`);
        const j = await r.json();
        if (cancelled) return;
        const subs = (j && j.submissions) || [];
        setMocShown(subs);
        const addrs = [...new Set(subs.map(s => (s.poster || "").toLowerCase()).filter(Boolean))];
        const got = await Promise.all(addrs.map(a => fetchUPProfile(a).catch(() => null)));
        if (!cancelled) setMocProfiles(prev => { const n = { ...prev }; addrs.forEach((a, i) => { if (got[i]) n[a] = got[i]; }); return n; });
      } catch {}
    };
    load();
    const iv = setInterval(load, 15000);
    return () => { cancelled = true; clearInterval(iv); };
    /* eslint-disable-next-line */
  }, [creatorAddress]);
  // Rotate through multiple shown submissions, ~10s each
  useEffect(() => {
    if (mocShown.length <= 1) { setMocIdx(0); return; }
    const iv = setInterval(() => setMocIdx(i => (i + 1) % mocShown.length), 10000);
    return () => clearInterval(iv);
  }, [mocShown.length]);
  const lastAlertRef = useRef(null);
  // Dedup across BOTH ingestion paths (DB alertQueue + data-tips polling).
  // Same tip can arrive via both; track shown tx hashes / queue timestamps to fire only once.
  const firedTipsRef = useRef(new Set());

  // Convert position key to CSS — handles grid strings AND custom {x, y} objects
  // For center positions, returns left:0, right:0, and a centerH flag so callers can use flex/margin centering
  const posCSS = (pos) => {
    // Custom position: {x, y} as percentage — top-left anchored to match drag canvas
    if (pos && typeof pos === "object" && pos.x !== undefined) {
      return { left: `${pos.x}%`, top: `${pos.y}%` };
    }
    // Grid position string — handles all 9 positions in 3x3 grid:
    // top-left, top-center, top-right
    // mid-left, center, mid-right
    // bottom-left, bottom-center, bottom-right
    if (!pos || typeof pos !== "string") return {};
    const s = {};
    const isTop = pos.includes("top");
    const isBottom = pos.includes("bottom");
    const isMid = pos.includes("mid") || pos === "center";
    const isLeft = pos.includes("left");
    const isRight = pos.includes("right");
    const isHCenter = (pos === "center" || pos === "top-center" || pos === "bottom-center") && !isLeft && !isRight;

    // Vertical positioning
    if (isTop) s.top = 20;
    else if (isBottom) s.bottom = 20;
    else if (isMid) {
      // Mid row: position at 50% from top, translate up by half height
      s.top = "50%";
      s.transform = "translateY(-50%)";
    }

    // Horizontal positioning
    if (isHCenter) {
      // Full-width container with flex centering — children center automatically
      s.left = 0;
      s.right = 0;
      s.display = "flex";
      s.justifyContent = "center";
      s.pointerEvents = "none";
    } else if (isLeft) {
      s.left = 20;
    } else if (isRight) {
      s.right = 20;
    }

    return s;
  };

  // Poll creator-register for overlay state from Control page (every 2s)
  useEffect(() => {
    let isFirstAlertLoad = true;
    const pollState = async () => {
      try {
        const res = await fetch(`/api/creator-register?address=${creatorAddress}`);
        const data = await res.json();
        const creator = data.creators?.[0] || data.creator;
        if (!creator) return;
        const platforms = typeof creator.platforms === "string" ? JSON.parse(creator.platforms || "{}") : (creator.platforms || {});
        const settings = typeof creator.settings === "string" ? JSON.parse(creator.settings || "{}") : (creator.settings || {});

        // Populate window globals so playTierSound() / TTS works in standalone overlay
        if (settings.selectedSound) window.__fullsend_selected_sounds = settings.selectedSound;
        if (settings.customSounds) {
          window.__fullsend_settings_data = { ...(window.__fullsend_settings_data || {}), customSounds: settings.customSounds };
        }
        if (typeof settings.masterVolume === "number") window.__fullsend_volume = settings.masterVolume;
        if (settings.soundToggles) window.__fullsend_sound_toggles = settings.soundToggles;
        if (settings.tts) window.__fullsend_tts = settings.tts;
        
        // Goal data — handled by useActiveGoal hook above (DB poll every 5s)

        // Overlay state from Control page
        const state = platforms._overlayState;
        if (state) {
          // Persist to window so QR and other components can read it
          window.__fullsend_overlay_state = { ...(window.__fullsend_overlay_state || {}), ...state };
          // Goal visibility
          setGoalVisible(state.goalVisible === true);
          
          // Poll — set base from Control push, then merge live vote counts below
          setActivePoll(prev => {
            const incoming = state.poll || null;
            if (!incoming) return null;
            // Preserve existing vote counts if same poll id (so 5s refresh doesn't flicker).
            // Must keep BOTH totalVotes AND per-option votes from prev — the Control-push
            // skeleton carries a stale/zero totalVotes, and clobbering it desyncs the
            // percentage calc (totalVotes=0 + option votes>0 → bar snaps to 0%).
            if (prev && prev.id === incoming.id && Array.isArray(prev.options)) {
              return { ...incoming, totalVotes: prev.totalVotes ?? incoming.totalVotes, options: incoming.options.map((o, i) => ({ ...o, votes: prev.options[i]?.votes ?? o.votes ?? 0 })) };
            }
            return incoming;
          });

          // Positions — use layoutMode to decide grid vs custom
          const mode = state.layoutMode || "grid";
          // Default grid positions (used as base for both modes)
          const defaultGrid = {
            tips: "bottom-right", follows: "top-left", micro: "bottom-right",
            chat: "bottom-left", polls: "top-right", goal: "bottom-center", qr: "bottom-left",
            leaderboard: "top-right", moc: "bottom-center",
          };
          if (mode === "custom" && state.customPositions) {
            // Custom layout: start from grid positions, then overlay custom positions
            const merged = { ...defaultGrid, ...(state.positions || {}) };
            for (const [key, val] of Object.entries(state.customPositions)) {
              if (val && typeof val === "object" && val.x !== undefined) {
                merged[key] = val;
              }
            }
            setPositions(merged);
          } else {
            // Grid layout: use grid positions only — fully reset to clear any stale custom positions
            setPositions({ ...defaultGrid, ...(state.positions || {}) });
          }
          // Visibility toggles from Control page
          if (state.visibility) setOverlayVis(prev => ({ ...prev, ...state.visibility }));
          
          // Alerts — process queue, show any new ones since last check (tips + follows)
          // Filter out anything older than 2 min — prevents OBS reload from replaying ancient tips
          const STALE_MS = 2 * 60 * 1000;
          const now = Date.now();
          const queue = (state.alertQueue || []).filter(a => (a.shownAt || 0) > now - STALE_MS);
          if (queue.length > 0) {
            // First load (e.g. fresh page or OBS browser source refresh):
            // just record the latest timestamp, don't fire alerts for existing queue items
            if (isFirstAlertLoad) {
              lastAlertRef.current = Math.max(...queue.map(a => a.shownAt || 0));
              isFirstAlertLoad = false;
            } else {
            const lastSeen = lastAlertRef.current || 0;
            const newAlerts = queue.filter(a => a.shownAt > lastSeen);
            if (newAlerts.length > 0) {
              lastAlertRef.current = Math.max(...newAlerts.map(a => a.shownAt));
              for (const a of newAlerts) {
                // Shared dedup key — tx hash if present, else shownAt timestamp
                const dedupKey = a.txHash || a.tx_hash || `ts:${a.shownAt}`;
                if (firedTipsRef.current.has(dedupKey)) continue;
                firedTipsRef.current.add(dedupKey);
                if (a.type === "test") {
                  setAlerts(prev => [...prev, {
                    id: a.shownAt,
                    type: "test",
                    createdAt: new Date(),
                    tier: { duration: 5000 },
                  }]);
                } else if (a.type === "follow") {
                  setAlerts(prev => [...prev, {
                    id: a.shownAt,
                    type: "follow",
                    profile: a.from || { name: "Follower", avatar: STOCK_AVATAR },
                    createdAt: new Date(),
                    tier: { duration: 4000 },
                  }]);
                } else {
                  const tier = getTier(a.amount || 0);
                  setAlerts(prev => [...prev, {
                    id: a.shownAt,
                    type: "tip",
                    amount: a.amount,
                    tier,
                    profile: a.from || { name: "Tipper", avatar: STOCK_AVATAR },
                    message: a.message || "",
                    createdAt: new Date(),
                  }]);
                }
                // Note: SFX + TTS now fire in the currentAlert effect when the alert becomes visible
                // This prevents rapid-fire alerts from playing all their sounds at once
              }
            }
            } // end else (not first load)
          }
        }
      } catch (e) { /* non-fatal */ }
    };
    pollState();
    const interval = setInterval(pollState, 2000);
    return () => clearInterval(interval);
  }, [creatorAddress]);

  // Direct tip polling — independent of Control page
  // This makes the overlay work even when Control is closed/minimized
  const lastTipRef = useRef(null);
  useEffect(() => {
    if (!creatorAddress) return;
    let isFirstLoad = true;
    const seenTxHashes = new Set();

    const pollTips = async () => {
      try {
        const res = await fetch(`/api/data-tips?action=activity&creator=${creatorAddress}&limit=20`);
        const data = await res.json();
        const tips = data.activity || [];
        if (tips.length === 0) return;

        // First load: just remember which tips exist, don't fire alerts for them
        if (isFirstLoad) {
          for (const t of tips) seenTxHashes.add(t.tx_hash);
          isFirstLoad = false;
          return;
        }

        // Find new tips (sorted newest first by API)
        const newTips = tips.filter(t => t.tx_hash && !seenTxHashes.has(t.tx_hash));
        if (newTips.length === 0) return;

        // Mark all as seen
        for (const t of newTips) seenTxHashes.add(t.tx_hash);

        // Fire alerts for new tips (oldest first so they appear in order)
        const ordered = newTips.reverse();
        for (const t of ordered) {
          // Shared dedup with DB alertQueue path — skip if already fired
          const dedupKey = t.tx_hash || `amount:${t.amount}:${t.tipper}:${t.created_at}`;
          if (firedTipsRef.current.has(dedupKey)) continue;
          firedTipsRef.current.add(dedupKey);
          const amount = parseFloat(t.amount || 0);
          const tier = getTier(amount);
          // Resolve UP profile in background
          let profile = { name: `${t.tipper.slice(0, 6)}...${t.tipper.slice(-4)}`, avatar: STOCK_AVATAR, address: t.tipper };
          try {
            const p = await fetchUPProfile(t.tipper);
            if (p) profile = { ...profile, name: p.name || profile.name, avatar: p.avatar || profile.avatar };
          } catch {}

          setAlerts(prev => [...prev, {
            id: t.tx_hash || Date.now(),
            type: "tip",
            amount,
            tier,
            profile,
            message: t.message || "",
            createdAt: new Date(),
          }]);
          // Note: SFX + TTS now fire in the currentAlert effect when the alert becomes visible
        }
      } catch (e) { /* non-fatal */ }
    };

    pollTips();
    const tipInterval = setInterval(pollTips, 5000);
    return () => clearInterval(tipInterval);
  }, [creatorAddress]);

  // Auto-dismiss alerts
  useEffect(() => {
    if (alerts.length === 0) return;
    const timer = setTimeout(() => {
      setAlerts(prev => prev.slice(1));
    }, alerts[0]?.tier?.duration || 8000);
    return () => clearTimeout(timer);
  }, [alerts]);

  const currentAlert = alerts[0] || null;

  // Play SFX + TTS when a new alert becomes visible (not when queued)
  // This ensures rapid-fire alerts space out their sounds properly
  const playedIdRef = useRef(null);
  useEffect(() => {
    if (!currentAlert) return;
    const currentId = currentAlert.id;
    if (playedIdRef.current === currentId) return;
    playedIdRef.current = currentId;
    const st = window.__fullsend_sound_toggles || {};

    if (currentAlert.type === "follow") {
      if (st.follows !== false && window.__fullsend_playTierSound) {
        try { window.__fullsend_playTierSound("follow"); } catch {}
      }
      return;
    }

    // Tip alert — play tier SFX, then TTS after a delay
    const tier = currentAlert.tier;
    const tierKey = tier?.label?.toLowerCase() || "micro";
    let sfxDelayMs = 0;
    if (st.tips !== false && window.__fullsend_playTierSound) {
      try { window.__fullsend_playTierSound(tierKey); } catch {}
      const soundName = window.__fullsend_selected_sounds?.[tierKey];
      const isMp3 = soundName && STOCK_MP3_SOUNDS[soundName];
      sfxDelayMs = isMp3 ? 3500 : (soundName && soundName !== "none" ? 1500 : 0);
    }

    // TTS — delayed until after SFX
    const tts = window.__fullsend_tts || {};
    const ttsTiers = tts.tiers || { micro: false, supporter: false, champion: true, legend: true, whale: true };
    if (tts.enabled && ttsTiers[tierKey] && window.speechSynthesis && st.tips !== false) {
      const text = currentAlert.message
        ? `${currentAlert.profile?.name || "Someone"} tipped ${currentAlert.amount} LYX: ${currentAlert.message}`
        : `${currentAlert.profile?.name || "Someone"} tipped ${currentAlert.amount} LYX`;
      const ttsTimer = setTimeout(() => {
        try {
          const utterance = new SpeechSynthesisUtterance(text);
          const voices = window.speechSynthesis.getVoices();
          const voice = voices.find(v => v.name === tts.voice);
          if (voice) utterance.voice = voice;
          utterance.rate = tts.rate || 1;
          utterance.volume = window.__fullsend_volume || 0.7;
          utterance.pitch = tts.pitch || 1;
          window.speechSynthesis.speak(utterance);
        } catch {}
      }, sfxDelayMs);
      // Cleanup: cancel scheduled TTS if alert changes before it fires
      return () => { clearTimeout(ttsTimer); };
    }
  }, [currentAlert?.id]);

  return (
    <div style={{
      width: "100vw", height: "100vh",
      background: "transparent",
      position: "relative", overflow: "hidden",
      fontFamily: "'Outfit', sans-serif",
    }}>
      {/* Tip Goal Bar — visibility driven by the goal's own showOnOverlay flag
          (DB-synced via GoalAPI.setVisibility), the single source of truth.
          overlayVis.goal/goalVisible kept as a secondary OR for back-compat. */}
      {tipGoal && tipGoal.status === "active" && tipGoal.target > 0 && tipGoal.showOnOverlay !== false && (overlayVis.goal || goalVisible) && (() => {
        const gp = positions.goal || "bottom-center";
        const gs = { position: "absolute", zIndex: 40 };
        let isHCenter = false;
        if (gp && typeof gp === "object" && gp.x !== undefined) {
          gs.left = `${gp.x}%`; gs.top = `${gp.y}%`;
          gs.width = "40%"; gs.minWidth = 340;
        } else if (typeof gp === "string") {
          const isTop = gp.includes("top");
          const isBottom = gp.includes("bottom");
          const isMid = gp.includes("mid") || gp === "center";
          const isLeft = gp.includes("left");
          const isRight = gp.includes("right");
          const hCenter = (gp === "center" || gp === "top-center" || gp === "bottom-center") && !isLeft && !isRight;

          // Vertical positioning
          if (isTop) gs.top = 20;
          else if (isBottom) gs.bottom = 20;
          else if (isMid) {
            // Mid row: position at 50% from top, then translate up by half its height
            gs.top = "50%";
            gs.transform = "translateY(-50%)";
          }

          // Horizontal positioning
          if (hCenter) {
            // Use full-width container with flex centering
            gs.left = 0; gs.right = 0;
            gs.display = "flex";
            gs.justifyContent = "center";
            gs.pointerEvents = "none";
            isHCenter = true;
          } else if (isLeft) {
            gs.left = 20; gs.width = "40%"; gs.minWidth = 340;
          } else if (isRight) {
            gs.right = 20; gs.width = "40%"; gs.minWidth = 340;
          }
        }
        return (
        <div style={gs}>
          <div style={{
            animation: "slideOverlayIn 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards",
            width: isHCenter ? "40%" : "100%",
            minWidth: isHCenter ? 340 : undefined,
            pointerEvents: "auto",
          }}>
          <div style={{
            background: "rgba(6,6,11,0.92)",
            backdropFilter: "blur(12px)",
            borderRadius: 12,
            padding: "16px 22px",
            border: `2px solid ${COLORS.accent}40`,
            boxShadow: `0 0 25px ${COLORS.accent}25, inset 0 0 20px ${COLORS.accent}05`,
          }}>
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              marginBottom: 12,
            }}>
              <span style={{ fontSize: 18, fontWeight: 800, color: "#ffffff", letterSpacing: "0.3px", textShadow: `0 0 12px ${COLORS.accent}60` }}>
                🎯 {tipGoal.title}
              </span>
              <span style={{
                fontSize: 17, fontWeight: 800, color: COLORS.accent,
                fontFamily: "'JetBrains Mono', monospace",
                textShadow: `0 0 10px ${COLORS.accent}80`,
              }}>{parseFloat(tipGoal.current).toFixed(1)} / {tipGoal.target} LYX</span>
            </div>
            <div style={{
              height: 10, borderRadius: 5,
              background: "rgba(255,255,255,0.08)",
              overflow: "hidden",
              border: `1px solid ${COLORS.accent}20`,
            }}>
              <div style={{
                height: "100%",
                width: `${Math.min((parseFloat(tipGoal.current) / tipGoal.target) * 100, 100)}%`,
                borderRadius: 5,
                background: `linear-gradient(90deg, ${COLORS.accent}, ${COLORS.cyan})`,
                boxShadow: `0 0 14px ${COLORS.accent}80`,
                transition: "width 1s ease-out",
              }} />
            </div>
          </div>
          </div>
        </div>
        );
      })()}

      {/* Moments on Chain — featured submission(s) on stream (framed floating photos; rotates if multiple shown) */}
      {mocShown.length > 0 && (() => {
        const sub = mocShown[Math.min(mocIdx, mocShown.length - 1)];
        if (!sub) return null;
        const prof = mocProfiles[(sub.poster || "").toLowerCase()];
        const name = (prof && prof.name) || `${(sub.poster || "").slice(0, 6)}...${(sub.poster || "").slice(-4)}`;
        const avatar = (prof && prof.avatar) || STOCK_AVATAR;
        const items = (Array.isArray(sub.moment_ids) ? sub.moment_ids : []).filter(it => it && it.image);
        if (items.length === 0) return null;
        const pos = positions.moc || "bottom-center";
        const wrap = { position: "absolute", zIndex: 45, pointerEvents: "none" };
        let isHCenter = false;
        if (pos && typeof pos === "object" && pos.x !== undefined) {
          wrap.left = `${pos.x}%`; wrap.top = `${pos.y}%`;
        } else {
          const s = String(pos);
          const isTop = s.includes("top"), isBottom = s.includes("bottom"), isMid = s.includes("mid") || s === "center";
          const isLeft = s.includes("left"), isRight = s.includes("right");
          const hCenter = (s === "center" || s === "top-center" || s === "bottom-center") && !isLeft && !isRight;
          if (isTop) wrap.top = 28; else if (isBottom) wrap.bottom = 28; else if (isMid) { wrap.top = "50%"; wrap.transform = "translateY(-50%)"; }
          if (hCenter || (!isLeft && !isRight)) { wrap.left = 0; wrap.right = 0; wrap.display = "flex"; wrap.justifyContent = "center"; isHCenter = true; }
          else if (isLeft) wrap.left = 28;
          else if (isRight) wrap.right = 28;
        }
        const size = items.length === 1 ? { w: 480, h: 330 } : items.length === 2 ? { w: 330, h: 260 } : { w: 250, h: 250 };
        const rots = items.length === 1 ? [0] : items.length === 2 ? [-3.5, 3.5] : [-5, 0, 5];
        return (
          <div style={wrap}>
            <div style={{
              animation: "slideOverlayIn 0.6s cubic-bezier(0.16,1,0.3,1) forwards",
              background: "linear-gradient(150deg, rgba(22,17,44,0.95), rgba(10,14,26,0.96))",
              backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)",
              border: "1px solid rgba(124,109,247,0.5)", borderRadius: 22,
              padding: "22px 30px 26px", maxWidth: 900, pointerEvents: "auto",
              boxShadow: "0 24px 70px rgba(0,0,0,0.62), 0 0 40px rgba(124,109,247,0.18)",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 18 }}>
                <img src={avatar} alt="" style={{ width: 54, height: 54, borderRadius: "50%", objectFit: "cover", border: "2px solid rgba(124,109,247,0.65)", boxShadow: "0 0 18px rgba(124,109,247,0.45)" }} />
                <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  <span style={{ fontSize: 26, fontWeight: 800, color: "#fff", fontFamily: "'Syne',sans-serif", lineHeight: 1 }}>{name}</span>
                  <span style={{ fontSize: 14, color: "#bdb0ff", fontWeight: 600 }}>📷 Powered by Forever Moments</span>
                </div>
                {mocShown.length > 1 && <span style={{ marginLeft: "auto", fontSize: 12, color: "#7c6df7", fontFamily: "monospace", fontWeight: 700 }}>{mocIdx + 1}/{mocShown.length}</span>}
              </div>
              <div style={{ display: "flex", gap: 16, justifyContent: "center", alignItems: "center", padding: "8px 4px" }}>
                {items.map((it, i) => (
                  <div key={i} style={{
                    transform: `rotate(${rots[i] || 0}deg)`,
                    background: "#ffffff", padding: 6, borderRadius: 12,
                    boxShadow: "0 16px 38px rgba(0,0,0,0.62)",
                  }}>
                    {it.type === "video"
                      ? <video src={fmResolveIpfs(it.image)} autoPlay muted loop playsInline style={{ width: size.w, height: size.h, objectFit: "cover", borderRadius: 7, display: "block" }} />
                      : <img src={fmResolveIpfs(it.image)} alt={(it && it.title) || ""} style={{ width: size.w, height: size.h, objectFit: "cover", borderRadius: 7, display: "block" }} />}
                  </div>
                ))}
              </div>
              {sub.message && <div style={{ fontSize: 17, color: "#eef1f5", marginTop: 18, fontStyle: "italic", textAlign: "center", fontWeight: 500 }}>"{sub.message}"</div>}
            </div>
          </div>
        );
      })()}

      {/* Active Poll — controlled by Control page */}
      {overlayVis.polls && activePoll && (() => {
        const pp = positions.polls || "top-right";
        const ps = { position: "absolute", zIndex: 45 };
        let isHCenter = false;
        if (pp && typeof pp === "object" && pp.x !== undefined) {
          // Top-left anchored to match the drag-canvas editor exactly (editor uses no
          // center transform). Using translate(-50%,-50%) here shifted the poll up-left
          // by half its size, pushing it off the top and onto the leaderboard.
          ps.left = `${pp.x}%`; ps.top = `${pp.y}%`;
          ps.width = "32%"; ps.minWidth = 200;
        } else if (typeof pp === "string") {
          const isTop = pp.includes("top");
          const isBottom = pp.includes("bottom");
          const isMid = pp.includes("mid") || pp === "center";
          const isLeft = pp.includes("left");
          const isRight = pp.includes("right");
          const hCenter = (pp === "center" || pp === "top-center" || pp === "bottom-center") && !isLeft && !isRight;

          // Vertical positioning
          if (isTop) ps.top = 20;
          else if (isBottom) ps.bottom = 20;
          else if (isMid) {
            ps.top = "50%";
            ps.transform = "translateY(-50%)";
          }

          // Horizontal positioning
          if (hCenter) {
            ps.left = 0; ps.right = 0;
            ps.display = "flex";
            ps.justifyContent = "center";
            ps.pointerEvents = "none";
            // Reset transform if vertical centering set it (flex parent doesn't need translate)
            if (isMid) {
              ps.transform = "translateY(-50%)";
              ps.alignItems = "flex-start";
            }
            isHCenter = true;
          } else if (isLeft) {
            ps.left = 20; ps.width = "25%"; ps.minWidth = 360;
          } else if (isRight) {
            ps.right = 20; ps.width = "25%"; ps.minWidth = 360;
          }
        }
        return (
        <div style={ps}>
          <div style={{
            animation: "slideOverlayIn 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards",
            width: isHCenter ? "25%" : "100%",
            minWidth: isHCenter ? 360 : undefined,
            pointerEvents: "auto",
          }}>
          <div style={{
            padding: "16px 20px", borderRadius: 14,
            background: "rgba(6,6,11,0.92)",
            border: `1px solid ${COLORS.cyan}30`,
            backdropFilter: "blur(10px)",
            boxShadow: `0 0 20px ${COLORS.cyan}10`,
          }}>
            {activePoll.headerImage && (
              <img src={activePoll.headerImage} alt="" style={{ width: "100%", maxHeight: 150, objectFit: "contain", background: "rgba(0,0,0,0.2)", borderRadius: 8, marginBottom: 10 }} />
            )}
            <div style={{ fontSize: 17, fontWeight: 800, color: COLORS.text, marginBottom: 14, fontFamily: "'Syne', sans-serif", lineHeight: 1.3 }}>
              📊 {activePoll.question}
            </div>
            {(activePoll.options || []).map((opt, i) => {
              const pct = activePoll.totalVotes > 0 ? (opt.votes / activePoll.totalVotes) * 100 : 0;
              const maxVotes = Math.max(...(activePoll.options || []).map(o => o.votes || 0));
              const isLeading = (opt.votes || 0) === maxVotes && activePoll.totalVotes > 0;
              return (
                <div key={i} style={{ position: "relative", padding: "8px 12px", borderRadius: 8, overflow: "hidden", background: "rgba(255,255,255,0.05)", marginBottom: 6 }}>
                  <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${pct}%`, background: `${opt.color || COLORS.accent}25`, borderRadius: 8, transition: "width 0.6s ease" }} />
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", position: "relative", zIndex: 1 }}>
                    <span style={{ fontSize: 14, fontWeight: isLeading ? 700 : 500, color: isLeading ? (opt.color || COLORS.accent) : COLORS.text, display: "flex", alignItems: "center", gap: 6 }}>
                      {activePoll.optionImages?.[i] && <img src={activePoll.optionImages[i]} alt="" style={{ width: 28, height: 28, borderRadius: 5, objectFit: "cover" }} />}
                      {opt.text}
                    </span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: opt.color || COLORS.accent, fontFamily: "'JetBrains Mono', monospace" }}>{pct.toFixed(0)}%</span>
                  </div>
                </div>
              );
            })}
            <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 8, fontFamily: "'JetBrains Mono', monospace" }}>{activePoll.totalVotes} votes</div>
          </div>
          </div>
        </div>
        );
      })()}

      {/* Overlay Test banner — fired from Control's "Test Overlay" button */}
      {currentAlert && currentAlert.type === "test" && (
        <div key={currentAlert.id} style={{
          position: "absolute", top: "50%", left: "50%",
          transform: "translate(-50%, -50%)", zIndex: 100, pointerEvents: "none",
        }}>
          <div style={{
            animation: "slideOverlayIn 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards",
            padding: "32px 56px", borderRadius: 20,
            background: "rgba(6,6,11,0.92)",
            border: `2px solid ${COLORS.accent}`,
            backdropFilter: "blur(16px)",
            boxShadow: `0 0 60px ${COLORS.accent}55`,
            textAlign: "center",
          }}>
            <div style={{ fontSize: 64, marginBottom: 6, lineHeight: 1 }}>🚀</div>
            <div style={{
              fontSize: 48, fontWeight: 900, color: COLORS.accent,
              fontFamily: "'Syne', sans-serif", letterSpacing: "1px", lineHeight: 1,
            }}>
              FULL SEND TEST
            </div>
            <div style={{ fontSize: 20, fontWeight: 600, color: COLORS.text, marginTop: 12, fontFamily: "'Outfit', sans-serif" }}>
              Your overlay is connected ✅
            </div>
          </div>
        </div>
      )}

      {/* Tip Alert — matches AlertAnimation style from Overlay preview */}
      {currentAlert && currentAlert.type === "tip" && (() => {
        const supMin = parseFloat(window.__fullsend_settings_data?.customTiers?.supporter?.min) || 5;
        const isMicro = currentAlert.amount < supMin;
        if (isMicro && !overlayVis.micro) return null;
        if (!isMicro && !overlayVis.tips) return null;
        const tier = currentAlert.tier;
        const isWhale = tier.label === "WHALE";
        // All non-micro tips (including whales) use the same tips position
        const pos = isMicro ? posCSS(positions.micro) : posCSS(positions.tips);

        if (isMicro) {
          return (
            <div key={currentAlert.id} style={{ position: "absolute", ...pos, zIndex: 60 }}>
              <div style={{
                animation: "slideOverlayIn 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards",
                pointerEvents: "auto",
              }}>
              <div style={{
                padding: "8px 18px", borderRadius: 10,
                background: "rgba(6,6,11,0.88)",
                border: `1px solid ${COLORS.accent}25`,
                backdropFilter: "blur(12px)",
                display: "flex", alignItems: "center", gap: 10,
                boxShadow: `0 0 15px ${COLORS.accent}10`,
              }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: COLORS.accent, fontFamily: "'JetBrains Mono', monospace" }}>
                  ⚡ {currentAlert.amount} LYX
                </span>
                <span style={{ fontSize: 13, color: COLORS.textSecondary }}>
                  from <span style={{ fontWeight: 600, color: COLORS.text }}>{currentAlert.profile?.name || "Anonymous"}</span>
                </span>
              </div>
              </div>
            </div>
          );
        }

        // All non-micro tips (including whales): same size, same position
        // Whales get a fancier gold gradient border + glow for visual distinction
        return (
          <div key={currentAlert.id} style={{ position: "absolute", ...pos, zIndex: 60 }}>
            <div style={{
              animation: "slideOverlayIn 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards",
              maxWidth: 880,
              width: "auto",
              pointerEvents: "auto",
            }}>
            <div style={{
              background: `${COLORS.bg}e5`,
              backdropFilter: "blur(24px) saturate(1.3)",
              borderRadius: 24,
              border: isWhale ? `5px solid #ffd700` : `2.5px solid ${tier.color}40`,
              padding: "32px 40px",
              position: "relative",
              overflow: "hidden",
            }}>
              {/* Tier color accent line */}
              <div style={{
                position: "absolute", top: 0, left: 0, right: 0, height: 3,
                background: `linear-gradient(90deg, transparent, ${tier.color}, transparent)`,
                opacity: 0.6,
              }} />
              <div style={{ display: "flex", alignItems: "center", gap: 24, position: "relative" }}>
                {/* Avatar */}
                <div style={{ position: "relative", flexShrink: 0 }}>
                  <div style={{
                    width: 96, height: 96,
                    borderRadius: 20,
                    background: `${tier.color}18`,
                    border: `3px solid ${tier.color}40`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 44,
                    boxShadow: `0 0 18px ${tier.color}15`,
                    overflow: "hidden",
                  }}>
                    {isImageAvatar(currentAlert.profile?.avatar)
                      ? <img src={currentAlert.profile.avatar} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 16 }} />
                      : tier.emoji}
                  </div>
                  <div style={{
                    position: "absolute", bottom: -6, left: -6, zIndex: 2,
                    fontSize: 22, background: COLORS.bg, borderRadius: "50%",
                    width: 38, height: 38,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    border: `2.5px solid ${tier.color}50`,
                  }}>{tier.emoji}</div>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{
                    display: "inline-block",
                    fontSize: 11, fontWeight: 800,
                    color: tier.color,
                    background: `${tier.color}18`,
                    border: `1.5px solid ${tier.color}40`,
                    padding: "3px 10px",
                    borderRadius: 4,
                    fontFamily: "'JetBrains Mono', monospace",
                    letterSpacing: "1.5px",
                    textTransform: "uppercase",
                    marginBottom: 8,
                  }}>
                    {tier.emoji} {tier.label}
                  </div>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 14, flexWrap: "wrap" }}>
                    <span style={{
                      fontFamily: "'Syne', sans-serif", fontSize: 40, fontWeight: 800,
                      color: tier.color, letterSpacing: "-0.5px",
                    }}>{currentAlert.amount} LYX</span>
                    <span style={{ fontSize: 22, color: COLORS.textMuted }}>from</span>
                    <span style={{ fontWeight: 700, fontSize: 26, color: COLORS.text }}>
                      {currentAlert.profile?.name || "Anonymous"}
                    </span>
                  </div>
                  {currentAlert.message && (
                    <div style={{ fontSize: 24, color: COLORS.textSecondary, marginTop: 10, lineHeight: 1.5 }}>
                      "{currentAlert.message}"
                    </div>
                  )}
                </div>
              </div>
              {/* Progress bar */}
              <div style={{ marginTop: 16, height: 3, background: `${tier.color}12`, borderRadius: 2, overflow: "hidden" }}>
                <div style={{
                  height: "100%",
                  background: `linear-gradient(90deg, ${tier.color}, ${tier.color}80)`,
                  borderRadius: 2,
                  animation: `shrink ${(tier.duration || 5000) / 1000}s linear forwards`,
                  boxShadow: `0 0 10px ${tier.color}40`,
                }} />
              </div>
            </div>
          </div>
          </div>
        );
      })()}

      {/* Follow Alert */}
      {overlayVis.follows && currentAlert && currentAlert.type === "follow" && (
        <div style={{ position: "absolute", ...posCSS(positions.follows), zIndex: 50 }}>
          <div style={{
            animation: "slideOverlayIn 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards",
            padding: "20px 32px", borderRadius: 16,
            background: "rgba(6,6,11,0.92)",
            border: `2px solid ${COLORS.cyan}50`,
            backdropFilter: "blur(16px)",
            display: "flex", alignItems: "center", gap: 18,
            pointerEvents: "auto",
            boxShadow: `0 0 30px ${COLORS.cyan}25`,
          }}>
            <div style={{
              width: 64, height: 64, borderRadius: 16,
              background: `${COLORS.cyan}20`,
              border: `2.5px solid ${COLORS.cyan}60`,
              display: "flex", alignItems: "center", justifyContent: "center",
              overflow: "hidden",
              boxShadow: `0 0 20px ${COLORS.cyan}30`,
              flexShrink: 0,
            }}>
              {isImageAvatar(currentAlert.profile?.avatar)
                ? <img src={currentAlert.profile.avatar} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 12 }} />
                : <span style={{ fontSize: 32 }}>👤</span>}
            </div>
            <div>
              <div style={{
                fontSize: 22, fontWeight: 800, color: COLORS.cyan,
                fontFamily: "'Syne', sans-serif",
                letterSpacing: "-0.5px",
                marginBottom: 4,
              }}>New Follower!</div>
              <div style={{ fontSize: 18, color: COLORS.text, fontWeight: 700 }}>
                {currentAlert.profile?.name || "Anonymous"}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Claim Drop Leaderboard (always on while toggle enabled) */}
      <OverlayLeaderboard creatorAddress={creatorAddress} posStyle={posCSS(positions.leaderboard)} />

      {/* QR Code on Stream */}
      {overlayVis.qr && creatorAddress && (() => {
        const state = window.__fullsend_overlay_state || {};
        const qrDest = state.qrDestination || "tip";
        const qrUrls = {
          tip: `https://fullsend.live/tip/${creatorAddress}`,
          profile: `https://universalprofile.cloud/${creatorAddress}`,
          custom: state.customQrUrl || "",
        };
        const isUploaded = qrDest === "uploaded" && state.uploadedQrImage;
        const qrColors = { tip: "00ff88", profile: "c084fc", custom: "22d3ee" };
        const qrColor = qrColors[qrDest] || "00ff88";
        const qrSrc = isUploaded ? state.uploadedQrImage
          : `https://api.qrserver.com/v1/create-qr-code/?size=200x200&bgcolor=06060b&color=${qrColor}&data=${encodeURIComponent(qrUrls[qrDest] || qrUrls.tip)}`;
        const label = qrDest === "profile" ? "Scan for Profile" : qrDest === "custom" ? "Scan Me" : "Scan to Tip";
        return (
        <div style={{
          position: "absolute",
          ...((() => {
            const qrPos = positions.qr;
            if (qrPos && typeof qrPos === "object" && qrPos.x !== undefined) {
              return { left: `${qrPos.x}%`, top: `${qrPos.y}%` };
            }
            // Use posCSS for grid positions (string-based)
            if (typeof qrPos === "string") {
              return posCSS(qrPos);
            }
            return { bottom: 30, left: 30 };
          })()),
          zIndex: 55,
        }}>
          <div style={{
            animation: "slideOverlayIn 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards",
            pointerEvents: "auto",
          }}>
          <div style={{
            background: "#06060bf0", borderRadius: 12, padding: 8,
            boxShadow: "0 0 20px rgba(0,0,0,0.3)", border: `1px solid ${qrColor === "c084fc" ? "#c084fc30" : qrColor === "22d3ee" ? "#22d3ee30" : "#00ff8830"}`,
          }}>
            <img
              src={qrSrc}
              alt="QR"
              style={{ width: 120, height: 120, display: "block", borderRadius: 6, objectFit: "contain" }}
            />
            <div style={{ textAlign: "center", fontSize: 9, fontWeight: 700, color: `#${qrColor}`, marginTop: 4, fontFamily: "'JetBrains Mono', monospace" }}>
              {label}
            </div>
          </div>
          </div>
        </div>
        );
      })()}

      <style>{`
        @keyframes slideOverlayIn {
          from { opacity: 0; translate: 0 15px; scale: 0.95; }
          to { opacity: 1; translate: 0 0; scale: 1; }
        }
        @keyframes fadeOverlayOut {
          from { opacity: 1; translate: 0 0; }
          to { opacity: 0; translate: 0 -10px; }
        }
        @keyframes shrink { from { width: 100%; } to { width: 0%; } }
        @keyframes slideDown { from { opacity: 0; translate: 0 -20px; } to { opacity: 1; translate: 0 0; } }
        @keyframes fadeInUp { from { opacity: 0; } to { opacity: 1; } }
      `}</style>
    </div>
  );
}

// --- LEGAL PAGES (Terms of Service + Privacy Policy) ---------
const LEGAL_EFFECTIVE = "April 12, 2026";

const TERMS_CONTENT = [
  { heading: "1. What FullSend Is", body: "FullSend (the \"Service\") is a decentralized tipping and streaming-engagement platform operating on the LUKSO blockchain. FullSend is a non-custodial interface: we do not hold, custody, or control your funds at any time. All tips, follows, votes, and transactions occur directly on-chain between your Universal Profile and the recipient's Universal Profile through audited smart contracts. FullSend is operated by Metaheads Studios (\"we\", \"us\", or \"our\")." },
  { heading: "2. Eligibility", body: "You must be at least 18 years old and legally permitted to use cryptocurrency in your jurisdiction to use FullSend. By using the Service, you represent that you meet these requirements and that using FullSend does not violate any laws applicable to you. We do not provide services in jurisdictions where our operation would be unlawful, and it is your responsibility to ensure compliance with your local laws." },
  { heading: "3. Your Universal Profile & Wallet", body: "You are solely responsible for the security of your Universal Profile, private keys, and any associated wallet software. We cannot recover lost keys, reverse transactions, or freeze assets. All transactions on the LUKSO blockchain are irreversible. Before sending any tip or interacting with any contract, verify the recipient address and amount — we cannot undo a confirmed transaction." },
  { heading: "4. Platform Fees", body: "FullSend charges a flat fee of 0.50 LYX per tip transaction, collected by the TipRouter smart contract and routed to the platform treasury. This fee is disclosed before every tip and appears in the \"You pay\" breakdown on the widget. Follows, poll votes, and goal updates are free to users; gas for these actions is covered by the FullSend relayer. Fees may be adjusted in future versions of the contract; any change will be announced in advance and apply only to transactions made after the change." },
  { heading: "5. Gasless Transactions", body: "FullSend covers the network gas for free poll votes, token-gated poll votes, and claim drops. The relayer submits these on the relevant contracts (PollVoting and the creator's token) — it is never granted controller permission on your Universal Profile and cannot transfer LYX, call TipRouter, or take any action on your account beyond those submissions. Tips, LYX-gated poll votes, and follows always require your wallet confirmation, so the LYX flow is always authorized by you directly. The relayer has no ability to move LYX from your Universal Profile under any configuration." },
  { heading: "6. Creator Content & Conduct", body: "Creators who register on FullSend are responsible for the content of their tip pages, including channel names, bios, links, goals, and poll questions. Creators must not use FullSend to solicit payment for illegal goods or services, impersonate other people or projects, or engage in fraudulent activity. FullSend reserves the right to remove or block creator profiles that violate these terms or applicable law." },
  { heading: "7. Tipper Conduct", body: "Tippers must not use the messaging or polling features to harass creators or other users, distribute malware or phishing links, or include content that is unlawful in the creator's or tipper's jurisdiction. Creators have moderation controls and may block specific addresses from interacting with their page." },
  { heading: "8. No Financial Advice or Investment Solicitation", body: "FullSend is a utility platform for tipping creators. Nothing on FullSend constitutes financial advice, an offer to sell securities, or a solicitation to buy any asset. LYX is a utility token used to pay for transactions on the LUKSO network; its value may fluctuate, and we make no guarantees about its price or liquidity." },
  { heading: "9. Third-Party Services", body: "FullSend integrates third-party services, including but not limited to: LUKSO (blockchain infrastructure), LSP profile indexers (SigmaCore), fiat on-ramps (Transak, Ramp Network — when available), GIF provider (Klipy), transactional email (Resend), and hosting (Vercel, Neon). Your use of those services is governed by their own terms and privacy policies. We are not responsible for outages, data handling, or failures of third-party providers." },
  { heading: "10. Smart Contracts & Technical Risk", body: "FullSend interacts with smart contracts deployed on LUKSO. Smart contracts, while audited where possible, carry inherent risk including bugs, exploits, and unforeseen interactions. You acknowledge and accept these risks. Contract addresses are publicly verifiable on the LUKSO block explorer. We make no warranty that the Service will be uninterrupted, secure, or free from bugs." },
  { heading: "11. Intellectual Property", body: "FullSend, the FullSend logo, the Metaheads Studios brand, and all associated visual design, code not explicitly open-sourced, and written content are the property of Metaheads Studios. You may not copy, modify, or republish these without written permission. Open-source components we use are governed by their respective licenses." },
  { heading: "12. Disclaimer of Warranties", body: "THE SERVICE IS PROVIDED \"AS IS\" AND \"AS AVAILABLE\" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, TIMELY, SECURE, OR ERROR-FREE." },
  { heading: "13. Limitation of Liability", body: "TO THE MAXIMUM EXTENT PERMITTED BY LAW, METAHEADS STUDIOS AND ITS OPERATORS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, REVENUES, DATA, OR DIGITAL ASSETS, ARISING FROM YOUR USE OF FULLSEND. OUR TOTAL LIABILITY UNDER ANY CIRCUMSTANCE SHALL NOT EXCEED THE FEES YOU HAVE PAID TO FULLSEND IN THE PRIOR 12 MONTHS." },
  { heading: "14. Changes to These Terms", body: "We may update these Terms from time to time. The \"Effective Date\" at the top of this page reflects the most recent version. Material changes will be announced via the FullSend site. Continued use of the Service after changes constitutes acceptance of the new Terms." },
  { heading: "15. Contact", body: "Questions or legal notices should be directed to alerts@fullsend.live. For bug reports or support, please use the in-app feedback tools." },
];

const PRIVACY_CONTENT = [
  { heading: "1. Our Approach", body: "FullSend is a non-custodial platform built on the LUKSO blockchain. Most of what you do on FullSend — tipping, following, voting — is public on the blockchain by design, not by anything we added. This policy explains the minimal additional data we collect, why we collect it, and how it is used." },
  { heading: "2. Information You Provide", body: "When you register as a creator, you voluntarily provide information including: your Universal Profile address, creator display name, bio, social links (YouTube, Twitch, Kick, X, etc.), custom widget preferences, and any email address you provide for alert notifications. You can update or remove this information at any time through your Creator Settings." },
  { heading: "3. Information We Collect Automatically", body: "When you interact with FullSend, we automatically collect: transaction metadata (addresses, amounts, timestamps) from the LUKSO blockchain for display purposes, basic web analytics (page views per creator, aggregate counts — no cookies or cross-site tracking), IP-derived rate-limiting signals retained only briefly to prevent relayer abuse, and error logs (no personally identifying content). We do not use third-party advertising cookies or behavioral tracking." },
  { heading: "4. Public Blockchain Data", body: "Your Universal Profile, tips, follows, votes, and on-chain activity are publicly visible by design on the LUKSO network. Anyone — not just FullSend — can read this data. FullSend displays this public data on creator pages, leaderboards, and overlays. We do not control or own this data; it exists independently on the blockchain." },
  { heading: "5. How We Use Information", body: "We use the information described above to: operate the Service (display tips, process votes, fire alerts), prevent spam and abuse (rate limiting, anti-exploit monitoring), communicate with creators (opt-in tip alert emails via Resend), improve the product based on aggregate analytics, and respond to support or legal inquiries. We do not sell personal information. We do not use your data for targeted advertising." },
  { heading: "6. Information Sharing", body: "We share information only as follows: (a) with service providers that help us operate FullSend — hosting (Vercel), database (Neon Postgres), email delivery (Resend), blockchain indexing (SigmaCore), fiat on-ramps (Transak, Ramp when live); (b) to comply with law, valid legal process, or to protect the rights, property, or safety of FullSend, its users, or the public; (c) in connection with a business transfer (merger, acquisition), with notice to users." },
  { heading: "7. Fiat On-Ramp Partners", body: "If you choose to purchase LYX through an integrated fiat on-ramp (such as Transak or Ramp Network), you interact directly with that provider, and their KYC, payment, and data practices apply. FullSend receives only aggregate transaction metadata from these partners (for affiliate tracking and analytics), never your payment card or banking details." },
  { heading: "8. Data Retention", body: "Off-chain data (creator profiles, analytics, email alert preferences) is retained while your account is active. You may request deletion of off-chain data associated with your Universal Profile by contacting us. On-chain data cannot be deleted by us or by you — it is permanent and public on the LUKSO network." },
  { heading: "9. Security", body: "We apply standard security practices including HTTPS encryption, hardened serverless infrastructure, scoped relayer permissions (the FullSend relayer cannot move your funds), and regular audits. No system is perfectly secure. In the event of a breach affecting user data, we will notify affected users in accordance with applicable law." },
  { heading: "10. Children", body: "FullSend is not directed to children under 18. We do not knowingly collect information from anyone under 18. If you believe a minor has provided information to us, contact alerts@fullsend.live and we will remove it." },
  { heading: "11. International Users", body: "FullSend is operated from the United States. If you access FullSend from outside the United States, your information may be transferred to, stored, and processed in the United States and other jurisdictions where our service providers operate. By using the Service you consent to such transfer." },
  { heading: "12. Your Rights", body: "Depending on your jurisdiction (GDPR, CCPA, or other), you may have the right to: access the personal data we hold about you, correct inaccurate data, request deletion of off-chain data, opt out of marketing communications (we send very few), and lodge a complaint with your local data protection authority. To exercise these rights, contact alerts@fullsend.live." },
  { heading: "13. Changes to This Policy", body: "We may update this policy. The \"Effective Date\" at the top reflects the latest version. Material changes will be announced through the FullSend site and, where appropriate, by email to affected creators." },
  { heading: "14. Contact", body: "Questions about this policy or your data: alerts@fullsend.live." },
];

function LegalPage({ type }) {
  const isTerms = type === "terms";
  const title = isTerms ? "Terms of Service" : "Privacy Policy";
  const content = isTerms ? TERMS_CONTENT : PRIVACY_CONTENT;
  const accent = isTerms ? COLORS.accent : COLORS.cyan;

  return (
    <div style={{
      minHeight: "100vh",
      background: COLORS.bg,
      color: COLORS.text,
      fontFamily: "'Outfit', sans-serif",
    }}>
      <link href="https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@300;400;500;600;700;800&family=Outfit:wght@300;400;500;600;700;800;900&family=Syne:wght@400;500;600;700;800&display=swap" rel="stylesheet" />

      {/* Sticky top bar */}
      <div style={{
        position: "sticky", top: 0, zIndex: 10,
        background: `${COLORS.bg}dd`, backdropFilter: "blur(12px)",
        borderBottom: `1px solid ${COLORS.border}`,
        padding: "14px 24px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <a href="/" style={{
          fontFamily: "'Syne', sans-serif",
          fontSize: 16, fontWeight: 800,
          color: COLORS.text, textDecoration: "none",
          display: "flex", alignItems: "center", gap: 8,
        }}>
          <span style={{
            width: 26, height: 26, borderRadius: 6,
            background: `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.cyan})`,
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            fontFamily: "'Syne', sans-serif", fontWeight: 900, fontSize: 14,
            color: COLORS.bg,
          }}>F</span>
          FullSend
        </a>
        <div style={{ display: "flex", gap: 18 }}>
          <a href="/terms" style={{
            fontSize: 11, fontWeight: 600,
            color: isTerms ? COLORS.accent : COLORS.textMuted,
            textDecoration: "none",
            fontFamily: "'JetBrains Mono', monospace",
            letterSpacing: "0.5px", textTransform: "uppercase",
          }}>Terms</a>
          <a href="/privacy" style={{
            fontSize: 11, fontWeight: 600,
            color: !isTerms ? COLORS.cyan : COLORS.textMuted,
            textDecoration: "none",
            fontFamily: "'JetBrains Mono', monospace",
            letterSpacing: "0.5px", textTransform: "uppercase",
          }}>Privacy</a>
        </div>
      </div>

      <div style={{
        maxWidth: 820, margin: "0 auto",
        padding: "56px 24px 80px",
      }}>
        {/* Header */}
        <div style={{ marginBottom: 44, position: "relative" }}>
          <div style={{
            position: "absolute", top: -18, left: 0,
            fontSize: 10, fontWeight: 800,
            color: accent,
            fontFamily: "'JetBrains Mono', monospace",
            letterSpacing: "3px", textTransform: "uppercase",
          }}>{isTerms ? "01 · Legal" : "02 · Legal"}</div>
          <h1 style={{
            fontFamily: "'Syne', sans-serif",
            fontSize: 52, fontWeight: 800,
            margin: 0,
            lineHeight: 1.05,
            letterSpacing: "-1.5px",
            background: `linear-gradient(135deg, ${COLORS.text}, ${accent})`,
            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
          }}>{title}</h1>
          <div style={{
            marginTop: 14, fontSize: 11,
            color: COLORS.textMuted,
            fontFamily: "'JetBrains Mono', monospace",
            letterSpacing: "1.5px", textTransform: "uppercase",
          }}>Effective: {LEGAL_EFFECTIVE} · Metaheads Studios</div>
          <div style={{
            marginTop: 20, height: 1,
            background: `linear-gradient(90deg, ${accent}60, transparent)`,
          }} />
        </div>

        {/* Intro prose (terms only) */}
        {isTerms && (
          <div style={{
            padding: "20px 22px", marginBottom: 32,
            borderRadius: 10,
            background: `linear-gradient(135deg, ${COLORS.surface}, ${accent}06)`,
            border: `1px solid ${accent}18`,
            fontSize: 14, lineHeight: 1.7,
            color: COLORS.textSecondary,
          }}>
            These Terms of Service (the <strong style={{ color: COLORS.text }}>"Terms"</strong>) govern your access to and use of FullSend (fullsend.live, the <strong style={{ color: COLORS.text }}>"Service"</strong>). By accessing or using FullSend, you agree to these Terms. If you do not agree, do not use the Service.
          </div>
        )}
        {!isTerms && (
          <div style={{
            padding: "20px 22px", marginBottom: 32,
            borderRadius: 10,
            background: `linear-gradient(135deg, ${COLORS.surface}, ${accent}06)`,
            border: `1px solid ${accent}18`,
            fontSize: 14, lineHeight: 1.7,
            color: COLORS.textSecondary,
          }}>
            This Privacy Policy describes what information FullSend collects, how we use it, and your rights. FullSend is built on public blockchain infrastructure — much of your activity is public by design. This policy covers what we do with the small amount of off-chain data we hold.
          </div>
        )}

        {/* Sections */}
        <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>
          {content.map((s, i) => (
            <div key={i}>
              <h2 style={{
                fontFamily: "'Syne', sans-serif",
                fontSize: 18, fontWeight: 700,
                margin: "0 0 10px 0",
                color: COLORS.text,
                letterSpacing: "-0.3px",
              }}>{s.heading}</h2>
              <p style={{
                fontSize: 13, lineHeight: 1.75,
                color: COLORS.textSecondary,
                margin: 0,
              }}>{s.body}</p>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div style={{
          marginTop: 56, paddingTop: 24,
          borderTop: `1px solid ${COLORS.border}`,
          display: "flex", justifyContent: "space-between", alignItems: "center",
          flexWrap: "wrap", gap: 12,
        }}>
          <div style={{
            fontSize: 10, color: COLORS.textMuted,
            fontFamily: "'JetBrains Mono', monospace",
            letterSpacing: "1.2px", textTransform: "uppercase",
          }}>© Metaheads Studios · FullSend</div>
          <a href={isTerms ? "/privacy" : "/terms"} style={{
            fontSize: 11, fontWeight: 700,
            color: isTerms ? COLORS.cyan : COLORS.accent,
            textDecoration: "none",
            fontFamily: "'JetBrains Mono', monospace",
            letterSpacing: "1px", textTransform: "uppercase",
            padding: "6px 14px", borderRadius: 5,
            background: `${isTerms ? COLORS.cyan : COLORS.accent}10`,
            border: `1px solid ${isTerms ? COLORS.cyan : COLORS.accent}25`,
          }}>{isTerms ? "Privacy Policy →" : "Terms of Service →"}</a>
        </div>
      </div>
    </div>
  );
}

// --- FULLSEND APP --------------------------------------------
export default function FullSendApp() {
  const path = window.location.pathname;
  const tipMatch = path.match(/^\/tip\/(.+)$/);
  const overlayMatch = path.match(/^\/overlay\/(.+)$/);
  const isOverlay = overlayMatch && /^0x[a-fA-F0-9]{40}$/.test(overlayMatch[1]);

  let content;
  if (tipMatch && /^0x[a-fA-F0-9]{40}$/.test(tipMatch[1])) {
    content = <TipPage creatorAddress={tipMatch[1]} />;
  } else if (isOverlay) {
    content = <StandaloneOverlay creatorAddress={overlayMatch[1]} />;
  } else if (path === "/admin" || path === "/admin/") {
    content = <AdminDashboard />;
  } else if (path === "/terms" || path === "/terms/") {
    content = <LegalPage type="terms" />;
  } else if (path === "/privacy" || path === "/privacy/") {
    content = <LegalPage type="privacy" />;
  } else {
    content = <FullSendMain />;
  }

  // Vercel Web Analytics — track human-facing routes; skip the OBS overlay source.
  return <>{content}{!isOverlay && <Analytics />}</>;
}

function FullSendMain() {
  // Load settings from localStorage into window global at startup
  // This ensures TipWidget can read goal/theme data even before CreatorSettings mounts
  if (!window.__fullsend_settings_data) {
    try {
      window.__fullsend_settings_data = JSON.parse(localStorage.getItem("fullsend_settings") || "{}");
    } catch { window.__fullsend_settings_data = {}; }
  }
  // Tier schema migration: force fresh non-overlapping ranges if stored tiers don't match.
  // Runs globally at load so every reader (Control, overlay, widget, settings) sees the new defaults.
  (() => {
    const sd = window.__fullsend_settings_data;
    const ct = sd?.customTiers;
    if (!ct) return;
    const supMin = parseFloat(ct.supporter?.min);
    const chaMin = parseFloat(ct.champion?.min);
    const legMin = parseFloat(ct.legend?.min);
    const whaMin = parseFloat(ct.whale?.min);
    if (supMin !== 5 || chaMin !== 20 || legMin !== 50 || whaMin !== 100) {
      sd.customTiers = {
        micro: { min: 0, max: 4.99 },
        supporter: { min: 5, max: 19.99 },
        champion: { min: 20, max: 49.99 },
        legend: { min: 50, max: 99.99 },
        whale: { min: 100, max: null },
      };
      try {
        const raw = JSON.parse(localStorage.getItem("fullsend_settings") || "{}");
        raw.customTiers = sd.customTiers;
        localStorage.setItem("fullsend_settings", JSON.stringify(raw));
      } catch {}
    }
  })();
  // Also restore TTS, volume, sound toggles, and selected sounds globals from localStorage
  // so they persist across page reloads (TTS toggle was reverting because of this)
  if (!window.__fullsend_tts) {
    const sd = window.__fullsend_settings_data || {};
    window.__fullsend_tts = sd.tts || { enabled: sd.ttsEnabled || false, voice: sd.ttsVoice || "", minAmount: sd.ttsMinAmount || 5, rate: sd.ttsRate || 1, pitch: sd.ttsPitch || 1, tiers: sd.ttsTiers || { micro: false, supporter: false, champion: true, legend: true, whale: true } };
  }
  if (window.__fullsend_volume === undefined) {
    window.__fullsend_volume = window.__fullsend_settings_data?.masterVolume ?? 0.7;
  }
  if (!window.__fullsend_sound_toggles) {
    window.__fullsend_sound_toggles = window.__fullsend_settings_data?.soundToggles || { tips: true, follows: true, polls: true };
  }
  if (!window.__fullsend_selected_sounds) {
    window.__fullsend_selected_sounds = window.__fullsend_settings_data?.selectedSound || {};
  }

  const [mode, setMode] = useState("creator"); // "creator" | "tipper"
  const [view, setView] = useState("home");
  const [prevView, setPrevView] = useState(null);
  const [transitioning, setTransitioning] = useState(false);
  const [connected, setConnected] = useState(false);
  const [upProfile, setUpProfile] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [lang, setLang] = useState("en");
  const [showLangMenu, setShowLangMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [mobileBannerDismissed, setMobileBannerDismissed] = useState(() => {
    try { return localStorage.getItem("fullsend_mobile_banner") === "dismissed"; } catch { return false; }
  });

  const LANGS = {
    en: { flag: "🇬🇧", label: "English" },
    de: { flag: "🇩🇪", label: "Deutsch" },
    fr: { flag: "🇫🇷", label: "Français" },
    es: { flag: "🇪🇸", label: "Español" },
    pt: { flag: "🇧🇷", label: "Português" },
    tr: { flag: "🇹🇷", label: "Türkçe" },
    zh: { flag: "🇨🇳", label: "中文" },
  };

  const T = {
    en: { myChannel: "My Channel", myProfile: "My Profile", about: "About", connectUP: "Connect", sendTip: "Send a tip", follow: "Follow", following: "Following", followers: "followers", liveNow: "Live Now", tipGoal: "Tip Goal", votes: "votes", noUP: "Don't have a Universal Profile? Create one here", settings: "Settings", analytics: "Analytics", overlay: "Overlay", polls: "Polls", control: "Control" },
    de: { myChannel: "Mein Kanal", myProfile: "Mein Profil", about: "Über", connectUP: "UP verbinden", sendTip: "Tipp senden", follow: "Folgen", following: "Folge ich", followers: "Follower", liveNow: "Jetzt Live", tipGoal: "Trinkgeld-Ziel", votes: "Stimmen", noUP: "Kein Universal Profile? Erstelle hier eins", settings: "Einstellungen", analytics: "Analytik", overlay: "Overlay", polls: "Umfragen", control: "Steuerung" },
    fr: { myChannel: "Ma Chaîne", myProfile: "Mon Profil", about: "À propos", connectUP: "Connecter UP", sendTip: "Envoyer un tip", follow: "Suivre", following: "Abonné", followers: "abonnés", liveNow: "En Direct", tipGoal: "Objectif Tips", votes: "votes", noUP: "Pas de Universal Profile ? Créez-en un ici", settings: "Paramètres", analytics: "Analytique", overlay: "Overlay", polls: "Sondages", control: "Contrôle" },
    es: { myChannel: "Mi Canal", myProfile: "Mi Perfil", about: "Acerca de", connectUP: "Conectar UP", sendTip: "Enviar propina", follow: "Seguir", following: "Siguiendo", followers: "seguidores", liveNow: "En Vivo", tipGoal: "Meta de Propinas", votes: "votos", noUP: "No tienes un Universal Profile? Crea uno aquí", settings: "Configuración", analytics: "Análisis", overlay: "Overlay", polls: "Encuestas", control: "Control" },
    pt: { myChannel: "Meu Canal", myProfile: "Meu Perfil", about: "Sobre", connectUP: "Conectar UP", sendTip: "Enviar gorjeta", follow: "Seguir", following: "Seguindo", followers: "seguidores", liveNow: "Ao Vivo", tipGoal: "Meta de Gorjetas", votes: "votos", noUP: "Não tem um Universal Profile? Crie um aqui", settings: "Configurações", analytics: "Análise", overlay: "Overlay", polls: "Enquetes", control: "Controle" },
    tr: { myChannel: "Kanalım", myProfile: "Profilim", about: "Hakkında", connectUP: "UP Bağla", sendTip: "Bahşiş Gönder", follow: "Takip Et", following: "Takip Ediliyor", followers: "takipçi", liveNow: "Canlı", tipGoal: "Bahşiş Hedefi", votes: "oy", noUP: "Universal Profile yok mu? Buradan oluşturun", settings: "Ayarlar", analytics: "Analitik", overlay: "Overlay", polls: "Anketler", control: "Kontrol" },
    zh: { myChannel: "我的频道", myProfile: "我的资料", about: "关于", connectUP: "连接 UP", sendTip: "发送打赏", follow: "关注", following: "已关注", followers: "粉丝", liveNow: "直播中", tipGoal: "打赏目标", votes: "票", noUP: "没有 Universal Profile？在这里创建一个", settings: "设置", analytics: "分析", overlay: "Overlay", polls: "投票", control: "控制" },
  };

  const t = T[lang] || T.en;
  const [notifications, setNotifications] = useState([
    // Notifications populated from contract events (needs indexer for production)
  ]);
  const [showNotifications, setShowNotifications] = useState(false);
  const unreadCount = notifications.filter(n => !n.read).length;

  // Listen for tip and follow events to add notifications
  useEffect(() => {
    const handleTip = (e) => {
      // Skip tips the local user SENT — fullsend-tip is dispatched in the
      // sender's own browser on send, so without this the sender sees a
      // "Someone tipped you" notification for their own outgoing tip.
      if (e.detail?.outgoing) return;
      setNotifications(prev => [{
        id: Date.now(), type: "tip",
        text: `${e.detail.from?.name || "Someone"} tipped ${e.detail.amount} LYX`,
        time: "just now", read: false,
      }, ...prev].slice(0, 20));
    };
    const handleFollow = (e) => {
      setNotifications(prev => [{
        id: Date.now(), type: "follow",
        text: `${e.detail.profile?.name || "Someone"} followed you`,
        time: "just now", read: false,
      }, ...prev].slice(0, 20));
    };
    window.addEventListener("fullsend-tip", handleTip);
    window.addEventListener("fullsend-sub", handleFollow);
    return () => {
      window.removeEventListener("fullsend-tip", handleTip);
      window.removeEventListener("fullsend-sub", handleFollow);
    };
  }, []);

  // Load feedback/partnership submissions into notifications (admin only)
  useEffect(() => {
    if (!ADMIN_WHITELIST.includes(upProfile?.address)) return;
    fetch("/api/submissions")
      .then(r => r.json())
      .then(data => {
        // Track which IDs we've already seen (persist across sessions)
        let seenIds = [];
        try { seenIds = JSON.parse(localStorage.getItem("fullsend_seen_notis") || "[]"); } catch {}
        
        const newBugs = (data.bugs || []).filter(b => b.status === "open").slice(0, 5).map(b => ({
          id: "bug-" + b.id, type: "bug",
          text: `🐛 Bug report: ${b.section || "General"}`,
          time: b.created_at ? new Date(b.created_at).toLocaleDateString() : "recently",
          read: seenIds.includes("bug-" + b.id),
        }));
        const newIdeas = (data.ideas || []).slice(0, 5).map(s => ({
          id: "idea-" + s.id, type: "idea",
          text: `💡 New idea from ${s.name || "Anonymous"}`,
          time: s.created_at ? new Date(s.created_at).toLocaleDateString() : "recently",
          read: seenIds.includes("idea-" + s.id),
        }));
        const newPartners = (data.partnerships || []).slice(0, 5).map(s => ({
          id: "partner-" + s.id, type: "partnership",
          text: `🤝 Partnership inquiry from ${s.name || "Anonymous"}`,
          time: s.created_at ? new Date(s.created_at).toLocaleDateString() : "recently",
          read: seenIds.includes("partner-" + s.id),
        }));
        const msgs = [...newBugs, ...newIdeas, ...newPartners];
        if (msgs.length > 0) {
          // Replace notifications (don't stack duplicates)
          setNotifications(msgs);
        }
      })
      .catch(() => {});
  }, [upProfile]);

  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showBugReport, setShowBugReport] = useState(false);

  const connectUP = async () => {
    setConnecting(true);
    try {
      // LUKSO Grid mini-app: connect via up-provider ONLY. WalletConnect/extension
      // cannot run inside a Grid iframe (it freezes the app), so when embedded we
      // connect through the host's up-provider and never fall through.
      if (isEmbeddedGrid()) {
        try {
          const gp = getGridProvider();
          if (!gp) throw new Error("Grid provider unavailable");
          let accounts = await gp.request({ method: "eth_requestAccounts" });
          if ((!accounts || !accounts.length) && gp.accounts && gp.accounts.length) accounts = gp.accounts;
          if (!accounts || !accounts.length) throw new Error("No account from Grid host");
          const address = accounts[0];
          const ctx = (gp.contextAccounts && gp.contextAccounts[0]) || null;
          if (ctx) window.__fullsend_context_account = ctx;
          const prof = await fetchUPProfile(address).catch(() => null);
          const balanceLYX = await rpcBalanceLYX(address, 1);
          const profileData = {
            name: (prof && prof.name) || `${address.slice(0, 6)}...${address.slice(-4)}`,
            address,
            avatar: (prof && prof.avatar) || STOCK_AVATAR,
            balance: balanceLYX,
            bio: "",
            links: {},
            isUP: true,
            providerType: "upprovider",
          };
          setUpProfile(profileData);
          window.__fullsend_provider = gp;
          window.__fullsend_profile = profileData;
          setConnected(true);
          // Rebuild the settings blob for THIS address first (same as the desktop
          // path below) so a previous login's channel identity can't leak onto the
          // newly-connected UP's directory row via syncCreatorToDB.
          await loadCreatorSettingsForAddress(address);
          syncCreatorToDB();
        } catch (gridErr) {
          console.log("Grid up-provider connect failed:", gridErr && gridErr.message);
        }
        setConnecting(false);
        return; // embedded = up-provider only — never WalletConnect (freezes in an iframe)
      }
      // Check for UP Browser Extension first, then MetaMask, then WalletConnect
      let provider = null;
      let providerType = null;
      if (window.lukso) {
        provider = window.lukso;
        providerType = "up";
      } else if (window.ethereum) {
        provider = window.ethereum;
        providerType = "metamask";
      } else {
        if (isMobileDevice()) {
          // Mobile — use WalletConnect to connect UP app or MetaMask
          try {
            wcDebug("mobile detected → creating WC provider…");
            provider = await createWCProvider();
            wcDebug("provider created (session=" + (provider?.session ? "EXISTS" : "none") + ")");
            // A stale session in storage makes connect() resolve instantly with
            // empty accounts, and the follow-up request then fails. Clear any
            // pre-existing session so we always do a clean pairing → real
            // approval → valid accounts. Timeout-guarded so a dead session
            // can't hang the disconnect.
            if (provider.session) {
              wcDebug("clearing stale session before connect…");
              try {
                await Promise.race([
                  provider.disconnect(),
                  new Promise((_, rej) => setTimeout(() => rej(new Error("disconnect timeout")), 4000)),
                ]);
                wcDebug("stale session cleared");
              } catch (de) { wcDebug("disconnect err/timeout: " + ((de && de.message) || de)); }
            }
            wcDebug("calling connect()…");
            await provider.connect();
            wcDebug("connect() RESOLVED ✓ chainId=" + provider?.chainId + " accounts=" + JSON.stringify(provider?.accounts || []) + " ns=" + JSON.stringify(Object.keys(provider?.session?.namespaces || {})));
            providerType = "walletconnect";
          } catch (wcErr) {
            const m = (wcErr && wcErr.message) || String(wcErr || "");
            wcDebug("connect() THREW ✗ : " + m.slice(0, 220));
            console.log("WalletConnect cancelled or failed:", m);
            setConnecting(false);
            // Surface the real reason — a silent return was indistinguishable
            // from "nothing happens" and made this impossible to diagnose.
            const cancelled = /reject|cancel|closed|reset by the user|abort/i.test(m) || wcErr?.code === 4001;
            if (!cancelled) {
              alert("Couldn't open the wallet connection.\n\n" + m.slice(0, 180) +
                "\n\nTip: open fullsend.live in Safari or Chrome (not an in-app browser), and make sure the Universal Profile mobile app is installed.");
            }
            return;
          }
        } else {
          alert("No wallet found. Install the UP Browser Extension or MetaMask.");
          setConnecting(false);
          return;
        }
      }

      // Request account access. For WalletConnect, provider.accounts and the
      // session namespaces can both be empty even with a valid settled session,
      // so try multiple sources, then RPC, then dump the raw session.
      let accounts;
      if (providerType === "walletconnect") {
        accounts = (provider.accounts && provider.accounts.length) ? provider.accounts.slice() : [];
        if (!accounts.length && provider.session && provider.session.namespaces) {
          for (const ns of Object.values(provider.session.namespaces)) {
            if (ns && ns.accounts && ns.accounts.length) {
              accounts = ns.accounts.map((a) => { const p = String(a).split(":"); return p[p.length - 1]; });
              break;
            }
          }
        }
        if (!accounts.length) {
          try { const r = await provider.request({ method: "eth_accounts" }); wcDebug("eth_accounts → " + JSON.stringify(r)); if (r && r.length) accounts = r; } catch (e) { wcDebug("eth_accounts err: " + ((e && e.message) || e)); }
        }
        if (!accounts.length) {
          try { const r = await provider.request({ method: "eth_requestAccounts" }); wcDebug("eth_requestAccounts → " + JSON.stringify(r)); if (r && r.length) accounts = r; } catch (e) { wcDebug("eth_requestAccounts err: " + ((e && e.message) || e)); }
        }
        if (!accounts.length) {
          try { wcDebug("RAW session: " + JSON.stringify(provider.session).slice(0, 700)); } catch (e) {}
        }
        wcDebug("WC accounts final: " + JSON.stringify(accounts));
      } else {
        accounts = await provider.request({ method: "eth_requestAccounts" });
      }
      if (!accounts || accounts.length === 0) { wcDebug("NO ACCOUNTS → bailing"); setConnecting(false); return; }
      const address = accounts[0];

      // Get LYX balance
      const balanceHex = await provider.request({ method: "eth_getBalance", params: [address, "latest"] });
      const balanceLYX = (Number(BigInt(balanceHex)) / 1e18).toFixed(1);

      // Default profile (EOA / MetaMask)
      let profileData = {
        name: `${address.slice(0, 6)}...${address.slice(-4)}`,
        avatar: STOCK_AVATAR,
        bio: "",
        links: {},
        isUP: false,
      };

      // UPs are smart-contract accounts; EOAs have no code. eth_getCode is a
      // reliable isUP signal independent of LSP3 metadata (which can fail to
      // fetch over WalletConnect on mobile — leaving real UPs flagged as EOAs).
      if (!profileData.isUP && (providerType === "walletconnect" || providerType === "up")) {
        try {
          const code = await provider.request({ method: "eth_getCode", params: [address, "latest"] });
          if (code && code !== "0x" && code !== "0x0") profileData.isUP = true;
        } catch (_) {}
      }

      // If UP extension or WalletConnect, try fetching LSP3 metadata
      if (providerType === "up" || providerType === "walletconnect") {
        try {
          const LSP3_KEY = "5ef83ad9559033e6e941db7d7c495acdce616347d28e90c7ce47cbfcfcad3bc5";
          const result = await provider.request({
            method: "eth_call",
            params: [{ to: address, data: "0x54f6127f" + LSP3_KEY }, "latest"],
          });
          if (result && result !== "0x" && result.length > 130) {
            // Decode ABI-encoded bytes to get IPFS/URL
            const data = result.slice(2);
            const offset = parseInt(data.slice(0, 64), 16) * 2;
            const length = parseInt(data.slice(offset, offset + 64), 16);
            const contentHex = data.slice(offset + 64, offset + 64 + length * 2);
            let raw = "";
            for (let i = 0; i < contentHex.length; i += 2) {
              raw += String.fromCharCode(parseInt(contentHex.slice(i, i + 2), 16));
            }
            // Find the URL in the raw bytes (after hash bytes)
            const ipfsIdx = raw.indexOf("ipfs://");
            const httpsIdx = raw.indexOf("https://");
            const urlStart = ipfsIdx > -1 ? ipfsIdx : httpsIdx;
            if (urlStart > -1) {
              let url = raw.slice(urlStart);
              if (url.startsWith("ipfs://")) url = "https://api.universalprofile.cloud/ipfs/" + url.slice(7);
              const resp = await fetch(url);
              const meta = await resp.json();
              const p = meta?.LSP3Profile || meta;
              if (p.name) profileData.name = p.name;
              if (p.description) profileData.bio = p.description;
              profileData.isUP = true;
              // Avatar
              const img = p.profileImage?.[0]?.url || p.profileImage?.[0]?.src;
              if (img) {
                profileData.avatar = img.startsWith("ipfs://")
                  ? "https://api.universalprofile.cloud/ipfs/" + img.slice(7)
                  : img;
              }
              // Social links
              if (p.links) {
                const links = {};
                for (const link of p.links) {
                  const u = link.url || "";
                  if (u.includes("youtube.com") || u.includes("youtu.be")) links.youtube = u;
                  else if (u.includes("twitch.tv")) links.twitch = u;
                  else if (u.includes("kick.com")) links.kick = u;
                  else if (u.includes("rumble.com")) links.rumble = u;
                  else if (u.includes("twitter.com") || u.includes("x.com")) links.x = u;
                }
                profileData.links = links;
              }
            }
          }
        } catch (err) {
          console.log("LSP3 fetch failed (non-fatal):", err);
        }
      }

      setUpProfile({
        name: profileData.name,
        address: address,
        avatar: profileData.avatar,
        balance: balanceLYX,
        bio: profileData.bio,
        links: profileData.links,
        isUP: profileData.isUP,
        providerType: providerType,
      });
      // Store provider globally so TipWidget can use it for on-chain transactions
      window.__fullsend_provider = provider;
      window.__fullsend_profile = {
        name: profileData.name,
        address: address,
        avatar: profileData.avatar,
        balance: balanceLYX,
        bio: profileData.bio,
        links: profileData.links,
        isUP: profileData.isUP,
        providerType: providerType,
      };
      setConnected(true);
      setConnecting(false);
      // Only show onboarding on first connect (not seen before), desktop only —
      // the wizard is a creator-setup flow and the mobile site is view/tip oriented.
      if (!localStorage.getItem("fullsend_onboarded") && !isMobileDevice()) {
        setShowOnboarding(true);
      }

      // Load THIS creator's settings from DB first so the previous account's
      // channel identity doesn't linger in the widget preview or get written
      // into this creator's directory record by the sync below.
      await loadCreatorSettingsForAddress(address);
      // Auto-register in creator directory (silent, non-blocking)
      syncCreatorToDB();
    } catch (err) {
      console.error("Connection failed:", err);
      console.error("Error stack:", err.stack);
      console.error("Error name:", err.name, "message:", err.message, "code:", err.code);
      setConnecting(false);
      if (err.code !== 4001) alert("Connection failed. Please try again. Error: " + (err.message || "unknown").slice(0, 100));
    }
  };

  const disconnectUP = () => {
    setConnected(false);
    setUpProfile(null);
    window.__fullsend_provider = null;
    window.__fullsend_profile = null;
  };

  // Expose the connect flow globally so surfaces outside this component — e.g.
  // the footer "Tip FullSend" button on mobile, where the nav Connect is hidden
  // by the mobile stopgap — can trigger a WalletConnect session before sending.
  useEffect(() => {
    window.__fullsend_connect = connectUP;
    return () => { if (window.__fullsend_connect === connectUP) window.__fullsend_connect = null; };
  });

  const navigate = useCallback((newView) => {
    if (newView === view) return;
    setTransitioning(true);
    setPrevView(view);
    // Push to browser history so back button works
    window.history.pushState({ view: newView, mode }, "", window.location.pathname);
    setTimeout(() => {
      setView(newView);
      setTransitioning(false);
      window.scrollTo(0, 0);
    }, 200);
  }, [view, mode]);

  // Expose navigation to deep, prop-less components (e.g. the My NFTs drop card's
  // "Settings" button jumps to creator > Control where the drop is configured).
  useEffect(() => {
    window.__fullsend_go = (m, v) => { if (m) setMode(m); navigate(v); };
  }, [navigate]);

  // Handle browser back button — go to previous view or home
  useEffect(() => {
    const handlePopState = (e) => {
      if (e.state?.view) {
        setView(e.state.view);
        if (e.state.mode) setMode(e.state.mode);
      } else {
        setView("home");
      }
    };
    // Push initial state
    window.history.replaceState({ view, mode }, "", window.location.pathname);
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  // Listen for programmatic navigation from child components
  useEffect(() => {
    const handler = (e) => { if (e.detail) navigate(e.detail); };
    window.addEventListener("fullsend-navigate", handler);
    return () => window.removeEventListener("fullsend-navigate", handler);
  }, [navigate]);

  const switchMode = useCallback((newMode) => {
    if (newMode === mode) {
      // Already in this mode — always go back to hub
      setTransitioning(true);
      window.history.pushState({ view: "home", mode }, "", window.location.pathname);
      setTimeout(() => {
        setView("home");
        setTransitioning(false);
        window.scrollTo(0, 0);
      }, 250);
      return;
    }
    setTransitioning(true);
    window.history.pushState({ view: "home", mode: newMode }, "", window.location.pathname);
    setTimeout(() => {
      setMode(newMode);
      setView("home");
      setTransitioning(false);
      window.scrollTo(0, 0);
    }, 250);
  }, [mode, view]);

  const creatorTabs = [
    { id: "widget", label: "Tip", icon: "💰" },
    { id: "control", label: t.control, icon: "🎮", desktopOnly: true },
    { id: "overlay", label: t.overlay, icon: "📺", desktopOnly: true },
    { id: "polls", label: t.polls, icon: "📊", desktopOnly: true },
    { id: "goals", label: "Goals", icon: "🎯", desktopOnly: true },
    { id: "moc", label: "Moments", icon: "📸", desktopOnly: true },
    { id: "chat", label: "Chat", icon: "💬", desktopOnly: true },
    { id: "leaderboard", label: "Board", icon: "🏆" },
    { id: "nfts", label: "NFTs", icon: "💎" },
    { id: "analytics", label: t.analytics, icon: "📈", desktopOnly: true },
    { id: "settings", label: t.settings, icon: "⚙️", desktopOnly: true },
  ];

  const tipperTabs = [
    { id: "rankings", label: "Rankings", icon: "📊" },
    { id: "nfts-profile", label: "My NFTs", icon: "💎" },
    { id: "activity", label: "Activity", icon: "⚡" },
    { id: "tipper-analytics", label: "My Stats", icon: "📈" },
    { id: "notifications", label: "Notifications", icon: "🔔" },
    { id: "discover", label: "Discover", icon: "🔍" },
  ];

  const isMobile = isMobileDevice();
  // Tab visibility keys off the actual device, not window width — a narrowed
  // desktop window (innerWidth <= 768) shouldn't strip the creator's dashboard
  // tabs. The tab strip is overflowX:auto, so a full set just scrolls on narrow.
  const isTouchMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const tabs = mode === "creator" ? (isTouchMobile ? creatorTabs.filter(t => !t.desktopOnly) : creatorTabs) : tipperTabs;

  return (
    <div style={{
      background: COLORS.bg,
      minHeight: "100vh",
      fontFamily: "'JetBrains Mono', 'SF Mono', 'Fira Code', monospace",
      color: COLORS.text,
    }}>
      <style>{GLOBAL_STYLES}</style>
      <link href="https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@300;400;500;600;700;800&family=Outfit:wght@300;400;500;600;700;800;900&family=Syne:wght@400;500;600;700;800&display=swap" rel="stylesheet" />

      {/* NAV */}
      <nav style={{
        display: "flex",
        flexDirection: "column",
        borderBottom: `1px solid ${COLORS.border}`,
        background: `${COLORS.bg}e8`,
        backdropFilter: "blur(24px) saturate(1.5)",
        position: "sticky",
        top: 0,
        zIndex: 100,
        animation: "navSlideDown 0.5s ease-out",
      }}>
        {/* Top row: Logo, Mode Toggle, About, Lang, Bell, Connect */}
        <div style={{
          display: "flex", alignItems: "center",
          padding: "10px 20px", gap: 12,
        }}>
        {/* Logo -- fixed left */}
        <div
          style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", flexShrink: 0 }}
          onClick={() => navigate("home")}
        >
          <div style={{
            width: 30, height: 30, borderRadius: 7,
            background: `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.cyan})`,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 14, fontWeight: 900,
            color: COLORS.bg,
            boxShadow: `0 0 16px ${COLORS.accentGlow}`,
          }}>F</div>
          <span style={{
            fontFamily: "'Syne', sans-serif",
            fontSize: 18, fontWeight: 800,
            letterSpacing: "-0.5px",
            background: `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.cyan})`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}>FULLSEND</span>
        </div>

        {/* Mode Toggle -- fixed width, no flex growth */}
        {!isMobile && (
        <div style={{
          display: "flex",
          background: COLORS.surface,
          borderRadius: 8,
          padding: 3,
          border: `1px solid ${COLORS.border}`,
          flexShrink: 0,
          flexGrow: 0,
          position: "relative",
          width: 234,
          marginLeft: 8,
        }}>
          {/* Sliding indicator */}
          <div style={{
            position: "absolute",
            top: 3, left: mode === "creator" ? 3 : "50%",
            width: "calc(50% - 3px)",
            height: "calc(100% - 6px)",
            borderRadius: 6,
            background: mode === "creator" ? `${COLORS.accent}15` : `${COLORS.purple}15`,
            border: `1px solid ${mode === "creator" ? COLORS.accent + "20" : COLORS.purple + "20"}`,
            transition: "all 0.3s cubic-bezier(0.4,0,0.2,1)",
            zIndex: 0,
          }} />
          {[
            { id: "creator", label: t.myChannel, icon: "📡" },
            { id: "tipper", label: t.myProfile, icon: "👤" },
          ].map(m => (
            <button key={m.id} onClick={() => switchMode(m.id)} style={{
              padding: "6px 0", borderRadius: 6, border: "none",
              background: "transparent",
              color: mode === m.id
                ? (m.id === "creator" ? COLORS.accent : COLORS.purple)
                : COLORS.textMuted,
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 10, fontWeight: 600,
              cursor: "pointer",
              transition: "color 0.25s cubic-bezier(0.4,0,0.2,1)",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
              letterSpacing: "0.3px",
              width: "50%",
              position: "relative",
              zIndex: 1,
            }}>
              <span style={{ fontSize: 11 }}>{m.icon}</span>
              {m.label}
            </button>
          ))}
        </div>
        )}

        {/* Spacer pushes right items */}
        <div style={{ flex: 1 }} />

        {/* Desktop-only nav items */}
        {!isMobile && (<>
        {/* About & Partners links */}
        <button onClick={() => navigate("about")} style={{
          padding: "6px 10px", borderRadius: 6, border: "none",
          background: view === "about" ? `${COLORS.text}10` : "transparent",
          color: view === "about" ? COLORS.text : COLORS.textMuted,
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 10, fontWeight: 500,
          cursor: "pointer",
          transition: "all 0.2s",
          flexShrink: 0,
        }}>
          {t.about}
        </button>
        <button onClick={() => navigate("partners")} style={{
          padding: "6px 10px", borderRadius: 6, border: "none",
          background: view === "partners" ? `${COLORS.text}10` : "transparent",
          color: view === "partners" ? COLORS.text : COLORS.textMuted,
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 10, fontWeight: 500,
          cursor: "pointer",
          transition: "all 0.2s",
          flexShrink: 0,
        }}>
          🤝 Partners
        </button>

        {/* Bug Report Button */}
        <button onClick={() => setShowBugReport(true)} style={{
          width: 38, height: 38, borderRadius: 9,
          background: "transparent",
          border: "1px solid transparent",
          cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 14, transition: "all 0.2s", color: COLORS.textMuted,
          flexShrink: 0,
        }}
        onMouseEnter={e => { e.currentTarget.style.background = `${COLORS.red}10`; e.currentTarget.style.borderColor = `${COLORS.red}20`; }}
        onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = "transparent"; }}
        title="Report a Bug"
        >
          🐛
        </button>

        {/* Language Selector */}
        <div style={{ position: "relative", flexShrink: 0 }}>
          <button onClick={() => setShowLangMenu(!showLangMenu)} style={{
            width: 38, height: 38, borderRadius: 9,
            background: showLangMenu ? `${COLORS.purple}12` : "transparent",
            border: `1px solid ${showLangMenu ? COLORS.purple + "20" : "transparent"}`,
            cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 11, transition: "all 0.2s", color: COLORS.textMuted,
            fontFamily: "'JetBrains Mono', monospace", fontWeight: 700,
          }}
          onMouseEnter={e => { if (!showLangMenu) e.currentTarget.style.background = `${COLORS.text}08`; }}
          onMouseLeave={e => { if (!showLangMenu) e.currentTarget.style.background = "transparent"; }}>
            {lang.toUpperCase()}
          </button>
          {showLangMenu && (
            <div style={{
              position: "absolute", top: "calc(100% + 6px)", right: 0,
              width: 160, background: COLORS.surface,
              border: `1px solid ${COLORS.border}`, borderRadius: 8,
              boxShadow: `0 8px 24px ${COLORS.bg}cc`,
              zIndex: 200, overflow: "hidden",
              animation: "slideDown 0.2s ease-out",
            }}>
              {Object.entries(LANGS).map(([code, info]) => (
                <div key={code} onClick={() => { setLang(code); setShowLangMenu(false); }} style={{
                  padding: "8px 12px", cursor: "pointer",
                  background: lang === code ? `${COLORS.purple}08` : "transparent",
                  display: "flex", alignItems: "center", gap: 8,
                  borderBottom: `1px solid ${COLORS.border}`,
                  transition: "background 0.15s",
                }}
                onMouseEnter={e => { if (lang !== code) e.currentTarget.style.background = `${COLORS.text}04`; }}
                onMouseLeave={e => { if (lang !== code) e.currentTarget.style.background = "transparent"; }}>
                  <span style={{ fontSize: 14 }}>{info.flag}</span>
                  <span style={{ fontSize: 10, fontWeight: lang === code ? 700 : 500, color: lang === code ? COLORS.purple : COLORS.text }}>{info.label}</span>
                  {lang === code && <span style={{ marginLeft: "auto", fontSize: 8, color: COLORS.purple }}>{"✓"}</span>}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Notification Bell */}
        <div style={{ position: "relative", flexShrink: 0 }}>
          <button onClick={() => {
            setShowNotifications(!showNotifications);
            if (!showNotifications) {
              // Mark all as read immediately when opening
              setNotifications(prev => {
                const updated = prev.map(n => ({ ...n, read: true }));
                try { localStorage.setItem("fullsend_seen_notis", JSON.stringify(updated.map(n => n.id))); } catch {}
                return updated;
              });
            }
          }} style={{
            width: 38, height: 38, borderRadius: 9,
            background: showNotifications ? `${COLORS.accent}12` : "transparent",
            border: `1px solid ${showNotifications ? COLORS.accent + "20" : "transparent"}`,
            cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            position: "relative",
            transition: "all 0.2s",
          }}
          onMouseEnter={e => { if (!showNotifications) e.currentTarget.style.background = `${COLORS.text}08`; }}
          onMouseLeave={e => { if (!showNotifications) e.currentTarget.style.background = "transparent"; }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={showNotifications ? COLORS.accent : COLORS.textMuted} strokeWidth="2" style={{ display: "block" }}>
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.73 21a2 2 0 0 1-3.46 0" />
            </svg>
            {unreadCount > 0 && (
              <div style={{
                position: "absolute", top: 4, right: 4,
                width: 14, height: 14, borderRadius: "50%",
                background: COLORS.red,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 8, fontWeight: 800, color: "#fff",
                boxShadow: `0 0 6px ${COLORS.red}50`,
                animation: "scaleIn 0.3s ease-out",
              }}>{unreadCount}</div>
            )}
          </button>

          {/* Dropdown */}
          {showNotifications && (
            <div style={{
              position: "absolute", top: "calc(100% + 8px)", right: 0,
              width: 280, maxHeight: 340,
              background: COLORS.surface,
              border: `1px solid ${COLORS.border}`,
              borderRadius: 10,
              boxShadow: `0 8px 32px ${COLORS.bg}cc`,
              overflow: "hidden",
              animation: "slideDown 0.2s ease-out",
              zIndex: 200,
            }}>
              {/* Header */}
              <div style={{
                padding: "10px 14px",
                borderBottom: `1px solid ${COLORS.border}`,
                display: "flex", alignItems: "center", justifyContent: "space-between",
              }}>
                <button onClick={() => {
                  setShowNotifications(false);
                  setMode("tipper");
                  navigate("notifications");
                }} style={{
                  fontSize: 11, fontWeight: 700, color: COLORS.text,
                  background: "transparent", border: "none", cursor: "pointer", padding: 0,
                  fontFamily: "inherit", display: "flex", alignItems: "center", gap: 5,
                }}
                onMouseEnter={e => e.currentTarget.style.color = COLORS.accent}
                onMouseLeave={e => e.currentTarget.style.color = COLORS.text}>
                  Notifications <span style={{ fontSize: 9, color: COLORS.accent }}>View all →</span>
                </button>
                {unreadCount > 0 && (
                  <button onClick={() => {
                    setNotifications(prev => {
                      const updated = prev.map(n => ({ ...n, read: true }));
                      try { localStorage.setItem("fullsend_seen_notis", JSON.stringify(updated.map(n => n.id))); } catch {}
                      return updated;
                    });
                  }} style={{
                    fontSize: 8, color: COLORS.accent, fontWeight: 600,
                    background: "transparent", border: "none", cursor: "pointer",
                    fontFamily: "'JetBrains Mono', monospace",
                  }}>Mark all read</button>
                )}
              </div>

              {/* Notification list */}
              <div style={{ overflowY: "auto", maxHeight: 280 }}>
                {notifications.length === 0 ? (
                  <div style={{ padding: 20, textAlign: "center", fontSize: 11, color: COLORS.textMuted }}>
                    No notifications yet
                  </div>
                ) : (
                  notifications.map(n => (
                    <div key={n.id} style={{
                      padding: "8px 14px",
                      borderBottom: `1px solid ${COLORS.border}`,
                      display: "flex", alignItems: "center", gap: 10,
                      background: n.read ? "transparent" : `${COLORS.accent}04`,
                      transition: "background 0.3s",
                    }}>
                      <div style={{
                        width: 26, height: 26, borderRadius: 6, flexShrink: 0,
                        background: n.type === "tip" ? `${COLORS.accent}12`
                          : n.type === "follow" ? `${COLORS.cyan}12`
                          : n.type === "poll" ? `${COLORS.orange}12`
                          : `${COLORS.purple}12`,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 12,
                      }}>
                        {n.type === "tip" ? "💰" : n.type === "follow" ? "👤" : n.type === "poll" ? "📊" : "💎"}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontSize: 10, fontWeight: n.read ? 500 : 700,
                          color: n.read ? COLORS.textSecondary : COLORS.text,
                          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        }}>{n.text}</div>
                        <div style={{ fontSize: 8, color: COLORS.textMuted, marginTop: 1 }}>{n.time}</div>
                      </div>
                      {!n.read && (
                        <div style={{
                          width: 6, height: 6, borderRadius: "50%",
                          background: COLORS.accent, flexShrink: 0,
                          boxShadow: `0 0 4px ${COLORS.accent}40`,
                        }} />
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
        </>)}

        {/* Mobile nav — stripped to Tip + About while the mobile site is rebuilt */}
        {isMobile && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
            <button onClick={() => navigate("widget")} style={{
              padding: "8px 14px", borderRadius: 8, border: "none",
              background: view === "widget" ? `${COLORS.accent}1f` : `${COLORS.accent}10`,
              color: COLORS.accent,
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 12, fontWeight: 700, cursor: "pointer",
              display: "flex", alignItems: "center", gap: 5,
            }}>
              <span style={{ fontSize: 13 }}>💰</span> Tip
            </button>
            <button onClick={() => navigate("about")} style={{
              padding: "8px 14px", borderRadius: 8, border: "none",
              background: view === "about" ? `${COLORS.text}12` : "transparent",
              color: view === "about" ? COLORS.text : COLORS.textMuted,
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 12, fontWeight: 600, cursor: "pointer",
            }}>
              About
            </button>
          </div>
        )}

        {/* Connect UP Button — desktop only (mobile connect disabled during rebuild) */}
        {!isMobile && (connected && upProfile ? (
          <div style={{
            display: "flex", alignItems: "center", gap: 8,
            padding: "4px 6px 4px 10px", borderRadius: 8,
            background: `${COLORS.accent}08`,
            border: `1px solid ${COLORS.accent}18`,
            flexShrink: 0,
            cursor: "pointer",
            transition: "all 0.2s",
          }}
          onClick={disconnectUP}
          onMouseEnter={e => e.currentTarget.style.borderColor = `${COLORS.red}30`}
          onMouseLeave={e => e.currentTarget.style.borderColor = `${COLORS.accent}18`}>
            <div style={{
              width: 22, height: 22, borderRadius: 5,
              background: `${COLORS.accent}15`,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 12, overflow: "hidden",
            }}>
              {isImageAvatar(upProfile.avatar) ? (
                <img src={upProfile.avatar} alt="" style={{ width: 22, height: 22, borderRadius: 5, objectFit: "cover" }} />
              ) : (
                upProfile.avatar
              )}
            </div>
            <div style={{ lineHeight: 1.2 }}>
              <div style={{ fontSize: 9, fontWeight: 600, color: COLORS.text, display: "flex", alignItems: "center", gap: 3 }}>
                {upProfile.name}
                {upProfile.isUP && <span style={{ fontSize: 7, color: COLORS.cyan }}>UP</span>}
              </div>
              <div style={{ fontSize: 8, color: COLORS.textMuted, fontFamily: "'JetBrains Mono', monospace" }}>{upProfile.balance} LYX</div>
            </div>
            <div style={{
              width: 5, height: 5, borderRadius: "50%",
              background: COLORS.accent,
              boxShadow: `0 0 6px ${COLORS.accent}`,
              flexShrink: 0,
              marginLeft: 2,
            }} />
          </div>
        ) : (
          <button onClick={connectUP} disabled={connecting} style={{
            padding: "7px 14px", borderRadius: 8,
            background: connecting ? COLORS.surface : `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.cyan})`,
            border: connecting ? `1px solid ${COLORS.border}` : "none",
            color: connecting ? COLORS.textMuted : COLORS.bg,
            fontFamily: "'Syne', sans-serif",
            fontSize: 11, fontWeight: 700,
            cursor: connecting ? "wait" : "pointer",
            transition: "all 0.3s",
            flexShrink: 0,
            display: "flex", alignItems: "center", gap: 6,
            boxShadow: connecting ? "none" : `0 0 16px ${COLORS.accentGlow}`,
          }}
          onMouseEnter={e => { if (!connecting) e.currentTarget.style.transform = "translateY(-1px)"; }}
          onMouseLeave={e => e.currentTarget.style.transform = "translateY(0)"}>
            {connecting ? (
              <>
                <span style={{ animation: "pulse 1s infinite" }}>●</span> Connecting...
              </>
            ) : (
              <>
                <svg width="12" height="12" viewBox="0 0 24 24" fill={COLORS.bg} style={{ display: "block" }}>
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                </svg>
                {t.connectUP}
              </>
            )}
          </button>
        ))}
        </div>

        {/* Tab Strip -- second row (desktop only on the stripped mobile build) */}
        {!isMobile && (
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: "6px 24px 8px",
          borderTop: `1px solid ${COLORS.border}`,
        }}>
          <div style={{ display: "flex", gap: 2, rowGap: 4, padding: 0, width: "100%", maxWidth: 920, justifyContent: "center", flexWrap: isMobile ? "wrap" : "nowrap", overflowX: isMobile ? "visible" : "auto" }}>
            {tabs.map(tab => {
              const activeColor = mode === "creator" ? COLORS.accent : COLORS.purple;
              return (
                <button key={tab.id} onClick={() => {
                  if (view === tab.id) {
                    window.dispatchEvent(new CustomEvent("fullsend-tab-reset"));
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  } else {
                    navigate(tab.id);
                  }
                }} style={{
                  padding: "8px 10px", borderRadius: 7, border: "none",
                  background: view === tab.id ? `${activeColor}15` : "transparent",
                  color: view === tab.id ? activeColor : COLORS.textMuted,
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 11, fontWeight: view === tab.id ? 700 : 500,
                  cursor: "pointer",
                  transition: "all 0.25s cubic-bezier(0.4,0,0.2,1)",
                  position: "relative",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 4,
                  whiteSpace: "nowrap", flexShrink: 0,
                }}>
                  <span style={{ fontSize: 13 }}>{tab.icon}</span>
                  {tab.label}
                  {view === tab.id && <div style={{
                    position: "absolute", bottom: -3, left: "50%", transform: "translateX(-50%)",
                    width: 14, height: 2, borderRadius: 1,
                    background: activeColor,
                    boxShadow: `0 0 8px ${activeColor}40`,
                  }} />}
                </button>
              );
            })}
          </div>
        </div>
        )}

        {/* Mobile menu panel (single, deduped) */}
        {isMobile && showMobileMenu && (
          <div style={{
            padding: "12px 16px 18px",
            borderTop: `1px solid ${COLORS.border}`,
            background: COLORS.bg,
            animation: "slideDown 0.2s ease-out",
            display: "flex", flexDirection: "column", gap: 8,
            position: "relative", zIndex: 101,
          }}>
            {/* Mode Toggle */}
            <div style={{
              display: "flex",
              background: COLORS.surface,
              borderRadius: 9,
              padding: 3,
              border: `1px solid ${COLORS.border}`,
              position: "relative",
            }}>
              <div style={{
                position: "absolute",
                top: 3, left: mode === "creator" ? 3 : "50%",
                width: "calc(50% - 3px)",
                height: "calc(100% - 6px)",
                borderRadius: 7,
                background: mode === "creator" ? `${COLORS.accent}15` : `${COLORS.purple}15`,
                border: `1px solid ${mode === "creator" ? COLORS.accent + "25" : COLORS.purple + "25"}`,
                transition: "all 0.3s cubic-bezier(0.4,0,0.2,1)",
                zIndex: 0,
              }} />
              {[
                { id: "creator", label: t.myChannel, icon: "📡" },
                { id: "tipper", label: t.myProfile, icon: "👤" },
              ].map(m => (
                <button key={m.id} onClick={() => { switchMode(m.id); setShowMobileMenu(false); }} style={{
                  flex: 1, padding: "11px 0", borderRadius: 7, border: "none",
                  background: "transparent",
                  color: mode === m.id ? (m.id === "creator" ? COLORS.accent : COLORS.purple) : COLORS.textMuted,
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 12, fontWeight: 700,
                  cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                  position: "relative", zIndex: 1,
                }}>
                  <span style={{ fontSize: 14 }}>{m.icon}</span>
                  {m.label}
                </button>
              ))}
            </div>

            {/* Nav links */}
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {[
                { label: t.about, icon: "ℹ️", action: () => { navigate("about"); setShowMobileMenu(false); }, active: view === "about" },
                { label: "Partners", icon: "🤝", action: () => { navigate("partners"); setShowMobileMenu(false); }, active: view === "partners" },
                { label: "Report Bug", icon: "🐛", action: () => { setShowBugReport(true); setShowMobileMenu(false); }, active: false },
              ].map((item, i) => (
                <button key={i} onClick={item.action} style={{
                  display: "flex", alignItems: "center", gap: 12,
                  padding: "13px 12px", borderRadius: 9, border: "none",
                  background: item.active ? `${COLORS.accent}10` : "transparent",
                  color: item.active ? COLORS.accent : COLORS.text,
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 13, fontWeight: item.active ? 700 : 500,
                  cursor: "pointer", textAlign: "left", width: "100%",
                }}>
                  <span style={{ fontSize: 16, width: 24, textAlign: "center" }}>{item.icon}</span>
                  {item.label}
                </button>
              ))}
            </div>

            {/* Notifications */}
            <button onClick={() => {
              setShowNotifications(true);
              setShowMobileMenu(false);
              setNotifications(prev => {
                const updated = prev.map(n => ({ ...n, read: true }));
                try { localStorage.setItem("fullsend_seen_notis", JSON.stringify(updated.map(n => n.id))); } catch {}
                return updated;
              });
            }} style={{
              display: "flex", alignItems: "center", gap: 12,
              padding: "13px 12px", borderRadius: 9, border: "none",
              background: "transparent", width: "100%",
              color: COLORS.text, fontFamily: "'JetBrains Mono', monospace",
              fontSize: 13, fontWeight: 500, cursor: "pointer", textAlign: "left",
            }}>
              <span style={{ fontSize: 16, width: 24, textAlign: "center" }}>🔔</span>
              Notifications
              {unreadCount > 0 && (
                <span style={{
                  background: COLORS.red, color: "#fff",
                  fontSize: 9, fontWeight: 800,
                  padding: "2px 7px", borderRadius: 10, marginLeft: 4,
                }}>{unreadCount}</span>
              )}
            </button>

            {/* Language selector */}
            <div style={{
              display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap",
              padding: "12px 10px 2px",
              marginTop: 2, borderTop: `1px solid ${COLORS.border}`,
            }}>
              <span style={{ fontSize: 15, width: 24, textAlign: "center", marginRight: 2 }}>🌐</span>
              {Object.entries(LANGS).map(([code, info]) => (
                <button key={code} onClick={() => { setLang(code); setShowMobileMenu(false); }} style={{
                  padding: "7px 11px", borderRadius: 7,
                  border: `1px solid ${lang === code ? COLORS.purple + "40" : COLORS.border}`,
                  background: lang === code ? `${COLORS.purple}12` : "transparent",
                  color: lang === code ? COLORS.purple : COLORS.textMuted,
                  fontSize: 11, fontWeight: lang === code ? 700 : 500,
                  cursor: "pointer", fontFamily: "'JetBrains Mono', monospace",
                  display: "flex", alignItems: "center", gap: 4,
                }}>
                  <span>{info.flag}</span>{code.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        )}

      </nav>
      {/* Mobile menu backdrop — sits below the sticky nav (z100) but above page
          content, so the page never bleeds through under the open menu. Tapping
          it closes the menu. */}
      {isMobile && showMobileMenu && (
        <div
          onClick={() => setShowMobileMenu(false)}
          style={{
            position: "fixed", inset: 0, zIndex: 99,
            background: COLORS.bg,
            animation: "fadeIn 0.2s ease-out",
          }}
        />
      )}
      {/* Mobile-only informational banner — the dense creator dashboard is best on desktop */}
      {isMobile && !mobileBannerDismissed && (
        <div style={{
          display: "flex", alignItems: "center", gap: 10,
          padding: "9px 16px",
          background: `${COLORS.accent}0e`,
          borderBottom: `1px solid ${COLORS.accent}22`,
          animation: "slideDown 0.3s ease-out",
        }}>
          <span style={{ fontSize: 14, flexShrink: 0 }}>🖥️</span>
          <span style={{
            flex: 1, fontSize: 11, lineHeight: 1.4,
            color: COLORS.textSecondary,
            fontFamily: "'JetBrains Mono', monospace",
          }}>
            Full dashboard available on <strong style={{ color: COLORS.accent, fontWeight: 700 }}>desktop</strong>
          </span>
          <button
            onClick={() => {
              setMobileBannerDismissed(true);
              try { localStorage.setItem("fullsend_mobile_banner", "dismissed"); } catch {}
            }}
            aria-label="Dismiss"
            style={{
              flexShrink: 0, width: 22, height: 22, borderRadius: 6,
              background: "transparent", border: "none", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              color: COLORS.textMuted,
            }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="6" y1="6" x2="18" y2="18" /><line x1="6" y1="18" x2="18" y2="6" />
            </svg>
          </button>
        </div>
      )}
      <div style={{
        opacity: transitioning ? 0 : 1,
        transform: transitioning ? "translateY(8px)" : "translateY(0)",
        transition: "all 0.2s ease-out",
        minHeight: "calc(100vh - 54px)",
      }}>
        {/* Shared views */}
        {view === "about" && <AboutPage />}
        {view === "partners" && <PartnersPage />}

        {/* EOA Preview Mode banner — shows only when creator-mode user is connected with a non-UP wallet */}
        {mode === "creator" && upProfile?.address && upProfile?.isUP === false && (
          <div style={{
            maxWidth: 720, margin: "12px auto 0",
            padding: "10px 14px",
            borderRadius: 10,
            background: `linear-gradient(135deg, ${COLORS.surface}, ${COLORS.cyan}08)`,
            border: `1px solid ${COLORS.cyan}30`,
            display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
          }}>
            <span style={{ fontSize: 16, flexShrink: 0 }}>💡</span>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{
                fontSize: 10, fontWeight: 800, color: COLORS.cyan,
                fontFamily: "'JetBrains Mono', monospace",
                letterSpacing: "1.2px", textTransform: "uppercase",
                marginBottom: 2,
              }}>Preview Mode</div>
              <div style={{ fontSize: 11, color: COLORS.textSecondary, lineHeight: 1.5 }}>
                You're connected with a standard wallet. Feel free to explore and customize — <strong style={{ color: COLORS.text }}>publishing requires a Universal Profile</strong>. Everything you configure will carry over.
              </div>
            </div>
            <a href={isMobileDevice() ? getUPMobileLink() : "https://my.universalprofile.cloud/"} target="_blank" rel="noopener noreferrer" style={{
              flexShrink: 0,
              padding: "6px 12px", borderRadius: 6,
              background: `${COLORS.cyan}15`, border: `1px solid ${COLORS.cyan}35`,
              color: COLORS.cyan, fontSize: 10, fontWeight: 700,
              fontFamily: "'JetBrains Mono', monospace",
              textDecoration: "none", letterSpacing: "0.5px",
            }}>{isMobileDevice() ? "Get UP App →" : "Create UP →"}</a>
          </div>
        )}

        {/* Creator mode views */}
        {mode === "creator" && view === "home" && <HomeView onNavigate={navigate} mode={mode} onSwitchMode={switchMode} />}
        {mode === "creator" && view === "widget" && (
          <div style={{ maxWidth: 460, margin: "0 auto", padding: "0 20px" }}>
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              marginBottom: 12, padding: "0 4px",
              position: "relative", zIndex: 20,
            }}>
              <div style={{
                fontSize: 10, fontWeight: 700, color: COLORS.textMuted,
                letterSpacing: "2px", textTransform: "uppercase",
                fontFamily: "'JetBrains Mono', monospace",
              }}>Widget Preview</div>
              <div style={{ fontSize: 9, color: COLORS.textMuted }}>This is what viewers see on your tip page</div>
            </div>
            <div style={{
              border: `1px dashed ${COLORS.accent}25`,
              borderRadius: 16,
              padding: 2,
              position: "relative",
            }}>
              <TipWidget creatorProfile={upProfile} />
            </div>
            <div style={{
              textAlign: "center", marginTop: 12,
              fontSize: 9, color: COLORS.textMuted,
              fontFamily: "'JetBrains Mono', monospace",
            }}>
              Everything above is your embeddable widget · Customize in Settings
            </div>

            {/* Share Your Tip Link */}
            {connected && upProfile?.address && (
              <div style={{
                marginTop: 16, padding: "14px 16px", borderRadius: 10,
                background: COLORS.surface, border: `1px solid ${COLORS.accent}20`,
              }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.text, marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
                  <span>🔗</span> Share Your Tip Link
                </div>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <div style={{
                    flex: 1, padding: "8px 10px", borderRadius: 6,
                    background: COLORS.bgAlt, border: `1px solid ${COLORS.border}`,
                    fontSize: 10, color: COLORS.cyan, fontFamily: "'JetBrains Mono', monospace",
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}>fullsend.live/tip/{upProfile.address}</div>
                  <CopyButton text={`https://fullsend.live/tip/${upProfile.address}`} label="Copy" color={COLORS.accent} />
                </div>
                <div style={{ fontSize: 9, color: COLORS.textMuted, marginTop: 6 }}>
                  Share this on stream, social media, or your bio. Viewers can tip you instantly.
                </div>
              </div>
            )}
          </div>
        )}
        {mode === "creator" && view === "control" && <ControlPanel />}
        {mode === "creator" && view === "analytics" && <CreatorAnalytics />}
        {mode === "creator" && view === "overlay" && <StreamOverlay />}
        {mode === "creator" && view === "polls" && <PollManager />}
        {mode === "creator" && view === "goals" && <GoalManager />}
        {mode === "creator" && view === "moc" && <MOCManager />}
        {mode === "creator" && view === "leaderboard" && <LeaderboardView />}
        {mode === "creator" && view === "nfts" && (
          <>
            <div style={{ maxWidth: 720, margin: "0 auto", padding: "28px 24px 0" }}>
              <ClaimDropConfig />
              <ClaimLeaderboard creatorAddr={window.__fullsend_profile?.address} />
            </div>
            <NFTGallery />
          </>
        )}
        {mode === "creator" && view === "chat" && <ChatHub />}
        {mode === "creator" && view === "settings" && <CreatorSettings />}
        {mode === "creator" && view === "discover" && <CreatorDirectory />}

        {/* Tipper mode views */}
        {mode === "tipper" && view === "home" && <TipperHome onNavigate={navigate} />}
        {mode === "tipper" && view === "rankings" && <TipperRankings />}
        {mode === "tipper" && view === "nfts-profile" && <TipperNFTs />}
        {mode === "tipper" && view === "activity" && <TipperActivity />}
        {mode === "tipper" && view === "tipper-analytics" && <TipperAnalytics />}
        {mode === "tipper" && view === "notifications" && <MyNotifications />}
        {mode === "tipper" && view === "discover" && <CreatorDirectory />}

        {/* Global Footer */}
        <SiteFooter onNavigate={navigate} />
      </div>

      {/* ONBOARDING WIZARD */}
      {showOnboarding && <OnboardingWizard onClose={() => setShowOnboarding(false)} onNavigate={navigate} upProfile={upProfile} />}

      {/* BUG REPORT MODAL */}
      {showBugReport && <BugReportForm onClose={() => setShowBugReport(false)} upProfile={upProfile} connected={connected} />}
    </div>
  );
}

// --- ONBOARDING WIZARD --------------------------------------
function OnboardingWizard({ onClose, onNavigate, upProfile }) {
  const [step, setStep] = useState(0);
  const [channelName, setChannelName] = useState(upProfile?.name || "");
  const [channelBio, setChannelBio] = useState(upProfile?.bio || "");
  const [socialLinks, setSocialLinks] = useState({
    youtube: upProfile?.links?.youtube || "",
    twitch: upProfile?.links?.twitch || "",
    kick: upProfile?.links?.kick || "",
    rumble: upProfile?.links?.rumble || "",
    x: upProfile?.links?.x || "",
    website: upProfile?.links?.website || "",
  });
  const [copied, setCopied] = useState(false);

  const addr = upProfile?.address || "0x0000";
  const overlayUrl = `https://fullsend.live/overlay/${addr}`;
  const tipUrl = `https://fullsend.live/tip/${addr}`;
  const totalSteps = 5;

  const next = () => { if (step < totalSteps - 1) setStep(step + 1); };
  const prev = () => { if (step > 0) setStep(step - 1); };
  const finish = () => {
    // Save onboarding data to settings first
    const s = window.__fullsend_settings_data || {};
    s.channelName = channelName || upProfile?.name || s.channelName;
    s.channelBio = channelBio || upProfile?.bio || s.channelBio;
    s.socialLinks = { ...s.socialLinks, ...socialLinks };
    window.__fullsend_settings_data = s;
    try { localStorage.setItem("fullsend_settings", JSON.stringify(s)); } catch {}
    // Sync to DB (uses complete platforms)
    syncCreatorToDB();
    localStorage.setItem("fullsend_onboarded", "true");
    onClose();
    onNavigate("settings");
  };

  const copyUrl = (url) => {
    navigator.clipboard?.writeText?.(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const steps = [
    { title: "Welcome to FullSend", icon: "🚀" },
    { title: "Set Up Your Channel", icon: "👤" },
    { title: "Add Social Links", icon: "🔗" },
    { title: "Add Your Overlay", icon: "📺" },
    { title: "You're Live!", icon: "🎉" },
  ];

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 1000,
      background: `${COLORS.bg}e8`,
      backdropFilter: "blur(20px)",
      display: "flex", alignItems: "center", justifyContent: "center",
      animation: "fadeIn 0.3s ease-out",
    }}>
      <div style={{
        width: "100%", maxWidth: 480,
        background: COLORS.surface,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 16,
        overflow: "hidden",
        boxShadow: `0 16px 64px ${COLORS.bg}cc`,
        animation: "scaleIn 0.35s cubic-bezier(0.16, 1, 0.3, 1)",
      }}>
        {/* Progress bar */}
        <div style={{ height: 3, background: COLORS.bgAlt }}>
          <div style={{
            height: "100%",
            width: `${((step + 1) / totalSteps) * 100}%`,
            background: `linear-gradient(90deg, ${COLORS.accent}, ${COLORS.cyan})`,
            borderRadius: 3,
            transition: "width 0.4s cubic-bezier(0.16, 1, 0.3, 1)",
          }} />
        </div>

        {/* Header */}
        <div style={{ padding: "20px 24px 0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 22 }}>{steps[step].icon}</span>
            <div>
              <div style={{ fontFamily: "'Syne', sans-serif", fontSize: 16, fontWeight: 700, color: COLORS.text }}>{steps[step].title}</div>
              <div style={{ fontSize: 9, color: COLORS.textMuted, fontFamily: "'JetBrains Mono', monospace" }}>Step {step + 1} of {totalSteps}</div>
            </div>
          </div>
          <button onClick={onClose} style={{
            width: 28, height: 28, borderRadius: 6,
            background: "transparent", border: `1px solid ${COLORS.border}`,
            color: COLORS.textMuted, fontSize: 14, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>×</button>
        </div>

        {/* Content */}
        <div style={{ padding: "20px 24px", minHeight: 220 }}>

          {/* Step 0: Welcome */}
          {step === 0 && (
            <div style={{ textAlign: "center", animation: "fadeInUp 0.3s ease-out" }}>
              <div style={{
                width: 60, height: 60, borderRadius: 14, margin: "0 auto 16px",
                background: `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.cyan})`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 26, fontWeight: 900, color: COLORS.bg,
                boxShadow: `0 0 30px ${COLORS.accentGlow}`,
              }}>F</div>
              <div style={{ fontSize: 13, color: COLORS.textSecondary, lineHeight: 1.7, maxWidth: 340, margin: "0 auto" }}>
                You're about to set up your FullSend channel. In the next few steps you'll configure your profile, connect your streaming platforms, and get your overlay URL.
              </div>
              <div style={{
                marginTop: 16, padding: "8px 14px", borderRadius: 8,
                background: `${COLORS.accent}06`, border: `1px solid ${COLORS.accent}12`,
                fontSize: 10, color: COLORS.accent, fontWeight: 600,
                display: "inline-block",
              }}>
                Takes about 2 minutes
              </div>
            </div>
          )}

          {/* Step 1: Channel Name & Bio */}
          {step === 1 && (
            <div style={{ animation: "fadeInUp 0.3s ease-out", display: "flex", flexDirection: "column", gap: 14 }}>
              <div style={{ fontSize: 11, color: COLORS.textMuted, lineHeight: 1.6, marginBottom: 4 }}>
                This is pre-filled from your Universal Profile. Edit if you want a different display name on FullSend.
              </div>
              <div>
                <label style={{ fontSize: 9, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "1.5px", display: "block", marginBottom: 4 }}>Channel Name</label>
                <input value={channelName} onChange={e => setChannelName(e.target.value)} style={{
                  width: "100%", padding: "10px 12px", background: COLORS.bgAlt, border: `1px solid ${COLORS.border}`,
                  borderRadius: 7, color: COLORS.text, fontFamily: "'Outfit', sans-serif", fontSize: 14, fontWeight: 600, outline: "none", boxSizing: "border-box",
                }} />
              </div>
              <div>
                <label style={{ fontSize: 9, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "1.5px", display: "block", marginBottom: 4 }}>Bio (optional)</label>
                <textarea value={channelBio} onChange={e => setChannelBio(e.target.value)} rows={2} placeholder="Tell viewers about your channel..." style={{
                  width: "100%", padding: "10px 12px", background: COLORS.bgAlt, border: `1px solid ${COLORS.border}`,
                  borderRadius: 7, color: COLORS.text, fontFamily: "'Outfit', sans-serif", fontSize: 11, outline: "none", boxSizing: "border-box", resize: "vertical",
                }} />
              </div>
            </div>
          )}

          {/* Step 2: Social Links */}
          {step === 2 && (
            <div style={{ animation: "fadeInUp 0.3s ease-out" }}>
              <div style={{ fontSize: 11, color: COLORS.textMuted, lineHeight: 1.6, marginBottom: 12 }}>
                Link your streaming platforms. These show on your tip widget and can be verified later.
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {[
                  { key: "youtube", label: "YouTube", icon: "▶", color: "#FF0000", placeholder: "https://youtube.com/@..." },
                  { key: "twitch", label: "Twitch", icon: "◆", color: "#9146FF", placeholder: "https://twitch.tv/..." },
                  { key: "kick", label: "Kick", icon: "●", color: "#53FC18", placeholder: "https://kick.com/..." },
                  { key: "x", label: "X", icon: "𝕏", color: "#fff", placeholder: "https://x.com/..." },
                  { key: "rumble", label: "Rumble", icon: "R", color: "#85c742", placeholder: "https://rumble.com/c/..." },
                  { key: "website", label: "Website", icon: "🌐", color: "#4a9eff", placeholder: "https://yoursite.com" },
                ].map(p => (
                  <div key={p.key} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{
                      width: 30, height: 30, borderRadius: 6, background: `${p.color}12`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 12, color: p.color, fontWeight: 700, flexShrink: 0,
                    }}>{p.icon}</div>
                    <div style={{ flex: 1, position: "relative" }}>
                      <input
                        value={socialLinks[p.key]}
                        onChange={e => setSocialLinks(prev => ({ ...prev, [p.key]: e.target.value }))}
                        placeholder={p.placeholder}
                        style={{
                          width: "100%", padding: "8px 28px 8px 10px", background: COLORS.bgAlt, border: `1px solid ${COLORS.border}`,
                          borderRadius: 6, color: COLORS.text, fontFamily: "'JetBrains Mono', monospace", fontSize: 10, outline: "none", boxSizing: "border-box",
                        }}
                      />
                      {socialLinks[p.key] && (
                        <button onClick={() => setSocialLinks(prev => ({ ...prev, [p.key]: "" }))} style={{
                          position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)",
                          width: 18, height: 18, borderRadius: 4,
                          background: `${COLORS.red}15`, border: `1px solid ${COLORS.red}20`,
                          color: COLORS.red, fontSize: 10, cursor: "pointer",
                          display: "flex", alignItems: "center", justifyContent: "center",
                        }}>×</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ fontSize: 9, color: COLORS.textMuted, marginTop: 8 }}>
                Skip any you don't use — you can add them later in Settings. Verify your links in Settings to get a <span style={{ color: COLORS.accent, fontWeight: 600 }}>✓ green checkmark</span> on your widget.
              </div>
            </div>
          )}

          {/* Step 3: Overlay URL */}
          {step === 3 && (
            <div style={{ animation: "fadeInUp 0.3s ease-out" }}>
              <div style={{ fontSize: 11, color: COLORS.textMuted, lineHeight: 1.6, marginBottom: 14 }}>
                Add this URL as a <strong style={{ color: COLORS.text }}>Browser Source</strong> in your streaming software. Set it to 1920×1080.
              </div>

              {/* Overlay URL */}
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 9, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "1.5px", display: "block", marginBottom: 4 }}>Overlay URL</label>
                <div style={{
                  display: "flex", alignItems: "center", gap: 6, padding: "8px 12px", borderRadius: 7,
                  background: COLORS.bgAlt, border: `1px solid ${COLORS.border}`,
                }}>
                  <span style={{ flex: 1, fontSize: 10, fontFamily: "'JetBrains Mono', monospace", color: COLORS.cyan, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{overlayUrl}</span>
                  <button onClick={() => copyUrl(overlayUrl)} style={{
                    padding: "4px 10px", borderRadius: 4, background: `${COLORS.cyan}12`, border: `1px solid ${COLORS.cyan}20`,
                    color: COLORS.cyan, fontSize: 9, fontWeight: 700, cursor: "pointer", fontFamily: "'JetBrains Mono', monospace", whiteSpace: "nowrap",
                  }}>{copied ? "✓ Copied" : "Copy"}</button>
                </div>
              </div>

              {/* Quick steps */}
              <div style={{
                padding: "10px 12px", borderRadius: 8,
                background: `${COLORS.cyan}05`, border: `1px solid ${COLORS.cyan}10`,
              }}>
                {[
                  "Open OBS, Streamlabs, or your streaming app",
                  "Add a new Browser Source",
                  "Paste the URL above, set to 1920×1080",
                  "Position it as the top layer in your scene",
                  "💡 For TTS in recordings/streams: enable a Desktop Audio source (System TTS plays through OS, not the browser tab)",
                ].map((s, i) => (
                  <div key={i} style={{ display: "flex", gap: 8, marginBottom: i < 4 ? 4 : 0, fontSize: 10, color: i === 4 ? COLORS.accent : COLORS.textSecondary }}>
                    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, color: i === 4 ? COLORS.accent : COLORS.cyan, width: 14, flexShrink: 0 }}>{i === 4 ? "!" : (i + 1) + "."}</span>
                    {s}
                  </div>
                ))}
              </div>

              {/* Tip page URL */}
              <div style={{ marginTop: 12 }}>
                <label style={{ fontSize: 9, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "1.5px", display: "block", marginBottom: 4 }}>Tip Page URL</label>
                <div style={{
                  display: "flex", alignItems: "center", gap: 6, padding: "8px 12px", borderRadius: 7,
                  background: COLORS.bgAlt, border: `1px solid ${COLORS.border}`,
                }}>
                  <span style={{ flex: 1, fontSize: 10, fontFamily: "'JetBrains Mono', monospace", color: COLORS.accent, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{tipUrl}</span>
                  <button onClick={() => copyUrl(tipUrl)} style={{
                    padding: "4px 10px", borderRadius: 4, background: `${COLORS.accent}12`, border: `1px solid ${COLORS.accent}20`,
                    color: COLORS.accent, fontSize: 9, fontWeight: 700, cursor: "pointer", fontFamily: "'JetBrains Mono', monospace", whiteSpace: "nowrap",
                  }}>{copied ? "✓ Copied" : "Copy"}</button>
                </div>
                <div style={{ fontSize: 9, color: COLORS.textMuted, marginTop: 4 }}>Share this in your YouTube description, Linktree, or social bios.</div>
              </div>
            </div>
          )}

          {/* Step 4: Done */}
          {step === 4 && (
            <div style={{ textAlign: "center", animation: "fadeInUp 0.3s ease-out" }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>🎉</div>
              <div style={{
                fontFamily: "'Syne', sans-serif", fontSize: 20, fontWeight: 800,
                background: `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.cyan})`,
                WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
                marginBottom: 8,
              }}>You're all set!</div>
              <div style={{ fontSize: 12, color: COLORS.textSecondary, lineHeight: 1.7, maxWidth: 320, margin: "0 auto" }}>
                Your FullSend channel is live. Start streaming and share your tip link with viewers. Tips, follows, polls, and NFT milestones are all ready to go.
              </div>
              <div style={{
                display: "flex", gap: 8, justifyContent: "center", marginTop: 16,
                flexWrap: "wrap",
              }}>
                {[
                  { label: "💰 100% to you", color: COLORS.accent },
                  { label: "⛽ Gas covered", color: COLORS.cyan },
                  { label: "💎 NFT milestones", color: COLORS.purple },
                  { label: "📊 Live polls", color: COLORS.orange },
                ].map((tag, i) => (
                  <span key={i} style={{
                    fontSize: 9, padding: "4px 8px", borderRadius: 4,
                    background: `${tag.color}10`, border: `1px solid ${tag.color}15`,
                    color: tag.color, fontWeight: 600,
                  }}>{tag.label}</span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: "14px 24px", borderTop: `1px solid ${COLORS.border}`,
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div>
            {step > 0 && step < totalSteps - 1 && (
              <button onClick={prev} style={{
                padding: "8px 16px", borderRadius: 7, background: "transparent", border: `1px solid ${COLORS.border}`,
                color: COLORS.textMuted, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "'JetBrains Mono', monospace",
              }}>Back</button>
            )}
          </div>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            {step < totalSteps - 1 && (
              <button onClick={() => { localStorage.setItem("fullsend_onboarded", "true"); onClose(); }} style={{
                padding: "8px 12px", borderRadius: 7, background: "transparent", border: "none",
                color: COLORS.textMuted, fontSize: 10, cursor: "pointer", fontFamily: "'JetBrains Mono', monospace",
              }}>Skip setup</button>
            )}
            {step < totalSteps - 1 ? (
              <button onClick={next} style={{
                padding: "8px 20px", borderRadius: 7,
                background: `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.cyan})`,
                border: "none", color: COLORS.bg, fontSize: 12, fontWeight: 700, cursor: "pointer",
                fontFamily: "'Syne', sans-serif",
                boxShadow: `0 2px 12px ${COLORS.accentGlow}`,
              }}>Next</button>
            ) : (
              <button onClick={finish} style={{
                padding: "8px 24px", borderRadius: 7,
                background: `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.cyan})`,
                border: "none", color: COLORS.bg, fontSize: 12, fontWeight: 700, cursor: "pointer",
                fontFamily: "'Syne', sans-serif",
                boxShadow: `0 2px 12px ${COLORS.accentGlow}`,
              }}>Go to Dashboard →</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// --- HOME VIEW -----------------------------------------------
function HomeView({ onNavigate, mode, onSwitchMode }) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => { requestAnimationFrame(() => setMounted(true)); }, []);

  return (
    <div style={{ maxWidth: 920, margin: "0 auto", padding: isMobileDevice() ? "0 14px" : "0 24px", position: "relative" }}>
      {/* Ambient background */}
      <div style={{ position: "fixed", inset: 0, zIndex: -1, overflow: "hidden", pointerEvents: "none" }}>
        <div style={{
          position: "absolute", top: "10%", left: "20%",
          width: 500, height: 500, borderRadius: "50%",
          background: `radial-gradient(circle, ${COLORS.accent}06 0%, transparent 70%)`,
          animation: "orbFloat1 20s ease-in-out infinite",
          filter: "blur(60px)",
        }} />
        <div style={{
          position: "absolute", top: "40%", right: "10%",
          width: 400, height: 400, borderRadius: "50%",
          background: `radial-gradient(circle, ${COLORS.purple}06 0%, transparent 70%)`,
          animation: "orbFloat2 25s ease-in-out infinite",
          filter: "blur(60px)",
        }} />
        <div style={{
          position: "absolute", bottom: "5%", left: "40%",
          width: 300, height: 300, borderRadius: "50%",
          background: `radial-gradient(circle, ${COLORS.blue}05 0%, transparent 70%)`,
          animation: "orbFloat1 18s ease-in-out infinite reverse",
          filter: "blur(50px)",
        }} />
        {/* Grid overlay */}
        <div style={{
          position: "absolute", inset: 0,
          backgroundImage: `linear-gradient(${COLORS.border} 1px, transparent 1px), linear-gradient(90deg, ${COLORS.border} 1px, transparent 1px)`,
          backgroundSize: "40px 40px",
          animation: "gridScroll 20s linear infinite",
          opacity: 0.3,
          maskImage: "radial-gradient(ellipse 60% 50% at 50% 30%, black, transparent)",
          WebkitMaskImage: "radial-gradient(ellipse 60% 50% at 50% 30%, black, transparent)",
        }} />
      </div>

      {/* HERO */}
      <div style={{
        textAlign: "center",
        paddingTop: isMobileDevice() ? 40 : 80, paddingBottom: isMobileDevice() ? 30 : 60,
        opacity: mounted ? 1 : 0,
        transform: mounted ? "translateY(0)" : "translateY(30px)",
        transition: "all 0.8s cubic-bezier(0.16, 1, 0.3, 1)",
      }}>
        <div style={{
          fontSize: isMobileDevice() ? 56 : 96, fontWeight: 900,
          fontFamily: "'Syne', sans-serif",
          letterSpacing: isMobileDevice() ? "-2px" : "-4px",
          lineHeight: 0.95,
          marginBottom: 20,
          background: `linear-gradient(135deg, ${COLORS.accent} 0%, ${COLORS.cyan} 40%, ${COLORS.blue} 70%, ${COLORS.purple} 100%)`,
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          animation: "heroGlow 4s ease-in-out infinite",
          filter: "drop-shadow(0 0 60px #00ff8815)",
        }}>FULL<br/>SEND</div>

        <div style={{
          fontSize: 16, color: COLORS.textSecondary,
          fontFamily: "'Outfit', sans-serif",
          fontWeight: 300,
          maxWidth: 440, margin: "0 auto",
          lineHeight: 1.7,
          letterSpacing: "0.3px",
          opacity: mounted ? 1 : 0,
          transform: mounted ? "translateY(0)" : "translateY(15px)",
          transition: "all 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.2s",
        }}>
          Decentralized tipping for live streams.<br/>
          <span style={{ color: COLORS.accent }}>No middleman. No cut.</span> 100% to the creator.<br/>
          <span style={{ color: COLORS.accent }}>No payout delays.</span> Paid instantly, straight to your wallet.
        </div>

        <div style={{
          display: "flex", gap: 6, justifyContent: "center", marginTop: 24,
          opacity: mounted ? 1 : 0,
          transform: mounted ? "translateY(0)" : "translateY(15px)",
          transition: "all 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.35s",
        }}>
          <span style={{
            fontSize: 10, color: COLORS.textMuted,
            padding: "4px 10px", borderRadius: 20,
            border: `1px solid ${COLORS.border}`,
            background: `${COLORS.surface}60`,
            backdropFilter: "blur(8px)",
          }}>Built on LUKSO</span>
          <span style={{
            fontSize: 10, color: COLORS.textMuted,
            padding: "4px 10px", borderRadius: 20,
            border: `1px solid ${COLORS.border}`,
            background: `${COLORS.surface}60`,
            backdropFilter: "blur(8px)",
          }}>Universal Profiles</span>
          <span style={{
            fontSize: 10, color: COLORS.textMuted,
            padding: "4px 10px", borderRadius: 20,
            border: `1px solid ${COLORS.border}`,
            background: `${COLORS.surface}60`,
            backdropFilter: "blur(8px)",
          }}>100% On-Chain</span>
        </div>
      </div>

      {/* PLATFORM COMPARISON */}
      <div style={{
        marginBottom: 40,
        padding: isMobileDevice() ? "20px 12px" : "22px 18px",
        background: COLORS.surface,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 16,
        opacity: mounted ? 1 : 0,
        transform: mounted ? "translateY(0)" : "translateY(15px)",
        transition: "all 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.4s",
      }}>
        <div style={{
          textAlign: "center", marginBottom: 16,
        }}>
          <div style={{
            fontSize: 13, color: COLORS.text,
            fontFamily: "'Syne', sans-serif",
            fontWeight: 700,
          }}>
            On a <span style={{ color: COLORS.accent, fontWeight: 800 }}>$100</span> tip, the creator receives:
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {[
            { name: "Twitch", cut: 50, creator: 50, color: "#9146FF", icon: "◆" },
            { name: "YouTube Super Chat", cut: 30, creator: 70, color: "#FF0000", icon: "▶" },
            { name: "Instagram", cut: 30, creator: 70, color: "#E1306C", icon: "◉" },
            { name: "Facebook Stars", cut: 30, creator: 70, color: "#1877F2", icon: "f" },
            { name: "Kick", cut: 5, creator: 95, color: "#53FC18", icon: "●" },
            { name: "FullSend", cut: 0, creator: 100, color: COLORS.accent, icon: "F", isFullSend: true },
          ].map((platform, i) => (
            <div key={i} style={{
              display: "flex", alignItems: "center", gap: isMobileDevice() ? 8 : 10,
              padding: platform.isFullSend ? (isMobileDevice() ? "11px 10px" : "11px 14px") : (isMobileDevice() ? "9px 10px" : "9px 14px"),
              borderRadius: 9,
              background: platform.isFullSend ? `${COLORS.accent}08` : `${COLORS.bg}50`,
              border: `1px solid ${platform.isFullSend ? COLORS.accent + "30" : COLORS.border}`,
              boxShadow: platform.isFullSend ? `0 0 20px ${COLORS.accent}08, inset 0 1px 0 ${COLORS.accent}10` : "none",
              transition: "all 0.3s",
            }}
            onMouseEnter={e => {
              e.currentTarget.style.borderColor = `${platform.color}${platform.isFullSend ? "50" : "30"}`;
              e.currentTarget.style.transform = "translateX(2px)";
            }}
            onMouseLeave={e => {
              e.currentTarget.style.borderColor = platform.isFullSend ? `${COLORS.accent}30` : COLORS.border;
              e.currentTarget.style.transform = "translateX(0)";
            }}>
              {/* Platform icon */}
              <div style={{
                width: isMobileDevice() ? 26 : 28, height: isMobileDevice() ? 26 : 28, borderRadius: 7,
                background: platform.isFullSend
                  ? `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.cyan})`
                  : `${platform.color}15`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: platform.isFullSend ? 11 : 10,
                fontWeight: 900,
                color: platform.isFullSend ? COLORS.bg : platform.color,
                flexShrink: 0,
                boxShadow: platform.isFullSend ? `0 0 10px ${COLORS.accent}25` : "none",
              }}>{platform.icon}</div>

              {/* Name */}
              <div style={{
                fontSize: isMobileDevice() ? 11 : 12, fontWeight: platform.isFullSend ? 700 : 500,
                color: platform.isFullSend ? COLORS.text : COLORS.textSecondary,
                width: isMobileDevice() ? 80 : 130, flexShrink: 0,
                lineHeight: 1.2,
              }}>{platform.name}</div>

              {/* Bar */}
              <div style={{ flex: 1, position: "relative", height: 24, borderRadius: 5, background: `${COLORS.bg}80` }}>
                {/* Creator portion */}
                <div style={{
                  position: "absolute", left: 0, top: 0, bottom: 0,
                  width: `${platform.creator}%`,
                  background: platform.isFullSend
                    ? `linear-gradient(90deg, ${COLORS.accent}50, ${COLORS.cyan}35)`
                    : `${platform.color}25`,
                  borderRadius: 5,
                  transition: "width 1s cubic-bezier(0.16, 1, 0.3, 1)",
                  display: "flex", alignItems: "center", paddingLeft: 10,
                  overflow: "hidden",
                }}>
                  <span style={{
                    fontSize: 10, fontWeight: 700,
                    color: platform.isFullSend ? COLORS.accent : platform.color,
                    whiteSpace: "nowrap",
                    textShadow: `0 1px 3px ${COLORS.bg}`,
                  }}>
                    {"$"}{platform.creator}
                  </span>
                </div>
                {/* Platform cut label */}
                {platform.cut > 0 && (
                  <span style={{
                    position: "absolute",
                    right: 5,
                    top: "50%", transform: "translateY(-50%)",
                    fontSize: 9, color: "#fff", fontWeight: 700,
                    background: `${COLORS.red}cc`, padding: "2px 6px", borderRadius: 3,
                    whiteSpace: "nowrap", zIndex: 2,
                  }}>
                    -{platform.cut}%
                  </span>
                )}
              </div>

              {/* Creator gets */}
              <div style={{
                fontFamily: "'Syne', sans-serif",
                fontSize: platform.isFullSend ? (isMobileDevice() ? 16 : 18) : (isMobileDevice() ? 13 : 14),
                fontWeight: 800,
                color: platform.isFullSend ? COLORS.accent : COLORS.textSecondary,
                minWidth: isMobileDevice() ? 38 : 48, textAlign: "right", flexShrink: 0,
                textShadow: platform.isFullSend ? `0 0 12px ${COLORS.accent}30` : "none",
              }}>{"$"}{platform.creator}</div>
            </div>
          ))}
        </div>

        <div style={{
          textAlign: "center", marginTop: 12,
          fontSize: 11, color: COLORS.textSecondary,
          fontFamily: "'JetBrains Mono', monospace",
          lineHeight: 1.7,
          textWrap: "balance",
          maxWidth: 760, marginLeft: "auto", marginRight: "auto",
        }}>
          <div style={{ color: "#ff7a7a", marginBottom: 6, fontWeight: 600, textWrap: "balance" }}>
            Based on $100 a fan spends. Facebook & Instagram tips are bought through Apple / Google, which take ~30% off the top; YouTube, Twitch & Kick lose an additional 2–4% to payment processors — creators net even less than shown.
          </div>
          <div style={{ textWrap: "balance" }}>
            FullSend is wallet-to-wallet · no processor fees · no payout delays · flat 0.50 LYX fee added on top, never deducted from the tip
          </div>
        </div>
      </div>

      {/* PAYOUT DELAY BREAKDOWN */}
      <div style={{
        background: `linear-gradient(135deg, ${COLORS.surface}, #0d0d16)`,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 16,
        padding: "28px 24px",
        marginBottom: 14,
        opacity: mounted ? 1 : 0,
        transform: mounted ? "translateY(0)" : "translateY(20px)",
        transition: "all 0.6s cubic-bezier(0.4,0,0.2,1)",
        transitionDelay: "0.4s",
      }}>
        <div style={{ textAlign: "center", marginBottom: 6 }}>
          <div style={{
            fontFamily: "'Syne', sans-serif", fontSize: 18, fontWeight: 800, color: COLORS.text,
          }}>Get paid the moment you earn it</div>
          <div style={{ fontSize: 12, color: COLORS.textMuted, fontFamily: "'JetBrains Mono', monospace", marginTop: 4 }}>
            Every other platform makes you wait. FullSend doesn't.
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 18 }}>
          {[
            { name: "Twitch", wait: "Paid monthly · clear $50 first · then a 2–6 week wait", color: "#9146FF", icon: "◆" },
            { name: "YouTube", wait: "Paid monthly · clear $100 first · up to ~2 months to land", color: "#FF0000", icon: "▶" },
            { name: "Facebook / Instagram", wait: "Paid monthly · clear $100 first · rolls over if you're under", color: "#1877F2", icon: "f" },
            { name: "Kick", wait: "Faster (same-day eligible) · but clear ~$50 first · paid to your bank via Stripe", color: "#53FC18", icon: "●" },
          ].map((p, i) => (
            <div key={i} style={{
              display: "flex", alignItems: "center", gap: 12,
              padding: "12px 16px", background: COLORS.bg, borderRadius: 10,
              border: `1px solid ${COLORS.border}`,
            }}>
              <span style={{ width: 26, height: 26, borderRadius: "50%", background: `${p.color}20`, color: p.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 900, flexShrink: 0 }}>{p.icon}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text }}>{p.name}</div>
                <div style={{ fontSize: 11, color: COLORS.textMuted, fontFamily: "'JetBrains Mono', monospace", marginTop: 2 }}>{p.wait}</div>
              </div>
              <span style={{ fontSize: 16 }}>⏳</span>
            </div>
          ))}

          {/* FullSend row */}
          <div style={{
            display: "flex", alignItems: "center", gap: 12,
            padding: "14px 16px", borderRadius: 10,
            background: `linear-gradient(135deg, ${COLORS.accent}12, ${COLORS.accent}05)`,
            border: `1px solid ${COLORS.accent}40`,
          }}>
            <span style={{ width: 26, height: 26, borderRadius: "50%", background: COLORS.accent, color: COLORS.bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 900, flexShrink: 0 }}>F</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: COLORS.accent }}>FullSend</div>
              <div style={{ fontSize: 11, color: COLORS.textSecondary, fontFamily: "'JetBrains Mono', monospace", marginTop: 2 }}>Instant · any amount · straight to your wallet</div>
            </div>
            <span style={{ fontSize: 16 }}>⚡</span>
          </div>
        </div>

        <div style={{
          fontSize: 12, color: COLORS.textSecondary, lineHeight: 1.7,
          fontFamily: "'JetBrains Mono', monospace", textAlign: "center",
          marginTop: 18, textWrap: "balance", maxWidth: 720, marginLeft: "auto", marginRight: "auto",
        }}>
          There's no company holding your money. Tips settle <span style={{ color: COLORS.text, fontWeight: 700 }}>on-chain, wallet-to-wallet</span> — the instant a fan sends LYX, it lands in your Universal Profile. No thresholds to clear, no monthly cycle, no account holds, no bank transit. <span style={{ color: COLORS.accent, fontWeight: 700 }}>It's decentralized — you're paid the second you earn it.</span>
        </div>
      </div>

      {/* ELIGIBILITY / GATEKEEPING BREAKDOWN */}
      <div style={{
        background: `linear-gradient(135deg, ${COLORS.surface}, #0d0d16)`,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 16,
        padding: "28px 24px",
        marginBottom: 14,
        opacity: mounted ? 1 : 0,
        transform: mounted ? "translateY(0)" : "translateY(20px)",
        transition: "all 0.6s cubic-bezier(0.4,0,0.2,1)",
        transitionDelay: "0.5s",
      }}>
        <div style={{ textAlign: "center", marginBottom: 6 }}>
          <div style={{ fontFamily: "'Syne', sans-serif", fontSize: 18, fontWeight: 800, color: COLORS.text }}>
            Start earning on day one
          </div>
          <div style={{ fontSize: 12, color: COLORS.textMuted, fontFamily: "'JetBrains Mono', monospace", marginTop: 4 }}>
            Everyone else locks tipping behind a partner program. FullSend doesn't.
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 18 }}>
          {[
            { name: "YouTube Super Chat", req: "Get into the Partner Program first — 500+ subs, eligible country, then manually switch it on in Studio. Not on age-restricted or 'made for kids' streams.", color: "#FF0000", icon: "▶" },
            { name: "Twitch", req: "Reach Affiliate first — 50 followers, 500 stream minutes, 7 stream days, 3 avg viewers.", color: "#9146FF", icon: "◆" },
            { name: "Facebook / Instagram", req: "Get approved for monetization and clear their follower + eligibility rules.", color: "#1877F2", icon: "f" },
            { name: "Kick", req: "Apply to and get accepted into the Creator Program.", color: "#53FC18", icon: "●" },
          ].map((p, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", background: COLORS.bg, borderRadius: 10, border: `1px solid ${COLORS.border}` }}>
              <span style={{ width: 26, height: 26, borderRadius: "50%", background: `${p.color}20`, color: p.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 900, flexShrink: 0 }}>{p.icon}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text }}>{p.name}</div>
                <div style={{ fontSize: 11, color: COLORS.textMuted, fontFamily: "'JetBrains Mono', monospace", marginTop: 2 }}>{p.req}</div>
              </div>
              <span style={{ fontSize: 16 }}>🔒</span>
            </div>
          ))}

          {/* FullSend row */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", borderRadius: 10, background: `linear-gradient(135deg, ${COLORS.accent}12, ${COLORS.accent}05)`, border: `1px solid ${COLORS.accent}40` }}>
            <span style={{ width: 26, height: 26, borderRadius: "50%", background: COLORS.accent, color: COLORS.bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 900, flexShrink: 0 }}>F</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: COLORS.accent }}>FullSend</div>
              <div style={{ fontSize: 11, color: COLORS.textSecondary, fontFamily: "'JetBrains Mono', monospace", marginTop: 2 }}>No program · no follower minimum · connect your Universal Profile and you're live</div>
            </div>
            <span style={{ fontSize: 16 }}>⚡</span>
          </div>
        </div>

        <div style={{ fontSize: 12, color: COLORS.textSecondary, lineHeight: 1.7, fontFamily: "'JetBrains Mono', monospace", textAlign: "center", marginTop: 18, textWrap: "balance", maxWidth: 720, marginLeft: "auto", marginRight: "auto" }}>
          On the big platforms you have to qualify before fans can pay you — hit subscriber counts, get accepted into a program, live in the right country. <span style={{ color: COLORS.text, fontWeight: 700 }}>FullSend has no gatekeeper.</span> Any creator with a Universal Profile can accept tips from their very first stream. <span style={{ color: COLORS.accent, fontWeight: 700 }}>Permissionless by design.</span>
        </div>
      </div>

      {/* FEATURE CARDS */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 14,
        marginBottom: 14,
      }}>
        {[
          { icon: "💰", title: "Tip Widget", desc: "Connect UP, send LYX with a message", view: "widget", color: COLORS.green },
          { icon: "🎮", title: "Control Panel", desc: "Approve or reject for on-screen display", view: "control", color: COLORS.blue },
          { icon: "📺", title: "Stream Overlay", desc: "Tier-based animated alerts on stream", view: "overlay", color: COLORS.purple },
          { icon: "📊", title: "Live Polls", desc: "Free or token-gated voting on stream", view: "polls", color: COLORS.cyan },
          { icon: "🎯", title: "Goals", desc: "Set fundraising targets for your community", view: "goals", color: COLORS.gold },
          { icon: "📈", title: "Analytics", desc: "Earnings, tiers, top supporters, export CSV", view: "analytics", color: COLORS.accent },
        ].map((item, i) => (
          <button key={item.view} onClick={() => onNavigate(item.view)} style={{
            background: COLORS.surface,
            border: `1px solid ${COLORS.border}`,
            borderRadius: 14,
            padding: "22px 20px",
            cursor: "pointer",
            textAlign: "left",
            transition: "all 0.35s cubic-bezier(0.4,0,0.2,1)",
            position: "relative",
            overflow: "hidden",
            opacity: mounted ? 1 : 0,
            transform: mounted ? "translateY(0)" : "translateY(20px)",
            transitionDelay: `${0.3 + i * 0.08}s`,
          }}
          onMouseEnter={e => {
            e.currentTarget.style.borderColor = `${item.color}30`;
            e.currentTarget.style.boxShadow = `0 4px 40px ${item.color}08, inset 0 1px 0 ${item.color}15`;
            e.currentTarget.style.transform = "translateY(-3px)";
            e.currentTarget.style.background = COLORS.surfaceAlt;
          }}
          onMouseLeave={e => {
            e.currentTarget.style.borderColor = COLORS.border;
            e.currentTarget.style.boxShadow = "none";
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.background = COLORS.surface;
          }}>
            <div style={{
              position: "absolute", top: -20, right: -20,
              width: 80, height: 80, borderRadius: "50%",
              background: `radial-gradient(circle, ${item.color}08 0%, transparent 70%)`,
            }} />
            <div style={{ fontSize: 28, marginBottom: 10, position: "relative" }}>{item.icon}</div>
            <div style={{
              fontFamily: "'Syne', sans-serif",
              fontSize: 16, fontWeight: 700, color: item.color,
              marginBottom: 5, position: "relative",
            }}>{item.title}</div>
            <div style={{
              fontSize: 12, color: COLORS.textMuted,
              lineHeight: 1.5, position: "relative",
            }}>{item.desc}</div>
            <div style={{
              position: "absolute", bottom: 14, right: 16,
              fontSize: 14, color: COLORS.textMuted, opacity: 0.3,
              transition: "all 0.3s",
            }}>→</div>
          </button>
        ))}
      </div>

      {/* MINT TO CLAIM CARD — Live */}
      <button onClick={() => onNavigate("nfts")} style={{
        width: "100%",
        background: `linear-gradient(135deg, ${COLORS.surface}, #0c1620)`,
        border: `1px solid ${COLORS.cyan}20`,
        borderRadius: 14,
        padding: "22px 24px",
        cursor: "pointer",
        textAlign: "left",
        display: "flex",
        alignItems: "center",
        gap: 18,
        transition: "all 0.35s cubic-bezier(0.4,0,0.2,1)",
        marginBottom: 14,
        opacity: mounted ? 1 : 0,
        transform: mounted ? "translateY(0)" : "translateY(20px)",
        transitionDelay: "0.6s",
        position: "relative",
        overflow: "hidden",
      }}
      onMouseEnter={e => {
        e.currentTarget.style.borderColor = `${COLORS.cyan}40`;
        e.currentTarget.style.boxShadow = `0 4px 40px ${COLORS.cyan}08`;
        e.currentTarget.style.transform = "translateY(-2px)";
      }}
      onMouseLeave={e => {
        e.currentTarget.style.borderColor = `${COLORS.cyan}20`;
        e.currentTarget.style.boxShadow = "none";
        e.currentTarget.style.transform = "translateY(0)";
      }}>
        <div style={{ position: "absolute", top: -20, right: -20, width: 100, height: 100, borderRadius: "50%", background: `radial-gradient(circle, ${COLORS.cyan}08 0%, transparent 70%)` }} />
        <div style={{ fontSize: 36 }}>🎁</div>
        <div style={{ flex: 1 }}>
          <div style={{
            fontFamily: "'Syne', sans-serif",
            fontSize: 16, fontWeight: 700, color: COLORS.cyan,
            marginBottom: 3, display: "flex", alignItems: "center", gap: 8,
          }}>
            Mint to Claim
            <span style={{
              fontSize: 8, fontWeight: 800, padding: "2px 6px", borderRadius: 3,
              background: `${COLORS.green}18`, border: `1px solid ${COLORS.green}30`,
              color: COLORS.green, letterSpacing: "0.5px",
            }}>LIVE</span>
          </div>
          <div style={{ fontSize: 12, color: COLORS.textMuted, lineHeight: 1.5 }}>
            Mint loyalty tokens straight to viewers when they claim on stream
          </div>
        </div>
        <div style={{ fontSize: 14, color: COLORS.textMuted, opacity: 0.3 }}>→</div>
      </button>

      {/* DISCOVER CREATORS CARD */}
      <button onClick={() => onNavigate("discover")} style={{
        width: "100%",
        background: `linear-gradient(135deg, ${COLORS.surface}, #0c1320)`,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 14,
        padding: "22px 24px",
        cursor: "pointer",
        textAlign: "left",
        display: "flex",
        alignItems: "center",
        gap: 18,
        transition: "all 0.35s cubic-bezier(0.4,0,0.2,1)",
        marginBottom: 14,
        opacity: mounted ? 1 : 0,
        transform: mounted ? "translateY(0)" : "translateY(20px)",
        transitionDelay: "0.58s",
      }}
      onMouseEnter={e => {
        e.currentTarget.style.borderColor = `${COLORS.blue}30`;
        e.currentTarget.style.boxShadow = `0 4px 40px ${COLORS.blue}08`;
        e.currentTarget.style.transform = "translateY(-2px)";
      }}
      onMouseLeave={e => {
        e.currentTarget.style.borderColor = COLORS.border;
        e.currentTarget.style.boxShadow = "none";
        e.currentTarget.style.transform = "translateY(0)";
      }}>
        <div style={{ fontSize: 36 }}>👥</div>
        <div style={{ flex: 1 }}>
          <div style={{
            fontFamily: "'Syne', sans-serif",
            fontSize: 16, fontWeight: 700, color: COLORS.blue,
            marginBottom: 3,
          }}>Discover Creators</div>
          <div style={{ fontSize: 12, color: COLORS.textMuted, lineHeight: 1.5 }}>
            Browse other creators on FullSend and support them
          </div>
        </div>
        <div style={{ fontSize: 14, color: COLORS.textMuted, opacity: 0.3 }}>→</div>
      </button>

      {/* LEADERBOARD CARD */}
      <button onClick={() => onNavigate("leaderboard")} style={{
        width: "100%",
        background: `linear-gradient(135deg, ${COLORS.surface}, #110f1e)`,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 14,
        padding: "22px 24px",
        cursor: "pointer",
        textAlign: "left",
        display: "flex",
        alignItems: "center",
        gap: 18,
        transition: "all 0.35s cubic-bezier(0.4,0,0.2,1)",
        marginBottom: 14,
        opacity: mounted ? 1 : 0,
        transform: mounted ? "translateY(0)" : "translateY(20px)",
        transitionDelay: "0.56s",
      }}
      onMouseEnter={e => {
        e.currentTarget.style.borderColor = `${COLORS.gold}25`;
        e.currentTarget.style.boxShadow = `0 4px 40px ${COLORS.gold}06`;
        e.currentTarget.style.transform = "translateY(-2px)";
      }}
      onMouseLeave={e => {
        e.currentTarget.style.borderColor = COLORS.border;
        e.currentTarget.style.boxShadow = "none";
        e.currentTarget.style.transform = "translateY(0)";
      }}>
        <div style={{ fontSize: 36 }}>🏆</div>
        <div style={{ flex: 1 }}>
          <div style={{
            fontFamily: "'Syne', sans-serif",
            fontSize: 16, fontWeight: 700, color: COLORS.gold,
            marginBottom: 3,
          }}>Leaderboard</div>
          <div style={{ fontSize: 12, color: COLORS.textMuted, lineHeight: 1.5 }}>
            On-chain rankings with NFT unlocks at milestones
          </div>
        </div>
        <div style={{ fontSize: 14, color: COLORS.textMuted, opacity: 0.3 }}>→</div>
      </button>

      {/* CHAT CARD — Coming Soon */}
      <div style={{
        width: "100%",
        background: `linear-gradient(135deg, ${COLORS.surface}, #1a1206)`,
        border: `1px solid ${COLORS.orange}12`,
        borderRadius: 14,
        padding: "22px 24px",
        textAlign: "left",
        display: "flex",
        alignItems: "center",
        gap: 18,
        marginBottom: 14,
        opacity: mounted ? 0.7 : 0,
        transform: mounted ? "translateY(0)" : "translateY(20px)",
        transition: "all 0.35s cubic-bezier(0.4,0,0.2,1)",
        transitionDelay: "0.64s",
      }}>
        <div style={{ fontSize: 36 }}>💬</div>
        <div style={{ flex: 1 }}>
          <div style={{
            fontFamily: "'Syne', sans-serif",
            fontSize: 16, fontWeight: 800, color: COLORS.orange,
            marginBottom: 4, display: "flex", alignItems: "center", gap: 8,
          }}>
            Chat
            <span style={{
              fontSize: 8, fontWeight: 700, padding: "2px 6px", borderRadius: 3,
              background: `${COLORS.orange}15`, border: `1px solid ${COLORS.orange}25`,
              color: COLORS.orange, letterSpacing: "0.5px",
            }}>COMING SOON</span>
          </div>
          <div style={{ fontSize: 12, color: COLORS.textSecondary }}>Stream overlay + YouTube, Twitch, Kick</div>
        </div>
        <div style={{ fontSize: 14, color: COLORS.textMuted, opacity: 0.3 }}>→</div>
      </div>

      {/* NFT COLLECTIONS CARD — Coming Soon */}
      <div style={{
        width: "100%",
        background: `linear-gradient(135deg, ${COLORS.surface}, #15101e)`,
        border: `1px solid ${COLORS.purple}12`,
        borderRadius: 14,
        padding: "22px 24px",
        textAlign: "left",
        display: "flex",
        alignItems: "center",
        gap: 18,
        marginBottom: 14,
        opacity: mounted ? 0.7 : 0,
        transform: mounted ? "translateY(0)" : "translateY(20px)",
        transition: "all 0.35s cubic-bezier(0.4,0,0.2,1)",
        transitionDelay: "0.68s",
        position: "relative",
        overflow: "hidden",
      }}>
        <div style={{ position: "absolute", top: -20, right: -20, width: 100, height: 100, borderRadius: "50%", background: `radial-gradient(circle, ${COLORS.purple}06 0%, transparent 70%)` }} />
        <div style={{ fontSize: 36 }}>💎</div>
        <div style={{ flex: 1 }}>
          <div style={{
            fontFamily: "'Syne', sans-serif",
            fontSize: 16, fontWeight: 700, color: COLORS.purple,
            marginBottom: 3, display: "flex", alignItems: "center", gap: 8,
          }}>
            NFT Collections
            <span style={{
              fontSize: 8, fontWeight: 700, padding: "2px 6px", borderRadius: 3,
              background: `${COLORS.orange}15`, border: `1px solid ${COLORS.orange}25`,
              color: COLORS.orange, letterSpacing: "0.5px",
            }}>COMING SOON</span>
          </div>
          <div style={{ fontSize: 12, color: COLORS.textMuted, lineHeight: 1.5 }}>
            Milestone NFTs — Bronze, Silver, Gold, Diamond unlocks
          </div>
        </div>
        <div style={{
          display: "flex", gap: 4, marginRight: 8,
        }}>
          {["🥉", "🥈", "🥇", "💎"].map((e, i) => (
            <span key={i} style={{ fontSize: 14, opacity: 0.6 }}>{e}</span>
          ))}
        </div>
        <div style={{ fontSize: 14, color: COLORS.textMuted, opacity: 0.3 }}>→</div>
      </div>

      {/* SETTINGS CARD */}

      {/* MEMBERSHIP CARD — Coming Soon */}
      <div style={{
        width: "100%",
        background: `linear-gradient(135deg, ${COLORS.surface}, #1a1508)`,
        border: `1px solid ${COLORS.gold}12`,
        borderRadius: 14,
        padding: "22px 24px",
        textAlign: "left",
        display: "flex", alignItems: "center", gap: 18,
        marginBottom: 14,
        opacity: mounted ? 0.7 : 0,
        transform: mounted ? "translateY(0)" : "translateY(20px)",
        transition: "all 0.35s cubic-bezier(0.4,0,0.2,1)",
        transitionDelay: "0.72s",
        position: "relative",
        overflow: "hidden",
      }}>
        <div style={{ position: "absolute", top: -20, right: -20, width: 100, height: 100, borderRadius: "50%", background: `radial-gradient(circle, ${COLORS.gold}06 0%, transparent 70%)` }} />
        <div style={{ fontSize: 36 }}>🏅</div>
        <div style={{ flex: 1 }}>
          <div style={{
            fontFamily: "'Syne', sans-serif",
            fontSize: 16, fontWeight: 700, color: COLORS.gold,
            marginBottom: 3, display: "flex", alignItems: "center", gap: 8,
          }}>
            Memberships
            <span style={{
              fontSize: 8, fontWeight: 700, padding: "2px 6px", borderRadius: 3,
              background: `${COLORS.orange}15`, border: `1px solid ${COLORS.orange}25`,
              color: COLORS.orange, letterSpacing: "0.5px",
            }}>COMING SOON</span>
          </div>
          <div style={{ fontSize: 12, color: COLORS.textMuted, lineHeight: 1.5 }}>
            Channel sponsorships with recurring LYX — exclusive perks, badges, priority access
          </div>
        </div>
        <div style={{ fontSize: 14, color: COLORS.textMuted, opacity: 0.3 }}>→</div>
      </div>

      {/* SETTINGS CARD actual */}
      <button onClick={() => onNavigate("settings")} style={{
        width: "100%",
        background: `linear-gradient(135deg, ${COLORS.surface}, #12121e)`,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 14,
        padding: "22px 24px",
        cursor: "pointer",
        textAlign: "left",
        display: "flex", alignItems: "center", gap: 16,
        marginBottom: 14,
        opacity: mounted ? 1 : 0,
        transform: mounted ? "translateY(0)" : "translateY(20px)",
        transition: "all 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.65s",
      }}
      onMouseEnter={e => {
        e.currentTarget.style.borderColor = `${COLORS.textSecondary}25`;
        e.currentTarget.style.boxShadow = `0 4px 40px ${COLORS.textSecondary}06`;
        e.currentTarget.style.transform = "translateY(-2px)";
      }}
      onMouseLeave={e => {
        e.currentTarget.style.borderColor = COLORS.border;
        e.currentTarget.style.boxShadow = "none";
        e.currentTarget.style.transform = "translateY(0)";
      }}>
        <div style={{ fontSize: 36 }}>⚙️</div>
        <div style={{ flex: 1 }}>
          <div style={{
            fontFamily: "'Syne', sans-serif",
            fontSize: 16, fontWeight: 800,
            color: COLORS.textSecondary,
            marginBottom: 4,
          }}>Settings</div>
          <div style={{ fontSize: 12, color: COLORS.textMuted }}>Profile, tipping, sounds, overlay, moderation, links, webhooks</div>
        </div>
        <div style={{ fontSize: 14, color: COLORS.textMuted, opacity: 0.3 }}>→</div>
      </button>

      {/* TIER PREVIEW */}
      <div style={{
        paddingBottom: 60,
        opacity: mounted ? 1 : 0,
        transform: mounted ? "translateY(0)" : "translateY(20px)",
        transition: "all 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.7s",
      }}>
        <div style={{
          fontFamily: "'Syne', sans-serif",
          fontSize: 11, fontWeight: 700, color: COLORS.textMuted,
          marginBottom: 14, letterSpacing: "3px",
          textTransform: "uppercase",
        }}>Tip Tiers</div>
        <div style={{ display: "flex", gap: 8 }}>
          {Object.values(TIERS).map((tier, i) => (
            <div key={tier.label} onClick={() => {
              window.__fullsend_settings_tab = "tipping";
              onNavigate("settings");
            }} style={{
              flex: 1, background: COLORS.surface,
              border: `1px solid ${COLORS.border}`,
              borderRadius: 10, padding: "14px 8px",
              textAlign: "center",
              transition: "all 0.3s",
              cursor: "pointer",
            }}
            onMouseEnter={e => {
              e.currentTarget.style.borderColor = `${tier.color}30`;
              e.currentTarget.style.boxShadow = `0 0 20px ${tier.color}08`;
              e.currentTarget.style.transform = "translateY(-2px)";
            }}
            onMouseLeave={e => {
              e.currentTarget.style.borderColor = COLORS.border;
              e.currentTarget.style.boxShadow = "none";
              e.currentTarget.style.transform = "translateY(0)";
            }}>
              <div style={{ fontSize: 22, marginBottom: 4 }}>{tier.emoji}</div>
              <div style={{ fontSize: 10, fontWeight: 700, color: tier.color, letterSpacing: "0.5px" }}>{tier.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// --- TIP WIDGET ----------------------------------------------
// --- GASLESS UX TOGGLES --------------------------------------
// Controls FullSend relayer permissions for follows + votes (free + NFT-gated).
// Tips and LYX-gated votes always require a wallet popup — they are NEVER
// part of this grant. The relayer cannot move LYX from any UP, by contract.
function GaslessToggles({ upAddress, provider, defaultExpanded = false }) {
  // Informational panel — advertises what FullSend covers for free. No grant/toggle:
  // free votes, NFT-gated votes, and claims run gasless on us with zero setup; follows
  // are a one-tap LUKSO popup (LUKSO sponsors the gas) so they stay on the standard
  // social graph. Tips and LYX votes always confirm in-wallet — never relayed.
  const [expanded, setExpanded] = useState(defaultExpanded);

  const COVERED = [
    { icon: "📊", label: "Free poll votes" },
    { icon: "🔒", label: "NFT-gated poll votes" },
    { icon: "🎁", label: "Token-drop claims" },
  ];

  return (
    <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${COLORS.border}` }}>
      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          cursor: "pointer", userSelect: "none",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 11 }}>⚡</span>
          <span style={{
            fontSize: 10, fontWeight: 600, color: COLORS.textSecondary,
            fontFamily: "'JetBrains Mono', monospace",
            letterSpacing: "0.5px", textTransform: "uppercase",
          }}>Gasless UX</span>
          <span style={{
            fontSize: 8, padding: "2px 5px", borderRadius: 4,
            background: COLORS.accent + "15", color: COLORS.accent,
            fontWeight: 700, fontFamily: "'JetBrains Mono', monospace",
          }}>ON US</span>
        </div>
        <span style={{
          fontSize: 10, color: COLORS.textMuted,
          transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
          transition: "transform 0.2s",
        }}>▼</span>
      </div>

      {expanded && (
        <div style={{ marginTop: 8, animation: "slideDown 0.2s ease-out" }}>
          <div style={{ fontSize: 10, color: COLORS.textMuted, lineHeight: 1.6, marginBottom: 8 }}>
            FullSend covers the gas so your audience can jump in with one click — no setup, no wallet popups, nothing to pay:
          </div>

          <div style={{
            padding: "8px 10px", borderRadius: 8,
            background: COLORS.surface, border: `1px solid ${COLORS.border}`,
            display: "flex", flexDirection: "column", gap: 7,
          }}>
            {COVERED.map((it) => (
              <div key={it.label} style={{
                display: "flex", alignItems: "center", gap: 8,
                fontSize: 11, color: COLORS.text, fontFamily: "'Outfit', sans-serif",
              }}>
                <span style={{ fontSize: 12 }}>{it.icon}</span>
                <span style={{ flex: 1 }}>{it.label}</span>
                <span style={{
                  fontSize: 8, fontWeight: 700, color: COLORS.accent,
                  fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.3px",
                }}>FREE · NO POPUP</span>
              </div>
            ))}
          </div>

          <div style={{ fontSize: 10, color: COLORS.textMuted, marginTop: 8, lineHeight: 1.6 }}>
            Follows are a single tap — LUKSO covers the gas, and they land on your Universal Profile across the whole ecosystem. <span style={{ color: COLORS.accent }}>Never for tips or LYX votes</span> — those always confirm in your wallet, so you authorize every LYX flow yourself. FullSend never touches your funds.
          </div>
        </div>
      )}
    </div>
  );
}

function TipWidget({ creatorAddress: creatorAddressProp, standalone, creatorInfo: creatorInfoProp, creatorProfile: creatorProfileProp } = {}) {
  const [connected, setConnected] = useState(false);
  const [profile, setProfile] = useState(null);
  const [inAppDismissed, setInAppDismissed] = useState(false);
  const [amount, setAmount] = useState("");
  const [message, setMessage] = useState("");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [particles, setParticles] = useState([]);
  const [mounted, setMounted] = useState(false);
  const [connectAnim, setConnectAnim] = useState(false);
  const [subCount, setSubCount] = useState(0);
  const [settingsVersion, setSettingsVersion] = useState(0); // bump to force re-render on settings change

  // Listen for settings changes from CreatorSettings
  useEffect(() => {
    const handler = () => setSettingsVersion(v => v + 1);
    window.addEventListener("fullsend-settings-changed", handler);
    return () => window.removeEventListener("fullsend-settings-changed", handler);
  }, []);

  // Fetch follower count from chain
  useEffect(() => {
    // Don't fetch when no creator is connected on main site — show demo placeholder
    if (!standalone && !window.__fullsend_profile?.address) return;

    const fetchFollowData = async () => {
      const creatorAddr = standalone ? creatorAddressProp : (creatorProfileProp?.address || window.__fullsend_profile?.address);
      if (!creatorAddr) return;
      try {
        const tipperAddr = window.__fullsend_profile?.address || "";
        const url = tipperAddr
          ? `/api/relay-follow?creator=${creatorAddr}&follower=${tipperAddr}`
          : `/api/relay-follow?creator=${creatorAddr}`;
        const response = await fetch(url);
        const result = await response.json();
        if (result.success) {
          if (result.followerCount !== undefined) setSubCount(result.followerCount);
          if (result.isFollowing) setFollowed(true);
          else if (tipperAddr && Date.now() - lastFollowTsRef.current > 60000) setFollowed(false); // Update if unfollowed elsewhere (but not right after a manual follow — read cache lags)
        }
      } catch (err) {
        console.log("Follow data fetch (non-fatal):", err);
      }
    };
    fetchFollowData();
    // Poll every 10 seconds for live updates (unfollows from other apps, etc.)
    const interval = setInterval(fetchFollowData, 20000);
    return () => clearInterval(interval);
  }, [standalone, creatorAddressProp, creatorProfileProp]);
  const [showThemePanel, setShowThemePanel] = useState(false);
  const [widgetTheme, setWidgetTheme] = useState(() => {
    // Standalone: use creator's saved theme from DB
    if (standalone && creatorInfoProp?.widgetTheme) {
      return {
        accent: creatorInfoProp.widgetTheme.accent || COLORS.accent,
        mode: creatorInfoProp.widgetTheme.mode || "dark",
        radius: creatorInfoProp.widgetTheme.radius || 12,
      };
    }
    // Main site: use saved settings
    const s = window.__fullsend_settings_data || {};
    return {
      accent: s.widgetAccent || COLORS.accent,
      mode: s.widgetMode || "dark",
      radius: s.widgetRadius || 12,
    };
  });
  const [themeSaved, setThemeSaved] = useState(false);

  // Update theme when standalone creator data loads from DB
  useEffect(() => {
    if (standalone && creatorInfoProp?.widgetTheme) {
      setWidgetTheme({
        accent: creatorInfoProp.widgetTheme.accent || COLORS.accent,
        mode: creatorInfoProp.widgetTheme.mode || "dark",
        radius: creatorInfoProp.widgetTheme.radius || 12,
      });
    }
  }, [standalone, creatorInfoProp?.widgetTheme]);
  const themeAccents = [
    { color: "#00ff88", label: "Green" },
    { color: "#00e5ff", label: "Cyan" },
    { color: "#b44aff", label: "Purple" },
    { color: "#ff4a6a", label: "Red" },
    { color: "#ffd700", label: "Gold" },
    { color: "#ff8c00", label: "Orange" },
    { color: "#4a9eff", label: "Blue" },
    { color: "#ff69b4", label: "Pink" },
  ];
  const [followed, setFollowed] = useState(false);
  const [justFollowed, setJustFollowed] = useState(false);
  // Timestamp of the last manual follow — stops the cached status poll from
  // briefly reverting the button to "not following" before the read cache clears.
  const lastFollowTsRef = useRef(0);

  // DB-only goal state — single source of truth, polls every 5s + bumps on tip events
  // In standalone mode use the page's creator address; otherwise use the connected user
  const widgetCreatorAddr = standalone ? creatorAddressProp : window.__fullsend_profile?.address;
  // Public Universal Profile link for the creator's pfp + handle (null in unconnected preview)
  const upProfileUrl = widgetCreatorAddr ? `https://universalprofile.cloud/${widgetCreatorAddr}` : null;
  // Message/comment threshold — anything above micro tier gets a message box
  const supporterMin = parseFloat(window.__fullsend_settings_data?.customTiers?.supporter?.min) || 5;
  const [showTiersList, setShowTiersList] = useState(false);
  const { active: tipGoalActive } = useActiveGoal(widgetCreatorAddr);
  // Normalize to legacy shape with `.active` boolean for existing render JSX
  const tipGoal = tipGoalActive
    ? { ...tipGoalActive, active: tipGoalActive.status === "active" }
    : { title: "", target: 0, current: 0, active: false };


  const [activePoll, setActivePoll] = useState(null);
  const [pollLoading, setPollLoading] = useState(true);
  const [hasVoted, setHasVoted] = useState(false);
  const [votedOption, setVotedOption] = useState(null);
  const [votePending, setVotePending] = useState(false);
  const [voteError, setVoteError] = useState(null);
  const [voteOnChain, setVoteOnChain] = useState(false);
  const [pollCollapsed, setPollCollapsed] = useState(false);

  // Self-healing on-chain badge: if relay response was lost (network/timeout) but
  // the vote actually landed on-chain, detect it via indexer and flip the badge.
  useEffect(() => {
    if (!hasVoted || voteOnChain || !activePoll?.onchainId) return;
    const voterAddr = window.__fullsend_profile?.address;
    if (!voterAddr) return;
    let cancelled = false;
    const check = async () => {
      try {
        const r = await fetch(`/api/data-polls?pollId=${activePoll.onchainId}&voter=${voterAddr}`);
        if (!r.ok) return;
        const d = await r.json();
        if (!cancelled && d?.voted) setVoteOnChain(true);
      } catch {}
    };
    check();
    const id = setInterval(check, 15000);
    return () => { cancelled = true; clearInterval(id); };
  }, [hasVoted, voteOnChain, activePoll?.onchainId]);

  // Reset vote state when active poll changes (new poll loaded)
  const prevPollIdRef = useRef(null);
  useEffect(() => {
    if (activePoll?.id && activePoll.id !== prevPollIdRef.current) {
      if (prevPollIdRef.current !== null) {
        setHasVoted(false);
        setVotedOption(null);
        setVotePending(false);
        setVoteError(null);
      }
      prevPollIdRef.current = activePoll.id;
    }
  }, [activePoll?.id]);

  // Fetch active poll — global first (main site), DB (fast), contract (background)
  useEffect(() => {
    const optionColors = [COLORS.accent, COLORS.blue, COLORS.purple, COLORS.orange, COLORS.cyan, COLORS.gold];
    const creatorAddr = standalone ? creatorAddressProp : (creatorProfileProp?.address || window.__fullsend_profile?.address);
    if (!creatorAddr) { setPollLoading(false); return; }

    // Instant: check global (set by PollManager on main site)
    if (window.__fullsend_active_poll && !activePoll) {
      setActivePoll(window.__fullsend_active_poll);
      setPollLoading(false);
    }

    const fetchActivePoll = async () => {
      const optColors = optionColors;
      try {
        // DB first — fast, has images
        const dbRes = await fetch(`/api/data-polls?creator=${creatorAddr}`);
        const dbData = await dbRes.json();
        let excludeIds = new Set();
        if (dbData.success && dbData.polls) {
          for (const p of dbData.polls) {
            if (p.status === "archived" || p.status === "deleted") excludeIds.add(p.id);
          }
          const dbActive = dbData.polls.find(p => (p.status === "active") && !excludeIds.has(p.id));
          if (dbActive) {
            setActivePoll(prev => {
              if (prev && prev.question === dbActive.question && prev.totalVotes === (dbActive.totalVotes || 0) && (prev.onchainId || null) === (dbActive.onchainId || null)) return prev;
              return {
                id: dbActive.id,
                question: dbActive.question,
                options: (dbActive.options || []).map((opt, i) => ({
                  text: opt.text, votes: opt.votes || 0,
                  color: optColors[i % optColors.length],
                })),
                totalVotes: dbActive.totalVotes || 0,
                gateType: parseInt(dbActive.gateType) === 0 ? "free" : parseInt(dbActive.gateType) === 1 ? "lyx" : "nft",
                lyxCost: parseInt(dbActive.gateType) === 1 ? Number(dbActive.votePrice || 0) / 1e18 : 0,
                onchainId: dbActive.onchainId || null,
                endTime: Number(dbActive.endTime || 0),
                timeRemaining: Number(dbActive.endTime || 0) > 0 ? Math.max(0, Number(dbActive.endTime) - Math.floor(Date.now() / 1000)) : null,
                status: "active",
                headerImage: dbActive.headerImage || null,
                optionImages: dbActive.optionImages || [],
              };
            });
            setPollLoading(false);
          } else {
            // No active poll in DB — clear widget
            setActivePoll(null);
            setPollLoading(false);
          }
        }
        setPollLoading(false);
      } catch (err) {
        console.log("Poll DB fetch (non-fatal):", err);
        setPollLoading(false);
      }

      // DB vote count refresh (fast, no RPC — uses indexed vote events)
      try {
        const voteRes = await fetch(`/api/data-polls?creator=${creatorAddr}&votes=true`);
        const voteData = await voteRes.json();
        if (voteData.success && voteData.polls) {
          let excludeIds = new Set();
          for (const p of voteData.polls) {
            if (p.status === "archived" || p.status === "deleted") excludeIds.add(p.id);
          }
          const active = voteData.polls.find(p => (p.status === "active") && !excludeIds.has(p.id));
          if (active) {
            setActivePoll(prev => {
              const sameQuestion = prev && prev.question === active.question;
              if (sameQuestion && prev.totalVotes === (active.totalVotes || 0)) return prev;
              return {
                id: active.id,
                question: active.question,
                options: (active.options || []).map((opt, i) => ({
                  text: opt.text, votes: opt.votes || 0,
                  color: optionColors[i % optionColors.length],
                })),
                totalVotes: active.totalVotes || 0,
                gateType: parseInt(active.gateType) === 0 ? "free" : parseInt(active.gateType) === 1 ? "lyx" : "nft",
                lyxCost: parseInt(active.gateType) === 1 ? Number(active.votePrice || 0) / 1e18 : 0,
                onchainId: active.onchainId || prev?.onchainId || null,
                status: "active",
                endTime: active.endTime || null,
                timeRemaining: active.endTime && active.endTime > 0 ? Math.max(0, active.endTime - Math.floor(Date.now() / 1000)) : null,
                headerImage: prev?.headerImage || null,
                optionImages: prev?.optionImages || [],
              };
            });
          } else {
            // No active poll — clear widget
            setActivePoll(null);
          }
        }
      } catch (err) {
        console.log("Poll vote refresh (non-fatal):", err);
      }
    };
    fetchActivePoll();
    const interval = setInterval(fetchActivePoll, 15000);
    return () => clearInterval(interval);
  }, [standalone, creatorAddressProp, creatorProfileProp]);

  // Listen for new polls
  useEffect(() => {
    const handler = (e) => {
      setActivePoll(e.detail);
      setHasVoted(false);
      setVotedOption(null);
      setPollCollapsed(false);
    };
    window.addEventListener("fullsend-poll", handler);
    return () => window.removeEventListener("fullsend-poll", handler);
  }, []);

  // Tick down poll countdown
  useEffect(() => {
    if (!activePoll || !activePoll.timeRemaining || activePoll.timeRemaining <= 0) return;
    const interval = setInterval(() => {
      setActivePoll(prev => {
        if (!prev || !prev.timeRemaining) return prev;
        if (prev.timeRemaining <= 1) return { ...prev, status: "ended", timeRemaining: 0 };
        return { ...prev, timeRemaining: prev.timeRemaining - 1 };
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [activePoll?.id, activePoll?.timeRemaining > 0]);

  useEffect(() => {
    requestAnimationFrame(() => setMounted(true));
    // In a LUKSO Grid, create the up-provider on load so the host registers this
    // mini-app (shows its connect control) before the user taps connect.
    if (isEmbeddedGrid()) { try { getGridProvider(); } catch (e) {} }
  }, []);

  // Sync wallet state from nav connection
  useEffect(() => {
    if (!connected && window.__fullsend_provider && window.__fullsend_profile) {
      setConnected(true);
      setProfile(window.__fullsend_profile);
    }
  }, [connected]);

  const [followSending, setFollowSending] = useState(false);

  const handleFollow = async () => {
    if (followed || followSending) return;

    // If not connected, trigger wallet connect first
    if (!window.__fullsend_provider || !window.__fullsend_profile?.address) {
      connectWallet();
      return;
    }

    setFollowSending(true);

    try {
      const provider = window.__fullsend_provider;
      const senderProfile = window.__fullsend_profile;
      const creatorAddress = creatorAddressProp || window.__fullsend_context_account || "0x8eF515A8B0d119fc0A331Cb847D9E52B1892f3aa";

      // Check if FullSend gasless is enabled for this user
      const hasGasless = await checkFullSendPermissions(provider, senderProfile.address);

      if (hasGasless) {
        // GASLESS PATH — FullSend relayer handles it, no popup
        const followCalldata = encodeFollowCall(creatorAddress);
        const response = await fetch("/api/relay-action", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            upAddress: senderProfile.address,
            targetContract: LSP26_ADDRESS,
            calldata: followCalldata,
            value: "0",
          }),
        });
        const result = await response.json();
        if (result.success) {
          setFollowSending(false);
          lastFollowTsRef.current = Date.now();
          setFollowed(true);
          setJustFollowed(true);
          setSubCount(prev => prev + 1);
          window.dispatchEvent(new CustomEvent("fullsend-sub", { detail: { id: Date.now(), profile: profile || MOCK_PROFILES[5], txHash: result.txHash } }));
          setTimeout(() => setJustFollowed(false), 2000);
        } else {
          console.error("Gasless follow failed:", result);
          // If relay fails, fall back to direct
          throw new Error(result.error || "Relay failed");
        }
      } else {
        // STANDARD PATH — direct UP.execute, LUKSO relay covers gas.
        const followCalldata = encodeFollowCall(creatorAddress);
        // Optimistic flip: the UP mobile in-app browser often doesn't resolve the
        // send promise even though the tx lands on-chain, so the code after the
        // await would never run. Update the UI now; revert only on an explicit
        // user rejection. The status poll self-corrects any rare failure.
        lastFollowTsRef.current = Date.now();
        setFollowSending(false);
        setFollowed(true);
        setJustFollowed(true);
        setSubCount(prev => prev + 1);
        window.dispatchEvent(new CustomEvent("fullsend-sub", { detail: { id: Date.now(), profile: profile || MOCK_PROFILES[5] } }));
        setTimeout(() => setJustFollowed(false), 2000);
        try {
          await sendUPCall(provider, senderProfile, LSP26_ADDRESS, "0", followCalldata);
        } catch (sendErr) {
          if (sendErr?.code === 4001 || /reject|denied|cancel/i.test(sendErr?.message || "")) {
            lastFollowTsRef.current = 0;
            setFollowed(false);
            setSubCount(prev => Math.max(0, prev - 1));
          }
        }
        return;
      }
    } catch (err) {
      console.error("Follow error:", err);
      setFollowSending(false);
      const msg = err?.message || "Follow failed";
      if (msg.includes("rejected") || msg.includes("denied") || msg.includes("cancelled")) {
        // User rejected — silent, no fallback needed
      } else if (msg.includes("Circuit breaker") || msg.includes("balance too low")) {
        // Don't fallback — show nothing, it'll fail there too
      } else if (msg.includes("insufficient funds") || msg.includes("Relayer out")) {
        // Don't fallback
      } else {
        // Last resort fallback — try direct UP.execute
        try {
          const provider = window.__fullsend_provider;
          const senderProfile = window.__fullsend_profile;
          const creatorAddress = creatorAddressProp || window.__fullsend_context_account || "0x8eF515A8B0d119fc0A331Cb847D9E52B1892f3aa";
          const followCalldata = encodeFollowCall(creatorAddress);
          const txHash = await sendUPCall(provider, senderProfile, LSP26_ADDRESS, "0", followCalldata);
          lastFollowTsRef.current = Date.now();
          setFollowed(true);
          setJustFollowed(true);
          setSubCount(prev => prev + 1);
          window.dispatchEvent(new CustomEvent("fullsend-sub", { detail: { id: Date.now(), profile: profile || MOCK_PROFILES[5], txHash } }));
          setTimeout(() => setJustFollowed(false), 2000);
        } catch (fallbackErr) {
          console.error("Fallback also failed:", fallbackErr);
        }
      }
    }
  };

  const connectWallet = async () => {
    setConnectAnim(true);
    try {
      // Check if already connected via nav
      if (window.__fullsend_provider && window.__fullsend_profile) {
        setConnected(true);
        setProfile(window.__fullsend_profile);
        setConnectAnim(false);
        return;
      }
      // LUKSO Grid mini-app: connect via up-provider ONLY. WalletConnect/extension
      // cannot run inside a Grid iframe — attempting it freezes the app — so when
      // embedded we connect through the host's up-provider and never fall through.
      if (isEmbeddedGrid()) {
        try {
          const gp = getGridProvider();
          if (!gp) throw new Error("Grid provider unavailable");
          let accounts = await gp.request({ method: "eth_requestAccounts" });
          if ((!accounts || !accounts.length) && gp.accounts && gp.accounts.length) accounts = gp.accounts;
          if (!accounts || !accounts.length) throw new Error("No account from Grid host");
          const address = accounts[0];
          // Context account = the profile whose Grid hosts this widget = creator to tip.
          const ctx = (gp.contextAccounts && gp.contextAccounts[0]) || null;
          if (ctx) window.__fullsend_context_account = ctx;
          const prof = await fetchUPProfile(address).catch(() => null);
          const balanceLYX = await rpcBalanceLYX(address, 2);
          const profileData = {
            name: (prof && prof.name) || `${address.slice(0, 6)}...${address.slice(-4)}`,
            address,
            avatar: (prof && prof.avatar) || STOCK_AVATAR,
            balance: balanceLYX,
            isUP: true,
            providerType: "upprovider",
          };
          window.__fullsend_provider = gp;
          window.__fullsend_profile = profileData;
          setConnected(true);
          setProfile(profileData);
        } catch (gridErr) {
          console.log("Grid up-provider connect failed:", gridErr && gridErr.message);
        }
        setConnectAnim(false);
        return; // embedded = up-provider only — never WalletConnect (freezes in an iframe)
      }
      // Otherwise connect fresh — UP extension first, MetaMask, then WalletConnect
      let provider = null;
      let providerType = null;
      if (window.lukso) {
        provider = window.lukso;
        providerType = "up";
      } else if (window.ethereum) {
        provider = window.ethereum;
        providerType = "metamask";
      } else {
        if (isMobileDevice()) {
          // Mobile — use WalletConnect
          try {
            wcDebug("mobile detected → creating WC provider…");
            provider = await createWCProvider();
            wcDebug("provider created (session=" + (provider?.session ? "EXISTS" : "none") + ")");
            // Clear any stale session before connect (see site 1 note) so we get
            // a fresh pairing with valid accounts instead of an empty dead one.
            if (provider.session) {
              wcDebug("clearing stale session before connect…");
              try {
                await Promise.race([
                  provider.disconnect(),
                  new Promise((_, rej) => setTimeout(() => rej(new Error("disconnect timeout")), 4000)),
                ]);
                wcDebug("stale session cleared");
              } catch (de) { wcDebug("disconnect err/timeout: " + ((de && de.message) || de)); }
            }
            wcDebug("calling connect()…");
            await provider.connect();
            wcDebug("connect() RESOLVED ✓ chainId=" + provider?.chainId + " accounts=" + JSON.stringify(provider?.accounts || []) + " ns=" + JSON.stringify(Object.keys(provider?.session?.namespaces || {})));
            providerType = "walletconnect";
          } catch (wcErr) {
            const m = (wcErr && wcErr.message) || String(wcErr || "");
            wcDebug("connect() THREW ✗ : " + m.slice(0, 220));
            console.log("WalletConnect cancelled or failed:", m);
            setConnectAnim(false);
            const cancelled = /reject|cancel|closed|reset by the user|abort/i.test(m) || wcErr?.code === 4001;
            if (!cancelled) {
              alert("Couldn't open the wallet connection.\n\n" + m.slice(0, 180) +
                "\n\nTip: open fullsend.live in Safari or Chrome (not an in-app browser), and make sure the Universal Profile mobile app is installed.");
            }
            return;
          }
        } else {
          alert("No wallet found. Install the UP Browser Extension or MetaMask.");
          setConnectAnim(false);
          return;
        }
      }

      // Ensure we're on LUKSO Testnet
      try {
        await provider.request({
          method: "wallet_switchEthereumChain",
          params: [{ chainId: LUKSO_TESTNET.chainId }],
        });
      } catch (switchErr) {
        if (switchErr.code === 4902) {
          await provider.request({
            method: "wallet_addEthereumChain",
            params: [LUKSO_TESTNET],
          });
        }
      }

      let accounts;
      if (providerType === "walletconnect") {
        accounts = (provider.accounts && provider.accounts.length) ? provider.accounts.slice() : [];
        if (!accounts.length && provider.session && provider.session.namespaces) {
          for (const ns of Object.values(provider.session.namespaces)) {
            if (ns && ns.accounts && ns.accounts.length) {
              accounts = ns.accounts.map((a) => { const p = String(a).split(":"); return p[p.length - 1]; });
              break;
            }
          }
        }
        if (!accounts.length) {
          try { const r = await provider.request({ method: "eth_accounts" }); wcDebug("eth_accounts → " + JSON.stringify(r)); if (r && r.length) accounts = r; } catch (e) { wcDebug("eth_accounts err: " + ((e && e.message) || e)); }
        }
        if (!accounts.length) {
          try { const r = await provider.request({ method: "eth_requestAccounts" }); wcDebug("eth_requestAccounts → " + JSON.stringify(r)); if (r && r.length) accounts = r; } catch (e) { wcDebug("eth_requestAccounts err: " + ((e && e.message) || e)); }
        }
        if (!accounts.length) {
          try { wcDebug("RAW session: " + JSON.stringify(provider.session).slice(0, 700)); } catch (e) {}
        }
        wcDebug("WC accounts final: " + JSON.stringify(accounts));
      } else {
        accounts = await provider.request({ method: "eth_requestAccounts" });
      }
      if (!accounts || accounts.length === 0) { wcDebug("NO ACCOUNTS → bailing"); setConnectAnim(false); return; }
      const address = accounts[0];
      const balanceHex = await provider.request({ method: "eth_getBalance", params: [address, "latest"] });
      const balanceLYX = (Number(BigInt(balanceHex)) / 1e18).toFixed(2);

      const profileData = {
        name: `${address.slice(0, 6)}...${address.slice(-4)}`,
        address: address,
        avatar: STOCK_AVATAR,
        balance: balanceLYX,
        isUP: providerType === "up",
        providerType: providerType,
      };

      // UPs are smart-contract accounts; EOAs have no code. eth_getCode is a
      // reliable isUP signal independent of LSP3 metadata (which can fail to
      // fetch over WalletConnect on mobile — leaving real UPs flagged as EOAs).
      if (!profileData.isUP && (providerType === "walletconnect" || providerType === "up")) {
        try {
          const code = await provider.request({ method: "eth_getCode", params: [address, "latest"] });
          if (code && code !== "0x" && code !== "0x0") profileData.isUP = true;
        } catch (_) {}
      }

      // Fetch LSP3 profile metadata for UP and WalletConnect users
      if (providerType === "up" || providerType === "walletconnect") {
        try {
          const LSP3_KEY = "5ef83ad9559033e6e941db7d7c495acdce616347d28e90c7ce47cbfcfcad3bc5";
          const result = await provider.request({
            method: "eth_call",
            params: [{ to: address, data: "0x54f6127f" + LSP3_KEY }, "latest"],
          });
          if (result && result !== "0x" && result.length > 130) {
            const data = result.slice(2);
            const offset = parseInt(data.slice(0, 64), 16) * 2;
            const length = parseInt(data.slice(offset, offset + 64), 16);
            const contentHex = data.slice(offset + 64, offset + 64 + length * 2);
            let raw = "";
            for (let i = 0; i < contentHex.length; i += 2) {
              raw += String.fromCharCode(parseInt(contentHex.slice(i, i + 2), 16));
            }
            const ipfsIdx = raw.indexOf("ipfs://");
            const httpsIdx = raw.indexOf("https://");
            const urlStart = ipfsIdx > -1 ? ipfsIdx : httpsIdx;
            if (urlStart > -1) {
              let url = raw.slice(urlStart);
              if (url.startsWith("ipfs://")) url = "https://api.universalprofile.cloud/ipfs/" + url.slice(7);
              const resp = await fetch(url);
              const meta = await resp.json();
              const p = meta?.LSP3Profile || meta;
              if (p.name) profileData.name = p.name;
              const img = p.profileImage?.[0]?.url || p.profileImage?.[0]?.src;
              if (img) {
                profileData.avatar = img.startsWith("ipfs://")
                  ? "https://api.universalprofile.cloud/ipfs/" + img.slice(7)
                  : img;
              }
            }
          }
        } catch (err) {
          console.log("LSP3 fetch for tipper (non-fatal):", err);
        }
      }

      window.__fullsend_provider = provider;
      window.__fullsend_profile = profileData;
      setConnected(true);
      setProfile(profileData);
      setConnectAnim(false);

      // Check if user already voted on active poll (fast DB read)
      if (activePoll && activePoll.id) {
        try {
          // Check by on-chain ID first, then frontend ID
          const checkId = activePoll.onchainId || activePoll.id;
          const voteCheckRes = await fetch(`/api/data-polls?pollId=${checkId}&voter=${profileData.address}`);
          const voteCheck = await voteCheckRes.json();
          if (voteCheck.success && voteCheck.voted) {
            setHasVoted(true);
            setVotedOption(voteCheck.choice);
          } else if (activePoll.onchainId && activePoll.id !== activePoll.onchainId) {
            // Also check by frontend ID in case vote was recorded against it
            const voteCheckRes2 = await fetch(`/api/data-polls?pollId=${activePoll.id}&voter=${profileData.address}`);
            const voteCheck2 = await voteCheckRes2.json();
            if (voteCheck2.success && voteCheck2.voted) {
              setHasVoted(true);
              setVotedOption(voteCheck2.choice);
            }
          }
        } catch (err) {
          console.log("Vote check (non-fatal):", err);
        }
      }
    } catch (err) {
      console.error("Widget connection failed:", err);
      setConnectAnim(false);
      if (err.code !== 4001) alert("Connection failed. Please try again.");
    }
  };

  const spawnConfetti = () => {
    const colors = [COLORS.accent, COLORS.blue, COLORS.purple, COLORS.gold, COLORS.cyan];
    const newParticles = Array.from({ length: 14 }, (_, i) => ({
      id: Date.now() + i,
      x: 40 + Math.random() * 20,
      color: colors[Math.floor(Math.random() * colors.length)],
      delay: Math.random() * 0.3,
      duration: 0.6 + Math.random() * 0.5,
      size: 4 + Math.random() * 4,
      drift: -30 + Math.random() * 60,
    }));
    setParticles(newParticles);
    setTimeout(() => setParticles([]), 1500);
  };

  const [txHash, setTxHash] = useState(null);
  const [tipError, setTipError] = useState(null);
  const [feeDisplay, setFeeDisplay] = useState(null);

  // Calculate fee when amount changes
  useEffect(() => {
    let cancelled = false;
    const calcFee = async () => {
      if (!amount || parseFloat(amount) <= 0) { setFeeDisplay(null); return; }
      try {
        const tipWei = lyxToWei(amount);
        const callData = encodeCalcTotalPayment(tipWei);
        const provider = window.__fullsend_provider;
        if (!provider) {
          if (cancelled) return;
          const tipVal = parseFloat(amount);
          setFeeDisplay({ fee: "0.5000", total: (tipVal + 0.50).toFixed(4) });
          return;
        }
        const result = await provider.request({
          method: "eth_call",
          params: [{ to: TIPROUTER_ADDRESS, data: callData }, "latest"],
        });
        if (cancelled) return;
        const totalWei = BigInt(result);
        const totalLyx = weiToLyx(totalWei.toString());
        const tipLyx = parseFloat(amount);
        const feeLyx = totalLyx - tipLyx;
        setFeeDisplay({ fee: feeLyx.toFixed(4), total: totalLyx.toFixed(4) });
      } catch (err) {
        if (cancelled) return;
        console.log("Fee calc error (non-fatal):", err);
        const tipVal = parseFloat(amount);
        setFeeDisplay({ fee: "0.5000", total: (tipVal + 0.50).toFixed(4) });
      }
    };
    const debounce = setTimeout(calcFee, 300);
    return () => { cancelled = true; clearTimeout(debounce); };
  }, [amount]);

  const sendTip = async () => {
    if (!amount || parseFloat(amount) <= 0) return;
    setTipError(null);
    setTxHash(null);

    const provider = window.__fullsend_provider;
    const senderProfile = window.__fullsend_profile;

    if (!provider || !senderProfile) {
      setTipError("Wallet not connected");
      return;
    }

    setSending(true);

    try {
      const creatorAddress = creatorAddressProp || window.__fullsend_context_account || "0x8eF515A8B0d119fc0A331Cb847D9E52B1892f3aa";

      // Block self-tipping
      if (senderProfile.address && creatorAddress.toLowerCase() === senderProfile.address.toLowerCase()) {
        setTipError("You can't tip yourself");
        setSending(false);
        return;
      }

      const tipWei = lyxToWei(amount);
      const feeWei = lyxToWei("0.5");
      const totalPaymentWei = (BigInt(tipWei) + BigInt(feeWei)).toString();
      const messageHash = hashMessage(message);
      const tipCalldata = encodeTipCall(creatorAddress, messageHash);

      let txh;

      if (senderProfile.isUP) {
        const pt = senderProfile.providerType;
        if (isAutoWrapProvider(pt)) {
          // MOBILE / GRID PATH — the UP mobile app and the Grid host auto-wrap
          // `from: UP` transactions into UP.execute themselves, so we send a
          // DIRECT call to TipRouter and let the wallet do the single correct
          // wrap. On the relayer-gas + Grid paths this carries the LYX value.
          const directParams = {
            from: senderProfile.address,
            to: TIPROUTER_ADDRESS,
            value: "0x" + BigInt(totalPaymentWei).toString(16),
            data: tipCalldata,
          };
          try {
            txh = await provider.request({ method: "eth_sendTransaction", params: [directParams] });
          } catch (wcErr) {
            // SELF-PAY FALLBACK ("This smartphone" gas) — the UP mobile app over
            // WalletConnect drops the top-level `value` when auto-wrapping a
            // direct call, so the tip reaches TipRouter with 0 LYX and reverts
            // "Must send LYX". Retry with the value baked INTO the UP.execute
            // calldata, where the wallet can't drop it. Grid (upprovider) is
            // never retried (it double-wraps a manual execute); user rejection
            // (4001) is never retried.
            const wm = (wcErr && wcErr.message) || "";
            const valueDropped = /Must send LYX|estimat(e|ing) gas|gas estimation|execution reverted|reverted by the EVM|insufficient/i.test(wm);
            const codeRetry = wcErr && (wcErr.code === -32603 || wcErr.code === -32000);
            if (pt === "walletconnect" && (valueDropped || codeRetry) && wcErr?.code !== 4001) {
              console.warn("Direct tip failed (likely self-pay value drop) — retrying with manual UP.execute wrap:", wm);
              txh = await provider.request({
                method: "eth_sendTransaction",
                params: [{
                  from: senderProfile.address,
                  to: senderProfile.address,
                  data: encodeUPExecute(TIPROUTER_ADDRESS, totalPaymentWei, tipCalldata),
                  value: "0x0",
                }],
              });
            } else {
              throw wcErr;
            }
          }
        } else {
          // DESKTOP UP BROWSER EXTENSION — manual UP.execute wrap (known-good).
          // LUKSO's relay covers gas; the LYX flow is authorized by the user,
          // never by the FullSend relayer.
          txh = await provider.request({
            method: "eth_sendTransaction",
            params: [{
              from: senderProfile.address,
              to: senderProfile.address,
              data: encodeUPExecute(TIPROUTER_ADDRESS, totalPaymentWei, tipCalldata),
              value: "0x0",
            }],
          });
        }
      } else {
        // EOA — direct transaction to TipRouter, user pays gas
        txh = await provider.request({
          method: "eth_sendTransaction",
          params: [{
            from: senderProfile.address,
            to: TIPROUTER_ADDRESS,
            value: "0x" + BigInt(totalPaymentWei).toString(16),
            data: tipCalldata,
          }],
        });
      }

      setTxHash(txh);
      setSending(false);
      setSent(true);
      spawnConfetti();

      // Show thank you card if enabled
      const tySettings = window.__fullsend_thankyou;
      if (tySettings?.enabled) {
        setTimeout(() => setShowThankYou(true), 1500); // show after confetti settles
      }

      const tip = {
        id: Date.now(),
        from: senderProfile,
        amount: parseFloat(amount),
        message: message || "",
        timestamp: new Date(),
        approved: null,
        txHash: txh,
      };
      window.__fullsend_tips = window.__fullsend_tips || [];
      window.__fullsend_tips.push(tip);
      window.dispatchEvent(new CustomEvent("fullsend-tip", { detail: { ...tip, outgoing: true } }));

      // Record tip to DB so Control page + analytics always have it
      // (every tip path needs this — tips are never recorded by the relayer)
      try {
        const recipient = standalone ? creatorAddressProp : window.__fullsend_profile?.address;
        fetch("/api/data-tips?action=record", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            creator: (recipient || creatorAddress || "").toLowerCase(),
            tipper: senderProfile.address.toLowerCase(),
            amount: parseFloat(amount),
            message: message || "",
            txHash: txh,
          }),
        }).catch(e => console.warn("record-tip failed (non-fatal):", e));
      } catch (e) { console.warn("record-tip error:", e); }

      // Bump the active goal in DB (single source of truth via GoalAPI)
      // recipient = creator being tipped (standalone widget) OR self (own page)
      try {
        const recipient = standalone ? creatorAddressProp : window.__fullsend_profile?.address;
        if (recipient) {
          GoalAPI.bumpProgress(recipient, parseFloat(amount)).then(updated => {
            if (updated) {
              console.log("[goal bump tip]", "current:", updated.current);
              window.dispatchEvent(new CustomEvent("fullsend-goal-updated"));
            }
          });
        }
      } catch (e) { console.warn("Goal bump failed:", e); }
      setTimeout(() => { setSent(false); setShowThankYou(false); setAmount(""); setMessage(""); setFeeDisplay(null); }, 8000);

    } catch (err) {
      console.error("Tip failed:", err);
      setSending(false);
      const msg = err.message || "";
      if (err.code === 4001) {
        setTipError("Transaction rejected");
      } else if (msg.includes("Must send LYX")) {
        setTipError("Wallet didn't attach the LYX — switch gas to the LUKSO relayer and retry");
      } else if (msg.includes("NotAuthorised") || msg.includes("not the owner") || msg.includes("not authorized")) {
        setTipError("Your wallet couldn't authorize this tip — try the LUKSO relayer gas option");
      } else if (msg.includes("insufficient funds") || msg.includes("exceeds balance") || msg.includes("execution reverted")) {
        setTipError("Not enough LYX — use Buy LYX below to top up");
      } else if (msg.includes("Circuit breaker") || msg.includes("balance too low")) {
        setTipError("Service temporarily unavailable — please try again later");
      } else if (msg.includes("chain") || msg.includes("unsupported") || msg.includes("wrong network") || err.code === 4902) {
        setTipError("Switch your wallet to the LUKSO network and try again");
      } else if (msg.includes("Failed to fetch") || err.code === -32603) {
        // Wallet internal RPC failure — common with EOA wallets on LUKSO
        const isEOA = senderProfile && !senderProfile.isUP;
        setTipError(isEOA
          ? "Your wallet couldn't submit — try a Universal Profile for the smoothest experience"
          : "Wallet error — please try again");
      } else if (msg.includes("network") || msg.includes("timeout") || msg.includes("fetch")) {
        setTipError("Network error — check your connection and try again");
      } else if (msg.includes("nonce")) {
        setTipError("Transaction conflict — please try again");
      } else if (msg.includes("Relay failed") || msg.includes("relay") || msg.includes("Relayer out")) {
        setTipError("Service temporarily unavailable — please try again later");
      } else if (msg.includes("budget") || msg.includes("Rate limit") || msg.includes("anti-spam")) {
        setTipError("Too many requests — please wait a moment");
      } else {
        setTipError("Transaction failed — please try again");
      }
      setTimeout(() => setTipError(null), 6000);
    }
  };

  const tier = getTier(parseFloat(amount) || 0);
  const settingsMinTip = parseFloat(window.__fullsend_settings_data?.minTip) || 0;
  const hasAmount = amount && parseFloat(amount) > 0 && (settingsMinTip === 0 || parseFloat(amount) >= settingsMinTip);

  const isLight = widgetTheme.mode === "light";
  const tc = {
    accent: widgetTheme.accent,
    bg: isLight ? "#f8f9fc" : COLORS.bg,
    bgAlt: isLight ? "#f0f1f5" : COLORS.bgAlt,
    surface: isLight ? "#ffffff" : COLORS.surface,
    surfaceAlt: isLight ? "#f4f5f8" : (COLORS.surfaceAlt || "#0a0a12"),
    text: isLight ? "#1a1b2e" : COLORS.text,
    textSecondary: isLight ? "#505070" : COLORS.textSecondary,
    textMuted: isLight ? "#808098" : COLORS.textMuted,
    border: isLight ? "#dfe0ea" : COLORS.border,
    accentBg: isLight ? `${widgetTheme.accent}12` : `${widgetTheme.accent}08`,
    accentBorder: isLight ? `${widgetTheme.accent}30` : `${widgetTheme.accent}25`,
    buttonBg: isLight ? widgetTheme.accent : widgetTheme.accent,
    buttonText: isLight ? "#ffffff" : COLORS.bg,
    shadow: isLight ? "0 2px 12px rgba(0,0,0,0.06)" : "none",
    r: widgetTheme.radius,
  };

  // In light mode, override COLORS locally so all widget elements adapt
  // This avoids changing 100+ individual COLORS.* references
  const WC = isLight ? {
    ...COLORS,
    bg: tc.bg,
    bgAlt: tc.bgAlt,
    surface: tc.surface,
    surfaceAlt: tc.surfaceAlt,
    text: tc.text,
    textSecondary: tc.textSecondary,
    textMuted: tc.textMuted,
    border: tc.border,
    // Darken neon colors for light mode readability
    accent: "#00b366", // darker green (was #00ff88)
    accentGlow: `${tc.accent}30`,
    cyan: "#00a0c0", // darker cyan (was #00e5ff)
    green: "#00b366",
    gold: "#c5a000",
    purple: "#9030d0",
    blue: "#3080d0",
    orange: "#d07000",
    red: "#d03050",
  } : COLORS;

  return (
    <div style={{
      maxWidth: 420, margin: "0 auto", padding: standalone ? "16px 14px" : "24px 20px",
      opacity: mounted ? 1 : 0,
      transform: mounted ? "translateY(0)" : "translateY(20px)",
      transition: "all 0.6s cubic-bezier(0.16, 1, 0.3, 1)",
      position: "relative",
      background: tc.bg,
      color: tc.text,
      borderRadius: tc.r + 4,
      border: isLight ? `1px solid ${tc.border}` : "none",
      boxShadow: isLight ? "0 4px 24px rgba(0,0,0,0.08)" : "none",
    }}>
      {/* In-app browser nudge — WalletConnect can't hand off inside X/IG/etc. webviews */}
      {isInAppBrowser() && !inAppDismissed && !connected && (
        <div style={{
          marginBottom: 14, padding: "10px 12px", borderRadius: 10,
          background: "#f59e0b18", border: "1px solid #f59e0b55",
          display: "flex", alignItems: "flex-start", gap: 8,
          fontSize: 12, lineHeight: 1.45, color: tc.text,
        }}>
          <span style={{ fontSize: 14, flexShrink: 0, lineHeight: 1.4 }}>⚠️</span>
          <div style={{ flex: 1 }}>
            <strong style={{ color: "#f59e0b" }}>Open in your browser to connect.</strong>{" "}
            You're viewing this inside another app. Tap the <strong>⋯</strong> menu and choose <strong>"Open in Browser"</strong> to connect your wallet and tip.
          </div>
          <span onClick={() => setInAppDismissed(true)} style={{
            cursor: "pointer", color: tc.text, opacity: 0.5, fontSize: 14,
            flexShrink: 0, padding: "0 2px", lineHeight: 1.2,
          }}>✕</span>
        </div>
      )}

      {/* Theme Customizer Toggle */}
      {!standalone && (
      <button onClick={() => setShowThemePanel(!showThemePanel)} style={{
        position: "absolute", top: 0, right: 0,
        width: 28, height: 28, borderRadius: 6,
        background: showThemePanel ? `${WC.purple}15` : "transparent",
        border: `1px solid ${showThemePanel ? WC.purple + "25" : "transparent"}`,
        cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 12, zIndex: 10,
      }}>🎨</button>
      )}

      {/* Theme Panel */}
      {!standalone && showThemePanel && (
        <div style={{
          marginBottom: 14, padding: "12px 14px", borderRadius: 10,
          background: WC.surface, border: `1px solid ${WC.purple}15`,
          animation: "slideDown 0.2s ease-out",
        }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: WC.purple, marginBottom: 8, display: "flex", alignItems: "center", gap: 5 }}>
            <span>🎨</span> Widget Theme
          </div>
          <div style={{ fontSize: 9, color: WC.textMuted, marginBottom: 8 }}>Accent Color</div>
          <div style={{ display: "flex", gap: 5, marginBottom: 10 }}>
            {themeAccents.map(a => (
              <button key={a.color} onClick={() => setWidgetTheme(prev => ({ ...prev, accent: a.color }))} style={{
                width: 24, height: 24, borderRadius: 6,
                background: a.color,
                border: widgetTheme.accent === a.color ? `2px solid #fff` : `2px solid transparent`,
                cursor: "pointer",
                boxShadow: widgetTheme.accent === a.color ? `0 0 8px ${a.color}50` : "none",
              }} title={a.label} />
            ))}
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <div>
              <div style={{ fontSize: 9, color: WC.textMuted, marginBottom: 4 }}>Mode</div>
              <div style={{ display: "flex", gap: 3 }}>
                {["dark", "light"].map(m => (
                  <button key={m} onClick={() => setWidgetTheme(prev => ({ ...prev, mode: m }))} style={{
                    padding: "3px 8px", borderRadius: 4, fontSize: 8, fontWeight: 600,
                    background: widgetTheme.mode === m ? `${WC.purple}12` : WC.bgAlt,
                    border: `1px solid ${widgetTheme.mode === m ? WC.purple + "25" : WC.border}`,
                    color: widgetTheme.mode === m ? WC.purple : WC.textMuted,
                    cursor: "pointer", textTransform: "capitalize",
                  }}>{m}</button>
                ))}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 9, color: WC.textMuted, marginBottom: 4 }}>Radius</div>
              <div style={{ display: "flex", gap: 3 }}>
                {[4, 8, 12, 16].map(r => (
                  <button key={r} onClick={() => setWidgetTheme(prev => ({ ...prev, radius: r }))} style={{
                    padding: "3px 8px", borderRadius: 4, fontSize: 8, fontWeight: 600,
                    background: widgetTheme.radius === r ? `${WC.purple}12` : WC.bgAlt,
                    border: `1px solid ${widgetTheme.radius === r ? WC.purple + "25" : WC.border}`,
                    color: widgetTheme.radius === r ? WC.purple : WC.textMuted,
                    cursor: "pointer",
                  }}>{r}px</button>
                ))}
              </div>
            </div>
          </div>
          <div style={{ fontSize: 8, color: WC.textMuted, marginTop: 8 }}>
            Preview updates live. Saved to your channel settings.
          </div>

          {/* Divider */}
          <div style={{ height: 1, background: WC.border, margin: "10px 0" }} />

          {/* Widget Element Toggles */}
          <div style={{ fontSize: 10, fontWeight: 700, color: WC.purple, marginBottom: 8, display: "flex", alignItems: "center", gap: 5 }}>
            <span>🔧</span> Widget Elements
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
            {[
              { key: "socialLinks", label: "Social Links", icon: "🔗", default: true },
              { key: "followers", label: "Followers", icon: "👥", default: true },
              { key: "liveStatus", label: "Live Status", icon: "🔴", default: true },
              { key: "tipGoal", label: "Tip Goal", icon: "🎯", default: true },
              { key: "poll", label: "Active Poll", icon: "📊", default: true },
              { key: "leaderboard", label: "Top Tippers", icon: "🏆", default: true },
              { key: "quickAmounts", label: "Quick Amounts", icon: "💰", default: true },
              { key: "upLink", label: "UP Link", icon: "🔗", default: true },
            ].map(el => {
              const isOn = widgetTheme[el.key] !== false;
              return (
                <button key={el.key} onClick={() => setWidgetTheme(prev => ({ ...prev, [el.key]: !isOn }))} style={{
                  padding: "4px 8px", borderRadius: 5,
                  background: isOn ? `${WC.accent}10` : WC.bgAlt,
                  border: `1px solid ${isOn ? WC.accent + "20" : WC.border}`,
                  color: isOn ? WC.accent : WC.textMuted,
                  fontSize: 8, fontWeight: 600, cursor: "pointer",
                  display: "flex", alignItems: "center", gap: 4,
                  opacity: isOn ? 1 : 0.5,
                }}>
                  <span style={{ fontSize: 10 }}>{el.icon}</span>
                  {el.label}
                </button>
              );
            })}
          </div>
          <div style={{ fontSize: 8, color: WC.textMuted, marginTop: 6 }}>
            Toggle elements on/off to customize what viewers see on your widget.
          </div>

          {/* Save Widget Button */}
          <button onClick={() => {
            const s = window.__fullsend_settings_data || {};
            s.widgetAccent = widgetTheme.accent;
            s.widgetMode = widgetTheme.mode;
            s.widgetRadius = widgetTheme.radius;
            // Also save element toggles
            ["socialLinks","followers","liveStatus","tipGoal","poll","leaderboard","quickAmounts","upLink"].forEach(k => {
              s["widget_" + k] = widgetTheme[k];
            });
            window.__fullsend_settings_data = s;
            try { localStorage.setItem("fullsend_settings", JSON.stringify(s)); } catch {}
            syncCreatorToDB();
            setThemeSaved(true);
            setTimeout(() => setThemeSaved(false), 2000);
          }} style={{
            width: "100%", marginTop: 10, padding: "8px 0", borderRadius: 6,
            background: themeSaved ? `${WC.accent}15` : `linear-gradient(135deg, ${WC.accent}, ${WC.cyan})`,
            border: "none",
            color: themeSaved ? WC.accent : WC.bg,
            fontSize: 11, fontWeight: 700,
            fontFamily: "'Syne', sans-serif",
            cursor: "pointer",
            transition: "all 0.2s",
          }}>
            {themeSaved ? "✓ Saved" : "Save Widget"}
          </button>
        </div>
      )}

      {/* Confetti */}
      {particles.map(p => (
        <div key={p.id} style={{
          position: "absolute", left: `${p.x}%`, bottom: "40%",
          width: p.size, height: p.size, borderRadius: p.size > 6 ? 1 : "50%",
          background: p.color,
          animation: `confetti ${p.duration}s ease-out ${p.delay}s forwards`,
          transform: `translateX(${p.drift}px)`,
          zIndex: 200,
          pointerEvents: "none",
        }} />
      ))}

      {/* WIDGET HEADER */}
      <div style={{
        textAlign: "center", marginBottom: 14,
        background: isLight
          ? `linear-gradient(180deg, ${tc.accent}08, ${tc.bg} 70%)`
          : `linear-gradient(180deg, ${tc.accent}08, transparent 80%)`,
        padding: "36px 24px 16px",
        borderRadius: tc.r,
        border: `1px solid ${tc.border}`,
        boxShadow: tc.shadow,
        position: "relative",
        overflow: "hidden",
      }}>
        {/* Connect banner for unconnected creators (not standalone) */}
        {!standalone && !window.__fullsend_profile?.address && (
          <div style={{
            position: "absolute", top: 0, left: 0, right: 0,
            padding: "8px 16px",
            background: `linear-gradient(135deg, ${WC.accent}20, ${WC.cyan}15)`,
            borderBottom: `1px solid ${WC.accent}20`,
            zIndex: 10,
          }}>
            <div style={{
              fontSize: 11, fontWeight: 700, color: WC.accent,
              fontFamily: "'Syne', sans-serif",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}>
              <span style={{ fontSize: 14 }}>✨</span> Connect your UP to create your widget
            </div>
          </div>
        )}
        <div style={{
          position: "absolute", inset: 0,
          background: `radial-gradient(circle at 50% 0%, ${tc.accent}08 0%, transparent 60%)`,
        }} />
        <div onClick={upProfileUrl ? () => window.open(upProfileUrl, "_blank", "noopener,noreferrer") : undefined}
          title={upProfileUrl ? "View Universal Profile" : undefined} style={{
          width: standalone ? 76 : 96, height: standalone ? 76 : 96, borderRadius: "50%",
          background: `linear-gradient(135deg, ${tc.accent}30, ${WC.cyan}30)`,
          border: `3px solid ${tc.accent}25`,
          display: "flex", alignItems: "center", justifyContent: "center",
          margin: standalone ? "0 auto 10px" : "0 auto 16px", fontSize: standalone ? 32 : 40,
          position: "relative",
          overflow: "hidden",
          cursor: upProfileUrl ? "pointer" : "default",
          marginTop: !standalone && !window.__fullsend_profile?.address ? 16 : 0,
        }}>{(() => {
          // Show placeholder for unconnected creators
          if (!standalone && !window.__fullsend_profile?.address && !creatorProfileProp?.avatar) {
            return <span style={{ fontSize: 10, fontWeight: 700, color: WC.textMuted, fontFamily: "'JetBrains Mono', monospace", textAlign: "center", lineHeight: 1.2 }}>YOUR<br/>PFP<br/>HERE</span>;
          }
          const avatar = standalone ? creatorInfoProp?.avatar : (creatorProfileProp?.avatar || window.__fullsend_profile?.avatar);
          if (isImageAvatar(avatar)) {
            return <img src={avatar} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} onError={e => { e.target.style.display = "none"; e.target.parentNode.innerHTML = '<span style="font-size:36px">👤</span>'; }} />;
          }
          return avatar || <span style={{ fontFamily: "'Syne', sans-serif", fontSize: 36, fontWeight: 900, background: `linear-gradient(135deg, ${tc.accent}, ${WC.cyan})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>F</span>;
        })()}</div>
        <div style={{
          fontFamily: "'Syne', sans-serif",
          fontSize: standalone ? 22 : 26, fontWeight: 800,
          position: "relative",
        }}>{(() => {
          const nameText = standalone && creatorInfoProp?.name ? creatorInfoProp.name : (connected ? (window.__fullsend_settings_data?.channelName || creatorProfileProp?.name || window.__fullsend_profile?.name || "Your Channel") : "Your Name");
          return upProfileUrl ? (
            <span onClick={() => window.open(upProfileUrl, "_blank", "noopener,noreferrer")} title="View Universal Profile"
              style={{ cursor: "pointer", borderBottom: `1px dashed ${tc.accent}55` }}>{nameText}</span>
          ) : nameText;
        })()}</div>

        {/* LIVE badge — shown when creator is broadcasting (with heartbeat) */}
        {(() => {
          // Standalone mode (viewers): use prop from server (already heartbeat-checked)
          if (standalone) return creatorInfoProp?.isLive;
          // Creator's own Tip tab: check window state with heartbeat expiry
          if (connected && window.__fullsend_overlay_state?.isLive) {
            const hb = window.__fullsend_overlay_state.liveHeartbeat || 0;
            return (Date.now() - hb) < 120000;
          }
          return false;
        })() && (() => {
          // Determine which stream URL to link to (respects primaryStream preference)
          const creatorLinks = standalone
            ? (creatorInfoProp?.links || {})
            : {
                youtube: window.__fullsend_settings_data?.socialLinks?.youtube || window.__fullsend_profile?.links?.youtube || "",
                twitch: window.__fullsend_settings_data?.socialLinks?.twitch || window.__fullsend_profile?.links?.twitch || "",
                kick: window.__fullsend_settings_data?.socialLinks?.kick || window.__fullsend_profile?.links?.kick || "",
                rumble: window.__fullsend_settings_data?.socialLinks?.rumble || window.__fullsend_profile?.links?.rumble || "",
                x: window.__fullsend_settings_data?.socialLinks?.x || window.__fullsend_profile?.links?.x || "",
                website: window.__fullsend_settings_data?.socialLinks?.website || window.__fullsend_profile?.links?.website || "",
              };
          // Check creator's primary stream preference (if set), otherwise auto-pick in priority order
          const primary = standalone
            ? (creatorInfoProp?.primaryStream || "auto")
            : (window.__fullsend_overlay_state?.primaryStream || "auto");
          let streamUrl;
          if (primary !== "auto" && creatorLinks[primary]) {
            streamUrl = creatorLinks[primary];
          } else {
            streamUrl = creatorLinks.youtube || creatorLinks.twitch || creatorLinks.kick || creatorLinks.rumble || creatorLinks.x || creatorLinks.website || null;
          }
          const badge = (
            <div style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              marginTop: 6, padding: "4px 12px", borderRadius: 20,
              background: "linear-gradient(135deg, #ff1a1a, #cc0000)",
              border: "1.5px solid #ff4d4d",
              boxShadow: "0 0 16px #ff000050",
              position: "relative",
              animation: "liveBadgePulse 2s infinite",
              cursor: streamUrl ? "pointer" : "default",
              transition: "transform 0.2s",
            }}>
              <span style={{
                width: 7, height: 7, borderRadius: "50%",
                background: "#fff",
                boxShadow: "0 0 6px #fff",
                animation: "livePulse 1.2s infinite",
              }} />
              <span style={{
                fontSize: 10, fontWeight: 900, color: "#fff",
                fontFamily: "'JetBrains Mono', monospace",
                letterSpacing: "1.5px",
              }}>LIVE NOW{streamUrl ? " →" : ""}</span>
            </div>
          );
          return streamUrl ? (
            <a href={streamUrl} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none", display: "inline-block" }}
              onMouseEnter={e => { e.currentTarget.firstChild.style.transform = "scale(1.05)"; }}
              onMouseLeave={e => { e.currentTarget.firstChild.style.transform = "scale(1)"; }}>
              {badge}
            </a>
          ) : badge;
        })()}
        <style>{`
          @keyframes liveBadgePulse {
            0%, 100% { box-shadow: 0 0 16px #ff000050; }
            50% { box-shadow: 0 0 24px #ff000080; }
          }
          @keyframes livePulse {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.6; transform: scale(1.2); }
          }
        `}</style>
        <div style={{
          fontSize: 13, color: WC.textSecondary, marginTop: 4,
          fontFamily: "'JetBrains Mono', monospace",
          position: "relative",
        }}>{standalone && creatorAddressProp ? `${creatorAddressProp.slice(0, 8)}...${creatorAddressProp.slice(-6)}` : (connected ? (creatorProfileProp?.address ? `${creatorProfileProp.address.slice(0, 8)}...${creatorProfileProp.address.slice(-6)}` : (window.__fullsend_profile?.address ? `${window.__fullsend_profile.address.slice(0, 8)}...${window.__fullsend_profile.address.slice(-6)}` : "@your_handle")) : "@your_handle")}</div>

        {/* Bio */}
        {(() => {
          const bio = standalone ? creatorInfoProp?.bio : (connected ? (window.__fullsend_settings_data?.channelBio || creatorProfileProp?.bio || window.__fullsend_profile?.bio) : "Your bio");
          if (!bio || !bio.trim()) return null;
          return (
            <div style={{
              fontSize: 12, color: WC.textSecondary,
              marginTop: 8, position: "relative",
              lineHeight: 1.5,
              fontFamily: "'Outfit', sans-serif",
              maxWidth: 320, margin: "8px auto 0",
              textAlign: "center",
            }}>{bio}</div>
          );
        })()}

        {/* Social / Channel Links */}
        {widgetTheme.socialLinks !== false && (
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "center", gap: 12,
          marginTop: 14, position: "relative",
        }}>
          {(() => {
            // Social links: read from settings (if saved), then UP profile, then empty
            const settings = window.__fullsend_settings_data || {};
            const profileLinks = standalone ? (creatorInfoProp?.links || {}) : (creatorProfileProp?.links || window.__fullsend_profile?.links || {});
            const savedLinks = settings.socialLinks || {};
            // Custom uploaded icons per link: standalone reads creator's DB payload, else own settings
            const socialIcons = standalone ? (creatorInfoProp?.socialIcons || {}) : (window.__fullsend_settings_data?.socialIcons || settings.socialIcons || {});
            
            // Merge: saved settings take priority, then UP profile links
            const links = {
              youtube: savedLinks.youtube || profileLinks.youtube || "",
              twitch: savedLinks.twitch || profileLinks.twitch || "",
              kick: savedLinks.kick || profileLinks.kick || "",
              rumble: savedLinks.rumble || profileLinks.rumble || "",
              x: savedLinks.x || profileLinks.x || "",
              website: savedLinks.website || profileLinks.website || "",
            };
            
            const hasAnyLink = Object.values(links).some(v => v && v.trim());
            const v = standalone ? (creatorInfoProp?.verification || settings.verification || {}) : (settings.verification || {});
            // When unconnected (not standalone), show all icons as placeholders
            const isPlaceholder = !standalone && !connected;
            const socials = [
              { icon: (
                <svg width="18" height="13" viewBox="0 0 20 14" style={{ display: "block" }}>
                  <rect fill="#FF0000" width="20" height="14" rx="3" />
                  <polygon fill="#FFF" points="8,3 8,11 14,7" />
                </svg>
              ), key: "youtube", label: "YouTube", color: "#FF0000", url: isPlaceholder ? "#placeholder" : (links.youtube || "#"), verified: isPlaceholder ? true : v.youtube?.status === "verified" },
              { icon: (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="#9146FF" style={{ display: "block" }}>
                  <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z"/>
                </svg>
              ), key: "twitch", label: "Twitch", color: "#9146FF", url: isPlaceholder ? "#placeholder" : (links.twitch || "#"), verified: isPlaceholder ? true : v.twitch?.status === "verified" },
              { icon: (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="#53FC18" style={{ display: "block" }}>
                  <path d="M5 3v18h4v-6.5l2 2.5 4.5 4H20l-6-7 5.5-8h-4.5L11 12V3z"/>
                </svg>
              ), key: "kick", label: "Kick", color: "#53FC18", url: isPlaceholder ? "#placeholder" : (links.kick || "#"), verified: v.kick?.status === "verified" },
              { icon: (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="#85c742" style={{ display: "block" }}>
                  <path d="M14.4528 13.5458c.8064-.6542.9297-1.8381.2756-2.6445a1.8802 1.8802 0 0 0-.2756-.2756 21.2127 21.2127 0 0 0-4.3121-2.776c-1.066-.51-2.256.2-2.4261 1.414a23.5226 23.5226 0 0 0-.14 5.5021c.116 1.23 1.292 1.964 2.372 1.492a19.6285 19.6285 0 0 0 4.5062-2.704v-.008zm6.9322-5.4002c2.0335 2.228 2.0396 5.637.014 7.8723A26.1487 26.1487 0 0 1 8.2946 23.846c-2.6848.6713-5.4168-.914-6.1662-3.5781-1.524-5.2002-1.3-11.0803.17-16.3045.772-2.744 3.3521-4.4661 6.0102-3.832 4.9242 1.174 9.5443 4.196 13.0764 8.0121v.002z"/>
                </svg>
              ), key: "rumble", label: "Rumble", color: "#85c742", url: isPlaceholder ? "#placeholder" : (links.rumble || "#"), verified: v.rumble?.status === "verified" },
              { icon: (
                <svg width="18" height="18" viewBox="0 0 24 24" fill={isLight ? "#1a1b2e" : "#fff"} style={{ display: "block" }}>
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
              ), key: "x", label: "X", color: isLight ? "#1a1b2e" : "#fff", url: isPlaceholder ? "#placeholder" : (links.x || "#"), verified: v.x?.status === "verified" },
              { icon: (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="#4a9eff" style={{ display: "block" }}>
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                </svg>
              ), key: "website", label: "Website", color: "#4a9eff", url: isPlaceholder ? "#placeholder" : (links.website || "#"), verified: false },
            ];
            // Only show icons that have real URLs (or all for placeholder mode)
            const filtered = isPlaceholder ? socials : socials.filter(s => s.url && s.url !== "#");
            return filtered.map((social, i) => (
            <a key={i} href={social.url.startsWith("#") ? undefined : social.url} target={!social.url.startsWith("#") ? "_blank" : undefined} rel="noopener noreferrer"
              onClick={social.url.startsWith("#") ? (e => e.preventDefault()) : undefined}
              style={{
              width: 44, height: 44, borderRadius: 11,
              background: `${WC.surface}`,
              border: `1px solid ${WC.border}`,
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: !social.url.startsWith("#") ? "pointer" : "default",
              transition: "all 0.2s",
              textDecoration: "none",
              position: "relative",
              opacity: social.url === "#placeholder" ? 0.5 : 1,
            }}
            onMouseEnter={e => {
              if (!social.url.startsWith("#")) {
                e.currentTarget.style.borderColor = `${social.color}40`;
                e.currentTarget.style.background = `${social.color}12`;
                e.currentTarget.style.transform = "translateY(-1px)";
              }
            }}
            onMouseLeave={e => {
              e.currentTarget.style.borderColor = WC.border;
              e.currentTarget.style.background = WC.surface;
              e.currentTarget.style.transform = "translateY(0)";
            }}>
              {socialIcons[social.key] ? (
                <img src={socialIcons[social.key]} alt={social.label} style={{ width: 22, height: 22, borderRadius: 5, objectFit: "cover", display: "block" }} />
              ) : social.icon}
              {social.verified && (
                <div style={{
                  position: "absolute", bottom: -4, right: -4,
                  width: 18, height: 18, borderRadius: "50%",
                  background: WC.accent,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 10, color: WC.bg, fontWeight: 900,
                  border: `2.5px solid ${WC.bg}`,
                  boxShadow: `0 0 6px ${WC.accent}40`,
                }}>{"✓"}</div>
              )}
            </a>
          ));
          })()}
          {(() => {
            // Creator-defined custom links — render after the fixed social icons
            const _s = window.__fullsend_settings_data || {};
            const customLinks = standalone ? (creatorInfoProp?.customLinks || []) : (Array.isArray(_s.customLinks) ? _s.customLinks : []);
            const valid = (customLinks || []).filter(l => l && l.url && l.url.trim() && !l.url.startsWith("#")).slice(0, 6);
            return valid.map((link, i) => (
              <a key={"cl" + (link.id || i)} href={link.url} target="_blank" rel="noopener noreferrer" title={link.url}
                style={{
                  width: 44, height: 44, borderRadius: 11,
                  background: WC.surface, border: `1px solid ${WC.border}`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  cursor: "pointer", transition: "all 0.2s", textDecoration: "none", position: "relative",
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = `${tc.accent}40`; e.currentTarget.style.background = `${tc.accent}12`; e.currentTarget.style.transform = "translateY(-1px)"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = WC.border; e.currentTarget.style.background = WC.surface; e.currentTarget.style.transform = "translateY(0)"; }}>
                {link.icon
                  ? <img src={link.icon} alt="" style={{ width: 22, height: 22, borderRadius: 5, objectFit: "cover", display: "block" }} />
                  : <svg width="18" height="18" viewBox="0 0 24 24" fill={tc.accent} style={{ display: "block" }}>
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                    </svg>}
              </a>
            ));
          })()}
        </div>
        )}

        {/* Subscriber count + follow */}
        {widgetTheme.followers !== false && (
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "center", gap: 12,
          marginTop: 14, position: "relative",
        }}>
          <div style={{
            display: "flex", alignItems: "center", gap: 6,
            padding: "6px 14px", borderRadius: 8,
            background: `${WC.surface}80`,
            border: `1px solid ${WC.border}`,
            fontSize: 13, color: WC.textSecondary,
          }}>
            <span style={{ fontSize: 14 }}>👥</span>
            <span style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontWeight: 700, color: WC.accent,
              display: "inline-block",
              animation: justFollowed ? "countUp 0.3s ease-out" : "none",
            }}>{(() => {
              // Show placeholder count when creator not connected (not standalone)
              if (!standalone && !window.__fullsend_profile?.address && subCount === 0) return "7,250";
              return subCount.toLocaleString();
            })()}</span>
            <span style={{ fontSize: 13, color: WC.textSecondary }}>followers</span>
          </div>

          {!followed && (() => {
            const isEOA = connected && profile && !profile.isUP;
            const disabled = !standalone || followSending || isEOA;
            return (
            <button onClick={standalone && !isEOA ? handleFollow : undefined} disabled={disabled} title={isEOA ? "Requires a LUKSO Universal Profile" : undefined} style={{
              padding: "6px 16px", borderRadius: 8,
              background: followSending ? WC.surface : isEOA ? WC.surface : `${WC.cyan}15`,
              border: `1px solid ${followSending ? WC.border : isEOA ? WC.border : WC.cyan + "30"}`,
              color: followSending || isEOA ? WC.textMuted : WC.cyan,
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 12, fontWeight: 700,
              cursor: standalone && !followSending && !isEOA ? "pointer" : "not-allowed",
              transition: "all 0.2s",
              display: "flex", alignItems: "center", gap: 5,
              position: "relative",
              opacity: standalone && !isEOA ? 1 : 0.6,
            }}
            onMouseEnter={e => { if (standalone && !followSending && !isEOA) { e.currentTarget.style.background = `${WC.cyan}25`; e.currentTarget.style.transform = "scale(1.05)"; } }}
            onMouseLeave={e => { if (standalone && !followSending && !isEOA) { e.currentTarget.style.background = `${WC.cyan}15`; e.currentTarget.style.transform = "scale(1)"; } }}>
              {followSending ? (
                <><span style={{ animation: "pulse 0.8s infinite" }}>●</span> Following...</>
              ) : isEOA ? (
                <><span style={{ fontSize: 11 }}>🔒</span> UP Only</>
              ) : (
                <><span style={{ fontSize: 13 }}>+</span> Follow</>
              )}
            </button>
            );
          })()}
          {followed && standalone && (
            <div style={{
              padding: "6px 14px", borderRadius: 8,
              background: `${WC.accent}10`,
              border: `1px solid ${WC.accent}20`,
              color: WC.accent,
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 12, fontWeight: 700,
              display: "flex", alignItems: "center", gap: 5,
              animation: justFollowed ? "scaleIn 0.3s ease-out" : "none",
            }}>
              ✓ Following
            </div>
          )}
        </div>
        )}


        {/* Gasless UX Toggles — only shown to UP users; EOAs get an upsell card */}
        {connected && profile?.isUP ? (
          <GaslessToggles upAddress={profile?.address || window.__fullsend_profile?.address} provider={window.__fullsend_provider} />
        ) : connected && !profile?.isUP ? (
          <a href={isMobileDevice() ? getUPMobileLink() : "https://my.universalprofile.cloud/"} target="_blank" rel="noopener noreferrer" style={{
            display: "block",
            marginTop: 8, paddingTop: 8,
            borderTop: `1px solid ${WC.border}`,
            textDecoration: "none",
            cursor: "pointer",
            transition: "opacity 0.2s",
          }}
          onMouseEnter={e => e.currentTarget.style.opacity = "0.85"}
          onMouseLeave={e => e.currentTarget.style.opacity = "1"}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6, marginBottom: 4 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontSize: 12 }}>💡</span>
                <span style={{
                  fontSize: 10, fontWeight: 800, color: WC.cyan,
                  fontFamily: "'JetBrains Mono', monospace",
                  letterSpacing: "1.2px", textTransform: "uppercase",
                }}>Upgrade to Universal Profile</span>
              </div>
              <span style={{
                fontSize: 10, fontWeight: 700,
                color: WC.cyan,
                fontFamily: "'JetBrains Mono', monospace",
              }}>{isMobileDevice() ? "Get UP App →" : "Create UP →"}</span>
            </div>
            <div style={{ fontSize: 11, color: WC.textSecondary, lineHeight: 1.5, marginBottom: 4 }}>
              You're connected with a standard wallet. Connect a <strong style={{ color: WC.text }}>LUKSO Universal Profile</strong> to unlock:
            </div>
            <ul style={{ margin: 0, padding: "0 0 0 16px", fontSize: 10, color: WC.textSecondary, lineHeight: 1.55 }}>
              <li><strong style={{ color: WC.accent }}>Gas on us</strong> — zero popups for free votes, token votes &amp; claims</li>
              <li><strong style={{ color: WC.accent }}>On-chain follow</strong> — build creator social graph on LUKSO</li>
              <li><strong style={{ color: WC.accent }}>On-chain voting</strong> — participate in creator polls</li>
              <li><strong style={{ color: WC.accent }}>On-chain PFP + name</strong> on stream alerts</li>
              <li><strong style={{ color: WC.accent }}>Leaderboard badges</strong> + future NFT rewards</li>
              <li><strong style={{ color: WC.accent }}>Nothing to enable</strong> — it's all automatic, everywhere on LUKSO</li>
            </ul>
          </a>
        ) : (
          <GaslessToggles />
        )}
      </div>

      {/* CLAIM DROP — viewer-facing (renders only when a drop is live) */}
      {(standalone || connected) && (
        <ClaimDropCard
          creatorAddr={standalone ? creatorAddressProp : (creatorProfileProp?.address || window.__fullsend_profile?.address)}
          viewerAddr={profile?.address}
          connected={connected}
          onConnect={connectWallet}
          WC={WC}
        />
      )}

      {/* TIP GOAL */}
      {widgetTheme.tipGoal !== false && tipGoal.active && tipGoal.target > 0 && (standalone || connected) && (
        <div style={{
          padding: "10px 14px", borderRadius: 10,
          background: `linear-gradient(135deg, ${WC.surface}, ${WC.accent}06)`,
          border: `1px solid ${tipGoal.current >= tipGoal.target ? WC.accent + "40" : WC.accent + "18"}`,
          marginBottom: 12,
          animation: "fadeInUp 0.4s ease-out",
          boxShadow: `0 0 12px ${WC.accent}08`,
        }}>
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            marginBottom: 8,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{
                fontSize: 9, fontWeight: 800, color: WC.accent,
                background: `${WC.accent}18`, padding: "3px 8px", borderRadius: 5,
                fontFamily: "'JetBrains Mono', monospace",
                letterSpacing: "1.2px", textTransform: "uppercase",
                border: `1px solid ${WC.accent}30`,
              }}>🎯 Goal</span>
              <span style={{ fontSize: 14, fontWeight: 800, color: WC.text, letterSpacing: "0.2px" }}>
                {tipGoal.title}
              </span>
            </div>
            <div style={{
              fontSize: 13, fontWeight: 800,
              fontFamily: "'JetBrains Mono', monospace",
              color: tipGoal.current >= tipGoal.target ? WC.accent : WC.text,
              textShadow: tipGoal.current >= tipGoal.target ? `0 0 8px ${WC.accent}80` : "none",
            }}>
              {tipGoal.current >= tipGoal.target ? "🎉 GOAL MET!" : `${tipGoal.current.toFixed(1)} / ${tipGoal.target} LYX`}
            </div>
          </div>
          <div style={{
            height: 10, borderRadius: 5,
            background: `${WC.bg}80`,
            overflow: "hidden",
            position: "relative",
            border: `1px solid ${WC.accent}10`,
          }}>
            <div style={{
              height: "100%",
              width: `${Math.min((tipGoal.current / tipGoal.target) * 100, 100)}%`,
              borderRadius: 5,
              background: tipGoal.current >= tipGoal.target
                ? `linear-gradient(90deg, ${WC.accent}, ${WC.gold})`
                : `linear-gradient(90deg, ${WC.accent}cc, ${WC.cyan})`,
              transition: "width 0.8s cubic-bezier(0.16, 1, 0.3, 1)",
              boxShadow: `0 0 10px ${WC.accent}40`,
            }} />
          </div>
          <div style={{
            fontSize: 9, color: WC.textMuted, marginTop: 5,
            textAlign: "right",
            fontFamily: "'JetBrains Mono', monospace",
          }}>
            {Math.min((tipGoal.current / tipGoal.target) * 100, 100).toFixed(0)}%
          </div>
        </div>
      )}

      {widgetTheme.poll !== false && pollLoading && !activePoll && (standalone || connected) && (
        <div style={{
          marginBottom: 10, padding: "12px 16px", borderRadius: 8,
          background: WC.surface, border: `1px solid ${WC.cyan}12`,
          display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
        }}>
          {[0,1,2].map(i => (
            <div key={i} style={{
              width: 5, height: 5, borderRadius: "50%", background: WC.cyan,
              animation: `dotBounce 0.6s ease-in-out ${i * 0.15}s infinite`,
            }} />
          ))}
          <span style={{ fontSize: 10, color: WC.textMuted }}>Loading poll...</span>
        </div>
      )}

      {/* ACTIVE POLL */}
      {widgetTheme.poll !== false && activePoll && activePoll.status === "active" && (standalone || connected) && (
        <div style={{
          marginBottom: 10,
          borderRadius: 8,
          background: WC.surface,
          border: `1px solid ${WC.cyan}18`,
          overflow: "hidden",
          animation: "fadeInUp 0.4s ease-out",
        }}>
          {/* Poll header -- clickable to collapse */}
          <div
            onClick={() => setPollCollapsed(!pollCollapsed)}
            style={{
              padding: "8px 12px",
              display: "flex", alignItems: "center", gap: 8,
              cursor: "pointer",
              background: `${WC.cyan}06`,
              borderBottom: pollCollapsed ? "none" : `1px solid ${WC.cyan}10`,
            }}
          >
            <span style={{ fontSize: 12 }}>📊</span>
            <div style={{ flex: 1 }}>
              {activePoll.headerImage && (
                <img src={activePoll.headerImage} alt="" style={{ width: "100%", maxHeight: 150, objectFit: "contain", background: "rgba(0,0,0,0.2)", borderRadius: 6, marginBottom: 6 }} />
              )}
              <div style={{ fontSize: 11, fontWeight: 700, color: WC.text }}>{activePoll.question}</div>
            </div>
            <div style={{
              display: "flex", alignItems: "center", gap: 4,
              fontSize: 8, color: WC.cyan, fontWeight: 700,
            }}>
              {activePoll.timeRemaining > 0 && (
                <span style={{ color: activePoll.timeRemaining < 60 ? WC.red : WC.orange, fontFamily: "'JetBrains Mono', monospace" }}>
                  ⏱ {formatPollTimer(activePoll.timeRemaining)}
                </span>
              )}
              {activePoll.onchainId && (
                <span style={{ fontSize: 7, color: WC.accent, fontFamily: "'JetBrains Mono', monospace", opacity: 0.7 }}>
                  ⛓ #{activePoll.onchainId}
                </span>
              )}
              <span style={{ width: 4, height: 4, borderRadius: "50%", background: WC.cyan, animation: "pulse 1.5s infinite" }} />
              LIVE
            </div>
            <span style={{ fontSize: 10, color: WC.textMuted, transition: "transform 0.2s", transform: pollCollapsed ? "rotate(-90deg)" : "rotate(0)" }}>▾</span>
          </div>

          {/* Poll options -- collapsible */}
          {!pollCollapsed && (
            <div style={{ padding: "8px 12px 10px" }}>
              {connected && profile && !profile.isUP && (
                <div style={{
                  marginBottom: 8, padding: "6px 10px", borderRadius: 6,
                  background: `${WC.cyan}08`, border: `1px solid ${WC.cyan}20`,
                  fontSize: 9, color: WC.cyan,
                  fontFamily: "'JetBrains Mono', monospace",
                  letterSpacing: "0.3px",
                  display: "flex", alignItems: "center", gap: 6,
                }}>
                  <span>🔒</span>
                  <span>Voting requires a Universal Profile</span>
                </div>
              )}
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {activePoll.options.map((opt, i) => {
                  const pct = activePoll.totalVotes > 0 ? (opt.votes / activePoll.totalVotes) * 100 : 0;
                  const isVoted = votedOption === i;
                  const maxVotes = Math.max(...activePoll.options.map(o => o.votes));
                  const isLeading = opt.votes === maxVotes && activePoll.totalVotes > 0;
                  const isEOAVoter = connected && profile && !profile.isUP;
                  return (
                    <button
                      key={i}
                      disabled={hasVoted || votePending || !activePoll.onchainId || isEOAVoter}
                      title={isEOAVoter ? "Voting requires a LUKSO Universal Profile" : undefined}
                      onClick={async () => {
                        if (hasVoted || votePending) return;
                        // EOAs cannot vote — PollVoting contract expects UP
                        if (isEOAVoter) return;
                        // If not connected, trigger connect flow
                        if (!connected) {
                          connectWallet();
                          return;
                        }
                        // Block all polls until on-chain ID is resolved
                        if (!activePoll.onchainId) return;
                        // If followers only and not followed, don't vote
                        if (activePoll.followersOnly && !followed) return;
                        // LYX-gated polls — direct wallet payment (UP popup required)
                        if (activePoll.gateType === "lyx") {
                          setVotePending(true);
                          const chainPollId = activePoll.onchainId;
                          const voterAddress = window.__fullsend_profile?.address;
                          const provider = window.__fullsend_provider;
                          if (!provider || !voterAddress) {
                            alert("Wallet not connected.");
                            return;
                          }
                          if (chainPollId) {
                            // UP PATH — user signs tx via UP Browser Extension;
                            // LUKSO public relay covers gas. The LYX cost moves
                            // from the UP to the creator under user authorization.
                            try {
                              const voteCalldata = encodeVoteCall(chainPollId, i);
                              const txHash = await sendUPCall(provider, { address: voterAddress, providerType: window.__fullsend_profile?.providerType }, POLL_VOTING_ADDRESS, lyxToWei(activePoll.lyxCost || 0.01), voteCalldata);
                              console.log("LYX vote tx:", txHash);
                              setVoteOnChain(true);
                              setHasVoted(true);
                              setVotedOption(i);
                              // LYX votes pay the creator — count toward active goal like a tip
                              try {
                                const voteAmt = parseFloat(activePoll.lyxCost || 0.01);
                                window.__fullsend_tips = window.__fullsend_tips || [];
                                const synthTip = { amount: voteAmt, tipper: voterAddress, message: `Poll vote: ${activePoll.question}`, txHash, source: "poll-vote", ts: Date.now() };
                                window.__fullsend_tips.push(synthTip);
                                window.dispatchEvent(new CustomEvent("fullsend-tip", { detail: { ...synthTip, outgoing: true } }));
                                const recipient = standalone ? creatorAddressProp : window.__fullsend_profile?.address;
                                if (recipient) {
                                  GoalAPI.bumpProgress(recipient, voteAmt).then(updated => {
                                    if (updated) {
                                      console.log("[goal bump vote]", "current:", updated.current);
                                      window.dispatchEvent(new CustomEvent("fullsend-goal-updated"));
                                    }
                                  });
                                }
                              } catch (e) { console.warn("Goal bump on vote failed (non-fatal):", e); }
                              setActivePoll(prev => {
                                const updated = { ...prev, options: prev.options.map((o, oi) => oi === i ? { ...o, votes: o.votes + 1 } : o) };
                                updated.totalVotes = updated.options.reduce((s, o) => s + o.votes, 0);
                                return updated;
                              });
                              // Record vote in DB
                              await fetch("/api/data-polls", {
                                method: "POST",
                                headers: { "Content-Type": "application/json" },
                                body: JSON.stringify({
                                  action: "recordVote",
                                  pollId: chainPollId,
                                  voter: voterAddress.toLowerCase(),
                                  optionIndex: i,
                                  txHash,
                                }),
                              });
                            } catch (err) {
                              console.error("LYX vote failed:", err);
                              setVotePending(false);
                              if (err.code !== 4001) alert("Vote failed: " + (err.message || "Unknown error"));
                            }
                          } else {
                            // No on-chain ID — should not reach here (button disabled), but safety check
                            setVotePending(false);
                            console.log("LYX poll: no on-chain ID, vote blocked");
                          }
                          return;
                        }
                        setVotePending(true);
                        // For token-gated polls, wait for relay confirmation before showing success
                        if (activePoll.gateType === "nft") {
                          try {
                            const voterAddress = window.__fullsend_profile?.address;
                            const chainPollId = activePoll.onchainId;
                            if (!voterAddress || !chainPollId) {
                              setVotePending(false);
                              return;
                            }
                            const response = await fetch("/api/relay-action", {
                              method: "POST",
                              headers: { "Content-Type": "application/json" },
                              body: JSON.stringify({
                                action: "votePoll",
                                pollId: chainPollId,
                                voter: voterAddress,
                                optionIndex: i,
                              }),
                            });
                            const result = await response.json();
                            if (result.success) {
                              console.log("Token-gated vote recorded on-chain:", result.txHash);
                              setVoteOnChain(true);
                              // Dual-write: record in DB so Polls tab shows vote immediately
                              fetch("/api/data-polls", {
                                method: "POST",
                                headers: { "Content-Type": "application/json" },
                                body: JSON.stringify({
                                  action: "recordVote",
                                  pollId: activePoll.onchainId,
                                  voter: (window.__fullsend_profile?.address || "").toLowerCase(),
                                  optionIndex: i,
                                  txHash: result.txHash,
                                }),
                              }).catch(() => {});
                              setHasVoted(true);
                              setVotedOption(i);
                              setActivePoll(prev => {
                                const updated = { ...prev, options: prev.options.map((o, oi) => oi === i ? { ...o, votes: o.votes + 1 } : o) };
                                updated.totalVotes = updated.options.reduce((s, o) => s + o.votes, 0);
                                return updated;
                              });
                            } else {
                              console.error("Token-gated vote rejected:", result.error);
                              const ge = (result.error || "").toLowerCase();
                              setVoteError(
                                ge.includes("insufficient") || ge.includes("token") || ge.includes("revert") || ge.includes("custom error") || ge.includes("not allowed") ? "Token not owned"
                                : ge.includes("circuit breaker") || ge.includes("balance too low") ? "Voting temporarily unavailable"
                                : ge.includes("rate limit") || ge.includes("budget") ? "Too many votes — please wait"
                                : ge.includes("already") ? "You already voted"
                                : (result.error || "Vote failed")
                              );
                            }
                          } catch (err) {
                            console.error("Token-gated vote error:", err);
                            setVoteError("Vote failed — please try again");
                          }
                          setVotePending(false);
                          return;
                        }
                        // Free votes — optimistic UI
                        setHasVoted(true);
                        setVotedOption(i);
                        setActivePoll(prev => {
                          const updated = { ...prev, options: prev.options.map((o, oi) => oi === i ? { ...o, votes: o.votes + 1 } : o) };
                          updated.totalVotes = updated.options.reduce((s, o) => s + o.votes, 0);
                          return updated;
                        });
                        // Send vote to relay (non-blocking)
                        try {
                          const voterAddress = window.__fullsend_profile?.address;
                          const chainPollId = activePoll.onchainId;
                          if (voterAddress && chainPollId) {
                            const response = await fetch("/api/relay-action", {
                              method: "POST",
                              headers: { "Content-Type": "application/json" },
                              body: JSON.stringify({
                                action: "votePoll",
                                pollId: chainPollId,
                                voter: voterAddress,
                                optionIndex: i,
                              }),
                            });
                            const result = await response.json();
                            if (result.success) {
                              console.log("Vote recorded on-chain:", result.txHash);
                              setVoteOnChain(true);
                              // Dual-write: record in DB so Polls tab shows vote immediately
                              fetch("/api/data-polls", {
                                method: "POST",
                                headers: { "Content-Type": "application/json" },
                                body: JSON.stringify({
                                  action: "recordVote",
                                  pollId: chainPollId,
                                  voter: voterAddress.toLowerCase(),
                                  optionIndex: i,
                                  txHash: result.txHash,
                                }),
                              }).catch(() => {});
                            } else {
                              console.error("Vote relay failed:", result.error);
                            }
                          } else if (voterAddress && !chainPollId) {
                            // No on-chain ID yet — record vote in DB directly
                            console.log("No on-chain poll ID, recording vote in DB only");
                            await fetch("/api/data-polls", {
                              method: "POST",
                              headers: { "Content-Type": "application/json" },
                              body: JSON.stringify({
                                action: "recordVote",
                                pollId: activePoll.id,
                                voter: voterAddress.toLowerCase(),
                                optionIndex: i,
                              }),
                            });
                          }
                        } catch (err) {
                          console.error("Vote relay error (non-fatal):", err);
                        }
                      }}
                      style={{
                        position: "relative",
                        padding: "7px 10px",
                        borderRadius: 6,
                        overflow: "hidden",
                        background: isVoted ? `${opt.color}12` : `${WC.bg}50`,
                        border: `1px solid ${isVoted ? opt.color + "30" : hasVoted ? WC.border : WC.border}`,
                        cursor: hasVoted || votePending ? "default" : "pointer",
                        transition: "all 0.2s",
                        textAlign: "left",
                        opacity: (hasVoted && !isVoted) ? 0.6 : !activePoll.onchainId ? 0.5 : 1,
                      }}
                      onMouseEnter={e => { if (!hasVoted) { e.currentTarget.style.borderColor = `${opt.color}30`; e.currentTarget.style.background = `${opt.color}08`; } }}
                      onMouseLeave={e => { if (!hasVoted) { e.currentTarget.style.borderColor = WC.border; e.currentTarget.style.background = `${WC.bg}50`; } }}
                    >
                      {/* Result bar -- shows after voting */}
                      {hasVoted && (
                        <div style={{
                          position: "absolute", left: 0, top: 0, bottom: 0,
                          width: `${pct}%`,
                          background: `${opt.color}15`,
                          borderRadius: 6,
                          transition: "width 0.6s cubic-bezier(0.16, 1, 0.3, 1)",
                        }} />
                      )}
                      <div style={{
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        position: "relative", zIndex: 1,
                      }}>
                        <span style={{
                          fontSize: 12,
                          fontWeight: isVoted || isLeading ? 700 : 500,
                          color: isVoted ? opt.color : hasVoted && isLeading ? opt.color : WC.textSecondary,
                          display: "flex", alignItems: "center", gap: 6,
                        }}>
                          {isVoted && <span style={{ fontSize: 10 }}>✓</span>}
                          {activePoll.optionImages?.[i] && (
                            <img src={activePoll.optionImages[i]} alt="" style={{ width: 28, height: 28, borderRadius: 4, objectFit: "cover" }} />
                          )}
                          {opt.text}
                        </span>
                        {hasVoted && (
                          <span style={{
                            fontSize: 9, fontWeight: 700,
                            color: isLeading ? opt.color : WC.textMuted,
                            fontFamily: "'JetBrains Mono', monospace",
                          }}>{pct.toFixed(0)}%</span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Footer */}
              <div style={{
                marginTop: 8,
                display: "flex", alignItems: "center", justifyContent: "space-between",
                fontSize: 10, color: WC.textMuted,
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                  <span style={{
                    padding: "3px 8px", borderRadius: 5, fontWeight: 700, fontSize: 10,
                    background: activePoll.gateType === "free" ? `${WC.accent}15` : activePoll.gateType === "lyx" ? "#f5a62318" : "#9146FF15",
                    color: activePoll.gateType === "free" ? WC.accent : activePoll.gateType === "lyx" ? "#f5a623" : "#9146FF",
                    border: `1px solid ${activePoll.gateType === "free" ? WC.accent + "30" : activePoll.gateType === "lyx" ? "#f5a62330" : "#9146FF30"}`,
                  }}>
                    {activePoll.gateType === "free" ? "🟢 Free" : activePoll.gateType === "lyx" ? `🪙 ${activePoll.lyxCost || 0.01} LYX to vote` : hasVoted ? "✅ Token Verified" : `🔒 ${activePoll.nftRequirement ? (Array.isArray(activePoll.nftRequirement) ? activePoll.nftRequirement.map(r => r.name || r.symbol || "NFT").join(" + ") : "Token Gated") : "Token Gated"}`}
                  </span>
                  {activePoll.followersOnly && <span style={{ padding: "3px 8px", borderRadius: 5, fontWeight: 700, fontSize: 10, background: "#9146FF10", color: "#9146FF", border: "1px solid #9146FF25" }}>👥 Followers</span>}
                  <span style={{ fontWeight: 600, fontSize: 10 }}>{activePoll.totalVotes} vote{activePoll.totalVotes !== 1 ? "s" : ""}</span>
                </div>
                {votePending && !hasVoted && !voteError && <span style={{ color: WC.orange, fontWeight: 700, fontSize: 10 }}>⏳ Voting...</span>}
                {voteError && <span style={{ color: WC.red || "#ff4444", fontWeight: 700, fontSize: 10, cursor: "pointer" }} onClick={() => setVoteError(null)}>🚫 {voteError}</span>}
                {hasVoted && (
                  <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    {!voteOnChain && <span style={{ color: WC.orange, fontWeight: 700, fontSize: 10 }}>⏳ Confirming on-chain...</span>}
                    {voteOnChain && <a href={`https://explorer.execution.mainnet.lukso.network/address/${POLL_VOTING_ADDRESS}`} target="_blank" rel="noopener noreferrer" style={{ color: "#ff8c00", fontWeight: 700, fontSize: 10, textDecoration: "none", cursor: "pointer" }}>⛓ On-chain</a>}
                    <span style={{ color: WC.accent, fontWeight: 700, fontSize: 10 }}>✓ Vote recorded</span>
                  </span>
                )}
                {!connected && !hasVoted && !voteError && <span style={{ color: WC.cyan, fontWeight: 600, fontSize: 10 }}>Connect UP to vote</span>}
                {connected && !hasVoted && !voteError && activePoll.followersOnly && !followed && <span style={{ color: WC.purple, fontWeight: 600, fontSize: 10 }}>Follow to vote</span>}
                {connected && !hasVoted && !voteError && !votePending && !(activePoll.followersOnly && !followed) && !activePoll.onchainId && <span style={{ color: WC.orange, fontWeight: 600, fontSize: 10 }}>⏳ Setting up on-chain — voting unlocks shortly</span>}
                {connected && !hasVoted && !voteError && !votePending && !(activePoll.followersOnly && !followed) && activePoll.onchainId && <span style={{ color: WC.textMuted, fontWeight: 600, fontSize: 10 }}>1 vote per UP</span>}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Moments on Chain — shows before connect (like polls/goals); only when an active MOC exists */}
      {(standalone || connected) && (
        <MOCWidgetSection
          creatorAddr={widgetCreatorAddr}
          viewerAddr={profile?.address || window.__fullsend_profile?.address}
          connected={connected}
          onConnect={connectWallet}
          wc={WC}
        />
      )}

      {/* CONNECT OR TIP */}
      {!connected ? (
        <>
        {/* Pre-connect teaser — what connecting unlocks */}
        <div style={{
          marginBottom: 10, padding: "10px 14px", borderRadius: 10,
          background: `linear-gradient(135deg, ${WC.accent}12, ${WC.cyan}08)`,
          border: `1px solid ${WC.accent}22`, textAlign: "center",
        }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: WC.accent, fontFamily: "'Syne', sans-serif", letterSpacing: "0.3px", marginBottom: 6, textTransform: "uppercase" }}>Connect to unlock</div>
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "center",
            gap: 4, flexWrap: "nowrap", whiteSpace: "nowrap",
            fontSize: 9.5, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace",
          }}>
            <span style={{ color: WC.orange }}>💸 Tip</span>
            <span style={{ color: WC.textMuted, opacity: 0.4 }}>·</span>
            <span style={{ color: "#4a9eff" }}>🗳️ Vote</span>
            <span style={{ color: WC.textMuted, opacity: 0.4 }}>·</span>
            <span style={{ color: WC.purple }}>➕ Follow</span>
            <span style={{ color: WC.textMuted, opacity: 0.4 }}>·</span>
            <span style={{ color: WC.cyan }}>🎁 Claim</span>
          </div>
        </div>
        <button onClick={connectWallet} disabled={connectAnim} style={{
          width: "100%", padding: "15px",
          background: connectAnim
            ? WC.surface
            : `linear-gradient(135deg, ${WC.accent}, ${WC.cyan})`,
          color: connectAnim ? WC.textMuted : WC.bg,
          border: "none", borderRadius: 12,
          fontFamily: "'Syne', sans-serif",
          fontSize: 15, fontWeight: 700,
          cursor: connectAnim ? "wait" : "pointer",
          transition: "all 0.35s cubic-bezier(0.4,0,0.2,1)",
          boxShadow: connectAnim ? "none" : `0 4px 24px ${WC.accentGlow}`,
          position: "relative",
          overflow: "hidden",
        }}
        onMouseEnter={e => { if (!connectAnim) e.currentTarget.style.transform = "translateY(-2px)"; }}
        onMouseLeave={e => e.currentTarget.style.transform = "translateY(0)"}>
          {connectAnim ? (
            <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
              <span style={{ display: "inline-flex", gap: 3 }}>
                {[0,1,2].map(i => (
                  <span key={i} style={{
                    width: 5, height: 5, borderRadius: "50%",
                    background: WC.accent,
                    animation: `dotBounce 0.6s ease-in-out ${i * 0.15}s infinite`,
                  }} />
                ))}
              </span>
              Connecting...
            </span>
          ) : "Connect"}
        </button>
        {widgetTheme.upLink !== false && (
        <div style={{ textAlign: "center", marginTop: 8 }}>
          <a href={isMobileDevice() ? getUPMobileLink() : "https://my.universalprofile.cloud/"} target="_blank" rel="noopener noreferrer" style={{
            fontSize: 10, color: WC.cyan, fontWeight: 600,
            fontFamily: "'JetBrains Mono', monospace",
            textDecoration: "none",
          }}>
            Don't have a Universal Profile? {isMobileDevice() ? "Get the app" : "Create one here"}
          </a>
          <div style={{ marginTop: 4 }}>
            <a href="https://transak.com/buy/lyx" target="_blank" rel="noopener noreferrer" style={{
              fontSize: 10, color: WC.textMuted, fontWeight: 600,
              fontFamily: "'JetBrains Mono', monospace",
              textDecoration: "none",
              transition: "color 0.2s",
            }}
            onMouseEnter={e => e.currentTarget.style.color = WC.accent}
            onMouseLeave={e => e.currentTarget.style.color = WC.textMuted}>
              Need LYX? Buy here →
            </a>
          </div>
        </div>
        )}
        </>
      ) : (
        <div style={{ animation: "fadeInUp 0.4s ease-out" }}>
          {/* Connected Badge */}
          <div style={{
            display: "flex", alignItems: "center", gap: 10,
            padding: "10px 14px", borderRadius: 10,
            background: `${WC.accent}08`,
            border: `1px solid ${WC.accent}18`,
            marginBottom: 18,
            animation: "scaleIn 0.3s ease-out",
          }}>
            <div style={{
              width: 38, height: 38, borderRadius: 9,
              background: `${WC.accent}12`,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 18, overflow: "hidden",
            }}>{isImageAvatar(profile.avatar) ? (
              <img src={profile.avatar} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            ) : (profile.avatar || "👤")}</div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{profile.name}</div>
              <div style={{ fontSize: 10, color: WC.textMuted, fontFamily: "'JetBrains Mono', monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{profile.address?.slice(0, 10)}...{profile.address?.slice(-6)}</div>
            </div>
            <div style={{
              marginLeft: "auto",
              display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2,
            }}>
              <div style={{
                display: "flex", alignItems: "center", gap: 5,
                fontSize: 10, color: WC.accent, fontWeight: 600,
              }}>
                <div style={{
                  width: 6, height: 6, borderRadius: "50%",
                  background: WC.accent,
                  boxShadow: `0 0 8px ${WC.accent}`,
                  animation: "pulse 2s infinite",
                }} />
                Connected
              </div>
              <div style={{
                fontSize: 11, fontWeight: 700,
                fontFamily: "'JetBrains Mono', monospace",
                color: WC.text,
                display: "flex", alignItems: "center", gap: 4,
              }}>
                {profile?.balance || "0"} <span style={{ fontSize: 9, color: WC.textMuted, fontWeight: 600 }}>LYX</span>
              </div>
            </div>
          </div>

          {/* Send a Tip banner — labels the tier shortcuts directly below it */}
          <div
            onClick={() => { if (!connected) connectWallet(); }}
            style={{
              marginBottom: 12, padding: "9px 14px", borderRadius: 10,
              background: `linear-gradient(135deg, ${WC.accent}12, ${WC.cyan}08)`,
              border: `1px solid ${WC.accent}22`,
              cursor: connected ? "default" : "pointer",
            }}
          >
            <div style={{
              fontSize: 12, fontWeight: 800, color: WC.accent,
              fontFamily: "'Syne', sans-serif", letterSpacing: "0.3px",
              textAlign: "center", marginBottom: 5,
            }}>💸 Send a Tip</div>
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "center",
              gap: 3, flexWrap: "nowrap",
              fontSize: 8, fontWeight: 700,
              fontFamily: "'JetBrains Mono', monospace",
              whiteSpace: "nowrap",
            }}>
              <span style={{ color: WC.orange }}>⚡ Micro</span>
              <span style={{ color: WC.textMuted, opacity: 0.4 }}>›</span>
              <span style={{ color: "#00cc66" }}>💚 Supporter</span>
              <span style={{ color: WC.textMuted, opacity: 0.4 }}>›</span>
              <span style={{ color: "#4a9eff" }}>💪 Champion</span>
              <span style={{ color: WC.textMuted, opacity: 0.4 }}>›</span>
              <span style={{ color: "#b44aff" }}>💎 Legend</span>
              <span style={{ color: WC.textMuted, opacity: 0.4 }}>›</span>
              <span style={{ color: "#ffd700" }}>🐋 Whale</span>
            </div>
          </div>

          {/* Quick Amounts — tier shortcuts (click to set tier minimum) */}
          {widgetTheme.quickAmounts !== false && (() => {
            const ct = window.__fullsend_settings_data?.customTiers || {};
            const tierButtons = [
              { key: "micro", emoji: "⚡", label: "Micro", color: WC.orange, amount: parseFloat(ct.micro?.min) || 0 },
              { key: "supporter", emoji: "💚", label: "Supporter", color: "#00cc66", amount: parseFloat(ct.supporter?.min) || 5 },
              { key: "champion", emoji: "💪", label: "Champion", color: "#4a9eff", amount: parseFloat(ct.champion?.min) || 20 },
              { key: "legend", emoji: "💎", label: "Legend", color: "#b44aff", amount: parseFloat(ct.legend?.min) || 50 },
              { key: "whale", emoji: "🐋", label: "Whale", color: "#ffd700", amount: parseFloat(ct.whale?.min) || 100 },
            ];
            // Ensure micro button sends something > 0 (in case min is 0)
            if (tierButtons[0].amount <= 0) tierButtons[0].amount = Math.max(0.1, (parseFloat(ct.supporter?.min) || 5) / 5);
            return (
              <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
                {tierButtons.map(t => {
                  const isSelected = parseFloat(amount) === t.amount;
                  return (
                    <button key={t.key} onClick={() => { setAmount(String(t.amount)); setTxHash(null); }} title={`${t.label} — ${t.amount} LYX`} style={{
                      flex: 1, padding: "10px 0",
                      background: isSelected ? `${t.color}15` : WC.surface,
                      border: `1px solid ${isSelected ? t.color + "35" : WC.border}`,
                      borderRadius: 8, color: isSelected ? t.color : WC.textSecondary,
                      fontFamily: "'Outfit', sans-serif",
                      fontSize: 18, fontWeight: 600,
                      cursor: "pointer",
                      transition: "all 0.2s cubic-bezier(0.4,0,0.2,1)",
                      transform: isSelected ? "scale(1.04)" : "scale(1)",
                      boxShadow: isSelected ? `0 0 12px ${t.color}20` : "none",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      {t.emoji}
                    </button>
                  );
                })}
              </div>
            );
          })()}

          {/* Custom Amount */}
          <div style={{ position: "relative", marginBottom: 10 }}>
            <input
              type="number"
              value={amount}
              onChange={e => { setAmount(e.target.value); setTxHash(null); }}
              onWheel={e => e.target.blur()}
              placeholder="Custom amount"
              style={{
                width: "100%", padding: "14px 60px 14px 16px",
                background: WC.surface,
                border: `1px solid ${hasAmount ? (tier.color + "30") : WC.border}`,
                borderRadius: 10, color: WC.text,
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 16, fontWeight: 600,
                outline: "none", boxSizing: "border-box",
                transition: "border-color 0.3s, box-shadow 0.3s",
                boxShadow: hasAmount ? `0 0 0 1px ${tier.color}10` : "none",
              }}
              onFocus={e => { if (!hasAmount) e.currentTarget.style.borderColor = `${WC.accent}25`; }}
              onBlur={e => { if (!hasAmount) e.currentTarget.style.borderColor = WC.border; }}
            />
            <span style={{
              position: "absolute", right: 16, top: "50%",
              transform: "translateY(-50%)",
              color: hasAmount ? tier.color : WC.textMuted,
              fontSize: 13, fontWeight: 700,
              transition: "color 0.3s",
            }}>LYX</span>
          </div>

          {/* Tier Indicator */}
          {hasAmount && (
            <div style={{
              display: "flex", alignItems: "center", gap: 8,
              padding: "8px 12px", borderRadius: 8,
              background: `${tier.color}08`,
              border: `1px solid ${tier.color}12`,
              marginBottom: 10,
              fontSize: 12,
              animation: "slideDown 0.25s ease-out",
            }}>
              <span style={{ fontSize: 14 }}>{tier.emoji}</span>
              <span style={{ color: tier.color, fontWeight: 700 }}>{tier.label}</span>
              <span style={{ color: WC.textMuted, marginLeft: "auto", fontSize: 10, display: "flex", alignItems: "center", gap: 4 }}>
                {(() => {
                  const label = tier.label?.toLowerCase();
                  if (label === "micro") return "Handle + amount (no msg)";
                  const descByLabel = {
                    supporter: "PFP + handle + 80 char msg",
                    champion: "120 char msg + TTS",
                    legend: "150 char msg + extended TTS",
                    whale: "200 char msg + Elite alert + TTS",
                  };
                  return (
                    <>
                      {profile.avatar && typeof profile.avatar === "string" && profile.avatar.startsWith("http") ? (
                        <img src={profile.avatar} alt="" style={{
                          width: 16, height: 16, borderRadius: 4,
                          objectFit: "cover",
                          border: `1px solid ${tier.color}30`,
                        }} onError={e => { e.target.style.display = "none"; }} />
                      ) : (
                        <span style={{
                          width: 16, height: 16, borderRadius: 4,
                          background: `${tier.color}20`,
                          border: `1px solid ${tier.color}30`,
                          display: "inline-flex", alignItems: "center", justifyContent: "center",
                          fontSize: 9,
                        }}>👤</span>
                      )}
                      {descByLabel[label] || "PFP + handle + message"}
                    </>
                  );
                })()}
              </span>
              <button onClick={(e) => { e.stopPropagation(); setShowTiersList(s => !s); }} style={{
                background: "transparent", border: "none", cursor: "pointer",
                color: WC.textMuted, fontSize: 10, padding: "2px 6px",
                fontFamily: "'JetBrains Mono', monospace",
                marginLeft: 4,
              }} title="Show all tier thresholds">{showTiersList ? "▲" : "▼"}</button>
            </div>
          )}

          {/* Tier thresholds list — expandable */}
          {hasAmount && showTiersList && (() => {
            const ct = window.__fullsend_settings_data?.customTiers || {};
            const rows = [
              { key: "micro", label: "Micro", emoji: "⚡", color: WC.orange, desc: "Handle + amount only", min: parseFloat(ct.micro?.min) || 0, max: (parseFloat(ct.supporter?.min) || 5) - 0.01 },
              { key: "supporter", label: "Supporter", emoji: "💚", color: "#00cc66", desc: "PFP + handle + 80 char msg", min: parseFloat(ct.supporter?.min) || 5, max: (parseFloat(ct.champion?.min) || 20) - 0.01 },
              { key: "champion", label: "Champion", emoji: "💪", color: "#4a9eff", desc: "120 char msg + TTS", min: parseFloat(ct.champion?.min) || 20, max: (parseFloat(ct.legend?.min) || 50) - 0.01 },
              { key: "legend", label: "Legend", emoji: "💎", color: "#b44aff", desc: "150 char msg + extended TTS", min: parseFloat(ct.legend?.min) || 50, max: (parseFloat(ct.whale?.min) || 100) - 0.01 },
              { key: "whale", label: "Whale", emoji: "🐋", color: "#ffd700", desc: "200 char msg + Elite alert + TTS", min: parseFloat(ct.whale?.min) || 100, max: null },
            ];
            return (
              <div style={{
                marginBottom: 10, padding: "10px 12px", borderRadius: 8,
                background: WC.surface, border: `1px solid ${WC.border}`,
                animation: "slideDown 0.25s ease-out",
              }}>
                <div style={{ fontSize: 9, color: WC.textMuted, marginBottom: 8, textTransform: "uppercase", letterSpacing: "1.2px", fontWeight: 600 }}>Tier Thresholds</div>
                {rows.map(r => (
                  <div key={r.key} style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "6px 0",
                    borderTop: r.key !== "micro" ? `1px solid ${WC.border}40` : "none",
                    gap: 8,
                  }}>
                    <span style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                      <span style={{ fontSize: 12 }}>{r.emoji}</span>
                      <span style={{ color: r.color, fontSize: 11, fontWeight: 700 }}>{r.label}</span>
                    </span>
                    <span style={{ color: WC.textSecondary, fontSize: 9, flex: 1, textAlign: "center", opacity: 0.75 }}>
                      {r.desc}
                    </span>
                    <span style={{ color: WC.textMuted, fontSize: 10, fontFamily: "'JetBrains Mono', monospace", flexShrink: 0 }}>
                      {r.max !== null ? `${r.min}–${r.max.toFixed(2)}` : `${r.min}+`}
                    </span>
                  </div>
                ))}
              </div>
            );
          })()}

          {/* Message */}
          {hasAmount && parseFloat(amount) >= supporterMin && (() => {
            const maxChars = tier.maxChars || 200;
            return (
            <div style={{ position: "relative", marginBottom: 10, animation: "slideDown 0.25s ease-out" }}>
              <div style={{ position: "relative" }}>
              <textarea
                value={message}
                onChange={e => setMessage(e.target.value.slice(0, maxChars))}
                placeholder={`Add a message (optional, ${maxChars} char max)`}
                maxLength={maxChars}
                rows={3}
                style={{
                  width: "100%", padding: "12px 40px 12px 16px",
                  background: WC.surface,
                  border: `1px solid ${WC.border}`,
                  borderRadius: 10, color: WC.text,
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 12, resize: "none",
                  outline: "none", boxSizing: "border-box",
                  transition: "border-color 0.3s",
                }}
                onFocus={e => e.currentTarget.style.borderColor = `${tier.color}25`}
                onBlur={e => e.currentTarget.style.borderColor = WC.border}
              />
              <button
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                style={{
                  position: "absolute", right: 8, top: 8,
                  background: "none", border: "none", cursor: "pointer",
                  fontSize: 18, padding: 4, lineHeight: 1,
                  opacity: 0.7, transition: "opacity 0.2s",
                }}
                onMouseEnter={e => e.currentTarget.style.opacity = "1"}
                onMouseLeave={e => e.currentTarget.style.opacity = "0.7"}
                title="Add emoji"
              >😀</button>
              </div>
              {showEmojiPicker && (
                <div style={{
                  display: "flex", flexWrap: "wrap", gap: 2, padding: "8px 4px",
                  maxHeight: 220, overflowY: "auto", overflowX: "hidden",
                  background: WC.surface, border: `1px solid ${WC.border}`,
                  borderRadius: 8, marginTop: 4, justifyContent: "center",
                }}>
                  {["❤️","🔥","💚","😂","🙏","💎","🎉","👏","💪","⚡","🐋","🚀","💰","😍","👀","🤑","💯","🎊","✨","😎","🫡","👑","🤝","💜","🥳","😭","🤣","🙌","💀","🤯","🆙","🐉","🧙","🐙","🤖","🥂","🐲","👽","👾","🛸","🌌","😱","☠️","✊","🤌","🫴","👁️","🗣️","🫂","🕵️","🦸‍♂️","🙅‍♂️","🙇","🏃‍♂️","🕺","🤷","☕️","🍻","🎧","🧩","🎯","📼","📷","📡","🔋","💡","🪙","🛠️","⛓️","⚙️","💣","🛡️","⚔️","📬","📍","🔎","🚫","🔜","♠️","♣️","♥️","♦️"].map(em => (
                    <button key={em} onClick={() => {
                      if (message.length < maxChars) {
                        setMessage(prev => (prev + em).slice(0, maxChars));
                        setShowEmojiPicker(false);
                      }
                    }} style={{
                      background: "none", border: "none", cursor: "pointer",
                      fontSize: 20, padding: "4px 5px", borderRadius: 6,
                      transition: "background 0.15s",
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = `${WC.border}`}
                    onMouseLeave={e => e.currentTarget.style.background = "none"}
                    >{em}</button>
                  ))}
                </div>
              )}
              <div style={{
                textAlign: "right", fontSize: 9, fontFamily: "'JetBrains Mono', monospace",
                color: message.length > maxChars - 20 ? WC.accent : `${WC.textMuted}80`,
                marginTop: 3,
              }}>{message.length}/{maxChars}</div>
              <div style={{
                fontSize: 9, fontFamily: "'Outfit', sans-serif",
                color: `${WC.textMuted}90`,
                marginTop: 2,
                textAlign: "center",
              }}>Messages are delivered to the creator and may appear on stream if live</div>
            </div>
            );
          })()}

          {/* Fee Breakdown */}
          {hasAmount && (() => {
            const tipAmt = parseFloat(amount);
            const fee = feeDisplay ? parseFloat(feeDisplay.fee) : 0.50;
            const total = feeDisplay ? parseFloat(feeDisplay.total) : tipAmt + 0.50;
            return (
            <div style={{
              padding: "8px 12px", borderRadius: 7,
              background: WC.surface,
              border: `1px solid ${WC.borderSubtle}`,
              marginBottom: 10,
              fontSize: 10, color: WC.textMuted,
              animation: "slideDown 0.2s ease-out",
            }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span>To creator:</span>
                  <span style={{ fontWeight: 700, color: WC.accent, fontFamily: "'JetBrains Mono', monospace" }}>{amount} LYX</span>
                  <span style={{ fontSize: 9, color: WC.accent }}>100%</span>
                </div>
                <div style={{ textAlign: "right" }}>
                  <span style={{ fontSize: 9, color: WC.textMuted }}>You pay </span>
                  <span style={{ fontWeight: 700, color: WC.text, fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}>
                    {total.toFixed(3)} LYX
                  </span>
                </div>
              </div>
              <div style={{
                display: "flex", alignItems: "center", gap: 6,
                fontSize: 9, color: WC.textMuted,
              }}>
                <span>Flat {fee.toFixed(2)} LYX platform fee · gas covered</span>
              </div>
              {tipError && (
                <div style={{ marginTop: 6, color: WC.red, fontSize: 10 }}>{tipError}</div>
              )}
              {txHash && (
                <div style={{ marginTop: 6, fontSize: 9 }}>
                  <a href={`${LUKSO_TESTNET.blockExplorerUrls[0]}/tx/${txHash}`} target="_blank" rel="noopener noreferrer"
                    style={{ color: WC.cyan, textDecoration: "none" }}>
                    View on Explorer →
                  </a>
                </div>
              )}
            </div>
            );
          })()}

          {/* Insufficient Balance → Buy LYX prompt */}
          {hasAmount && connected && (() => {
            const userBalance = parseFloat(profile?.balance || "0");
            const tipAmt = parseFloat(amount) || 0;
            const feeLyx = feeDisplay ? parseFloat(feeDisplay.fee) : 0.50;
            const totalNeeded = tipAmt + feeLyx;
            if (userBalance >= totalNeeded) return null;
            const shortfall = (totalNeeded - userBalance).toFixed(2);
            return (
              <div style={{
                marginBottom: 10, padding: "12px 14px", borderRadius: 10,
                background: `linear-gradient(135deg, ${WC.surface}, ${WC.cyan}08)`,
                border: `1px solid ${WC.cyan}25`,
                animation: "slideDown 0.25s ease-out",
              }}>
                <div style={{
                  fontSize: 10, fontWeight: 800, color: WC.cyan,
                  fontFamily: "'JetBrains Mono', monospace",
                  letterSpacing: "1.2px", textTransform: "uppercase",
                  marginBottom: 6,
                }}>💰 Not Enough LYX</div>
                <div style={{ fontSize: 11, color: WC.textSecondary, lineHeight: 1.5, marginBottom: 10 }}>
                  You need <strong style={{ color: WC.accent, fontFamily: "'JetBrains Mono', monospace" }}>{shortfall} more LYX</strong> to send this tip. Buy directly with card or Apple Pay — delivered to your UP in minutes.
                </div>
                <a href="https://transak.com/buy/lyx" target="_blank" rel="noopener noreferrer" style={{
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                  padding: "10px 14px", borderRadius: 8,
                  background: `linear-gradient(135deg, ${WC.cyan}, ${WC.accent})`,
                  color: WC.bg,
                  fontSize: 12, fontWeight: 700,
                  fontFamily: "'Syne', sans-serif",
                  textDecoration: "none",
                  transition: "transform 0.2s",
                }}
                onMouseEnter={e => e.currentTarget.style.transform = "translateY(-1px)"}
                onMouseLeave={e => e.currentTarget.style.transform = "translateY(0)"}>
                  Buy LYX with Card or Apple Pay →
                </a>
              </div>
            );
          })()}

          {/* Send Button */}
          <button onClick={sendTip} disabled={!hasAmount || sending} style={{
            width: "100%", padding: "15px",
            background: sent
              ? `linear-gradient(135deg, ${WC.green}, ${WC.accent})`
              : sending
                ? WC.surface
                : `linear-gradient(135deg, ${WC.accent}, ${WC.cyan})`,
            color: sending ? WC.textMuted : WC.bg,
            border: sending ? `1px solid ${WC.border}` : "none",
            borderRadius: 12,
            fontFamily: "'Syne', sans-serif",
            fontSize: 15, fontWeight: 700,
            cursor: sending ? "wait" : hasAmount ? "pointer" : "not-allowed",
            transition: "all 0.35s cubic-bezier(0.4,0,0.2,1)",
            opacity: !hasAmount ? 0.4 : 1,
            boxShadow: hasAmount && !sending && !sent ? `0 4px 24px ${WC.accentGlow}` : "none",
            position: "relative",
            overflow: "hidden",
          }}
          onMouseEnter={e => { if (hasAmount && !sending) e.currentTarget.style.transform = "translateY(-2px)"; }}
          onMouseLeave={e => e.currentTarget.style.transform = "translateY(0)"}>
            {sent ? "✓ SENT" : sending ? (
              <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                <span style={{ display: "inline-flex", gap: 3 }}>
                  {[0,1,2].map(i => (
                    <span key={i} style={{
                      width: 5, height: 5, borderRadius: "50%",
                      background: WC.accent,
                      animation: `dotBounce 0.6s ease-in-out ${i * 0.15}s infinite`,
                    }} />
                  ))}
                </span>
                Confirming...
              </span>
            ) : (amount && parseFloat(amount) > 0 && settingsMinTip > 0 && parseFloat(amount) < settingsMinTip)
              ? `Min ${settingsMinTip} LYX`
              : `FULLSEND ${amount || "0"} LYX`}
          </button>

          {/* Transaction Hash Link */}
          {txHash && (
            <div style={{
              marginTop: 8, padding: "8px 12px", borderRadius: 8,
              background: `${WC.accent}08`,
              border: `1px solid ${WC.accent}15`,
              animation: "slideDown 0.25s ease-out",
              display: "flex", alignItems: "center", justifyContent: "space-between",
            }}>
              <span style={{ fontSize: 10, color: WC.accent }}>✓ On-chain</span>
              <a
                href={`${LUKSO_TESTNET.blockExplorerUrls[0]}/tx/${txHash}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  fontSize: 10, color: WC.cyan,
                  fontFamily: "'JetBrains Mono', monospace",
                  textDecoration: "none",
                }}
                onMouseEnter={e => e.currentTarget.style.textDecoration = "underline"}
                onMouseLeave={e => e.currentTarget.style.textDecoration = "none"}
              >
                {txHash.slice(0, 10)}...{txHash.slice(-8)} →
              </a>
            </div>
          )}

          {/* Thank You Card */}
          {showThankYou && txHash && (() => {
            const ty = window.__fullsend_thankyou || { enabled: true, message: "Thank you for your support! 💚", image: "" };
            if (!ty.enabled) return null;
            return (
              <div onClick={() => setShowThankYou(false)} style={{
                marginTop: 12, padding: 20, borderRadius: 14,
                background: `linear-gradient(135deg, ${WC.surface}, ${WC.accent}08)`,
                border: `1px solid ${WC.accent}25`,
                textAlign: "center",
                animation: "fadeInUp 0.5s cubic-bezier(0.16, 1, 0.3, 1)",
                cursor: "pointer",
                position: "relative",
                overflow: "hidden",
              }}>
                {/* Accent glow */}
                <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, transparent, ${WC.accent}, transparent)`, opacity: 0.5 }} />
                {ty.image && (() => {
                  const ytMatch = ty.image.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
                  const isVideo = ty.image.includes("video") || ty.image.match(/\.(mp4|webm|mov)($|\?)/);
                  if (ytMatch) return <iframe src={`https://www.youtube.com/embed/${ytMatch[1]}?autoplay=1&mute=1`} style={{ width: "100%", maxHeight: 160, borderRadius: 10, border: "none", marginBottom: 10 }} allow="autoplay" />;
                  if (isVideo) return <video src={ty.image} autoPlay muted loop style={{ maxWidth: "100%", maxHeight: 160, borderRadius: 10, marginBottom: 10 }} />;
                  return <img src={ty.image} alt="" style={{ maxWidth: "100%", maxHeight: 160, borderRadius: 10, marginBottom: 10, objectFit: "contain" }} />;
                })()}
                <div style={{ fontSize: 15, fontWeight: 700, color: WC.accent, lineHeight: 1.5 }}>
                  {ty.message}
                </div>
                <div style={{ fontSize: 10, color: WC.textMuted, marginTop: 6 }}>
                  — {(() => {
                    const creatorName = standalone ? creatorInfoProp?.name : (creatorProfileProp?.name || window.__fullsend_profile?.name);
                    return creatorName || "Creator";
                  })()}
                </div>
                <div style={{ fontSize: 8, color: WC.textMuted, marginTop: 8, opacity: 0.5 }}>tap to dismiss</div>
              </div>
            );
          })()}

          {/* Tip Error */}
          {tipError && (
            <div style={{
              marginTop: 8, padding: "8px 12px", borderRadius: 8,
              background: `${WC.red}08`,
              border: `1px solid ${WC.red}15`,
              fontSize: 11, color: WC.red,
              animation: "slideDown 0.25s ease-out",
            }}>
              {tipError}
            </div>
          )}

          {/* Chat Preview */}
          {hasAmount && parseFloat(amount) >= supporterMin && message && (
            <div style={{
              marginTop: 14, padding: "10px 14px", borderRadius: 8,
              background: WC.surface,
              border: `1px solid ${WC.border}`,
              animation: "slideDown 0.2s ease-out",
            }}>
              <div style={{ fontSize: 9, color: WC.textMuted, marginBottom: 5, textTransform: "uppercase", letterSpacing: "1.5px", fontWeight: 600 }}>
                Chat Preview
              </div>
              <div style={{ fontSize: 12, lineHeight: 1.5, display: "flex", alignItems: "center", gap: 6 }}>
                {profile.avatar && typeof profile.avatar === "string" && profile.avatar.startsWith("http") ? (
                  <img src={profile.avatar} alt="" style={{
                    width: 20, height: 20, borderRadius: 5,
                    objectFit: "cover", flexShrink: 0,
                    border: `1px solid ${tier.color}30`,
                  }} onError={e => { e.target.style.display = "none"; }} />
                ) : (
                  <span style={{
                    width: 20, height: 20, borderRadius: 5,
                    background: `${tier.color}20`,
                    border: `1px solid ${tier.color}30`,
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    fontSize: 11, flexShrink: 0,
                  }}>👤</span>
                )}
                <span>
                  <span style={{ fontWeight: 700, color: tier.color }}>@{profile.name}</span>{" "}
                  <span style={{ color: WC.textSecondary }}>tipped {amount} LYX — "{message}"</span>
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Daily Tip Leaderboard — hidden until indexer provides real data */}

      {/* TOP TIPPERS LEADERBOARD — always at bottom, after tipping flow */}
      {widgetTheme.leaderboard !== false && (window.__fullsend_show_top_tippers !== false) && (standalone || connected) && (
        <TopTippersWidget creatorAddress={standalone ? creatorAddressProp : window.__fullsend_profile?.address} WC={WC} />
      )}

      {/* FullSend Branding */}
      <a href="/" onClick={e => { if (standalone) { e.preventDefault(); window.open("/", "_blank"); }}} style={{
        marginTop: 24,
        paddingTop: 16,
        borderTop: `1px solid ${WC.border}`,
        display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
        textDecoration: "none",
        cursor: "pointer",
        transition: "opacity 0.2s",
      }}
      onMouseEnter={e => e.currentTarget.style.opacity = "0.7"}
      onMouseLeave={e => e.currentTarget.style.opacity = "1"}>
        <div style={{
          width: 18, height: 18, borderRadius: 4,
          background: `linear-gradient(135deg, ${WC.accent}, ${WC.cyan})`,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 9, fontWeight: 900,
          color: WC.bg,
        }}>F</div>
        <span style={{
          fontSize: 10, color: WC.textMuted,
          fontFamily: "'JetBrains Mono', monospace",
          letterSpacing: "0.3px",
        }}>Powered by</span>
        <span style={{
          fontFamily: "'Syne', sans-serif",
          fontSize: 11, fontWeight: 800,
          background: `linear-gradient(135deg, ${WC.accent}, ${WC.cyan})`,
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
        }}>FULLSEND</span>
        <span style={{ fontSize: 9, color: WC.textMuted }}>·</span>
        <span style={{
          fontSize: 9, color: WC.textMuted,
          fontFamily: "'JetBrains Mono', monospace",
        }}>on LUKSO</span>
      </a>
    </div>
  );
}

// --- CREATOR ANALYTICS ---------------------------------------
function CreatorAnalytics() {
  const [mounted, setMounted] = useState(false);
  const [period, setPeriod] = useState("week");
  const [selectedSession, setSelectedSession] = useState("all");
  const [showSessions, setShowSessions] = useState(false);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { requestAnimationFrame(() => setMounted(true)); }, []);

  // Fetch analytics from indexer API
  const creatorAddr = window.__fullsend_profile?.address;
  useEffect(() => {
    if (!creatorAddr) { setLoading(false); return; }
    const periodMap = { today: "today", week: "week", month: "month", allTime: "all" };
    fetch(`/api/data-tips?action=analytics&creator=${creatorAddr}&period=${periodMap[period] || "all"}`)
      .then(r => r.json())
      .then(data => { setAnalyticsData(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [creatorAddr, period]);

  // Also trigger a sync in the background
  useEffect(() => { fetch("/api/indexer-sync").catch(() => {}); }, []);

  // Mock live sessions (auto-detected from platform APIs — needs indexer session tracking)
  const sessions = [
    { id: "all", label: "All Sessions", platform: null, duration: null, date: null },
  ];

  const platformIcons = { youtube: "▶", twitch: "◆", kick: "●", rumble: "R" };
  const platformColors = { youtube: "#FF0000", twitch: "#9146FF", kick: "#53FC18", rumble: "#85c742" };

  // Map API data to component format
  const summary = analyticsData?.summary || {};
  const s = {
    tips: parseInt(summary.total_tips || 0),
    earned: parseFloat(summary.total_earnings || 0),
    followers: 0, // needs follow indexer
    polls: 0, // needs poll indexer
  };

  const dailyData = (analyticsData?.dailyEarnings || []).map(d => ({
    day: new Date(d.day).toLocaleDateString("en", { weekday: "short" }),
    tips: parseFloat(d.earnings || 0),
  }));
  // Pad to at least 7 days if less
  while (dailyData.length < 7) {
    dailyData.unshift({ day: "—", tips: 0 });
  }
  const maxTip = Math.max(...dailyData.map(d => d.tips), 0.01);

  // Tier breakdown from API
  const tierColors = { micro: COLORS.textSecondary, supporter: COLORS.accent, champion: COLORS.blue, legend: COLORS.purple, whale: COLORS.gold };
  const tierLabels = { micro: "Micro", supporter: "Supporter", champion: "Champion", legend: "Legend", whale: "Whale" };
  const rawTiers = analyticsData?.tiers || [];
  const totalTierCount = rawTiers.reduce((sum, t) => sum + parseInt(t.count || 0), 0) || 1;
  const tierBreakdown = rawTiers.map(t => ({
    tier: tierLabels[t.tier] || t.tier,
    pct: Math.round((parseInt(t.count || 0) / totalTierCount) * 100),
    color: tierColors[t.tier] || COLORS.textMuted,
    count: parseInt(t.count || 0),
  }));
  // Ensure all tiers shown
  ["Micro", "Supporter", "Champion", "Legend", "Whale"].forEach(tier => {
    if (!tierBreakdown.find(t => t.tier === tier)) {
      tierBreakdown.push({ tier, pct: 0, color: tierColors[tier.toLowerCase()] || COLORS.textMuted, count: 0 });
    }
  });

  // Top supporters from API
  const rawSupporters = analyticsData?.topSupporters || [];
  const rawTips = analyticsData?.recentTips || [];
  const allAddresses = [...rawSupporters.map(s2 => s2.tipper), ...rawTips.map(t => t.tipper)].filter(Boolean);
  const upProfiles = useUPProfiles(allAddresses);

  const topSupporters = rawSupporters.map(s2 => {
    const profile = upProfiles[s2.tipper?.toLowerCase()] || {};
    return {
      name: profile.name || `${s2.tipper.slice(0, 6)}...${s2.tipper.slice(-4)}`,
      avatar: profile.avatar || STOCK_AVATAR,
      total: parseFloat(s2.total_amount || 0),
      tips: parseInt(s2.tip_count || 0),
      address: s2.tipper,
    };
  });

  // Recent tips from API
  const recentTips = rawTips.map(t => {
    const amount = parseFloat(t.amount || 0);
    const tier = getTier(amount);
    const timeDiff = Math.floor(Date.now() / 1000) - parseInt(t.timestamp || 0);
    const timeStr = timeDiff < 60 ? "just now" : timeDiff < 3600 ? `${Math.floor(timeDiff / 60)}m ago` : timeDiff < 86400 ? `${Math.floor(timeDiff / 3600)}h ago` : `${Math.floor(timeDiff / 86400)}d ago`;
    const profile = upProfiles[t.tipper?.toLowerCase()] || {};
    return {
      from: profile.name || `${t.tipper.slice(0, 6)}...${t.tipper.slice(-4)}`,
      amount,
      time: timeStr,
      tier: tier.label,
    };
  });

  if (!window.__fullsend_profile?.address) {
    return (
      <div style={{ maxWidth: 720, margin: "0 auto", padding: "80px 24px", textAlign: "center", opacity: mounted ? 1 : 0, transform: mounted ? "translateY(0)" : "translateY(20px)", transition: "all 0.5s cubic-bezier(0.16, 1, 0.3, 1)" }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
        <div style={{ fontFamily: "'Syne', sans-serif", fontSize: 20, fontWeight: 800, color: COLORS.text, marginBottom: 8 }}>Connect to Access Analytics</div>
        <div style={{ fontSize: 12, color: COLORS.textMuted, lineHeight: 1.6, maxWidth: 360, margin: "0 auto" }}>
          Connect your Universal Profile to view your stream analytics, tip history, and performance data.
        </div>
      </div>
    );
  }

  return (
    <div style={{
      maxWidth: 800, margin: "0 auto", padding: "40px 24px",
      opacity: mounted ? 1 : 0, transform: mounted ? "translateY(0)" : "translateY(20px)",
      transition: "all 0.6s cubic-bezier(0.16, 1, 0.3, 1)",
    }}>
      <div style={{ fontFamily: "'Syne', sans-serif", fontSize: 24, fontWeight: 800, marginBottom: 4 }}>Analytics</div>
      <div style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 20 }}>Track your earnings, supporters, and growth</div>

      {/* Period selector */}
      <div style={{ display: "flex", gap: 4, marginBottom: 20 }}>
        {[
          { key: "today", label: "Today" },
          { key: "week", label: "This Week" },
          { key: "month", label: "This Month" },
          { key: "allTime", label: "All Time" },
        ].map(p => (
          <button key={p.key} onClick={() => setPeriod(p.key)} style={{
            padding: "6px 14px", borderRadius: 6,
            background: period === p.key ? `${COLORS.accent}12` : COLORS.surface,
            border: `1px solid ${period === p.key ? COLORS.accent + "25" : COLORS.border}`,
            color: period === p.key ? COLORS.accent : COLORS.textMuted,
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 10, fontWeight: 600, cursor: "pointer",
          }}>{p.label}</button>
        ))}

        {/* Session filter */}
        <div style={{ marginLeft: "auto", position: "relative" }}>
          <button onClick={() => setShowSessions(!showSessions)} style={{
            padding: "6px 12px", borderRadius: 6,
            background: selectedSession !== "all" ? `${COLORS.purple}12` : COLORS.surface,
            border: `1px solid ${selectedSession !== "all" ? COLORS.purple + "25" : COLORS.border}`,
            color: selectedSession !== "all" ? COLORS.purple : COLORS.textMuted,
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 10, fontWeight: 600, cursor: "pointer",
            display: "flex", alignItems: "center", gap: 5,
          }}>
            {selectedSession === "all" ? "All Sessions" : sessions.find(s2 => s2.id === selectedSession)?.label || "Session"}
            <span style={{ fontSize: 8, transition: "transform 0.2s", transform: showSessions ? "rotate(180deg)" : "rotate(0)" }}>v</span>
          </button>

          {showSessions && (
            <div style={{
              position: "absolute", top: "calc(100% + 4px)", right: 0,
              width: 300, background: COLORS.surface,
              border: `1px solid ${COLORS.border}`, borderRadius: 8,
              boxShadow: `0 8px 24px ${COLORS.bg}cc`,
              zIndex: 100, overflow: "hidden",
              animation: "slideDown 0.2s ease-out",
            }}>
              {sessions.map(sess => (
                <div key={sess.id} onClick={() => { setSelectedSession(sess.id); setShowSessions(false); }} style={{
                  padding: "8px 12px", cursor: "pointer",
                  background: selectedSession === sess.id ? `${COLORS.purple}08` : "transparent",
                  borderBottom: `1px solid ${COLORS.border}`,
                  display: "flex", alignItems: "center", gap: 8,
                  transition: "background 0.15s",
                }}
                onMouseEnter={e => { if (selectedSession !== sess.id) e.currentTarget.style.background = `${COLORS.text}04`; }}
                onMouseLeave={e => { if (selectedSession !== sess.id) e.currentTarget.style.background = "transparent"; }}>
                  {sess.platform ? (
                    <div style={{
                      width: 22, height: 22, borderRadius: 4,
                      background: `${platformColors[sess.platform]}12`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 9, color: platformColors[sess.platform], fontWeight: 700,
                      flexShrink: 0,
                    }}>{platformIcons[sess.platform]}</div>
                  ) : (
                    <div style={{ width: 22, height: 22, borderRadius: 4, background: `${COLORS.textMuted}10`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10 }}>*</div>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: 10, fontWeight: 600,
                      color: selectedSession === sess.id ? COLORS.purple : COLORS.text,
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }}>{sess.label}</div>
                    {sess.date && (
                      <div style={{ fontSize: 8, color: COLORS.textMuted, display: "flex", gap: 6 }}>
                        <span>{sess.date}</span>
                        <span>{sess.duration}</span>
                        <span>{sess.tips} tips</span>
                      </div>
                    )}
                  </div>
                  {sess.earned && (
                    <div style={{
                      fontSize: 9, fontWeight: 700, color: COLORS.accent,
                      fontFamily: "'JetBrains Mono', monospace", flexShrink: 0,
                    }}>{sess.earned} LYX</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Active session banner */}
      {selectedSession !== "all" && (() => {
        const sess = sessions.find(s2 => s2.id === selectedSession);
        if (!sess) return null;
        return (
          <div style={{
            padding: "10px 14px", borderRadius: 8, marginBottom: 16,
            background: `${COLORS.purple}06`, border: `1px solid ${COLORS.purple}15`,
            display: "flex", alignItems: "center", gap: 10,
          }}>
            <div style={{
              width: 26, height: 26, borderRadius: 5,
              background: `${platformColors[sess.platform]}12`,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 11, color: platformColors[sess.platform], fontWeight: 700,
            }}>{platformIcons[sess.platform]}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.text }}>{sess.label}</div>
              <div style={{ fontSize: 9, color: COLORS.textMuted }}>{sess.date} on {sess.platform.charAt(0).toUpperCase() + sess.platform.slice(1)} - {sess.duration}</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: COLORS.accent, fontFamily: "'JetBrains Mono', monospace" }}>{sess.earned} LYX</div>
              <div style={{ fontSize: 8, color: COLORS.textMuted }}>{sess.tips} tips</div>
            </div>
            <button onClick={() => setSelectedSession("all")} style={{
              padding: "4px 8px", borderRadius: 4, background: "transparent",
              border: `1px solid ${COLORS.border}`, color: COLORS.textMuted,
              fontSize: 8, cursor: "pointer", marginLeft: 4,
            }}>Clear</button>
          </div>
        );
      })()}

      {/* Stats cards */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 10, marginBottom: 24 }}>
        {[
          { label: "Tips Received", value: s.tips, icon: "💰", color: COLORS.accent },
          { label: "LYX Earned", value: `${s.earned.toFixed(2)} LYX`, icon: "💎", color: COLORS.cyan },
          { label: "New Followers", value: s.followers, icon: "👤", color: COLORS.purple },
          { label: "Polls Run", value: s.polls, icon: "📊", color: COLORS.orange },
        ].map((stat, i) => (
          <div key={i} style={{
            padding: "16px 14px", borderRadius: 10,
            background: COLORS.surface, border: `1px solid ${COLORS.border}`,
            textAlign: "center",
          }}>
            <div style={{ fontSize: 18, marginBottom: 4 }}>{stat.icon}</div>
            <div style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 18, fontWeight: 800, color: stat.color,
            }}>{stat.value}</div>
            <div style={{ fontSize: 9, color: COLORS.textMuted, marginTop: 2 }}>{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Chart + Tier breakdown row */}
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 14, marginBottom: 24 }}>
        {/* Earnings chart */}
        <div style={{
          padding: "16px", borderRadius: 10,
          background: COLORS.surface, border: `1px solid ${COLORS.border}`,
        }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "1.5px", marginBottom: 14 }}>
            Earnings This Week
          </div>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 120 }}>
            {dailyData.map((d, i) => (
              <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                <div style={{
                  fontSize: 8, fontWeight: 700, color: COLORS.accent,
                  fontFamily: "'JetBrains Mono', monospace",
                }}>{d.tips.toFixed(0)}</div>
                <div style={{
                  width: "100%", borderRadius: 4,
                  height: `${(d.tips / maxTip) * 80}px`,
                  background: `linear-gradient(180deg, ${COLORS.accent}, ${COLORS.cyan})`,
                  opacity: 0.8,
                  transition: "height 0.5s cubic-bezier(0.16, 1, 0.3, 1)",
                }} />
                <div style={{ fontSize: 8, color: COLORS.textMuted }}>{d.day}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Tier breakdown */}
        <div style={{
          padding: "16px", borderRadius: 10,
          background: COLORS.surface, border: `1px solid ${COLORS.border}`,
        }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "1.5px", marginBottom: 14 }}>
            Tips by Tier
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {tierBreakdown.map((t, i) => (
              <div key={i}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                  <span style={{ fontSize: 10, fontWeight: 600, color: t.color }}>{t.tier}</span>
                  <span style={{ fontSize: 9, color: COLORS.textMuted, fontFamily: "'JetBrains Mono', monospace" }}>{t.count} ({t.pct}%)</span>
                </div>
                <div style={{ height: 4, borderRadius: 2, background: `${COLORS.bg}80`, overflow: "hidden" }}>
                  <div style={{
                    height: "100%", width: `${t.pct}%`, borderRadius: 2,
                    background: t.color, transition: "width 0.6s cubic-bezier(0.16, 1, 0.3, 1)",
                  }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Top supporters + Recent tips row */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        {/* Top supporters */}
        <div style={{
          padding: "16px", borderRadius: 10,
          background: COLORS.surface, border: `1px solid ${COLORS.border}`,
        }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "1.5px", marginBottom: 12 }}>
            Top Supporters
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {topSupporters.map((sup, i) => (
              <div key={i} style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: "6px 8px", borderRadius: 6,
                background: i === 0 ? `${COLORS.gold}06` : "transparent",
              }}>
                <div style={{ width: 18, fontSize: 10, fontWeight: 800, color: i < 3 ? COLORS.gold : COLORS.textMuted,
                  fontFamily: "'JetBrains Mono', monospace",
                }}>#{i + 1}</div>
                <div style={{ width: 28, height: 28, borderRadius: 6, overflow: "hidden", flexShrink: 0 }}>{isImageAvatar(sup.avatar) ? <img src={sup.avatar} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} onError={e => { e.target.src = STOCK_AVATAR; }} /> : "👤"}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, fontWeight: 600 }}>{sup.name}</div>
                  <div style={{ fontSize: 8, color: COLORS.textMuted }}>{sup.tips} tips</div>
                </div>
                <div style={{
                  fontSize: 11, fontWeight: 700, color: COLORS.accent,
                  fontFamily: "'JetBrains Mono', monospace",
                }}>{typeof sup.total === "number" ? sup.total.toFixed(2) : sup.total} LYX</div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent tips */}
        <div style={{
          padding: "16px", borderRadius: 10,
          background: COLORS.surface, border: `1px solid ${COLORS.border}`,
        }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "1.5px", marginBottom: 12 }}>
            Recent Tips
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {recentTips.map((tip, i) => {
              const tierColors = { Micro: COLORS.textSecondary, Supporter: COLORS.accent, Champion: COLORS.blue, Legend: COLORS.purple, Whale: COLORS.gold };
              return (
                <div key={i} style={{
                  display: "flex", alignItems: "center", gap: 8,
                  padding: "6px 8px", borderRadius: 6,
                }}>
                  <div style={{
                    padding: "2px 5px", borderRadius: 3,
                    background: `${tierColors[tip.tier]}10`,
                    border: `1px solid ${tierColors[tip.tier]}18`,
                    fontSize: 7, fontWeight: 700, color: tierColors[tip.tier],
                    minWidth: 42, textAlign: "center",
                  }}>{tip.tier.toUpperCase()}</div>
                  <div style={{ flex: 1, fontSize: 10, fontWeight: 600 }}>{tip.from}</div>
                  <div style={{
                    fontSize: 11, fontWeight: 700, color: COLORS.accent,
                    fontFamily: "'JetBrains Mono', monospace",
                  }}>{tip.amount} LYX</div>
                  <div style={{ fontSize: 8, color: COLORS.textMuted, minWidth: 40, textAlign: "right" }}>{tip.time}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Export */}
      <div style={{ marginTop: 20, display: "flex", gap: 8 }}>
        <button onClick={() => {}} style={{
          padding: "8px 16px", borderRadius: 6,
          background: `${COLORS.cyan}10`, border: `1px solid ${COLORS.cyan}20`,
          color: COLORS.cyan, fontFamily: "'JetBrains Mono', monospace",
          fontSize: 10, fontWeight: 600, cursor: "pointer",
          display: "flex", alignItems: "center", gap: 5,
        }}>Export CSV</button>
        <div style={{ fontSize: 9, color: COLORS.textMuted, display: "flex", alignItems: "center" }}>
          Download tip history for tax reporting or analysis
        </div>
      </div>
    </div>
  );
}

// --- CONTROL PANEL -------------------------------------------
function ControlPanel() {
  const ct = window.__fullsend_settings_data?.customTiers || {};
  // DB-only goal — single source of truth
  const controlAddr = window.__fullsend_profile?.address;
  const { active: controlActiveGoal } = useActiveGoal(controlAddr);
  const demoPfps = [
    STOCK_AVATAR,
    DEMO_PFP_SUPPORTER,
    DEMO_PFP_CHAMPION,
    DEMO_PFP_LEGEND,
    DEMO_PFP_WHALE,
  ];
  const [tips, setTips] = useState([]);
  // Persist which tips have been shown/skipped so they don't reappear as
  // pending when Control unmounts/remounts (state is lost, DB re-fetches them).
  const handledTipsKey = () => `fullsend_handled_tips_${window.__fullsend_profile?.address || ""}`;
  const getHandledTips = () => { try { return JSON.parse(localStorage.getItem(handledTipsKey()) || "{}"); } catch { return {}; } };
  const markTipHandled = (id, action) => { try { const h = getHandledTips(); h[id] = action; const keys = Object.keys(h); if (keys.length > 300) delete h[keys[0]]; localStorage.setItem(handledTipsKey(), JSON.stringify(h)); } catch {} };
  const [filter, setFilter] = useState("pending");
  const [mounted, setMounted] = useState(false);
  const [activePoll, _setActivePoll] = useState(() => window.__fullsend_active_poll || null);
  const setActivePoll = (poll) => { window.__fullsend_active_poll = poll; _setActivePoll(poll); };
  const [pollOnStream, setPollOnStream] = useState(() => {
    const state = window.__fullsend_overlay_state || {};
    return !!state.poll;
  });
  const [autoShowVersion, setAutoShowVersion] = useState(0);

  // LIVE status: creator manually toggles on, requires heartbeat to stay on
  const [isLive, setIsLive] = useState(() => {
    const state = window.__fullsend_overlay_state || {};
    // Check heartbeat expiry — if flag is set but heartbeat is stale, consider it off
    if (!state.isLive) return false;
    const hb = state.liveHeartbeat || 0;
    const fresh = (Date.now() - hb) < 120000;
    if (!fresh) {
      // Stale — clear the flag so widget preview doesn't falsely show LIVE
      if (window.__fullsend_overlay_state) {
        window.__fullsend_overlay_state.isLive = false;
        window.__fullsend_overlay_state.liveHeartbeat = null;
      }
      return false;
    }
    return true;
  });

  // Sync isLive from DB on mount (source of truth for live state)
  useEffect(() => {
    const addr = window.__fullsend_profile?.address;
    if (!addr) return;
    fetch(`/api/creator-register?address=${addr}`)
      .then(r => r.json())
      .then(data => {
        const creator = data.creator || data.creators?.[0];
        if (!creator) return;
        const plat = typeof creator.platforms === "string" ? JSON.parse(creator.platforms || "{}") : (creator.platforms || {});
        const os = plat._overlayState || {};
        const hb = os.liveHeartbeat || 0;
        const fresh = os.isLive === true && (Date.now() - hb) < 120000;
        // Update window state and React state to match DB
        window.__fullsend_overlay_state = { ...(window.__fullsend_overlay_state || {}), ...os, isLive: fresh };
        setIsLive(fresh);
        if (os.primaryStream) setPrimaryStream(os.primaryStream);
      })
      .catch(() => {});
  }, []);

  // Which platform the LIVE badge links to (youtube, twitch, kick, rumble)
  const [primaryStream, setPrimaryStream] = useState(() => {
    const state = window.__fullsend_overlay_state || {};
    return state.primaryStream || "auto";
  });
  const updatePrimaryStream = (platform) => {
    setPrimaryStream(platform);
    window.__fullsend_overlay_state = { ...(window.__fullsend_overlay_state || {}), primaryStream: platform };
    syncCreatorToDB({ _overlayState: { primaryStream: platform } });
  };

  // Heartbeat: every 30s while isLive is true, POST a live ping to keep it active
  // If Control is closed / tab backgrounded for 2+ min, server auto-flips to false
  useEffect(() => {
    if (!isLive) return;
    const sendHeartbeat = () => {
      syncCreatorToDB({ _overlayState: { isLive: true, liveHeartbeat: Date.now() } });
    };
    sendHeartbeat(); // immediately on toggle-on
    const interval = setInterval(sendHeartbeat, 30000);
    return () => clearInterval(interval);
  }, [isLive]);

  // Toggle live on/off
  const toggleLive = () => {
    const newVal = !isLive;
    setIsLive(newVal);
    if (newVal) {
      syncCreatorToDB({ _overlayState: { isLive: true, liveHeartbeat: Date.now() } });
    } else {
      syncCreatorToDB({ _overlayState: { isLive: false, liveHeartbeat: null } });
    }
  };

  // Live refresh: poll votes + goal progress update every 5s
  useEffect(() => {
    const interval = setInterval(async () => {
      // Update poll from global (PollManager pushes vote updates there)
      if (window.__fullsend_active_poll && activePoll) {
        const g = window.__fullsend_active_poll;
        if (g.totalVotes !== activePoll.totalVotes || g.id !== activePoll.id) {
          setActivePoll(g);
        }
      }

      // Also poll DB for active poll (catches polls created in other tabs/sessions)
      const creatorAddr = window.__fullsend_profile?.address;
      if (creatorAddr) {
        try {
          const res = await fetch(`/api/data-polls?creator=${creatorAddr}&votes=true`);
          const data = await res.json();
          if (data.success && data.polls) {
            const dbActive = data.polls.find(p => p.status === "active");
            if (dbActive) {
              // Map DB poll to local format
              const optionColors = [COLORS.accent, COLORS.blue, COLORS.purple, COLORS.orange, COLORS.cyan, COLORS.gold];
              const mapped = {
                id: dbActive.id,
                question: dbActive.question,
                options: (dbActive.options || []).map((opt, i) => ({
                  text: opt.text, votes: opt.votes || 0,
                  color: optionColors[i % optionColors.length],
                })),
                totalVotes: dbActive.totalVotes || 0,
                gateType: parseInt(dbActive.gateType) === 0 ? "free" : parseInt(dbActive.gateType) === 1 ? "lyx" : "nft",
                lyxCost: parseInt(dbActive.gateType) === 1 ? Number(dbActive.votePrice || 0) / 1e18 : 0,
                onchainId: dbActive.onchainId || null,
                endTime: Number(dbActive.endTime || 0),
                timeRemaining: Number(dbActive.endTime || 0) > 0 ? Math.max(0, Number(dbActive.endTime) - Math.floor(Date.now() / 1000)) : null,
                status: "active",
                headerImage: dbActive.headerImage || null,
                optionImages: dbActive.optionImages || [],
              };
              // Only update if different from current
              if (!activePoll || activePoll.id !== mapped.id || activePoll.totalVotes !== mapped.totalVotes || (!activePoll.onchainId && mapped.onchainId)) {
                setActivePoll(mapped);
              }
            } else if (activePoll) {
              // DB has no active poll but Control has one cached — clear it
              // (could be expired, archived, or manually ended from Polls page)
              setActivePoll(null);
            }
          }
        } catch (e) { /* non-fatal */ }
      }

      // Force re-render for goal (reads from window.__fullsend_settings_data)
      setAutoShowVersion(v => v + 1);
    }, 5000);
    return () => clearInterval(interval);
  }, [activePoll]);

  // Tick down poll countdown on Control page
  useEffect(() => {
    if (!activePoll || !activePoll.timeRemaining || activePoll.timeRemaining <= 0) return;
    const interval = setInterval(() => {
      _setActivePoll(prev => {
        if (!prev || !prev.timeRemaining) return prev;
        if (prev.timeRemaining <= 1) {
          const ended = { ...prev, status: "ended", timeRemaining: 0 };
          window.__fullsend_active_poll = ended;
          return ended;
        }
        const updated = { ...prev, timeRemaining: prev.timeRemaining - 1 };
        window.__fullsend_active_poll = updated;
        return updated;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [activePoll?.id, activePoll?.timeRemaining > 0]);

  const [visibility, setVisibility] = useState(() => {
    const state = window.__fullsend_overlay_state || {};
    return state.visibility || { tips: true, follows: true, micro: true, chat: false, polls: false, goal: false };
  });

  // Sync visibility to DB when changed
  const updateVisibility = (key) => {
    if (key === "sounds") {
      // Toggle master sound mute
      setVisibility(prev => {
        const next = { ...prev, sounds: !prev.sounds };
        // Update global sound toggles
        const muted = !next.sounds;
        window.__fullsend_sound_toggles = { tips: !muted, follows: !muted, polls: !muted };
        window.__fullsend_volume = muted ? 0 : 0.7;
        window.__fullsend_overlay_state = { ...(window.__fullsend_overlay_state || {}), visibility: next };
        syncCreatorToDB({ _overlayState: { visibility: next } });
        return next;
      });
      return;
    }
    setVisibility(prev => {
      const next = { ...prev, [key]: !prev[key] };
      window.__fullsend_overlay_state = { ...(window.__fullsend_overlay_state || {}), visibility: next };
      syncCreatorToDB({ _overlayState: { visibility: next } });
      // Goal visibility is authoritative on the goal's own showOnOverlay flag
      // (DB-synced). Keep this toggle in sync with it so the OBS overlay honors it.
      if (key === "goal" && controlAddr) {
        window.__fullsend_overlay_state = { ...(window.__fullsend_overlay_state || {}), goalVisible: next.goal };
        syncCreatorToDB({ _overlayState: { goalVisible: next.goal } });
        GoalAPI.setVisibility(controlAddr, { showOnOverlay: next.goal }).catch(() => {});
      }
      return next;
    });
  };

  // Listen for polls launched from PollManager (same-tab real-time)
  useEffect(() => {
    const handler = (e) => setActivePoll(e.detail);
    window.addEventListener("fullsend-poll", handler);
    return () => window.removeEventListener("fullsend-poll", handler);
  }, []);

  // Read overlay state from DB on mount (syncs with what OBS sees)
  useEffect(() => {
    requestAnimationFrame(() => setMounted(true));
    const addr = window.__fullsend_profile?.address;
    if (!addr) return;
    fetch(`/api/creator-register?address=${addr}`)
      .then(r => r.json())
      .then(data => {
        const creator = data.creators?.[0] || data.creator;
        if (!creator) return;
        const platforms = typeof creator.platforms === "string" ? JSON.parse(creator.platforms || "{}") : (creator.platforms || {});
        const state = platforms._overlayState;
        if (state) {
          window.__fullsend_overlay_state = state;
          if (state.poll) { setActivePoll(state.poll); setPollOnStream(true); }
          if (state.visibility) setVisibility(prev => ({ ...prev, ...state.visibility, polls: state.poll ? true : false }));
        }
      })
      .catch(() => {});
  }, []);

  // Fetch active poll — global first, then DB, then contract as fallback
  const [pollLoading, setPollLoading] = useState(() => !window.__fullsend_active_poll);
  const pollFoundRef = useRef(!!window.__fullsend_active_poll);
  useEffect(() => {
    // If global already has it, use immediately
    if (window.__fullsend_active_poll && !activePoll) {
      setActivePoll(window.__fullsend_active_poll);
      setPollLoading(false);
      pollFoundRef.current = true;
    }

    const creatorAddress = window.__fullsend_profile?.address;
    if (!creatorAddress) { setPollLoading(false); return; }
    const optionColors = [COLORS.accent, COLORS.blue, COLORS.purple, COLORS.orange, COLORS.cyan, COLORS.gold];

    const mapPoll = (p, source) => ({
      id: p.id,
      question: p.question,
      options: (p.options || []).map((opt, i) => ({
        text: opt.text, votes: opt.votes || 0,
        color: optionColors[i % optionColors.length],
      })),
      totalVotes: p.totalVotes || 0,
      gateType: parseInt(p.gateType) === 0 ? "free" : parseInt(p.gateType) === 1 ? "lyx" : "nft",
      onchainId: p.onchainId || null,
      endTime: Number(p.endTime || 0),
      timeRemaining: Number(p.endTime || 0) > 0 ? Math.max(0, Number(p.endTime) - Math.floor(Date.now() / 1000)) : null,
      status: "active",
      headerImage: p.headerImage || null,
      optionImages: p.optionImages || [],
    });
    
    // 1. Check DB (fast)
    fetch(`/api/data-polls?creator=${creatorAddress}`)
      .then(r => r.json())
      .then(data => {
        if (data.success && data.polls) {
          const active = data.polls.find(p => p.status === "active");
          if (active) {
            const mapped = mapPoll(active, "db");
            setActivePoll(mapped);
            window.__fullsend_active_poll = mapped;
            setPollLoading(false);
            pollFoundRef.current = true;
            return;
          }
        }
        // DB had no active poll — done (DB is source of truth with indexer)
        if (!pollFoundRef.current) {
          setPollLoading(false);
        } else {
          setPollLoading(false);
        }
      })
      .catch(() => setPollLoading(false));

    // Refresh from DB every 10s (with vote counts from indexed events)
    const interval = setInterval(() => {
      if (window.__fullsend_active_poll) {
        setActivePoll(window.__fullsend_active_poll);
        return;
      }
      fetch(`/api/data-polls?creator=${creatorAddress}&votes=true`)
        .then(r => r.json())
        .then(data => {
          if (data.success && data.polls) {
            const active = data.polls.find(p => p.status === "active");
            if (active) {
              const mapped = mapPoll(active, "db");
              setActivePoll(mapped);
              window.__fullsend_active_poll = mapped;
            }
          }
        })
        .catch(() => {});
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  const togglePollOnStream = () => {
    if (!pollOnStream) {
      window.dispatchEvent(new CustomEvent("fullsend-poll-live", { detail: activePoll }));
      setPollOnStream(true);
      const vis = { ...(window.__fullsend_overlay_state?.visibility || visibility), polls: true };
      window.__fullsend_overlay_state = { ...(window.__fullsend_overlay_state || {}), poll: activePoll, visibility: vis };
      syncCreatorToDB({ _overlayState: { poll: activePoll, visibility: { polls: true } } });
      setVisibility(prev => ({ ...prev, polls: true }));
    } else {
      window.dispatchEvent(new CustomEvent("fullsend-poll-remove", {}));
      setPollOnStream(false);
      const vis = { ...(window.__fullsend_overlay_state?.visibility || visibility), polls: false };
      window.__fullsend_overlay_state = { ...(window.__fullsend_overlay_state || {}), poll: null, visibility: vis };
      syncCreatorToDB({ _overlayState: { poll: null, visibility: { polls: false } } });
      setVisibility(prev => ({ ...prev, polls: false }));
    }
  };

  useEffect(() => {
    // Fetch real tips from API
    const creatorAddr = window.__fullsend_profile?.address;
    const fetchTips = () => {
      if (!creatorAddr) return;
      fetch(`/api/data-tips?action=activity&creator=${creatorAddr}&limit=50`)
        .then(r => r.json())
        .then(data => {
          const autoShow = window.__fullsend_settings_data?.autoShowTiers || {};
          const handled = getHandledTips();
          const realTips = (data.activity || []).map((t, i) => {
            const amount = parseFloat(t.amount || 0);
            const tier = getTier(amount);
            const tierKey = tier.label?.toLowerCase() || "micro";
            const isAutoShow = autoShow[tierKey] || false;
            const id = t.tx_hash || i;
            return {
              id,
              from: { address: t.tipper, name: `${t.tipper.slice(0, 6)}...${t.tipper.slice(-4)}`, avatar: STOCK_AVATAR },
              amount,
              message: "",
              isPlatform: (t.message_hash || "").startsWith("Tip to FullSend"),
              timestamp: new Date(parseInt(t.timestamp) * 1000),
              approved: handled[id] || (isAutoShow ? "auto" : null),
            };
          });
          if (realTips.length > 0) {
            setTips(prev => {
              // Add real tips that aren't already in the list
              const existingIds = new Set(prev.map(t => t.id));
              const newReal = realTips.filter(t => !existingIds.has(t.id));
              return [...newReal, ...prev];
            });
            // Resolve profiles in background
            realTips.forEach((tip, idx) => {
              fetchUPProfile(tip.from.address).then(profile => {
                if (profile) setTips(prev => prev.map(t => t.id === tip.id ? { ...t, from: { ...t.from, name: profile.name || t.from.name, avatar: profile.avatar || t.from.avatar } } : t));
              }).catch(() => {});
            });
          }
        })
        .catch(() => {});
    };
    fetchTips();
    // Poll the DB so tips from OTHER users (not just this browser's own
    // fullsend-tip events) appear on the Control feed fast during a stream.
    const tipPoll = setInterval(fetchTips, 4000);

    const handler = (e) => {
      const autoShow = window.__fullsend_settings_data?.autoShowTiers || {};
      const tier = getTier(e.detail.amount);
      const tierKey = tier.label?.toLowerCase() || "micro";
      const isAutoShow = autoShow[tierKey] || false;
      const tip = { ...e.detail, approved: isAutoShow ? "auto" : null };
      setTips(prev => [tip, ...prev]);
      // If auto-show, fire overlay immediately + write to DB queue
      if (isAutoShow) {
        window.dispatchEvent(new CustomEvent("fullsend-overlay", { detail: tip }));
        const alert = { type: "tip", amount: tip.amount, from: tip.from, message: tip.message || "", shownAt: Date.now(), txHash: tip.txHash };
        const queue = [...((window.__fullsend_overlay_state || {}).alertQueue || []), alert].slice(-10);
        window.__fullsend_overlay_state = { ...(window.__fullsend_overlay_state || {}), alertQueue: queue };
        syncCreatorToDB({ _overlayState: { alertQueue: queue } });
      }
    };
    window.addEventListener("fullsend-tip", handler);
    return () => { clearInterval(tipPoll); window.removeEventListener("fullsend-tip", handler); };
  }, []);

  const [visibilityWarning, setVisibilityWarning] = useState(null);
  const [overlayTestSent, setOverlayTestSent] = useState(false);

  // Fire a test alert on the live OBS overlay so the creator can confirm their
  // browser source is wired up — without waiting for a real tip/follow/poll.
  // Pushes a `test` alert into the same DB alertQueue the standalone overlay polls.
  const triggerOverlayTest = () => {
    const alert = { type: "test", shownAt: Date.now() };
    const queue = [...((window.__fullsend_overlay_state || {}).alertQueue || []), alert].slice(-10);
    window.__fullsend_overlay_state = { ...(window.__fullsend_overlay_state || {}), alertQueue: queue };
    syncCreatorToDB({ _overlayState: { alertQueue: queue } });
    setOverlayTestSent(true);
    setTimeout(() => setOverlayTestSent(false), 4000);
  };

  const handleAction = (id, action) => {
    setTips(prev => prev.map(t => t.id === id ? { ...t, approved: action } : t));
    markTipHandled(id, action);
    if (action === "approved") {
      const tip = tips.find(t => t.id === id);
      // Check if the relevant tier visibility is toggled on
      const supMin = parseFloat(window.__fullsend_settings_data?.customTiers?.supporter?.min) || 5;
      const isMicroTip = tip.amount < supMin;
      const visKey = isMicroTip ? "micro" : "tips";
      if (!visibility[visKey]) {
        // Show warning — tip was queued but won't display because visibility is off
        setVisibilityWarning({
          message: `${isMicroTip ? "Micro tips" : "Tips"} are hidden from stream. Toggle "${isMicroTip ? "Micro" : "Tips"}" on to show alerts.`,
          tier: visKey,
        });
        setTimeout(() => setVisibilityWarning(null), 5000);
        return;
      }
      window.dispatchEvent(new CustomEvent("fullsend-overlay", { detail: tip }));
      // Write to DB alert queue so standalone OBS overlay picks it up
      const alert = { type: "tip", amount: tip.amount, from: tip.from, message: tip.message || "", shownAt: Date.now(), txHash: tip.txHash };
      const queue = [...((window.__fullsend_overlay_state || {}).alertQueue || []), alert].slice(-10);
      window.__fullsend_overlay_state = { ...(window.__fullsend_overlay_state || {}), alertQueue: queue };
      // Send ONLY the alertQueue update, not the full _overlayState
      // (prevents stale Control page state from overwriting positions/visibility set elsewhere)
      syncCreatorToDB({ _overlayState: { alertQueue: queue } });
    }
  };

  const filtered = tips.filter(t => {
    if (filter === "pending") return t.approved === null;
    if (filter === "approved") return t.approved === "approved";
    if (filter === "rejected") return t.approved === "rejected";
    if (filter === "auto") return t.approved === "auto";
    return true;
  });

  const totalLYX = tips.reduce((sum, t) => sum + t.amount, 0);
  const pendingCount = tips.filter(t => t.approved === null).length;
  const revealSet = useStaggerReveal(filtered.length, 50);

  if (!window.__fullsend_profile?.address) {
    return (
      <div style={{ maxWidth: 720, margin: "0 auto", padding: "80px 24px", textAlign: "center", opacity: mounted ? 1 : 0, transform: mounted ? "translateY(0)" : "translateY(20px)", transition: "all 0.5s cubic-bezier(0.16, 1, 0.3, 1)" }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
        <div style={{ fontFamily: "'Syne', sans-serif", fontSize: 20, fontWeight: 800, color: COLORS.text, marginBottom: 8 }}>Connect to Access Controls</div>
        <div style={{ fontSize: 12, color: COLORS.textMuted, lineHeight: 1.6, maxWidth: 360, margin: "0 auto" }}>
          Connect your Universal Profile to manage your live stream controls, tips, and overlay settings.
        </div>
      </div>
    );
  }

  return (
    <div style={{
      maxWidth: 720, margin: "0 auto", padding: "28px 24px",
      opacity: mounted ? 1 : 0,
      transform: mounted ? "translateY(0)" : "translateY(15px)",
      transition: "all 0.5s cubic-bezier(0.16, 1, 0.3, 1)",
    }}>
      {/* ── Live Stream Controls ── */}
      <div style={{
        background: COLORS.surface, border: `1px solid ${COLORS.cyan}15`,
        borderRadius: 12, padding: 18, marginBottom: 20,
      }}>
        <div style={{
          fontSize: 11, fontWeight: 700, color: COLORS.cyan,
          textTransform: "uppercase", letterSpacing: "1.5px", marginBottom: 14,
          fontFamily: "'Syne', sans-serif",
          display: "flex", alignItems: "center", gap: 6,
        }}>
          <span>📡</span> Live Stream Controls
        </div>

        {/* ON AIR sign — big retro tv studio button */}
        <button onClick={toggleLive} style={{
          width: "100%", padding: "10px 24px",
          borderRadius: 10,
          background: isLive
            ? "linear-gradient(135deg, #ff1a1a, #cc0000)"
            : `${COLORS.bgAlt}`,
          border: isLive
            ? "2.5px solid #ff4d4d"
            : `2px solid ${COLORS.border}`,
          cursor: "pointer",
          transition: "all 0.3s",
          marginBottom: 14,
          position: "relative",
          overflow: "hidden",
          boxShadow: isLive
            ? `0 0 40px #ff000060, 0 0 80px #ff000030, inset 0 0 20px #ffffff20`
            : "none",
          animation: isLive ? "onAirPulse 2s infinite" : "none",
        }}>
          {/* Subtle scanline effect when live */}
          {isLive && (
            <div style={{
              position: "absolute", inset: 0,
              background: "repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(255,255,255,0.04) 3px, rgba(255,255,255,0.04) 4px)",
              pointerEvents: "none",
            }} />
          )}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "center", gap: 12,
            position: "relative",
          }}>
            {/* Red dot indicator */}
            <div style={{
              width: 11, height: 11, borderRadius: "50%",
              background: isLive ? "#fff" : COLORS.textMuted + "40",
              boxShadow: isLive ? "0 0 10px #fff, 0 0 20px #fff" : "none",
              animation: isLive ? "livePulse 1.2s infinite" : "none",
            }} />
            <div style={{
              fontFamily: "'Syne', sans-serif",
              fontSize: 20, fontWeight: 900,
              color: isLive ? "#fff" : COLORS.textMuted,
              letterSpacing: "6px",
              textShadow: isLive ? "0 0 16px #fff, 0 2px 4px #00000080" : "none",
              textTransform: "uppercase",
            }}>
              ON AIR
            </div>
            <div style={{
              width: 11, height: 11, borderRadius: "50%",
              background: isLive ? "#fff" : COLORS.textMuted + "40",
              boxShadow: isLive ? "0 0 10px #fff, 0 0 20px #fff" : "none",
              animation: isLive ? "livePulse 1.2s infinite 0.6s" : "none",
            }} />
          </div>
          <div style={{
            fontSize: 8, marginTop: 4,
            color: isLive ? "#ffffffb0" : COLORS.textMuted,
            fontFamily: "'JetBrains Mono', monospace",
            letterSpacing: "1px",
            position: "relative",
          }}>
            {isLive ? "● BROADCASTING — CLICK TO STOP" : "○ CLICK TO GO LIVE"}
          </div>
        </button>
        <div style={{
          fontSize: 10, color: COLORS.textMuted, marginBottom: 10,
          padding: "8px 12px", borderRadius: 6,
          background: `${COLORS.bgAlt}60`, border: `1px solid ${COLORS.border}`,
          lineHeight: 1.5,
        }}>
          <span style={{ color: COLORS.cyan, fontWeight: 700 }}>ℹ️ What this does:</span> Flips a "LIVE NOW" badge on your FullSend tip widget and creator directory listing — letting viewers know you're streaming right now. <strong>This does not start your stream</strong> — start your stream on YouTube/Twitch/Kick as usual, then click On Air here to show the badge. Auto-turns off if Control is closed for 2+ minutes.
        </div>

        {/* Primary Stream Selector — lists every link from the FS profile.
            First link is the default; click any to change where LIVE NOW points. */}
        {(() => {
          const links = window.__fullsend_settings_data?.socialLinks || window.__fullsend_profile?.links || {};
          const availablePlatforms = [
            { key: "youtube", label: "YouTube", color: "#FF0000", url: links.youtube },
            { key: "twitch", label: "Twitch", color: "#9146FF", url: links.twitch },
            { key: "kick", label: "Kick", color: "#53FC18", url: links.kick },
            { key: "rumble", label: "Rumble", color: "#85c742", url: links.rumble },
            { key: "x", label: "X", color: COLORS.text, url: links.x },
            { key: "website", label: "Website", color: "#4a9eff", url: links.website },
          ].filter(p => p.url);
          if (availablePlatforms.length === 0) {
            return (
              <div style={{
                padding: "8px 12px", borderRadius: 6, marginBottom: 14,
                background: `${COLORS.orange}08`, border: `1px solid ${COLORS.orange}20`,
                fontSize: 9, color: COLORS.orange, lineHeight: 1.5,
              }}>
                ⚠️ Add a link in Settings → Profile → Links so viewers clicking LIVE NOW can find your stream.
              </div>
            );
          }
          // First link is the default; honor a saved choice only if it still exists.
          const effectivePrimary = availablePlatforms.some(p => p.key === primaryStream)
            ? primaryStream
            : availablePlatforms[0].key;
          return (
            <div style={{
              padding: "10px 12px", borderRadius: 6, marginBottom: 14,
              background: `${COLORS.bgAlt}80`, border: `1px solid ${COLORS.border}`,
            }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: COLORS.textMuted, marginBottom: 8, textTransform: "uppercase", letterSpacing: "1px" }}>
                🔗 When viewers click LIVE NOW, send them to:
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {availablePlatforms.map(p => {
                  const selected = effectivePrimary === p.key;
                  return (
                    <button key={p.key} onClick={() => updatePrimaryStream(p.key)} style={{
                      padding: "5px 10px", borderRadius: 5,
                      background: selected ? `${p.color}18` : COLORS.surface,
                      border: `1px solid ${selected ? p.color + "60" : COLORS.border}`,
                      color: selected ? p.color : COLORS.textMuted,
                      fontSize: 9, fontWeight: 700, cursor: "pointer",
                      fontFamily: "'JetBrains Mono', monospace",
                      display: "flex", alignItems: "center", gap: 4,
                    }}>
                      {p.label}
                      {selected && <span style={{ fontSize: 7 }}>✓</span>}
                    </button>
                  );
                })}
              </div>
              <div style={{ fontSize: 8, color: COLORS.textMuted, marginTop: 6, fontFamily: "'JetBrains Mono', monospace" }}>
                Sending viewers to your {availablePlatforms.find(p => p.key === effectivePrimary)?.label || effectivePrimary}. First link is the default — click any to change.
              </div>
            </div>
          );
        })()}
        <style>{`
          @keyframes onAirPulse {
            0%, 100% { box-shadow: 0 0 40px #ff000060, 0 0 80px #ff000030, inset 0 0 20px #ffffff20; }
            50% { box-shadow: 0 0 60px #ff000090, 0 0 120px #ff000050, inset 0 0 20px #ffffff30; }
          }
          @keyframes livePulse {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.6; transform: scale(1.2); }
          }
        `}</style>

        {/* Stream Overlay URL */}
        <div style={{
          padding: "10px 12px", borderRadius: 8,
          background: COLORS.bgAlt, border: `1px solid ${COLORS.cyan}15`,
          marginBottom: 12,
        }}>
          <div style={{ fontSize: 9, fontWeight: 700, color: COLORS.textMuted, marginBottom: 6, textTransform: "uppercase", letterSpacing: "1px", display: "flex", alignItems: "center", gap: 4 }}>
            <span>🎬</span> Your Overlay URL
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{
              flex: 1, fontSize: 10, color: COLORS.cyan,
              fontFamily: "'JetBrains Mono', monospace",
              padding: "6px 10px", borderRadius: 5,
              background: COLORS.surface, border: `1px solid ${COLORS.border}`,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
              https://fullsend.live/overlay/{window.__fullsend_profile?.address}
            </div>
            <CopyButton text={`https://fullsend.live/overlay/${window.__fullsend_profile?.address}`} label="Copy" color={COLORS.cyan} />
          </div>
          <div style={{ fontSize: 8, color: COLORS.textMuted, marginTop: 5 }}>
            Add as Browser Source in OBS · 1920×1080
          </div>
          <div style={{ fontSize: 8, color: COLORS.accent, marginTop: 4, display: "flex", alignItems: "center", gap: 4 }}>
            <span>💡</span>
            <span>TTS requires a Desktop Audio source in OBS — System TTS plays through OS, not the browser tab</span>
          </div>
          <button
            onClick={triggerOverlayTest}
            style={{
              marginTop: 10, width: "100%",
              padding: "9px 12px", borderRadius: 6,
              background: overlayTestSent ? `${COLORS.accent}1f` : COLORS.accent,
              color: overlayTestSent ? COLORS.accent : "#06060b",
              border: `1px solid ${COLORS.accent}`,
              fontSize: 11, fontWeight: 800, cursor: "pointer",
              fontFamily: "'Outfit', sans-serif", letterSpacing: "0.3px",
              transition: "all 0.2s",
            }}
          >
            {overlayTestSent ? "✓ Sent — check your overlay" : "🚀 Test Overlay"}
          </button>
          <div style={{ fontSize: 8, color: COLORS.textMuted, marginTop: 4, lineHeight: 1.4 }}>
            Fires a test alert on your overlay so you can confirm it's connected — no tip needed.
          </div>
        </div>

        {/* Active Layout Toggle */}
        <div style={{
          display: "flex", alignItems: "center", gap: 8,
          padding: "8px 12px", borderRadius: 6,
          background: COLORS.bgAlt, border: `1px solid ${COLORS.border}`,
          marginBottom: 14,
        }}>
          <span style={{ fontSize: 9, fontWeight: 700, color: COLORS.textMuted, whiteSpace: "nowrap" }}>Layout:</span>
          <div style={{ display: "flex", gap: 4 }}>
            <button onClick={() => {
              const newState = { ...(window.__fullsend_overlay_state || {}), layoutMode: "grid" };
              window.__fullsend_overlay_state = newState;
              try { localStorage.setItem("fullsend_layout_mode", "grid"); } catch {}
              syncCreatorToDB({ _overlayState: newState });
              setAutoShowVersion(v => v + 1);
            }} style={{
              padding: "4px 10px", borderRadius: 5,
              background: (window.__fullsend_overlay_state?.layoutMode || "grid") === "grid" ? `${COLORS.accent}15` : COLORS.surface,
              border: `1px solid ${(window.__fullsend_overlay_state?.layoutMode || "grid") === "grid" ? COLORS.accent + "35" : COLORS.border}`,
              color: (window.__fullsend_overlay_state?.layoutMode || "grid") === "grid" ? COLORS.accent : COLORS.textMuted,
              fontSize: 9, fontWeight: 700, cursor: "pointer",
              fontFamily: "'JetBrains Mono', monospace",
              display: "flex", alignItems: "center", gap: 4,
            }}>
              ⚙️ Grid
              {(window.__fullsend_overlay_state?.layoutMode || "grid") === "grid" && <span style={{ width: 4, height: 4, borderRadius: "50%", background: COLORS.accent, boxShadow: `0 0 4px ${COLORS.accent}` }} />}
            </button>
            <button onClick={() => {
              const newState = { ...(window.__fullsend_overlay_state || {}), layoutMode: "custom" };
              window.__fullsend_overlay_state = newState;
              try { localStorage.setItem("fullsend_layout_mode", "custom"); } catch {}
              syncCreatorToDB({ _overlayState: newState });
              setAutoShowVersion(v => v + 1);
            }} style={{
              padding: "4px 10px", borderRadius: 5,
              background: (window.__fullsend_overlay_state?.layoutMode || "grid") === "custom" ? `${COLORS.accent}15` : COLORS.surface,
              border: `1px solid ${(window.__fullsend_overlay_state?.layoutMode || "grid") === "custom" ? COLORS.accent + "35" : COLORS.border}`,
              color: (window.__fullsend_overlay_state?.layoutMode || "grid") === "custom" ? COLORS.accent : COLORS.textMuted,
              fontSize: 9, fontWeight: 700, cursor: "pointer",
              fontFamily: "'JetBrains Mono', monospace",
              display: "flex", alignItems: "center", gap: 4,
            }}>
              📐 Custom
              {(window.__fullsend_overlay_state?.layoutMode || "grid") === "custom" && <span style={{ width: 4, height: 4, borderRadius: "50%", background: COLORS.accent, boxShadow: `0 0 4px ${COLORS.accent}` }} />}
            </button>
          </div>
          <span style={{ fontSize: 8, color: COLORS.textMuted, marginLeft: "auto" }}>Live on stream</span>
        </div>

        {/* Overlay Element Toggles */}
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 14 }}>
          {[
            { key: "tips", label: "Tips", emoji: "💰", color: COLORS.accent },
            { key: "follows", label: "Follows", emoji: "👤", color: COLORS.cyan },
            { key: "micro", label: "Micro", emoji: "⚡", color: COLORS.orange },
            { key: "polls", label: "Polls", emoji: "📊", color: COLORS.purple },
            { key: "goal", label: "Goal", emoji: "🎯", color: COLORS.orange },
            { key: "qr", label: "QR", emoji: "📱", color: COLORS.text },
            { key: "sounds", label: "Sounds", emoji: "🔊", color: COLORS.gold },
          ].map(item => {
            const isOn = visibility[item.key];
            return (
              <button key={item.key} onClick={() => updateVisibility(item.key)} style={{
                padding: "4px 6px", borderRadius: 6,
                background: isOn ? `${item.color}15` : COLORS.bgAlt,
                border: `1px solid ${isOn ? item.color + "30" : COLORS.border}`,
                color: isOn ? item.color : COLORS.textMuted,
                fontSize: 10, fontWeight: 700, cursor: "pointer",
                fontFamily: "'JetBrains Mono', monospace",
                display: "flex", alignItems: "center", gap: 5,
                transition: "all 0.2s",
              }}>
                <span style={{ fontSize: 11 }}>{item.emoji}</span> {item.label}
                <span style={{
                  width: 6, height: 6, borderRadius: "50%",
                  background: isOn ? item.color : COLORS.textMuted + "40",
                  boxShadow: isOn ? `0 0 6px ${item.color}50` : "none",
                }} />
              </button>
            );
          })}
        </div>

        {/* Visibility warning toast — shown when user clicks Show on a hidden tier */}
        {visibilityWarning && (
          <div style={{
            marginBottom: 14, padding: "10px 14px", borderRadius: 8,
            background: `${COLORS.orange}15`, border: `1px solid ${COLORS.orange}40`,
            display: "flex", alignItems: "center", gap: 10,
            animation: "slideDown 0.3s ease-out",
          }}>
            <span style={{ fontSize: 14 }}>⚠️</span>
            <span style={{ fontSize: 11, color: COLORS.orange, fontWeight: 600, flex: 1 }}>
              {visibilityWarning.message}
            </span>
            <button onClick={() => {
              updateVisibility(visibilityWarning.tier);
              setVisibilityWarning(null);
            }} style={{
              padding: "4px 10px", borderRadius: 5,
              background: `${COLORS.orange}25`, border: `1px solid ${COLORS.orange}50`,
              color: COLORS.orange, fontSize: 10, fontWeight: 700, cursor: "pointer",
              fontFamily: "'JetBrains Mono', monospace",
            }}>
              Toggle on
            </button>
            <button onClick={() => setVisibilityWarning(null)} style={{
              padding: "4px 8px", borderRadius: 5,
              background: "transparent", border: "none",
              color: COLORS.textMuted, fontSize: 14, cursor: "pointer",
            }}>×</button>
          </div>
        )}

        {/* QR Code — shown when toggled on */}
        {visibility.qr && window.__fullsend_profile?.address && (() => {
          const s = window.__fullsend_settings_data || {};
          const qrDest = (window.__fullsend_overlay_state || {}).qrDestination || "tip";
          const addr = window.__fullsend_profile.address;
          const qrOptions = [
            { value: "tip", label: "💰 Tip Page", url: `https://fullsend.live/tip/${addr}`, qrColor: "00ff88" },
            { value: "profile", label: "👤 UP Profile", url: `https://universalprofile.cloud/${addr}`, qrColor: "c084fc" },
          ];
          if (s.customQrUrl) qrOptions.push({ value: "custom", label: "🔗 Custom URL", url: s.customQrUrl, qrColor: "22d3ee" });
          if (s.uploadedQrImage) qrOptions.push({ value: "uploaded", label: "📁 Uploaded QR", url: null });
          const activeQr = qrOptions.find(q => q.value === qrDest) || qrOptions[0];
          const qrImgSrc = activeQr.value === "uploaded" ? s.uploadedQrImage
            : `https://api.qrserver.com/v1/create-qr-code/?size=200x200&bgcolor=06060b&color=${activeQr.qrColor || "00ff88"}&data=${encodeURIComponent(activeQr.url)}`;
          return (
          <div style={{
            marginBottom: 14, padding: "12px 16px", borderRadius: 8,
            background: COLORS.bgAlt, border: `1px solid ${COLORS.border}`,
            display: "flex", alignItems: "center", gap: 14,
          }}>
            <img
              src={qrImgSrc}
              alt="QR Code"
              style={{ width: 80, height: 80, borderRadius: 6, border: `1px solid ${COLORS.accent}20`, objectFit: "contain", background: activeQr.value === "uploaded" ? COLORS.surface : "transparent" }}
            />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.accent, marginBottom: 4 }}>📱 QR Code on Stream</div>
              <div style={{ fontSize: 9, color: COLORS.textMuted, lineHeight: 1.5, marginBottom: 6 }}>
                Viewers scan to open the selected link
              </div>
              <select
                value={qrDest}
                onChange={e => {
                  const ss = window.__fullsend_settings_data || {};
                  window.__fullsend_overlay_state = {
                    ...(window.__fullsend_overlay_state || {}),
                    qrDestination: e.target.value,
                    customQrUrl: ss.customQrUrl || "",
                    uploadedQrImage: ss.uploadedQrImage || "",
                  };
                  // Send only QR fields, not full state
                  syncCreatorToDB({ _overlayState: {
                    qrDestination: e.target.value,
                    customQrUrl: ss.customQrUrl || "",
                    uploadedQrImage: ss.uploadedQrImage || "",
                  } });
                }}
                style={{
                  padding: "4px 8px", borderRadius: 4, background: COLORS.surface, border: `1px solid ${COLORS.border}`,
                  color: COLORS.text, fontFamily: "'JetBrains Mono', monospace", fontSize: 9, outline: "none",
                }}
              >
                {qrOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>
          );
        })()}

        <div style={{ display: "flex", gap: 10 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
              📊 Active Poll
            </div>
            {activePoll ? (
              <div style={{
                padding: "10px 14px", borderRadius: 8,
                background: pollOnStream ? `${COLORS.cyan}06` : COLORS.bgAlt,
                border: `1px solid ${pollOnStream ? COLORS.cyan + "20" : COLORS.border}`,
              }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginBottom: 4 }}>
                  {activePoll.question}
                </div>
                <div style={{ fontSize: 9, color: COLORS.textMuted, marginBottom: 8 }}>
                  {activePoll.totalVotes} votes · {activePoll.status === "active" ? "● Live" : "Ended"}
                  {activePoll.timeRemaining > 0 && <span style={{ color: COLORS.orange, fontWeight: 700 }}> · ⏱ {formatPollTimer(activePoll.timeRemaining)}</span>}
                  {pollOnStream && <span style={{ color: COLORS.cyan, fontWeight: 700 }}> · ON STREAM</span>}
                </div>
                <button onClick={togglePollOnStream} style={{
                  width: "100%", padding: "7px 0", borderRadius: 6,
                  background: pollOnStream ? `${COLORS.red}10` : `${COLORS.cyan}12`,
                  border: `1px solid ${pollOnStream ? COLORS.red + "20" : COLORS.cyan + "25"}`,
                  color: pollOnStream ? COLORS.red : COLORS.cyan,
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 10, fontWeight: 700, cursor: "pointer",
                }}>
                  {pollOnStream ? "✕ Hide from Stream" : "📺 Show on Stream"}
                </button>
              </div>
            ) : (
              <div style={{ padding: "14px", borderRadius: 8, background: COLORS.bgAlt, border: `1px solid ${COLORS.border}`, fontSize: 10, color: COLORS.textMuted, textAlign: "center" }}>
                {pollLoading ? (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    {[0,1,2].map(i => <span key={i} style={{ width: 4, height: 4, borderRadius: "50%", background: COLORS.cyan, animation: `dotBounce 0.6s ease-in-out ${i*0.15}s infinite` }} />)}
                    <span style={{ marginLeft: 4 }}>Loading poll...</span>
                  </span>
                ) : "No active poll"}
              </div>
            )}
          </div>

          {/* Tip Goal */}
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
              🎯 Tip Goal
            </div>
            {(() => {
              const goal = controlActiveGoal;
              const hasGoal = goal && goal.title;
              if (!hasGoal) return (
                <div style={{ padding: "14px", borderRadius: 8, background: COLORS.bgAlt, border: `1px solid ${COLORS.border}`, fontSize: 10, color: COLORS.textMuted, textAlign: "center" }}>
                  No active goal
                </div>
              );
              const title = goal.title;
              const target = parseFloat(goal.target) || 100;
              const current = parseFloat(goal.current) || 0;
              const pct = Math.min((current / target) * 100, 100);
              const showOnOverlay = goal.showOnOverlay !== false;
              return (
                <div style={{
                  padding: "10px 14px", borderRadius: 8,
                  background: showOnOverlay ? `${COLORS.accent}06` : COLORS.bgAlt,
                  border: `1px solid ${showOnOverlay ? COLORS.accent + "20" : COLORS.border}`,
                }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.text, marginBottom: 4 }}>{title}</div>
                  <div style={{ fontSize: 9, color: COLORS.textMuted, marginBottom: 8, fontFamily: "'JetBrains Mono', monospace" }}>
                    {current.toFixed(1)} / {target} LYX ({pct.toFixed(0)}%)
                    {showOnOverlay && <span style={{ color: COLORS.accent, fontWeight: 700 }}> · ON STREAM</span>}
                  </div>
                  <button onClick={async () => {
                    const newVal = !showOnOverlay;
                    await GoalAPI.setVisibility(controlAddr, { showOnOverlay: newVal });
                    // Sync overlay visibility flag in _overlayState for OBS
                    const vis = { ...(window.__fullsend_overlay_state?.visibility || visibility), goal: newVal };
                    window.__fullsend_overlay_state = { ...(window.__fullsend_overlay_state || {}), goalVisible: newVal, visibility: vis };
                    syncCreatorToDB({ _overlayState: { goalVisible: newVal, visibility: { goal: newVal } } });
                    setVisibility(prev => ({ ...prev, goal: newVal }));
                    setAutoShowVersion(v => v + 1);
                    window.dispatchEvent(new CustomEvent("fullsend-goal-updated"));
                  }} style={{
                    width: "100%", padding: "7px 0", borderRadius: 6,
                    background: showOnOverlay ? `${COLORS.red}10` : `${COLORS.accent}12`,
                    border: `1px solid ${showOnOverlay ? COLORS.red + "20" : COLORS.accent + "25"}`,
                    color: showOnOverlay ? COLORS.red : COLORS.accent,
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 10, fontWeight: 700, cursor: "pointer",
                  }}>
                    {showOnOverlay ? "✕ Hide from Stream" : "📺 Show on Stream"}
                  </button>
                </div>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Claim Drop control */}
      <ControlClaimDrop creatorAddr={controlAddr} />

      {/* Moments on Chain — also surfaced on Control, below Claim Drop/Leaderboards */}
      <div style={{ marginTop: 24, marginBottom: 24 }}><MOCManager /></div>

      {/* Stats Bar */}
      <div style={{
        display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr",
        gap: 10, marginBottom: 20,
      }}>
        {[
          { label: "Total Tips", value: tips.length, color: COLORS.text },
          { label: "Total LYX", value: totalLYX.toFixed(1), color: COLORS.accent },
          { label: "Pending", value: pendingCount, color: pendingCount > 0 ? COLORS.orange : COLORS.textMuted },
          { label: "Approved", value: tips.filter(t => t.approved === "approved").length, color: COLORS.green },
        ].map((stat, i) => (
          <div key={stat.label} style={{
            background: COLORS.surface,
            border: `1px solid ${COLORS.border}`,
            borderRadius: 10, padding: "14px 12px",
            textAlign: "center",
            animation: `fadeInUp 0.4s ease-out ${i * 0.05}s both`,
          }}>
            <div style={{
              fontFamily: "'Syne', sans-serif",
              fontSize: 22, fontWeight: 800, color: stat.color,
            }}>{stat.value}</div>
            <div style={{ fontSize: 9, color: COLORS.textMuted, marginTop: 3, letterSpacing: "1.5px", textTransform: "uppercase", fontWeight: 600 }}>
              {stat.label}
            </div>
          </div>
        ))}
      </div>

      {/* Filter Tabs */}
      <div style={{
        display: "flex", gap: 2, marginBottom: 14,
        background: COLORS.surface,
        borderRadius: 8, padding: 3,
        border: `1px solid ${COLORS.borderSubtle}`,
      }}>
        {["all", "pending", "approved", "rejected", "auto"].map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{
            flex: 1, padding: "7px 10px", borderRadius: 6,
            background: filter === f ? `${COLORS.accent}12` : "transparent",
            color: filter === f ? COLORS.accent : COLORS.textMuted,
            border: "none", fontSize: 10, fontWeight: 700,
            fontFamily: "'JetBrains Mono', monospace",
            cursor: "pointer", textTransform: "uppercase",
            letterSpacing: "0.5px",
            transition: "all 0.2s",
          }}>
            {f} {f === "pending" && pendingCount > 0 ? `(${pendingCount})` : ""}
          </button>
        ))}
      </div>

      {/* Alert Sound Toggles */}
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12, flexWrap: "wrap" }}>
        <span style={{ fontSize: 9, color: COLORS.textMuted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "1px" }}>Sounds:</span>
        {[
          { key: "tips", label: "Tips", icon: "💰", color: COLORS.accent },
          { key: "follows", label: "Follows", icon: "👤", color: COLORS.cyan },
          { key: "polls", label: "Polls", icon: "📊", color: COLORS.orange },
        ].map(t => {
          const st = window.__fullsend_sound_toggles || { tips: true, follows: true, polls: true };
          const enabled = st[t.key] !== false;
          return (
            <button key={t.key} onClick={() => {
              const current = window.__fullsend_sound_toggles || { tips: true, follows: true, polls: true };
              const updated = { ...current, [t.key]: !enabled };
              window.__fullsend_sound_toggles = updated;
              setAutoShowVersion(v => v + 1); // force re-render
            }} style={{
              padding: "4px 10px", borderRadius: 5,
              background: enabled ? `${t.color}12` : "transparent",
              border: `1px solid ${enabled ? t.color + "25" : COLORS.border}`,
              color: enabled ? t.color : COLORS.textMuted,
              fontSize: 9, fontWeight: 600, cursor: "pointer",
              display: "flex", alignItems: "center", gap: 4,
              transition: "all 0.15s",
            }}>
              <span>{t.icon}</span> {t.label} {enabled ? "🔊" : "🔇"}
            </button>
          );
        })}
        <div style={{ width: 1, height: 16, background: COLORS.border }} />
        <button onClick={() => {
          const tts = window.__fullsend_tts || { enabled: false };
          const updated = { ...tts, enabled: !tts.enabled };
          window.__fullsend_tts = updated;
          try { const s = JSON.parse(localStorage.getItem("fullsend_settings") || "{}"); s.ttsEnabled = updated.enabled; localStorage.setItem("fullsend_settings", JSON.stringify(s)); } catch {}
          setAutoShowVersion(v => v + 1);
        }} style={{
          padding: "4px 10px", borderRadius: 5,
          background: (window.__fullsend_tts?.enabled) ? `${COLORS.purple}12` : "transparent",
          border: `1px solid ${(window.__fullsend_tts?.enabled) ? COLORS.purple + "25" : COLORS.border}`,
          color: (window.__fullsend_tts?.enabled) ? COLORS.purple : COLORS.textMuted,
          fontSize: 9, fontWeight: 600, cursor: "pointer",
          display: "flex", alignItems: "center", gap: 4,
          transition: "all 0.15s",
        }}>
          <span>🗣️</span> TTS {(window.__fullsend_tts?.enabled) ? "ON" : "OFF"}
        </button>
      </div>

      {/* Auto-Show Quick Toggles */}
      <div style={{
        display: "flex", alignItems: "center", gap: 6, marginBottom: 14,
        padding: "10px 14px", borderRadius: 10,
        background: COLORS.surface, border: `1px solid ${COLORS.border}`,
        flexWrap: "wrap",
      }}>
        <span style={{ fontSize: 9, color: COLORS.textMuted, fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase", marginRight: 4 }}>Auto-Show:</span>
        {[
          { key: "micro", label: "Micro", emoji: "⚡", color: COLORS.orange },
          { key: "supporter", label: "Supporter", emoji: "💚", color: COLORS.accent },
          { key: "champion", label: "Champion", emoji: "💪", color: COLORS.blue },
          { key: "legend", label: "Legend", emoji: "💎", color: COLORS.purple },
          { key: "whale", label: "Whale", emoji: "🐋", color: COLORS.gold },
        ].map(tier => {
          const autoShow = window.__fullsend_settings_data?.autoShowTiers || {};
          const isOn = autoShow[tier.key] || false;
          return (
            <button key={tier.key} onClick={() => {
              const current = window.__fullsend_settings_data?.autoShowTiers || {};
              const updated = { ...current, [tier.key]: !isOn };
              if (window.__fullsend_settings_data) window.__fullsend_settings_data.autoShowTiers = updated;
              try { const s = JSON.parse(localStorage.getItem("fullsend_settings") || "{}"); s.autoShowTiers = updated; localStorage.setItem("fullsend_settings", JSON.stringify(s)); } catch {}
              setAutoShowVersion(v => v + 1);
            }} style={{
              padding: "4px 10px", borderRadius: 5,
              background: isOn ? `${tier.color}12` : "transparent",
              border: `1px solid ${isOn ? tier.color + "25" : COLORS.border}`,
              color: isOn ? tier.color : COLORS.textMuted,
              fontSize: 9, fontWeight: 600, cursor: "pointer",
              display: "flex", alignItems: "center", gap: 4,
              transition: "all 0.15s",
            }}>
              <span>{tier.emoji}</span> {tier.label} {isOn ? "✓" : ""}
            </button>
          );
        })}
        <div style={{ marginLeft: "auto" }}>
          <button onClick={() => { setTips(prev => prev.map(t => { if (t.approved === null) { markTipHandled(t.id, "rejected"); return { ...t, approved: "rejected" }; } return t; })); }} style={{
            padding: "5px 12px", borderRadius: 5,
            background: `${COLORS.red}08`, border: `1px solid ${COLORS.red}18`,
            color: COLORS.red, fontSize: 9, fontWeight: 700, cursor: "pointer",
            fontFamily: "'JetBrains Mono', monospace",
          }}>Clear Pending</button>
        </div>
      </div>

      {/* Tip Queue */}
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {filtered.length === 0 && (
          <div style={{
            textAlign: "center", padding: 48,
            color: COLORS.textMuted, fontSize: 12,
          }}>
            No {filter === "all" ? "" : filter} tips yet
          </div>
        )}
        {filtered.map((tip, idx) => {
          const tier = getTier(tip.amount);
          const isVisible = revealSet.has(idx);
          return (
            <div key={tip.id} style={{
              background: COLORS.surface,
              border: `1px solid ${tip.approved === null ? tier.color + "20" : COLORS.border}`,
              borderRadius: 10,
              padding: "14px 16px",
              display: "flex",
              alignItems: "flex-start",
              gap: 12,
              transition: "all 0.3s cubic-bezier(0.4,0,0.2,1)",
              boxShadow: tip.approved === null ? `0 0 20px ${tier.color}06` : "none",
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? "translateX(0)" : "translateX(-15px)",
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = COLORS.surfaceAlt;
              e.currentTarget.style.borderColor = tip.approved === null ? tier.color + "35" : `${COLORS.accent}15`;
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = COLORS.surface;
              e.currentTarget.style.borderColor = tip.approved === null ? tier.color + "20" : COLORS.border;
            }}>
              {/* Avatar — micro shows ⚡, others show PFP */}
              <div style={{
                width: 38, height: 38, borderRadius: 8,
                background: `${tier.color}10`,
                border: `1px solid ${tier.color}15`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 18, flexShrink: 0, overflow: "hidden",
              }}>{tier.label === "Micro" ? "⚡" : (isImageAvatar(tip.from.avatar) ? <img src={tip.from.avatar} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 8 }} onError={e => { e.target.src = STOCK_AVATAR; }} /> : "👤")}</div>

              {/* Content */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
                  <span style={{ fontWeight: 600, fontSize: 12 }}>{tip.from.name}</span>
                  <span style={{ fontSize: 10, color: COLORS.textMuted }}>
                    {new Date(tip.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: tip.message ? 6 : 0 }}>
                  <span style={{ fontSize: 13 }}>{tier.emoji}</span>
                  <span style={{ color: tier.color, fontWeight: 700, fontSize: 14, fontFamily: "'JetBrains Mono', monospace" }}>{tip.amount} LYX</span>
                  {tip.isPlatform ? (
                    <span style={{
                      fontSize: 9, padding: "2px 6px", borderRadius: 3,
                      background: `${COLORS.accent}15`, color: COLORS.accent,
                      fontWeight: 700, letterSpacing: "0.5px",
                    }}>💚 FullSend Supporter</span>
                  ) : (
                    <span style={{
                      fontSize: 9, padding: "2px 6px", borderRadius: 3,
                      background: `${tier.color}10`, color: tier.color,
                      fontWeight: 700, letterSpacing: "0.5px",
                    }}>{tier.label}</span>
                  )}
                </div>
                {tip.message && (
                  <div style={{
                    fontSize: 12, color: COLORS.textSecondary,
                    padding: "7px 10px",
                    background: `${COLORS.bg}80`,
                    borderRadius: 6,
                    lineHeight: 1.5,
                    borderLeft: `2px solid ${tier.color}20`,
                  }}>"{tip.message}"</div>
                )}
              </div>

              {/* Actions */}
              <div style={{ display: "flex", gap: 5, flexShrink: 0 }}>
                {tip.approved === null ? (
                  <>
                    <button onClick={() => handleAction(tip.id, "approved")} style={{
                      padding: "7px 14px", borderRadius: 6,
                      background: `${COLORS.green}12`,
                      border: `1px solid ${COLORS.green}25`,
                      color: COLORS.green,
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: 10, fontWeight: 700,
                      cursor: "pointer",
                      transition: "all 0.2s",
                      letterSpacing: "0.5px",
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = `${COLORS.green}20`; e.currentTarget.style.transform = "scale(1.05)"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = `${COLORS.green}12`; e.currentTarget.style.transform = "scale(1)"; }}>
                      SHOW
                    </button>
                    <button onClick={() => handleAction(tip.id, "rejected")} style={{
                      padding: "7px 14px", borderRadius: 6,
                      background: `${COLORS.red}08`,
                      border: `1px solid ${COLORS.red}18`,
                      color: COLORS.red,
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: 10, fontWeight: 700,
                      cursor: "pointer",
                      transition: "all 0.2s",
                      letterSpacing: "0.5px",
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = `${COLORS.red}15`; e.currentTarget.style.transform = "scale(1.05)"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = `${COLORS.red}08`; e.currentTarget.style.transform = "scale(1)"; }}>
                      SKIP
                    </button>
                  </>
                ) : (
                  <span style={{
                    fontSize: 9, padding: "5px 10px", borderRadius: 4,
                    background: tip.approved === "approved" ? `${COLORS.green}10` :
                               tip.approved === "auto" ? `${COLORS.blue}10` : `${COLORS.red}08`,
                    color: tip.approved === "approved" ? COLORS.green :
                           tip.approved === "auto" ? COLORS.blue : COLORS.red,
                    fontWeight: 700, textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    border: `1px solid ${
                      tip.approved === "approved" ? COLORS.green + "15" :
                      tip.approved === "auto" ? COLORS.blue + "15" : COLORS.red + "10"
                    }`,
                  }}>
                    {tip.approved === "auto" ? "AUTO ✓" : tip.approved === "approved" ? "SHOWN ✓" : "SKIPPED"}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

    </div>
  );
}
function StreamOverlay() {
  const [alerts, setAlerts] = useState([]);
  const [subAlerts, setSubAlerts] = useState([]);
  const [microAlerts, setMicroAlerts] = useState([]);
  const [mounted, setMounted] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [showSettings, setShowSettings] = useState(false);
  const [showSoundsPanel, setShowSoundsPanel] = useState(false);
  const [showCustomLayout, setShowCustomLayout] = useState(false);
  const [customVis, setCustomVis] = useState({ tips: true, follows: true, micro: true, chat: true, polls: true, goal: true, qr: true, leaderboard: true });

  useEffect(() => {
    const handler = () => { setShowSettings(false); setShowSoundsPanel(false); setShowCustomLayout(false); };
    window.addEventListener("fullsend-tab-reset", handler);
    return () => window.removeEventListener("fullsend-tab-reset", handler);
  }, []);

  // Preload html2canvas and bind export buttons when overlay mounts
  useEffect(() => {
    if (!window.html2canvas) {
      const s = document.createElement("script");
      s.src = "https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js";
      document.head.appendChild(s);
    }
    const bindExport = () => {
      const btn = document.getElementById("export-png-custom");
      if (btn) {
        btn.onclick = function() {
          if (!window.html2canvas) return;
          var el = document.getElementById("fullsend-drag-canvas");
          var origBg = el.style.background;
          var origBT = el.style.borderTop;
          var origBB = el.style.borderBottom;
          el.style.background = "transparent";
          el.style.borderTop = "none";
          el.style.borderBottom = "none";
          var bgEls = el.querySelectorAll("[data-overlay-bg]");
          bgEls.forEach(function(e) { e.style.visibility = "hidden"; });
          window.html2canvas(el, {backgroundColor:null, scrollX: -window.scrollX, scrollY: -window.scrollY, windowWidth: document.documentElement.scrollWidth, windowHeight: document.documentElement.scrollHeight}).then(function(c) {
            el.style.background = origBg;
            el.style.borderTop = origBT;
            el.style.borderBottom = origBB;
            bgEls.forEach(function(e) { e.style.visibility = ""; });
            var link = document.createElement("a"); link.download = "fullsend-overlay.png"; link.href = c.toDataURL("image/png"); link.click();
          });
        };
      }
      const btn2 = document.getElementById("export-png-layout");
      if (btn2) {
        btn2.onclick = function() {
          if (!window.html2canvas) return;
          window.html2canvas(document.getElementById("fullsend-overlay-preview"), {backgroundColor:null, scrollX: -window.scrollX, scrollY: -window.scrollY, windowWidth: document.documentElement.scrollWidth, windowHeight: document.documentElement.scrollHeight}).then(function(c) { var link = document.createElement("a"); link.download = "fullsend-overlay.png"; link.href = c.toDataURL("image/png"); link.click(); });
        };
      }
    };
    const interval = setInterval(bindExport, 500);
    bindExport();
    return () => clearInterval(interval);
  }, []);

  // Global PNG export handler — runs outside React click event context
  useEffect(() => {
    const exportHandler = () => {
      const el = document.getElementById("fullsend-drag-canvas");
      if (!el || !window.html2canvas) { console.error("Export not ready:", { el: !!el, h2c: !!window.html2canvas }); return; }
      window.html2canvas(el, {backgroundColor:null}).then(c => {
        const link = document.createElement("a");
        link.download = "fullsend-overlay.png";
        link.href = c.toDataURL("image/png");
        link.click();
      }).catch(err => console.error("Export failed:", err));
    };
    window.addEventListener("fullsend-export-png", exportHandler);
    return () => window.removeEventListener("fullsend-export-png", exportHandler);
  }, []);

  // Reactive sound state (syncs with Settings via globals)
  const [overlayTts, setOverlayTts] = useState(() => window.__fullsend_tts || { enabled: false, voice: "", minAmount: 5, rate: 1, pitch: 1 });
  const [overlayVolume, setOverlayVolume] = useState(() => window.__fullsend_volume || 0.7);
  const [overlaySoundToggles, setOverlaySoundToggles] = useState(() => window.__fullsend_sound_toggles || { tips: true, follows: true, polls: true });

  // Sync from globals every second (picks up Settings changes)
  useEffect(() => {
    const sync = setInterval(() => {
      const tts = window.__fullsend_tts;
      if (tts) setOverlayTts({ ...tts });
      if (window.__fullsend_volume !== undefined) setOverlayVolume(window.__fullsend_volume);
      if (window.__fullsend_sound_toggles) setOverlaySoundToggles({ ...window.__fullsend_sound_toggles });
    }, 1000);
    return () => clearInterval(sync);
  }, []);
  const [positions, setPositions] = useState({
    tips: "bottom-right",
    follows: "top-left",
    micro: "bottom-right",
    chat: "bottom-left",
    polls: "top-right",
    goal: "bottom-center",
    qr: "bottom-left",
    moc: "bottom-center",
  });
  const [positionMode, setPositionMode] = useState("grid"); // "grid" | "custom"
  const [customPositions, setCustomPositions] = useState(() => {
    try { return JSON.parse(localStorage.getItem("fullsend_custom_positions") || "{}"); } catch { return {}; }
  });
  const [savedCustomLayouts, setSavedCustomLayouts] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem("fullsend_saved_custom_layouts") || "null");
      if (stored && stored.length) return stored;
    } catch {}
    return [
      { id: 1, name: "Default", customPositions: {}, customVis: { tips: true, follows: true, micro: true, chat: true, polls: true, goal: true, qr: true, leaderboard: true } },
    ];
  });
  const [activeCustomLayout, setActiveCustomLayout] = useState(() => {
    try { return Number(localStorage.getItem("fullsend_active_custom_layout")) || 1; } catch { return 1; }
  });
  const [editingCustomLayoutName, setEditingCustomLayoutName] = useState(null);
  const [customLayoutSaveFlash, setCustomLayoutSaveFlash] = useState(false);
  const [streamBgImage, setStreamBgImage] = useState(() => localStorage.getItem("fullsend_stream_bg") || "");
  const [draggingElement, setDraggingElement] = useState(null);
  const canvasRef = useRef(null);
  const [visibility, setVisibility] = useState({
    tips: true,
    follows: true,
    micro: true,
    chat: false,
    polls: false,
    goal: false,
    leaderboard: true,
  });
  const [savedLayouts, setSavedLayouts] = useState([
    { id: 1, name: "Default", positions: { tips: "bottom-right", follows: "top-left", micro: "bottom-right", chat: "bottom-left", polls: "top-right", goal: "bottom-center", qr: "bottom-left" }, visibility: { tips: true, follows: true, micro: true, chat: false, polls: true, goal: false, qr: false } },
    { id: 2, name: "Gaming", positions: { tips: "top-right", follows: "top-left", micro: "top-right", chat: "bottom-left", polls: "center", goal: "bottom-center" }, visibility: { tips: true, follows: true, micro: true, chat: true, polls: true, goal: false } },
    { id: 3, name: "Minimal", positions: { tips: "bottom-right", follows: "bottom-right", micro: "bottom-right", chat: "bottom-left", polls: "top-right", goal: "bottom-center" }, visibility: { tips: true, follows: false, micro: false, chat: false, polls: false, goal: false } },
  ]);
  const [activeLayout, setActiveLayout] = useState(1);
  const [editingLayoutName, setEditingLayoutName] = useState(null);
  const [gridLayoutSaveFlash, setGridLayoutSaveFlash] = useState(false);
  const [showPoll, setShowPoll] = useState(false);
  const [overlayPolls, setOverlayPolls] = useState([]);

  const [layoutMode, setLayoutMode] = useState(() => {
    try { return localStorage.getItem("fullsend_layout_mode") || "grid"; } catch { return "grid"; }
  });

  // Sync positions + layoutMode to DB so standalone OBS overlay can read them
  // Note: we send only positions/layoutMode/customPositions, NOT visibility
  // Visibility is controlled by Control page; sending it from here would overwrite Control's settings
  useEffect(() => {
    const state = window.__fullsend_overlay_state || {};
    window.__fullsend_overlay_state = { ...state, positions, customPositions, layoutMode };
    syncCreatorToDB({ _overlayState: { positions, customPositions, layoutMode } });
  }, [positions, customPositions, layoutMode]);

  // Overlay is demo-only for layout — no real poll data needed

  const visiblePolls = [];
  const [showArchivedOverlay, setShowArchivedOverlay] = useState(false);

  useEffect(() => { requestAnimationFrame(() => setMounted(true)); }, []);

  // Tip alerts ($5+, via control panel approval)
  useEffect(() => {
    const handler = (e) => {
      const tip = e.detail;
      const tier = getTier(tip.amount);
      const alertObj = { ...tip, tier, showing: true, id: tip.id || Date.now() };
      setAlerts(prev => [...prev, alertObj]);
      setTimeout(() => {
        setAlerts(prev => prev.filter(a => a.id !== alertObj.id));
      }, tier.duration || 5000);

      // SFX + TTS intentionally NOT played here — OBS overlay (/overlay/[address])
      // owns audio playback. Control panel is silent to prevent double-fire when
      // creator has both Control and OBS open simultaneously.
    };
    window.addEventListener("fullsend-overlay", handler);
    return () => window.removeEventListener("fullsend-overlay", handler);
  }, []);

  // Micro tip alerts (under $5 -- auto, no moderation)
  useEffect(() => {
    const handler = (e) => {
      const tip = e.detail;
      const supMin = parseFloat(window.__fullsend_settings_data?.customTiers?.supporter?.min) || 5;
      if (tip.amount < supMin) {
        const microObj = { id: tip.id || Date.now(), from: tip.from, amount: tip.amount };
        setMicroAlerts(prev => [...prev, microObj]);
        setTimeout(() => {
          setMicroAlerts(prev => prev.filter(m => m.id !== microObj.id));
        }, 3500);
        // SFX intentionally NOT played here — OBS overlay owns audio
      }
    };
    window.addEventListener("fullsend-tip", handler);
    return () => window.removeEventListener("fullsend-tip", handler);
  }, []);

  // Sub alerts
  useEffect(() => {
    const handler = (e) => {
      const sub = e.detail;
      const subObj = { ...sub, id: sub.id || Date.now() };
      setSubAlerts(prev => [...prev, subObj]);
      setTimeout(() => {
        setSubAlerts(prev => prev.filter(s => s.id !== subObj.id));
      }, 4000);
      // Play follow sound
      const st = window.__fullsend_sound_toggles || {};
      if (st.follows !== false) playTierSound("follow");
    };
    window.addEventListener("fullsend-sub", handler);
    return () => window.removeEventListener("fullsend-sub", handler);
  }, []);

  const triggerDemoSub = () => {
    // Queue follows — only show 1 at a time
    if (subAlerts.length > 0) return;
    const profile = MOCK_PROFILES[Math.floor(Math.random() * MOCK_PROFILES.length)];
    const subObj = { id: Date.now() + Math.random(), profile };
    setSubAlerts([subObj]);
    setTimeout(() => {
      setSubAlerts(prev => prev.filter(s => s.id !== subObj.id));
    }, 4000);

    // Also push to DB alertQueue so it shows in standalone OBS overlay
    const alert = { type: "follow", from: profile, shownAt: Date.now() };
    const queue = [...((window.__fullsend_overlay_state || {}).alertQueue || []), alert].slice(-10);
    window.__fullsend_overlay_state = { ...(window.__fullsend_overlay_state || {}), alertQueue: queue };
    syncCreatorToDB({ _overlayState: { alertQueue: queue } });
  };

  // Seed chat + auto-generate messages
  useEffect(() => {
    const seed = [
      { id: 1, name: "ChainWizard", text: "this stream is fire 🔥", color: "#3ea6ff" },
      { id: 2, name: "LUKSOgang", text: "UP's are so underrated", color: "#3ea6ff" },
      { id: 3, name: "web3builder", text: "LUKSO is the move fr", color: "#3ea6ff" },
      { id: 4, name: "chillvibes", text: "gm everyone", color: "#3ea6ff" },
    ];
    setChatMessages(seed);

    const phrases = [
      "let's gooo", "facts", "W stream", "LUKSO 🚀", "this is the way",
      "based", "so clean", "love this", "when mint?", "bullish",
      "someone tip lol", "🔥🔥🔥", "fr fr", "wen moon", "LFG",
      "first time here, this is sick", "UP gang", "big facts", "100%",
    ];
    const names = ["viewer42", "LUKSOgang", "web3builder", "chillvibes", "cryptofan99", "nftmaxi", "degenlife", "hodlgang", "moonboy", "blocknerd"];

    const interval = setInterval(() => {
      if (!showChat) return;
      setChatMessages(prev => {
        const msg = {
          id: Date.now() + Math.random(),
          name: names[Math.floor(Math.random() * names.length)],
          text: phrases[Math.floor(Math.random() * phrases.length)],
          color: "#3ea6ff",
        };
        const next = [...prev, msg];
        return next.slice(-20); // keep last 20
      });
    }, 2500 + Math.random() * 2000);

    return () => clearInterval(interval);
  }, [showChat]);

  const triggerDemoMicro = () => {
    const profile = { name: "ChainWizard", avatar: STOCK_AVATAR, address: "0xdemo" };
    const amount = [1, 2, 3, 4][Math.floor(Math.random() * 4)];
    const microObj = { id: Date.now() + Math.random(), from: profile, amount };
    setMicroAlerts([microObj]); // one at a time
    setTimeout(() => {
      setMicroAlerts(prev => prev.filter(m => m.id !== microObj.id));
    }, 3500);
  };

  const triggerDemo = (tierKey) => {
    const tierInfo = TIERS[tierKey];
    const amount = tierKey === "fire" ? 100 : tierKey === "purple" ? 35 : tierKey === "blue" ? 15 : 7;
    const profileIdx = tierKey === "fire" ? 3 : tierKey === "purple" ? 2 : tierKey === "blue" ? 1 : 0;
    const profile = MOCK_PROFILES[profileIdx];
    const messages = [
      "Let's gooo! LUKSO to the moon!",
      "Best stream on the internet right now",
      "This is what Web3 looks like 🔥",
      "Universal Profiles are the future",
      "FullSend baby!! No middleman!",
    ];
    const tip = {
      id: Date.now() + Math.random(),
      from: profile,
      amount,
      message: messages[Math.floor(Math.random() * messages.length)],
      timestamp: new Date(),
    };
    const tier = getTier(amount);
    const alertObj = { ...tip, tier, showing: true };
    setAlerts([alertObj]);
    setTimeout(() => {
      setAlerts(prev => prev.filter(a => a.id !== alertObj.id));
    }, tier.duration || 5000);

    // TTS for demo alerts (respects per-tier toggles + sound toggles)
    const tts = window.__fullsend_tts;
    const soundToggles = window.__fullsend_sound_toggles || {};
    const demoTierKey = Object.keys(TIERS).find(k => TIERS[k] === tier) || "micro";
    const demoTtsTiers = tts?.tiers || { micro: false, green: false, blue: true, purple: true, fire: true };
    if (tts && tts.enabled && demoTtsTiers[demoTierKey] && window.speechSynthesis && soundToggles.tips !== false) {
      const text = `${profile.name} tipped ${amount} LYX: ${tip.message}`;
      const utterance = new SpeechSynthesisUtterance(text);
      const voices = window.speechSynthesis.getVoices();
      const voice = voices.find(v => v.name === tts.voice);
      if (voice) utterance.voice = voice;
      utterance.rate = tts.rate || 1;
      utterance.pitch = tts.pitch || 1;
      utterance.volume = window.__fullsend_volume || 0.7;
      window.speechSynthesis.speak(utterance);
    }
  };

  return (
    <div style={{
      padding: "28px 24px",
      opacity: mounted ? 1 : 0,
      transform: mounted ? "translateY(0)" : "translateY(15px)",
      transition: "all 0.5s cubic-bezier(0.16, 1, 0.3, 1)",
    }}>
      {/* Overlay Setup */}
      <div style={{
        maxWidth: 720, margin: "0 auto 20px",
        padding: "18px 20px", borderRadius: 12,
        background: COLORS.surface, border: `1px solid ${COLORS.accent}15`,
      }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.accent, marginBottom: 10, fontFamily: "'Syne', sans-serif", display: "flex", alignItems: "center", gap: 6 }}>
          <span>📺</span> Your Overlay URL
        </div>
        <div style={{
          display: "flex", alignItems: "center", gap: 8,
          padding: "10px 14px", borderRadius: 8,
          background: COLORS.bgAlt, border: `1px solid ${COLORS.border}`,
          marginBottom: 12,
        }}>
          <span style={{ flex: 1, fontSize: 11, fontFamily: "'JetBrains Mono', monospace", color: window.__fullsend_profile?.address ? COLORS.cyan : COLORS.textMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {window.__fullsend_profile?.address 
              ? `https://fullsend.live/overlay/${window.__fullsend_profile.address}`
              : "Connect your UP to get your overlay URL"}
          </span>
          {window.__fullsend_profile?.address && (
            <CopyButton text={`https://fullsend.live/overlay/${window.__fullsend_profile.address}`} />
          )}
        </div>
        <div style={{ fontSize: 10, color: COLORS.textMuted, lineHeight: 1.6 }}>
          Add this as a Browser Source in OBS, Streamlabs, XSplit, or any streaming app. Set to 1920×1080.
        </div>

        {/* Active Layout Mode */}
        <div style={{
          marginTop: 14, padding: "14px 18px", borderRadius: 10,
          background: `${COLORS.accent}06`,
          border: `1.5px solid ${COLORS.accent}25`,
          display: "flex", alignItems: "center", gap: 12,
          boxShadow: `0 0 20px ${COLORS.accent}08`,
        }}>
          <span style={{
            fontSize: 11, fontWeight: 800, color: COLORS.accent,
            whiteSpace: "nowrap",
            textTransform: "uppercase",
            letterSpacing: "1px",
            fontFamily: "'Syne', sans-serif",
          }}>⚡ Active Layout:</span>
          <div style={{ display: "flex", gap: 6 }}>
            <button onClick={() => { setLayoutMode("grid"); try { localStorage.setItem("fullsend_layout_mode", "grid"); } catch {} }} style={{
              padding: "7px 16px", borderRadius: 7,
              background: layoutMode === "grid" ? `${COLORS.accent}20` : COLORS.surface,
              border: `1.5px solid ${layoutMode === "grid" ? COLORS.accent : COLORS.border}`,
              color: layoutMode === "grid" ? COLORS.accent : COLORS.textMuted,
              fontSize: 11, fontWeight: 800, cursor: "pointer", transition: "all 0.2s",
              fontFamily: "'JetBrains Mono', monospace",
              display: "flex", alignItems: "center", gap: 6,
              boxShadow: layoutMode === "grid" ? `0 0 12px ${COLORS.accent}30` : "none",
            }}>
              ⚙️ Grid Layout
              {layoutMode === "grid" && <span style={{
                fontSize: 8, padding: "2px 6px", borderRadius: 3,
                background: COLORS.accent, color: COLORS.bg,
                fontWeight: 900, letterSpacing: "0.5px",
              }}>LIVE</span>}
            </button>
            <button onClick={() => { setLayoutMode("custom"); try { localStorage.setItem("fullsend_layout_mode", "custom"); } catch {} }} style={{
              padding: "7px 16px", borderRadius: 7,
              background: layoutMode === "custom" ? `${COLORS.accent}20` : COLORS.surface,
              border: `1.5px solid ${layoutMode === "custom" ? COLORS.accent : COLORS.border}`,
              color: layoutMode === "custom" ? COLORS.accent : COLORS.textMuted,
              fontSize: 11, fontWeight: 800, cursor: "pointer", transition: "all 0.2s",
              fontFamily: "'JetBrains Mono', monospace",
              display: "flex", alignItems: "center", gap: 6,
              boxShadow: layoutMode === "custom" ? `0 0 12px ${COLORS.accent}30` : "none",
            }}>
              📐 Custom Layout
              {layoutMode === "custom" && <span style={{
                fontSize: 8, padding: "2px 6px", borderRadius: 3,
                background: COLORS.accent, color: COLORS.bg,
                fontWeight: 900, letterSpacing: "0.5px",
              }}>LIVE</span>}
            </button>
          </div>
          <span style={{ fontSize: 10, color: COLORS.accent, marginLeft: "auto", fontWeight: 600, opacity: 0.8 }}>
            {layoutMode === "grid" ? "Grid positions are live on stream" : "Custom positions are live on stream"}
          </span>
        </div>
      </div>

      {/* Controls */}
      <div style={{
        maxWidth: 720, margin: "0 auto 20px",
        display: "flex", alignItems: "center", gap: 6,
        flexWrap: "wrap",
      }}>
        <span style={{ fontSize: 11, color: COLORS.textMuted, marginRight: 6, fontWeight: 600 }}>Demo alerts:</span>
        <button onClick={() => { if (showCustomLayout) { setCustomVis(prev => ({ ...prev, micro: !prev.micro })); } else { triggerDemoMicro(); } }} style={{
          padding: "7px 14px", borderRadius: 7,
          background: showCustomLayout && customVis.micro ? `${COLORS.textMuted}15` : `${COLORS.textMuted}10`,
          border: `1px solid ${showCustomLayout && customVis.micro ? COLORS.textMuted + "40" : COLORS.textMuted + "20"}`,
          boxShadow: showCustomLayout && customVis.micro ? `0 0 12px ${COLORS.textMuted}20` : "none",
          color: COLORS.textMuted,
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 11, fontWeight: 600,
          cursor: "pointer",
          transition: "all 0.2s",
          display: "flex", alignItems: "center", gap: 5,
        }}
        onMouseEnter={e => { e.currentTarget.style.background = `${COLORS.textMuted}18`; e.currentTarget.style.transform = "translateY(-1px)"; }}
        onMouseLeave={e => { e.currentTarget.style.background = `${COLORS.textMuted}10`; e.currentTarget.style.transform = "translateY(0)"; }}>
          ⚡ Micro
        </button>
        {Object.entries(TIERS).filter(([k]) => k !== "micro").map(([key, tier]) => {
          const visKey = key === "fire" ? "whale" : "tips";
          return (
          <button key={key} onClick={() => { if (showCustomLayout) { setCustomVis(prev => ({ ...prev, [visKey]: !prev[visKey] })); } else { triggerDemo(key); } }} style={{
            padding: "7px 14px", borderRadius: 7,
            background: showCustomLayout && customVis[visKey] ? `${tier.color}15` : `${tier.color}10`,
            border: `1px solid ${showCustomLayout && customVis[visKey] ? tier.color + "40" : tier.color + "20"}`,
            color: tier.color,
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 11, fontWeight: 600,
            cursor: "pointer",
            transition: "all 0.2s",
            display: "flex", alignItems: "center", gap: 5,
            boxShadow: showCustomLayout && customVis[visKey] ? `0 0 12px ${tier.color}20` : "none",
          }}
          onMouseEnter={e => { e.currentTarget.style.background = `${tier.color}18`; e.currentTarget.style.transform = "translateY(-1px)"; }}
          onMouseLeave={e => { e.currentTarget.style.background = showCustomLayout && customVis[visKey] ? `${tier.color}15` : `${tier.color}10`; e.currentTarget.style.transform = "translateY(0)"; }}>
            {tier.emoji} {tier.label}
          </button>
          );
        })}
        <div style={{ width: 1, height: 20, background: COLORS.border, marginLeft: 4, marginRight: 4 }} />
        <button onClick={() => { if (showCustomLayout) { setCustomVis(prev => ({ ...prev, follows: !prev.follows })); } else { triggerDemoSub(); } }} style={{
          padding: "7px 14px", borderRadius: 7,
          background: showCustomLayout && customVis.follows ? `${COLORS.cyan}15` : `${COLORS.cyan}10`,
          border: `1px solid ${showCustomLayout && customVis.follows ? COLORS.cyan + "40" : COLORS.cyan + "20"}`,
          boxShadow: showCustomLayout && customVis.follows ? `0 0 12px ${COLORS.cyan}20` : "none",
          color: COLORS.cyan,
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 11, fontWeight: 600,
          cursor: "pointer",
          transition: "all 0.2s",
          display: "flex", alignItems: "center", gap: 5,
        }}
        onMouseEnter={e => { e.currentTarget.style.background = `${COLORS.cyan}18`; e.currentTarget.style.transform = "translateY(-1px)"; }}
        onMouseLeave={e => { e.currentTarget.style.background = `${COLORS.cyan}10`; e.currentTarget.style.transform = "translateY(0)"; }}>
          👤 Follow Alert
        </button>
        <div style={{ width: 1, height: 20, background: COLORS.border, marginLeft: 4, marginRight: 4 }} />
        {[
          { key: "polls", label: "📊 Poll", color: COLORS.orange },
          { key: "goal", label: "🎯 Goal", color: COLORS.gold },
          { key: "leaderboard", label: "🏆 Leaderboard", color: COLORS.cyan },
          { key: "qr", label: "📱 QR", color: COLORS.text },
        ].map(item => {
          const isActive = showCustomLayout ? customVis[item.key] : visibility[item.key];
          return (
          <button key={item.key} onClick={() => {
            if (showCustomLayout) {
              setCustomVis(prev => ({ ...prev, [item.key]: !prev[item.key] }));
            } else {
              setVisibility(prev => {
                const next = { ...prev, [item.key]: !prev[item.key] };
                window.__fullsend_overlay_state = { ...(window.__fullsend_overlay_state || {}), visibility: next };
                return next;
              });
            }
          }} style={{
            padding: "7px 14px", borderRadius: 7,
            background: isActive ? `${item.color}15` : COLORS.surface,
            border: `1px solid ${isActive ? item.color + "30" : COLORS.border}`,
            color: isActive ? item.color : COLORS.textMuted,
            fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 600,
            cursor: "pointer", transition: "all 0.2s",
            display: "flex", alignItems: "center", gap: 5,
          }}>{item.label}{item.v2 && <span style={{ fontSize: 7, padding: "1px 4px", borderRadius: 3, background: `${COLORS.orange}15`, border: `1px solid ${COLORS.orange}25`, color: COLORS.orange, fontWeight: 700, letterSpacing: "0.5px" }}>V2</span>}</button>
          );
        })}
        <button onClick={() => {
          setAlerts([]); setSubAlerts([]); setMicroAlerts([]);
          setVisibility(prev => ({ ...prev, polls: false, goal: false, qr: false }));
          const mp = { name: "ChainWizard", avatar: STOCK_AVATAR, address: "0xdemo" };
          let t = 300;
          setTimeout(() => { const m = { id: Date.now()+Math.random(), from: mp, amount: 2 }; setMicroAlerts([m]); setTimeout(() => setMicroAlerts(p => p.filter(x => x.id !== m.id)), 3500); }, t); t += 2000;
          setTimeout(() => { const tip = { id: Date.now()+Math.random(), from: MOCK_PROFILES[0], amount: 7, message: "Love the stream!", tier: getTier(7), showing: true }; setAlerts([tip]); setTimeout(() => setAlerts(p => p.filter(a => a.id !== tip.id)), 4500); }, t); t += 5000;
          setTimeout(() => { const tip = { id: Date.now()+Math.random(), from: MOCK_PROFILES[1], amount: 15, message: "LUKSO is the future!", tier: getTier(15), showing: true }; setAlerts([tip]); setTimeout(() => setAlerts(p => p.filter(a => a.id !== tip.id)), 4500); }, t); t += 5000;
          setTimeout(() => { const tip = { id: Date.now()+Math.random(), from: MOCK_PROFILES[2], amount: 35, message: "Universal Profiles are the future", tier: getTier(35), showing: true }; setAlerts([tip]); setTimeout(() => setAlerts(p => p.filter(a => a.id !== tip.id)), 4500); }, t); t += 5000;
          setTimeout(() => { const tip = { id: Date.now()+Math.random(), from: MOCK_PROFILES[3], amount: 100, message: "FULL SEND!! No middleman!", tier: getTier(100), showing: true }; setAlerts([tip]); setTimeout(() => setAlerts(p => p.filter(a => a.id !== tip.id)), 6000); }, t); t += 6500;
          setTimeout(() => { setSubAlerts([{ id: Date.now()+Math.random(), profile: MOCK_PROFILES[4] }]); setTimeout(() => setSubAlerts([]), 4000); }, t); t += 4500;
          setTimeout(() => setVisibility(prev => ({ ...prev, polls: true })), t); t += 2000;
          setTimeout(() => setVisibility(prev => ({ ...prev, goal: true })), t); t += 2000;
          setTimeout(() => setVisibility(prev => ({ ...prev, qr: true })), t); t += 4000;
          setTimeout(() => { setAlerts([]); setSubAlerts([]); setMicroAlerts([]); setVisibility(prev => ({ ...prev, polls: false, goal: false, qr: false })); }, t);
        }} style={{
          padding: "7px 14px", borderRadius: 7,
          background: `${COLORS.gold}10`, border: `1px solid ${COLORS.gold}20`, color: COLORS.gold,
          fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 600,
          cursor: "pointer", transition: "all 0.2s",
          display: "flex", alignItems: "center", gap: 5,
        }}
        onMouseEnter={e => { e.currentTarget.style.background = `${COLORS.gold}18`; e.currentTarget.style.transform = "translateY(-1px)"; }}
        onMouseLeave={e => { e.currentTarget.style.background = `${COLORS.gold}10`; e.currentTarget.style.transform = "translateY(0)"; }}>
          🎬 Test All
        </button>
        <div style={{ width: 1, height: 20, background: COLORS.border, marginLeft: 4, marginRight: 4 }} />
        {/* Sounds / Grid Layout / Custom Layout buttons — layout pair grouped at bottom */}
        <button onClick={() => setShowSoundsPanel(!showSoundsPanel)} style={{
          padding: "7px 14px", borderRadius: 7,
          background: showSoundsPanel ? `${COLORS.purple}15` : COLORS.surface,
          border: `1px solid ${showSoundsPanel ? COLORS.purple + "30" : COLORS.border}`,
          color: showSoundsPanel ? COLORS.purple : COLORS.textMuted,
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 11, fontWeight: 600,
          cursor: "pointer",
          transition: "all 0.2s",
          display: "flex", alignItems: "center", gap: 5,
        }}>
          🔊 Sounds
        </button>
        <button onClick={() => { setShowSettings(!showSettings); if (!showSettings) setShowCustomLayout(false); }} style={{
          padding: "7px 14px", borderRadius: 7,
          background: showSettings ? `${COLORS.textSecondary}15` : COLORS.surface,
          border: `1px solid ${showSettings ? COLORS.textSecondary + "30" : COLORS.border}`,
          color: showSettings ? COLORS.textSecondary : COLORS.textMuted,
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 11, fontWeight: 600,
          cursor: "pointer",
          transition: "all 0.2s",
          display: "flex", alignItems: "center", gap: 5,
        }}>
          ⚙️ Grid Layout
          <span style={{ fontSize: 7, padding: "1px 5px", borderRadius: 3, background: layoutMode === "grid" ? `${COLORS.accent}20` : `${COLORS.textMuted}10`, border: `1px solid ${layoutMode === "grid" ? COLORS.accent + "40" : COLORS.textMuted + "20"}`, color: layoutMode === "grid" ? COLORS.accent : COLORS.textMuted, fontWeight: 700, letterSpacing: "0.5px" }}>{layoutMode === "grid" ? "LIVE" : "OFF"}</span>
        </button>
        <button onClick={() => { setShowCustomLayout(!showCustomLayout); if (!showCustomLayout) setShowSettings(false); }} style={{
          padding: "7px 14px", borderRadius: 7,
          background: showCustomLayout ? `${COLORS.orange}15` : COLORS.surface,
          border: `1px solid ${showCustomLayout ? COLORS.orange + "30" : COLORS.border}`,
          color: showCustomLayout ? COLORS.orange : COLORS.textMuted,
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 11, fontWeight: 600,
          cursor: "pointer",
          transition: "all 0.2s",
          display: "flex", alignItems: "center", gap: 5,
        }}>
          📐 Custom Layout
          <span style={{ fontSize: 7, padding: "1px 5px", borderRadius: 3, background: layoutMode === "custom" ? `${COLORS.accent}20` : `${COLORS.textMuted}10`, border: `1px solid ${layoutMode === "custom" ? COLORS.accent + "40" : COLORS.textMuted + "20"}`, color: layoutMode === "custom" ? COLORS.accent : COLORS.textMuted, fontWeight: 700, letterSpacing: "0.5px" }}>{layoutMode === "custom" ? "LIVE" : "OFF"}</span>
        </button>
      </div>

      {/* Position Settings Panel */}
      {showSettings && (
        <div style={{
          maxWidth: 920, margin: "0 auto 16px",
          background: COLORS.surface,
          border: `1px solid ${COLORS.border}`,
          borderRadius: 12,
          padding: 16,
          animation: "slideDown 0.25s ease-out",
        }}>
          <div style={{
            fontSize: 10, fontWeight: 700, color: COLORS.textMuted,
            textTransform: "uppercase", letterSpacing: "1.5px", marginBottom: 12,
          }}>Alert Positions</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(90px, 1fr))", gap: 10, marginBottom: 16 }}>
            {[
              { key: "tips", label: "Tip Alerts", icon: "💰", color: COLORS.accent },
              { key: "follows", label: "Follows", icon: "👤", color: COLORS.cyan },
              { key: "micro", label: "Micro Tips", icon: "⚡", color: COLORS.orange },
              { key: "polls", label: "Polls", icon: "📊", color: COLORS.orange },
              { key: "goal", label: "Tip Goal", icon: "🎯", color: COLORS.gold },
              { key: "moc", label: "Moments", icon: "📸", color: COLORS.purple },
              { key: "leaderboard", label: "Leaderboard", icon: "🏆", color: COLORS.cyan },
              { key: "qr", label: "QR Code", icon: "📱", color: COLORS.text },
            ].map(item => (
              <div key={item.key}>
                <div style={{
                  fontSize: 10, fontWeight: 600, color: item.color,
                  marginBottom: 6, display: "flex", alignItems: "center", gap: 4,
                }}>
                  <span style={{ fontSize: 11 }}>{item.icon}</span> {item.label}
                </div>
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr",
                  gridTemplateRows: "1fr 1fr 1fr",
                  gap: 3,
                  background: `${COLORS.bg}`,
                  border: `1px solid ${item.color}25`,
                  borderRadius: 8,
                  padding: 4,
                  aspectRatio: "16/9",
                }}>
                  {[
                    "top-left", "top-center", "top-right",
                    "mid-left", "center", "mid-right",
                    "bottom-left", "bottom-center", "bottom-right",
                  ].map(pos => {
                    const isActive = positions[item.key] === pos;
                    return (
                      <button key={pos} onClick={() => setPositions(prev => ({ ...prev, [item.key]: pos }))} style={{
                        borderRadius: 4,
                        border: `1px solid ${isActive ? item.color + "70" : COLORS.textMuted + "20"}`,
                        background: isActive ? `${item.color}25` : `${COLORS.surface}`,
                        cursor: "pointer",
                        transition: "all 0.15s",
                        position: "relative",
                        minHeight: 0,
                      }}
                      onMouseEnter={e => { if (!isActive) { e.currentTarget.style.background = `${item.color}10`; e.currentTarget.style.borderColor = `${item.color}35`; } }}
                      onMouseLeave={e => { if (!isActive) { e.currentTarget.style.background = COLORS.surface; e.currentTarget.style.borderColor = `${COLORS.textMuted}20`; } }}>
                        {isActive && <div style={{
                          position: "absolute", inset: "22%",
                          borderRadius: 3,
                          background: item.color,
                          boxShadow: `0 0 10px ${item.color}60`,
                        }} />}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <div style={{
            fontSize: 10, fontWeight: 700, color: COLORS.textMuted,
            textTransform: "uppercase", letterSpacing: "1.5px", marginBottom: 12,
          }}>Saved Layouts</div>

          {/* Layout presets */}
          <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
            {savedLayouts.map(layout => (
              <div key={layout.id} style={{ display: "flex", alignItems: "center" }}>
                {editingLayoutName === layout.id ? (
                  <input
                    autoFocus
                    defaultValue={layout.name}
                    onBlur={e => {
                      setSavedLayouts(prev => prev.map(l => l.id === layout.id ? { ...l, name: e.target.value || layout.name } : l));
                      setEditingLayoutName(null);
                    }}
                    onKeyDown={e => { if (e.key === "Enter") e.target.blur(); }}
                    style={{
                      padding: "6px 10px", borderRadius: 6, width: 100,
                      background: COLORS.bgAlt, border: `1px solid ${COLORS.purple}30`,
                      color: COLORS.text, fontSize: 10, fontWeight: 600,
                      fontFamily: "'JetBrains Mono', monospace", outline: "none",
                    }}
                  />
                ) : (
                  <button onClick={() => {
                    setActiveLayout(layout.id);
                    setPositions({ ...layout.positions });
                    setVisibility({ ...layout.visibility });
                    setLayoutMode("grid"); try { localStorage.setItem("fullsend_layout_mode", "grid"); } catch {}
                  }} onDoubleClick={() => setEditingLayoutName(layout.id)} style={{
                    padding: "6px 12px", borderRadius: 6,
                    background: activeLayout === layout.id ? `${COLORS.purple}15` : COLORS.surface,
                    border: `1px solid ${activeLayout === layout.id ? COLORS.purple + "30" : COLORS.border}`,
                    color: activeLayout === layout.id ? COLORS.purple : COLORS.textMuted,
                    fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 600,
                    cursor: "pointer", transition: "all 0.15s",
                  }}>
                    {layout.name}
                  </button>
                )}
              </div>
            ))}

            {/* Save current */}
            <button onClick={() => {
              const existing = savedLayouts.find(l => l.id === activeLayout);
              if (existing) {
                setSavedLayouts(prev => prev.map(l => l.id === activeLayout ? { ...l, positions: { ...positions }, visibility: { ...visibility } } : l));
              }
              setLayoutMode("grid"); try { localStorage.setItem("fullsend_layout_mode", "grid"); } catch {}
              setGridLayoutSaveFlash(true);
              setTimeout(() => setGridLayoutSaveFlash(false), 1500);
            }} style={{
              padding: "6px 10px", borderRadius: 6,
              background: gridLayoutSaveFlash ? `${COLORS.accent}20` : `${COLORS.accent}08`,
              border: `1px solid ${gridLayoutSaveFlash ? COLORS.accent + "40" : COLORS.accent + "15"}`,
              color: COLORS.accent, fontFamily: "'JetBrains Mono', monospace",
              fontSize: 9, fontWeight: 600, cursor: "pointer", transition: "all 0.3s",
            }}>{gridLayoutSaveFlash ? "Saved ✓" : "Save"}</button>

            {/* New layout */}
            {savedLayouts.length < 5 && (
              <button onClick={() => {
                const newId = Date.now();
                setSavedLayouts(prev => [...prev, {
                  id: newId, name: `Layout ${prev.length + 1}`,
                  positions: { ...positions }, visibility: { ...visibility },
                }]);
                setActiveLayout(newId);
              }} style={{
                padding: "6px 10px", borderRadius: 6,
                background: COLORS.surface, border: `1px dashed ${COLORS.textMuted}25`,
                color: COLORS.textMuted, fontFamily: "'JetBrains Mono', monospace",
                fontSize: 9, fontWeight: 600, cursor: "pointer",
              }}>+ New</button>
            )}

            {/* Delete (not for default) */}
            {savedLayouts.length > 1 && activeLayout !== 1 && (
              <button onClick={() => {
                setSavedLayouts(prev => prev.filter(l => l.id !== activeLayout));
                setActiveLayout(1);
                const def = savedLayouts.find(l => l.id === 1);
                if (def) { setPositions({ ...def.positions }); setVisibility({ ...def.visibility }); }
              }} style={{
                padding: "6px 8px", borderRadius: 6,
                background: `${COLORS.red}06`, border: `1px solid ${COLORS.red}12`,
                color: COLORS.red, fontSize: 9, fontWeight: 600, cursor: "pointer",
              }}>Delete</button>
            )}
          </div>

          <div style={{ fontSize: 8, color: COLORS.textMuted, marginBottom: 14 }}>
            Click a layout to load it. Double-click to rename. Up to 5 saved layouts.
          </div>

          {/* Stream background */}
          <div style={{ marginTop: 8, paddingTop: 12, borderTop: `1px solid ${COLORS.border}` }}>
            <div style={{ fontSize: 9, color: COLORS.textMuted, marginBottom: 6 }}>Stream background for preview alignment</div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              {streamBgImage && streamBgImage.startsWith("data:") ? (
                <div style={{ flex: 1, padding: "5px 10px", background: COLORS.bgAlt, border: `1px solid ${COLORS.accent}20`, borderRadius: 6, color: COLORS.accent, fontFamily: "'JetBrains Mono', monospace", fontSize: 9 }}>
                  ✓ Screenshot loaded
                </div>
              ) : (
                <input
                  value={streamBgImage}
                  onChange={e => { setStreamBgImage(e.target.value); localStorage.setItem("fullsend_stream_bg", e.target.value); }}
                  placeholder="Paste image URL or upload..."
                  style={{ flex: 1, padding: "5px 10px", background: COLORS.bgAlt, border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.text, fontFamily: "'JetBrains Mono', monospace", fontSize: 9, outline: "none" }}
                />
              )}
              <label style={{ padding: "5px 10px", borderRadius: 6, background: `${COLORS.cyan}10`, border: `1px solid ${COLORS.cyan}20`, color: COLORS.cyan, fontSize: 9, fontWeight: 600, cursor: "pointer", fontFamily: "'JetBrains Mono', monospace", whiteSpace: "nowrap" }}>
                📁 Upload
                <input type="file" accept="image/*" style={{ display: "none" }} onChange={e => {
                  const file = e.target.files?.[0];
                  if (file) { const reader = new FileReader(); reader.onload = () => { setStreamBgImage(reader.result); localStorage.setItem("fullsend_stream_bg", reader.result); }; reader.readAsDataURL(file); }
                }} />
              </label>
              {streamBgImage && (
                <button onClick={() => { setStreamBgImage(""); localStorage.removeItem("fullsend_stream_bg"); }} style={{ padding: "6px 10px", borderRadius: 6, background: `${COLORS.red}10`, border: `1px solid ${COLORS.red}20`, color: COLORS.red, fontSize: 9, cursor: "pointer" }}>✕</button>
              )}
              <div id="export-png-layout" style={{ padding: "5px 10px", borderRadius: 6, background: `${COLORS.purple}10`, border: `1px solid ${COLORS.purple}20`, color: COLORS.purple, fontSize: 9, fontWeight: 600, cursor: "pointer", fontFamily: "'JetBrains Mono', monospace", whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 4 }}>
                📥 Export PNG
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom Layout Panel */}
      {showCustomLayout && (
        <div style={{
          maxWidth: 920, margin: "0 auto 16px",
          background: COLORS.surface,
          border: `1px solid ${COLORS.orange}20`,
          borderRadius: 12,
          padding: 16,
        }}>
          <div style={{
            fontSize: 11, fontWeight: 700, color: COLORS.orange,
            textTransform: "uppercase", letterSpacing: "1.5px", marginBottom: 12,
          }}>Custom Overlay Layout</div>

          {/* Background image import */}
          <div style={{ fontSize: 9, color: COLORS.textMuted, marginBottom: 6 }}>Upload a screenshot of your stream for alignment (any resolution)</div>
          <div style={{ display: "flex", gap: 8, marginBottom: 10, alignItems: "center" }}>
            {streamBgImage && streamBgImage.startsWith("data:") ? (
              <div style={{
                flex: 1, padding: "6px 10px", background: COLORS.bgAlt, border: `1px solid ${COLORS.accent}20`,
                borderRadius: 6, color: COLORS.accent, fontFamily: "'JetBrains Mono', monospace", fontSize: 9,
              }}>
                ✓ Screenshot loaded
              </div>
            ) : (
              <input
                value={streamBgImage}
                onChange={e => { setStreamBgImage(e.target.value); localStorage.setItem("fullsend_stream_bg", e.target.value); }}
                placeholder="Paste image URL or upload a screenshot..."
                style={{
                  flex: 1, padding: "6px 10px", background: COLORS.bgAlt, border: `1px solid ${COLORS.border}`,
                  borderRadius: 6, color: COLORS.text, fontFamily: "'JetBrains Mono', monospace", fontSize: 9, outline: "none",
                }}
              />
            )}
            <label style={{
              padding: "6px 10px", borderRadius: 6, background: `${COLORS.cyan}10`, border: `1px solid ${COLORS.cyan}20`,
              color: COLORS.cyan, fontSize: 9, fontWeight: 600, cursor: "pointer", fontFamily: "'JetBrains Mono', monospace",
              whiteSpace: "nowrap",
            }}>
              📁 Upload
              <input type="file" accept="image/*" style={{ display: "none" }} onChange={e => {
                const file = e.target.files?.[0];
                if (file) {
                  const reader = new FileReader();
                  reader.onload = () => { setStreamBgImage(reader.result); localStorage.setItem("fullsend_stream_bg", reader.result); };
                  reader.readAsDataURL(file);
                }
              }} />
            </label>
            {streamBgImage && (
              <button onClick={() => { setStreamBgImage(""); localStorage.removeItem("fullsend_stream_bg"); }} style={{
                padding: "6px 8px", borderRadius: 6, background: `${COLORS.red}10`, border: `1px solid ${COLORS.red}20`,
                color: COLORS.red, fontSize: 9, cursor: "pointer",
              }}>✕</button>
            )}
            <div id="export-png-custom" style={{ padding: "6px 10px", borderRadius: 6, background: `${COLORS.purple}10`, border: `1px solid ${COLORS.purple}20`, color: COLORS.purple, fontSize: 9, fontWeight: 600, cursor: "pointer", fontFamily: "'JetBrains Mono', monospace", whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 4 }}>
              📥 Export PNG
            </div>
          </div>

          {/* Drag canvas with demo elements */}
          {(() => {
            // Clamp positions to prevent elements from going off-screen
            // Element sizes as % of canvas (width%, height%)
            const elPct = {
              tips: { w: 40, h: 20 }, whale: { w: 55, h: 22 }, follows: { w: 22, h: 8 }, micro: { w: 28, h: 8 },
              polls: { w: 32, h: 28 }, goal: { w: 42, h: 8 }, chat: { w: 38, h: 100 },
              qr: { w: 8, h: 16 }, leaderboard: { w: 24, h: 16 },
            };
            const clampPos = (key, pos) => {
              const s = elPct[key] || { w: 20, h: 10 };
              if (key === "chat") return { x: Math.max(0, Math.min(100 - s.w, pos.x)), y: 0 };
              return { x: Math.max(0, Math.min(100 - s.w, pos.x)), y: Math.max(0, Math.min(100 - s.h, pos.y)) };
            };
            const getPos = (key, fallback) => {
              const raw = customPositions[key] || fallback;
              return clampPos(key, raw);
            };
            return (
          <div
            id="fullsend-drag-canvas"
            ref={canvasRef}
            style={{
              position: "relative", width: "calc(100% + 32px)", marginLeft: -16, marginRight: -16,
              aspectRatio: "16/9",
              background: COLORS.bg,
              borderTop: `1px solid ${COLORS.border}`, borderBottom: `1px solid ${COLORS.border}`,
              overflow: "hidden", cursor: draggingElement ? "grabbing" : "default",
            }}
            onMouseMove={e => {
              if (!draggingElement || !canvasRef.current) return;
              const rect = canvasRef.current.getBoundingClientRect();
              // Estimate element half-size as % of canvas to prevent overflow
              const elPctDrag = {
                tips: { w: 40, h: 20 }, whale: { w: 55, h: 22 }, follows: { w: 22, h: 8 }, micro: { w: 28, h: 8 },
                polls: { w: 32, h: 28 }, goal: { w: 42, h: 8 }, chat: { w: 38, h: 100 },
                qr: { w: 8, h: 16 }, leaderboard: { w: 24, h: 16 },
              };
              const sz = elPctDrag[draggingElement] || { w: 20, h: 10 };
              const x = Math.max(0, Math.min(100 - sz.w, ((e.clientX - rect.left) / rect.width) * 100));
              const y = draggingElement === "chat" ? 0 : Math.max(0, Math.min(100 - sz.h, ((e.clientY - rect.top) / rect.height) * 100));
              setCustomPositions(prev => ({ ...prev, [draggingElement]: { x, y } }));
            }}
            onMouseUp={() => {
              if (draggingElement) {
                setDraggingElement(null);
                setLayoutMode("custom"); try { localStorage.setItem("fullsend_layout_mode", "custom"); } catch {}
                try { localStorage.setItem("fullsend_custom_positions", JSON.stringify(customPositions)); } catch {}
                window.__fullsend_overlay_state = {
                  ...(window.__fullsend_overlay_state || {}),
                  customPositions, layoutMode: "custom",
                };
                syncCreatorToDB({ _overlayState: window.__fullsend_overlay_state });
              }
            }}
            onMouseLeave={() => setDraggingElement(null)}
          >
            {/* Uploaded background image */}
            {streamBgImage && <img data-overlay-bg src={streamBgImage} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain", objectPosition: "center", pointerEvents: "none" }} />}
            {streamBgImage && <div data-overlay-bg style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.15)", pointerEvents: "none" }} />}

            {/* Grid reference lines */}
            {!streamBgImage && (
              <div data-overlay-bg>
                <div style={{ position: "absolute", left: "33.3%", top: 0, bottom: 0, width: 1, background: `${COLORS.border}30` }} />
                <div style={{ position: "absolute", left: "66.6%", top: 0, bottom: 0, width: 1, background: `${COLORS.border}30` }} />
                <div style={{ position: "absolute", top: "33.3%", left: 0, right: 0, height: 1, background: `${COLORS.border}30` }} />
                <div style={{ position: "absolute", top: "66.6%", left: 0, right: 0, height: 1, background: `${COLORS.border}30` }} />
              </div>
            )}

            {/* Demo tip alert */}
            {customVis.tips && (() => {
              const tipPos = getPos("tips", { x: 55, y: 65 });
              const tier = { color: COLORS.blue, emoji: "💪", label: "CHAMPION", duration: 5000 };
              const demoAlert = { amount: "15", from: { name: "MKTiaraGirl", avatar: DEMO_PFP_CHAMPION }, message: "LUKSO is the future!", tier };
              const isWhale = tier.label === "WHALE";
              return (
                <div
                  onMouseDown={e => { e.preventDefault(); setDraggingElement("tips"); }}
                  style={{
                    position: "absolute", left: `${tipPos.x}%`, top: `${tipPos.y}%`,
                    cursor: "grab", userSelect: "none",
                    zIndex: draggingElement === "tips" ? 65 : 60,
                    maxWidth: isWhale ? 480 : 360, width: isWhale ? "75%" : "auto",
                  }}
                >
                  <div style={{
                    background: isWhale ? `${COLORS.bg}f0` : `${COLORS.bg}e5`,
                    backdropFilter: "blur(24px) saturate(1.3)",
                    borderRadius: isWhale ? 18 : 12,
                    border: `1.5px solid ${tier.color}40`,
                    padding: isWhale ? "22px 26px" : "14px 18px",
                    position: "relative", overflow: "hidden",
                  }}>
                    <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, transparent, ${tier.color}, transparent)`, opacity: 0.6 }} />
                    <div style={{ display: "flex", alignItems: "center", gap: 12, position: "relative" }}>
                      <div style={{ position: "relative", flexShrink: 0 }}>
                        <div style={{
                          width: isWhale ? 56 : 40, height: isWhale ? 56 : 40,
                          borderRadius: isWhale ? 14 : 10,
                          background: `${tier.color}18`, border: `2px solid ${tier.color}40`,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          boxShadow: isWhale ? `0 0 20px ${tier.color}25` : `0 0 10px ${tier.color}15`,
                          overflow: "hidden",
                        }}>
                          <img src={demoAlert.from.avatar} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: isWhale ? 12 : 8 }} />
                        </div>
                        <div style={{ position: "absolute", bottom: -4, left: -4, zIndex: 2, fontSize: isWhale ? 14 : 11, background: COLORS.bg, borderRadius: "50%", width: isWhale ? 22 : 18, height: isWhale ? 22 : 18, display: "flex", alignItems: "center", justifyContent: "center", border: `1.5px solid ${tier.color}50` }}>{tier.emoji}</div>
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: "flex", alignItems: "baseline", gap: 7, flexWrap: "wrap" }}>
                          <span style={{ fontFamily: "'Syne', sans-serif", fontSize: isWhale ? 26 : 17, fontWeight: 800, color: tier.color, letterSpacing: "-0.5px" }}>{demoAlert.amount} LYX</span>
                          <span style={{ fontSize: isWhale ? 12 : 11, color: COLORS.textMuted }}>from</span>
                          <span style={{ fontWeight: 700, fontSize: isWhale ? 14 : 12, color: COLORS.text }}>{demoAlert.from.name}</span>
                        </div>
                        <div style={{ fontSize: isWhale ? 14 : 12, color: COLORS.textSecondary, marginTop: 5, lineHeight: 1.5 }}>"{demoAlert.message}"</div>
                      </div>
                    </div>
                    <div style={{ marginTop: 10, height: 2, background: `${tier.color}12`, borderRadius: 1, overflow: "hidden" }}>
                      <div style={{ height: "100%", width: "65%", background: `linear-gradient(90deg, ${tier.color}, ${tier.color}80)`, borderRadius: 1, boxShadow: `0 0 6px ${tier.color}40` }} />
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Demo follow alert */}
            {customVis.follows && (() => {
              const followPos = getPos("follows", { x: 5, y: 5 });
              return (
                <div
                  onMouseDown={e => { e.preventDefault(); setDraggingElement("follows"); }}
                  style={{
                    position: "absolute", left: `${followPos.x}%`, top: `${followPos.y}%`,
                    cursor: "grab", userSelect: "none",
                    zIndex: draggingElement === "follows" ? 65 : 50,
                  }}
                >
                  <div style={{
                    background: `${COLORS.bg}dd`,
                    backdropFilter: "blur(16px)",
                    borderRadius: 10,
                    border: `1px solid ${COLORS.cyan}30`,
                    padding: "8px 14px",
                    display: "flex", alignItems: "center", gap: 8,
                    boxShadow: `0 0 16px ${COLORS.cyan}10`,
                    whiteSpace: "nowrap",
                  }}>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.text }}>
                        <span style={{ color: COLORS.cyan }}>@ChainWizard</span>{" "}
                        <span style={{ color: COLORS.textSecondary, fontWeight: 500 }}>followed</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Demo micro tip */}
            {customVis.micro && (() => {
              const microPos = getPos("micro", { x: 38, y: 88 });
              return (
                <div
                  onMouseDown={e => { e.preventDefault(); setDraggingElement("micro"); }}
                  style={{
                    position: "absolute", left: `${microPos.x}%`, top: `${microPos.y}%`,
                    cursor: "grab", userSelect: "none",
                    zIndex: draggingElement === "micro" ? 65 : 60,
                  }}
                >
                  <div style={{
                    background: `${COLORS.bg}cc`,
                    backdropFilter: "blur(12px)",
                    borderRadius: 8,
                    border: `1px solid ${COLORS.textMuted}20`,
                    padding: "6px 12px",
                    display: "flex", alignItems: "center", gap: 7,
                    whiteSpace: "nowrap",
                  }}>
                    <span style={{ fontSize: 12, width: 20, height: 20, borderRadius: 5, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", flexShrink: 0, background: `${COLORS.textMuted}15` }}>
                      ⚡
                    </span>
                    <span style={{ fontSize: 11, fontWeight: 600, color: COLORS.textSecondary }}>@ChainWizard</span>
                    <span style={{ fontSize: 10, color: COLORS.textMuted }}>tipped</span>
                    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700, color: COLORS.text }}>0.5 LYX</span>
                  </div>
                </div>
              );
            })()}

            {/* Demo poll */}
            {customVis.polls && (() => {
              const pollPos = getPos("polls", { x: 65, y: 10 });
              const demoPoll = {
                id: "demo", question: "What should we build next?",
                options: [
                  { text: "NFT Rewards", votes: 42, color: COLORS.accent },
                  { text: "Multi-Token", votes: 31, color: COLORS.blue },
                  { text: "Raid Alerts", votes: 18, color: COLORS.purple },
                ],
                totalVotes: 91, status: "active",
              };
              return (
                <div
                  onMouseDown={e => { e.preventDefault(); setDraggingElement("polls"); }}
                  style={{
                    position: "absolute", left: `${pollPos.x}%`, top: `${pollPos.y}%`,
                    cursor: "grab", userSelect: "none",
                    zIndex: draggingElement === "polls" ? 65 : 45,
                    width: "32%", minWidth: 200,
                  }}
                >
                  <PollOverlay poll={demoPoll} posKey="custom" static />
                </div>
              );
            })()}

            {/* Demo goal */}
            {customVis.goal && (() => {
              const goalPos = getPos("goal", { x: 30, y: 88 });
              const title = "DAO Marketing Fund";
              const target = 420;
              const current = 187.5;
              const pct = Math.min((current / target) * 100, 100);
              return (
                <div
                  onMouseDown={e => { e.preventDefault(); setDraggingElement("goal"); }}
                  style={{
                    position: "absolute", left: `${goalPos.x}%`, top: `${goalPos.y}%`,
                    cursor: "grab", userSelect: "none",
                    zIndex: draggingElement === "goal" ? 65 : 40,
                    width: "40%", minWidth: 200,
                  }}
                >
                  <div style={{
                    background: `${COLORS.bg}d0`,
                    backdropFilter: "blur(12px)",
                    borderRadius: 8,
                    padding: "6px 10px",
                    border: `1px solid ${COLORS.accent}15`,
                  }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                      <span style={{ fontSize: 8, fontWeight: 600, color: COLORS.text }}>🎯 {title}</span>
                      <span style={{ fontSize: 8, fontWeight: 700, color: COLORS.accent, fontFamily: "'JetBrains Mono', monospace" }}>{current.toFixed(1)} / {target} LYX</span>
                    </div>
                    <div style={{ height: 4, borderRadius: 2, background: `${COLORS.bg}80`, overflow: "hidden" }}>
                      <div style={{ height: "100%", width: `${pct}%`, borderRadius: 2, background: `linear-gradient(90deg, ${COLORS.accent}, ${COLORS.cyan})`, boxShadow: `0 0 6px ${COLORS.accent}30` }} />
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Demo claim leaderboard */}
            {customVis.leaderboard && (() => {
              const lbPos = getPos("leaderboard", { x: 74, y: 6 });
              const demo = [
                { name: "Thalos", total: 30, avatar: DEMO_PFP_LEGEND },
                { name: "Arion", total: 25, avatar: DEMO_PFP_CHAMPION },
                { name: "ChainWizard", total: 20, avatar: DEMO_PFP_SUPPORTER },
                { name: "LUKSOgang", total: 15, avatar: DEMO_PFP_WHALE },
                { name: "web3builder", total: 10, avatar: STOCK_AVATAR },
              ];
              return (
                <div
                  onMouseDown={e => { e.preventDefault(); setDraggingElement("leaderboard"); }}
                  style={{
                    position: "absolute", left: `${lbPos.x}%`, top: `${lbPos.y}%`,
                    cursor: "grab", userSelect: "none",
                    zIndex: draggingElement === "leaderboard" ? 65 : 44,
                    width: "24%", minWidth: 150,
                  }}
                >
                  <div style={{ background: `${COLORS.bg}d0`, backdropFilter: "blur(12px)", borderRadius: 8, border: `1px solid ${COLORS.cyan}30`, overflow: "hidden" }}>
                    <div style={{ padding: "4px 8px", borderBottom: `1px solid ${COLORS.border}`, fontSize: 7, fontWeight: 800, color: COLORS.cyan, fontFamily: "'Syne', sans-serif", letterSpacing: "0.5px" }}>🏆 LOYALTY TOKEN LEADERBOARD</div>
                    <div style={{ padding: "4px 6px", display: "flex", flexDirection: "column", gap: 2 }}>
                      {demo.map((d, i) => (
                        <div key={i} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 7 }}>
                          <span style={{ fontWeight: 800, color: i < 3 ? COLORS.cyan : COLORS.textMuted, minWidth: 12 }}>{i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `#${i + 1}`}</span>
                          <img src={d.avatar} alt="" style={{ width: 10, height: 10, borderRadius: "50%", objectFit: "cover" }} />
                          <span style={{ flex: 1, fontWeight: 700, color: COLORS.text }}>{d.name}</span>
                          <span style={{ fontWeight: 800, color: COLORS.cyan, fontFamily: "'JetBrains Mono', monospace" }}>{d.total}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Demo chat — disabled until V2 chat ingestion ships (was confusing on the custom layout) */}
            {false && customVis.chat && (() => {
              const chatPos = getPos("chat", { x: 2, y: 0 });
              const demoMessages = [
                { name: "ChainWizard", msg: "this stream is fire 🔥", color: "#3ea6ff" },
                { name: "LUKSOgang", msg: "UP's are underrated", color: "#3ea6ff" },
                { name: "web3builder", msg: "LUKSO is the move", color: "#3ea6ff" },
                { name: "chillvibes", msg: "gm everyone", color: "#3ea6ff" },
                { name: "cryptofan99", msg: "W stream", color: "#3ea6ff" },
                { name: "Dracos", msg: "full send baby!!", color: "#3ea6ff" },
                { name: "MKTiaraGirl", msg: "love this community", color: "#3ea6ff" },
                { name: "Arion", msg: "LFG 🚀", color: "#3ea6ff" },
                { name: "Thalos", msg: "LUKSO will flip ETH", color: "#3ea6ff" },
                { name: "NFTmaxi", msg: "when mint?", color: "#3ea6ff" },
                { name: "PoolNoodle", msg: "based stream", color: "#3ea6ff" },
                { name: "AltsAnon", msg: "UP > wallet", color: "#3ea6ff" },
                { name: "ChainWizard", msg: "banger content fr", color: "#3ea6ff" },
                { name: "LUKSOgang", msg: "who else is here daily?", color: "#3ea6ff" },
                { name: "web3builder", msg: "need more builders", color: "#3ea6ff" },
              ];
              const totalMsgs = demoMessages.length;
              return (
                <div
                  onMouseDown={e => { e.preventDefault(); setDraggingElement("chat"); }}
                  style={{
                    position: "absolute", left: `${chatPos.x}%`, top: 0, bottom: 0,
                    cursor: "grab", userSelect: "none",
                    zIndex: draggingElement === "chat" ? 65 : 35,
                    width: "38%",
                    pointerEvents: "auto",
                  }}
                >
                  {/* Fade gradient at top — matches ChatOverlay */}
                  <div style={{
                    position: "absolute", top: 0, left: 0, right: 0, height: 60,
                    background: `linear-gradient(180deg, ${COLORS.bg}d0 0%, transparent 100%)`,
                    zIndex: 1,
                  }} />
                  <div style={{
                    padding: "30px 12px 10px",
                    overflowY: "hidden",
                    maxHeight: "100%",
                    display: "flex", flexDirection: "column", justifyContent: "flex-end",
                    height: "100%",
                  }}>
                    {demoMessages.map((m, idx) => {
                      const isRecent = idx >= totalMsgs - 2;
                      return (
                        <div key={idx} style={{
                          padding: "2px 0",
                          opacity: isRecent ? 0.95 : 0.5 + (idx / totalMsgs) * 0.4,
                        }}>
                          <span style={{
                            fontSize: 11,
                            fontFamily: "Arial, sans-serif",
                            textShadow: "0 1px 3px rgba(0,0,0,0.8), 0 0 8px rgba(0,0,0,0.5)",
                          }}>
                            <span style={{ color: m.color, fontWeight: 700 }}>{m.name}</span>{" "}
                            <span style={{ color: "#ddd" }}>{m.msg}</span>
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* Demo QR code */}
            {customVis.qr && (() => {
              const qrPos = getPos("qr", { x: 2, y: 75 });
              return (
                <div
                  onMouseDown={e => { e.preventDefault(); setDraggingElement("qr"); }}
                  style={{
                    position: "absolute", left: `${qrPos.x}%`, top: `${qrPos.y}%`,
                    background: "rgba(255,255,255,0.95)", borderRadius: 8, padding: 5,
                    cursor: "grab", userSelect: "none",
                    zIndex: draggingElement === "qr" ? 65 : 55,
                    boxShadow: "0 0 10px rgba(0,0,0,0.3)",
                  }}
                >
                  <img src={DEMO_QR_CODE} alt="QR" style={{ width: 50, height: 50, borderRadius: 4 }} />
                  <div style={{ textAlign: "center", fontSize: 6, fontWeight: 700, color: "#06060b", marginTop: 2, fontFamily: "'JetBrains Mono', monospace" }}>Scan to Tip</div>
                </div>
              );
            })()}
          </div>
          );
          })()}

          <div style={{ fontSize: 8, color: COLORS.textMuted, marginTop: 8 }}>
            Drag elements to position them exactly.
          </div>

          {/* Saved Custom Layouts */}
          <div style={{
            fontSize: 10, fontWeight: 700, color: COLORS.textMuted,
            textTransform: "uppercase", letterSpacing: "1.5px", marginTop: 16, marginBottom: 12,
          }}>Saved Layouts</div>

          <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
            {savedCustomLayouts.map(layout => (
              <div key={layout.id} style={{ display: "flex", alignItems: "center" }}>
                {editingCustomLayoutName === layout.id ? (
                  <input
                    autoFocus
                    defaultValue={layout.name}
                    onBlur={e => {
                      setSavedCustomLayouts(prev => {
                        const updated = prev.map(l => l.id === layout.id ? { ...l, name: e.target.value || layout.name } : l);
                        try { localStorage.setItem("fullsend_saved_custom_layouts", JSON.stringify(updated)); } catch {}
                        return updated;
                      });
                      setEditingCustomLayoutName(null);
                    }}
                    onKeyDown={e => { if (e.key === "Enter") e.target.blur(); }}
                    style={{
                      padding: "6px 10px", borderRadius: 6, width: 100,
                      background: COLORS.bgAlt, border: `1px solid ${COLORS.orange}30`,
                      color: COLORS.text, fontSize: 10, fontWeight: 600,
                      fontFamily: "'JetBrains Mono', monospace", outline: "none",
                    }}
                  />
                ) : (
                  <button onClick={() => {
                    setActiveCustomLayout(layout.id);
                    setCustomPositions({ ...layout.customPositions });
                    setCustomVis({ ...layout.customVis });
                    setLayoutMode("custom"); try { localStorage.setItem("fullsend_layout_mode", "custom"); } catch {}
                    try {
                      localStorage.setItem("fullsend_custom_positions", JSON.stringify(layout.customPositions));
                      localStorage.setItem("fullsend_active_custom_layout", String(layout.id));
                    } catch {}
                    window.__fullsend_overlay_state = {
                      ...(window.__fullsend_overlay_state || {}),
                      customPositions: layout.customPositions, layoutMode: "custom",
                    };
                    syncCreatorToDB({ _overlayState: window.__fullsend_overlay_state });
                  }} onDoubleClick={() => setEditingCustomLayoutName(layout.id)} style={{
                    padding: "6px 12px", borderRadius: 6,
                    background: activeCustomLayout === layout.id ? `${COLORS.orange}15` : COLORS.surface,
                    border: `1px solid ${activeCustomLayout === layout.id ? COLORS.orange + "30" : COLORS.border}`,
                    color: activeCustomLayout === layout.id ? COLORS.orange : COLORS.textMuted,
                    fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 600,
                    cursor: "pointer", transition: "all 0.15s",
                  }}>
                    {layout.name}
                  </button>
                )}
              </div>
            ))}

            {/* Save current */}
            <button onClick={() => {
              setSavedCustomLayouts(prev => {
                const updated = prev.map(l => l.id === activeCustomLayout ? { ...l, customPositions: { ...customPositions }, customVis: { ...customVis } } : l);
                try { localStorage.setItem("fullsend_saved_custom_layouts", JSON.stringify(updated)); } catch {}
                return updated;
              });
              setLayoutMode("custom"); try { localStorage.setItem("fullsend_layout_mode", "custom"); } catch {}
              setCustomLayoutSaveFlash(true);
              setTimeout(() => setCustomLayoutSaveFlash(false), 1500);
            }} style={{
              padding: "6px 10px", borderRadius: 6,
              background: customLayoutSaveFlash ? `${COLORS.accent}20` : `${COLORS.accent}08`,
              border: `1px solid ${customLayoutSaveFlash ? COLORS.accent + "40" : COLORS.accent + "15"}`,
              color: COLORS.accent, fontFamily: "'JetBrains Mono', monospace",
              fontSize: 9, fontWeight: 600, cursor: "pointer", transition: "all 0.3s",
            }}>{customLayoutSaveFlash ? "Saved ✓" : "Save"}</button>

            {/* New layout */}
            {savedCustomLayouts.length < 5 && (
              <button onClick={() => {
                const newId = Date.now();
                setSavedCustomLayouts(prev => {
                  const updated = [...prev, {
                    id: newId, name: `Layout ${prev.length + 1}`,
                    customPositions: { ...customPositions }, customVis: { ...customVis },
                  }];
                  try { localStorage.setItem("fullsend_saved_custom_layouts", JSON.stringify(updated)); } catch {}
                  return updated;
                });
                setActiveCustomLayout(newId);
                try { localStorage.setItem("fullsend_active_custom_layout", String(newId)); } catch {}
              }} style={{
                padding: "6px 10px", borderRadius: 6,
                background: COLORS.surface, border: `1px dashed ${COLORS.textMuted}25`,
                color: COLORS.textMuted, fontFamily: "'JetBrains Mono', monospace",
                fontSize: 9, fontWeight: 600, cursor: "pointer",
              }}>+ New</button>
            )}

            {/* Delete (not for default) */}
            {savedCustomLayouts.length > 1 && activeCustomLayout !== 1 && (
              <button onClick={() => {
                setSavedCustomLayouts(prev => {
                  const updated = prev.filter(l => l.id !== activeCustomLayout);
                  try { localStorage.setItem("fullsend_saved_custom_layouts", JSON.stringify(updated)); } catch {}
                  return updated;
                });
                setActiveCustomLayout(1);
                try { localStorage.setItem("fullsend_active_custom_layout", "1"); } catch {}
                const def = savedCustomLayouts.find(l => l.id === 1);
                if (def) {
                  setCustomPositions({ ...def.customPositions });
                  setCustomVis({ ...def.customVis });
                  try { localStorage.setItem("fullsend_custom_positions", JSON.stringify(def.customPositions)); } catch {}
                  window.__fullsend_overlay_state = {
                    ...(window.__fullsend_overlay_state || {}),
                    customPositions: def.customPositions,
                  };
                  syncCreatorToDB({ _overlayState: window.__fullsend_overlay_state });
                }
              }} style={{
                padding: "6px 8px", borderRadius: 6,
                background: `${COLORS.red}06`, border: `1px solid ${COLORS.red}12`,
                color: COLORS.red, fontSize: 9, fontWeight: 600, cursor: "pointer",
              }}>Delete</button>
            )}
          </div>

          <div style={{ fontSize: 8, color: COLORS.textMuted }}>
            Click a layout to load it. Double-click to rename. Up to 5 saved layouts.
          </div>
        </div>
      )}

      {showSoundsPanel && (() => {
        const tts = overlayTts;
        const vol = overlayVolume;
        const st = overlaySoundToggles;
        return (
        <div style={{
          maxWidth: 720, margin: "0 auto 16px",
          background: COLORS.surface,
          border: `1px solid ${COLORS.purple}20`,
          borderRadius: 12,
          padding: 16,
          animation: "fadeInUp 0.3s ease-out",
        }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.purple, marginBottom: 14, fontFamily: "'Syne', sans-serif" }}>
            🔊 Sound & TTS Controls
          </div>

          {/* Volume slider */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: COLORS.text }}>Master Volume</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: COLORS.accent, fontFamily: "'JetBrains Mono', monospace" }}>{Math.round(vol * 100)}%</span>
            </div>
            <input type="range" min="0" max="1" step="0.05" value={vol} onChange={e => {
              const v = parseFloat(e.target.value);
              window.__fullsend_volume = v;
              setOverlayVolume(v);
            }} style={{ width: "100%", accentColor: COLORS.accent }} />
          </div>

          {/* Sound preview per tier */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "1.5px" }}>
              Preview Alert Sounds
            </div>
            <button onClick={() => {
              window.__fullsend_settings_tab = "sounds";
              window.dispatchEvent(new CustomEvent("fullsend-navigate", { detail: "settings" }));
            }} style={{
              background: "none", border: "none", color: COLORS.accent, fontSize: 9, fontWeight: 600,
              cursor: "pointer", fontFamily: "'JetBrains Mono', monospace",
              display: "flex", alignItems: "center", gap: 4,
            }}>
              ⚙️ Customize in Settings → Sounds
            </button>
          </div>
          <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap" }}>
            {[
              { key: "micro", label: "Micro", color: COLORS.orange, defaultSound: "pop" },
              { key: "supporter", label: "Supporter", color: COLORS.accent, defaultSound: "coin-drop" },
              { key: "champion", label: "Champion", color: COLORS.blue, defaultSound: "cash-register" },
              { key: "legend", label: "Legend", color: COLORS.purple, defaultSound: "level-up" },
              { key: "whale", label: "WHALE", color: COLORS.gold, defaultSound: "fanfare" },
              { key: "follow", label: "Follow", color: COLORS.cyan, defaultSound: "notification" },
              { key: "poll-created", label: "Poll Start", color: COLORS.orange, defaultSound: "poll-created" },
              { key: "poll-vote", label: "Poll Vote", color: COLORS.orange, defaultSound: "poll-vote" },
              { key: "poll-ended", label: "Poll End", color: COLORS.orange, defaultSound: "poll-ended" },
            ].map(t => {
              const sounds = window.__fullsend_selected_sounds || {};
              const soundName = sounds[t.key] || t.defaultSound;
              return (
              <button key={t.key} onClick={() => {
                if (soundName === "none") return;
                playTierSound(t.key);
                // Fallback: if no custom sound assigned, play the default
                if (!sounds[t.key] || sounds[t.key] === "none") {
                  playFullSendSound(t.defaultSound);
                }
              }} style={{
                padding: "6px 12px", borderRadius: 6,
                background: `${t.color}10`, border: `1px solid ${t.color}20`,
                color: t.color, fontFamily: "'JetBrains Mono', monospace",
                fontSize: 10, fontWeight: 600, cursor: soundName === "none" ? "default" : "pointer",
                opacity: soundName === "none" ? 0.4 : 1,
              }}>🔊 {t.label} {soundName !== "none" && <span style={{ fontSize: 8, color: COLORS.textMuted }}>({soundName})</span>}</button>
              );
            })}
          </div>

          {/* TTS test */}
          <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "1.5px", marginBottom: 8 }}>
            Text-to-Speech
          </div>
          <div style={{
            padding: 12, borderRadius: 8,
            background: COLORS.bgAlt,
            border: `1px solid ${COLORS.border}`,
          }}>
            <button onClick={() => {
              if (!window.speechSynthesis) return;
              window.speechSynthesis.cancel();
              const utterance = new SpeechSynthesisUtterance("Dracos tipped 25 LYX: LUKSO is the future!");
              utterance.volume = window.__fullsend_volume || 0.7;
              const voices = window.speechSynthesis.getVoices();
              const voice = voices.find(v => v.name === tts.voice);
              if (voice) utterance.voice = voice;
              utterance.rate = tts.rate || 1;
              utterance.pitch = tts.pitch || 1;
              window.speechSynthesis.speak(utterance);
            }} style={{
              width: "100%", padding: "8px", borderRadius: 6,
              background: `${COLORS.accent}08`, border: `1px solid ${COLORS.accent}15`,
              color: COLORS.accent, fontFamily: "'JetBrains Mono', monospace",
              fontSize: 10, fontWeight: 600, cursor: "pointer",
            }}>
              🔊 Test TTS Voice
            </button>
            <div style={{ fontSize: 9, color: COLORS.textMuted, marginTop: 6 }}>
              TTS toggle on Control page · Full config in Settings → Sounds
            </div>
          </div>
        </div>
        );
      })()}

      {/* Quick test row — visible when Layout or Custom Layout is open */}
      {(showSettings || showCustomLayout) && (
        <div style={{ maxWidth: 920, margin: "10px auto 8px", display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
          <span style={{ fontSize: 9, color: COLORS.textMuted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "1px" }}>Quick test:</span>
          {[
            { label: "⚡ Micro", handler: () => triggerDemoMicro() },
            { label: "💚 Supporter", handler: () => triggerDemo("green") },
            { label: "💪 Champion", handler: () => triggerDemo("blue") },
            { label: "💎 Legend", handler: () => triggerDemo("purple") },
            { label: "🐋 WHALE", handler: () => triggerDemo("fire") },
            { label: "👤 Follow", handler: () => triggerDemoSub() },
          ].map((btn, i) => (
            <button key={i} onClick={btn.handler} style={{
              padding: "4px 10px", borderRadius: 5, background: COLORS.surface, border: `1px solid ${COLORS.border}`,
              color: COLORS.textSecondary, fontSize: 9, fontWeight: 600, cursor: "pointer",
              fontFamily: "'JetBrains Mono', monospace",
            }}>{btn.label}</button>
          ))}
          {[
            { key: "polls", label: "📊 Poll", color: COLORS.orange },
            { key: "goal", label: "🎯 Goal", color: COLORS.gold },
            { key: "qr", label: "📱 QR", color: COLORS.text },
          ].map(item => (
            <button key={item.key} onClick={() => {
              if (item.key === "chat") {
                const newVal = !visibility.chat;
                setVisibility(prev => ({ ...prev, chat: newVal }));
                if (newVal) {
                  setShowChat(true);
                  if (chatMessages.length === 0) {
                    setChatMessages([
                      { id: 1, name: "ChainWizard", text: "this stream is fire 🔥", color: "#3ea6ff" },
                      { id: 2, name: "LUKSOgang", text: "UP's are underrated", color: "#3ea6ff" },
                      { id: 3, name: "web3builder", text: "LUKSO is the move", color: "#3ea6ff" },
                      { id: 4, name: "chillvibes", text: "gm everyone", color: "#3ea6ff" },
                      { id: 5, name: "cryptofan99", text: "W stream", color: "#3ea6ff" },
                      { id: 6, name: "Dracos", text: "full send baby!!", color: "#3ea6ff" },
                      { id: 7, name: "MKTiaraGirl", text: "love this community", color: "#3ea6ff" },
                      { id: 8, name: "Arion", text: "LFG 🚀", color: "#3ea6ff" },
                    ]);
                  }
                } else { setShowChat(false); }
              } else {
                setVisibility(prev => {
                  const next = { ...prev, [item.key]: !prev[item.key] };
                  window.__fullsend_overlay_state = { ...(window.__fullsend_overlay_state || {}), visibility: next };
                  return next;
                });
              }
            }} style={{
              padding: "4px 10px", borderRadius: 5,
              background: visibility[item.key] ? `${item.color}12` : COLORS.surface,
              border: `1px solid ${visibility[item.key] ? item.color + "25" : COLORS.border}`,
              color: visibility[item.key] ? item.color : COLORS.textMuted,
              fontSize: 9, fontWeight: 600, cursor: "pointer",
              fontFamily: "'JetBrains Mono', monospace",
              display: "flex", alignItems: "center", gap: 4,
            }}>{item.label}{item.v2 && <span style={{ fontSize: 6, padding: "1px 3px", borderRadius: 2, background: `${COLORS.orange}15`, border: `1px solid ${COLORS.orange}25`, color: COLORS.orange, fontWeight: 700 }}>V2</span>}</button>
          ))}
        </div>
      )}

      {(() => {
        // Helper: convert position key to CSS — handles grid strings AND custom {x, y}
        const posCSS = (pos) => {
          if (pos && typeof pos === "object" && pos.x !== undefined) {
            return { left: `${pos.x}%`, top: `${pos.y}%` };
          }
          const map = {
            "top-left":      { top: 14, left: 14 },
            "top-center":    { top: 14, left: "50%", transform: "translateX(-50%)" },
            "top-right":     { top: 14, right: 14 },
            "mid-left":      { top: "50%", left: 14, transform: "translateY(-50%)" },
            "center":        { top: "50%", left: "50%", transform: "translate(-50%, -50%)" },
            "mid-right":     { top: "50%", right: 14, transform: "translateY(-50%)" },
            "bottom-left":   { bottom: 16, left: 14 },
            "bottom-center": { bottom: 16, left: "50%", transform: "translateX(-50%)" },
            "bottom-right":  { bottom: 16, right: 14 },
          };
          return map[pos] || map["bottom-right"];
        };
        // Positions mirror drag canvas when Custom Layout is open, otherwise grid
        const useCustom = showCustomLayout || layoutMode === "custom";
        const effectivePos = (key) => (useCustom && customPositions[key]) ? customPositions[key] : positions[key];
        // Preview always uses normal visibility so Quick Test toggles work
        const effectiveVis = visibility;
        const tipPos = posCSS(effectivePos("tips"));
        const whalePos = posCSS(effectivePos("whale") || effectivePos("tips"));
        const followPos = posCSS(effectivePos("follows"));
        const microPos = posCSS(effectivePos("micro"));
        const chatPos = posCSS(effectivePos("chat"));
        const isRight = (pos) => typeof pos === "string" && pos.includes("right");
        const isBottom = (pos) => typeof pos === "string" && pos.includes("bottom");

        return (
      <div id="fullsend-overlay-preview" style={{
        maxWidth: 920, margin: "0 auto",
        aspectRatio: "16/9",
        background: `linear-gradient(160deg, #080812, #0e0e20, #0a0a15)`,
        borderRadius: 16,
        border: `1px solid ${COLORS.border}`,
        position: "relative",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}>
        {/* Atmospheric background (only when no bg image) */}
        {!streamBgImage && (
        <div data-overlay-bg style={{
          position: "absolute", inset: 0,
          background: `
            radial-gradient(ellipse at 25% 35%, #1a1a3510 0%, transparent 50%),
            radial-gradient(ellipse at 75% 65%, #2a1a3510 0%, transparent 50%),
            radial-gradient(ellipse at 50% 80%, #0a1a2510 0%, transparent 40%)
          `,
        }} />
        )}
        {/* Stream background image */}
        {streamBgImage && <img data-overlay-bg src={streamBgImage} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain", objectPosition: "center", pointerEvents: "none" }} />}
        {/* Subtle grid (only when no bg image) */}
        {!streamBgImage && (
        <div data-overlay-bg style={{
          position: "absolute", inset: 0,
          backgroundImage: `linear-gradient(#ffffff03 1px, transparent 1px), linear-gradient(90deg, #ffffff03 1px, transparent 1px)`,
          backgroundSize: "60px 60px",
          opacity: 0.5,
        }} />
        )}

        <div data-overlay-placeholder style={{
          color: COLORS.textMuted, fontSize: 13,
          fontFamily: "'Outfit', sans-serif",
          opacity: alerts.length > 0 ? 0.15 : 0.5,
          transition: "opacity 0.5s",
          textAlign: "center",
          lineHeight: 1.8,
        }}>
          <div>{streamBgImage ? "" : "Browser Source Preview"}</div>
          <div style={{ fontSize: 11 }}>{streamBgImage ? "" : "Test your alerts above before going live"}</div>
        </div>

        {/* Layout mode badge */}
        <div style={{
          position: "absolute", top: 8, right: 10, zIndex: 70,
          padding: "3px 8px", borderRadius: 4,
          background: `${COLORS.bg}cc`, backdropFilter: "blur(8px)",
          border: `1px solid ${COLORS.accent}25`,
          fontSize: 8, fontWeight: 700, color: COLORS.accent,
          fontFamily: "'JetBrains Mono', monospace",
          display: "flex", alignItems: "center", gap: 4,
          opacity: 0.7,
        }}>
          {layoutMode === "grid" ? "⚙️ Grid" : "📐 Custom"}
          <span style={{ width: 4, height: 4, borderRadius: "50%", background: COLORS.accent }} />
        </div>

        {/* Alert Overlay */}
        {effectiveVis.tips && alerts.filter(a => a.tier.label !== "WHALE").map((alert) => (
          <AlertAnimation key={alert.id} alert={alert} position={tipPos} isRight={isRight(positions.tips)} />
        ))}
        {effectiveVis.tips && alerts.filter(a => a.tier.label === "WHALE").map((alert) => (
          <AlertAnimation key={alert.id} alert={alert} position={whalePos} isRight={isRight(positions.tips)} />
        ))}

        {/* Sub Alerts */}
        {effectiveVis.follows && (
        <div style={{
          position: "absolute", ...followPos,
          display: "flex", flexDirection: "column", gap: 6,
          zIndex: 50,
        }}>
          {subAlerts.map((sub) => (
            <SubAlertAnimation key={sub.id} sub={sub} />
          ))}
        </div>
        )}

        {/* Micro Tip Alerts */}
        {effectiveVis.micro && (
        <div style={{
          position: "absolute", ...microPos,
          display: "flex", flexDirection: isBottom(positions.micro) ? "column-reverse" : "column", gap: 5,
          zIndex: 60,
          alignItems: isRight(positions.micro) ? "flex-end" : "flex-start",
        }}>
          {microAlerts.map((micro) => (
            <MicroAlertAnimation key={micro.id} micro={micro} />
          ))}
        </div>
        )}

        {/* LIVE badge — shown when platform API confirms live status */}

        {/* Viewer count — shown when platform API provides data */}

        {/* Chat Overlay — disabled until V2 chat ingestion ships */}
        {false && effectiveVis.chat && showChat && (
          <ChatOverlay messages={chatMessages} position={chatPos} posKey={effectivePos("chat")} />
        )}

        {/* Poll — always demo for placement */}
        {effectiveVis.polls && (() => {
          const demoPoll = {
            id: "demo", question: "What should we build next?",
            options: [
              { text: "NFT Rewards", votes: 42, color: COLORS.accent },
              { text: "Multi-Token", votes: 31, color: COLORS.blue },
              { text: "Raid Alerts", votes: 18, color: COLORS.purple },
            ],
            totalVotes: 91, status: "active",
          };
          return <PollOverlay key="demo" poll={demoPoll} posKey={effectivePos("polls")} />;
        })()}

        {/* Tip Goal Bar */}
        {effectiveVis.goal && (() => {
          const gp = effectivePos("goal") || "bottom-center";
          const gs = { position: "absolute", zIndex: 40, width: "40%", minWidth: 200 };
          if (typeof gp === "object" && gp.x !== undefined) {
            gs.left = `${gp.x}%`; gs.top = `${gp.y}%`;
          } else {
          if (gp.includes("top")) gs.top = 14;
          if (gp.includes("bottom")) gs.bottom = 14;
          if (gp.includes("right")) { gs.right = 14; }
          else if (gp.includes("left")) { gs.left = 14; }
          if (gp.includes("center") || (!gp.includes("left") && !gp.includes("right"))) { gs.left = "50%"; gs.transform = "translateX(-50%)"; }
          if (gp === "center" || gp === "mid-left" || gp === "mid-right") { gs.top = "50%"; gs.transform = (gs.transform || "") + " translateY(-50%)"; }
          }
          return (
          <div style={gs}>
            <div style={{
              background: `${COLORS.bg}d0`,
              backdropFilter: "blur(12px)",
              borderRadius: 8,
              padding: "6px 10px",
              border: `1px solid ${COLORS.accent}15`,
            }}>
              {(() => {
                const title = "DAO Marketing Fund";
                const target = 420;
                const current = 187.5;
                const pct = Math.min((current / target) * 100, 100);
                return (<>
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                marginBottom: 4,
              }}>
                <span style={{ fontSize: 8, fontWeight: 600, color: COLORS.text }}>
                  🎯 {title}
                </span>
                <span style={{
                  fontSize: 8, fontWeight: 700, color: COLORS.accent,
                  fontFamily: "'JetBrains Mono', monospace",
                }}>{current.toFixed(1)} / {target} LYX</span>
              </div>
              <div style={{
                height: 4, borderRadius: 2,
                background: `${COLORS.bg}80`,
                overflow: "hidden",
              }}>
                <div style={{
                  height: "100%",
                  width: `${pct}%`,
                  borderRadius: 2,
                  background: `linear-gradient(90deg, ${COLORS.accent}, ${COLORS.cyan})`,
                  boxShadow: `0 0 6px ${COLORS.accent}30`,
                  transition: "width 0.8s ease",
                }} />
              </div>
                </>);
              })()}
            </div>
          </div>
          );
        })()}

        {/* Claim Leaderboard (demo placement) */}
        {effectiveVis.leaderboard !== false && (() => {
          const lp = effectivePos("leaderboard") || "top-right";
          const ls = { position: "absolute", zIndex: 48, width: "24%", minWidth: 150 };
          if (typeof lp === "object" && lp.x !== undefined) {
            ls.left = `${lp.x}%`; ls.top = `${lp.y}%`;
          } else {
            if (lp.includes("top")) ls.top = 14;
            if (lp.includes("bottom")) ls.bottom = 14;
            if (lp.includes("right")) { ls.right = 14; }
            else if (lp.includes("left")) { ls.left = 14; }
            if (lp.includes("center") || (!lp.includes("left") && !lp.includes("right"))) { ls.left = "50%"; ls.transform = "translateX(-50%)"; }
            if (lp === "center" || lp === "mid-left" || lp === "mid-right") { ls.top = "50%"; ls.transform = (ls.transform || "") + " translateY(-50%)"; }
          }
          const demo = [
            { name: "Thalos", total: 30, avatar: DEMO_PFP_LEGEND },
            { name: "Arion", total: 25, avatar: DEMO_PFP_CHAMPION },
            { name: "ChainWizard", total: 20, avatar: DEMO_PFP_SUPPORTER },
            { name: "LUKSOgang", total: 15, avatar: DEMO_PFP_WHALE },
            { name: "web3builder", total: 10, avatar: STOCK_AVATAR },
          ];
          return (
            <div style={ls}>
              <div style={{ background: `${COLORS.bg}d0`, backdropFilter: "blur(12px)", borderRadius: 8, border: `1px solid ${COLORS.cyan}30`, overflow: "hidden" }}>
                <div style={{ padding: "4px 8px", borderBottom: `1px solid ${COLORS.border}`, fontSize: 7, fontWeight: 800, color: COLORS.cyan, fontFamily: "'Syne', sans-serif", letterSpacing: "0.5px" }}>🏆 LOYALTY TOKEN LEADERBOARD</div>
                <div style={{ padding: "4px 6px", display: "flex", flexDirection: "column", gap: 2 }}>
                  {demo.map((d, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 7 }}>
                      <span style={{ fontWeight: 800, color: i < 3 ? COLORS.cyan : COLORS.textMuted, minWidth: 12 }}>{i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `#${i + 1}`}</span>
                      <img src={d.avatar} alt="" style={{ width: 10, height: 10, borderRadius: "50%", objectFit: "cover" }} />
                      <span style={{ flex: 1, fontWeight: 700, color: COLORS.text }}>{d.name}</span>
                      <span style={{ fontWeight: 800, color: COLORS.cyan, fontFamily: "'JetBrains Mono', monospace" }}>{d.total}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })()}

        {/* QR Code */}
        {effectiveVis.qr && (() => {
          const qp = effectivePos("qr") || "bottom-left";
          const qs = { position: "absolute", zIndex: 55 };
          if (typeof qp === "object" && qp.x !== undefined) {
            qs.left = `${qp.x}%`; qs.top = `${qp.y}%`;
          } else {
          if (qp.includes("top")) qs.top = 14;
          if (qp.includes("bottom")) qs.bottom = 14;
          if (qp.includes("right")) { qs.right = 14; } else if (qp.includes("left")) { qs.left = 14; }
          if (qp.includes("center") || (!qp.includes("left") && !qp.includes("right"))) { qs.left = "50%"; qs.transform = "translateX(-50%)"; }
          if (qp === "center" || qp === "mid-left" || qp === "mid-right") { qs.top = "50%"; qs.transform = (qs.transform || "") + " translateY(-50%)"; }
          }
          return (
            <div style={qs}>
              <div style={{ background: "rgba(255,255,255,0.95)", borderRadius: 8, padding: 5, boxShadow: "0 0 10px rgba(0,0,0,0.3)" }}>
                <img src={DEMO_QR_CODE} alt="QR" style={{ width: 50, height: 50, borderRadius: 4 }} />
                <div style={{ textAlign: "center", fontSize: 6, fontWeight: 700, color: "#06060b", marginTop: 2, fontFamily: "'JetBrains Mono', monospace" }}>Scan to Tip</div>
              </div>
            </div>
          );
        })()}
      </div>
        );
      })()}
    </div>
  );
}

// --- CHAT OVERLAY (STREAM) --------------------------------------
function ChatOverlay({ messages, position, posKey }) {
  const chatRef = useRef(null);

  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [messages]);

  const isRight = typeof posKey === "string" && posKey.includes("right");

  return (
    <div style={{
      position: "absolute",
      top: 0, bottom: 0,
      left: position?.left || position?.right ? undefined : 14,
      right: position?.right ? (typeof position.right === "number" ? position.right : 14) : undefined,
      ...(position?.left ? { left: position.left } : {}),
      width: "38%",
      zIndex: 35,
      pointerEvents: "none",
      animation: "fadeIn 0.4s ease-out",
    }}>
      {/* Fade gradient at top */}
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, height: 60,
        background: `linear-gradient(180deg, ${COLORS.bg}d0 0%, transparent 100%)`,
        zIndex: 1,
      }} />

      <div
        ref={chatRef}
        style={{
          padding: "30px 12px 10px",
          overflowY: "hidden",
          maxHeight: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-end",
        }}
      >
        {messages.slice(-15).map((msg, idx) => {
          const isRecent = idx >= messages.slice(-15).length - 2;
          return (
            <div key={msg.id} style={{
              padding: "2px 0",
              opacity: isRecent ? 0.95 : 0.5 + (idx / messages.slice(-15).length) * 0.4,
              animation: idx === messages.slice(-15).length - 1 ? "chatSlideIn 0.3s ease-out" : "none",
            }}>
              <span style={{
                fontSize: 11,
                fontFamily: "Arial, sans-serif",
                textShadow: "0 1px 3px rgba(0,0,0,0.8), 0 0 8px rgba(0,0,0,0.5)",
              }}>
                <span style={{
                  color: msg.color || window.__fullsend_settings_data?.chatNameColor || "#3ea6ff",
                  fontWeight: 700,
                }}>{msg.name}</span>{" "}
                <span style={{ color: "#ddd" }}>{msg.text}</span>
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// --- POLL OVERLAY (STREAM) --------------------------------------
function PollOverlay({ poll, posKey, static: isStatic }) {
  const maxVotes = Math.max(...poll.options.map(o => o.votes), 1);

  const posStyle = isStatic ? {} : (() => {
    const p = posKey || "top-right";
    if (typeof p === "object" && p.x !== undefined) {
      return { position: "absolute", zIndex: 45, width: "32%", minWidth: 200, left: `${p.x}%`, top: `${p.y}%` };
    }
    const s = { position: "absolute", zIndex: 45, width: "32%", minWidth: 200 };
    if (p.includes("top")) s.top = 14;
    if (p.includes("bottom")) s.bottom = 14;
    if (p.includes("right")) s.right = 14;
    if (p.includes("left")) s.left = 14;
    if (p.includes("center") && !p.includes("top") && !p.includes("bottom")) { s.top = "50%"; s.transform = "translateY(-50%)"; }
    if (p.includes("center") && !p.includes("left") && !p.includes("right")) { s.left = "50%"; s.transform = (s.transform || "") + " translateX(-50%)"; }
    if (p === "center") { s.top = "50%"; s.left = "50%"; s.transform = "translate(-50%, -50%)"; }
    if (p === "top-center") { s.left = "50%"; s.transform = "translateX(-50%)"; }
    if (p === "bottom-center") { s.left = "50%"; s.transform = "translateX(-50%)"; }
    if (p === "mid-left") { s.top = "50%"; s.transform = "translateY(-50%)"; }
    if (p === "mid-right") { s.top = "50%"; s.transform = "translateY(-50%)"; }
    return s;
  })();

  return (
    <div style={{
      ...posStyle,
      animation: isStatic ? "none" : "fadeInUp 0.4s cubic-bezier(0.16, 1, 0.3, 1)",
    }}>
      <div style={{
        background: `${COLORS.bg}e8`,
        backdropFilter: "blur(20px) saturate(1.3)",
        borderRadius: 12,
        border: `1px solid ${COLORS.cyan}25`,
        padding: "12px 14px",
        boxShadow: `0 4px 24px ${COLORS.bg}80`,
      }}>
        {/* Header */}
        <div style={{
          display: "flex", alignItems: "center", gap: 6,
          marginBottom: 10,
        }}>
          <span style={{ fontSize: 12 }}>📊</span>
          <span style={{
            fontFamily: "'Syne', sans-serif",
            fontSize: 11, fontWeight: 700, color: COLORS.text,
            flex: 1,
            lineHeight: 1.3,
          }}>{poll.question}</span>
        </div>

        {poll.headerImage && (
          <img src={poll.headerImage} alt="" style={{ width: "100%", maxHeight: 150, objectFit: "contain", background: "rgba(0,0,0,0.2)", borderRadius: 4, marginBottom: 4 }} />
        )}

        {/* Options */}
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {poll.options.map((opt, i) => {
            const pct = poll.totalVotes > 0 ? (opt.votes / poll.totalVotes) * 100 : 0;
            const isLeading = opt.votes === maxVotes && poll.totalVotes > 0;
            return (
              <div key={i} style={{
                position: "relative",
                padding: "6px 10px",
                borderRadius: 6,
                overflow: "hidden",
                background: `${COLORS.bg}60`,
              }}>
                {/* Bar */}
                <div style={{
                  position: "absolute", left: 0, top: 0, bottom: 0,
                  width: `${pct}%`,
                  background: `${opt.color}20`,
                  borderRadius: 6,
                  transition: "width 0.8s cubic-bezier(0.16, 1, 0.3, 1)",
                }} />
                <div style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  position: "relative", zIndex: 1,
                }}>
                  <span style={{
                    fontSize: 10, fontWeight: isLeading ? 700 : 500,
                    color: isLeading ? opt.color : COLORS.textSecondary,
                    display: "flex", alignItems: "center", gap: 4,
                  }}>
                    {poll.optionImages?.[i] && <img src={poll.optionImages[i]} alt="" style={{ width: 14, height: 14, borderRadius: 2, objectFit: "cover" }} />}
                    {opt.text}
                  </span>
                  <span style={{
                    fontSize: 10, fontWeight: 700,
                    color: isLeading ? opt.color : COLORS.textMuted,
                    fontFamily: "'JetBrains Mono', monospace",
                  }}>{pct.toFixed(0)}%</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Countdown timer bar */}
        {poll.showLive && poll.timeRemaining > 0 && poll.duration && (
          <div style={{ marginTop: 8 }}>
            <div style={{
              height: 3, borderRadius: 2,
              background: `${COLORS.orange}15`,
              overflow: "hidden",
            }}>
              <div style={{
                height: "100%",
                width: `${(poll.timeRemaining / poll.duration) * 100}%`,
                background: `linear-gradient(90deg, ${COLORS.orange}, ${COLORS.gold})`,
                borderRadius: 2,
                transition: "width 1s linear",
                boxShadow: `0 0 4px ${COLORS.orange}30`,
              }} />
            </div>
          </div>
        )}

        {/* Footer */}
        <div style={{
          marginTop: poll.showLive && poll.timeRemaining > 0 ? 4 : 8,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          fontSize: 8, color: COLORS.textMuted,
        }}>
          <span>{poll.totalVotes} vote{poll.totalVotes !== 1 ? "s" : ""} · {poll.gateType === "free" ? "🟢 Free" : poll.gateType === "lyx" ? "🪙 LYX Gated" : "🔒 Token Gated"}</span>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            {poll.showLive && poll.timeRemaining > 0 && (
              <span style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 9, fontWeight: 700, color: COLORS.orange,
              }}>
                ⏱ {formatPollTimer(poll.timeRemaining)}
              </span>
            )}
            <span style={{
              display: "flex", alignItems: "center", gap: 3,
              color: poll.status === "active" ? COLORS.accent : COLORS.textMuted,
            }}>
              {poll.status === "active" && <span style={{
                width: 4, height: 4, borderRadius: "50%",
                background: COLORS.accent, animation: "pulse 1.5s infinite",
              }} />}
              {poll.status === "active" ? "LIVE" : "ENDED"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function AlertAnimation({ alert, position, isRight }) {
  const [phase, setPhase] = useState("enter");
  const tier = alert.tier;

  useEffect(() => {
    const fadeTime = tier.duration ? tier.duration - 1000 : 4000;
    const timer = setTimeout(() => setPhase("exit"), fadeTime);
    return () => clearTimeout(timer);
  }, [tier.duration]);

  const isWhale = tier.label === "WHALE";

  // All tips use the same configured position (no special whale centering)
  const posStyle = { ...position, transform: position?.transform || "none" };

  return (
    <div style={{
      position: "absolute",
      ...posStyle,
      animation: phase === "enter"
        ? `slideOverlayIn 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards`
        : `fadeOverlayOut 0.8s ease-in forwards`,
      zIndex: 60,
      maxWidth: 460,
      width: "auto",
    }}>
      <div style={{
        background: `${COLORS.bg}e5`,
        backdropFilter: "blur(24px) saturate(1.3)",
        borderRadius: 14,
        border: isWhale ? `3px solid #ffd700` : `1.5px solid ${tier.color}40`,
        padding: "18px 22px",
        position: "relative",
        overflow: "hidden",
      }}>
        {/* Tier color accent line */}
        <div style={{
          position: "absolute", top: 0, left: 0, right: 0, height: 2,
          background: `linear-gradient(90deg, transparent, ${tier.color}, transparent)`,
          opacity: 0.6,
        }} />

        <div style={{ display: "flex", alignItems: "center", gap: 14, position: "relative" }}>
          {/* UP Avatar / PFP */}
          <div style={{ position: "relative", flexShrink: 0 }}>
            <div style={{
              width: 52,
              height: 52,
              borderRadius: 12,
              background: `${tier.color}18`,
              border: `2px solid ${tier.color}40`,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 24,
              boxShadow: `0 0 10px ${tier.color}15`,
              overflow: "hidden",
            }}>
              {isImageAvatar(alert.from.avatar) ? (
                <img src={alert.from.avatar} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 10 }} />
              ) : alert.from.avatar}
            </div>
            {/* Tier badge — sits on top of PFP */}
            <div style={{
              position: "absolute", bottom: -4, left: -4, zIndex: 2,
              fontSize: 13,
              background: COLORS.bg,
              borderRadius: "50%",
              width: 22,
              height: 22,
              display: "flex", alignItems: "center", justifyContent: "center",
              border: `1.5px solid ${tier.color}50`,
            }}>{tier.emoji}</div>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
              <span style={{
                fontFamily: "'Syne', sans-serif",
                fontSize: 22,
                fontWeight: 800,
                color: tier.color,
                letterSpacing: "-0.5px",
              }}>{alert.amount} LYX</span>
              <span style={{ fontSize: 13, color: COLORS.textMuted }}>from</span>
              <span style={{
                fontWeight: 700,
                fontSize: 15,
                color: COLORS.text,
              }}>{alert.from.name}</span>
            </div>
            {alert.message && (
              <div style={{
                fontSize: 14,
                color: COLORS.textSecondary,
                marginTop: 6,
                lineHeight: 1.5,
              }}>"{alert.message}"</div>
            )}
          </div>
        </div>

        {/* Progress bar */}
        <div style={{
          marginTop: 10, height: 2,
          background: `${tier.color}12`,
          borderRadius: 1,
          overflow: "hidden",
        }}>
          <div style={{
            height: "100%",
            background: `linear-gradient(90deg, ${tier.color}, ${tier.color}80)`,
            borderRadius: 1,
            animation: `shrink ${(tier.duration || 5000) / 1000}s linear forwards`,
            boxShadow: `0 0 6px ${tier.color}40`,
          }} />
        </div>
      </div>
    </div>
  );
}

// --- SUB ALERT ANIMATION -------------------------------------
function SubAlertAnimation({ sub }) {
  const [phase, setPhase] = useState("enter");

  useEffect(() => {
    const timer = setTimeout(() => setPhase("exit"), 3200);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div style={{
      animation: phase === "enter"
        ? "subSlideIn 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards"
        : "subFadeOut 0.6s ease-in forwards",
    }}>
      <div style={{
        background: `${COLORS.bg}dd`,
        backdropFilter: "blur(16px)",
        borderRadius: 10,
        border: `1px solid ${COLORS.cyan}30`,
        padding: "8px 14px",
        display: "flex",
        alignItems: "center",
        gap: 8,
        boxShadow: `0 0 16px ${COLORS.cyan}10`,
        whiteSpace: "nowrap",
      }}>
        <div>
          <div style={{
            fontSize: 11, fontWeight: 700, color: COLORS.text,
          }}>
            <span style={{ color: COLORS.cyan }}>@{sub.profile.name}</span>{" "}
            <span style={{ color: COLORS.textSecondary, fontWeight: 500 }}>followed</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// --- MICRO TIP ALERT ANIMATION ------------------------------
function MicroAlertAnimation({ micro }) {
  const [phase, setPhase] = useState("enter");

  useEffect(() => {
    const timer = setTimeout(() => setPhase("exit"), 2800);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div style={{
      animation: phase === "enter"
        ? "subSlideIn 0.35s cubic-bezier(0.16, 1, 0.3, 1) forwards"
        : "subFadeOut 0.5s ease-in forwards",
    }}>
      <div style={{
        background: `${COLORS.bg}cc`,
        backdropFilter: "blur(12px)",
        borderRadius: 8,
        border: `1px solid ${COLORS.textMuted}20`,
        padding: "6px 12px",
        display: "flex",
        alignItems: "center",
        gap: 7,
        whiteSpace: "nowrap",
      }}>
        <span style={{ fontSize: 12, width: 20, height: 20, borderRadius: 5, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", flexShrink: 0, background: `${COLORS.textMuted}15` }}>
          ⚡
        </span>
        <span style={{
          fontSize: 11, fontWeight: 600, color: COLORS.textSecondary,
        }}>@{micro.from.name}</span>
        <span style={{
          fontSize: 10, color: COLORS.textMuted,
        }}>tipped</span>
        <span style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 11, fontWeight: 700, color: COLORS.text,
        }}>{micro.amount} LYX</span>
      </div>
    </div>
  );
}

// --- POLL MANAGER (Creator) ----------------------------------
// --- GOAL MANAGER -------------------------------------------
function MOCWidgetSection({ creatorAddr, viewerAddr, connected, onConnect, wc }) {
  const [moc, setMoc] = useState(null);
  const [checked, setChecked] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [loadingMoments, setLoadingMoments] = useState(false);
  const [momentsErr, setMomentsErr] = useState(false);
  const [myMoments, setMyMoments] = useState([]);
  const [selected, setSelected] = useState([]);
  const [message, setMessage] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    let c = false;
    if (!creatorAddr) { setChecked(true); return; }
    MOCAPI.getActive(creatorAddr).then(m => { if (!c) { setMoc(m); setChecked(true); } });
    return () => { c = true; };
  }, [creatorAddr]);

  useEffect(() => {
    if (!expanded || !connected || !viewerAddr) return;
    let c = false;
    setLoadingMoments(true); setMomentsErr(false);
    fmFetchMoments(viewerAddr).then(arr => { if (!c) { setMyMoments(arr || []); setLoadingMoments(false); } })
      .catch(() => { if (!c) { setMomentsErr(true); setLoadingMoments(false); } });
    return () => { c = true; };
  }, [expanded, connected, viewerAddr]);

  if (!checked || !moc) return null;

  const gateNote = {
    free: null,
    lyx: `Tip ${moc.lyx_amount || "0"} LYX to unlock`,
    password: "Password required (ask the streamer)",
    token: "Requires a specific token to submit",
  }[moc.gate_type];

  const toggleSel = (id) => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : (prev.length >= 3 ? prev : [...prev, id]));

  const submit = async () => {
    if (submitting || selected.length === 0) return;
    if (moc.gate_type === "password" && !password.trim()) { setResult("Enter the password to submit."); return; }
    setSubmitting(true); setResult(null);
    const chosen = myMoments.filter(m => selected.includes(fmMomentId(m)))
      .map(m => ({ id: fmMomentId(m), image: fmMomentImage(m), title: fmMomentTitle(m), type: fmMomentIsVideo(m) ? "video" : "image" }));
    const res = await MOCAPI.submit({ mocId: moc.id, creator: creatorAddr, poster: viewerAddr, moments: chosen, message: message.trim(), password: password.trim() });
    setSubmitting(false);
    if (res && res.success && res.duplicate) setResult("dup");
    else if (res && res.success) setResult("ok");
    else setResult((res && res.error) || "Submission failed");
  };

  return (
    <div style={{ marginBottom: 16, background: `linear-gradient(135deg, ${wc.purple}10, ${wc.cyan}08)`, border: `1px solid ${wc.purple}30`, borderRadius: 14, padding: "14px 16px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 18 }}>📸</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 800, fontSize: 14, color: wc.text, fontFamily: "'Syne', sans-serif" }}>{moc.name || "Moments on Chain"}</div>
          <div style={{ fontSize: 10, color: wc.textMuted }}>Share your on-chain moments to the stream{gateNote ? ` · ${gateNote}` : ""}</div>
        </div>
        {!expanded && (
          <button onClick={() => setExpanded(true)} style={{ padding: "7px 12px", borderRadius: 8, background: `linear-gradient(135deg, ${wc.purple}, ${wc.cyan})`, border: "none", color: "#fff", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>Share</button>
        )}
      </div>

      {expanded && (
        <div style={{ marginTop: 12 }}>
          {result === "ok" ? (
            <div style={{ textAlign: "center", padding: "16px 8px", color: wc.accent, fontSize: 13, fontWeight: 700 }}>✓ Submitted! The streamer will choose which to show on stream.</div>
          ) : result === "dup" ? (
            <div style={{ textAlign: "center", padding: "16px 8px", color: wc.orange, fontSize: 13, fontWeight: 700 }}>You've already submitted to this MOC.</div>
          ) : !connected ? (
            <div style={{ textAlign: "center", padding: "12px 8px" }}>
              <div style={{ fontSize: 12, color: wc.textMuted, marginBottom: 10 }}>Connect your Universal Profile to load your moments.</div>
              <button onClick={onConnect} style={{ padding: "9px 18px", borderRadius: 9, background: `linear-gradient(135deg, ${wc.accent}, ${wc.cyan})`, border: "none", color: "#04140c", fontSize: 12, fontWeight: 800, cursor: "pointer" }}>Connect UP</button>
            </div>
          ) : loadingMoments ? (
            <div style={{ textAlign: "center", padding: 20, color: wc.textMuted, fontSize: 12 }}>Loading your moments…</div>
          ) : momentsErr ? (
            <div style={{ textAlign: "center", padding: 16, color: wc.orange, fontSize: 12, lineHeight: 1.5 }}>Moments are temporarily unavailable — please try again shortly.</div>
          ) : myMoments.length === 0 ? (
            <div style={{ textAlign: "center", padding: 16, color: wc.textMuted, fontSize: 12, lineHeight: 1.5 }}>You don't have any moments yet.<br />Post photos on-chain at forevermoments.life first.</div>
          ) : (
            <>
              <div style={{ fontSize: 10, color: wc.textMuted, marginBottom: 8 }}>Pick up to 3 ({selected.length}/3)</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6, maxHeight: 260, overflowY: "auto", overflowX: "hidden", marginBottom: 10 }}>
                {myMoments.map((m, i) => {
                  const id = fmMomentId(m);
                  const img = fmMomentImage(m);
                  const isVid = fmMomentIsVideo(m);
                  const sel = selected.includes(id);
                  const idx = selected.indexOf(id);
                  return (
                    <div key={id || i} onClick={() => toggleSel(id)} style={{ position: "relative", aspectRatio: "1", borderRadius: 8, overflow: "hidden", cursor: "pointer", border: `2px solid ${sel ? wc.accent : "transparent"}`, background: wc.bg }}>
                      {img
                        ? (isVid
                            ? <video src={img} muted playsInline preload="metadata" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", opacity: sel ? 1 : 0.92 }} />
                            : <img src={img} alt={fmMomentTitle(m)} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", opacity: sel ? 1 : 0.92 }} />)
                        : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>{isVid ? "🎬" : "🖼️"}</div>}
                      {isVid && <span style={{ position: "absolute", bottom: 4, left: 4, fontSize: 13, filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.8))" }}>▶</span>}
                      {sel && <div style={{ position: "absolute", top: 4, right: 4, width: 18, height: 18, borderRadius: "50%", background: wc.accent, color: "#04140c", fontSize: 10, fontWeight: 900, display: "flex", alignItems: "center", justifyContent: "center" }}>{idx + 1}</div>}
                    </div>
                  );
                })}
              </div>

              {moc.gate_type === "password" && (
                <input value={password} onChange={e => setPassword(e.target.value)} placeholder="Password" style={{ width: "100%", padding: "9px 12px", marginBottom: 8, background: wc.bg, border: `1px solid ${wc.border}`, borderRadius: 8, color: wc.text, fontSize: 12, outline: "none", boxSizing: "border-box" }} />
              )}
              <input value={message} onChange={e => setMessage(e.target.value)} maxLength={280} placeholder="Add a note (optional)" style={{ width: "100%", padding: "9px 12px", marginBottom: 10, background: wc.bg, border: `1px solid ${wc.border}`, borderRadius: 8, color: wc.text, fontSize: 12, outline: "none", boxSizing: "border-box" }} />

              {result && result !== "ok" && result !== "dup" && <div style={{ fontSize: 11, color: wc.orange, marginBottom: 8, textAlign: "center" }}>{result}</div>}

              <button onClick={submit} disabled={submitting || selected.length === 0} style={{ width: "100%", padding: "11px 0", borderRadius: 9, background: submitting || selected.length === 0 ? wc.surface : `linear-gradient(135deg, ${wc.purple}, ${wc.cyan})`, border: "none", color: submitting || selected.length === 0 ? wc.textMuted : "#fff", fontSize: 13, fontWeight: 800, cursor: submitting || selected.length === 0 ? "default" : "pointer", fontFamily: "'Syne', sans-serif" }}>
                {submitting ? "Submitting…" : `Submit ${selected.length || ""} moment${selected.length === 1 ? "" : "s"}`}
              </button>
            </>
          )}
        </div>
      )}
      <a href="https://www.forevermoments.life" target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 5, marginTop: 10, fontSize: 9, color: wc.textMuted, textDecoration: "none", letterSpacing: "0.3px" }}>
        📷 Powered by <span style={{ fontWeight: 800, color: wc.cyan }}>Forever Moments ↗</span>
      </a>
    </div>
  );
}

function MOCManager() {
  const creatorAddr = window.__fullsend_profile?.address;
  const [mounted, setMounted] = useState(false);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fmOnline, setFmOnline] = useState(null); // null=checking
  // launch form
  const [name, setName] = useState("");
  const [gate, setGate] = useState("free");
  const [lyxAmount, setLyxAmount] = useState("");
  const [password, setPassword] = useState("");
  const [tokenAddress, setTokenAddress] = useState("");
  const [tokenMin, setTokenMin] = useState("");
  const [launching, setLaunching] = useState(false);
  const [copied, setCopied] = useState(false);
  // moderation queue
  const [subs, setSubs] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [profiles, setProfiles] = useState({});
  const [moments, setMoments] = useState({});

  useEffect(() => { requestAnimationFrame(() => setMounted(true)); }, []);
  useEffect(() => { fmHealthCheck().then(setFmOnline); }, []);

  const refreshActive = async () => {
    if (!creatorAddr) { setLoading(false); return; }
    const m = await MOCAPI.getActive(creatorAddr);
    setActive(m); setLoading(false);
  };
  useEffect(() => { refreshActive(); /* eslint-disable-next-line */ }, [creatorAddr]);

  // Poll moderation queue + hydrate poster profiles & moment images
  useEffect(() => {
    if (!creatorAddr || !active) { setSubs([]); return; }
    let cancelled = false;
    const load = async () => {
      const rows = await MOCAPI.queue(creatorAddr, active.id);
      if (cancelled) return;
      setSubs(rows);
      const addrs = [...new Set(rows.map(r => r.poster).filter(Boolean).map(a => a.toLowerCase()))];
      const missing = addrs.filter(a => !profiles[a]);
      if (missing.length) {
        const got = await Promise.all(missing.map(a => fetchUPProfile(a).catch(() => null)));
        if (!cancelled) setProfiles(prev => { const n = { ...prev }; missing.forEach((a, i) => { if (got[i]) n[a] = got[i]; }); return n; });
      }
      // moment images now travel in the submission snapshot — no per-id refetch needed
    };
    load();
    const iv = setInterval(load, 7000);
    return () => { cancelled = true; clearInterval(iv); };
    /* eslint-disable-next-line */
  }, [creatorAddr, active && active.id]);

  const doLaunch = async () => {
    if (!name.trim() || launching) return;
    if (gate === "password" && !password.trim()) { alert("Set a password or pick a different gate."); return; }
    if (gate === "token" && !tokenAddress.trim()) { alert("Enter a token address or pick a different gate."); return; }
    setLaunching(true);
    const cfg = { name: name.trim(), gateType: gate };
    if (gate === "lyx") cfg.lyxAmount = lyxAmount || "0";
    if (gate === "password") cfg.password = password.trim();
    if (gate === "token") { cfg.tokenAddress = tokenAddress.trim(); cfg.tokenMin = tokenMin || "0"; }
    const res = await MOCAPI.launch(creatorAddr, cfg);
    setLaunching(false);
    if (res && res.success) {
      setName(""); setPassword(""); setLyxAmount(""); setTokenAddress(""); setTokenMin(""); setGate("free");
      refreshActive();
    } else { alert((res && res.error) || "Launch failed"); }
  };

  const endMOC = async () => {
    if (!active) return;
    await MOCAPI.end(creatorAddr, active.id);
    setActive(null); setSubs([]);
  };

  const toggleShow = async (sub) => {
    const v = !sub.show_on_stream;
    setSubs(prev => prev.map(s => {
      if (s.id === sub.id) return { ...s, show_on_stream: v, status: v ? "approved" : "pending" };
      return v ? { ...s, show_on_stream: false } : s; // spotlight: showing one hides the rest
    }));
    await MOCAPI.setShow(sub.id, v);
  };
  const rejectSub = async (sub) => {
    setSubs(prev => prev.map(s => s.id === sub.id ? { ...s, status: "rejected", show_on_stream: false } : s));
    await MOCAPI.reject(sub.id);
  };
  const archiveSub = async (sub) => {
    setSubs(prev => prev.map(s => s.id === sub.id ? { ...s, status: "archived", show_on_stream: false } : s));
    await MOCAPI.archive(sub.id);
  };
  const unarchiveSub = async (sub) => {
    setSubs(prev => prev.map(s => s.id === sub.id ? { ...s, status: "pending" } : s));
    await MOCAPI.unarchive(sub.id);
  };

  const pName = (a) => { const p = profiles[(a || "").toLowerCase()]; return (p && (p.name || p.username || p.handle)) || ((a || "").slice(0, 6) + "…" + (a || "").slice(-4)); };
  const pImg = (a) => { const p = profiles[(a || "").toLowerCase()]; return fmResolveIpfs((p && (p.image || p.avatar || p.profileImage || p.icon)) || ""); };
  const gateLabel = (g) => ({ free: "Free", lyx: "LYX-gated", password: "Password", token: "Token-gated" }[g] || g);

  const tipUrl = `https://fullsend.live/tip/${creatorAddr || "0x0"}`;
  const inputStyle = { width: "100%", padding: "9px 12px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.text, fontFamily: "'JetBrains Mono', monospace", fontSize: 12, outline: "none", boxSizing: "border-box" };
  const lblStyle = { fontSize: 10, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "1.5px", display: "block", marginBottom: 6, marginTop: 14 };

  return (
    <div style={{ maxWidth: 720, margin: "0 auto", opacity: mounted ? 1 : 0, transition: "opacity 0.4s" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6, flexWrap: "wrap", gap: 8 }}>
        <div style={{ fontFamily: "'Syne', sans-serif", fontSize: 22, fontWeight: 800, color: COLORS.text }}>📸 Moments on Chain</div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 10, fontFamily: "'JetBrains Mono', monospace", color: COLORS.textMuted }}>
          <span style={{ width: 7, height: 7, borderRadius: "50%", background: fmOnline === false ? COLORS.red : fmOnline ? COLORS.accent : COLORS.textMuted, boxShadow: fmOnline ? `0 0 6px ${COLORS.accent}60` : "none" }} />
          {fmOnline === false ? "Moments API offline" : fmOnline ? "Moments connected" : "Checking…"}
        </div>
      </div>
      <div style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 16, lineHeight: 1.5 }}>
        Ask viewers to share their on-chain moments (photos) from Forever Moments — you choose which ones land on stream.
      </div>

      {fmOnline === false && (
        <div style={{ marginBottom: 14, padding: "10px 12px", borderRadius: 8, background: `${COLORS.red}12`, border: `1px solid ${COLORS.red}30`, fontSize: 11, color: COLORS.red, lineHeight: 1.5 }}>
          ⚠️ Forever Moments is unreachable right now — viewers won't be able to load their moments to submit. You can still launch; it'll work once the API is back.
        </div>
      )}

      {loading ? (
        <div style={{ padding: 40, textAlign: "center", color: COLORS.textMuted, fontSize: 12 }}>Loading…</div>
      ) : !active ? (
        <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: "20px 22px" }}>
          <label style={{ ...lblStyle, marginTop: 0 }}>MOC Name</label>
          <input value={name} onChange={e => setName(e.target.value)} maxLength={120} placeholder="e.g. Share your shots from the meetup" style={inputStyle} />

          <label style={lblStyle}>Who can submit?</label>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {[["free", "🆓 Free"], ["lyx", "⚡ LYX-gated"], ["password", "🔑 Password"], ["token", "💎 Token-gated"]].map(([g, lab]) => (
              <button key={g} onClick={() => setGate(g)} style={{
                padding: "7px 12px", borderRadius: 8, cursor: "pointer", fontSize: 11, fontWeight: 700,
                fontFamily: "'JetBrains Mono', monospace",
                background: gate === g ? `${COLORS.accent}15` : COLORS.bgAlt,
                border: `1px solid ${gate === g ? COLORS.accent + "40" : COLORS.border}`,
                color: gate === g ? COLORS.accent : COLORS.textMuted,
              }}>{lab}</button>
            ))}
          </div>

          {gate === "lyx" && (<><label style={lblStyle}>Minimum tip to unlock (LYX)</label><input type="number" step="0.1" value={lyxAmount} onChange={e => setLyxAmount(e.target.value)} placeholder="5" style={inputStyle} /></>)}
          {gate === "password" && (<><label style={lblStyle}>Password <span style={{ textTransform: "none", letterSpacing: 0, color: COLORS.textMuted }}>(hand out on cards at your event)</span></label><input value={password} onChange={e => setPassword(e.target.value)} placeholder="e.g. LUKSO!" style={inputStyle} /></>)}
          {gate === "token" && (<>
            <label style={lblStyle}>Token address (LSP7 / LSP8)</label><input value={tokenAddress} onChange={e => setTokenAddress(e.target.value)} placeholder="0x…" style={inputStyle} />
            <label style={lblStyle}>Minimum balance</label><input type="number" value={tokenMin} onChange={e => setTokenMin(e.target.value)} placeholder="1" style={inputStyle} />
          </>)}

          <button onClick={doLaunch} disabled={launching || !name.trim()} style={{
            width: "100%", marginTop: 20, padding: "12px 0", borderRadius: 10,
            background: launching || !name.trim() ? COLORS.bgAlt : `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.cyan})`,
            border: "none", color: launching || !name.trim() ? COLORS.textMuted : "#04140c",
            fontSize: 14, fontWeight: 800, cursor: launching || !name.trim() ? "default" : "pointer",
            fontFamily: "'Syne', sans-serif",
          }}>{launching ? "Launching…" : "🚀 Launch MOC"}</button>
        </div>
      ) : (
        <>
          <div style={{ background: `linear-gradient(135deg, ${COLORS.surface}, #0c1320)`, border: `1px solid ${COLORS.accent}25`, borderRadius: 14, padding: "16px 20px", marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
              <div>
                <div style={{ fontSize: 9, color: COLORS.accent, fontWeight: 700, letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: 3 }}>● Live MOC</div>
                <div style={{ fontFamily: "'Syne', sans-serif", fontSize: 18, fontWeight: 800, color: COLORS.text }}>{active.name || "Untitled MOC"}</div>
                <div style={{ fontSize: 10, color: COLORS.textMuted, fontFamily: "'JetBrains Mono', monospace", marginTop: 2 }}>Gate: {gateLabel(active.gate_type)}{active.gate_type === "lyx" && ` · ${active.lyx_amount} LYX`}</div>
              </div>
              <button onClick={endMOC} style={{ padding: "8px 14px", borderRadius: 8, background: `${COLORS.red}12`, border: `1px solid ${COLORS.red}25`, color: COLORS.red, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "'JetBrains Mono', monospace" }}>End MOC</button>
            </div>
            <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 8, background: COLORS.bgAlt, border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: "8px 10px" }}>
              <span style={{ fontSize: 10, color: COLORS.textMuted, fontFamily: "'JetBrains Mono', monospace", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{tipUrl}</span>
              <button onClick={() => { navigator.clipboard?.writeText(tipUrl); setCopied(true); setTimeout(() => setCopied(false), 1500); }} style={{ padding: "5px 10px", borderRadius: 6, background: `${COLORS.accent}15`, border: `1px solid ${COLORS.accent}30`, color: COLORS.accent, fontSize: 10, fontWeight: 700, cursor: "pointer" }}>{copied ? "Copied!" : "Copy link"}</button>
            </div>
            <div style={{ fontSize: 9, color: COLORS.textMuted, marginTop: 6 }}>Viewers open this and tap the <strong>Moments</strong> tab to submit.</div>
          </div>

          <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "1.5px", marginBottom: 10 }}>Submissions ({subs.filter(s => s.status !== "rejected" && s.status !== "archived").length})</div>

          {subs.filter(s => s.status !== "rejected" && s.status !== "archived").length === 0 ? (
            <div style={{ padding: 30, textAlign: "center", color: COLORS.textMuted, fontSize: 12, background: COLORS.surface, border: `1px dashed ${COLORS.border}`, borderRadius: 12 }}>No submissions yet — share the link and they'll show up here.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {subs.filter(s => s.status !== "rejected" && s.status !== "archived").map(sub => {
                const items = Array.isArray(sub.moment_ids) ? sub.moment_ids : [];
                const on = sub.show_on_stream;
                return (
                  <div key={sub.id} style={{ background: COLORS.surface, border: `1px solid ${on ? COLORS.accent + "40" : COLORS.border}`, borderRadius: 12, padding: 12 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                      {pImg(sub.poster) ? <img src={pImg(sub.poster)} alt="" style={{ width: 26, height: 26, borderRadius: "50%", objectFit: "cover" }} /> : <div style={{ width: 26, height: 26, borderRadius: "50%", background: COLORS.bgAlt }} />}
                      <span style={{ fontSize: 12, fontWeight: 700, color: COLORS.text }}>{pName(sub.poster)}</span>
                      {on && <span style={{ fontSize: 9, color: COLORS.accent, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace" }}>● ON STREAM</span>}
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: sub.message ? 8 : 10 }}>
                      {items.map((it, i) => {
                        const img = fmResolveIpfs((it && it.image) || "");
                        const isVid = it && it.type === "video";
                        if (!img) return <div key={i} style={{ width: 92, height: 92, borderRadius: 8, background: COLORS.bgAlt, border: `1px solid ${COLORS.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>{isVid ? "🎬" : "🖼️"}</div>;
                        return isVid
                          ? <div key={i} style={{ position: "relative" }}><video src={img} muted playsInline preload="metadata" style={{ width: 92, height: 92, borderRadius: 8, objectFit: "cover", border: `1px solid ${COLORS.border}`, display: "block" }} /><span style={{ position: "absolute", bottom: 4, left: 4, fontSize: 13, filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.8))" }}>▶</span></div>
                          : <img key={i} src={img} alt={(it && it.title) || ""} title={(it && it.title) || ""} style={{ width: 92, height: 92, borderRadius: 8, objectFit: "cover", border: `1px solid ${COLORS.border}` }} />;
                      })}
                    </div>
                    {sub.message && <div style={{ fontSize: 12, color: COLORS.textSecondary, marginBottom: 10, lineHeight: 1.4 }}>{sub.message}</div>}
                    <div style={{ display: "flex", gap: 8 }}>
                      <button onClick={() => toggleShow(sub)} style={{ flex: 1, padding: "8px 0", borderRadius: 8, background: on ? `${COLORS.red}10` : `${COLORS.accent}15`, border: `1px solid ${on ? COLORS.red + "25" : COLORS.accent + "30"}`, color: on ? COLORS.red : COLORS.accent, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "'JetBrains Mono', monospace" }}>{on ? "✕ Hide from Stream" : "📺 Show on Stream"}</button>
                      <button onClick={() => archiveSub(sub)} style={{ padding: "8px 14px", borderRadius: 8, background: `${COLORS.purple}12`, border: `1px solid ${COLORS.purple}30`, color: COLORS.purple, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "'JetBrains Mono', monospace" }}>📥 Archive</button>
                      <button onClick={() => rejectSub(sub)} style={{ padding: "8px 14px", borderRadius: 8, background: "transparent", border: `1px solid ${COLORS.border}`, color: COLORS.textMuted, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "'JetBrains Mono', monospace" }}>Reject</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {subs.filter(s => s.status === "archived").length > 0 && (
            <div style={{ marginTop: 18 }}>
              <button onClick={() => setShowArchived(v => !v)} style={{ display: "flex", alignItems: "center", gap: 6, background: "transparent", border: "none", cursor: "pointer", color: COLORS.textMuted, fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "1.5px", padding: 0 }}>
                {showArchived ? "▼" : "▶"} 📥 Archived ({subs.filter(s => s.status === "archived").length})
              </button>
              {showArchived && (
                <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 10 }}>
                  {subs.filter(s => s.status === "archived").map(sub => {
                    const items = Array.isArray(sub.moment_ids) ? sub.moment_ids : [];
                    return (
                      <div key={sub.id} style={{ background: COLORS.bgAlt, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: 12, opacity: 0.9 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                          {pImg(sub.poster) ? <img src={pImg(sub.poster)} alt="" style={{ width: 26, height: 26, borderRadius: "50%", objectFit: "cover" }} /> : <div style={{ width: 26, height: 26, borderRadius: "50%", background: COLORS.surface }} />}
                          <span style={{ fontSize: 12, fontWeight: 700, color: COLORS.text }}>{pName(sub.poster)}</span>
                          <span style={{ fontSize: 9, color: COLORS.textMuted, fontFamily: "'JetBrains Mono', monospace" }}>ARCHIVED</span>
                        </div>
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: sub.message ? 8 : 10 }}>
                          {items.map((it, i) => {
                            const img = fmResolveIpfs((it && it.image) || "");
                            const isVid = it && it.type === "video";
                            if (!img) return <div key={i} style={{ width: 76, height: 76, borderRadius: 8, background: COLORS.surface, border: `1px solid ${COLORS.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}>{isVid ? "🎬" : "🖼️"}</div>;
                            return isVid
                              ? <div key={i} style={{ position: "relative" }}><video src={img} muted playsInline preload="metadata" style={{ width: 76, height: 76, borderRadius: 8, objectFit: "cover", border: `1px solid ${COLORS.border}`, display: "block" }} /><span style={{ position: "absolute", bottom: 3, left: 3, fontSize: 11, filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.8))" }}>▶</span></div>
                              : <img key={i} src={img} alt={(it && it.title) || ""} title={(it && it.title) || ""} style={{ width: 76, height: 76, borderRadius: 8, objectFit: "cover", border: `1px solid ${COLORS.border}` }} />;
                          })}
                        </div>
                        {sub.message && <div style={{ fontSize: 12, color: COLORS.textSecondary, marginBottom: 10, lineHeight: 1.4 }}>{sub.message}</div>}
                        <div style={{ display: "flex", gap: 8 }}>
                          <button onClick={() => unarchiveSub(sub)} style={{ flex: 1, padding: "8px 0", borderRadius: 8, background: `${COLORS.accent}12`, border: `1px solid ${COLORS.accent}30`, color: COLORS.accent, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "'JetBrains Mono', monospace" }}>↩ Restore</button>
                          <button onClick={() => rejectSub(sub)} style={{ padding: "8px 14px", borderRadius: 8, background: "transparent", border: `1px solid ${COLORS.border}`, color: COLORS.textMuted, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "'JetBrains Mono', monospace" }}>Reject</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </>
      )}
      <a href="https://www.forevermoments.life" target="_blank" rel="noopener noreferrer" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginTop: 18, paddingTop: 14, fontSize: 11, color: COLORS.textMuted, textDecoration: "none", borderTop: `1px solid ${COLORS.border}` }}>
        <span>📷 Powered by</span>
        <span style={{ fontWeight: 800, color: COLORS.cyan }}>Forever Moments ↗</span>
      </a>
    </div>
  );
}

function GoalManager() {
  const [mounted, setMounted] = useState(false);
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState("");
  const [target, setTarget] = useState("");
  const [showArchived, setShowArchived] = useState(false);

  useEffect(() => { requestAnimationFrame(() => setMounted(true)); }, []);

  // DB-only goal state — single source of truth, polls every 5s
  const creatorAddr = window.__fullsend_profile?.address;
  const { active, history, refresh } = useActiveGoal(creatorAddr);

  // Reconstruct legacy-shaped goals array from {active, history} so render JSX works unchanged
  const goals = [
    ...(active ? [active] : []),
    ...history,
  ];

  const createGoal = async () => {
    if (!title.trim() || !target || parseFloat(target) <= 0) return;
    const ok = await GoalAPI.create(creatorAddr, { title: title.trim(), target: parseFloat(target) });
    if (ok) {
      setTitle("");
      setTarget("");
      setCreating(false);
      window.dispatchEvent(new CustomEvent("fullsend-goal-updated"));
      refresh();
    }
  };

  const activateGoal = async (id) => {
    const ok = await GoalAPI.activate(creatorAddr, id);
    if (ok) { window.dispatchEvent(new CustomEvent("fullsend-goal-updated")); refresh(); }
  };

  const completeGoal = async (id) => {
    // Mark completed by setting current = target and archiving
    const goal = goals.find(g => g.id === id);
    if (!goal) return;
    await GoalAPI.setActive(creatorAddr, { ...goal, current: goal.target, completedAt: new Date().toISOString() });
    await GoalAPI.archive(creatorAddr, id);
    window.dispatchEvent(new CustomEvent("fullsend-goal-updated"));
    refresh();
  };

  const archiveGoal = async (id) => {
    const ok = await GoalAPI.archive(creatorAddr, id);
    if (ok) { window.dispatchEvent(new CustomEvent("fullsend-goal-updated")); refresh(); }
  };

  const deleteGoal = async (id) => {
    const ok = await GoalAPI.remove(creatorAddr, id);
    if (ok) { window.dispatchEvent(new CustomEvent("fullsend-goal-updated")); refresh(); }
  };

  const resetGoal = async (id) => {
    if (active?.id === id) {
      await GoalAPI.reset(creatorAddr);
      window.dispatchEvent(new CustomEvent("fullsend-goal-updated"));
      refresh();
    }
  };

  const activeGoals = goals.filter(g => g.status === "active" || g.status === "paused");
  const completedGoals = goals.filter(g => g.status === "completed" || (g.completedAt && g.status !== "archived"));
  const archivedGoals = goals.filter(g => g.status === "archived");


  if (!window.__fullsend_profile?.address) {
    return (
      <div style={{ maxWidth: 720, margin: "0 auto", padding: "80px 24px", textAlign: "center", opacity: mounted ? 1 : 0, transform: mounted ? "translateY(0)" : "translateY(20px)", transition: "all 0.5s cubic-bezier(0.16, 1, 0.3, 1)" }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
        <div style={{ fontFamily: "'Syne', sans-serif", fontSize: 20, fontWeight: 800, color: COLORS.text, marginBottom: 8 }}>Connect to Access Goals</div>
        <div style={{ fontSize: 12, color: COLORS.textMuted, lineHeight: 1.6, maxWidth: 360, margin: "0 auto" }}>
          Connect your Universal Profile to set fundraising goals for your community.
        </div>
      </div>
    );
  }

  return (
    <div style={{
      maxWidth: 680, margin: "0 auto", padding: "28px 24px",
      opacity: mounted ? 1 : 0,
      transform: mounted ? "translateY(0)" : "translateY(20px)",
      transition: "all 0.5s cubic-bezier(0.16, 1, 0.3, 1)",
    }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <div style={{
            fontFamily: "'Syne', sans-serif", fontSize: 26, fontWeight: 800,
            background: `linear-gradient(135deg, ${COLORS.textSecondary}, ${COLORS.text})`,
            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
            letterSpacing: "-0.5px",
          }}>Tip Goals</div>
          <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 5, display: "flex", alignItems: "center", gap: 8 }}>
            Set fundraising goals for your community. Shown on your widget and overlay.
            <span style={{ fontSize: 9, padding: "2px 8px", borderRadius: 4, background: COLORS.bgAlt, border: `1px solid ${COLORS.border}`, color: COLORS.textMuted }}>1 goal at a time</span>
          </div>
        </div>
        {activeGoals.some(g => g.status === "active") && !creating ? (
          <div style={{
            padding: "12px 16px", borderRadius: 8,
            background: `${COLORS.orange}08`, border: `1px solid ${COLORS.orange}20`,
            fontSize: 12, color: COLORS.orange, lineHeight: 1.5,
          }}>⚠ You have an active goal. End or archive it first before creating a new one.</div>
        ) : (
          <button onClick={() => setCreating(!creating)} style={{
            padding: "8px 18px", borderRadius: 8,
            background: creating ? COLORS.surface : `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.cyan})`,
            border: creating ? `1px solid ${COLORS.border}` : "none",
            color: creating ? COLORS.textMuted : COLORS.bg,
            fontFamily: "'Syne', sans-serif", fontSize: 12, fontWeight: 700,
            cursor: "pointer", transition: "all 0.2s",
          }}>
            {creating ? "Cancel" : "+ New Goal"}
          </button>
        )}
      </div>

      {/* Create Goal Form */}
      {creating && (
        <div style={{
          padding: "18px 20px", borderRadius: 12,
          background: COLORS.surface, border: `1px solid ${COLORS.accent}20`,
          marginBottom: 20, animation: "slideDown 0.2s ease-out",
        }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.accent, marginBottom: 12, fontFamily: "'Syne', sans-serif" }}>
            🎯 Create New Goal
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Goal title (e.g. New mic fund, 1000 LYX milestone)" style={{
              padding: "10px 14px", background: COLORS.bgAlt, border: `1px solid ${COLORS.border}`,
              borderRadius: 8, color: COLORS.text, fontFamily: "'Outfit', sans-serif", fontSize: 13, outline: "none",
            }} />
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input value={target} onChange={e => setTarget(e.target.value)} placeholder="Target amount" type="number" style={{
                flex: 1, padding: "10px 14px", background: COLORS.bgAlt, border: `1px solid ${COLORS.border}`,
                borderRadius: 8, color: COLORS.text, fontFamily: "'JetBrains Mono', monospace", fontSize: 13, outline: "none",
              }} />
              <span style={{ fontSize: 12, color: COLORS.textMuted, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace" }}>LYX</span>
            </div>
            <button onClick={createGoal} disabled={!title.trim() || !target || parseFloat(target) <= 0} style={{
              padding: "10px 0", borderRadius: 8,
              background: title.trim() && target && parseFloat(target) > 0 ? `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.cyan})` : COLORS.surface,
              border: "none", color: title.trim() && target ? COLORS.bg : COLORS.textMuted,
              fontFamily: "'Syne', sans-serif", fontSize: 13, fontWeight: 700, cursor: "pointer",
            }}>
              Create Goal
            </button>
          </div>
        </div>
      )}

      {/* Active & Paused Goals */}
      {activeGoals.length === 0 && !creating && (
        <div style={{
          textAlign: "center", padding: "48px 20px",
          background: COLORS.surface, borderRadius: 14, border: `1px solid ${COLORS.border}`,
          marginBottom: 20,
        }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🎯</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.text, marginBottom: 4 }}>No active goals</div>
          <div style={{ fontSize: 11, color: COLORS.textMuted }}>Create a tip goal to show on your widget and motivate your community</div>
        </div>
      )}

      {activeGoals.map(goal => {
        const pct = goal.target > 0 ? Math.min(100, (goal.current / goal.target) * 100) : 0;
        const isActive = goal.status === "active";
        return (
          <div key={goal.id} style={{
            padding: "16px 18px", borderRadius: 12,
            background: COLORS.surface, border: `1px solid ${isActive ? COLORS.accent + "25" : COLORS.border}`,
            marginBottom: 10, transition: "all 0.2s",
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {isActive && <div style={{ width: 8, height: 8, borderRadius: "50%", background: COLORS.accent, boxShadow: `0 0 6px ${COLORS.accent}60` }} />}
                <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.text }}>{goal.title}</div>
                {!isActive && <span style={{ fontSize: 8, padding: "2px 6px", borderRadius: 3, background: `${COLORS.orange}12`, color: COLORS.orange, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace" }}>PAUSED</span>}
              </div>
              <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.accent, fontFamily: "'JetBrains Mono', monospace" }}>
                {goal.current.toFixed(1)} / {goal.target} LYX
              </div>
            </div>

            {/* Progress bar */}
            <div style={{ height: 8, borderRadius: 4, background: COLORS.bgAlt, overflow: "hidden", marginBottom: 10 }}>
              <div style={{
                height: "100%", borderRadius: 4, width: `${pct}%`,
                background: pct >= 100 ? `linear-gradient(90deg, ${COLORS.accent}, ${COLORS.gold})` : COLORS.accent,
                transition: "width 0.5s ease-out",
              }} />
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontSize: 10, color: COLORS.textMuted }}>{pct.toFixed(0)}% complete</div>
              <div style={{ display: "flex", gap: 6 }}>
                {!isActive && (
                  <button onClick={() => activateGoal(goal.id)} style={{
                    padding: "4px 10px", borderRadius: 5, fontSize: 9, fontWeight: 700,
                    background: `${COLORS.accent}10`, border: `1px solid ${COLORS.accent}20`, color: COLORS.accent,
                    cursor: "pointer", fontFamily: "'JetBrains Mono', monospace",
                  }}>Activate</button>
                )}
                <button onClick={() => resetGoal(goal.id)} style={{
                  padding: "4px 10px", borderRadius: 5, fontSize: 9, fontWeight: 700,
                  background: `${COLORS.cyan}10`, border: `1px solid ${COLORS.cyan}20`, color: COLORS.cyan,
                  cursor: "pointer", fontFamily: "'JetBrains Mono', monospace",
                }}>Reset</button>
                {pct >= 100 && (
                  <button onClick={() => completeGoal(goal.id)} style={{
                    padding: "4px 10px", borderRadius: 5, fontSize: 9, fontWeight: 700,
                    background: `${COLORS.gold}10`, border: `1px solid ${COLORS.gold}20`, color: COLORS.gold,
                    cursor: "pointer", fontFamily: "'JetBrains Mono', monospace",
                  }}>Complete 🎉</button>
                )}
                <button onClick={() => archiveGoal(goal.id)} style={{
                  padding: "4px 10px", borderRadius: 5, fontSize: 9, fontWeight: 700,
                  background: `${COLORS.textSecondary}08`, border: `1px solid ${COLORS.textSecondary}15`, color: COLORS.textSecondary,
                  cursor: "pointer", fontFamily: "'JetBrains Mono', monospace",
                }}>Archive</button>
                <button onClick={() => { if (confirm(`Delete goal "${goal.title}"? This cannot be undone.`)) deleteGoal(goal.id); }} style={{
                  padding: "4px 10px", borderRadius: 5, fontSize: 9, fontWeight: 700,
                  background: `${COLORS.red}08`, border: `1px solid ${COLORS.red}18`, color: COLORS.red,
                  cursor: "pointer", fontFamily: "'JetBrains Mono', monospace",
                }}>Delete</button>
              </div>
            </div>
          </div>
        );
      })}

      {/* Completed Goals */}
      {completedGoals.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <div style={{
            fontSize: 12, fontWeight: 700, color: COLORS.gold,
            fontFamily: "'Syne', sans-serif", marginBottom: 10,
            display: "flex", alignItems: "center", gap: 6,
          }}>
            <span>🏆</span> Completed ({completedGoals.length})
          </div>
          {completedGoals.map(goal => (
            <div key={goal.id} style={{
              padding: "12px 16px", borderRadius: 10,
              background: `${COLORS.gold}06`, border: `1px solid ${COLORS.gold}15`,
              marginBottom: 6, display: "flex", alignItems: "center", justifyContent: "space-between",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 14 }}>✅</span>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.text }}>{goal.title}</div>
                  <div style={{ fontSize: 9, color: COLORS.textMuted }}>{goal.target} LYX · Completed {goal.completedAt ? new Date(goal.completedAt).toLocaleDateString() : ""}</div>
                </div>
              </div>
              <div style={{ display: "flex", gap: 4 }}>
                <button onClick={() => archiveGoal(goal.id)} style={{
                  padding: "3px 8px", borderRadius: 4, fontSize: 8, fontWeight: 600,
                  background: COLORS.bgAlt, border: `1px solid ${COLORS.border}`, color: COLORS.textMuted,
                  cursor: "pointer",
                }}>Archive</button>
                <button onClick={() => { if (confirm(`Delete goal "${goal.title}"? This cannot be undone.`)) deleteGoal(goal.id); }} style={{
                  padding: "3px 8px", borderRadius: 4, fontSize: 8, fontWeight: 600,
                  background: `${COLORS.red}08`, border: `1px solid ${COLORS.red}18`, color: COLORS.red,
                  cursor: "pointer",
                }}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Archived Goals */}
      {archivedGoals.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <button onClick={() => setShowArchived(!showArchived)} style={{
            fontSize: 11, fontWeight: 600, color: COLORS.textMuted, cursor: "pointer",
            background: "none", border: "none", padding: 0, display: "flex", alignItems: "center", gap: 6,
          }}>
            <span>📁</span> Archived ({archivedGoals.length}) <span style={{ fontSize: 8, transform: showArchived ? "rotate(180deg)" : "rotate(0)", transition: "transform 0.2s" }}>▼</span>
          </button>
          {showArchived && archivedGoals.map(goal => (
            <div key={goal.id} style={{
              padding: "10px 14px", borderRadius: 8,
              background: COLORS.surface, border: `1px solid ${COLORS.border}`,
              marginTop: 6, display: "flex", alignItems: "center", justifyContent: "space-between",
              opacity: 0.6,
            }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMuted }}>{goal.title}</div>
                <div style={{ fontSize: 9, color: COLORS.textMuted }}>{goal.current?.toFixed(1) || 0} / {goal.target} LYX</div>
              </div>
              <div style={{ display: "flex", gap: 4 }}>
                <button onClick={() => activateGoal(goal.id)} style={{
                  padding: "3px 8px", borderRadius: 4, fontSize: 8, fontWeight: 600,
                  background: `${COLORS.accent}08`, border: `1px solid ${COLORS.accent}15`, color: COLORS.accent,
                  cursor: "pointer",
                }}>Restore</button>
                <button onClick={() => deleteGoal(goal.id)} style={{
                  padding: "3px 8px", borderRadius: 4, fontSize: 8, fontWeight: 600,
                  background: `${COLORS.red}06`, border: `1px solid ${COLORS.red}12`, color: COLORS.red,
                  cursor: "pointer",
                }}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Info */}
      <div style={{
        marginTop: 24, padding: "12px 16px", borderRadius: 10,
        background: COLORS.surface, border: `1px solid ${COLORS.border}`,
      }}>
        <div style={{ fontSize: 10, color: COLORS.textMuted, lineHeight: 1.6 }}>
          <span style={{ fontWeight: 700, color: COLORS.textSecondary }}>How goals work:</span> The active goal shows on your tip widget and stream overlay as a progress bar. When viewers tip, the progress updates automatically. Only one goal can be active at a time — creating or activating a new goal pauses the previous one. Completed goals are saved in your history.
        </div>
      </div>
    </div>
  );
}

function formatPollTimer(seconds) {
  if (!seconds || seconds <= 0) return "0:00";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function PollManager() {
  const [mounted, setMounted] = useState(false);
  const [polls, _setPolls] = useState(() => window.__fullsend_polls_cache || []);
  const setPolls = (updater) => {
    _setPolls(prev => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      window.__fullsend_polls_cache = next;
      return next;
    });
  };
  const [pollsLoading, setPollsLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [question, setQuestion] = useState("");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [options, setOptions] = useState(["", ""]);
  const [optionImages, setOptionImages] = useState(["", ""]); // per-option image/GIF URLs
  const [pollHeaderImage, setPollHeaderImage] = useState(""); // main poll image/GIF
  const [giphySearch, setGiphySearch] = useState("");
  const [giphyResults, setGiphyResults] = useState([]);
  const [giphyTarget, setGiphyTarget] = useState(null); // "header" | index number
  const [showGiphyPicker, setShowGiphyPicker] = useState(false);
  const [gateType, setGateType] = useState("free"); // "free" | "lyx" | "nft"
  const [lyxCost, setLyxCost] = useState("1");
  const [nftRequirements, setNftRequirements] = useState([]); // array of selected NFT/token objects (max 5)
  const [pollDuration, setPollDuration] = useState(null); // null = no timer, or seconds
  const [showPollLive, setShowPollLive] = useState(false);
  const [followersOnly, setFollowersOnly] = useState(false);
  const [customDurationInput, setCustomDurationInput] = useState("");
  const [showArchivedPolls, setShowArchivedPolls] = useState(false);
  const [nftSearch, setNftSearch] = useState("");
  const [nftManualAddress, setNftManualAddress] = useState(false);
  const [nftCustomAddress, setNftCustomAddress] = useState("");

  // Track profile address — re-triggers poll fetch when profile loads after mount
  const [profileAddress, setProfileAddress] = useState(() => window.__fullsend_profile?.address || null);
  useEffect(() => {
    if (profileAddress) return; // already have it
    const check = setInterval(() => {
      const addr = window.__fullsend_profile?.address;
      if (addr) { setProfileAddress(addr); clearInterval(check); }
    }, 500);
    return () => clearInterval(check);
  }, [profileAddress]);

  // Assets in creator's UP — read from LSP5 ReceivedAssets on-chain
  // V2 TODO: Add sponsored NFTs/Tokens section here

  // GIF search via Klipy API (Tenor v1 shut down June 2026)
  const searchGiphy = async (query) => {
    if (!query.trim()) { setGiphyResults([]); return; }
    setGiphyResults(await klipyGifSearch(query, 12));
  };

  const selectGif = (url) => {
    if (giphyTarget === "header") {
      setPollHeaderImage(url);
    } else if (typeof giphyTarget === "number") {
      setOptionImages(prev => { const u = [...prev]; u[giphyTarget] = url; return u; });
    }
    setShowGiphyPicker(false);
    setGiphySearch("");
    setGiphyResults([]);
    setGiphyTarget(null);
  };

  const openGiphyPicker = (target) => {
    setGiphyTarget(target);
    setShowGiphyPicker(true);
    setGiphySearch("");
    setGiphyResults([]);
  };

  const [profileAssets, setProfileAssets] = useState([]);
  const [assetsLoading, setAssetsLoading] = useState(true);

  useEffect(() => {
    const loadAssets = async () => {
      // Auto-browse disabled: mainnet UPs carry hundreds of unnamed/spam received
      // assets and a 50-cap made it unreliable. Token-gating is paste-only now
      // (fetchTokenMeta resolves name/type/balance/image on paste). The proper
      // browsable token list comes with the SigmaCore indexer (V1.5).
      setProfileAssets([]);
      setAssetsLoading(false);
      return;
      // eslint-disable-next-line no-unreachable
      const addr = window.__fullsend_profile?.address;
      if (!addr) { setAssetsLoading(false); return; }

      // Read on-chain via the mainnet RPC directly (same reliable path as the
      // profile fetcher) instead of the injected wallet provider, whose eth_call
      // for getData returns nothing on mainnet — that's why holdings stopped
      // loading after the testnet→mainnet move.
      const rpc = LUKSO_TESTNET.rpcUrls[0]; // NB: misnamed const — points at mainnet
      const rpcCall = async (to, data, retry = true) => {
        try {
          const resp = await fetch(rpc, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ jsonrpc: "2.0", id: 1, method: "eth_call", params: [{ to, data }, "latest"] }),
          });
          const json = await resp.json();
          if (json && "result" in json) return json.result;
          // No result field = RPC error / rate-limit — retry once after a beat.
          if (retry) { await new Promise(r => setTimeout(r, 200)); return rpcCall(to, data, false); }
          return null;
        } catch {
          if (retry) { await new Promise(r => setTimeout(r, 200)); return rpcCall(to, data, false); }
          return null;
        }
      };

      // ERC725Y getData(bytes32) returns ABI-encoded bytes: word0 = offset,
      // word@offset = byte-length, then the value. Decode to the raw value hex.
      // (The loader was running parseInt on the whole blob, so the array length
      // came out as ~4e155 and the >200 guard aborted the load every time.)
      const decodeERC725Bytes = (hex) => {
        if (!hex || hex === "0x") return "";
        const h = hex.slice(2);
        if (h.length < 128) return h;
        const off = parseInt(h.slice(0, 64), 16) * 2;
        if (!Number.isFinite(off) || off + 64 > h.length) return "";
        const blen = parseInt(h.slice(off, off + 64), 16);
        if (blen > 0 && blen <= 256 && off + 64 + blen * 2 <= h.length) {
          return h.slice(off + 64, off + 64 + blen * 2);
        }
        return "";
      };

      try {
        // Read LSP5ReceivedAssets[] array length
        const arrayLengthKey = "0x6460ee3c0aac563ccbf76d6e1d07bada78e3a9514e6382b736ed3f478ab7b90b";
        const lengthResult = await rpcCall(addr, "0x54f6127f" + arrayLengthKey.slice(2));
        const lenHex = decodeERC725Bytes(lengthResult);
        const count = lenHex ? parseInt(lenHex, 16) : 0;
        if (!count || count > 500) { setAssetsLoading(false); return; }

        const assets = [];
        // Read each asset address from LSP5ReceivedAssets[i]
        for (let i = 0; i < Math.min(count, 50); i++) {
          try {
            // Array element key = first 16 bytes of array key + uint128 index
            const indexHex = i.toString(16).padStart(32, "0");
            const elementKey = "0x6460ee3c0aac563ccbf76d6e1d07bada" + indexHex;
            const assetResult = await rpcCall(addr, "0x54f6127f" + elementKey.slice(2));
            const elemHex = decodeERC725Bytes(assetResult);
            if (elemHex && elemHex.length >= 40) {
              const assetAddr = "0x" + elemHex.slice(0, 40);
              if (assetAddr !== "0x0000000000000000000000000000000000000000") {
                // Try to read name from the asset contract (LSP4 metadata)
                let name = assetAddr.slice(0, 6) + "..." + assetAddr.slice(-4);
                let standard = "LSP8";
                try {
                  // LSP4 name key: 0xdeba1e292f8ba88238e10ab3c7f88bd4be4fac56cad5194b6eccefc1c8558531
                  const nameResult = await rpcCall(assetAddr, "0x54f6127fdeba1e292f8ba88238e10ab3c7f88bd4be4fac56cad5194b6eccefc1c8558531");
                  const nameHex = decodeERC725Bytes(nameResult);
                  if (nameHex) {
                    const decoded = nameHex.match(/.{2}/g).map(h => String.fromCharCode(parseInt(h, 16))).join("").replace(/[^\x20-\x7E]/g, "").trim();
                    if (decoded) name = decoded;
                  }
                } catch {}
                assets.push({
                  address: assetAddr,
                  name: name || assetAddr.slice(0, 10) + "...",
                  icon: "🖼️",
                  color: COLORS.purple,
                  type: "NFT",
                  standard,
                });
              }
            }
          } catch { /* skip individual asset read failures */ }
        }
        setProfileAssets(assets);
      } catch (e) {
        console.log("LSP5 asset read (non-fatal):", e.message);
      }
      setAssetsLoading(false);
    };
    loadAssets();
  }, [profileAddress]);

  // No hardcoded network assets — users paste contract addresses directly
  const networkAssets = [];

  const filterFn = (n) =>
    n.name.toLowerCase().includes(nftSearch.toLowerCase()) ||
    n.address.toLowerCase().includes(nftSearch.toLowerCase()) ||
    (n.symbol && n.symbol.toLowerCase().includes(nftSearch.toLowerCase()));

  const filteredProfile = (nftSearch.trim() ? profileAssets.filter(filterFn) : profileAssets)
    .filter(n => !nftRequirements.some(r => r.address === n.address));
  const filteredNetwork = (nftSearch.trim() ? networkAssets.filter(filterFn) : networkAssets)
    .filter(n => !nftRequirements.some(r => r.address === n.address));

  // Pull a contract address out of whatever's pasted — a bare 0x… address OR a
  // full Universal Everything link (…/asset/0x…?network=mainnet). So pasting the
  // token's page URL works, not just the raw address.
  const pastedAddr = (nftSearch.match(/0x[a-fA-F0-9]{40}/) || [])[0] || "";

  useEffect(() => { requestAnimationFrame(() => setMounted(true)); }, []);

  useEffect(() => {
    const handler = () => { setCreating(false); setShowGiphyPicker(false); setShowArchivedPolls(false); };
    window.addEventListener("fullsend-tab-reset", handler);
    return () => window.removeEventListener("fullsend-tab-reset", handler);
  }, []);

  // Local overrides — these take priority over DB status to prevent race conditions
  const [localOverrides, setLocalOverrides] = useState(() => {
    try { return JSON.parse(localStorage.getItem("fullsend_poll_overrides") || "{}"); } catch { return {}; }
  });
  const localOverridesRef = useRef(localOverrides);
  useEffect(() => {
    localOverridesRef.current = localOverrides;
    window.__fullsend_poll_overrides = localOverrides;
    try { localStorage.setItem("fullsend_poll_overrides", JSON.stringify(localOverrides)); } catch {}
  }, [localOverrides]);

  // Fetch polls — DB first (fast, instant display), contract updates votes in background
  useEffect(() => {
    const optionColors = [COLORS.accent, COLORS.blue, COLORS.purple, COLORS.orange, COLORS.cyan, COLORS.gold];
    const creatorAddress = profileAddress;
    if (!creatorAddress) { setPollsLoading(false); return; }

    // Image cache — persists across all refresh cycles
    const getImageCache = () => {
      try { return JSON.parse(localStorage.getItem("fullsend_poll_images") || "{}"); } catch { return {}; }
    };
    const saveImageCache = (question, headerImage, optionImages) => {
      if (!headerImage && (!optionImages || optionImages.length === 0 || optionImages.every(i => !i))) return;
      try {
        const cache = getImageCache();
        cache[question] = { headerImage, optionImages };
        localStorage.setItem("fullsend_poll_images", JSON.stringify(cache));
      } catch {}
    };

    const mapPoll = (p, source) => {
      const imgCache = getImageCache();
      const cached = imgCache[p.question] || {};
      const endTimeNum = source === "db" ? Number(p.endTime || 0) : (p.endTime || 0);
      const remaining = endTimeNum > 0 ? Math.max(0, endTimeNum - Math.floor(Date.now() / 1000)) : null;
      return {
        id: p.id,
        question: p.question,
        options: (p.options || []).map((opt, i) => ({
          text: opt.text, votes: opt.votes || 0,
          color: optionColors[i % optionColors.length],
        })),
        gateType: source === "db" 
          ? (parseInt(p.gateType) === 0 ? "free" : parseInt(p.gateType) === 1 ? "lyx" : "nft")
          : (parseInt(p.gateType) === 0 ? "free" : parseInt(p.gateType) === 1 ? "lyx" : "nft"),
        lyxCost: parseInt(p.gateType) === 1 ? Number(p.votePrice || 0) / 1e18 : 0,
        status: localOverridesRef.current[p.id] || (remaining === 0 && endTimeNum > 0 ? "ended" : p.status) || "ended",
        totalVotes: p.totalVotes || 0,
        endTime: endTimeNum,
        timeRemaining: remaining,
        duration: remaining, // for progress bar
        createdAt: p.endTime ? new Date(((source === "db" ? Number(p.endTime) : p.endTime) - 3600) * 1000) : (p.createdAt ? new Date(p.createdAt) : new Date()),
        onChain: true,
        onchainId: p.onchainId || null,
        headerImage: p.headerImage || cached.headerImage || null,
        optionImages: (p.optionImages?.length > 0 && p.optionImages.some(i => i)) ? p.optionImages : (cached.optionImages || []),
      };
    };

    // 1. DB fetch — instant display, merges with local
    const loadFromDB = async () => {
      try {
        const dbRes = await fetch(`/api/data-polls?creator=${creatorAddress}`);
        const dbData = await dbRes.json();
        if (dbData.success && dbData.polls && dbData.polls.length > 0) {
          const dbMapped = dbData.polls
            .map(p => mapPoll(p, "db"))
            .filter(p => p.status !== "deleted");
          
          // Merge: DB polls + any local-only polls (not yet in DB)
          setPolls(prev => {
            const dbByQ = {};
            for (const p of dbMapped) dbByQ[p.question] = p;
            
            // Keep local polls that aren't in DB yet, preserve their images
            const localOnly = prev.filter(p => !dbByQ[p.question]);
            
            // For DB polls, preserve images from local if DB doesn't have them
            const merged = dbMapped.map(dbP => {
              const localP = prev.find(p => p.question === dbP.question);
              if (localP) {
                dbP.headerImage = dbP.headerImage || localP.headerImage;
                dbP.optionImages = (dbP.optionImages?.length > 0) ? dbP.optionImages : localP.optionImages;
              }
              return dbP;
            });
            
            return [...localOnly, ...merged];
          });
          
          const active = dbMapped.find(p => p.status === "active");
          if (active) {
            window.__fullsend_active_poll = active;
            const currentOverlayPoll = window.__fullsend_overlay_state?.poll;
            // If overlay already shows this exact poll → just refresh vote counts.
            // If overlay shows a DIFFERENT poll → replace wholesale (stale poll cleanup).
            // If overlay has no poll → don't push (hide from stream was intentional).
            if (currentOverlayPoll) {
              const sameId = String(currentOverlayPoll.id) === String(active.id);
              const newPoll = sameId
                ? { ...currentOverlayPoll, options: active.options, totalVotes: active.totalVotes }
                : { ...active };
              window.__fullsend_overlay_state = { ...window.__fullsend_overlay_state, poll: newPoll };
              syncCreatorToDB({ _overlayState: window.__fullsend_overlay_state });
            }
          }
          setPollsLoading(false);
          return true;
        }
      } catch (e) { /* non-fatal */ }
      return false;
    };

    // 2. DB vote refresh — uses indexed vote events for fast vote counts (no RPC)
    const refreshVoteCounts = async () => {
      try {
        const res = await fetch(`/api/data-polls?creator=${creatorAddress}&votes=true`);
        const data = await res.json();
        if (data.success && data.polls) {
          // Merge with existing: preserve images, update votes from indexed DB
          setPolls(prev => {
            const prevById = {};
            const prevByQ = {};
            for (const p of prev) {
              prevById[p.id] = p;
              prevByQ[p.question] = p;
            }

            const merged = data.polls.map(p => {
              const existing = prevById[p.id] || prevByQ[p.question];
              const mapped = mapPoll(p, "db");
              // Preserve images from local if DB doesn't have them
              if (existing) {
                mapped.headerImage = existing.headerImage || mapped.headerImage;
                mapped.optionImages = (existing.optionImages?.length > 0) ? existing.optionImages : mapped.optionImages;
              }
              return mapped;
            }).filter(p => p.status !== "deleted");

            const active = merged.find(p => p.status === "active");
            if (active) {
              window.__fullsend_active_poll = active;
              // Push updated votes to OBS overlay — if overlay poll id mismatches active, replace wholesale
              const currentOverlayPoll = window.__fullsend_overlay_state?.poll;
              if (currentOverlayPoll) {
                const sameId = String(currentOverlayPoll.id) === String(active.id);
                const newPoll = sameId
                  ? { ...currentOverlayPoll, options: active.options, totalVotes: active.totalVotes }
                  : { ...active };
                window.__fullsend_overlay_state = { ...window.__fullsend_overlay_state, poll: newPoll };
                syncCreatorToDB({ _overlayState: window.__fullsend_overlay_state });
              }
            }
            return merged;
          });
          setPollsLoading(false);
        }
      } catch (err) {
        console.log("Poll vote refresh (non-fatal):", err);
        setPollsLoading(false);
      }
    };

    // Load DB first (one-time initial paint), then keep vote counts fresh.
    loadFromDB().then(hadData => {
      if (!hadData) setPollsLoading(true);
      refreshVoteCounts();
    });

    // Only refreshVoteCounts repeats — it fetches the full poll list WITH accurate
    // counts from the indexed votes table. Running loadFromDB on an interval too made
    // the % flicker, because it pulls vote totals from the (stale/zero) polls table
    // and the two loops alternated. One repeating updater = stable counts.
    const voteInterval = setInterval(refreshVoteCounts, 15000);
    return () => { clearInterval(voteInterval); };
  }, [profileAddress]);

  const addOption = () => {
    if (options.length < 6) setOptions([...options, ""]);
  };

  const removeOption = (idx) => {
    if (options.length > 2) setOptions(options.filter((_, i) => i !== idx));
  };

  const updateOption = (idx, val) => {
    setOptions(options.map((o, i) => i === idx ? val : o));
  };

  const createPoll = async () => {
    if (!question.trim() || options.filter(o => o.trim()).length < 2) return;
    const optionColors = [COLORS.accent, COLORS.blue, COLORS.purple, COLORS.orange, COLORS.cyan, COLORS.gold];
    const filteredOptions = options.filter(o => o.trim());
    const creatorAddress = window.__fullsend_profile?.address || "0x8eF515A8B0d119fc0A331Cb847D9E52B1892f3aa";

    const newPoll = {
      id: Date.now(),
      question: question.trim(),
      options: filteredOptions.map((text, i) => ({
        text: text.trim(),
        votes: 0,
        color: optionColors[i % optionColors.length],
      })),
      gateType,
      lyxCost: gateType === "lyx" ? parseFloat(lyxCost) : 0,
      nftRequirement: gateType === "nft" ? nftRequirements : null,
      status: "active",
      totalVotes: 0,
      createdAt: new Date(),
      duration: pollDuration || 86400,
      timeRemaining: pollDuration || 86400,
      showLive: showPollLive,
      followersOnly: followersOnly,
      headerImage: pollHeaderImage || null,
      optionImages: optionImages.filter((_, i) => i < options.length),
    };

    // Auto-end any currently active poll
    setPolls(prev => prev.map(p => p.status === "active" ? { ...p, status: "ended", timeRemaining: null } : p));
    // Cache images in localStorage (survives all refresh cycles)
    if (newPoll.headerImage || newPoll.optionImages?.some(i => i)) {
      try {
        const cache = JSON.parse(localStorage.getItem("fullsend_poll_images") || "{}");
        cache[newPoll.question] = { headerImage: newPoll.headerImage, optionImages: newPoll.optionImages };
        localStorage.setItem("fullsend_poll_images", JSON.stringify(cache));
      } catch {}
    }
    // Persist to global + broadcast to widget
    window.__fullsend_active_poll = newPoll;
    window.dispatchEvent(new CustomEvent("fullsend-poll", { detail: newPoll }));
    if (newPoll.showLive) {
      window.dispatchEvent(new CustomEvent("fullsend-poll-live", { detail: newPoll }));
    }
    setPolls(prev => [newPoll, ...prev]);

    // Save poll to DB — MANDATORY. If this fails, roll back local state so we
    // don't end up with a "ghost" poll that Control/overlay show but widget can't.
    try {
      const saveRes = await fetch("/api/data-polls", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "sync",
          creator: creatorAddress,
          polls: [{
            id: newPoll.id,
            question: newPoll.question,
            options: newPoll.options,
            gateType: gateType === "free" ? 0 : gateType === "lyx" ? 1 : 2,
            votePrice: gateType === "lyx" ? (parseFloat(lyxCost) * 1e18).toString() : "0",
            totalVotes: 0,
            active: true,
            endTime: Math.floor(Date.now() / 1000) + (newPoll.duration || 86400),
            headerImage: newPoll.headerImage || null,
            optionImages: newPoll.optionImages || [],
            gateTokens: gateType === "nft" ? nftRequirements.map(r => ({ address: r.address, minAmount: String(r.minAmount || "1"), name: r.name || r.symbol || "Token" })) : [],
          }],
        }),
      });
      if (!saveRes.ok) throw new Error(`DB save failed: ${saveRes.status}`);
    } catch (e) {
      console.error("Poll DB save failed — rolling back:", e);
      // Roll back optimistic local state
      window.__fullsend_active_poll = null;
      window.dispatchEvent(new CustomEvent("fullsend-poll", { detail: null }));
      setPolls(prev => prev.filter(p => p.id !== newPoll.id));
      alert("Poll could not be saved (network or server error). Please try again.");
      setCreating(false);
      return;
    }

    // Always sync poll to overlay state (standalone reads it from creator-register)
    window.__fullsend_overlay_state = {
      ...(window.__fullsend_overlay_state || {}),
      poll: newPoll,
      visibility: {
        ...(window.__fullsend_overlay_state?.visibility || {}),
        ...(newPoll.showLive ? { polls: true } : {}),
      },
    };
    syncCreatorToDB({ _overlayState: window.__fullsend_overlay_state });
    setCreating(false);
    setQuestion("");
    setOptions(["", ""]);
    setGateType("free");
    setPollDuration(null);
    setShowPollLive(false);
    setFollowersOnly(false);
    setPollHeaderImage("");
    setOptionImages(["", ""]);
    setShowGiphyPicker(false);
    setNftRequirements([]);
    setNftSearch("");
    setNftManualAddress(false);
    setNftCustomAddress("");

    // Send to relay API (non-blocking — UI updates instantly)
    try {
      const gateTypeNum = gateType === "free" ? 0 : gateType === "lyx" ? 1 : 2;
      const tokenAddr = gateType === "nft" && nftRequirements.length > 0 ? nftRequirements[0].address : "0x0000000000000000000000000000000000000000";
      const tokenMin = gateType === "nft" && nftRequirements.length > 0 ? (nftRequirements[0].minAmount || "1") : "0";
      const votePrice = gateType === "lyx" ? lyxToWei(lyxCost) : "0";
      const duration = pollDuration || 86400; // manual = 24h max (contract limit)

      const response = await fetch("/api/relay-action", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "createPoll",
          creator: creatorAddress,
          question: question.trim() || newPoll.question,
          options: filteredOptions.map(o => o.trim()),
          gateType: gateTypeNum,
          votePrice,
          tokenAddress: tokenAddr,
          tokenMinBalance: tokenMin,
          duration,
        }),
      });
      const result = await response.json();
      if (result.success) {
        console.log("Poll created on-chain:", result.txHash);
        setPolls(prev => prev.map(p => p.id === newPoll.id ? { ...p, onChainTxHash: result.txHash } : p));

        // Fast path: relay-action now returns the on-chain poll id directly (from the
        // PollCreated event). Use it immediately so the poll unlocks for voting at once.
        if (result.onchainId) {
          const onchainId = result.onchainId;
          console.log("On-chain poll ID (from relay):", onchainId);
          setPolls(prev => prev.map(p => p.id === newPoll.id ? { ...p, onchainId } : p));
          fetch("/api/data-polls", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ action: "updateOnchainId", pollId: newPoll.id, onchainId }),
          }).catch(() => {});
        } else {
        // Fallback: resolve on-chain poll ID client-side (retry up to 5 times, 3s apart)
        (async () => {
          for (let attempt = 0; attempt < 5; attempt++) {
            await new Promise(r => setTimeout(r, 3000));
            try {
              const pcRes = await fetch("/api/data-polls?pollCount=true");
              const pcData = await pcRes.json();
              if (pcData.success && pcData.pollCount) {
                const onchainId = pcData.pollCount;
                console.log("Resolved on-chain poll ID:", onchainId);
                setPolls(prev => prev.map(p => p.id === newPoll.id ? { ...p, onchainId } : p));
                fetch("/api/data-polls", {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ action: "updateOnchainId", pollId: newPoll.id, onchainId }),
                }).catch(() => {});
                break;
              }
            } catch (e) {
              console.log(`pollCount resolve attempt ${attempt + 1}/5 failed`);
            }
          }
        })();
        }
      } else {
        console.error("Poll relay failed:", result.error);
        // Even if relay returned error, try to resolve on-chain ID (tx may have succeeded)
        (async () => {
          for (let attempt = 0; attempt < 5; attempt++) {
            await new Promise(r => setTimeout(r, 3000));
            try {
              const pcRes = await fetch("/api/data-polls?pollCount=true");
              const pcData = await pcRes.json();
              if (pcData.success && pcData.pollCount) {
                const onchainId = pcData.pollCount;
                console.log("Resolved on-chain poll ID (after relay error):", onchainId);
                setPolls(prev => prev.map(p => p.id === newPoll.id ? { ...p, onchainId } : p));
                fetch("/api/data-polls", {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ action: "updateOnchainId", pollId: newPoll.id, onchainId }),
                }).catch(() => {});
                break;
              }
            } catch (e) {
              console.log(`pollCount resolve attempt ${attempt + 1}/5 failed`);
            }
          }
        })();
      }
    } catch (err) {
      console.error("Poll relay error (non-fatal, poll still visible locally):", err);
    }
  };

  const endPoll = async (id) => {
    setPolls(prev => prev.map(p => p.id === id ? { ...p, status: "ended", timeRemaining: null } : p));
    
    // Clear active poll global
    if (window.__fullsend_active_poll?.id === id) {
      window.__fullsend_active_poll = null;
    }
    // Remove from overlay (live event + persisted DB state so overlay re-render from DB clears it too)
    window.dispatchEvent(new CustomEvent("fullsend-poll-remove", {}));
    if (window.__fullsend_overlay_state) {
      window.__fullsend_overlay_state = { ...window.__fullsend_overlay_state, poll: null };
    }
    try {
      const creatorAddr = window.__fullsend_profile?.address;
      if (creatorAddr) {
        fetch("/api/creator-register", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ address: creatorAddr.toLowerCase(), platforms_merge: { _overlayState: { poll: null } } }),
        }).catch(() => {});
      }
    } catch {}

    // Update DB status to ended
    fetch("/api/data-polls", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "sync", creator: window.__fullsend_profile?.address, polls: [{ id, question: polls.find(p => p.id === id)?.question || "", options: polls.find(p => p.id === id)?.options || [], active: false, endTime: Math.floor(Date.now() / 1000), totalVotes: polls.find(p => p.id === id)?.totalVotes || 0 }] }),
    }).catch(() => {});

    // End on-chain via relay (non-blocking) — use onchainId
    try {
      const creatorAddress = window.__fullsend_profile?.address;
      const poll = polls.find(p => p.id === id);
      const chainPollId = poll?.onchainId;
      if (creatorAddress && chainPollId) {
        const response = await fetch("/api/relay-action", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            action: "endPoll",
            pollId: chainPollId,
            creator: creatorAddress,
          }),
        });
        const result = await response.json();
        if (result.success) {
          console.log("Poll ended on-chain:", result.txHash);
        }
      } else if (!chainPollId) {
        console.log("No on-chain poll ID — poll ended in DB only");
      }
    } catch (err) {
      console.error("End poll relay error (non-fatal):", err);
    }
  };

  const archivePoll = (id) => {
    const wasActive = window.__fullsend_active_poll?.id === id;
    setPolls(prev => prev.map(p => p.id === id ? { ...p, status: "archived" } : p));
    setLocalOverrides(prev => ({ ...prev, [id]: "archived" }));
    // Clear active poll global if this was the active one
    if (wasActive) {
      window.__fullsend_active_poll = null;
      if (window.__fullsend_overlay_state) window.__fullsend_overlay_state = { ...window.__fullsend_overlay_state, poll: null };
      const creatorAddr = window.__fullsend_profile?.address;
      if (creatorAddr) fetch("/api/creator-register", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ address: creatorAddr.toLowerCase(), platforms_merge: { _overlayState: { poll: null } } }) }).catch(() => {});
    }
    // Broadcast to overlay and other components
    window.__fullsend_poll_overrides = { ...(window.__fullsend_poll_overrides || {}), [id]: "archived" };
    window.dispatchEvent(new CustomEvent("fullsend-poll-status", { detail: { id, status: "archived" } }));
    fetch("/api/data-polls", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "archive", pollId: id }),
    }).catch(() => {});
  };

  const unarchivePoll = (id) => {
    setPolls(prev => prev.map(p => p.id === id ? { ...p, status: "ended" } : p));
    setLocalOverrides(prev => { const n = { ...prev }; delete n[id]; return n; });
    const overrides = { ...(window.__fullsend_poll_overrides || {}) };
    delete overrides[id];
    window.__fullsend_poll_overrides = overrides;
    window.dispatchEvent(new CustomEvent("fullsend-poll-status", { detail: { id, status: "ended" } }));
    fetch("/api/data-polls", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "unarchive", pollId: id }),
    }).catch(() => {});
  };

  const deletePoll = (id) => {
    const wasActive = window.__fullsend_active_poll?.id === id;
    setPolls(prev => prev.filter(p => p.id !== id));
    setLocalOverrides(prev => ({ ...prev, [id]: "deleted" }));
    window.__fullsend_poll_overrides = { ...(window.__fullsend_poll_overrides || {}), [id]: "deleted" };
    // Clear active poll global if this was the active one
    if (wasActive) {
      window.__fullsend_active_poll = null;
      if (window.__fullsend_overlay_state) window.__fullsend_overlay_state = { ...window.__fullsend_overlay_state, poll: null };
      const creatorAddr = window.__fullsend_profile?.address;
      if (creatorAddr) fetch("/api/creator-register", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ address: creatorAddr.toLowerCase(), platforms_merge: { _overlayState: { poll: null } } }) }).catch(() => {});
    }
    window.dispatchEvent(new CustomEvent("fullsend-poll-remove", {}));
    window.dispatchEvent(new CustomEvent("fullsend-poll-status", { detail: { id, status: "deleted" } }));
    fetch("/api/data-polls", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "delete", pollId: id }),
    }).catch(() => {});
  };

  const activePollsList = polls.filter(p => p.status === "active" || p.status === "ended");
  const archivedPollsList = polls.filter(p => p.status === "archived");

  // Countdown timer for timed polls
  useEffect(() => {
    const hasTimedPolls = polls.some(p => p.status === "active" && p.timeRemaining > 0);
    if (!hasTimedPolls) return;
    const interval = setInterval(() => {
      setPolls(prev => prev.map(p => {
        if (p.status !== "active" || !p.timeRemaining) return p;
        if (p.timeRemaining <= 1) {
          // Time's up -- end poll and broadcast results to overlay
          window.dispatchEvent(new CustomEvent("fullsend-poll-results", { detail: { ...p, status: "ended", timeRemaining: null } }));
          return { ...p, status: "ended", timeRemaining: null };
        }
        return { ...p, timeRemaining: p.timeRemaining - 1 };
      }));
    }, 1000);
    return () => clearInterval(interval);
  }, [polls]);

  const simulateVotes = (pollId) => {
    setPolls(prev => prev.map(p => {
      if (p.id !== pollId || p.status !== "active") return p;
      const updated = { ...p, options: p.options.map(o => ({
        ...o, votes: o.votes + Math.floor(Math.random() * 8),
      }))};
      updated.totalVotes = updated.options.reduce((s, o) => s + o.votes, 0);
      return updated;
    }));
  };

  if (!window.__fullsend_profile?.address) {
    return (
      <div style={{ maxWidth: 720, margin: "0 auto", padding: "80px 24px", textAlign: "center", opacity: mounted ? 1 : 0, transform: mounted ? "translateY(0)" : "translateY(20px)", transition: "all 0.5s cubic-bezier(0.16, 1, 0.3, 1)" }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
        <div style={{ fontFamily: "'Syne', sans-serif", fontSize: 20, fontWeight: 800, color: COLORS.text, marginBottom: 8 }}>Connect to Access Polls</div>
        <div style={{ fontSize: 12, color: COLORS.textMuted, lineHeight: 1.6, maxWidth: 360, margin: "0 auto" }}>
          Connect your Universal Profile to create and manage polls for your stream.
        </div>
      </div>
    );
  }

  return (
    <div style={{
      maxWidth: 680, margin: "0 auto", padding: "28px 24px",
      opacity: mounted ? 1 : 0,
      transform: mounted ? "translateY(0)" : "translateY(15px)",
      transition: "all 0.5s cubic-bezier(0.16, 1, 0.3, 1)",
      position: "relative",
    }}>
      {/* Giphy Picker Modal */}
      {showGiphyPicker && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(0,0,0,0.7)", zIndex: 9999,
          display: "flex", alignItems: "center", justifyContent: "center",
          animation: "fadeInUp 0.2s ease-out",
        }} onClick={() => setShowGiphyPicker(false)}>
          <div onClick={e => e.stopPropagation()} style={{
            width: 420, maxHeight: "80vh", background: COLORS.surface,
            border: `1px solid ${COLORS.border}`, borderRadius: 14,
            overflow: "hidden", display: "flex", flexDirection: "column",
          }}>
            <div style={{ padding: "14px 16px", borderBottom: `1px solid ${COLORS.border}`, display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: COLORS.text, fontFamily: "'Syne', sans-serif" }}>🎞️ Search GIFs</span>
              <span style={{ flex: 1 }} />
              <button onClick={() => setShowGiphyPicker(false)} style={{
                width: 24, height: 24, borderRadius: "50%", background: COLORS.bgAlt,
                border: `1px solid ${COLORS.border}`, color: COLORS.textMuted, fontSize: 12,
                cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
              }}>×</button>
            </div>
            <div style={{ padding: "10px 16px" }}>
              <input
                value={giphySearch}
                onChange={e => { setGiphySearch(e.target.value); clearTimeout(window.__giphyTimer); window.__giphyTimer = setTimeout(() => searchGiphy(e.target.value), 400); }}
                onKeyDown={e => { if (e.key === "Enter") { clearTimeout(window.__giphyTimer); searchGiphy(giphySearch); } }}
                placeholder="Search KLIPY"
                autoFocus
                style={{
                  width: "100%", padding: "10px 12px", background: COLORS.bgAlt,
                  border: `1px solid ${COLORS.border}`, borderRadius: 8,
                  color: COLORS.text, fontFamily: "'JetBrains Mono', monospace", fontSize: 12,
                  outline: "none", boxSizing: "border-box",
                }}
              />
              <div style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
                {["trending", "reaction", "celebrate", "thumbs up", "fire", "laughing"].map(tag => (
                  <button key={tag} onClick={() => { setGiphySearch(tag); searchGiphy(tag); }} style={{
                    padding: "3px 8px", borderRadius: 4, background: COLORS.bgAlt,
                    border: `1px solid ${COLORS.border}`, color: COLORS.textMuted,
                    fontSize: 9, fontWeight: 600, cursor: "pointer",
                    fontFamily: "'JetBrains Mono', monospace",
                  }}>{tag}</button>
                ))}
              </div>
            </div>
            <div style={{ flex: 1, overflow: "auto", padding: "0 16px 16px" }}>
              {giphyResults.length === 0 ? (
                <div style={{ padding: 30, textAlign: "center", color: COLORS.textMuted, fontSize: 11 }}>
                  {giphySearch ? "Searching..." : "Type to search for GIFs"}
                </div>
              ) : (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6 }}>
                  {giphyResults.map(gif => (
                    <img key={gif.id} src={gif.preview} alt="" onClick={() => selectGif(gif.full)} style={{
                      width: "100%", height: 80, objectFit: "cover", borderRadius: 6,
                      cursor: "pointer", border: `2px solid transparent`,
                      transition: "border-color 0.15s",
                    }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = COLORS.accent}
                    onMouseLeave={e => e.currentTarget.style.borderColor = "transparent"}
                    />
                  ))}
                </div>
              )}
              <div style={{ textAlign: "center", marginTop: 8, fontSize: 8, color: COLORS.textMuted, opacity: 0.5 }}>
                Powered by KLIPY
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <div style={{
            fontFamily: "'Syne', sans-serif",
            fontSize: 24, fontWeight: 800,
            background: `linear-gradient(135deg, ${COLORS.cyan}, ${COLORS.blue})`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}>Live Polls</div>
          <div style={{ fontSize: 13, color: COLORS.textMuted, marginTop: 3 }}>
            Create polls for your stream — free or token-gated
          </div>
        </div>
        {!creating && (() => {
          const now = Math.floor(Date.now() / 1000);
          const hasActive = polls.some(p => p.status === "active" && (Number(p.endTime || 0) === 0 || Number(p.endTime) > now)) || (window.__fullsend_active_poll?.status === "active" && (Number(window.__fullsend_active_poll?.endTime || 0) === 0 || Number(window.__fullsend_active_poll?.endTime) > now));
          return hasActive ? (
            <div style={{
              padding: "12px 16px", borderRadius: 8,
              background: `${COLORS.orange}08`, border: `1px solid ${COLORS.orange}20`,
              fontSize: 12, color: COLORS.orange, textAlign: "center", lineHeight: 1.5,
            }}>
              ⚠ You have a live poll. End it first before creating a new one.
            </div>
          ) : (
          <button onClick={() => setCreating(true)} style={{
            padding: "10px 20px", borderRadius: 8,
            background: `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.cyan})`,
            color: COLORS.bg, border: "none",
            fontFamily: "'Syne', sans-serif",
            fontSize: 13, fontWeight: 700,
            cursor: "pointer",
            transition: "all 0.2s",
            boxShadow: `0 2px 16px ${COLORS.accentGlow}`,
          }}
          onMouseEnter={e => e.currentTarget.style.transform = "translateY(-1px)"}
          onMouseLeave={e => e.currentTarget.style.transform = "translateY(0)"}>
            + New Poll
          </button>
          );
        })()}
      </div>

      {/* Create Poll Form */}
      {creating && (
        <div style={{
          background: COLORS.surface,
          border: `1px solid ${COLORS.cyan}20`,
          borderRadius: 14,
          padding: 20,
          marginBottom: 20,
          animation: "fadeInUp 0.3s ease-out",
        }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: COLORS.cyan, marginBottom: 14, fontFamily: "'Syne', sans-serif" }}>
            Create New Poll
          </div>

          {/* Question */}
          <div style={{ position: "relative", marginBottom: 12 }}>
            <input
              value={question}
              onChange={e => setQuestion(e.target.value)}
              placeholder="Ask your viewers something..."
              style={{
                width: "100%", padding: "12px 40px 12px 14px",
                background: COLORS.bgAlt,
                border: `1px solid ${COLORS.border}`,
                borderRadius: 8, color: COLORS.text,
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 13, outline: "none", boxSizing: "border-box",
                transition: "border-color 0.2s",
              }}
              onFocus={e => e.currentTarget.style.borderColor = `${COLORS.cyan}30`}
              onBlur={e => e.currentTarget.style.borderColor = COLORS.border}
            />
            <button onClick={() => setShowEmojiPicker(!showEmojiPicker)} style={{
              position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)",
              width: 28, height: 28, borderRadius: 6,
              background: showEmojiPicker ? `${COLORS.cyan}15` : "transparent",
              border: `1px solid ${showEmojiPicker ? COLORS.cyan + "30" : "transparent"}`,
              cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 15, padding: 0,
            }}>😀</button>
            {showEmojiPicker && (
              <div style={{
                position: "absolute", top: "100%", right: 0, zIndex: 20,
                marginTop: 4, padding: "10px",
                background: COLORS.surface, border: `1px solid ${COLORS.border}`,
                borderRadius: 10, boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
                display: "grid", gridTemplateColumns: "repeat(8, 1fr)", gap: 4,
                maxHeight: 200, overflowY: "auto", overflowX: "hidden", width: 280,
              }}>
                {["🔥","🎮","🎵","🎨","💰","🏆","⚡","❓","🗳️","📊","🎯","🚀","💎","👑","🐋","💪","🎉","🤔","👀","💜","🔴","🟢","🔵","⭐","🎬","📺","🎤","🎧","💡","🧠","❤️","💯","🏅","🎁","📢","🌟","🤝","🎊","✅","❌","👍","👎","🍕","🎸","⚽","🏀","🎲","🃏","🐲","👽","👾","🛸","🆙","🫡","🤣","😭","🌌","😱","☠️","🤖","✊","🙏","👏","🙌","🤌","🫴","👁️","🗣️","🫂","🕵️","🦸‍♂️","🙅‍♂️","🙇","🏃‍♂️","🕺","🤷","☕️","🥂","🍻","🧩","📼","📷","📡","🔋","🪙","🛠️","⛓️","⚙️","💣","🛡️","⚔️","📬","📍","🔎","🚫","🔜","♠️","♣️","♥️","♦️"].map(emoji => (
                  <button key={emoji} onClick={() => {
                    setQuestion(prev => prev + emoji);
                    setShowEmojiPicker(false);
                  }} style={{
                    width: "100%", height: 32, borderRadius: 6,
                    background: "transparent", border: "none",
                    cursor: "pointer", fontSize: 18,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    transition: "background 0.15s",
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = `${COLORS.accent}15`}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                  >{emoji}</button>
                ))}
              </div>
            )}
          </div>

          {/* Poll Header Image */}
          <div style={{ marginBottom: 12 }}>
            {pollHeaderImage ? (
              <div style={{ position: "relative", display: "inline-block", marginBottom: 6 }}>
                <img src={pollHeaderImage} alt="" style={{ maxWidth: "100%", maxHeight: 120, borderRadius: 8, objectFit: "contain" }} />
                <button onClick={() => setPollHeaderImage("")} style={{
                  position: "absolute", top: -6, right: -6, width: 18, height: 18, borderRadius: "50%",
                  background: COLORS.red, border: "none", color: "#fff", fontSize: 10, fontWeight: 700,
                  cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                }}>×</button>
              </div>
            ) : (
              <div style={{ display: "flex", gap: 6 }}>
                <button onClick={() => openGiphyPicker("header")} style={{
                  padding: "6px 10px", borderRadius: 6, background: COLORS.bgAlt,
                  border: `1px solid ${COLORS.border}`, color: COLORS.textMuted,
                  fontSize: 10, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 4,
                  fontFamily: "'JetBrains Mono', monospace",
                }}>🎞️ Add GIF</button>
                <label style={{
                  padding: "6px 10px", borderRadius: 6, background: COLORS.bgAlt,
                  border: `1px solid ${COLORS.border}`, color: COLORS.textMuted,
                  fontSize: 10, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 4,
                  fontFamily: "'JetBrains Mono', monospace",
                }}>
                  🖼️ Add Image
                  <input type="file" accept="image/*" style={{ display: "none" }} onChange={e => {
                    const file = e.target.files?.[0];
                    if (file) { const r = new FileReader(); r.onload = ev => setPollHeaderImage(ev.target.result); r.readAsDataURL(file); }
                  }} />
                </label>
              </div>
            )}
          </div>

          {/* Options */}
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
            {options.map((opt, i) => (
              <div key={i}>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <span style={{ fontSize: 12, color: COLORS.textMuted, width: 16, textAlign: "center", fontWeight: 700 }}>{i + 1}</span>
                  <input
                    value={opt}
                    onChange={e => updateOption(i, e.target.value)}
                    placeholder={`Option ${i + 1}`}
                    style={{
                      flex: 1, padding: "9px 12px",
                      background: COLORS.bgAlt,
                      border: `1px solid ${COLORS.border}`,
                      borderRadius: 6, color: COLORS.text,
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: 14, outline: "none", boxSizing: "border-box",
                    }}
                  />
                  <button onClick={() => openGiphyPicker(i)} title="Add GIF" style={{
                    width: 28, height: 28, borderRadius: 5,
                    background: optionImages[i] ? `${COLORS.cyan}12` : COLORS.bgAlt,
                    border: `1px solid ${optionImages[i] ? COLORS.cyan + "30" : COLORS.border}`,
                    color: optionImages[i] ? COLORS.cyan : COLORS.textMuted, fontSize: 11, cursor: "pointer",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>🎞️</button>
                  <label title="Upload image" style={{
                    width: 28, height: 28, borderRadius: 5,
                    background: COLORS.bgAlt, border: `1px solid ${COLORS.border}`,
                    color: COLORS.textMuted, fontSize: 11, cursor: "pointer",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    🖼️
                    <input type="file" accept="image/*" style={{ display: "none" }} onChange={e => {
                      const file = e.target.files?.[0];
                      if (file) { const r = new FileReader(); r.onload = ev => { setOptionImages(prev => { const u = [...prev]; u[i] = ev.target.result; return u; }); }; r.readAsDataURL(file); }
                    }} />
                  </label>
                  {options.length > 2 && (
                    <button onClick={() => { removeOption(i); setOptionImages(prev => prev.filter((_, idx) => idx !== i)); }} style={{
                      width: 28, height: 28, borderRadius: 5,
                      background: `${COLORS.red}08`, border: `1px solid ${COLORS.red}15`,
                      color: COLORS.red, fontSize: 13, cursor: "pointer",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>×</button>
                  )}
                </div>
                {optionImages[i] && (
                  <div style={{ marginLeft: 22, marginTop: 4, position: "relative", display: "inline-block" }}>
                    <img src={optionImages[i]} alt="" style={{ maxWidth: 120, maxHeight: 60, borderRadius: 6, objectFit: "cover" }} />
                    <button onClick={() => setOptionImages(prev => { const u = [...prev]; u[i] = ""; return u; })} style={{
                      position: "absolute", top: -4, right: -4, width: 16, height: 16, borderRadius: "50%",
                      background: COLORS.red, border: "none", color: "#fff", fontSize: 9, fontWeight: 700,
                      cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                    }}>×</button>
                  </div>
                )}
              </div>
            ))}
            {options.length < 6 && (
              <button onClick={() => { addOption(); setOptionImages(prev => [...prev, ""]); }} style={{
                padding: "7px 0", borderRadius: 6,
                background: "transparent",
                border: `1px dashed ${COLORS.border}`,
                color: COLORS.textMuted, fontSize: 13,
                cursor: "pointer", fontFamily: "'JetBrains Mono', monospace",
                transition: "all 0.2s",
              }}
              onMouseEnter={e => e.currentTarget.style.borderColor = `${COLORS.cyan}30`}
              onMouseLeave={e => e.currentTarget.style.borderColor = COLORS.border}>
                + Add Option
              </button>
            )}
          </div>

          {/* Gate Type */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "1.5px", marginBottom: 8 }}>
              Voting Access
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              {[
                { id: "free", label: "🟢 Free Vote", desc: "Anyone can vote" },
                { id: "lyx", label: "🪙 Costs LYX", desc: "Pay to vote" },
                { id: "nft", label: "🔒 Token Gated", desc: "NFT or Token" },
              ].map(g => (
                <button key={g.id} onClick={() => setGateType(g.id)} style={{
                  flex: 1, padding: "10px 8px", borderRadius: 8,
                  background: gateType === g.id ? `${COLORS.cyan}10` : COLORS.bgAlt,
                  border: `1px solid ${gateType === g.id ? COLORS.cyan + "30" : COLORS.border}`,
                  cursor: "pointer",
                  transition: "all 0.2s",
                  textAlign: "center",
                }}>
                  <div style={{ fontSize: 14, marginBottom: 3 }}>{g.label.split(" ")[0]}</div>
                  <div style={{
                    fontSize: 11,
                    fontWeight: gateType === g.id ? 700 : 500,
                    color: gateType === g.id ? COLORS.cyan : COLORS.textMuted,
                    fontFamily: "'JetBrains Mono', monospace",
                  }}>{g.label.split(" ").slice(1).join(" ")}</div>
                  <div style={{ fontSize: 10, color: COLORS.textMuted, marginTop: 2 }}>{g.desc}</div>
                </button>
              ))}
            </div>

            {/* LYX cost setting */}
            {gateType === "lyx" && (
              <div style={{
                marginTop: 8, padding: "10px 12px", borderRadius: 8,
                background: COLORS.bgAlt,
                border: `1px solid ${COLORS.border}`,
                display: "flex", alignItems: "center", gap: 8,
                animation: "slideDown 0.2s ease-out",
              }}>
                <span style={{ fontSize: 11, color: COLORS.textMuted }}>Cost per vote:</span>
                <input
                  type="number"
                  value={lyxCost}
                  onChange={e => setLyxCost(e.target.value)}
                  style={{
                    width: 60, padding: "6px 10px",
                    background: COLORS.surface,
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: 5, color: COLORS.text,
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 12, fontWeight: 700, outline: "none",
                    textAlign: "center",
                  }}
                />
                <span style={{ fontSize: 11, color: COLORS.accent, fontWeight: 700 }}>LYX</span>
              </div>
            )}

            {/* Token requirement setting */}
            {gateType === "nft" && (
              <div style={{
                marginTop: 8, padding: "12px", borderRadius: 8,
                background: COLORS.bgAlt,
                border: `1px solid ${COLORS.border}`,
                animation: "slideDown 0.2s ease-out",
              }}>
                {/* Selected tokens list */}
                {nftRequirements.length > 0 && (
                  <div style={{ marginBottom: 8 }}>
                    <div style={{
                      fontSize: 10, color: COLORS.textMuted, fontWeight: 600, marginBottom: 6,
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                    }}>
                      <span>Required to vote ({nftRequirements.length}/5):</span>
                      <span style={{ fontSize: 8, color: COLORS.textMuted }}>Voter must hold ALL</span>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      {nftRequirements.map((req, ri) => (
                        <div key={ri} style={{
                          display: "flex", alignItems: "center", gap: 8,
                          padding: "6px 10px", borderRadius: 6,
                          background: `${req.color}08`,
                          border: `1px solid ${req.color}18`,
                        }}>
                          {req.image ? (
                            <img src={req.image} alt="" style={{ width: 22, height: 22, borderRadius: 5, objectFit: "cover", flexShrink: 0 }} onError={e => { e.currentTarget.style.display = "none"; }} />
                          ) : (
                            <span style={{ fontSize: 14 }}>{req.icon}</span>
                          )}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 10, fontWeight: 600, color: req.color }}>
                              {req.name}
                              {req.symbol && <span style={{ fontWeight: 400, color: COLORS.textSecondary }}> ({req.symbol})</span>}
                            </div>
                            <div style={{ fontSize: 8, color: COLORS.textMuted, fontFamily: "'JetBrains Mono', monospace" }}>{req.address.slice(0, 10)}…{req.address.slice(-6)}</div>
                            {req.held != null && (
                              <div style={{ fontSize: 8, color: req.color, fontWeight: 600 }}>You hold {req.held}</div>
                            )}
                          </div>
                          {/* Min amount — for LSP7 tokens and LSP8 NFTs */}
                          <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
                            <span style={{ fontSize: 8, color: COLORS.textMuted }}>min</span>
                            <input
                              type="number"
                              value={req.minAmount || "1"}
                              onChange={e => {
                                const val = e.target.value;
                                setNftRequirements(prev => prev.map((r, i) => i === ri ? { ...r, minAmount: val } : r));
                              }}
                              min="1"
                              style={{
                                width: 48, padding: "6px 8px",
                                background: COLORS.surface,
                                border: `1px solid ${COLORS.border}`,
                                borderRadius: 3, color: COLORS.text,
                                fontFamily: "'JetBrains Mono', monospace",
                                fontSize: 11, fontWeight: 700, outline: "none",
                                textAlign: "center",
                                MozAppearance: "textfield",
                                WebkitAppearance: "none",
                                appearance: "textfield",
                              }}
                            />
                            <span style={{ fontSize: 8, color: req.color, fontWeight: 600 }}>{req.symbol || "held"}</span>
                          </div>
                          <span style={{
                            fontSize: 7, padding: "2px 4px", borderRadius: 2,
                            background: `${req.color}12`, color: req.color, fontWeight: 600,
                          }}>{req.standard}</span>
                          <button onClick={() => setNftRequirements(prev => prev.filter((_, i) => i !== ri))} style={{
                            width: 20, height: 20, borderRadius: 4,
                            background: "transparent",
                            border: `1px solid ${COLORS.border}`,
                            color: COLORS.textMuted, fontSize: 11, cursor: "pointer",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            flexShrink: 0,
                          }}>×</button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Search to add more (up to 5) */}
                {nftRequirements.length < 5 && (
                  <div>
                    <span style={{ fontSize: 10, color: COLORS.textMuted, display: "block", marginBottom: 6, fontWeight: 600 }}>
                      {nftRequirements.length === 0 ? "Paste collection address or link:" : "+ Add another (up to 5):"}
                    </span>
                    <input
                      value={nftSearch}
                      onChange={e => { setNftSearch(e.target.value); setNftManualAddress(false); }}
                      placeholder="Paste contract address or Universal Everything link"
                      style={{
                        width: "100%", padding: "9px 12px",
                        background: COLORS.surface,
                        border: `1px solid ${nftSearch ? COLORS.purple + "30" : COLORS.border}`,
                        borderRadius: 6, color: COLORS.text,
                        fontFamily: "'JetBrains Mono', monospace",
                        fontSize: 11, outline: "none", boxSizing: "border-box",
                        marginBottom: 6,
                        transition: "border-color 0.2s",
                      }}
                    />

                    {/* Address auto-resolve — when search looks like an address, try to read its name */}
                    {pastedAddr && !nftManualAddress && !nftRequirements.some(r => r.address.toLowerCase() === pastedAddr.toLowerCase()) && (
                      <div style={{ marginBottom: 6 }}>
                        <button onClick={async () => {
                          const addr = pastedAddr;
                          if (nftRequirements.some(r => r.address.toLowerCase() === addr.toLowerCase())) {
                            setNftSearch("");
                            return;
                          }
                          const owner = window.__fullsend_profile?.address;
                          const meta = await fetchTokenMeta(addr, owner);
                          setNftRequirements(prev => {
                            if (prev.some(r => r.address.toLowerCase() === addr.toLowerCase())) return prev;
                            return [...prev, {
                              address: addr,
                              name: meta.name || (addr.slice(0, 6) + "..." + addr.slice(-4)),
                              image: meta.image || "",
                              icon: "📦",
                              color: COLORS.purple,
                              type: "Custom",
                              standard: meta.standard || "LSP8",
                              held: meta.held,
                              minAmount: "1",
                            }];
                          });
                          setNftSearch("");
                        }} style={{
                          width: "100%", padding: "8px 12px", borderRadius: 6,
                          background: `${COLORS.purple}10`, border: `1px solid ${COLORS.purple}20`,
                          color: COLORS.purple, fontFamily: "'JetBrains Mono', monospace",
                          fontSize: 10, fontWeight: 600, cursor: "pointer", textAlign: "left",
                          display: "flex", alignItems: "center", gap: 8,
                        }}>
                          <span>📦</span>
                          <div>
                            <div>Add {pastedAddr.slice(0, 6)}...{pastedAddr.slice(-4)}</div>
                            <div style={{ fontSize: 8, color: COLORS.textMuted, fontWeight: 400 }}>Tap to resolve name and add as requirement</div>
                          </div>
                        </button>
                      </div>
                    )}

                    {/* Paste-only gating hint */}
                    {!nftManualAddress && !pastedAddr && nftRequirements.length === 0 && (
                      <div style={{
                        textAlign: "center", padding: "10px 6px",
                        fontSize: 10, color: COLORS.textMuted, lineHeight: 1.5,
                      }}>
                        Paste a collection's address or Universal Everything link above to gate by it — its name, type and your balance load automatically.
                      </div>
                    )}

                    {/* Manual address */}
                    <div style={{ borderTop: `1px solid ${COLORS.border}`, paddingTop: 6 }}>
                      <button onClick={() => setNftManualAddress(!nftManualAddress)} style={{
                        fontSize: 9, color: COLORS.purple, fontWeight: 600,
                        background: "transparent", border: "none", cursor: "pointer",
                        fontFamily: "'JetBrains Mono', monospace",
                        padding: 0, display: "flex", alignItems: "center", gap: 4,
                      }}>
                        {nftManualAddress ? "← Back to search" : "Paste contract address →"}
                      </button>
                      {nftManualAddress && (
                        <div style={{ marginTop: 6, display: "flex", gap: 6, animation: "slideDown 0.2s ease-out" }}>
                          <input
                            value={nftCustomAddress}
                            onChange={e => setNftCustomAddress(e.target.value)}
                            placeholder="Paste 0x… address or Universal Everything link"
                            style={{
                              flex: 1, padding: "7px 10px",
                              background: COLORS.surface,
                              border: `1px solid ${COLORS.border}`,
                              borderRadius: 5, color: COLORS.text,
                              fontFamily: "'JetBrains Mono', monospace",
                              fontSize: 10, outline: "none", boxSizing: "border-box",
                            }}
                          />
                          <button onClick={async () => {
                            // Extract the address from a bare 0x… OR a pasted UE link.
                            const addr = (nftCustomAddress.match(/0x[a-fA-F0-9]{40}/) || [])[0] || "";
                            if (!addr) return;
                            if (nftRequirements.some(r => r.address.toLowerCase() === addr.toLowerCase())) { setNftCustomAddress(""); return; }
                            const meta = await fetchTokenMeta(addr, window.__fullsend_profile?.address);
                            setNftRequirements(prev => {
                              if (prev.some(r => r.address.toLowerCase() === addr.toLowerCase())) return prev;
                              return [...prev, {
                                address: addr,
                                name: meta.name || (addr.slice(0, 6) + "…" + addr.slice(-4)),
                                image: meta.image || "",
                                icon: "📦",
                                color: COLORS.purple,
                                type: "Custom",
                                standard: meta.standard || "LSP8",
                                held: meta.held,
                                minAmount: "1",
                              }];
                            });
                            setNftCustomAddress("");
                          }} style={{
                            padding: "7px 14px", borderRadius: 5,
                            background: `${COLORS.purple}15`,
                            border: `1px solid ${COLORS.purple}25`,
                            color: COLORS.purple,
                            fontFamily: "'JetBrains Mono', monospace",
                            fontSize: 10, fontWeight: 700, cursor: "pointer",
                            whiteSpace: "nowrap",
                          }}>Add</button>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {nftRequirements.length >= 5 && (
                  <div style={{ fontSize: 9, color: COLORS.orange, textAlign: "center", marginTop: 4 }}>
                    Maximum 5 token/NFT requirements reached
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Duration */}
          <div style={{ marginBottom: 14 }}>
            <div style={{
              fontSize: 11, fontWeight: 700, color: COLORS.textMuted,
              textTransform: "uppercase", letterSpacing: "1.5px", marginBottom: 8,
              display: "flex", alignItems: "center", justifyContent: "space-between",
            }}>
              <span>Duration <span style={{ fontSize: 9, color: COLORS.textMuted, fontWeight: 500, letterSpacing: "0.5px", textTransform: "none", marginLeft: 4 }}>(max 24 hours)</span></span>
              {gateType === "lyx" && (
                <span style={{ fontSize: 8, fontWeight: 500, letterSpacing: "0.5px", textTransform: "none", color: COLORS.orange }}>
                  Gas covered by FullSend
                </span>
              )}
            </div>
            <div style={{ display: "flex", gap: 5 }}>
              {[
                { value: "manual", label: "Manual", desc: gateType === "lyx" ? "24h max" : "No limit" },
                { value: 300, label: "5 min", desc: "Auto-close" },
                { value: 600, label: "10 min", desc: "Auto-close" },
                { value: 1800, label: "30 min", desc: "Auto-close" },
                { value: 3600, label: "60 min", desc: "Auto-close" },
                { value: "custom", label: "Custom", desc: "Set time" },
              ].map(d => {
                const isSelected = d.value === "manual"
                  ? pollDuration === null && customDurationInput === ""
                  : d.value === "custom"
                    ? customDurationInput !== ""
                    : pollDuration === d.value;
                return (
                  <button key={String(d.value)} onClick={() => {
                    if (d.value === "manual") { setPollDuration(null); setCustomDurationInput(""); }
                    else if (d.value === "custom") { setCustomDurationInput(customDurationInput || "15"); setPollDuration(Math.min(parseInt(customDurationInput || "15") * 60, 86400)); }
                    else { setPollDuration(d.value); setCustomDurationInput(""); }
                  }} style={{
                    flex: 1, padding: "10px 4px", borderRadius: 7,
                    background: isSelected ? `${COLORS.orange}12` : COLORS.bgAlt,
                    border: `1px solid ${isSelected ? COLORS.orange + "30" : COLORS.border}`,
                    cursor: "pointer",
                    transition: "all 0.2s",
                    textAlign: "center",
                  }}>
                    <div style={{
                      fontSize: 12,
                      fontWeight: isSelected ? 700 : 500,
                      color: isSelected ? COLORS.orange : COLORS.textSecondary,
                      fontFamily: "'JetBrains Mono', monospace",
                    }}>{d.label}</div>
                    <div style={{ fontSize: 9, color: COLORS.textMuted, marginTop: 1 }}>{d.desc}</div>
                  </button>
                );
              })}
            </div>

            {/* Custom duration input */}
            {customDurationInput !== "" && (
              <div style={{
                marginTop: 8, padding: "8px 12px", borderRadius: 8,
                background: COLORS.bgAlt,
                border: `1px solid ${COLORS.orange}20`,
                display: "flex", alignItems: "center", gap: 8,
                animation: "slideDown 0.2s ease-out",
              }}>
                <span style={{ fontSize: 10, color: COLORS.textMuted }}>Run for:</span>
                <input
                  type="number"
                  value={customDurationInput}
                  onChange={e => {
                    const val = e.target.value;
                    setCustomDurationInput(val);
                    const mins = parseInt(val) || 0;
                    const maxMins = gateType === "lyx" ? 1440 : 99999;
                    const capped = Math.min(Math.max(mins, 1), maxMins);
                    setPollDuration(capped * 60);
                  }}
                  min="1" max={gateType === "lyx" ? "1440" : "99999"}
                  style={{
                    width: 56, padding: "6px 10px",
                    background: COLORS.surface,
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: 5, color: COLORS.text,
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 12, fontWeight: 700, outline: "none",
                    textAlign: "center",
                  }}
                />
                <span style={{ fontSize: 10, color: COLORS.orange, fontWeight: 600 }}>minutes</span>
                <span style={{ fontSize: 9, color: COLORS.textMuted, marginLeft: "auto" }}>
                  {gateType === "lyx" ? "max 1440 min (24h)" : "no limit"}
                </span>
              </div>
            )}

            {/* Timer info */}
            {pollDuration && (
              <div style={{
                marginTop: 8, padding: "8px 12px", borderRadius: 8,
                background: COLORS.bgAlt,
                border: `1px solid ${COLORS.orange}15`,
                fontSize: 12, color: COLORS.orange, display: "flex", alignItems: "center", gap: 5,
                animation: "slideDown 0.2s ease-out",
              }}>
                <span>⏱</span> Auto-closes after {pollDuration >= 3600 ? `${(pollDuration / 3600).toFixed(1)}h` : pollDuration >= 60 ? `${pollDuration / 60} min` : `${pollDuration}s`} — results post to overlay automatically
              </div>
            )}

            {/* Show live on stream toggle -- available for all polls */}
            <div style={{
              marginTop: 8, padding: "10px 12px", borderRadius: 8,
              background: COLORS.bgAlt,
              border: `1px solid ${showPollLive ? COLORS.cyan + "25" : COLORS.border}`,
              animation: "slideDown 0.2s ease-out",
            }}>
              <div
                onClick={() => setShowPollLive(!showPollLive)}
                style={{
                  display: "flex", alignItems: "center", gap: 10,
                  cursor: "pointer",
                }}
              >
                <div style={{
                  width: 34, height: 18, borderRadius: 9,
                  background: showPollLive ? `${COLORS.cyan}40` : `${COLORS.bg}80`,
                  border: `1px solid ${showPollLive ? COLORS.cyan + "50" : COLORS.border}`,
                  position: "relative",
                  transition: "all 0.25s cubic-bezier(0.4,0,0.2,1)",
                  flexShrink: 0,
                }}>
                  <div style={{
                    width: 12, height: 12, borderRadius: "50%",
                    background: showPollLive ? COLORS.cyan : COLORS.textMuted,
                    position: "absolute", top: 2,
                    left: showPollLive ? 18 : 2,
                    transition: "all 0.25s cubic-bezier(0.4,0,0.2,1)",
                    boxShadow: showPollLive ? `0 0 6px ${COLORS.cyan}50` : "none",
                  }} />
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: showPollLive ? COLORS.cyan : COLORS.textSecondary }}>
                    Show live on stream
                  </div>
                  <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 1 }}>
                    {pollDuration
                      ? "Viewers watch results update in real-time with a countdown timer on screen"
                      : "Poll appears on the overlay immediately — viewers see votes update live"
                    }
                  </div>
                </div>
              </div>
            </div>

            {/* Followers Only toggle */}
            <div style={{
              marginTop: 8, padding: "10px 12px", borderRadius: 8,
              background: COLORS.bgAlt,
              border: `1px solid ${followersOnly ? COLORS.purple + "25" : COLORS.border}`,
              animation: "slideDown 0.2s ease-out",
            }}>
              <div
                onClick={() => setFollowersOnly(!followersOnly)}
                style={{
                  display: "flex", alignItems: "center", gap: 10,
                  cursor: "pointer",
                }}
              >
                <div style={{
                  width: 34, height: 18, borderRadius: 9,
                  background: followersOnly ? `${COLORS.purple}40` : `${COLORS.bg}80`,
                  border: `1px solid ${followersOnly ? COLORS.purple + "50" : COLORS.border}`,
                  position: "relative",
                  transition: "all 0.25s cubic-bezier(0.4,0,0.2,1)",
                  flexShrink: 0,
                }}>
                  <div style={{
                    width: 12, height: 12, borderRadius: "50%",
                    background: followersOnly ? COLORS.purple : COLORS.textMuted,
                    position: "absolute", top: 2,
                    left: followersOnly ? 18 : 2,
                    transition: "all 0.25s cubic-bezier(0.4,0,0.2,1)",
                    boxShadow: followersOnly ? `0 0 6px ${COLORS.purple}50` : "none",
                  }} />
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: followersOnly ? COLORS.purple : COLORS.textSecondary }}>
                    Followers only
                  </div>
                  <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 1 }}>
                    Only accounts that follow you can vote on this poll
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={createPoll} style={{
              flex: 1, padding: "14px",
              background: `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.cyan})`,
              color: COLORS.bg, border: "none", borderRadius: 8,
              fontFamily: "'Syne', sans-serif",
              fontSize: 15, fontWeight: 700, cursor: "pointer",
              opacity: (!question.trim() || options.filter(o => o.trim()).length < 2) ? 0.4 : 1,
            }}>
              Launch Poll
            </button>
            <button onClick={() => setCreating(false)} style={{
              padding: "11px 20px", borderRadius: 8,
              background: COLORS.bgAlt, border: `1px solid ${COLORS.border}`,
              color: COLORS.textMuted, fontSize: 12, cursor: "pointer",
              fontFamily: "'JetBrains Mono', monospace",
            }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Active / Past Polls */}
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {pollsLoading && polls.length === 0 && !creating && (
          <div style={{ textAlign: "center", padding: 48, color: COLORS.textMuted, fontSize: 12 }}>
            <div style={{ display: "inline-flex", gap: 4, marginBottom: 8 }}>
              {[0,1,2].map(i => (
                <div key={i} style={{
                  width: 6, height: 6, borderRadius: "50%", background: COLORS.accent,
                  animation: `dotBounce 0.6s ease-in-out ${i * 0.15}s infinite`,
                }} />
              ))}
            </div>
            <div>Loading polls...</div>
          </div>
        )}
        {!pollsLoading && polls.length === 0 && !creating && (
          <div style={{ textAlign: "center", padding: 48, color: COLORS.textMuted, fontSize: 12 }}>
            No polls yet — create one to engage your viewers
          </div>
        )}

        {/* Active & Ended Polls */}
        {activePollsList.map(poll => {
          const maxVotes = Math.max(...poll.options.map(o => o.votes), 1);
          return (
            <div key={poll.id} style={{ background: COLORS.surface, border: `1px solid ${poll.status === "active" ? COLORS.cyan + "20" : COLORS.border}`, borderRadius: 12, padding: 18, transition: "all 0.3s" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
                <div style={{ padding: "3px 8px", borderRadius: 4, background: poll.status === "active" ? `${COLORS.accent}15` : `${COLORS.textMuted}10`, border: `1px solid ${poll.status === "active" ? COLORS.accent + "25" : COLORS.textMuted + "15"}`, fontSize: 11, fontWeight: 700, color: poll.status === "active" ? COLORS.accent : COLORS.textMuted, textTransform: "uppercase", letterSpacing: "0.5px", display: "flex", alignItems: "center", gap: 4 }}>
                  {poll.status === "active" && <div style={{ width: 5, height: 5, borderRadius: "50%", background: COLORS.accent, animation: "pulse 1.5s infinite" }} />}
                  {poll.status === "active" ? "LIVE" : "ENDED"}
                </div>
                <div style={{ padding: "3px 7px", borderRadius: 4, background: COLORS.bgAlt, border: `1px solid ${COLORS.border}`, fontSize: 11, fontWeight: 600, color: COLORS.textSecondary }}>
                  {poll.gateType === "free" ? "🟢 Free" : poll.gateType === "lyx" ? `🪙 ${poll.lyxCost || 1} LYX` : `🔒 ${Array.isArray(poll.nftRequirement) ? poll.nftRequirement.map(r => r.name).join(" + ") : "Token Gated"}`}
                </div>
                {poll.followersOnly && (
                  <div style={{ padding: "3px 7px", borderRadius: 4, background: `${COLORS.purple}08`, border: `1px solid ${COLORS.purple}20`, fontSize: 11, fontWeight: 600, color: COLORS.purple }}>
                    👥 Followers Only
                  </div>
                )}
                <span style={{ fontSize: 12, fontWeight: 700, color: COLORS.text, marginLeft: "auto", fontFamily: "'JetBrains Mono', monospace" }}>{poll.totalVotes} vote{poll.totalVotes !== 1 ? "s" : ""}</span>
                {poll.status === "active" && poll.timeRemaining > 0 && (
                  <span style={{ padding: "3px 7px", borderRadius: 4, background: `${COLORS.orange}12`, border: `1px solid ${COLORS.orange}20`, fontSize: 10, fontWeight: 700, color: COLORS.orange, fontFamily: "'JetBrains Mono', monospace", display: "flex", alignItems: "center", gap: 4 }}>
                    ⏱ {formatPollTimer(poll.timeRemaining)}
                  </span>
                )}
              </div>
              {/* On-chain ID + syncing status */}
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                {poll.onchainId ? (
                  <a
                    href={`https://explorer.execution.mainnet.lukso.network/address/${POLL_VOTING_ADDRESS}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      padding: "2px 8px", borderRadius: 4, fontSize: 9, fontWeight: 600,
                      background: `${COLORS.accent}08`, border: `1px solid ${COLORS.accent}15`,
                      color: COLORS.accent, fontFamily: "'JetBrains Mono', monospace",
                      textDecoration: "none", display: "flex", alignItems: "center", gap: 4,
                      cursor: "pointer",
                    }}
                  >
                    ⛓ On-chain #{poll.onchainId}
                  </a>
                ) : poll.gateType !== "free" ? (
                  <span style={{ fontSize: 9, color: COLORS.orange, fontWeight: 600 }}>
                    ⏳ Chain ID resolving — voting locked until verified
                  </span>
                ) : (
                  <span style={{ fontSize: 9, color: COLORS.textMuted }}>
                    ⏳ Awaiting chain confirmation
                  </span>
                )}
              </div>
              {poll.headerImage && (
                <img src={poll.headerImage} alt="" style={{ width: "100%", maxHeight: 150, objectFit: "contain", background: "rgba(0,0,0,0.2)", borderRadius: 8, marginBottom: 10 }} />
              )}
              <div style={{ fontFamily: "'Syne', sans-serif", fontSize: 17, fontWeight: 700, marginBottom: 14, lineHeight: 1.4 }}>{poll.question}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {poll.options.map((opt, oi) => {
                  const pct = poll.totalVotes > 0 ? ((opt.votes / poll.totalVotes) * 100) : 0;
                  const isWinner = opt.votes === maxVotes && poll.totalVotes > 0;
                  return (
                    <div key={oi} style={{ position: "relative", padding: "10px 14px", borderRadius: 7, overflow: "hidden", background: `${COLORS.bg}60` }}>
                      <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${pct}%`, background: `${opt.color}15`, borderRadius: 7, transition: "width 0.8s cubic-bezier(0.16, 1, 0.3, 1)" }} />
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", position: "relative", zIndex: 1 }}>
                        <span style={{ fontSize: 14, fontWeight: isWinner ? 700 : 500, color: isWinner ? opt.color : COLORS.textSecondary, display: "flex", alignItems: "center", gap: 6 }}>
                          {poll.optionImages?.[oi] && <img src={poll.optionImages[oi]} alt="" style={{ width: 24, height: 24, borderRadius: 4, objectFit: "cover" }} />}
                          {opt.text}
                        </span>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ fontSize: 13, fontWeight: 700, color: isWinner ? opt.color : COLORS.textMuted }}>{pct.toFixed(0)}%</span>
                          <span style={{ fontSize: 12, color: COLORS.textMuted }}>{opt.votes}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div style={{ display: "flex", gap: 6, marginTop: 12 }}>
                {poll.status === "active" && (
                  <>
                    <button onClick={() => simulateVotes(poll.id)} style={{ flex: 1, padding: "8px", borderRadius: 6, background: `${COLORS.cyan}10`, border: `1px solid ${COLORS.cyan}20`, color: COLORS.cyan, fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Simulate Votes</button>
                    <button onClick={() => endPoll(poll.id)} style={{ padding: "8px 16px", borderRadius: 6, background: `${COLORS.red}08`, border: `1px solid ${COLORS.red}18`, color: COLORS.red, fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>End Poll</button>
                  </>
                )}
                {poll.status === "ended" && (
                  <>
                    <button onClick={() => archivePoll(poll.id)} style={{ flex: 1, padding: "8px", borderRadius: 6, background: `${COLORS.textSecondary}08`, border: `1px solid ${COLORS.textSecondary}15`, color: COLORS.textSecondary, fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}>📁 Archive</button>
                    <button onClick={() => deletePoll(poll.id)} style={{ padding: "8px 16px", borderRadius: 6, background: `${COLORS.red}06`, border: `1px solid ${COLORS.red}12`, color: COLORS.red, fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 600, cursor: "pointer", opacity: 0.7 }}>Delete</button>
                  </>
                )}
              </div>
            </div>
          );
        })}

        {/* Archived Polls */}
        {archivedPollsList.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <div
              onClick={() => setShowArchivedPolls(!showArchivedPolls)}
              style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "1.5px", marginBottom: 8, display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}
            >
              <span style={{ fontSize: 12 }}>📁</span> Archived ({archivedPollsList.length})
              <span style={{ fontSize: 10, transition: "transform 0.2s", transform: showArchivedPolls ? "rotate(0deg)" : "rotate(-90deg)" }}>▾</span>
            </div>
            {showArchivedPolls && (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {archivedPollsList.map(poll => (
                <div key={poll.id} style={{ padding: "10px 14px", borderRadius: 8, background: COLORS.surface, border: `1px solid ${COLORS.border}`, display: "flex", alignItems: "center", gap: 10, opacity: 0.6 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.textSecondary }}>{poll.question}</div>
                    <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 2 }}>
                      {poll.totalVotes} vote{poll.totalVotes !== 1 ? "s" : ""} · {poll.gateType === "free" ? "🟢 Free" : poll.gateType === "lyx" ? "🪙 LYX Gated" : "🔒 Token Gated"}
                      {poll.onchainId && (
                        <a href={`https://explorer.execution.mainnet.lukso.network/address/${POLL_VOTING_ADDRESS}`} target="_blank" rel="noopener noreferrer" style={{ color: COLORS.accent, textDecoration: "none", marginLeft: 6, fontFamily: "'JetBrains Mono', monospace" }}>
                          ⛓ #{poll.onchainId}
                        </a>
                      )}
                    </div>
                  </div>
                  <button onClick={() => unarchivePoll(poll.id)} style={{ padding: "5px 10px", borderRadius: 5, background: `${COLORS.accent}08`, border: `1px solid ${COLORS.accent}15`, color: COLORS.accent, fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>Recover</button>
                  <button onClick={() => deletePoll(poll.id)} style={{ padding: "5px 10px", borderRadius: 5, background: `${COLORS.red}06`, border: `1px solid ${COLORS.red}12`, color: COLORS.red, fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 600, cursor: "pointer" }}>Delete</button>
                </div>
              ))}
            </div>
            )}
          </div>
        )}
      </div>

      {/* Info */}
      <div style={{
        marginTop: 16, padding: 12, borderRadius: 8,
        background: COLORS.surface,
        border: `1px solid ${COLORS.border}`,
        fontSize: 13, color: COLORS.textMuted, lineHeight: 1.6,
        display: "flex", alignItems: "flex-start", gap: 8,
      }}>
        <span style={{ fontSize: 14, flexShrink: 0, marginTop: 1 }}>📊</span>
        <div>
          <span style={{ fontWeight: 700, color: COLORS.textSecondary }}>How it works — </span>
          Active polls appear in the tip widget for viewers. Free votes are on-chain and gasless — one click, no wallet popup, the relay handles everything. LYX-gated votes require a connected UP + payment per vote. Token-gated votes verify the voter holds a required NFT (LSP8) or token balance (LSP7) on-chain. Results update live on the stream overlay.
        </div>
      </div>
      <div style={{
        marginTop: 8, padding: 12, borderRadius: 8,
        background: `${COLORS.accent}05`,
        border: `1px solid ${COLORS.accent}12`,
        fontSize: 11, color: COLORS.textMuted, lineHeight: 1.6,
        display: "flex", alignItems: "flex-start", gap: 8,
      }}>
        <span style={{ fontSize: 14, flexShrink: 0, marginTop: 1 }}>💚</span>
        <div>
          <span style={{ fontWeight: 700, color: COLORS.accent }}>No platform fee on polls — </span>
          For LYX-gated votes, 100% of the vote payment goes to the creator. FullSend covers gas on every vote transaction. Free and token-gated polls have zero cost for voters.
        </div>
      </div>
    </div>
  );
}

// --- LEADERBOARD ---------------------------------------------
function LeaderboardView() {
  const [mounted, setMounted] = useState(false);
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { requestAnimationFrame(() => setMounted(true)); }, []);

  const creatorAddr = window.__fullsend_profile?.address;
  useEffect(() => {
    if (!creatorAddr) { setLoading(false); return; }
    fetch(`/api/data-tips?action=leaderboard&creator=${creatorAddr}&limit=20`)
      .then(r => r.json())
      .then(data => {
        const rows = (data.leaderboard || []).map((row, i) => ({
          profile: { address: row.tipper, name: `${row.tipper.slice(0, 6)}...${row.tipper.slice(-4)}`, avatar: STOCK_AVATAR },
          total: parseFloat(row.total_amount || 0),
          tips: parseInt(row.tip_count || 0),
          rank: i + 1,
          nft: parseFloat(row.total_amount || 0) >= 5000 ? "🔥 Diamond"
            : parseFloat(row.total_amount || 0) >= 1000 ? "💜 Gold"
            : parseFloat(row.total_amount || 0) >= 300 ? "💙 Silver"
            : parseFloat(row.total_amount || 0) >= 100 ? "💚 Bronze"
            : null,
        }));
        setLeaderboardData(rows);
        setLoading(false);
        // Resolve UP profiles
        rows.forEach(row => {
          fetchUPProfile(row.profile.address).then(p => {
            if (p) {
              setLeaderboardData(prev => prev.map(r =>
                r.profile.address === row.profile.address
                  ? { ...r, profile: { ...r.profile, name: p.name, avatar: p.avatar } }
                  : r
              ));
            }
          });
        });
      })
      .catch(() => setLoading(false));
  }, [creatorAddr]);

  const maxTotal = leaderboardData.length > 0 ? leaderboardData[0].total : 1;

  const nftTiers = [
    { name: "Bronze", threshold: 100, color: "#00cc66", icon: "🥉" },
    { name: "Silver", threshold: 300, color: "#4a9eff", icon: "🥈" },
    { name: "Gold", threshold: 1000, color: "#b44aff", icon: "🥇" },
    { name: "Diamond", threshold: 5000, color: "#ffd700", icon: "💎" },
  ];

  const revealSet = useStaggerReveal(leaderboardData.length, 70);

  return (
    <div style={{
      maxWidth: 620, margin: "0 auto", padding: "28px 24px",
      opacity: mounted ? 1 : 0,
      transform: mounted ? "translateY(0)" : "translateY(15px)",
      transition: "all 0.5s cubic-bezier(0.16, 1, 0.3, 1)",
    }}>
      {!window.__fullsend_profile?.address && (
        <div style={{ padding: "10px 16px", borderRadius: 8, background: `${COLORS.cyan}08`, border: `1px solid ${COLORS.cyan}15`, fontSize: 11, color: COLORS.cyan, textAlign: "center", marginBottom: 16 }}>
          Connect your Universal Profile to see your leaderboard position
        </div>
      )}
      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: 28 }}>
        <div style={{
          fontSize: 36, marginBottom: 2,
          animation: "float 3s ease-in-out infinite",
        }}>🏆</div>
        <div style={{
          fontFamily: "'Syne', sans-serif",
          fontSize: 26, fontWeight: 800,
          background: `linear-gradient(135deg, ${COLORS.gold}, ${COLORS.orange})`,
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          letterSpacing: "-0.5px",
        }}>Top Supporters</div>
        <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 5 }}>
          On-chain rankings — NFT unlocks at milestones
        </div>
      </div>

      {/* NFT Tiers */}
      <div style={{
        display: "flex", gap: 6, marginBottom: 22,
        padding: 10, borderRadius: 10,
        background: COLORS.surface,
        border: `1px solid ${COLORS.border}`,
      }}>
        {nftTiers.map(nft => (
          <div key={nft.name} style={{
            flex: 1, textAlign: "center", padding: "6px 4px",
            cursor: "default",
            transition: "all 0.2s",
            borderRadius: 6,
          }}
          onMouseEnter={e => e.currentTarget.style.background = `${nft.color}08`}
          onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
            <div style={{ fontSize: 18, marginBottom: 2 }}>{nft.icon}</div>
            <div style={{ fontSize: 10, fontWeight: 700, color: nft.color }}>{nft.name}</div>
            <div style={{ fontSize: 9, color: COLORS.textMuted, marginTop: 1 }}>{nft.threshold}+ LYX</div>
          </div>
        ))}
      </div>

      {/* Rankings */}
      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
        {leaderboardData.map((entry, idx) => {
          const isTop3 = idx < 3;
          const rankColors = ["#ffd700", "#c0c0c0", "#cd7f32"];
          const barWidth = `${(entry.total / maxTotal) * 100}%`;
          const isVisible = revealSet.has(idx);

          return (
            <div key={entry.profile.address} style={{
              background: COLORS.surface,
              border: `1px solid ${isTop3 ? rankColors[idx] + "15" : COLORS.border}`,
              borderRadius: 10,
              padding: "11px 14px",
              display: "flex",
              alignItems: "center",
              gap: 10,
              transition: "all 0.35s cubic-bezier(0.4,0,0.2,1)",
              cursor: "default",
              position: "relative",
              overflow: "hidden",
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? "translateX(0)" : "translateX(-20px)",
            }}
            onMouseEnter={e => {
              e.currentTarget.style.borderColor = isTop3 ? rankColors[idx] + "35" : `${COLORS.accent}15`;
              e.currentTarget.style.background = COLORS.surfaceAlt;
            }}
            onMouseLeave={e => {
              e.currentTarget.style.borderColor = isTop3 ? rankColors[idx] + "15" : COLORS.border;
              e.currentTarget.style.background = COLORS.surface;
            }}>
              {/* Background bar */}
              <div style={{
                position: "absolute", left: 0, top: 0, bottom: 0,
                width: barWidth,
                background: `linear-gradient(90deg, ${isTop3 ? rankColors[idx] : COLORS.accent}04, ${isTop3 ? rankColors[idx] : COLORS.accent}01)`,
                borderRadius: 10,
                animation: isVisible ? `barGrow 0.8s cubic-bezier(0.16, 1, 0.3, 1) ${0.2 + idx * 0.06}s both` : "none",
                "--bar-width": barWidth,
              }} />

              {/* Rank */}
              <div style={{
                width: 30, height: 30, borderRadius: 7,
                background: isTop3 ? `${rankColors[idx]}15` : `${COLORS.bg}80`,
                border: isTop3 ? `1px solid ${rankColors[idx]}25` : `1px solid ${COLORS.border}`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 12,
                fontWeight: 800,
                color: isTop3 ? rankColors[idx] : COLORS.textMuted,
                position: "relative",
                flexShrink: 0,
              }}>
                #{entry.rank}
              </div>

              {/* Avatar */}
              <div style={{
                width: 34, height: 34, borderRadius: 7,
                background: `${COLORS.accent}08`,
                border: `1px solid ${COLORS.border}`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 16, position: "relative", flexShrink: 0,
              }}>{isImageAvatar(entry.profile.avatar) ? <img src={entry.profile.avatar} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 7 }} onError={e => { e.target.src = STOCK_AVATAR; }} /> : "👤"}</div>

              {/* Name */}
              <div style={{ flex: 1, position: "relative" }}>
                <div style={{ fontSize: 12, fontWeight: 600 }}>{entry.profile.name}</div>
                <div style={{ fontSize: 10, color: COLORS.textMuted }}>{entry.tips} tips</div>
              </div>

              {/* NFT Badge */}
              {entry.nft && (
                <span style={{
                  fontSize: 9, padding: "3px 7px", borderRadius: 4,
                  background: COLORS.bgAlt,
                  border: `1px solid ${COLORS.border}`,
                  fontWeight: 600, position: "relative",
                  color: COLORS.textSecondary,
                }}>{entry.nft}</span>
              )}

              {/* Total */}
              <div style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 14, fontWeight: 700,
                color: isTop3 ? rankColors[idx] : COLORS.accent,
                position: "relative",
                minWidth: 70, textAlign: "right",
              }}>{entry.total} LYX</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// --- NFT GALLERY ---------------------------------------------
// ===== Mint-on-Claim Drop config (creator NFTs tab) =====
function ClaimDropConfig() {
  const creatorAddress = window.__fullsend_profile?.address || "";
  const RPC = LUKSO_TESTNET.rpcUrls[0]; // misnamed const — mainnet RPC
  const [loading, setLoading] = useState(true);
  const [drop, setDrop] = useState(null);
  const [tokenInput, setTokenInput] = useState("");
  const [meta, setMeta] = useState(null);
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [canMint, setCanMint] = useState(null); // null unknown / true / false
  const [amount, setAmount] = useState("");
  const [passphrase, setPassphrase] = useState("");
  const [windowMin, setWindowMin] = useState("");
  const [label, setLabel] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [now, setNow] = useState(Math.floor(Date.now() / 1000));
  const [copied, setCopied] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);

  useEffect(() => { const t = setInterval(() => setNow(Math.floor(Date.now() / 1000)), 1000); return () => clearInterval(t); }, []);

  const loadDrop = async () => {
    if (!creatorAddress) { setLoading(false); return; }
    try {
      const r = await fetch(`/api/data-polls?claimDrop=true&creator=${creatorAddress}`);
      const j = await r.json();
      if (j.drop) {
        setDrop(j.drop);
        setTokenInput(j.drop.token);
        setMeta({ name: j.drop.tokenName, symbol: j.drop.tokenSymbol, image: j.drop.tokenImage, decimals: j.drop.decimals, standard: "LSP7", held: null });
        // Amount / window / label intentionally start empty on load — creator re-enters per drop.
        setCanMint(true);
        // Enrich with fresh on-chain data (supply, balance, name/symbol)
        fetchTokenMeta(j.drop.token, creatorAddress).then(m => {
          if (!m) return;
          setMeta(prev => ({ ...prev, ...m }));
          setDisplayName(prev => prev || m.name || m.symbol || "");
        }).catch(() => {});
      } else {
        setDrop(null);
      }
    } catch {}
    setLoading(false);
  };
  useEffect(() => { loadDrop(); /* eslint-disable-next-line */ }, [creatorAddress]);

  // Verify the relayer can mint by SIMULATING mint() via eth_call (from=relayer).
  // No revert = relayer is an authorized minter. (Reliable vs. guessing storage.)
  const verifyCanMint = async (token) => {
    try {
      const padAddr = a => a.slice(2).toLowerCase().padStart(64, "0");
      const padNum = n => Number(n).toString(16).padStart(64, "0");
      const calldata = "0x7580d920" + padAddr(creatorAddress) + padNum(1) + padNum(1) + padNum(128) + padNum(0);
      const r = await fetch(RPC, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ jsonrpc: "2.0", id: 1, method: "eth_call", params: [{ from: FULLSEND_RELAYER, to: token, data: calldata }, "latest"] }) });
      const j = await r.json();
      return !j.error;
    } catch { return false; }
  };

  const handleLoadToken = async () => {
    const addr = (tokenInput.match(/0x[a-fA-F0-9]{40}/) || [])[0] || "";
    if (!addr) return;
    setLoadingMeta(true); setCanMint(null); setMeta(null);
    // Fresh token = fresh form — don't carry over the previous drop's values.
    setLabel(""); setPassphrase(""); setAmount(""); setWindowMin("");
    const m = await fetchTokenMeta(addr, creatorAddress);
    setMeta(m);
    // Fresh load of a (possibly different) token — reset the name to this token's,
    // don't keep the previously loaded token's name.
    setDisplayName(m.name || m.symbol || "");
    const ok = await verifyCanMint(addr);
    setCanMint(ok);
    setLoadingMeta(false);
  };

  const handleSave = async () => {
    const addr = (tokenInput.match(/0x[a-fA-F0-9]{40}/) || [])[0] || "";
    if (!addr || !meta) return;
    setSaving(true);
    try {
      await fetch("/api/data-polls", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({
        action: "claimDropSave", creator: creatorAddress, token: addr,
        tokenName: (meta.name || "").trim(), tokenSymbol: (meta.symbol || "").trim(), tokenImage: meta.image || "", decimals: meta.decimals || 0,
        amount: String(amount || "1"), passphrase, windowSeconds: Math.max(30, (Number(windowMin) || 5) * 60), label,
      }) });
      await loadDrop();
    } catch {}
    setSaving(false);
  };

  const handleToggle = async (on) => {
    setToggling(true);
    try {
      await fetch("/api/data-polls", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "claimDropToggle", creator: creatorAddress, enabled: on }) });
      await loadDrop();
    } catch {}
    setToggling(false);
  };

  const copyRelayer = () => { try { navigator.clipboard.writeText(FULLSEND_RELAYER); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch {} };

  const live = drop && drop.live;
  const secsLeft = live ? Math.max(0, drop.endsAt - now) : 0;
  const mmss = `${String(Math.floor(secsLeft / 60)).padStart(2, "0")}:${String(secsLeft % 60).padStart(2, "0")}`;
  const addrResolved = (tokenInput.match(/0x[a-fA-F0-9]{40}/) || [])[0] || "";

  const cardStyle = { background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: "18px 20px", marginBottom: 16 };
  const inputStyle = { width: "100%", padding: "9px 12px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.text, fontSize: 12, fontFamily: "'JetBrains Mono', monospace", outline: "none", boxSizing: "border-box" };

  if (!creatorAddress) return null;

  return (
    <div style={{ marginBottom: 24 }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <span style={{ fontSize: 20 }}>🎁</span>
        <span style={{ fontFamily: "'Syne', sans-serif", fontSize: 20, fontWeight: 800, color: COLORS.cyan }}>Live Claim Drops</span>
        <span style={{ fontSize: 9, fontWeight: 700, color: COLORS.bg, background: COLORS.green, padding: "2px 7px", borderRadius: 20, letterSpacing: "0.5px" }}>LIVE</span>
      </div>
      <div style={{ fontSize: 12, color: COLORS.textSecondary, marginBottom: 16, lineHeight: 1.5 }}>
        Viewers can mint your token in the claim process during your stream. Tokens can be used as reputation, voting power, leaderboards, etc.
      </div>

      {/* Mint-on-claim notice */}
      <div style={{ ...cardStyle, background: `linear-gradient(135deg, ${COLORS.accent}10, ${COLORS.cyan}06)`, border: `1px solid ${COLORS.accent}25` }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: COLORS.accent, marginBottom: 6, display: "flex", alignItems: "center", gap: 6, fontFamily: "'Syne', sans-serif" }}>
          <span>⚡</span> Mint-on-Claim
        </div>
        <div style={{ fontSize: 11.5, color: COLORS.textSecondary, lineHeight: 1.6 }}>
          Claims <strong style={{ color: COLORS.text }}>mint new tokens straight to viewers</strong>, so this UP must be the token's creator and FullSend's relayer must be a <strong style={{ color: COLORS.text }}>Minting Admin</strong>. Otherwise the claim won't work.
        </div>
        <div style={{ marginTop: 12, padding: "10px 12px", background: COLORS.bg, borderRadius: 10, border: `1px solid ${COLORS.border}` }}>
          <div style={{ fontSize: 9, color: COLORS.textMuted, marginBottom: 5, textTransform: "uppercase", letterSpacing: "1px" }}>FullSend Relayer — add as Minting Admin</div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <code style={{ flex: 1, fontSize: 10, color: COLORS.cyan, fontFamily: "'JetBrains Mono', monospace", wordBreak: "break-all" }}>{FULLSEND_RELAYER}</code>
            <button onClick={copyRelayer} style={{ padding: "6px 12px", background: copied ? COLORS.green : COLORS.accent, color: COLORS.bg, border: "none", borderRadius: 7, fontSize: 10, fontWeight: 700, cursor: "pointer", flexShrink: 0, transition: "all 0.2s" }}>{copied ? "✓ Copied" : "Copy"}</button>
          </div>
        </div>
      </div>

      {/* How to create a loyalty token — stationary guide */}
      <div style={cardStyle}>
        <div style={{ fontSize: 13, fontWeight: 800, color: COLORS.text, marginBottom: 4, fontFamily: "'Syne', sans-serif" }}>How to create a loyalty token</div>
        <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 14, lineHeight: 1.5 }}>Make your token on Universal Everything with these settings, then paste its address below.</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 9, marginBottom: 14 }}>
          {[
            ["Category", "Loyalty Points (LSP7)"],
            ["Initial mint", "0 — tokens are created as people claim"],
            ["Mintable", "ON — so you can keep letting viewers claim on future streams"],
            ["Minting Admin", "Add the relayer address above (at creation!)"],
            ["Non-Transferable", "ON — makes it reputation, not tradeable"],
          ].map(([k, v], i) => (
            <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
              <span style={{ width: 18, height: 18, borderRadius: "50%", background: `${COLORS.cyan}18`, color: COLORS.cyan, fontSize: 9, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>{i + 1}</span>
              <div style={{ fontSize: 11, lineHeight: 1.5 }}>
                <span style={{ color: COLORS.text, fontWeight: 700 }}>{k}</span>
                <span style={{ color: COLORS.textSecondary }}> — {v}</span>
              </div>
            </div>
          ))}
        </div>
        <a href="https://universaleverything.io/create-asset" target="_blank" rel="noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "10px 18px", background: COLORS.cyan, color: "#06141a", borderRadius: 9, fontSize: 12, fontWeight: 800, textDecoration: "none", fontFamily: "'Syne', sans-serif" }}>Create Token on Universal Everything →</a>
      </div>

      {/* Token paste + verify */}
      <div style={cardStyle}>
        <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.text, marginBottom: 8 }}>Your token</div>
        <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
          <input value={tokenInput} onChange={e => { setTokenInput(e.target.value); setMeta(d => d); }} placeholder="Paste token address or Universal Everything link" style={inputStyle} />
          <button onClick={handleLoadToken} disabled={!addrResolved || loadingMeta} style={{ padding: "9px 16px", background: addrResolved && !loadingMeta ? COLORS.accent : COLORS.surface, color: addrResolved && !loadingMeta ? COLORS.bg : COLORS.textMuted, border: "none", borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: addrResolved && !loadingMeta ? "pointer" : "default", flexShrink: 0 }}>{loadingMeta ? "..." : "Load"}</button>
        </div>

        {meta && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", background: COLORS.surface, borderRadius: 8, border: `1px solid ${COLORS.border}`, marginBottom: 10 }}>
            {meta.image ? <img src={meta.image} alt="" style={{ width: 36, height: 36, borderRadius: 8, objectFit: "cover" }} onError={e => { e.currentTarget.style.display = "none"; }} /> : <span style={{ fontSize: 24 }}>🪙</span>}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text }}>{fmtToken(meta.name, meta.symbol)}</div>
              <div style={{ fontSize: 9, color: COLORS.textMuted }}>{meta.standard || "LSP7"} · {meta.decimals || 0} decimals{meta.held != null ? ` · you hold ${meta.held}` : ""}{meta.supply != null ? ` · supply ${meta.supply}` : ""}</div>
            </div>
            {canMint === true && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 10, fontWeight: 700, color: COLORS.green, background: `${COLORS.green}14`, border: `1px solid ${COLORS.green}40`, padding: "4px 9px", borderRadius: 20 }}>
                <span style={{ width: 14, height: 14, borderRadius: "50%", background: COLORS.green, color: COLORS.bg, fontSize: 9, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900 }}>✓</span> Relayer can mint
              </span>
            )}
            {canMint === false && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 10, fontWeight: 700, color: COLORS.red, background: `${COLORS.red}14`, border: `1px solid ${COLORS.red}40`, padding: "4px 9px", borderRadius: 20 }}>
                <span style={{ width: 14, height: 14, borderRadius: "50%", background: COLORS.red, color: "#fff", fontSize: 9, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900 }}>✕</span> Not a minter
              </span>
            )}
            {canMint === null && loadingMeta && <span style={{ fontSize: 10, color: COLORS.textMuted }}>checking…</span>}
          </div>
        )}

        {canMint === false && (
          <div style={{ fontSize: 11, color: COLORS.red, background: `${COLORS.red}10`, border: `1px solid ${COLORS.red}30`, borderRadius: 8, padding: "8px 10px", lineHeight: 1.5 }}>
            FullSend's relayer isn't a Minting Admin on this token, so claims would fail. Minting Admins can only be added <strong>at token creation</strong> — you'll need to create a new token with the relayer added.
          </div>
        )}

        {meta && canMint === true && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 4 }}>
            <div>
              <div style={{ fontSize: 9, color: COLORS.textMuted, marginBottom: 4, textTransform: "uppercase", letterSpacing: "1px" }}>Token name &amp; symbol (locked to on-chain metadata)</div>
              <div style={{ ...inputStyle, display: "flex", alignItems: "center", gap: 7, background: COLORS.bg, cursor: "default" }}>
                <span style={{ fontSize: 11 }}>🔒</span>
                <span style={{ color: COLORS.text, fontWeight: 600, fontFamily: "inherit" }}>{fmtToken(meta.name, meta.symbol)}</span>
              </div>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 9, color: COLORS.textMuted, marginBottom: 4, textTransform: "uppercase", letterSpacing: "1px" }}>Amount per claim</div>
                <input value={amount} onChange={e => setAmount(e.target.value.replace(/[^0-9.]/g, ""))} placeholder="e.g. 5" style={inputStyle} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 9, color: COLORS.textMuted, marginBottom: 4, textTransform: "uppercase", letterSpacing: "1px" }}>Window (minutes, max 10)</div>
                <input value={windowMin} onChange={e => { const v = e.target.value.replace(/[^0-9]/g, ""); setWindowMin(v === "" ? "" : Math.min(10, Math.max(1, parseInt(v) || 1))); }} placeholder="e.g. 5" style={inputStyle} />
              </div>
            </div>
            <div>
              <div style={{ fontSize: 9, color: COLORS.textMuted, marginBottom: 4, textTransform: "uppercase", letterSpacing: "1px" }}>Passphrase (optional — say it on stream)</div>
              <input value={passphrase} onChange={e => setPassphrase(e.target.value)} placeholder="leave blank for open claim" style={{ ...inputStyle, fontFamily: "inherit" }} />
            </div>
            <div>
              <div style={{ fontSize: 9, color: COLORS.textMuted, marginBottom: 4, textTransform: "uppercase", letterSpacing: "1px" }}>Label (optional)</div>
              <input value={label} onChange={e => setLabel(e.target.value)} placeholder="e.g. Claim tonight's points!" style={{ ...inputStyle, fontFamily: "inherit" }} />
            </div>
            <button onClick={handleSave} disabled={saving} style={{ padding: "12px 0", background: COLORS.cyan, color: "#06141a", border: "none", borderRadius: 10, fontSize: 13, fontWeight: 800, fontFamily: "'Syne', sans-serif", cursor: saving ? "wait" : "pointer", opacity: saving ? 0.6 : 1 }}>{saving ? "Saving…" : drop ? "Update Drop" : "Save Drop"}</button>
          </div>
        )}
      </div>

      {/* Live toggle + countdown (shown once a drop is saved) */}
      {drop && (
        <div style={{ ...cardStyle, background: live ? `${COLORS.green}0c` : COLORS.card, border: `1px solid ${live ? COLORS.green + "40" : COLORS.border}` }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: live ? COLORS.green : COLORS.text }}>{live ? "🟢 Drop is LIVE" : drop.inCooldown ? "⏳ Cooldown" : "Drop is off"}</div>
              <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 2 }}>
                {live
                  ? `Claiming open — ${mmss} left`
                  : drop.inCooldown
                    ? `Relaunch available in ${String(Math.floor(Math.max(0, drop.cooldownUntil - now) / 60)).padStart(2, "0")}:${String(Math.max(0, drop.cooldownUntil - now) % 60).padStart(2, "0")}`
                    : `Mints ${drop.amount} ${drop.tokenName || "tokens"} per viewer · ${Math.round(drop.windowSeconds / 60)} min window`}
              </div>
            </div>
            <button onClick={() => handleToggle(!live)} disabled={toggling || (!live && drop.inCooldown)} style={{ padding: "10px 18px", background: live ? `${COLORS.red}18` : (drop.inCooldown ? COLORS.surface : COLORS.green), color: live ? COLORS.red : (drop.inCooldown ? COLORS.textMuted : "#06141a"), border: live ? `1px solid ${COLORS.red}40` : "none", borderRadius: 10, fontSize: 12, fontWeight: 800, fontFamily: "'Syne', sans-serif", cursor: (toggling || (!live && drop.inCooldown)) ? "default" : "pointer", flexShrink: 0, opacity: (!live && drop.inCooldown) ? 0.6 : 1 }}>{toggling ? "…" : live ? "Stop" : "Go Live"}</button>
          </div>
          <div style={{ fontSize: 10, color: COLORS.textMuted, marginTop: 10, lineHeight: 1.5 }}>
            You can also start/stop this from the Control panel during your stream.
          </div>
        </div>
      )}
    </div>
  );
}

// ===== Control-panel claim drop (start/stop + overlay prefs + leaderboard) =====
function ControlClaimDrop({ creatorAddr }) {
  const [drop, setDrop] = useState(null);
  const [now, setNow] = useState(Math.floor(Date.now() / 1000));
  const [toggling, setToggling] = useState(false);

  useEffect(() => { const t = setInterval(() => setNow(Math.floor(Date.now() / 1000)), 1000); return () => clearInterval(t); }, []);

  const load = async () => {
    if (!creatorAddr) return;
    try {
      const r = await fetch(`/api/data-polls?claimDrop=true&creator=${creatorAddr}&reveal=passphrase`);
      const j = await r.json();
      setDrop(j.drop || null);
    } catch {}
  };
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [creatorAddr]);

  if (!drop) return null;

  const live = drop.live;
  const secsLeft = live ? Math.max(0, drop.endsAt - now) : 0;
  const mmss = `${String(Math.floor(secsLeft / 60)).padStart(2, "0")}:${String(secsLeft % 60).padStart(2, "0")}`;
  const cdLeft = drop.inCooldown ? Math.max(0, drop.cooldownUntil - now) : 0;
  const cdmmss = `${String(Math.floor(cdLeft / 60)).padStart(2, "0")}:${String(cdLeft % 60).padStart(2, "0")}`;

  const toggle = async (on) => {
    setToggling(true);
    try { await fetch("/api/data-polls", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "claimDropToggle", creator: creatorAddr, enabled: on }) }); await load(); } catch {}
    setToggling(false);
  };
  const setOverlay = async (enabled, top) => {
    setDrop(d => ({ ...d, overlayEnabled: enabled, overlayTop: top }));
    try { await fetch("/api/data-polls", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "claimDropOverlay", creator: creatorAddr, overlayEnabled: enabled, overlayTop: top }) }); } catch {}
  };

  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, marginBottom: 8, display: "flex", alignItems: "center", gap: 6, textTransform: "uppercase", letterSpacing: "0.5px" }}>🎁 Claim Drop</div>

      {/* Start / stop */}
      <div style={{ background: live ? `${COLORS.green}0c` : COLORS.card, border: `1px solid ${live ? COLORS.green + "40" : COLORS.border}`, borderRadius: 12, padding: "14px 16px", marginBottom: 10 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
            {drop.tokenImage ? <img src={drop.tokenImage} alt="" style={{ width: 36, height: 36, borderRadius: 8, objectFit: "cover", flexShrink: 0 }} /> : <span style={{ fontSize: 26 }}>🪙</span>}
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: live ? COLORS.green : COLORS.text }}>{live ? "🟢 LIVE" : drop.inCooldown ? "⏳ Cooldown" : (drop.tokenName ? fmtToken(drop.tokenName, drop.tokenSymbol) : "Drop")}</div>
              <div style={{ fontSize: 10, color: COLORS.textMuted }}>
                {live ? `${mmss} left · ${drop.amount} ${drop.tokenName || "tokens"}/claim` : drop.inCooldown ? `Relaunch in ${cdmmss}` : `${drop.amount} ${drop.tokenName || "tokens"} · ${Math.round(drop.windowSeconds / 60)} min`}
              </div>
            </div>
          </div>
          <button onClick={() => toggle(!live)} disabled={toggling || (!live && drop.inCooldown)} style={{ padding: "10px 18px", background: live ? `${COLORS.red}18` : (drop.inCooldown ? COLORS.surface : COLORS.green), color: live ? COLORS.red : (drop.inCooldown ? COLORS.textMuted : "#06141a"), border: live ? `1px solid ${COLORS.red}40` : "none", borderRadius: 10, fontSize: 12, fontWeight: 800, fontFamily: "'Syne', sans-serif", cursor: (toggling || (!live && drop.inCooldown)) ? "default" : "pointer", flexShrink: 0, opacity: (!live && drop.inCooldown) ? 0.6 : 1 }}>{toggling ? "…" : live ? "Stop" : "Go Live"}</button>
        </div>

        {/* Passphrase — creator-only readback so they can announce it on stream */}
        {drop.passphrase ? (
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12, padding: "8px 10px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8 }}>
            <span style={{ fontSize: 11, color: COLORS.textMuted, flexShrink: 0 }}>🔑 Passphrase</span>
            <span style={{ fontSize: 13, fontWeight: 800, color: COLORS.cyan, fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.5px", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{drop.passphrase}</span>
            <button onClick={() => { try { navigator.clipboard.writeText(drop.passphrase); } catch {} }} style={{ padding: "5px 10px", background: `${COLORS.cyan}18`, color: COLORS.cyan, border: `1px solid ${COLORS.cyan}40`, borderRadius: 7, fontSize: 11, fontWeight: 700, cursor: "pointer", flexShrink: 0 }}>Copy</button>
          </div>
        ) : (
          <div style={{ marginTop: 12, padding: "8px 10px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, fontSize: 11, color: COLORS.textMuted }}>🔓 Open claim — no passphrase required</div>
        )}
      </div>

      {/* Overlay display controls */}
      <div style={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: "12px 16px", marginBottom: 10 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: drop.overlayEnabled ? 10 : 0 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.text }}>Show leaderboard on stream</div>
          <div onClick={() => setOverlay(!drop.overlayEnabled, drop.overlayTop || 5)} style={{ width: 40, height: 22, borderRadius: 20, background: drop.overlayEnabled ? COLORS.accent : COLORS.surface, border: `1px solid ${drop.overlayEnabled ? COLORS.accent : COLORS.border}`, position: "relative", cursor: "pointer", transition: "all 0.2s", flexShrink: 0 }}>
            <div style={{ width: 16, height: 16, borderRadius: "50%", background: drop.overlayEnabled ? "#06141a" : COLORS.textMuted, position: "absolute", top: 2, left: drop.overlayEnabled ? 20 : 2, transition: "all 0.2s" }} />
          </div>
        </div>
        {drop.overlayEnabled && (
          <div>
            <div style={{ fontSize: 9, color: COLORS.textMuted, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.5px" }}>Show top</div>
            <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
              {[3, 4, 5, 6, 7, 8, 9, 10].map(n => (
                <button key={n} onClick={() => setOverlay(true, n)} style={{ width: 32, height: 30, borderRadius: 8, border: `1px solid ${(drop.overlayTop || 5) === n ? COLORS.accent : COLORS.border}`, background: (drop.overlayTop || 5) === n ? `${COLORS.accent}18` : COLORS.surface, color: (drop.overlayTop || 5) === n ? COLORS.accent : COLORS.textMuted, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>{n}</button>
              ))}
            </div>
            <div style={{ fontSize: 9, color: COLORS.textMuted, marginTop: 8, lineHeight: 1.4 }}>Overlay shows the top {drop.overlayTop || 5} and auto-cycles through the rest (up to 100). Make sure the overlay URL is added as a Browser Source in OBS.</div>
          </div>
        )}
      </div>

      {/* Leaderboard — scrollable to 100 */}
      <div style={{ maxHeight: 360, overflowY: "auto" }}>
        <ClaimLeaderboard creatorAddr={creatorAddr} onlyToken={drop.token} limit={100} />
      </div>
    </div>
  );
}

// ===== OBS overlay leaderboard (auto-cycles pages of top-N) =====
function OverlayLeaderboard({ creatorAddress, posStyle }) {
  const [drop, setDrop] = useState(null);
  const [rows, setRows] = useState([]);
  const [profiles, setProfiles] = useState({});
  const [page, setPage] = useState(0);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      if (!creatorAddress) return;
      try {
        const r = await fetch(`/api/data-polls?claimDrop=true&creator=${creatorAddress}`);
        const j = await r.json();
        if (!alive) return;
        setDrop(j.drop || null);
        if (j.drop && j.drop.overlayEnabled && j.drop.token) {
          const lr = await fetch(`/api/data-polls?claimLeaderboard=true&creator=${creatorAddress}&token=${j.drop.token}`);
          const lj = await lr.json();
          if (!alive) return;
          const lb = (lj.leaderboard || []).slice(0, 100);
          setRows(lb);
          lb.forEach(async (row) => {
            const p = await fetchUPProfile(row.claimer).catch(() => null);
            if (p && alive) setProfiles(prev => ({ ...prev, [row.claimer.toLowerCase()]: p }));
          });
        } else {
          setRows([]);
        }
      } catch {}
    };
    load();
    const t = setInterval(load, 15000);
    return () => { alive = false; clearInterval(t); };
  }, [creatorAddress]);

  const top = drop?.overlayTop || 5;
  const totalPages = Math.max(1, Math.ceil(rows.length / top));

  useEffect(() => {
    if (totalPages <= 1) { setPage(0); return; }
    const t = setInterval(() => setPage(p => (p + 1) % totalPages), 5000);
    return () => clearInterval(t);
  }, [totalPages]);

  if (!drop || !drop.overlayEnabled || rows.length === 0) return null;

  const start = (page % totalPages) * top;
  const visible = rows.slice(start, start + top);
  const tokenName = drop.tokenName || "TOKEN";

  // Size multiplier — creator-adjustable via drop.overlaySize; defaults larger so
  // names are readable on stream (the old fixed 10px text was unreadable once OBS
  // scaled the 1920×1080 canvas). px() scales every dimension proportionally.
  const S = Number(drop.overlaySize) || 1.8;
  const px = (n) => Math.round(n * S);

  return (
    <div style={{ position: "absolute", ...posStyle, zIndex: 48, width: `${px(240)}px`, maxWidth: "55%" }}>
      <div style={{ background: `${COLORS.bg}d8`, backdropFilter: "blur(12px)", borderRadius: px(12), border: `1px solid ${COLORS.cyan}30`, overflow: "hidden", boxShadow: `0 0 ${px(24)}px ${COLORS.cyan}18` }}>
        <div style={{ display: "flex", alignItems: "center", gap: px(8), padding: `${px(8)}px ${px(12)}px`, borderBottom: `1px solid ${COLORS.border}` }}>
          {drop.tokenImage ? <img src={drop.tokenImage} alt="" style={{ width: px(22), height: px(22), borderRadius: px(6), objectFit: "cover" }} /> : <span style={{ fontSize: px(16) }}>🏆</span>}
          <span style={{ fontSize: px(11), fontWeight: 800, color: COLORS.cyan, fontFamily: "'Syne', sans-serif", letterSpacing: "0.5px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{tokenName.toUpperCase()} LEADERBOARD</span>
        </div>
        <div style={{ padding: `${px(6)}px ${px(8)}px`, display: "flex", flexDirection: "column", gap: px(3) }}>
          {visible.map((row, i) => {
            const rank = start + i;
            const p = profiles[row.claimer.toLowerCase()];
            return (
              <div key={row.claimer} style={{ display: "flex", alignItems: "center", gap: px(7), padding: `${px(4)}px ${px(6)}px`, borderRadius: px(6), background: rank < 3 ? `${COLORS.cyan}10` : "transparent" }}>
                <span style={{ fontSize: px(10), fontWeight: 800, color: rank < 3 ? COLORS.cyan : COLORS.textMuted, minWidth: px(18) }}>{rank === 0 ? "🥇" : rank === 1 ? "🥈" : rank === 2 ? "🥉" : `#${rank + 1}`}</span>
                {p?.avatar ? <img src={p.avatar} alt="" style={{ width: px(18), height: px(18), borderRadius: "50%", objectFit: "cover" }} /> : <div style={{ width: px(18), height: px(18), borderRadius: "50%", background: COLORS.border }} />}
                <span style={{ flex: 1, fontSize: px(10), fontWeight: 700, color: COLORS.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p?.name || `${row.claimer.slice(0, 6)}…`}</span>
                <span style={{ fontSize: px(10), fontWeight: 800, color: COLORS.cyan, fontFamily: "'JetBrains Mono', monospace" }}>{row.total}</span>
              </div>
            );
          })}
        </div>
        {totalPages > 1 && (
          <div style={{ display: "flex", justifyContent: "center", gap: px(3), padding: `${px(2)}px 0 ${px(6)}px` }}>
            {Array.from({ length: totalPages }).map((_, i) => (<span key={i} style={{ width: px(4), height: px(4), borderRadius: "50%", background: i === (page % totalPages) ? COLORS.cyan : COLORS.border }} />))}
          </div>
        )}
      </div>
    </div>
  );
}

// ===== Discover: most active claim drops (searchable) =====
function DiscoverLeaderboards() {
  const [drops, setDrops] = useState(null);
  const [profiles, setProfiles] = useState({});
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`/api/data-polls?activeDrops=true`);
        const j = await r.json();
        if (!alive) return;
        const ds = j.drops || [];
        setDrops(ds);
        ds.forEach(async (d) => {
          const p = await fetchUPProfile(d.creator).catch(() => null);
          if (p && alive) setProfiles(prev => ({ ...prev, [d.creator.toLowerCase()]: p }));
          if (!d.tokenName || !d.tokenImage) {
            const m = await fetchTokenMeta(d.token).catch(() => null);
            if (m && alive) setDrops(prev => prev.map(x => (x.creator === d.creator && x.token === d.token) ? { ...x, tokenName: x.tokenName || m.name || "", tokenImage: x.tokenImage || m.image || "" } : x));
          }
        });
      } catch { if (alive) setDrops([]); }
    })();
    return () => { alive = false; };
  }, []);

  const filtered = (drops || []).filter(d => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    const p = profiles[d.creator.toLowerCase()];
    return (d.tokenName || "").toLowerCase().includes(q) || (p?.name || "").toLowerCase().includes(q) || d.creator.toLowerCase().includes(q);
  });

  return (
    <div>
      <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by creator or token…" style={{ width: "100%", padding: "10px 14px", marginBottom: 14, background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, color: COLORS.text, fontSize: 13, outline: "none", boxSizing: "border-box" }} />
      {drops == null ? (
        <div style={{ fontSize: 12, color: COLORS.textMuted, padding: "20px 0", textAlign: "center" }}>Loading…</div>
      ) : filtered.length === 0 ? (
        <div style={{ fontSize: 12, color: COLORS.textMuted, padding: "30px 0", textAlign: "center" }}>{drops.length === 0 ? "No claim drops yet — check back once creators start running them." : "No matches."}</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {filtered.map((d, idx) => {
            const p = profiles[d.creator.toLowerCase()];
            const key = `${d.creator}:${d.token}`;
            const isOpen = expanded === key;
            return (
              <div key={key} style={{ background: COLORS.card, border: `1px solid ${isOpen ? COLORS.cyan + "40" : COLORS.border}`, borderRadius: 12, overflow: "hidden" }}>
                <button onClick={() => setExpanded(isOpen ? null : key)} style={{ width: "100%", display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", background: "none", border: "none", cursor: "pointer", textAlign: "left" }}>
                  <span style={{ fontSize: 13, fontWeight: 800, color: idx < 3 ? COLORS.cyan : COLORS.textMuted, minWidth: 20 }}>{idx + 1}</span>
                  {d.tokenImage ? <img src={d.tokenImage} alt="" style={{ width: 38, height: 38, borderRadius: 10, objectFit: "cover", flexShrink: 0 }} /> : <span style={{ fontSize: 28 }}>🪙</span>}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.tokenName ? fmtToken(d.tokenName, d.tokenSymbol) : `${d.token.slice(0, 6)}…`}</div>
                    <div style={{ fontSize: 10, color: COLORS.textMuted, display: "flex", alignItems: "center", gap: 5 }}>
                      {p?.avatar ? <img src={p.avatar} alt="" style={{ width: 14, height: 14, borderRadius: "50%", objectFit: "cover" }} /> : null}
                      {p?.name || `${d.creator.slice(0, 6)}...${d.creator.slice(-4)}`}
                    </div>
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 800, color: COLORS.cyan, fontFamily: "'JetBrains Mono', monospace" }}>{d.claims}</div>
                    <div style={{ fontSize: 8, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "0.5px" }}>claims</div>
                  </div>
                  <span style={{ fontSize: 11, color: COLORS.textMuted, marginLeft: 4 }}>{isOpen ? "▲" : "▼"}</span>
                </button>
                {isOpen && (
                  <div style={{ padding: "0 14px 14px" }}>
                    <ClaimLeaderboard creatorAddr={d.creator} defaultToken={d.token} limit={100} rowsOnly />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ===== Claim leaderboard (reusable: NFT page, Control, Discover, overlay) =====
// Per-token: tabs across the tokens a creator has run drops for, banner for the active token.
function ClaimLeaderboard({ creatorAddr, WC = COLORS, limit = 100, defaultToken = null, onlyToken = null, compact = false, rowsOnly = false }) {
  const [tokens, setTokens] = useState(null); // [{token, tokenName, tokenImage, claims, total}]
  const [activeToken, setActiveToken] = useState(onlyToken ? onlyToken.toLowerCase() : (defaultToken ? defaultToken.toLowerCase() : null));
  const [rows, setRows] = useState(null);
  const [profiles, setProfiles] = useState({});

  // Token tabs
  useEffect(() => {
    let alive = true;
    const load = async () => {
      if (!creatorAddr) return;
      try {
        const r = await fetch(`/api/data-polls?claimTokens=true&creator=${creatorAddr}`);
        const j = await r.json();
        if (!alive) return;
        const toks = j.tokens || [];
        const shown = onlyToken ? toks.filter(t => t.token === onlyToken.toLowerCase()) : toks;
        setTokens(shown);
        setActiveToken(prev => {
          if (onlyToken) return onlyToken.toLowerCase();
          if (prev && shown.some(t => t.token === prev)) return prev;
          if (defaultToken && shown.find(t => t.token === defaultToken.toLowerCase())) return defaultToken.toLowerCase();
          return shown[0]?.token || null;
        });
        // Enrich any token missing a name/image (e.g. tokens that set only LSP4TokenSymbol)
        shown.forEach(async (t) => {
          if (!t.tokenName || !t.tokenImage) {
            const m = await fetchTokenMeta(t.token).catch(() => null);
            if (m && alive) setTokens(prev => prev.map(x => x.token === t.token ? { ...x, tokenName: x.tokenName || m.name || "", tokenImage: x.tokenImage || m.image || "" } : x));
          }
        });
      } catch { if (alive) setTokens([]); }
    };
    load();
    const t = setInterval(load, 20000);
    return () => { alive = false; clearInterval(t); };
  }, [creatorAddr, defaultToken, onlyToken]);

  // Leaderboard for the active token
  useEffect(() => {
    let alive = true;
    const load = async () => {
      if (!creatorAddr || !activeToken) { setRows([]); return; }
      try {
        const r = await fetch(`/api/data-polls?claimLeaderboard=true&creator=${creatorAddr}&token=${activeToken}`);
        const j = await r.json();
        if (!alive) return;
        const lb = (j.leaderboard || []).slice(0, limit);
        setRows(lb);
        lb.forEach(async (row) => {
          const p = await fetchUPProfile(row.claimer).catch(() => null);
          if (p && alive) setProfiles(prev => ({ ...prev, [row.claimer.toLowerCase()]: p }));
        });
      } catch { if (alive) setRows([]); }
    };
    load();
    const t = setInterval(load, 15000);
    return () => { alive = false; clearInterval(t); };
  }, [creatorAddr, activeToken, limit]);

  const medal = (i) => i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `#${i + 1}`;
  const surface = WC.surface || COLORS.surface;
  const card = WC.card || COLORS.card;
  const active = tokens ? tokens.find(t => t.token === activeToken) : null;
  const unit = active?.tokenName || "tokens";

  const rowsBody = (
    rows == null ? (
      <div style={{ fontSize: 11, color: WC.textMuted, padding: "8px 0" }}>Loading…</div>
    ) : rows.length === 0 ? (
      <div style={{ fontSize: 11, color: WC.textMuted, padding: "8px 0" }}>No claims yet.</div>
    ) : (
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {rows.map((row, i) => {
          const p = profiles[row.claimer.toLowerCase()];
          return (
            <div key={row.claimer} style={{ display: "flex", alignItems: "center", gap: 10, padding: compact ? "6px 8px" : "8px 10px", background: i < 3 ? `${WC.cyan}0c` : surface, borderRadius: 8, border: `1px solid ${i < 3 ? WC.cyan + "25" : "transparent"}` }}>
              <span style={{ fontSize: 12, fontWeight: 800, color: i < 3 ? WC.cyan : WC.textMuted, minWidth: 24, textAlign: "center" }}>{medal(i)}</span>
              {p?.avatar ? <img src={p.avatar} alt="" style={{ width: compact ? 22 : 26, height: compact ? 22 : 26, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} /> : <div style={{ width: compact ? 22 : 26, height: compact ? 22 : 26, borderRadius: "50%", background: WC.border, flexShrink: 0 }} />}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: WC.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p?.name || `${row.claimer.slice(0, 6)}...${row.claimer.slice(-4)}`}</div>
                <div style={{ fontSize: 9, color: WC.textMuted }}>{row.claims} claim{row.claims === 1 ? "" : "s"}</div>
              </div>
              <div style={{ textAlign: "right", flexShrink: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: WC.cyan, fontFamily: "'JetBrains Mono', monospace" }}>{row.total}</div>
                <div style={{ fontSize: 8, color: WC.textMuted, textTransform: "uppercase", letterSpacing: "0.5px", maxWidth: 70, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{unit}</div>
              </div>
            </div>
          );
        })}
      </div>
    )
  );

  const tabsBar = (!onlyToken && tokens && tokens.length >= 1) ? (
    <div style={{ display: "flex", gap: 6, marginBottom: 12, overflowX: "auto", paddingBottom: 2 }}>
      {tokens.map(t => (
        <button key={t.token} onClick={() => setActiveToken(t.token)} style={{ display: "flex", alignItems: "center", gap: 5, padding: "5px 10px", borderRadius: 20, border: `1px solid ${t.token === activeToken ? WC.cyan : WC.border}`, background: t.token === activeToken ? `${WC.cyan}18` : "transparent", color: t.token === activeToken ? WC.cyan : WC.textMuted, fontSize: 10, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0 }}>
          {t.tokenImage ? <img src={t.tokenImage} alt="" style={{ width: 16, height: 16, borderRadius: "50%", objectFit: "cover" }} /> : null}
          {t.tokenName || `${t.token.slice(0, 6)}…`}
        </button>
      ))}
    </div>
  ) : null;

  const banner = active ? (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
      {active.tokenImage ? <img src={active.tokenImage} alt="" style={{ width: 34, height: 34, borderRadius: 10, objectFit: "cover", flexShrink: 0 }} /> : <span style={{ fontSize: 26 }}>🏆</span>}
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: WC.text, fontFamily: "'Syne', sans-serif", letterSpacing: "0.5px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{(active.tokenName || "TOKEN").toUpperCase()} LEADERBOARD</div>
        <div style={{ fontSize: 9, color: WC.textMuted }}>{active.total} {unit} claimed · {active.claims} total claims</div>
      </div>
    </div>
  ) : null;

  // Empty (no claims for any token yet)
  if (tokens != null && tokens.length === 0) {
    if (compact) return <div style={{ fontSize: 11, color: WC.textMuted, padding: "8px 0" }}>No claims yet — launch a drop to get started.</div>;
    return (
      <div style={{ background: card, border: `1px solid ${WC.border}`, borderRadius: 14, padding: "16px 18px" }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: WC.text, fontFamily: "'Syne', sans-serif", marginBottom: 8 }}>🏆 Claim Leaderboards</div>
        <div style={{ fontSize: 11, color: WC.textMuted }}>No claims yet — launch a drop to get started.</div>
      </div>
    );
  }

  if (rowsOnly) return <div>{rowsBody}</div>;
  if (compact) return <div>{tabsBar}{rowsBody}</div>;

  return (
    <div style={{ background: card, border: `1px solid ${WC.border}`, borderRadius: 14, padding: "16px 18px" }}>
      <div style={{ fontSize: 13, fontWeight: 800, color: WC.text, fontFamily: "'Syne', sans-serif", marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>🏆 Token Leaderboards</div>
      {tabsBar}
      {banner}
      {rowsBody}
    </div>
  );
}

// ===== Viewer-facing claim card (tip page) =====
function ClaimDropCard({ creatorAddr, viewerAddr, connected, onConnect, WC }) {
  const [drop, setDrop] = useState(null);
  const [now, setNow] = useState(Math.floor(Date.now() / 1000));
  const [passphrase, setPassphrase] = useState("");
  const [claiming, setClaiming] = useState(false);
  const [claimedNow, setClaimedNow] = useState(false);
  const [claimedAmt, setClaimedAmt] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => { const t = setInterval(() => setNow(Math.floor(Date.now() / 1000)), 1000); return () => clearInterval(t); }, []);

  useEffect(() => {
    let alive = true;
    const fetchDrop = async () => {
      if (!creatorAddr) return;
      try {
        const q = viewerAddr ? `&claimer=${viewerAddr}` : "";
        const r = await fetch(`/api/data-polls?claimDrop=true&creator=${creatorAddr}${q}`);
        const j = await r.json();
        if (alive) setDrop(j.drop || null);
      } catch {}
    };
    fetchDrop();
    const t = setInterval(fetchDrop, 15000);
    return () => { alive = false; clearInterval(t); };
  }, [creatorAddr, viewerAddr]);

  if (!drop || !drop.live) return null;

  const secsLeft = Math.max(0, drop.endsAt - now);
  const mmss = `${String(Math.floor(secsLeft / 60)).padStart(2, "0")}:${String(secsLeft % 60).padStart(2, "0")}`;
  const claimed = drop.claimed || claimedNow;

  const handleClaim = async () => {
    if (!connected) { onConnect && onConnect(); return; }
    if (!viewerAddr) { setError("Connect your wallet first"); return; }
    setClaiming(true); setError("");
    try {
      const r = await fetch("/api/relay-action", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "claim-mint", creator: creatorAddr, claimer: viewerAddr, passphrase }) });
      const j = await r.json();
      if (j.success) { setClaimedNow(true); setClaimedAmt(j.amount); }
      else setError(j.error || "Claim failed");
    } catch { setError("Claim failed — try again"); }
    setClaiming(false);
  };

  return (
    <div style={{ marginBottom: 14, padding: "14px 16px", borderRadius: 12, background: `linear-gradient(135deg, ${WC.cyan}16, ${WC.accent}0a)`, border: `1px solid ${WC.cyan}45`, boxShadow: `0 0 24px ${WC.cyan}14` }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <span style={{ fontSize: 12, fontWeight: 800, color: WC.cyan, fontFamily: "'Syne', sans-serif", display: "flex", alignItems: "center", gap: 6 }}>🎁 Claim Drop</span>
        <span style={{ fontSize: 11, fontWeight: 700, color: secsLeft < 30 ? WC.red : WC.text, fontFamily: "'JetBrains Mono', monospace" }}>⏳ {mmss}</span>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: claimed ? 4 : 12 }}>
        {drop.tokenImage ? <img src={drop.tokenImage} alt="" style={{ width: 44, height: 44, borderRadius: 10, objectFit: "cover", flexShrink: 0 }} onError={e => { e.currentTarget.style.display = "none"; }} /> : <span style={{ fontSize: 32 }}>🪙</span>}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: WC.text }}>{drop.label || `Claim ${drop.amount} ${drop.tokenName || "tokens"}`}</div>
          <div style={{ fontSize: 11, color: WC.textMuted }}>{drop.amount} {drop.tokenName || "tokens"} minted to your UP</div>
        </div>
      </div>
      {claimed ? (
        <div style={{ textAlign: "center", padding: "8px 0 2px", fontSize: 13, fontWeight: 800, color: WC.green, fontFamily: "'Syne', sans-serif" }}>✓ Claimed{claimedAmt ? ` ${claimedAmt} ${drop.tokenName || ""}` : ""}!</div>
      ) : (
        <>
          {drop.hasPassphrase && connected && (
            <input value={passphrase} onChange={e => setPassphrase(e.target.value)} placeholder="Enter passphrase" style={{ width: "100%", padding: "9px 12px", marginBottom: 8, background: WC.surface, border: `1px solid ${WC.border}`, borderRadius: 8, color: WC.text, fontSize: 12, outline: "none", boxSizing: "border-box" }} />
          )}
          <button onClick={handleClaim} disabled={claiming} style={{ width: "100%", padding: "12px 0", background: WC.cyan, color: "#06141a", border: "none", borderRadius: 10, fontSize: 13, fontWeight: 800, fontFamily: "'Syne', sans-serif", cursor: claiming ? "wait" : "pointer", opacity: claiming ? 0.6 : 1 }}>
            {claiming ? "Claiming…" : connected ? (drop.hasPassphrase ? "Claim" : "Claim Now") : "Connect to Claim"}
          </button>
          {error && <div style={{ fontSize: 10, color: WC.red, textAlign: "center", marginTop: 6, lineHeight: 1.4 }}>{error}</div>}
        </>
      )}
    </div>
  );
}

function NFTGallery() {
  const [mounted, setMounted] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  useEffect(() => { requestAnimationFrame(() => setMounted(true)); }, []);

  const nftData = [
    {
      name: "Bronze Supporter",
      threshold: 100,
      color: "#00cc66",
      colorDim: "#00cc6612",
      icon: "🥉",
      emoji: "💚",
      unlocked: 3,
      supply: 8,
      desc: "First step into the FullSend community. Awarded to tippers who cross the 100 LYX lifetime threshold.",
      rarity: "Common",
      perks: ["Name on supporter wall", "Bronze chat badge", "Custom UP metadata tag"],
      recentMinters: [MOCK_PROFILES[3], MOCK_PROFILES[6], MOCK_PROFILES[5]],
      shapes: [
        { type: "circle", x: "72%", y: "22%", size: 44, opacity: 0.12 },
        { type: "circle", x: "28%", y: "68%", size: 30, opacity: 0.08 },
        { type: "diamond", x: "55%", y: "52%", size: 54, opacity: 0.05 },
        { type: "circle", x: "15%", y: "30%", size: 18, opacity: 0.06 },
      ],
    },
    {
      name: "Silver Champion",
      threshold: 300,
      color: "#4a9eff",
      colorDim: "#4a9eff12",
      icon: "🥈",
      emoji: "💙",
      unlocked: 2,
      supply: 8,
      desc: "Proven dedication to the stream. 300 LYX lifetime tips earns this mark of true support.",
      rarity: "Uncommon",
      perks: ["Silver chat badge", "Priority moderation queue", "Exclusive emote set"],
      recentMinters: [MOCK_PROFILES[1], MOCK_PROFILES[2]],
      shapes: [
        { type: "hexagon", x: "68%", y: "28%", size: 48, opacity: 0.1 },
        { type: "circle", x: "22%", y: "58%", size: 34, opacity: 0.08 },
        { type: "circle", x: "78%", y: "72%", size: 22, opacity: 0.06 },
        { type: "diamond", x: "40%", y: "35%", size: 28, opacity: 0.04 },
      ],
    },
    {
      name: "Gold Legend",
      threshold: 1000,
      color: "#b44aff",
      colorDim: "#b44aff12",
      icon: "🥇",
      emoji: "💜",
      unlocked: 1,
      supply: 8,
      desc: "Elite supporter status. Only the most dedicated earn this — 1,000 LYX lifetime puts you in rare company.",
      rarity: "Rare",
      perks: ["Gold chat badge", "Highlighted in leaderboard", "Custom alert sound", "Direct message to creator"],
      recentMinters: [MOCK_PROFILES[4]],
      shapes: [
        { type: "triangle", x: "62%", y: "25%", size: 50, opacity: 0.1 },
        { type: "circle", x: "28%", y: "62%", size: 38, opacity: 0.07 },
        { type: "diamond", x: "72%", y: "68%", size: 26, opacity: 0.09 },
        { type: "circle", x: "18%", y: "28%", size: 20, opacity: 0.05 },
      ],
    },
    {
      name: "Diamond Whale",
      threshold: 5000,
      color: "#ffd700",
      colorDim: "#ffd70012",
      icon: "💎",
      emoji: "🔥",
      unlocked: 1,
      supply: 8,
      desc: "The ultimate FullSend flex. 5,000 LYX lifetime — you are the backbone of this stream.",
      rarity: "Legendary",
      perks: ["Diamond animated badge", "Elite alert", "Permanent leaderboard crown", "1-of-1 metadata variant", "Creator shoutout on stream"],
      recentMinters: [MOCK_PROFILES[0]],
      shapes: [
        { type: "star", x: "58%", y: "28%", size: 52, opacity: 0.1 },
        { type: "circle", x: "22%", y: "48%", size: 32, opacity: 0.08 },
        { type: "circle", x: "78%", y: "62%", size: 24, opacity: 0.06 },
        { type: "diamond", x: "38%", y: "72%", size: 28, opacity: 0.07 },
        { type: "circle", x: "65%", y: "78%", size: 16, opacity: 0.05 },
      ],
    },
  ];

  // Mock user progress
  const userTotal = 82;
  const currentTierIdx = nftData.findIndex(n => userTotal < n.threshold);
  const nextTier = nftData[currentTierIdx] || nftData[nftData.length - 1];
  const prevThreshold = currentTierIdx > 0 ? nftData[currentTierIdx - 1].threshold : 0;
  const progress = ((userTotal - prevThreshold) / (nextTier.threshold - prevThreshold)) * 100;

  return (
    <div style={{
      maxWidth: 720, margin: "0 auto", padding: "28px 24px",
      opacity: mounted ? 1 : 0,
      transform: mounted ? "translateY(0)" : "translateY(15px)",
      transition: "all 0.5s cubic-bezier(0.16, 1, 0.3, 1)",
      position: "relative",
      minHeight: "70vh",
    }}>
      {/* Coming Soon Overlay */}
      <div style={{
        position: "absolute", inset: 0, zIndex: 10,
        display: "flex", alignItems: "flex-start", justifyContent: "center",
        paddingTop: 40,
        background: `${COLORS.bg}f5`, backdropFilter: "blur(12px)",
        borderRadius: 16,
      }}>
        <div style={{ textAlign: "center", maxWidth: 420 }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>💎</div>
          <div style={{
            fontFamily: "'Syne', sans-serif", fontSize: 28, fontWeight: 800,
            background: `linear-gradient(135deg, ${COLORS.purple}, ${COLORS.blue})`,
            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
            marginBottom: 8,
          }}>Coming Soon</div>
          <div style={{ fontSize: 13, color: COLORS.textSecondary, maxWidth: 340, margin: "0 auto 20px" }}>
            Tipping history will count retroactively because it's all stored on-chain even before more utility rails are deployed.
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, textAlign: "left" }}>
            {[
              { icon: "📊", title: "Leaderboard Rewards", desc: "Top tipper of the week or month automatically receives a special edition NFT", color: COLORS.accent },
              { icon: "🎨", title: "Creator NFTs", desc: "Design custom branded NFTs for your community. Use for loyalty perks, exclusive content, or leaderboard rewards", color: COLORS.purple },
              { icon: "🏆", title: "Milestone NFTs", desc: "Auto-mint when tippers hit thresholds — 100 LYX, 500 LYX, 1,000 LYX and beyond", color: COLORS.gold },
              { icon: "🪂", title: "Community Airdrops", desc: "Drop tokens or NFTs to all your followers at once — reward your whole community live during a stream", color: COLORS.cyan },
              { icon: "🏅", title: "Channel Memberships", desc: "Recurring LYX subscriptions with member-only perks, badges, priority chat, and exclusive drops", color: COLORS.gold },
            ].map((item, i) => (
              <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "10px 12px", background: `${COLORS.bg}80`, borderRadius: 8, border: `1px solid ${item.color}15` }}>
                <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>{item.icon}</span>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: item.color, marginBottom: 2 }}>{item.title}</div>
                  <div style={{ fontSize: 10, color: COLORS.textMuted, lineHeight: 1.5 }}>{item.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: 28 }}>
        <div style={{
          fontSize: 36, marginBottom: 2,
          animation: "float 3.5s ease-in-out infinite",
        }}>💎</div>
        <div style={{
          fontFamily: "'Syne', sans-serif",
          fontSize: 26, fontWeight: 800,
          background: `linear-gradient(135deg, ${COLORS.purple}, ${COLORS.blue})`,
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          letterSpacing: "-0.5px",
        }}>NFT Collection</div>
        <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 5 }}>
          Milestone NFTs minted as LSP8 tokens on LUKSO
        </div>
      </div>

      {/* User Progress Tracker */}
      <div style={{
        padding: 16, borderRadius: 12,
        background: COLORS.surface,
        border: `1px solid ${COLORS.border}`,
        marginBottom: 24,
        animation: "fadeInUp 0.5s ease-out 0.1s both",
      }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{
              width: 30, height: 30, borderRadius: 7,
              background: `${COLORS.accent}12`,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 15,
            }}>🎭</div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600 }}>AltsAnon</div>
              <div style={{ fontSize: 10, color: COLORS.textMuted }}>Your progress</div>
            </div>
          </div>
          <div style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 16, fontWeight: 700, color: COLORS.accent,
          }}>{userTotal} LYX</div>
        </div>

        {/* Progress bar with tier markers */}
        <div style={{ position: "relative", marginBottom: 6 }}>
          <div style={{
            height: 6, borderRadius: 3,
            background: `${COLORS.accent}10`,
            overflow: "hidden",
          }}>
            <div style={{
              height: "100%",
              width: `${(userTotal / 300) * 100}%`,
              background: `linear-gradient(90deg, ${COLORS.green}, ${COLORS.blue})`,
              borderRadius: 3,
              boxShadow: `0 0 8px ${COLORS.accent}30`,
              transition: "width 1.5s cubic-bezier(0.16, 1, 0.3, 1)",
            }} />
          </div>
          {/* Tier markers */}
          {nftData.map((tier, i) => (
            <div key={tier.name} style={{
              position: "absolute",
              top: -2,
              left: `${(tier.threshold / 300) * 100}%`,
              transform: "translateX(-50%)",
              width: 10, height: 10, borderRadius: "50%",
              background: userTotal >= tier.threshold ? tier.color : `${COLORS.bg}`,
              border: `2px solid ${userTotal >= tier.threshold ? tier.color : COLORS.textMuted}`,
              transition: "all 0.3s",
            }} />
          ))}
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 9, color: COLORS.textMuted }}>
          <span>0 LYX</span>
          <span style={{ color: nextTier.color, fontWeight: 600 }}>
            Next: {nextTier.name} at {nextTier.threshold} LYX ({nextTier.threshold - userTotal} to go)
          </span>
          <span>300 LYX</span>
        </div>
      </div>

      {/* NFT Grid */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        {nftData.map((nft, i) => {
          const isUnlocked = userTotal >= nft.threshold;
          const isSelected = selectedTier === i;
          return (
            <div
              key={nft.name}
              onClick={() => setSelectedTier(isSelected ? null : i)}
              style={{
                cursor: "pointer",
                opacity: mounted ? 1 : 0,
                transform: mounted ? "translateY(0)" : "translateY(20px)",
                transition: `all 0.6s cubic-bezier(0.16, 1, 0.3, 1) ${0.2 + i * 0.08}s`,
              }}
            >
              <div style={{
                background: COLORS.surface,
                border: `1px solid ${isSelected ? nft.color + "35" : COLORS.border}`,
                borderRadius: 14,
                overflow: "hidden",
                transition: "all 0.3s cubic-bezier(0.4,0,0.2,1)",
                boxShadow: isSelected ? `0 4px 30px ${nft.color}10, inset 0 1px 0 ${nft.color}10` : "none",
              }}
              onMouseEnter={e => {
                if (!isSelected) {
                  e.currentTarget.style.borderColor = `${nft.color}25`;
                  e.currentTarget.style.boxShadow = `0 2px 20px ${nft.color}06`;
                }
              }}
              onMouseLeave={e => {
                if (!isSelected) {
                  e.currentTarget.style.borderColor = COLORS.border;
                  e.currentTarget.style.boxShadow = "none";
                }
              }}>
                {/* Art area */}
                <div style={{
                  height: 170,
                  background: `linear-gradient(160deg, ${COLORS.bg}, ${nft.colorDim}, ${COLORS.bgAlt})`,
                  position: "relative",
                  overflow: "hidden",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  opacity: isUnlocked ? 1 : 0.55,
                  transition: "opacity 0.3s",
                }}>
                  {/* Generative shapes */}
                  {nft.shapes.map((shape, si) => (
                    <div key={si} style={{
                      position: "absolute",
                      left: shape.x, top: shape.y,
                      width: shape.size, height: shape.size,
                      opacity: shape.opacity,
                      border: `1.5px solid ${nft.color}`,
                      borderRadius:
                        shape.type === "circle" ? "50%" :
                        shape.type === "diamond" ? 3 :
                        shape.type === "hexagon" ? 4 : 0,
                      transform: `translate(-50%, -50%) ${
                        shape.type === "diamond" ? "rotate(45deg)" : ""
                      }`,
                      animation: isSelected ? `orbFloat${(si % 2) + 1} ${8 + si * 3}s ease-in-out infinite` : "none",
                      transition: "opacity 0.4s",
                    }} />
                  ))}

                  {/* Radial glow */}
                  <div style={{
                    position: "absolute", inset: 0,
                    background: `radial-gradient(circle at 50% 50%, ${nft.color}06 0%, transparent 60%)`,
                    animation: isSelected ? "glowPulse 2s ease-in-out infinite" : "none",
                  }} />

                  {/* Grid texture */}
                  <div style={{
                    position: "absolute", inset: 0,
                    backgroundImage: `linear-gradient(${nft.color}05 1px, transparent 1px), linear-gradient(90deg, ${nft.color}05 1px, transparent 1px)`,
                    backgroundSize: "24px 24px",
                    opacity: 0.5,
                  }} />

                  {/* Center icon */}
                  <div style={{
                    fontSize: 52,
                    filter: isUnlocked ? `drop-shadow(0 0 20px ${nft.color}30)` : "grayscale(0.5)",
                    animation: isSelected ? "float 3s ease-in-out infinite" : "none",
                    position: "relative", zIndex: 2,
                    transition: "filter 0.3s",
                  }}>{nft.icon}</div>

                  {/* Lock overlay */}
                  {!isUnlocked && (
                    <div style={{
                      position: "absolute", inset: 0,
                      background: `${COLORS.bg}40`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      zIndex: 3,
                    }}>
                      <div style={{
                        fontSize: 20, padding: "6px 12px", borderRadius: 8,
                        background: `${COLORS.bg}c0`,
                        backdropFilter: "blur(4px)",
                        border: `1px solid ${COLORS.border}`,
                        display: "flex", alignItems: "center", gap: 6,
                      }}>
                        🔒 <span style={{ fontSize: 10, color: COLORS.textMuted, fontWeight: 600 }}>{nft.threshold} LYX</span>
                      </div>
                    </div>
                  )}

                  {/* Rarity badge */}
                  <div style={{
                    position: "absolute", top: 10, right: 10,
                    fontSize: 8, fontWeight: 700,
                    color: nft.color,
                    background: `${COLORS.bg}c0`,
                    backdropFilter: "blur(6px)",
                    padding: "3px 7px", borderRadius: 4,
                    letterSpacing: "1px",
                    textTransform: "uppercase",
                    border: `1px solid ${nft.color}20`,
                    zIndex: 4,
                  }}>{nft.rarity}</div>

                  {/* Unlocked badge */}
                  {isUnlocked && (
                    <div style={{
                      position: "absolute", top: 10, left: 10,
                      fontSize: 8, fontWeight: 700,
                      color: COLORS.green,
                      background: `${COLORS.bg}c0`,
                      backdropFilter: "blur(6px)",
                      padding: "3px 7px", borderRadius: 4,
                      letterSpacing: "0.5px",
                      border: `1px solid ${COLORS.green}20`,
                      zIndex: 4,
                      display: "flex", alignItems: "center", gap: 3,
                    }}>
                      <span style={{ fontSize: 8 }}>✓</span> MINTED
                    </div>
                  )}
                </div>

                {/* Info */}
                <div style={{ padding: "14px 16px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                    <span style={{ fontSize: 13 }}>{nft.emoji}</span>
                    <span style={{
                      fontFamily: "'Syne', sans-serif",
                      fontSize: 14, fontWeight: 700, color: nft.color,
                    }}>{nft.name}</span>
                  </div>
                  <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 10, lineHeight: 1.4 }}>
                    {nft.desc}
                  </div>

                  {/* Mint count */}
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                    <span style={{ fontSize: 10, color: COLORS.textMuted, fontWeight: 600 }}>
                      {nft.unlocked}/{nft.supply} minted
                    </span>
                    <span style={{
                      fontSize: 10, fontWeight: 700,
                      fontFamily: "'JetBrains Mono', monospace",
                      color: nft.color,
                    }}>{nft.threshold} LYX</span>
                  </div>
                  <div style={{
                    height: 3, borderRadius: 2,
                    background: `${nft.color}10`,
                    overflow: "hidden",
                  }}>
                    <div style={{
                      height: "100%",
                      width: `${(nft.unlocked / nft.supply) * 100}%`,
                      background: `linear-gradient(90deg, ${nft.color}80, ${nft.color})`,
                      borderRadius: 2,
                      boxShadow: `0 0 6px ${nft.color}30`,
                    }} />
                  </div>

                  {/* Expanded details */}
                  {isSelected && (
                    <div style={{
                      marginTop: 12,
                      paddingTop: 12,
                      borderTop: `1px solid ${COLORS.border}`,
                      animation: "slideDown 0.3s ease-out",
                    }}>
                      {/* Perks */}
                      <div style={{ marginBottom: 10 }}>
                        <div style={{ fontSize: 9, color: COLORS.textMuted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "1px", marginBottom: 6 }}>
                          Perks
                        </div>
                        {nft.perks.map((perk, pi) => (
                          <div key={pi} style={{
                            fontSize: 10, color: COLORS.textSecondary,
                            padding: "3px 0",
                            display: "flex", alignItems: "center", gap: 6,
                          }}>
                            <span style={{ color: nft.color, fontSize: 8 }}>●</span>
                            {perk}
                          </div>
                        ))}
                      </div>

                      {/* Recent minters */}
                      <div>
                        <div style={{ fontSize: 9, color: COLORS.textMuted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "1px", marginBottom: 6 }}>
                          Holders
                        </div>
                        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                          {nft.recentMinters.map((m, mi) => (
                            <div key={mi} style={{
                              display: "flex", alignItems: "center", gap: 4,
                              padding: "3px 8px", borderRadius: 5,
                              background: COLORS.bgAlt,
                              border: `1px solid ${COLORS.border}`,
                              fontSize: 10,
                            }}>
                              <span style={{ fontSize: 12 }}>{m.avatar}</span>
                              <span style={{ color: COLORS.textSecondary, fontWeight: 500 }}>{m.name}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Token details */}
                      <div style={{
                        marginTop: 10, padding: "8px 10px", borderRadius: 6,
                        background: COLORS.bgAlt,
                        border: `1px solid ${COLORS.borderSubtle}`,
                      }}>
                        {[
                          { label: "Standard", value: "LSP8" },
                          { label: "Network", value: "LUKSO" },
                          { label: "Metadata", value: "IPFS" },
                        ].map(row => (
                          <div key={row.label} style={{
                            display: "flex", justifyContent: "space-between",
                            padding: "2px 0", fontSize: 9,
                          }}>
                            <span style={{ color: COLORS.textMuted, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>{row.label}</span>
                            <span style={{ color: COLORS.textSecondary, fontWeight: 600, fontFamily: "'JetBrains Mono', monospace" }}>{row.value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Bottom note */}
      <div style={{
        marginTop: 16, padding: 12, borderRadius: 8,
        background: COLORS.surface,
        border: `1px solid ${COLORS.border}`,
        fontSize: 11, color: COLORS.textMuted, lineHeight: 1.6,
        display: "flex", alignItems: "flex-start", gap: 8,
      }}>
        <span style={{ fontSize: 14, flexShrink: 0, marginTop: 1 }}>⛓️</span>
        <div>
          <span style={{ fontWeight: 700, color: COLORS.textSecondary }}>On-chain NFTs — </span>
          Auto-minted as LSP8 tokens when a tipper crosses a lifetime threshold. Metadata + placeholder art pinned to IPFS. Each token links to the Universal Profile that earned it. Placeholder art shown — final generative art TBD.
        </div>
      </div>
    </div>
  );
}

// --- YOUTUBE CHAT --------------------------------------------
// --- CHAT HUB (V2 Preview) ----------------------------------
function ChatHub() {
  const [mounted, setMounted] = useState(false);
  useEffect(() => { requestAnimationFrame(() => setMounted(true)); }, []);

  return (
    <div style={{
      maxWidth: 720, margin: "0 auto", padding: "28px 24px",
      opacity: mounted ? 1 : 0,
      transform: mounted ? "translateY(0)" : "translateY(15px)",
      transition: "all 0.5s cubic-bezier(0.16, 1, 0.3, 1)",
    }}>
      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <div style={{
          fontFamily: "'Syne', sans-serif", fontSize: 22, fontWeight: 800,
          color: COLORS.text, marginBottom: 8, display: "flex", alignItems: "center", gap: 10,
        }}>
          <span style={{ fontSize: 24 }}>💬</span>
          Chat Features
          <span style={{
            fontSize: 9, fontWeight: 700, padding: "3px 8px", borderRadius: 4,
            background: `${COLORS.orange}15`, border: `1px solid ${COLORS.orange}25`,
            color: COLORS.orange, letterSpacing: "1px", textTransform: "uppercase",
          }}>V2</span>
        </div>
        <div style={{ fontSize: 12, color: COLORS.textSecondary, lineHeight: 1.7 }}>
          Chat integrations coming in V2 — a native stream overlay chat, plus YouTube, Twitch, and Kick live chat injection. All previewed below.
        </div>
      </div>

      {/* Stream Chat Overlay */}
      <div style={{
        background: COLORS.surface, border: `1px solid ${COLORS.border}`,
        borderRadius: 14, padding: "20px 18px", marginBottom: 20,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
          <span style={{ fontSize: 16 }}>🖥️</span>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text }}>Stream Chat Overlay</div>
            <div style={{ fontSize: 10, color: COLORS.textMuted }}>Transparent chat feed rendered directly on your stream via browser source</div>
          </div>
        </div>

        {/* Preview */}
        <div style={{
          position: "relative", aspectRatio: "16/9", borderRadius: 10, overflow: "hidden",
          background: "linear-gradient(160deg, #080812, #0e0e20, #0a0a15)",
          border: `1px solid ${COLORS.border}`, marginBottom: 12,
        }}>
          <div style={{
            position: "absolute", top: 0, left: 0, width: "38%", height: "100%",
          }}>
            <div style={{
              position: "absolute", top: 0, left: 0, right: 0, height: 40,
              background: `linear-gradient(180deg, #080812d0 0%, transparent 100%)`,
              zIndex: 1,
            }} />
            <div style={{
              padding: "24px 12px 10px",
              display: "flex", flexDirection: "column", justifyContent: "flex-end",
              height: "100%",
            }}>
              {[
                { name: "ChainWizard", msg: "this stream is fire 🔥" },
                { name: "LUKSOgang", msg: "UP's are underrated" },
                { name: "web3builder", msg: "LUKSO is the move" },
                { name: "chillvibes", msg: "gm everyone" },
                { name: "Dracos", msg: "full send baby!!" },
                { name: "MKTiaraGirl", msg: "love this community" },
                { name: "Arion", msg: "LFG 🚀" },
                { name: "Thalos", msg: "LUKSO will flip ETH" },
              ].map((m, i, arr) => (
                <div key={i} style={{
                  padding: "2px 0",
                  opacity: i >= arr.length - 2 ? 0.95 : 0.4 + (i / arr.length) * 0.5,
                }}>
                  <span style={{
                    fontSize: 11, fontFamily: "Arial, sans-serif",
                    textShadow: "0 1px 3px rgba(0,0,0,0.8), 0 0 8px rgba(0,0,0,0.5)",
                  }}>
                    <span style={{ color: "#3ea6ff", fontWeight: 700 }}>{m.name}</span>{" "}
                    <span style={{ color: "#ddd" }}>{m.msg}</span>
                  </span>
                </div>
              ))}
            </div>
          </div>
          <div style={{
            position: "absolute", top: "50%", left: "55%", transform: "translate(-50%, -50%)",
            color: COLORS.textMuted, fontSize: 10, opacity: 0.4, textAlign: "center",
          }}>
            Your stream content here
          </div>
        </div>

        <div style={{ fontSize: 11, color: COLORS.textSecondary, lineHeight: 1.7, marginBottom: 10 }}>
          A Twitch-style transparent chat feed that renders directly on your stream overlay. Messages appear with text shadow for readability over any background. Positioned via the Custom Layout drag canvas on the Overlay tab.
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {["Platform-agnostic", "Transparent background", "Customizable name colors", "Drag-positionable"].map(tag => (
            <span key={tag} style={{
              fontSize: 9, padding: "3px 8px", borderRadius: 4,
              background: `${COLORS.blue}10`, border: `1px solid ${COLORS.blue}15`,
              color: COLORS.blue, fontWeight: 600,
            }}>{tag}</span>
          ))}
        </div>

        <div style={{
          marginTop: 12, padding: "8px 12px", borderRadius: 6,
          background: `${COLORS.orange}08`, border: `1px solid ${COLORS.orange}12`,
          fontSize: 10, color: COLORS.orange, fontWeight: 600,
        }}>
          ⚠ V2 — Requires live chat source integration (YouTube Data API, Kick, Twitch)
        </div>
      </div>

      {/* YouTube Live Chat Injection */}
      <div style={{
        background: COLORS.surface, border: `1px solid ${COLORS.border}`,
        borderRadius: 14, padding: "20px 18px",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
          <svg width="20" height="14" viewBox="0 0 20 14">
            <rect fill="#FF0000" width="20" height="14" rx="3" />
            <polygon fill="#FFF" points="8,3 8,11 14,7" />
          </svg>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text }}>YouTube Live Chat Injection</div>
            <div style={{ fontSize: 10, color: COLORS.textMuted }}>Tip alerts posted directly into your YouTube stream's live chat</div>
          </div>
          <span style={{
            marginLeft: "auto", fontSize: 9, fontWeight: 700, padding: "3px 8px", borderRadius: 4,
            background: `${COLORS.orange}15`, border: `1px solid ${COLORS.orange}25`,
            color: COLORS.orange, letterSpacing: "1px", textTransform: "uppercase",
          }}>V2</span>
        </div>

        {/* Inline YouTubeChat preview */}
        <YouTubeChat />
      </div>

      {/* Twitch Live Chat Injection */}
      <div style={{
        background: COLORS.surface, border: `1px solid ${COLORS.border}`,
        borderRadius: 14, padding: "20px 18px", marginTop: 20,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
          <svg width="20" height="20" viewBox="0 0 20 20">
            <path fill="#9146FF" d="M4 0L1 3v14h5v3l3-3h3l5-5V0H4zm12 9l-3 3h-3l-2.5 2.5V12H5V2h11v7z"/>
            <path fill="#9146FF" d="M12 4h1.5v4.5H12V4zM9 4h1.5v4.5H9V4z"/>
          </svg>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text }}>Twitch Live Chat Injection</div>
            <div style={{ fontSize: 10, color: COLORS.textMuted }}>Tip alerts posted directly into your Twitch stream's live chat</div>
          </div>
          <span style={{
            marginLeft: "auto", fontSize: 9, fontWeight: 700, padding: "3px 8px", borderRadius: 4,
            background: `${COLORS.orange}15`, border: `1px solid ${COLORS.orange}25`,
            color: COLORS.orange, letterSpacing: "1px", textTransform: "uppercase",
          }}>V2</span>
        </div>

        <div style={{
          padding: "24px 18px", borderRadius: 10,
          background: `linear-gradient(135deg, #18181b, #0e0e10)`,
          border: `1px solid ${COLORS.border}`, marginBottom: 12, textAlign: "center",
        }}>
          <div style={{ fontSize: 32, marginBottom: 8, opacity: 0.6 }}>💬</div>
          <div style={{ fontSize: 12, color: COLORS.textSecondary, marginBottom: 4 }}>Twitch IRC Chat Integration</div>
          <div style={{ fontSize: 10, color: COLORS.textMuted }}>Connect your Twitch channel to inject tip alerts into your live chat feed</div>
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {["Twitch IRC API", "Tip alerts in chat", "Custom bot name", "Emote support"].map(tag => (
            <span key={tag} style={{
              fontSize: 9, padding: "3px 8px", borderRadius: 4,
              background: "#9146FF10", border: "1px solid #9146FF15",
              color: "#9146FF", fontWeight: 600,
            }}>{tag}</span>
          ))}
        </div>
      </div>

      {/* Kick Live Chat Injection */}
      <div style={{
        background: COLORS.surface, border: `1px solid ${COLORS.border}`,
        borderRadius: 14, padding: "20px 18px", marginTop: 20,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
          <div style={{
            width: 20, height: 20, borderRadius: 4, background: "#53FC18",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 11, fontWeight: 900, color: "#000",
          }}>K</div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text }}>Kick Live Chat Injection</div>
            <div style={{ fontSize: 10, color: COLORS.textMuted }}>Tip alerts posted directly into your Kick stream's live chat</div>
          </div>
          <span style={{
            marginLeft: "auto", fontSize: 9, fontWeight: 700, padding: "3px 8px", borderRadius: 4,
            background: `${COLORS.orange}15`, border: `1px solid ${COLORS.orange}25`,
            color: COLORS.orange, letterSpacing: "1px", textTransform: "uppercase",
          }}>V2</span>
        </div>

        <div style={{
          padding: "24px 18px", borderRadius: 10,
          background: `linear-gradient(135deg, #0a1a0a, #0e1e0e)`,
          border: `1px solid ${COLORS.border}`, marginBottom: 12, textAlign: "center",
        }}>
          <div style={{ fontSize: 32, marginBottom: 8, opacity: 0.6 }}>💬</div>
          <div style={{ fontSize: 12, color: COLORS.textSecondary, marginBottom: 4 }}>Kick Chat Integration</div>
          <div style={{ fontSize: 10, color: COLORS.textMuted }}>Connect your Kick channel to inject tip alerts into your live chat feed</div>
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {["Kick API", "Tip alerts in chat", "Custom bot name", "Sub-only mode support"].map(tag => (
            <span key={tag} style={{
              fontSize: 9, padding: "3px 8px", borderRadius: 4,
              background: "#53FC1810", border: "1px solid #53FC1815",
              color: "#53FC18", fontWeight: 600,
            }}>{tag}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

function YouTubeChat() {
  const [messages, setMessages] = useState([]);
  const chatRef = useRef(null);

  useEffect(() => {
    const mockChat = [
      { id: 1, type: "chat", name: "ChainWizard", text: "anyone else hype for this stream?", time: "2m ago" },
      { id: 2, type: "chat", name: "LUKSOgang", text: "UP's are so underrated fr", time: "2m ago" },
      { id: 3, type: "tip", tier: TIERS.micro, name: "NewbieViewer", amount: 2, text: "", time: "1m ago" },
      { id: 4, type: "chat", name: "web3builder", text: "this is the way", time: "1m ago" },
      { id: 5, type: "tip", tier: TIERS.green, name: "Dracos", avatar: "👑", amount: 7, text: "Keep it up!", time: "45s ago" },
      { id: 6, type: "chat", name: "chillvibes", text: "what chain is this on?", time: "30s ago" },
      { id: 7, type: "chat", name: "LUKSOgang", text: "LUKSO! It's all on Universal Profiles", time: "25s ago" },
      { id: 8, type: "tip", tier: TIERS.blue, name: "Arion", avatar: "🦍", amount: 15, text: "FullSend is the future of streaming", time: "15s ago" },
      { id: 9, type: "tip", tier: TIERS.purple, name: "ChainWizard", avatar: "🧙", amount: 30, text: "💜 Dropping a bag because this deserves it", time: "10s ago" },
      { id: 10, type: "chat", name: "ChainWizard", text: "WHALE ALERT 🐋", time: "8s ago" },
      { id: 11, type: "tip", tier: TIERS.fire, name: "Thalos", avatar: "🤖", amount: 100, text: "FULLSEND!! No middleman, no cut. This is Web3.", time: "5s ago" },
      { id: 12, type: "chat", name: "web3builder", text: "HOLY SMOKES 100 LYX", time: "3s ago" },
    ];
    setMessages(mockChat);
  }, []);

  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [messages]);

  const revealSet = useStaggerReveal(messages.length, 80);

  return (
    <div style={{
      maxWidth: 420, margin: "0 auto",
    }}>
      {/* YouTube Chat Header */}
      <div style={{
        background: "#1f1f1f",
        borderRadius: "12px 12px 0 0",
        padding: "11px 16px",
        display: "flex", alignItems: "center", gap: 8,
        borderBottom: "1px solid #333",
      }}>
        <svg width="20" height="14" viewBox="0 0 20 14">
          <rect fill="#FF0000" width="20" height="14" rx="3" />
          <polygon fill="#FFF" points="8,3 8,11 14,7" />
        </svg>
        <span style={{
          fontFamily: "'Outfit', sans-serif",
          fontSize: 13, fontWeight: 600, color: "#fff",
        }}>Live Chat</span>
        <div style={{
          marginLeft: "auto", fontSize: 9,
          color: "#777",
          display: "flex", alignItems: "center", gap: 4,
        }}>
          <div style={{
            width: 5, height: 5, borderRadius: "50%",
            background: COLORS.accent,
            boxShadow: `0 0 6px ${COLORS.accent}`,
            animation: "pulse 2s infinite",
          }} />
          Powered by FullSend
        </div>
      </div>

      {/* Chat Messages */}
      <div ref={chatRef} style={{
        background: "#141414",
        height: 460,
        overflowY: "auto",
        padding: "6px 0",
      }}>
        {messages.map((msg, idx) => {
          const isVisible = revealSet.has(idx);
          return (
            <div key={msg.id} style={{
              padding: "5px 14px",
              display: "flex",
              gap: 8,
              alignItems: "flex-start",
              background: msg.type === "tip" ? `${msg.tier.color}06` : "transparent",
              borderLeft: msg.type === "tip" ? `2px solid ${msg.tier.color}50` : "2px solid transparent",
              marginBottom: 1,
              transition: "background 0.3s",
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? "translateX(0)" : "translateX(-8px)",
              transitionDelay: `${idx * 0.04}s`,
            }}>
              {msg.type === "tip" ? (
                <div style={{ fontSize: 12, lineHeight: 1.7, fontFamily: "'Outfit', Arial, sans-serif", display: "flex", alignItems: "center", gap: 6 }}>
                  {(() => {
                    const supMin = parseFloat(window.__fullsend_settings_data?.customTiers?.supporter?.min) || 5;
                    return msg.avatar && msg.amount >= supMin;
                  })() ? (
                    <span style={{
                      width: 20, height: 20, borderRadius: 5,
                      background: `${msg.tier.color}20`,
                      border: `1px solid ${msg.tier.color}30`,
                      display: "inline-flex", alignItems: "center", justifyContent: "center",
                      fontSize: 11, flexShrink: 0,
                    }}>{msg.avatar}</span>
                  ) : (
                    <span style={{ fontSize: 13 }}>{msg.tier.emoji}</span>
                  )}
                  <span>
                    <span style={{ color: msg.tier.color, fontWeight: 700 }}>@{msg.name}</span>{" "}
                    <span style={{ color: "#999" }}>
                      tipped {msg.amount} LYX
                      {msg.text ? ` — "${msg.text}"` : ""}
                    </span>
                  </span>
                </div>
              ) : (
                <div style={{ fontSize: 12, lineHeight: 1.7, fontFamily: "'Outfit', Arial, sans-serif" }}>
                  <span style={{ color: "#3ea6ff", fontWeight: 600 }}>{msg.name}</span>{" "}
                  <span style={{ color: "#ccc" }}>{msg.text}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Chat Input */}
      <div style={{
        background: "#1f1f1f",
        borderRadius: "0 0 12px 12px",
        padding: "10px 14px",
        display: "flex", gap: 8,
        borderTop: "1px solid #333",
      }}>
        <input disabled placeholder="Tips appear here via FullSend..." style={{
          flex: 1, padding: "8px 12px",
          background: "#2a2a2a", border: "1px solid #3a3a3a",
          borderRadius: 6, color: "#666",
          fontFamily: "'Outfit', Arial, sans-serif",
          fontSize: 12,
        }} />
      </div>

      {/* Explanation */}
      <div style={{
        marginTop: 14, padding: 14, borderRadius: 10,
        background: COLORS.surface,
        border: `1px solid ${COLORS.border}`,
        fontSize: 11, color: COLORS.textMuted, lineHeight: 1.7,
      }}>
        <div style={{
          fontWeight: 700, color: COLORS.textSecondary, marginBottom: 5,
          fontSize: 10, letterSpacing: "1px", textTransform: "uppercase",
        }}>How it works in production</div>
        Tips inject into YouTube Live Chat via YouTube Data API v3. Under $5 shows name + amount. $5+ includes message. All emoji-coded by tier.
        <div style={{
          marginTop: 8, padding: "6px 10px", borderRadius: 5,
          background: `${COLORS.orange}08`,
          border: `1px solid ${COLORS.orange}12`,
          color: COLORS.orange, fontSize: 10, fontWeight: 600,
        }}>
          ⚠ Phase 2 — Requires Google OAuth + liveChatId wiring
        </div>
      </div>
    </div>
  );
}

// ===========================================================
// TIPPER MODE VIEWS
// ===========================================================

// --- TIPPER HOME ---------------------------------------------
function TipperHome({ onNavigate }) {
  const [mounted, setMounted] = useState(false);
  const [tipperData, setTipperData] = useState(null);
  useEffect(() => { requestAnimationFrame(() => setMounted(true)); }, []);

  const tipperAddr = window.__fullsend_profile?.address;
  const upProfile = window.__fullsend_profile;
  useEffect(() => {
    if (!tipperAddr) return;
    fetch(`/api/data-tips?action=tipper-stats&tipper=${tipperAddr}`)
      .then(r => r.json())
      .then(data => setTipperData(data))
      .catch(() => {});
  }, [tipperAddr]);

  const summary = tipperData?.summary || {};
  const perCreator = tipperData?.perCreator || [];
  const rankColors = [COLORS.accent, COLORS.gold, COLORS.blue, COLORS.purple, COLORS.orange];
  const d = {
    profile: {
      name: upProfile?.name || `${(tipperAddr || "").slice(0, 6)}...${(tipperAddr || "").slice(-4)}`,
      avatar: upProfile?.avatar || STOCK_AVATAR,
      address: tipperAddr || "Not connected",
    },
    totalTipped: parseFloat(summary.total_spent || 0),
    totalChannels: parseInt(summary.creators_supported || 0),
    totalTips: parseInt(summary.total_tips || 0),
    channels: perCreator.map((c, i) => ({
      channel: { name: `${c.creator.slice(0, 6)}...${c.creator.slice(-4)}`, avatar: "💰", color: rankColors[i % rankColors.length] },
      rank: i + 1,
      total: parseFloat(c.total || 0),
      tips: parseInt(c.count || 0),
      nft: parseFloat(c.total || 0) >= 300 ? "🔥 Diamond"
        : parseFloat(c.total || 0) >= 200 ? "💜 Gold"
        : parseFloat(c.total || 0) >= 100 ? "💙 Silver"
        : parseFloat(c.total || 0) >= 50 ? "💚 Bronze" : null,
    })),
    nfts: [],
  };
  // Build earned NFTs list
  perCreator.forEach((c, i) => {
    const total = parseFloat(c.total || 0);
    const channel = { name: `${c.creator.slice(0, 6)}...${c.creator.slice(-4)}`, avatar: "💰", color: rankColors[i % rankColors.length] };
    if (total >= 100) d.nfts.push({ channel, tier: "Bronze Supporter", icon: "🥉", color: "#00cc66", threshold: 100 });
    if (total >= 300) d.nfts.push({ channel, tier: "Silver Champion", icon: "🥈", color: "#4a9eff", threshold: 300 });
    if (total >= 1000) d.nfts.push({ channel, tier: "Gold Legend", icon: "🥇", color: "#b44aff", threshold: 1000 });
    if (total >= 5000) d.nfts.push({ channel, tier: "Diamond Whale", icon: "💎", color: "#ffd700", threshold: 5000 });
  });

  return (
    <div style={{ maxWidth: 720, margin: "0 auto", padding: "0 24px", position: "relative" }}>
      {/* Ambient bg */}
      <div style={{ position: "fixed", inset: 0, zIndex: -1, overflow: "hidden", pointerEvents: "none" }}>
        <div style={{
          position: "absolute", top: "15%", left: "25%",
          width: 400, height: 400, borderRadius: "50%",
          background: `radial-gradient(circle, ${COLORS.purple}06 0%, transparent 70%)`,
          animation: "orbFloat1 22s ease-in-out infinite",
          filter: "blur(60px)",
        }} />
        <div style={{
          position: "absolute", top: "50%", right: "15%",
          width: 300, height: 300, borderRadius: "50%",
          background: `radial-gradient(circle, ${COLORS.blue}05 0%, transparent 70%)`,
          animation: "orbFloat2 18s ease-in-out infinite",
          filter: "blur(50px)",
        }} />
      </div>

      {/* Profile Header */}
      <div style={{
        textAlign: "center",
        paddingTop: 50, paddingBottom: 32,
        opacity: mounted ? 1 : 0,
        transform: mounted ? "translateY(0)" : "translateY(20px)",
        transition: "all 0.7s cubic-bezier(0.16, 1, 0.3, 1)",
      }}>
        <div style={{
          width: 72, height: 72, borderRadius: 16,
          background: `linear-gradient(135deg, ${COLORS.purple}25, ${COLORS.blue}25)`,
          border: `2px solid ${COLORS.purple}30`,
          display: "flex", alignItems: "center", justifyContent: "center",
          margin: "0 auto 12px", fontSize: 34,
          boxShadow: `0 0 30px ${COLORS.purple}15`,
        }}>{isImageAvatar(d.profile.avatar) ? <img src={d.profile.avatar} style={{ width: "100%", height: "100%", borderRadius: 14, objectFit: "cover" }} onError={e => { e.target.src = STOCK_AVATAR; }} /> : "👤"}</div>
        <div style={{
          fontFamily: "'Syne', sans-serif",
          fontSize: 24, fontWeight: 800,
          marginBottom: 4,
        }}>{d.profile.name}</div>
        <div style={{ fontSize: 11, color: COLORS.textMuted, fontFamily: "'JetBrains Mono', monospace" }}>
          {d.profile.address}
        </div>
      </div>

      {/* Stats Row */}
      <div style={{
        display: "grid", gridTemplateColumns: "1fr 1fr 1fr",
        gap: 10, marginBottom: 24,
        opacity: mounted ? 1 : 0,
        transform: mounted ? "translateY(0)" : "translateY(15px)",
        transition: "all 0.7s cubic-bezier(0.16, 1, 0.3, 1) 0.1s",
      }}>
        {[
          { label: "Total Tipped", value: `${d.totalTipped.toFixed(2)} LYX`, color: COLORS.purple },
          { label: "Channels", value: d.totalChannels, color: COLORS.blue },
          { label: "Total Tips", value: d.totalTips, color: COLORS.accent },
        ].map((stat, i) => (
          <div key={stat.label} style={{
            background: COLORS.surface,
            border: `1px solid ${COLORS.border}`,
            borderRadius: 10, padding: "14px 12px",
            textAlign: "center",
          }}>
            <div style={{
              fontFamily: "'Syne', sans-serif",
              fontSize: 20, fontWeight: 800, color: stat.color,
            }}>{stat.value}</div>
            <div style={{ fontSize: 9, color: COLORS.textMuted, marginTop: 3, letterSpacing: "1.5px", textTransform: "uppercase", fontWeight: 600 }}>
              {stat.label}
            </div>
          </div>
        ))}
      </div>

      {/* Feature Cards */}
      <div style={{
        display: "flex", flexDirection: "column", gap: 10, marginBottom: 24,
        opacity: mounted ? 1 : 0,
        transform: mounted ? "translateY(0)" : "translateY(15px)",
        transition: "all 0.7s cubic-bezier(0.16, 1, 0.3, 1) 0.2s",
      }}>
        {/* Rankings Card */}
        <button onClick={() => onNavigate("rankings")} style={{
          width: "100%",
          background: COLORS.surface,
          border: `1px solid ${COLORS.border}`,
          borderRadius: 14,
          padding: "18px 20px",
          cursor: "pointer",
          textAlign: "left",
          display: "flex",
          alignItems: "center",
          gap: 16,
          transition: "all 0.3s cubic-bezier(0.4,0,0.2,1)",
        }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = `${COLORS.blue}25`; e.currentTarget.style.transform = "translateY(-2px)"; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = COLORS.border; e.currentTarget.style.transform = "translateY(0)"; }}>
          <div style={{ fontSize: 28 }}>📊</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: "'Syne', sans-serif", fontSize: 15, fontWeight: 700, color: COLORS.blue, marginBottom: 2 }}>
              My Rankings
            </div>
            <div style={{ fontSize: 11, color: COLORS.textMuted, lineHeight: 1.5 }}>
              Your position on {d.totalChannels} channel leaderboards
            </div>
          </div>
          {/* Mini preview */}
          <div style={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "flex-end" }}>
            {d.channels.slice(0, 2).map((ch, i) => (
              <span key={i} style={{ fontSize: 9, color: COLORS.textSecondary }}>
                #{ch.rank} on {ch.channel.name.split(" ")[0]}
              </span>
            ))}
          </div>
          <div style={{ fontSize: 14, color: COLORS.textMuted, opacity: 0.3 }}>→</div>
        </button>

        {/* NFTs Card */}
        <button onClick={() => onNavigate("nfts-profile")} style={{
          width: "100%",
          background: COLORS.surface,
          border: `1px solid ${COLORS.border}`,
          borderRadius: 14,
          padding: "18px 20px",
          cursor: "pointer",
          textAlign: "left",
          display: "flex",
          alignItems: "center",
          gap: 16,
          transition: "all 0.3s cubic-bezier(0.4,0,0.2,1)",
        }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = `${COLORS.purple}25`; e.currentTarget.style.transform = "translateY(-2px)"; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = COLORS.border; e.currentTarget.style.transform = "translateY(0)"; }}>
          <div style={{ fontSize: 28 }}>💎</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: "'Syne', sans-serif", fontSize: 15, fontWeight: 700, color: COLORS.purple, marginBottom: 2 }}>
              My NFTs
            </div>
            <div style={{ fontSize: 11, color: COLORS.textMuted, lineHeight: 1.5 }}>
              {d.nfts.length} milestone NFTs earned across channels
            </div>
          </div>
          <div style={{ display: "flex", gap: 3 }}>
            {d.nfts.map((n, i) => (
              <span key={i} style={{ fontSize: 16 }}>{n.icon}</span>
            ))}
          </div>
          <div style={{ fontSize: 14, color: COLORS.textMuted, opacity: 0.3 }}>→</div>
        </button>

        {/* Activity Card */}
        <button onClick={() => onNavigate("activity")} style={{
          width: "100%",
          background: COLORS.surface,
          border: `1px solid ${COLORS.border}`,
          borderRadius: 14,
          padding: "18px 20px",
          cursor: "pointer",
          textAlign: "left",
          display: "flex",
          alignItems: "center",
          gap: 16,
          transition: "all 0.3s cubic-bezier(0.4,0,0.2,1)",
        }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = `${COLORS.accent}25`; e.currentTarget.style.transform = "translateY(-2px)"; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = COLORS.border; e.currentTarget.style.transform = "translateY(0)"; }}>
          <div style={{ fontSize: 28 }}>⚡</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: "'Syne', sans-serif", fontSize: 15, fontWeight: 700, color: COLORS.accent, marginBottom: 2 }}>
              Recent Activity
            </div>
            <div style={{ fontSize: 11, color: COLORS.textMuted, lineHeight: 1.5 }}>
              Your tipping history across all channels
            </div>
          </div>
          <div style={{ fontSize: 14, color: COLORS.textMuted, opacity: 0.3 }}>→</div>
        </button>

        {/* My Stats Card */}
        <button onClick={() => onNavigate("tipper-analytics")} style={{
          width: "100%",
          background: COLORS.surface,
          border: `1px solid ${COLORS.border}`,
          borderRadius: 14,
          padding: "18px 20px",
          cursor: "pointer",
          textAlign: "left",
          display: "flex",
          alignItems: "center",
          gap: 16,
          transition: "all 0.3s cubic-bezier(0.4,0,0.2,1)",
        }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = `${COLORS.cyan}25`; e.currentTarget.style.transform = "translateY(-2px)"; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = COLORS.border; e.currentTarget.style.transform = "translateY(0)"; }}>
          <div style={{ fontSize: 28 }}>📈</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: "'Syne', sans-serif", fontSize: 15, fontWeight: 700, color: COLORS.cyan, marginBottom: 2 }}>
              My Stats
            </div>
            <div style={{ fontSize: 11, color: COLORS.textMuted, lineHeight: 1.5 }}>
              Your tipping analytics, streaks, and insights
            </div>
          </div>
          <div style={{ fontSize: 14, color: COLORS.textMuted, opacity: 0.3 }}>→</div>
        </button>

        {/* Discover Card */}
        <button onClick={() => onNavigate("discover")} style={{
          width: "100%",
          background: COLORS.surface,
          border: `1px solid ${COLORS.border}`,
          borderRadius: 14,
          padding: "18px 20px",
          cursor: "pointer",
          textAlign: "left",
          display: "flex",
          alignItems: "center",
          gap: 16,
          transition: "all 0.3s cubic-bezier(0.4,0,0.2,1)",
        }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = `${COLORS.gold}25`; e.currentTarget.style.transform = "translateY(-2px)"; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = COLORS.border; e.currentTarget.style.transform = "translateY(0)"; }}>
          <div style={{ fontSize: 28 }}>🔍</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: "'Syne', sans-serif", fontSize: 15, fontWeight: 700, color: COLORS.gold, marginBottom: 2 }}>
              Discover Creators
            </div>
            <div style={{ fontSize: 11, color: COLORS.textMuted, lineHeight: 1.5 }}>
              Find creators to support and follow
            </div>
          </div>
          <div style={{ fontSize: 14, color: COLORS.textMuted, opacity: 0.3 }}>→</div>
        </button>
      </div>
    </div>
  );
}
function TipperRankings() {
  const [mounted, setMounted] = useState(false);
  const [tipperData, setTipperData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { requestAnimationFrame(() => setMounted(true)); }, []);

  const tipperAddr = window.__fullsend_profile?.address;
  useEffect(() => {
    if (!tipperAddr) { setLoading(false); return; }
    fetch(`/api/data-tips?action=tipper-stats&tipper=${tipperAddr}`)
      .then(r => r.json())
      .then(data => { setTipperData(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [tipperAddr]);

  const summary = tipperData?.summary || {};
  const rankColors = [COLORS.accent, COLORS.gold, COLORS.blue, COLORS.purple, COLORS.orange];
  const perCreator = tipperData?.perCreator || [];
  const creatorAddresses = perCreator.map(c => c.creator).filter(Boolean);
  const upProfiles = useUPProfiles(creatorAddresses);
  const d = {
    totalTipped: parseFloat(summary.total_spent || 0),
    totalChannels: parseInt(summary.creators_supported || 0),
    totalTips: parseInt(summary.total_tips || 0),
    channels: perCreator.map((c, i) => {
      const profile = upProfiles[c.creator?.toLowerCase()] || {};
      return {
        channel: { name: profile.name || `${c.creator.slice(0, 6)}...${c.creator.slice(-4)}`, avatar: profile.avatar || STOCK_AVATAR, color: rankColors[i % rankColors.length] },
        rank: i + 1,
        total: parseFloat(c.total || 0),
        tips: parseInt(c.count || 0),
        nft: parseFloat(c.total || 0) >= 300 ? "🔥 Diamond"
          : parseFloat(c.total || 0) >= 200 ? "💜 Gold"
          : parseFloat(c.total || 0) >= 100 ? "💙 Silver"
          : parseFloat(c.total || 0) >= 50 ? "💚 Bronze" : null,
      };
    }),
  };
  const revealSet = useStaggerReveal(d.channels.length, 80);

  return (
    <div style={{
      maxWidth: 620, margin: "0 auto", padding: "28px 24px",
      opacity: mounted ? 1 : 0,
      transform: mounted ? "translateY(0)" : "translateY(15px)",
      transition: "all 0.5s cubic-bezier(0.16, 1, 0.3, 1)",
    }}>
      {!window.__fullsend_profile?.address && (
        <div style={{ padding: "10px 16px", borderRadius: 8, background: `${COLORS.cyan}08`, border: `1px solid ${COLORS.cyan}15`, fontSize: 11, color: COLORS.cyan, textAlign: "center", marginBottom: 16 }}>
          Connect your Universal Profile to see your rankings
        </div>
      )}
      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: 28 }}>
        <div style={{ fontSize: 36, marginBottom: 2, animation: "float 3s ease-in-out infinite" }}>📊</div>
        <div style={{
          fontFamily: "'Syne', sans-serif",
          fontSize: 26, fontWeight: 800,
          background: `linear-gradient(135deg, ${COLORS.blue}, ${COLORS.purple})`,
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          letterSpacing: "-0.5px",
        }}>My Rankings</div>
        <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 5 }}>
          Your position on every channel you've tipped
        </div>
      </div>

      {/* Summary bar */}
      <div style={{
        display: "flex", gap: 8, marginBottom: 20,
        padding: "12px 14px", borderRadius: 10,
        background: COLORS.surface,
        border: `1px solid ${COLORS.border}`,
      }}>
        <div style={{ flex: 1, textAlign: "center" }}>
          <div style={{ fontFamily: "'Syne', sans-serif", fontSize: 18, fontWeight: 800, color: COLORS.purple }}>{d.totalTipped.toFixed(2)} LYX</div>
          <div style={{ fontSize: 9, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "1px", marginTop: 2 }}>Total Tipped</div>
        </div>
        <div style={{ width: 1, background: COLORS.border }} />
        <div style={{ flex: 1, textAlign: "center" }}>
          <div style={{ fontFamily: "'Syne', sans-serif", fontSize: 18, fontWeight: 800, color: COLORS.blue }}>#{d.channels.length > 0 ? Math.min(...d.channels.map(c => c.rank)) : "—"}</div>
          <div style={{ fontSize: 9, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "1px", marginTop: 2 }}>Best Rank</div>
        </div>
        <div style={{ width: 1, background: COLORS.border }} />
        <div style={{ flex: 1, textAlign: "center" }}>
          <div style={{ fontFamily: "'Syne', sans-serif", fontSize: 18, fontWeight: 800, color: COLORS.accent }}>{d.channels.length}</div>
          <div style={{ fontSize: 9, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "1px", marginTop: 2 }}>Channels</div>
        </div>
      </div>

      {/* Channel Rankings */}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {d.channels.map((ch, idx) => {
          const isVisible = revealSet.has(idx);
          const isTopRank = ch.rank <= 3;
          return (
            <div key={idx} style={{
              background: COLORS.surface,
              border: `1px solid ${isTopRank ? ch.channel.color + "20" : COLORS.border}`,
              borderRadius: 12,
              padding: "16px 18px",
              transition: "all 0.35s cubic-bezier(0.4,0,0.2,1)",
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? "translateX(0)" : "translateX(-20px)",
              cursor: "default",
            }}
            onMouseEnter={e => {
              e.currentTarget.style.borderColor = `${ch.channel.color}30`;
              e.currentTarget.style.background = COLORS.surfaceAlt;
            }}
            onMouseLeave={e => {
              e.currentTarget.style.borderColor = isTopRank ? ch.channel.color + "20" : COLORS.border;
              e.currentTarget.style.background = COLORS.surface;
            }}>
              {/* Channel info row */}
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 10,
                  background: `${ch.channel.color}12`,
                  border: `1px solid ${ch.channel.color}20`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 20, flexShrink: 0,
                }}>{isImageAvatar(ch.channel.avatar) ? <img src={ch.channel.avatar} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 10 }} onError={e => { e.target.src = STOCK_AVATAR; }} /> : "💰"}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: "'Syne', sans-serif", fontSize: 14, fontWeight: 700 }}>{ch.channel.name}</div>
                  <div style={{ fontSize: 10, color: COLORS.textMuted, display: "flex", alignItems: "center", gap: 6, marginTop: 2 }}>
                    <span>{ch.tips} tips sent</span>
                  </div>
                </div>
                {/* Rank badge */}
                <div style={{
                  display: "flex", flexDirection: "column", alignItems: "center",
                  padding: "6px 14px", borderRadius: 8,
                  background: isTopRank ? `${ch.channel.color}12` : `${COLORS.bg}80`,
                  border: `1px solid ${isTopRank ? ch.channel.color + "25" : COLORS.border}`,
                }}>
                  <div style={{
                    fontFamily: "'Syne', sans-serif",
                    fontSize: 20, fontWeight: 800,
                    color: isTopRank ? ch.channel.color : COLORS.textSecondary,
                  }}>#{ch.rank}</div>
                  <div style={{ fontSize: 8, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "0.5px" }}>Rank</div>
                </div>
              </div>

              {/* Stats row */}
              <div style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: "8px 10px", borderRadius: 7,
                background: `${COLORS.bg}60`,
              }}>
                <div style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 14, fontWeight: 700, color: ch.channel.color,
                }}>{typeof ch.total === "number" ? ch.total.toFixed(2) : ch.total} LYX</div>
                <div style={{ fontSize: 10, color: COLORS.textMuted }}>total tipped</div>
                {ch.nft && (
                  <span style={{
                    marginLeft: "auto",
                    fontSize: 9, padding: "3px 7px", borderRadius: 4,
                    background: COLORS.surfaceAlt,
                    border: `1px solid ${COLORS.border}`,
                    fontWeight: 600, color: COLORS.textSecondary,
                  }}>{ch.nft}</span>
                )}
                {!ch.nft && (
                  <span style={{
                    marginLeft: "auto",
                    fontSize: 9, color: COLORS.textMuted,
                  }}>No NFT yet</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// --- TIPPER NFTs ---------------------------------------------
// --- YOUR CLAIM DROP (creator self-view, My Profile > My NFTs) --
// Shows the CONNECTED creator's own active token drop (one slot per creator) so
// they can see its live claim count and jump to its settings. Claiming happens on
// the stream / tip page — not here — so there is no claim button.
function TipperClaimDrops() {
  const me = window.__fullsend_profile?.address;
  const [drop, setDrop] = useState(undefined); // undefined = loading, null = none
  useEffect(() => {
    if (!me) { setDrop(null); return; }
    let alive = true;
    const load = () => fetch(`/api/data-polls?claimDrop=true&creator=${me}`)
      .then(r => r.json())
      .then(j => { if (alive) setDrop(j.drop || null); })
      .catch(() => { if (alive) setDrop(null); });
    load();
    const t = setInterval(load, 20000);
    return () => { alive = false; clearInterval(t); };
  }, [me]);

  const live = !!(drop && drop.enabled);
  const goSettings = () => { if (window.__fullsend_go) window.__fullsend_go("creator", "control"); };

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
        <span style={{ fontFamily: "'Syne', sans-serif", fontSize: 20, fontWeight: 800, color: COLORS.cyan }}>🎁 Live Claim Drops</span>
        {live && (
          <span style={{ fontSize: 9, fontWeight: 800, color: COLORS.cyan, background: `${COLORS.cyan}15`, border: `1px solid ${COLORS.cyan}30`, borderRadius: 5, padding: "2px 7px", fontFamily: "'JetBrains Mono', monospace" }}>LIVE</span>
        )}
      </div>
      <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 12 }}>Your current token drop claim</div>

      {drop === undefined ? (
        <div style={{ padding: "24px 0", textAlign: "center", color: COLORS.textMuted, fontSize: 12 }}>Loading…</div>
      ) : !drop ? (
        <div style={{ padding: 18, borderRadius: 12, background: COLORS.surface, border: `1px solid ${COLORS.border}`, textAlign: "center" }}>
          <div style={{ fontSize: 24 }}>🎁</div>
          <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 6 }}>No token drop set up yet — create one in Control to run it on stream.</div>
          <button onClick={goSettings} style={{ marginTop: 10, padding: "8px 14px", borderRadius: 8, background: `${COLORS.cyan}15`, color: COLORS.cyan, border: `1px solid ${COLORS.cyan}30`, fontSize: 11, fontWeight: 800, fontFamily: "'Syne', sans-serif", cursor: "pointer" }}>⚙ Set up a drop</button>
        </div>
      ) : (
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderRadius: 12, background: `linear-gradient(135deg, ${COLORS.cyan}10, ${COLORS.surface})`, border: `1px solid ${COLORS.cyan}30` }}>
          {drop.tokenImage ? <img src={drop.tokenImage} alt="" style={{ width: 42, height: 42, borderRadius: 10, objectFit: "cover", flexShrink: 0 }} onError={e => { e.currentTarget.style.display = "none"; }} /> : <span style={{ fontSize: 30 }}>🪙</span>}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{drop.tokenName || `${(drop.token || "").slice(0, 6)}…`}</div>
            <div style={{ fontSize: 10, color: COLORS.textMuted, display: "flex", alignItems: "center", gap: 5 }}>
              {live ? <span style={{ color: COLORS.cyan }}>Live on stream</span> : <span>Paused</span>}
              {typeof drop.claims !== "undefined" && <span style={{ color: COLORS.cyan, fontFamily: "'JetBrains Mono', monospace" }}>· {drop.claims} claimed</span>}
            </div>
          </div>
          <button onClick={goSettings} style={{ flexShrink: 0, padding: "8px 14px", borderRadius: 8, background: COLORS.cyan, color: "#06141a", border: "none", fontSize: 11, fontWeight: 800, fontFamily: "'Syne', sans-serif", cursor: "pointer" }}>⚙ Settings</button>
        </div>
      )}
    </div>
  );
}

function TipperNFTs() {
  const [mounted, setMounted] = useState(false);
  const [tipperData, setTipperData] = useState(null);
  useEffect(() => { requestAnimationFrame(() => setMounted(true)); }, []);

  const tipperAddr = window.__fullsend_profile?.address;
  useEffect(() => {
    if (!tipperAddr) return;
    fetch(`/api/data-tips?action=tipper-stats&tipper=${tipperAddr}`)
      .then(r => r.json())
      .then(data => setTipperData(data))
      .catch(() => {});
  }, [tipperAddr]);

  const perCreator = tipperData?.perCreator || [];
  const rankColors = [COLORS.accent, COLORS.gold, COLORS.blue, COLORS.purple, COLORS.orange];

  const allPossible = perCreator.flatMap((ch, ci) => {
    const total = parseFloat(ch.total || 0);
    const channel = { name: `${ch.creator.slice(0, 6)}...${ch.creator.slice(-4)}`, avatar: "💰", color: rankColors[ci % rankColors.length] };
    const nftTiers = [
      { tier: "Bronze Supporter", icon: "🥉", color: "#00cc66", threshold: 100 },
      { tier: "Silver Champion", icon: "🥈", color: "#4a9eff", threshold: 300 },
      { tier: "Gold Legend", icon: "🥇", color: "#b44aff", threshold: 1000 },
      { tier: "Diamond Whale", icon: "💎", color: "#ffd700", threshold: 5000 },
    ];
    return nftTiers.map(nft => ({
      ...nft,
      channel,
      earned: total >= nft.threshold,
      progress: Math.min(100, (total / nft.threshold) * 100),
      totalTipped: total,
    }));
  });

  const earned = allPossible.filter(n => n.earned);
  const nextUp = allPossible.filter(n => !n.earned && n.progress > 10).sort((a, b) => b.progress - a.progress).slice(0, 3);

  return (
    <div style={{
      maxWidth: 620, margin: "0 auto", padding: "28px 24px",
      opacity: mounted ? 1 : 0,
      transform: mounted ? "translateY(0)" : "translateY(15px)",
      transition: "all 0.5s cubic-bezier(0.16, 1, 0.3, 1)",
    }}>
      {/* Live Claim Drops — real & active, ABOVE the Coming Soon overlay */}
      <TipperClaimDrops />

      {/* Milestone NFT gallery — still upcoming, kept behind the overlay */}
      <div style={{ position: "relative", minHeight: "55vh" }}>
      {/* Coming Soon Overlay */}
      <div style={{
        position: "absolute", inset: 0, zIndex: 10,
        display: "flex", alignItems: "flex-start", justifyContent: "center",
        paddingTop: 40,
        background: `${COLORS.bg}f5`, backdropFilter: "blur(12px)",
        borderRadius: 16,
      }}>
        <div style={{ textAlign: "center", maxWidth: 420 }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>💎</div>
          <div style={{
            fontFamily: "'Syne', sans-serif", fontSize: 28, fontWeight: 800,
            background: `linear-gradient(135deg, ${COLORS.purple}, ${COLORS.gold})`,
            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
            marginBottom: 8,
          }}>Coming Soon</div>
          <div style={{ fontSize: 13, color: COLORS.textSecondary, maxWidth: 340, margin: "0 auto 20px" }}>
            Tipping history will count retroactively because it's all stored on-chain even before more utility rails are deployed.
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, textAlign: "left" }}>
            {[
              { icon: "📊", title: "Leaderboard Rewards", desc: "Top tipper of the week or month gets a special edition NFT from the creator", color: COLORS.accent },
              { icon: "🔓", title: "Token-Gated Perks", desc: "Hold creator NFTs to unlock exclusive content, polls, and community features", color: COLORS.purple },
              { icon: "🏆", title: "Milestone NFTs", desc: "Earn unique NFTs when you hit tipping milestones — 100 LYX, 500 LYX, 1,000 LYX. Auto-minted to your UP", color: COLORS.gold },
            ].map((item, i) => (
              <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "10px 12px", background: `${COLORS.bg}80`, borderRadius: 8, border: `1px solid ${item.color}15` }}>
                <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>{item.icon}</span>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: item.color, marginBottom: 2 }}>{item.title}</div>
                  <div style={{ fontSize: 10, color: COLORS.textMuted, lineHeight: 1.5 }}>{item.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: 28 }}>
        <div style={{ fontSize: 36, marginBottom: 2, animation: "float 3.5s ease-in-out infinite" }}>💎</div>
        <div style={{
          fontFamily: "'Syne', sans-serif",
          fontSize: 26, fontWeight: 800,
          background: `linear-gradient(135deg, ${COLORS.purple}, ${COLORS.gold})`,
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          letterSpacing: "-0.5px",
        }}>My NFTs</div>
        <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 5 }}>
          Milestone NFTs earned across all channels
        </div>
      </div>

      {/* Earned NFTs */}
      {earned.length > 0 && (
        <div style={{ marginBottom: 28 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, letterSpacing: "2px", textTransform: "uppercase", marginBottom: 12 }}>
            Earned ({earned.length})
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            {earned.map((nft, i) => (
              <div key={i} style={{
                background: COLORS.surface,
                border: `1px solid ${nft.color}20`,
                borderRadius: 12,
                overflow: "hidden",
                transition: "all 0.3s",
                animation: `fadeInUp 0.5s ease-out ${i * 0.08}s both`,
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = `${nft.color}35`; e.currentTarget.style.boxShadow = `0 2px 20px ${nft.color}08`; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = `${nft.color}20`; e.currentTarget.style.boxShadow = "none"; }}>
                {/* Art */}
                <div style={{
                  height: 100,
                  background: `linear-gradient(160deg, ${COLORS.bg}, ${nft.color}10, ${COLORS.bgAlt})`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  position: "relative",
                }}>
                  <div style={{
                    position: "absolute", inset: 0,
                    backgroundImage: `linear-gradient(${nft.color}05 1px, transparent 1px), linear-gradient(90deg, ${nft.color}05 1px, transparent 1px)`,
                    backgroundSize: "20px 20px", opacity: 0.5,
                  }} />
                  <div style={{
                    fontSize: 40,
                    filter: `drop-shadow(0 0 16px ${nft.color}30)`,
                    position: "relative", zIndex: 2,
                  }}>{nft.icon}</div>
                  <div style={{
                    position: "absolute", top: 8, left: 8,
                    fontSize: 7, fontWeight: 700, color: COLORS.green,
                    background: `${COLORS.bg}c0`, backdropFilter: "blur(4px)",
                    padding: "2px 6px", borderRadius: 3,
                    border: `1px solid ${COLORS.green}20`,
                    display: "flex", alignItems: "center", gap: 3,
                  }}>✓ MINTED</div>
                </div>
                <div style={{ padding: "10px 12px" }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: nft.color, fontFamily: "'Syne', sans-serif" }}>{nft.tier}</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 4 }}>
                    <span style={{ fontSize: 12 }}>{nft.channel.avatar}</span>
                    <span style={{ fontSize: 10, color: COLORS.textMuted }}>{nft.channel.name}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Next Up */}
      {nextUp.length > 0 && (
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, letterSpacing: "2px", textTransform: "uppercase", marginBottom: 12 }}>
            Almost There
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {nextUp.map((nft, i) => (
              <div key={i} style={{
                background: COLORS.surface,
                border: `1px solid ${COLORS.border}`,
                borderRadius: 10,
                padding: "12px 14px",
                display: "flex", alignItems: "center", gap: 12,
                animation: `fadeInUp 0.5s ease-out ${(earned.length + i) * 0.08}s both`,
              }}>
                <div style={{
                  width: 36, height: 36, borderRadius: 8,
                  background: `${nft.color}10`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 18, opacity: 0.6,
                }}>{nft.icon}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.textSecondary }}>{nft.tier}</div>
                  <div style={{ fontSize: 10, color: COLORS.textMuted, display: "flex", alignItems: "center", gap: 4 }}>
                    <span>{nft.channel.avatar}</span> {nft.channel.name}
                  </div>
                </div>
                <div style={{ textAlign: "right", minWidth: 80 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: nft.color, fontFamily: "'JetBrains Mono', monospace" }}>
                    {nft.totalTipped}/{nft.threshold} LYX
                  </div>
                  <div style={{ height: 3, borderRadius: 2, background: `${nft.color}10`, marginTop: 4, overflow: "hidden", width: 80 }}>
                    <div style={{
                      height: "100%", width: `${nft.progress}%`,
                      background: `linear-gradient(90deg, ${nft.color}60, ${nft.color})`,
                      borderRadius: 2,
                    }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

// --- TIPPER ACTIVITY -----------------------------------------
// --- MY NOTIFICATIONS (per-connected-user) -------------------
// Each connected user's own notifications: tips they RECEIVED on their channel.
// Distinct from Activity (tips they SENT). Archive state is per-address in
// localStorage so it doesn't bleed across accounts on a shared browser.
function MyNotifications() {
  const [mounted, setMounted] = useState(false);
  const [tips, setTips] = useState([]);
  const [loading, setLoading] = useState(true);
  const addr = window.__fullsend_profile?.address;
  const archivedKey = `fullsend_noti_archived_${(addr || "").toLowerCase()}`;
  const [archived, setArchived] = useState(() => { try { return JSON.parse(localStorage.getItem(archivedKey) || "[]"); } catch { return []; } });
  const [showArchived, setShowArchived] = useState(false);
  const persist = (arr) => { const u = [...new Set(arr)]; setArchived(u); try { localStorage.setItem(archivedKey, JSON.stringify(u)); } catch {} };
  useEffect(() => { requestAnimationFrame(() => setMounted(true)); }, []);
  useEffect(() => {
    if (!addr) { setLoading(false); return; }
    fetch(`/api/data-tips?action=activity&creator=${addr}&limit=50`)
      .then(r => r.json())
      .then(data => setTips((data.activity || []).filter(t => t && t.tx_hash)))
      .catch(() => setTips([]))
      .finally(() => setLoading(false));
  }, [addr]);

  if (!addr) {
    return (
      <div style={{ maxWidth: 720, margin: "0 auto", padding: "80px 24px", textAlign: "center", opacity: mounted ? 1 : 0, transform: mounted ? "translateY(0)" : "translateY(20px)", transition: "all 0.5s cubic-bezier(0.16, 1, 0.3, 1)" }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🔔</div>
        <div style={{ fontFamily: "'Syne', sans-serif", fontSize: 20, fontWeight: 800, color: COLORS.text, marginBottom: 8 }}>Connect to See Your Notifications</div>
        <div style={{ fontSize: 12, color: COLORS.textMuted, lineHeight: 1.6, maxWidth: 360, margin: "0 auto" }}>
          Connect your Universal Profile to see tips you've received and other activity on your channel.
        </div>
      </div>
    );
  }

  const parseTs = (ts) => {
    if (!ts) return null;
    if (typeof ts === "string" && isNaN(Number(ts))) { const d = new Date(ts); return isNaN(d.getTime()) ? null : d; }
    const n = Number(ts); return new Date(n < 1e12 ? n * 1000 : n);
  };
  const active = tips.filter(t => !archived.includes(t.tx_hash));
  const arch = tips.filter(t => archived.includes(t.tx_hash));
  const row = (t, isArch) => {
    const d = parseTs(t.timestamp);
    const isPlatform = (t.message || "") === PLATFORM_TIP_TAG;
    return (
      <div key={t.tx_hash} style={{
        padding: "12px 14px", borderRadius: 8, background: COLORS.bgAlt,
        border: `1px solid ${COLORS.border}`, opacity: isArch ? 0.55 : 1,
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
      }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 12, color: COLORS.text, fontWeight: 600, fontFamily: "'JetBrains Mono', monospace" }}>
            💸 Received {parseFloat(t.amount || 0)} LYX{isPlatform ? " (Tip FullSend)" : ""}
          </div>
          <div style={{ fontSize: 10, color: COLORS.textMuted, marginTop: 3, fontFamily: "'JetBrains Mono', monospace" }}>
            from {t.tipper ? `${t.tipper.slice(0, 6)}…${t.tipper.slice(-4)}` : "Anonymous"}{d ? ` · ${d.toLocaleString()}` : ""}
          </div>
          {t.message && !isPlatform && (
            <div style={{ fontSize: 11, color: COLORS.textSecondary, marginTop: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              “{t.message.slice(0, 80)}{t.message.length > 80 ? "…" : ""}”
            </div>
          )}
        </div>
        <button onClick={() => isArch ? persist(archived.filter(k => k !== t.tx_hash)) : persist([...archived, t.tx_hash])} style={{
          padding: "4px 10px", borderRadius: 5, flexShrink: 0,
          background: isArch ? `${COLORS.cyan}08` : `${COLORS.textSecondary}08`,
          border: `1px solid ${isArch ? COLORS.cyan + "15" : COLORS.textSecondary + "15"}`,
          color: isArch ? COLORS.cyan : COLORS.textSecondary, fontSize: 9, fontWeight: 600,
          cursor: "pointer", fontFamily: "'JetBrains Mono', monospace",
        }}>{isArch ? "Restore" : "Archive"}</button>
      </div>
    );
  };

  return (
    <div style={{
      maxWidth: 720, margin: "0 auto", padding: "40px 24px",
      opacity: mounted ? 1 : 0, transform: mounted ? "translateY(0)" : "translateY(20px)",
      transition: "all 0.6s cubic-bezier(0.16, 1, 0.3, 1)",
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
        <div style={{ fontFamily: "'Syne', sans-serif", fontSize: 24, fontWeight: 800 }}>🔔 Notifications</div>
        {active.length > 0 && (
          <button onClick={() => persist([...archived, ...active.map(t => t.tx_hash)])} style={{
            padding: "6px 14px", borderRadius: 7, background: `${COLORS.red}10`,
            border: `1px solid ${COLORS.red}25`, color: COLORS.red, fontSize: 10, fontWeight: 700,
            cursor: "pointer", fontFamily: "'Syne', sans-serif",
          }}>Clear all</button>
        )}
      </div>
      <div style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 20 }}>Tips you've received on your channel</div>

      <div style={{ padding: 20, borderRadius: 14, background: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: "30px 0", color: COLORS.textMuted, fontSize: 12 }}>Loading…</div>
        ) : active.length === 0 ? (
          <div style={{ textAlign: "center", padding: "30px 0" }}>
            <div style={{ fontSize: 28 }}>🔔</div>
            <div style={{ color: COLORS.textMuted, fontSize: 12, marginTop: 8 }}>No new notifications</div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {active.map(t => row(t, false))}
          </div>
        )}
      </div>

      {arch.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <button onClick={() => setShowArchived(!showArchived)} style={{
            display: "flex", alignItems: "center", gap: 8, background: "transparent", border: "none",
            color: COLORS.textMuted, fontSize: 12, fontWeight: 600, cursor: "pointer",
            fontFamily: "'JetBrains Mono', monospace", marginBottom: 10,
          }}>
            📦 Archived ({arch.length}) <span style={{ fontSize: 9, transform: showArchived ? "rotate(180deg)" : "rotate(0)", transition: "transform 0.2s" }}>▼</span>
          </button>
          {showArchived && (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {arch.map(t => row(t, true))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TipperActivity() {
  const [mounted, setMounted] = useState(false);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { requestAnimationFrame(() => setMounted(true)); }, []);

  const tipperAddr = window.__fullsend_profile?.address;
  useEffect(() => {
    if (!tipperAddr) { setLoading(false); return; }
    fetch(`/api/data-tips?action=activity&tipper=${tipperAddr}&limit=30`)
      .then(r => r.json())
      .then(data => {
        const items = (data.activity || []).map((t, i) => {
          const amount = parseFloat(t.amount || 0);
          const tier = getTier(amount);
          const timeDiff = Math.floor(Date.now() / 1000) - parseInt(t.timestamp || 0);
          const timeStr = timeDiff < 60 ? "just now" : timeDiff < 3600 ? `${Math.floor(timeDiff / 60)}m ago` : timeDiff < 86400 ? `${Math.floor(timeDiff / 3600)}h ago` : `${Math.floor(timeDiff / 86400)}d ago`;
          return {
            id: t.tx_hash || i,
            type: "tip",
            creatorAddress: t.creator,
            channel: { name: `${t.creator.slice(0, 6)}...${t.creator.slice(-4)}`, avatar: STOCK_AVATAR, color: tier.color },
            amount,
            message: "",
            time: timeStr,
            tier,
          };
        });
        setActivities(items);
        setLoading(false);

        // Resolve UP profiles for creators
        const uniqueCreators = [...new Set(items.map(i => i.creatorAddress).filter(Boolean))];
        uniqueCreators.forEach(addr => {
          fetchUPProfile(addr).then(p => {
            if (p) {
              setActivities(prev => prev.map(a =>
                a.creatorAddress === addr
                  ? { ...a, channel: { ...a.channel, name: p.name, avatar: p.avatar } }
                  : a
              ));
            }
          });
        });
      })
      .catch(() => setLoading(false));
  }, [tipperAddr]);

  const revealSet = useStaggerReveal(activities.length, 60);

  return (
    <div style={{
      maxWidth: 620, margin: "0 auto", padding: "28px 24px",
      opacity: mounted ? 1 : 0,
      transform: mounted ? "translateY(0)" : "translateY(15px)",
      transition: "all 0.5s cubic-bezier(0.16, 1, 0.3, 1)",
    }}>
      {!window.__fullsend_profile?.address && (
        <div style={{ padding: "10px 16px", borderRadius: 8, background: `${COLORS.cyan}08`, border: `1px solid ${COLORS.cyan}15`, fontSize: 11, color: COLORS.cyan, textAlign: "center", marginBottom: 16 }}>
          Connect your Universal Profile to see your activity
        </div>
      )}
      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: 28 }}>
        <div style={{ fontSize: 36, marginBottom: 2, animation: "float 3s ease-in-out infinite" }}>⚡</div>
        <div style={{
          fontFamily: "'Syne', sans-serif",
          fontSize: 26, fontWeight: 800,
          background: `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.cyan})`,
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          letterSpacing: "-0.5px",
        }}>Activity</div>
        <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 5 }}>
          Your tipping history across all channels
        </div>
      </div>

      {/* Activity Feed */}
      <div style={{ position: "relative" }}>
        {/* Vertical timeline line */}
        <div style={{
          position: "absolute", left: 19, top: 0, bottom: 0,
          width: 1, background: `linear-gradient(180deg, ${COLORS.border}, transparent)`,
        }} />

        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {activities.map((act, idx) => {
            const isVisible = revealSet.has(idx);
            const dotColor = act.type === "vote" ? COLORS.cyan : act.type === "follow" ? COLORS.purple : act.tier?.color || COLORS.accent;
            return (
              <div key={act.id} style={{
                display: "flex", gap: 14,
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? "translateX(0)" : "translateX(-15px)",
                transition: `all 0.4s cubic-bezier(0.4,0,0.2,1) ${idx * 0.03}s`,
              }}>
                {/* Timeline dot */}
                <div style={{
                  width: 38, flexShrink: 0,
                  display: "flex", justifyContent: "center",
                  paddingTop: 14,
                }}>
                  <div style={{
                    width: 10, height: 10, borderRadius: "50%",
                    background: `${dotColor}30`,
                    border: `2px solid ${dotColor}50`,
                    boxShadow: `0 0 8px ${dotColor}20`,
                  }} />
                </div>

                {/* Card */}
                <div style={{
                  flex: 1,
                  background: COLORS.surface,
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: 10,
                  padding: "12px 14px",
                  transition: "all 0.3s",
                  cursor: "default",
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = `${dotColor}20`; e.currentTarget.style.background = COLORS.surfaceAlt; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = COLORS.border; e.currentTarget.style.background = COLORS.surface; }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                    <span style={{ width: 22, height: 22, borderRadius: 5, overflow: "hidden", display: "inline-flex", flexShrink: 0 }}>{isImageAvatar(act.channel.avatar) ? <img src={act.channel.avatar} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} onError={e => { e.target.src = STOCK_AVATAR; }} /> : "👤"}</span>
                    <span style={{ fontSize: 11, fontWeight: 600, color: COLORS.textSecondary }}>{act.channel.name}</span>
                    <span style={{ marginLeft: "auto", fontSize: 10, color: COLORS.textMuted }}>{act.time}</span>
                  </div>

                  {act.type === "tip" && (
                    <>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ fontSize: 12 }}>{act.tier.emoji}</span>
                        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13, fontWeight: 700, color: act.tier.color }}>
                          {act.amount} LYX
                        </span>
                        <span style={{
                          fontSize: 9, padding: "2px 5px", borderRadius: 3,
                          background: `${act.tier.color}10`, color: act.tier.color,
                          fontWeight: 700,
                        }}>{act.tier.label}</span>
                      </div>
                      {act.message && (
                        <div style={{
                          marginTop: 6, fontSize: 11, color: COLORS.textSecondary,
                          padding: "6px 10px", borderRadius: 5,
                          background: `${COLORS.bg}60`,
                          borderLeft: `2px solid ${act.tier.color}20`,
                          lineHeight: 1.5,
                        }}>"{act.message}"</div>
                      )}
                    </>
                  )}

                  {act.type === "vote" && (
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ fontSize: 12 }}>📊</span>
                      <span style={{ fontSize: 11, color: COLORS.cyan, fontWeight: 600 }}>Voted on poll</span>
                      <span style={{ fontSize: 10, color: COLORS.textMuted }}>"{act.poll}"</span>
                      <span style={{
                        fontSize: 9, padding: "2px 5px", borderRadius: 3,
                        background: `${COLORS.cyan}10`, color: COLORS.cyan,
                        fontWeight: 700, marginLeft: "auto",
                      }}>{act.choice}</span>
                    </div>
                  )}

                  {act.type === "follow" && (
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ fontSize: 12 }}>👥</span>
                      <span style={{ fontSize: 11, color: COLORS.purple, fontWeight: 600 }}>Followed this creator</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// --- TIPPER ANALYTICS ----------------------------------------
function TipperAnalytics() {
  const [mounted, setMounted] = useState(false);
  const [period, setPeriod] = useState("allTime");
  const [tipperData, setTipperData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { requestAnimationFrame(() => setMounted(true)); }, []);

  const tipperAddr = window.__fullsend_profile?.address;
  useEffect(() => {
    if (!tipperAddr) { setLoading(false); return; }
    fetch(`/api/data-tips?action=tipper-stats&tipper=${tipperAddr}`)
      .then(r => r.json())
      .then(data => { setTipperData(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [tipperAddr]);

  const summary = tipperData?.summary || {};
  const s = {
    totalTipped: parseFloat(summary.total_spent || 0),
    tipCount: parseInt(summary.total_tips || 0),
    channelsSupported: parseInt(summary.creators_supported || 0),
    nftsEarned: tipperData?.milestones ? [tipperData.milestones.bronze, tipperData.milestones.silver, tipperData.milestones.gold, tipperData.milestones.diamond].filter(m => m?.reached).length : 0,
    avgTip: parseFloat(summary.avg_tip || 0),
    pollsVoted: 0, // needs vote indexer
  };

  const monthlyData = [
    { month: "—", amount: s.totalTipped }, // Simplified until we have per-month data
  ];
  while (monthlyData.length < 6) monthlyData.unshift({ month: "—", amount: 0 });
  const maxMonth = Math.max(...monthlyData.map(d => d.amount), 0.01);

  const rawPerCreator = (tipperData?.perCreator || []).slice(0, 4);
  const channelAddresses = rawPerCreator.map(c => c.creator).filter(Boolean);
  const channelProfiles = useUPProfiles(channelAddresses);
  const channelBreakdown = rawPerCreator.map(c => {
    const profile = channelProfiles[c.creator?.toLowerCase()] || {};
    return {
      name: profile.name || `${c.creator.slice(0, 6)}...${c.creator.slice(-4)}`,
      avatar: profile.avatar || STOCK_AVATAR,
      amount: parseFloat(c.total || 0),
      tips: parseInt(c.count || 0),
      pct: s.totalTipped > 0 ? Math.round((parseFloat(c.total || 0) / s.totalTipped) * 100) : 0,
    };
  });

  const tierColors2 = { micro: COLORS.textSecondary, supporter: COLORS.accent, champion: COLORS.blue, legend: COLORS.purple, whale: COLORS.gold };
  const tierLabels2 = { micro: "Micro", supporter: "Supporter", champion: "Champion", legend: "Legend", whale: "Whale" };
  const tierHistory = (tipperData?.tiers || []).map(t => ({
    tier: tierLabels2[t.tier] || t.tier,
    count: parseInt(t.count || 0),
    color: tierColors2[t.tier] || COLORS.textMuted,
  }));
  // Ensure all tiers shown
  ["Micro", "Supporter", "Champion", "Legend", "Whale"].forEach(tier => {
    if (!tierHistory.find(t => t.tier === tier)) {
      tierHistory.push({ tier, count: 0, color: tierColors2[tier.toLowerCase()] || COLORS.textMuted });
    }
  });
  const maxTierCount = Math.max(...tierHistory.map(t => t.count), 1);

  return (
    <div style={{
      maxWidth: 700, margin: "0 auto", padding: "40px 24px",
      opacity: mounted ? 1 : 0, transform: mounted ? "translateY(0)" : "translateY(20px)",
      transition: "all 0.6s cubic-bezier(0.16, 1, 0.3, 1)",
    }}>
      {!window.__fullsend_profile?.address && (
        <div style={{ padding: "10px 16px", borderRadius: 8, background: `${COLORS.cyan}08`, border: `1px solid ${COLORS.cyan}15`, fontSize: 11, color: COLORS.cyan, textAlign: "center", marginBottom: 16 }}>
          Connect your Universal Profile to see your stats
        </div>
      )}
      <div style={{ fontFamily: "'Syne', sans-serif", fontSize: 24, fontWeight: 800, marginBottom: 4 }}>My Tipping Stats</div>
      <div style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 20 }}>Track where your LYX goes</div>

      {/* Period */}
      <div style={{ display: "flex", gap: 4, marginBottom: 20 }}>
        {[{ key: "month", label: "This Month" }, { key: "allTime", label: "All Time" }].map(p => (
          <button key={p.key} onClick={() => setPeriod(p.key)} style={{
            padding: "6px 14px", borderRadius: 6,
            background: period === p.key ? `${COLORS.cyan}12` : COLORS.surface,
            border: `1px solid ${period === p.key ? COLORS.cyan + "25" : COLORS.border}`,
            color: period === p.key ? COLORS.cyan : COLORS.textMuted,
            fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 600, cursor: "pointer",
          }}>{p.label}</button>
        ))}
      </div>

      {/* Stat cards */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 24 }}>
        {[
          { label: "Total Tipped", value: `${s.totalTipped.toFixed(2)} LYX`, color: COLORS.accent },
          { label: "Tips Sent", value: s.tipCount, color: COLORS.cyan },
          { label: "Channels", value: s.channelsSupported, color: COLORS.purple },
          { label: "NFTs Earned", value: s.nftsEarned, color: COLORS.gold },
          { label: "Avg Tip", value: `${s.avgTip.toFixed(2)} LYX`, color: COLORS.blue },
          { label: "Polls Voted", value: s.pollsVoted, color: COLORS.orange },
        ].map((stat, i) => (
          <div key={i} style={{
            padding: "14px 8px", borderRadius: 10, textAlign: "center",
            background: COLORS.surface, border: `1px solid ${COLORS.border}`,
          }}>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 16, fontWeight: 800, color: stat.color }}>{stat.value}</div>
            <div style={{ fontSize: 8, color: COLORS.textMuted, marginTop: 2 }}>{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Chart + Tier row */}
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 14, marginBottom: 24 }}>
        {/* Monthly spending chart */}
        <div style={{ padding: "16px", borderRadius: 10, background: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "1.5px", marginBottom: 14 }}>
            Monthly Spending
          </div>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 100 }}>
            {monthlyData.map((d, i) => (
              <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                <div style={{ fontSize: 8, fontWeight: 700, color: COLORS.cyan, fontFamily: "'JetBrains Mono', monospace" }}>{d.amount}</div>
                <div style={{
                  width: "100%", borderRadius: 4,
                  height: `${(d.amount / maxMonth) * 70}px`,
                  background: `linear-gradient(180deg, ${COLORS.cyan}, ${COLORS.purple})`,
                  opacity: 0.8,
                }} />
                <div style={{ fontSize: 8, color: COLORS.textMuted }}>{d.month}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Tier breakdown */}
        <div style={{ padding: "16px", borderRadius: 10, background: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "1.5px", marginBottom: 14 }}>
            My Tip Tiers
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {tierHistory.map((t, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 9, fontWeight: 600, color: t.color, width: 55 }}>{t.tier}</span>
                <div style={{ flex: 1, height: 4, borderRadius: 2, background: `${COLORS.bg}80`, overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${(t.count / maxTierCount) * 100}%`, borderRadius: 2, background: t.color }} />
                </div>
                <span style={{ fontSize: 9, color: COLORS.textMuted, fontFamily: "'JetBrains Mono', monospace", width: 16, textAlign: "right" }}>{t.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Channels supported */}
      <div style={{ padding: "16px", borderRadius: 10, background: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "1.5px", marginBottom: 12 }}>
          Channels I Support
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {channelBreakdown.map((ch, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 8px", borderRadius: 6 }}>
              <div style={{ width: 28, height: 28, borderRadius: 6, overflow: "hidden", flexShrink: 0 }}>{isImageAvatar(ch.avatar) ? <img src={ch.avatar} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} onError={e => { e.target.src = STOCK_AVATAR; }} /> : "👤"}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, fontWeight: 600 }}>{ch.name}</div>
                <div style={{ fontSize: 8, color: COLORS.textMuted }}>{ch.tips} tips</div>
              </div>
              <div style={{ width: 80 }}>
                <div style={{ height: 4, borderRadius: 2, background: `${COLORS.bg}80`, overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${ch.pct}%`, borderRadius: 2, background: COLORS.accent }} />
                </div>
              </div>
              <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.accent, fontFamily: "'JetBrains Mono', monospace", minWidth: 60, textAlign: "right" }}>{ch.amount.toFixed(2)} LYX</div>
            </div>
          ))}
        </div>
      </div>

      {/* Export */}
      <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
        <button style={{
          padding: "8px 16px", borderRadius: 6,
          background: `${COLORS.cyan}10`, border: `1px solid ${COLORS.cyan}20`,
          color: COLORS.cyan, fontFamily: "'JetBrains Mono', monospace",
          fontSize: 10, fontWeight: 600, cursor: "pointer",
        }}>Export Tip History</button>
        <div style={{ fontSize: 9, color: COLORS.textMuted, display: "flex", alignItems: "center" }}>
          Download your complete tipping history for records
        </div>
      </div>
    </div>
  );
}

// --- CREATOR DIRECTORY ----------------------------------------
function CreatorDirectory() {
  const [mounted, setMounted] = useState(false);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("tips"); // "tips" | "newest" | "followers" | "name"
  const [tab, setTab] = useState("discover"); // "discover" | "following" | "followers"
  const [followingList, setFollowingList] = useState([]);
  const [followersList, setFollowersList] = useState([]);
  const [creators, setCreators] = useState([]);
  const [loadingCreators, setLoadingCreators] = useState(true);
  useEffect(() => { requestAnimationFrame(() => setMounted(true)); }, []);

  // Fetch registered creators from database
  useEffect(() => {
    fetch("/api/creator-register?limit=50")
      .then(r => r.json())
      .then(data => {
        const rows = (data.creators || []).map(c => {
          const plat = typeof c.platforms === "string" ? JSON.parse(c.platforms || "{}") : (c.platforms || {});
          const platformList = Object.keys(plat).filter(k => plat[k]);
          // LIVE status with heartbeat expiry check (2 min)
          const overlayState = plat._overlayState || null;
          const heartbeat = overlayState?.liveHeartbeat || 0;
          const isLive = overlayState?.isLive === true && (Date.now() - heartbeat) < 120000;
          // Stream URL for LIVE badge click — respects primaryStream preference
          const primary = overlayState?.primaryStream || "auto";
          let streamUrl = null;
          if (primary !== "auto" && plat[primary]) {
            streamUrl = plat[primary];
          } else {
            streamUrl = plat.youtube || plat.twitch || plat.kick || plat.rumble || plat.x || plat.website || null;
          }
          return {
            name: c.name || `${c.address.slice(0, 6)}...${c.address.slice(-4)}`,
            avatar: c.avatar || STOCK_AVATAR,
            address: `${c.address.slice(0, 6)}...${c.address.slice(-4)}`,
            realAddress: c.address,
            platforms: platformList,
            followers: parseInt(c.follower_count || 0),
            totalTips: parseFloat(c.tip_volume || 0),
            category: (c.tags && c.tags.length > 0) ? c.tags : [],
            verified: c.verified || false,
            live: isLive,
            streamUrl,
            bio: c.bio || "",
          };
        });
        setCreators(rows);
        setLoadingCreators(false);

        // Resolve UP profiles for creators missing avatars
        rows.forEach(creator => {
          if (!creator.avatar || creator.avatar === STOCK_AVATAR) {
            fetchUPProfile(creator.realAddress).then(p => {
              if (p && p.avatar && p.avatar !== STOCK_AVATAR) {
                setCreators(prev => prev.map(c =>
                  c.realAddress === creator.realAddress
                    ? { ...c, avatar: p.avatar, name: p.name || c.name }
                    : c
                ));
                // Also update the database so next load is instant
                fetch("/api/creator-register", {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ address: creator.realAddress, name: p.name, avatar: p.avatar }),
                }).catch(() => {});
              }
            });
          }
          // Fetch real follower count from LSP26
          fetch(`/api/relay-follow?creator=${creator.realAddress}`)
            .then(r => r.json())
            .then(result => {
              if (result.success && result.followerCount !== undefined) {
                setCreators(prev => prev.map(c =>
                  c.realAddress === creator.realAddress
                    ? { ...c, followers: result.followerCount }
                    : c
                ));
              }
            })
            .catch(() => {});
        });
      })
      .catch(() => setLoadingCreators(false));
  }, []);

  // Poll for live status updates every 15 seconds (lightweight — only updates live flag)
  useEffect(() => {
    const refreshLiveStatus = async () => {
      try {
        const res = await fetch("/api/creator-register?limit=50");
        const data = await res.json();
        const liveMap = {};
        (data.creators || []).forEach(c => {
          const plat = typeof c.platforms === "string" ? JSON.parse(c.platforms || "{}") : (c.platforms || {});
          const os = plat._overlayState || {};
          const hb = os.liveHeartbeat || 0;
          const isLive = os.isLive === true && (Date.now() - hb) < 120000;
          liveMap[c.address] = isLive;
        });
        setCreators(prev => prev.map(creator => ({
          ...creator,
          live: liveMap[creator.realAddress] || false,
        })));
      } catch {}
    };
    const interval = setInterval(refreshLiveStatus, 15000);
    return () => clearInterval(interval);
  }, []);

  // Fetch who the connected user follows
  useEffect(() => {
    const fetchFollowing = async () => {
      const myAddr = window.__fullsend_profile?.address;
      if (!myAddr || creators.length === 0) return;
      const following = [];
      for (const creator of creators) {
        if (creator.realAddress) {
          try {
            const res = await fetch(`/api/relay-follow?creator=${creator.realAddress}&follower=${myAddr}`);
            const data = await res.json();
            if (data.success && data.isFollowing) following.push(creator.realAddress.toLowerCase());
          } catch (e) {}
        }
      }
      setFollowingList(following);
    };
    if (creators.length > 0) fetchFollowing();
  }, [creators]);

  // Fetch who follows the connected user (Follows Me)
  useEffect(() => {
    const myAddr = window.__fullsend_profile?.address;
    if (!myAddr || creators.length === 0) return;
    const fetchFollowers = async () => {
      const followers = [];
      for (const creator of creators) {
        if (creator.realAddress && creator.realAddress.toLowerCase() !== myAddr.toLowerCase()) {
          try {
            const res = await fetch(`/api/relay-follow?creator=${myAddr}&follower=${creator.realAddress}`);
            const data = await res.json();
            if (data.success && data.isFollowing) followers.push(creator.realAddress.toLowerCase());
          } catch (e) {}
        }
      }
      setFollowersList(followers);
    };
    fetchFollowers();
  }, [creators]);

  const platformColors = { youtube: "#FF0000", twitch: "#9146FF", kick: "#53FC18", rumble: "#85c742", x: "#fff" };
  const platformIcons = { youtube: "▶", twitch: "◆", kick: "●", rumble: "R", x: "𝕏" };
  const categories = ["all", "crypto", "gaming", "music", "art", "tech", "education", "irl", "cooking", "fitness", "comedy", "news", "defi"];

  const filtered = creators.filter(c => {
    const matchesSearch = !search || c.name.toLowerCase().includes(search.toLowerCase()) || c.address.toLowerCase().includes(search.toLowerCase());
    const matchesFilter = filter === "all" || (Array.isArray(c.category) ? c.category.includes(filter) : c.category === filter);
    const matchesTab = tab === "discover"
      || (tab === "following" && c.realAddress && followingList.includes(c.realAddress.toLowerCase()))
      || (tab === "followers" && c.realAddress && followersList.includes(c.realAddress.toLowerCase()));
    return matchesSearch && matchesFilter && matchesTab;
  }).sort((a, b) => {
    if (sort === "tips") return (b.totalTips || 0) - (a.totalTips || 0);
    if (sort === "followers") return (b.followers || 0) - (a.followers || 0);
    if (sort === "name") return (a.name || "").localeCompare(b.name || "");
    if (sort === "newest") return 0; // already sorted by creation date from API
    return 0;
  });

  // Split into live and offline for separate sections
  const liveCreators = filtered.filter(c => c.live);
  const offlineCreators = filtered.filter(c => !c.live);

  return (
    <div style={{
      maxWidth: 800, margin: "0 auto", padding: "40px 24px",
      opacity: mounted ? 1 : 0, transform: mounted ? "translateY(0)" : "translateY(20px)",
      transition: "all 0.6s cubic-bezier(0.16, 1, 0.3, 1)",
    }}>
      <div style={{ fontFamily: "'Syne', sans-serif", fontSize: 24, fontWeight: 800, marginBottom: 4 }}>Discover Creators</div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <div style={{ fontSize: 12, color: COLORS.textMuted }}>Find creators on FullSend and start supporting</div>
        <div style={{
          padding: "4px 12px", borderRadius: 6,
          background: `${COLORS.accent}10`, border: `1px solid ${COLORS.accent}20`,
          fontSize: 11, fontWeight: 700, color: COLORS.accent,
          fontFamily: "'JetBrains Mono', monospace",
          display: "flex", alignItems: "center", gap: 5,
        }}>
          <span style={{ fontSize: 13 }}>👤</span> {creators.length} creator{creators.length !== 1 ? "s" : ""}
        </div>
      </div>

      {/* Discover / Following tabs */}
      <div style={{ display: "flex", gap: 4, marginBottom: 16 }}>
        {[
          { key: "discover", label: "Discover", icon: "🔍" },
          { key: "leaderboards", label: "Leaderboards", icon: "🏆" },
          { key: "following", label: "Following", icon: "👥" },
          { key: "followers", label: "Follows Me", icon: "💚" },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            padding: "8px 18px", borderRadius: 8,
            background: tab === t.key ? `${COLORS.accent}12` : COLORS.surface,
            border: `1px solid ${tab === t.key ? COLORS.accent + "25" : COLORS.border}`,
            color: tab === t.key ? COLORS.accent : COLORS.textMuted,
            fontFamily: "'Syne', sans-serif", fontSize: 12, fontWeight: 700,
            cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
            transition: "all 0.2s",
          }}>
            <span>{t.icon}</span> {t.label}
            {t.key === "following" && followingList.length > 0 && (
              <span style={{
                fontSize: 9, padding: "1px 5px", borderRadius: 4,
                background: `${COLORS.accent}15`, color: COLORS.accent, fontWeight: 700,
                fontFamily: "'JetBrains Mono', monospace",
              }}>{followingList.length}</span>
            )}
            {t.key === "followers" && followersList.length > 0 && (
              <span style={{
                fontSize: 9, padding: "1px 5px", borderRadius: 4,
                background: `${COLORS.purple}15`, color: COLORS.purple, fontWeight: 700,
                fontFamily: "'JetBrains Mono', monospace",
              }}>{followersList.length}</span>
            )}
          </button>
        ))}
      </div>

      {tab === "leaderboards" && <DiscoverLeaderboards />}

      {/* Featured Creators — highlighted row above search */}
      {tab === "discover" && (() => {
        // Featured creators — manually curated, one per category
        // Add entries here: { address, name, avatar, category, tagline }
        const featured = creators.filter(c => c.totalTips > 0 || c.followers > 0).slice(0, 4);
        // If no real creators yet, show empty state
        if (featured.length === 0 && !loadingCreators) return (
          <div style={{
            padding: "16px 18px", borderRadius: 12,
            background: `linear-gradient(135deg, ${COLORS.gold}06, ${COLORS.orange}06)`,
            border: `1px solid ${COLORS.gold}15`,
            marginBottom: 16, textAlign: "center",
          }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.gold, fontFamily: "'Syne', sans-serif", marginBottom: 4 }}>
              ⭐ Featured Creators
            </div>
            <div style={{ fontSize: 10, color: COLORS.textMuted }}>
              Top creators will be featured here. Start tipping and streaming to get noticed.
            </div>
          </div>
        );
        if (featured.length === 0) return null;
        return (
          <div style={{ marginBottom: 16 }}>
            <div style={{
              display: "flex", alignItems: "center", gap: 6, marginBottom: 10,
            }}>
              <span style={{ fontSize: 14 }}>⭐</span>
              <span style={{ fontSize: 12, fontWeight: 800, color: COLORS.gold, fontFamily: "'Syne', sans-serif" }}>Featured Creators</span>
              <span style={{ fontSize: 8, color: COLORS.textMuted, fontFamily: "'JetBrains Mono', monospace" }}>Updated weekly</span>
            </div>
            <div style={{
              display: "flex", gap: 12, overflowX: "auto", paddingBottom: 8,
              scrollbarWidth: "none", msOverflowStyle: "none",
            }}>
              {featured.map((creator, i) => (
                <div key={i} onClick={() => {
                  window.open(`https://fullsend.live/tip/${creator.realAddress}`, "_blank");
                }} style={{
                  minWidth: 160, padding: "14px 16px", borderRadius: 12,
                  background: `linear-gradient(135deg, ${COLORS.surface}, ${COLORS.bgAlt})`,
                  border: `1px solid ${COLORS.gold}20`,
                  cursor: "pointer",
                  transition: "all 0.2s",
                  display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
                  position: "relative", overflow: "hidden",
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.borderColor = `${COLORS.gold}40`; }}
                onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.borderColor = `${COLORS.gold}20`; }}
                >
                  {/* Featured badge */}
                  <div style={{
                    position: "absolute", top: 6, right: 6,
                    fontSize: 7, fontWeight: 700, padding: "2px 5px", borderRadius: 3,
                    background: `${COLORS.gold}15`, border: `1px solid ${COLORS.gold}25`,
                    color: COLORS.gold, letterSpacing: "0.5px",
                    fontFamily: "'JetBrains Mono', monospace",
                  }}>⭐ FEATURED</div>
                  {/* Avatar */}
                  <div style={{
                    width: 48, height: 48, borderRadius: 12,
                    background: `${COLORS.gold}10`, border: `2px solid ${COLORS.gold}30`,
                    overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    {isImageAvatar(creator.avatar) ? (
                      <img src={creator.avatar} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    ) : (
                      <span style={{ fontSize: 20 }}>{creator.avatar || "👤"}</span>
                    )}
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.text, fontFamily: "'Syne', sans-serif" }}>{creator.name}</div>
                    <div style={{ fontSize: 9, color: COLORS.textMuted, fontFamily: "'JetBrains Mono', monospace" }}>{creator.address}</div>
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    {creator.totalTips > 0 && (
                      <span style={{ fontSize: 8, color: COLORS.accent, fontFamily: "'JetBrains Mono', monospace", fontWeight: 700 }}>
                        💰 {creator.totalTips.toFixed(1)} LYX
                      </span>
                    )}
                    {creator.followers > 0 && (
                      <span style={{ fontSize: 8, color: COLORS.cyan, fontFamily: "'JetBrains Mono', monospace", fontWeight: 700 }}>
                        👥 {creator.followers}
                      </span>
                    )}
                  </div>
                  {creator.category && creator.category.length > 0 && (
                    <span style={{
                      fontSize: 7, padding: "2px 6px", borderRadius: 3,
                      background: `${COLORS.accent}10`, color: COLORS.accent,
                      fontWeight: 600, textTransform: "capitalize",
                    }}>{creator.category[0]}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Search */}
      <div style={{ marginBottom: 10 }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search creators..." style={{
          width: "100%", padding: "8px 14px",
          background: COLORS.surface, border: `1px solid ${COLORS.border}`,
          borderRadius: 7, color: COLORS.text, fontFamily: "'JetBrains Mono', monospace",
          fontSize: 11, outline: "none", boxSizing: "border-box",
        }} />
      </div>

      {/* Category filters */}
      <div style={{ display: "flex", gap: 5, marginBottom: 16, flexWrap: "wrap" }}>
        {categories.map(cat => (
          <button key={cat} onClick={() => setFilter(cat)} style={{
            padding: "6px 12px", borderRadius: 6,
            background: filter === cat ? `${COLORS.accent}12` : COLORS.surface,
            border: `1px solid ${filter === cat ? COLORS.accent + "25" : COLORS.border}`,
            color: filter === cat ? COLORS.accent : COLORS.textMuted,
            fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 600,
            cursor: "pointer", textTransform: "capitalize",
          }}>{cat}</button>
        ))}
      </div>

      {/* Sort options */}
      <div style={{ display: "flex", gap: 4, marginBottom: 16, alignItems: "center" }}>
        <span style={{ fontSize: 9, color: COLORS.textMuted, fontFamily: "'JetBrains Mono', monospace", marginRight: 4 }}>Sort:</span>
        {[
          { key: "tips", label: "Most Tipped", icon: "💰" },
          { key: "followers", label: "Most Followers", icon: "👥" },
          { key: "newest", label: "Newest", icon: "🆕" },
          { key: "name", label: "A-Z", icon: "🔤" },
        ].map(s => (
          <button key={s.key} onClick={() => setSort(s.key)} style={{
            padding: "4px 10px", borderRadius: 5,
            background: sort === s.key ? `${COLORS.cyan}12` : "transparent",
            border: `1px solid ${sort === s.key ? COLORS.cyan + "25" : COLORS.border}`,
            color: sort === s.key ? COLORS.cyan : COLORS.textMuted,
            fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 600,
            cursor: "pointer", display: "flex", alignItems: "center", gap: 4,
            transition: "all 0.15s",
          }}>
            <span style={{ fontSize: 10 }}>{s.icon}</span> {s.label}
          </button>
        ))}
      </div>

      {/* Creator cards */}
      {liveCreators.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{
            display: "flex", alignItems: "center", gap: 10, marginBottom: 12,
            padding: "10px 14px", borderRadius: 8,
            background: "linear-gradient(135deg, #ff1a1a15, #cc000008)",
            border: "1px solid #ff4d4d30",
          }}>
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "4px 10px", borderRadius: 4,
              background: "linear-gradient(135deg, #ff1a1a, #cc0000)",
              border: "1px solid #ff4d4d",
              boxShadow: "0 0 12px #ff000050",
              animation: "liveBadgePulse 2s infinite",
            }}>
              <span style={{
                width: 6, height: 6, borderRadius: "50%",
                background: "#fff",
                boxShadow: "0 0 4px #fff",
                animation: "livePulse 1.2s infinite",
              }} />
              <span style={{
                fontSize: 10, fontWeight: 900, color: "#fff",
                fontFamily: "'JetBrains Mono', monospace",
                letterSpacing: "1.5px",
              }}>LIVE NOW</span>
            </span>
            <span style={{ fontSize: 11, color: COLORS.text, fontWeight: 700, fontFamily: "'Syne', sans-serif" }}>
              {liveCreators.length} {liveCreators.length === 1 ? "creator" : "creators"} streaming right now
            </span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            {liveCreators.map((creator, i) => (
              <div key={`live-${i}`} onClick={() => {
                if (creator.realAddress) {
                  window.open(`/tip/${creator.realAddress}`, "_blank");
                }
              }} style={{
                padding: "16px", borderRadius: 12,
                background: COLORS.surface, border: `1px solid #ff4d4d40`,
                boxShadow: "0 0 16px #ff000020",
                transition: "all 0.2s", cursor: creator.realAddress ? "pointer" : "default",
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = "#ff4d4d80"; e.currentTarget.style.transform = "translateY(-2px)"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = "#ff4d4d40"; e.currentTarget.style.transform = "translateY(0)"; }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{
                    width: 44, height: 44, borderRadius: "50%",
                    background: `linear-gradient(135deg, ${COLORS.accent}30, ${COLORS.cyan}30)`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 22, position: "relative", flexShrink: 0,
                  }}>
                    {isImageAvatar(creator.avatar) ? <img src={creator.avatar} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }} onError={e => { e.target.src = STOCK_AVATAR; }} /> : "👤"}
                    <div style={{
                      position: "absolute", bottom: 0, right: 0,
                      width: 10, height: 10, borderRadius: "50%",
                      background: "#ff4444", border: `2px solid ${COLORS.surface}`,
                      animation: "pulse 1.5s infinite",
                    }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                      <span style={{ fontSize: 13, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{creator.name}</span>
                      {creator.verified && <span style={{ fontSize: 8, color: COLORS.accent }}>{"✓"}</span>}
                      {creator.streamUrl ? (
                        <a href={creator.streamUrl} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} style={{
                          display: "inline-flex", alignItems: "center", gap: 3,
                          padding: "2px 7px", borderRadius: 3,
                          background: "linear-gradient(135deg, #ff1a1a, #cc0000)",
                          border: "1px solid #ff4d4d",
                          boxShadow: "0 0 8px #ff000050",
                          fontSize: 8, fontWeight: 900, color: "#fff",
                          fontFamily: "'JetBrains Mono', monospace",
                          letterSpacing: "0.5px",
                          animation: "liveBadgePulse 2s infinite",
                          textDecoration: "none",
                          cursor: "pointer",
                        }}>
                          <span style={{ width: 4, height: 4, borderRadius: "50%", background: "#fff", boxShadow: "0 0 4px #fff" }} />
                          LIVE →
                        </a>
                      ) : (
                        <span style={{
                          display: "inline-flex", alignItems: "center", gap: 3,
                          padding: "2px 7px", borderRadius: 3,
                          background: "linear-gradient(135deg, #ff1a1a, #cc0000)",
                          border: "1px solid #ff4d4d",
                          boxShadow: "0 0 8px #ff000050",
                          fontSize: 8, fontWeight: 900, color: "#fff",
                          fontFamily: "'JetBrains Mono', monospace",
                          letterSpacing: "0.5px",
                          animation: "liveBadgePulse 2s infinite",
                        }}>
                          <span style={{ width: 4, height: 4, borderRadius: "50%", background: "#fff", boxShadow: "0 0 4px #fff" }} />
                          LIVE
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 9, color: COLORS.textMuted, fontFamily: "'JetBrains Mono', monospace" }}>{creator.address}</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
                      {creator.platforms.map(p => (
                        <div key={p} style={{
                          width: 16, height: 16, borderRadius: 3,
                          background: `${platformColors[p]}15`,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: 7, color: platformColors[p], fontWeight: 700,
                        }}>{platformIcons[p]}</div>
                      ))}
                      <span style={{ fontSize: 8, color: COLORS.textMuted, marginLeft: 2 }}>{creator.followers.toLocaleString()} followers</span>
                    </div>
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <div style={{ fontSize: 9, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "0.5px" }}>Tipped</div>
                    <div style={{ fontSize: 13, fontWeight: 800, color: COLORS.accent, fontFamily: "'JetBrains Mono', monospace" }}>
                      {creator.totalTips >= 1000 ? `${(creator.totalTips / 1000).toFixed(1)}k` : creator.totalTips.toFixed(1)}
                    </div>
                    <div style={{ fontSize: 8, color: COLORS.textMuted }}>LYX</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Creator cards — offline creators */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        {offlineCreators.map((creator, i) => (
          <div key={i} onClick={() => {
            if (creator.realAddress) {
              window.open(`/tip/${creator.realAddress}`, "_blank");
            }
          }} style={{
            padding: "16px", borderRadius: 12,
            background: COLORS.surface, border: `1px solid ${creator.live ? COLORS.accent + "20" : COLORS.border}`,
            transition: "all 0.2s", cursor: creator.realAddress ? "pointer" : "default",
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = `${COLORS.accent}30`; e.currentTarget.style.transform = "translateY(-2px)"; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = creator.live ? `${COLORS.accent}20` : COLORS.border; e.currentTarget.style.transform = "translateY(0)"; }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{
                width: 44, height: 44, borderRadius: "50%",
                background: `linear-gradient(135deg, ${COLORS.accent}30, ${COLORS.cyan}30)`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 22, position: "relative", flexShrink: 0,
              }}>
                {isImageAvatar(creator.avatar) ? <img src={creator.avatar} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }} onError={e => { e.target.src = STOCK_AVATAR; }} /> : "👤"}
                {creator.live && (
                  <div style={{
                    position: "absolute", bottom: 0, right: 0,
                    width: 10, height: 10, borderRadius: "50%",
                    background: "#ff4444", border: `2px solid ${COLORS.surface}`,
                    animation: "pulse 1.5s infinite",
                  }} />
                )}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{creator.name}</span>
                  {creator.verified && <span style={{ fontSize: 8, color: COLORS.accent }}>{"✓"}</span>}
                  {creator.live && (
                    creator.streamUrl ? (
                      <a href={creator.streamUrl} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} style={{
                        display: "inline-flex", alignItems: "center", gap: 3,
                        padding: "2px 7px", borderRadius: 3,
                        background: "linear-gradient(135deg, #ff1a1a, #cc0000)",
                        border: "1px solid #ff4d4d",
                        boxShadow: "0 0 8px #ff000050",
                        fontSize: 8, fontWeight: 900, color: "#fff",
                        fontFamily: "'JetBrains Mono', monospace",
                        letterSpacing: "0.5px",
                        animation: "liveBadgePulse 2s infinite",
                        textDecoration: "none",
                        cursor: "pointer",
                      }}>
                        <span style={{
                          width: 4, height: 4, borderRadius: "50%",
                          background: "#fff",
                          boxShadow: "0 0 4px #fff",
                        }} />
                        LIVE →
                      </a>
                    ) : (
                      <span style={{
                        display: "inline-flex", alignItems: "center", gap: 3,
                        padding: "2px 7px", borderRadius: 3,
                        background: "linear-gradient(135deg, #ff1a1a, #cc0000)",
                        border: "1px solid #ff4d4d",
                        boxShadow: "0 0 8px #ff000050",
                        fontSize: 8, fontWeight: 900, color: "#fff",
                        fontFamily: "'JetBrains Mono', monospace",
                        letterSpacing: "0.5px",
                        animation: "liveBadgePulse 2s infinite",
                      }}>
                        <span style={{
                          width: 4, height: 4, borderRadius: "50%",
                          background: "#fff",
                          boxShadow: "0 0 4px #fff",
                        }} />
                        LIVE
                      </span>
                    )
                  )}
                </div>
                <div style={{ fontSize: 9, color: COLORS.textMuted, fontFamily: "'JetBrains Mono', monospace" }}>{creator.address}</div>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
                  {creator.platforms.map(p => (
                    <div key={p} style={{
                      width: 16, height: 16, borderRadius: 3,
                      background: `${platformColors[p]}15`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 7, color: platformColors[p], fontWeight: 700,
                    }}>{platformIcons[p]}</div>
                  ))}
                  <span style={{ fontSize: 8, color: COLORS.textMuted, marginLeft: 2 }}>{creator.followers.toLocaleString()} followers</span>
                </div>
              </div>
              <div style={{ textAlign: "right", flexShrink: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: COLORS.accent, fontFamily: "'JetBrains Mono', monospace" }}>
                  {creator.totalTips >= 1000 ? `${(creator.totalTips / 1000).toFixed(1)}k` : creator.totalTips.toFixed(1)}
                </div>
                <div style={{ fontSize: 7, color: COLORS.textMuted }}>LYX tipped</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filtered.length === 0 && (
        <div style={{ textAlign: "center", padding: 40, color: COLORS.textMuted, fontSize: 12 }}>
          No creators found matching your search
        </div>
      )}

      {filtered.length === 0 && tab === "following" && (
        <div style={{
          textAlign: "center", padding: "40px 20px",
          background: COLORS.surface, borderRadius: 12,
          border: `1px solid ${COLORS.border}`,
        }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>👥</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.text, marginBottom: 4 }}>
            {window.__fullsend_profile ? "Not following anyone yet" : "Connect your UP to see who you follow"}
          </div>
          <div style={{ fontSize: 11, color: COLORS.textMuted }}>
            Follow creators from their tip page — it's one click and gasless
          </div>
        </div>
      )}

      {filtered.length === 0 && tab === "followers" && (
        <div style={{
          textAlign: "center", padding: "40px 20px",
          background: COLORS.surface, borderRadius: 12,
          border: `1px solid ${COLORS.border}`,
        }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>💚</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.text, marginBottom: 4 }}>
            {window.__fullsend_profile ? "No one follows you yet" : "Connect your UP to see your followers"}
          </div>
          <div style={{ fontSize: 11, color: COLORS.textMuted }}>
            Share your tip page and start building your community
          </div>
        </div>
      )}

      <div style={{ textAlign: "center", marginTop: 16, fontSize: 9, color: COLORS.textMuted }}>
        {tab === "following" ? `${filtered.length} creators you follow`
          : tab === "followers" ? `${filtered.length} people follow you`
          : `${creators.length} creators on FullSend`}
      </div>
    </div>
  );
}

// --- SITE FOOTER ---------------------------------------------
// --- CREATOR SETTINGS ----------------------------------------
function CreatorSettings() {
  const [mounted, setMounted] = useState(false);
  const [activeSection, setActiveSection] = useState(() => {
    const tab = window.__fullsend_settings_tab;
    if (tab) { window.__fullsend_settings_tab = null; return tab; }
    return "profile";
  });

  // Listen for deep-link to specific settings tab
  useEffect(() => {
    const handler = (e) => setActiveSection(e.detail);
    window.addEventListener("fullsend-settings-tab", handler);
    const resetHandler = () => { setActiveSection("profile"); };
    window.addEventListener("fullsend-tab-reset", resetHandler);
    return () => { window.removeEventListener("fullsend-settings-tab", handler); window.removeEventListener("fullsend-tab-reset", resetHandler); };
  }, []);

  // Settings state — persist across tab changes via window global
  // Priority: saved settings > UP profile data > empty defaults
  const _s = window.__fullsend_settings_data || (() => { try { return JSON.parse(localStorage.getItem("fullsend_settings") || "{}"); } catch { return {}; } })();
  const up = window.__fullsend_profile || {};
  const upLinks = up.links || {};

  // Name: saved > UP name > empty (placeholder shown in input)
  const [channelName, setChannelName] = useState(_s.channelName || up.name || "");
  // Bio: saved > UP bio > empty
  const [channelBio, setChannelBio] = useState(_s.channelBio || up.bio || "");
  const [channelTags, setChannelTags] = useState(_s.channelTags || []);
  const [webhookUrl, setWebhookUrl] = useState(_s.webhookUrl || "");
  // Social links: saved > UP links > empty — UP links are the base
  const [socialLinks, setSocialLinks] = useState(() => {
    const saved = _s.socialLinks || {};
    return {
      youtube: saved.youtube || upLinks.youtube || "",
      twitch: saved.twitch || upLinks.twitch || "",
      kick: saved.kick || upLinks.kick || "",
      rumble: saved.rumble || upLinks.rumble || "",
      x: saved.x || upLinks.x || "",
      website: saved.website || upLinks.website || "",
    };
  });
  // Custom uploaded icons per social link (key -> base64 data URL). Empty = use default icon.
  const [socialIcons, setSocialIcons] = useState(() => _s.socialIcons || {});
  // Creator-defined extra links (beyond the fixed 6): [{ id, url, icon }]
  const [customLinks, setCustomLinks] = useState(() => Array.isArray(_s.customLinks) ? _s.customLinks : []);
  const [verification, setVerification] = useState(() => {
    const defaults = { code: null, status: "none", verifiedAt: null };
    const saved = _s.verification || {};
    return {
      youtube: saved.youtube || { ...defaults },
      twitch: saved.twitch || { ...defaults },
      kick: saved.kick || { ...defaults },
      x: saved.x || { ...defaults },
      website: saved.website || { ...defaults },
    };
  });
  const [verifyingPlatform, setVerifyingPlatform] = useState(null);
  const [verifyChecking, setVerifyChecking] = useState(false);
  const [verifyError, setVerifyError] = useState(null);
  const [copiedCode, setCopiedCode] = useState(null);
  const [minTip, setMinTip] = useState(_s.minTip || "");
  const [autoApproveThreshold, setAutoApproveThreshold] = useState(_s.autoApproveThreshold || "5");
  const [autoShowTiers, setAutoShowTiers] = useState(_s.autoShowTiers || {
    micro: false, supporter: false, champion: false, legend: false, whale: false,
  });
  const [customTiers, setCustomTiers] = useState(() => {
    const fresh = {
      micro: { min: 0, max: 4.99 },
      supporter: { min: 5, max: 19.99 },
      champion: { min: 20, max: 49.99 },
      legend: { min: 50, max: 99.99 },
      whale: { min: 100, max: null },
    };
    const saved = _s.customTiers;
    if (!saved) return fresh;
    // Migrate if stored values don't match the current non-overlapping scheme
    const supMin = parseFloat(saved.supporter?.min);
    const chaMin = parseFloat(saved.champion?.min);
    const legMin = parseFloat(saved.legend?.min);
    const whaMin = parseFloat(saved.whale?.min);
    const isStale = (
      supMin !== 5 ||
      chaMin !== 20 ||
      legMin !== 50 ||
      whaMin !== 100
    );
    return isStale ? fresh : saved;
  });
  // While a tier min is being typed, hold the raw string here so the value isn't
  // clamped/cascaded mid-keystroke (commit happens on blur).
  const [tierEdit, setTierEdit] = useState(null);
  const [overlayUrl] = useState(`https://fullsend.live/overlay/${up.address || "0x0000"}`);
  const [tipPageUrl] = useState(`https://fullsend.live/tip/${up.address || "0x0000"}`);
  const [blockedProfiles, setBlockedProfiles] = useState(_s.blockedProfiles || []);
  const [blockSearch, setBlockSearch] = useState("");
  const [selectedSound, setSelectedSound] = useState(() => window.__fullsend_selected_sounds || _s.selectedSound || {
    micro: "none",
    supporter: "none",
    champion: "none",
    legend: "none",
    whale: "none",
    follow: "none",
  });
  const [customSounds, setCustomSounds] = useState(_s.customSounds || {});

  // Expose selected sounds globally for overlay
  useEffect(() => {
    window.__fullsend_selected_sounds = selectedSound;
  }, [selectedSound]);
  const [saved, setSaved] = useState(false);
  const [showUpGate, setShowUpGate] = useState(false);
  // Goal state moved to GoalManager (DB-only via GoalAPI). No goal fields in settings.
  const [showTopTippers, setShowTopTippers] = useState(_s.showTopTippers !== undefined ? _s.showTopTippers : true);

  // Thank You card settings
  const [thankYouEnabled, setThankYouEnabled] = useState(_s.thankYouEnabled || true);
  const [thankYouMessage, setThankYouMessage] = useState(_s.thankYouMessage || "Thank you for your support! 💚");
  const [thankYouImage, setThankYouImage] = useState(_s.thankYouImage || "");
  const [thankYouGifSearch, setThankYouGifSearch] = useState(false);
  const [thankYouGifQuery, setThankYouGifQuery] = useState("");
  const [thankYouGifResults, setThankYouGifResults] = useState([]);
  const [customQrUrl, setCustomQrUrl] = useState(_s.customQrUrl || "");
  const [uploadedQrImage, setUploadedQrImage] = useState(_s.uploadedQrImage || "");

  // Persist all settings to window global + localStorage on change
  useEffect(() => {
    const data = {
      channelName, channelBio, channelTags, webhookUrl, socialLinks, socialIcons, customLinks, verification,
      minTip, autoApproveThreshold, autoShowTiers, customTiers, blockedProfiles, selectedSound, customSounds,
      showTopTippers,
      thankYouEnabled, thankYouMessage, thankYouImage,
      customQrUrl, uploadedQrImage,
      // TTS + sound persistence — read from window globals (set by other effects)
      tts: window.__fullsend_tts || { enabled: false },
      masterVolume: window.__fullsend_volume ?? 0.7,
      soundToggles: window.__fullsend_sound_toggles || { tips: true, follows: true, polls: true },
    };
    window.__fullsend_settings_data = data;
    try { localStorage.setItem("fullsend_settings", JSON.stringify(data)); } catch {}
    // Notify other components (like TipWidget) that settings changed
    window.dispatchEvent(new CustomEvent("fullsend-settings-changed", { detail: data }));
  }, [channelName, channelBio, channelTags, webhookUrl, socialLinks, socialIcons, customLinks, verification,
    minTip, autoApproveThreshold, autoShowTiers, customTiers, blockedProfiles, selectedSound, customSounds,
    showTopTippers,
    thankYouEnabled, thankYouMessage, thankYouImage, customQrUrl, uploadedQrImage]);

  // Expose thank you card settings globally for widget
  useEffect(() => {
    window.__fullsend_thankyou = { enabled: thankYouEnabled, message: thankYouMessage, image: thankYouImage };
  }, [thankYouEnabled, thankYouMessage, thankYouImage]);

  // Auto-fill empty fields from UP profile when component mounts
  useEffect(() => {
    const profile = window.__fullsend_profile;
    if (!profile) return;
    // Only fill if the field is empty (user hasn't customized it yet)
    if (!channelName && profile.name) setChannelName(profile.name);
    if (!channelBio && profile.bio) setChannelBio(profile.bio);
    // Fill social links from UP if none are saved
    if (profile.links) {
      setSocialLinks(prev => {
        const hasAny = Object.values(prev).some(v => v && v.trim());
        if (hasAny) return prev; // User already has links, don't overwrite
        return {
          youtube: prev.youtube || profile.links.youtube || "",
          twitch: prev.twitch || profile.links.twitch || "",
          kick: prev.kick || profile.links.kick || "",
          rumble: prev.rumble || profile.links.rumble || "",
          x: prev.x || profile.links.x || "",
          website: prev.website || profile.links.website || "",
        };
      });
    }
    // Load tags from DB if localStorage has none
    if (channelTags.length === 0 && profile.address) {
      fetch(`/api/creator-register?address=${profile.address}`)
        .then(r => r.json())
        .then(data => {
          const creator = data.creators?.[0] || data.creator;
          if (creator?.tags && creator.tags.length > 0) {
            setChannelTags(creator.tags);
          }
        })
        .catch(() => {});
    }
  }, []);

  useEffect(() => { requestAnimationFrame(() => setMounted(true)); }, []);

  const [showUPGate, setShowUPGate] = useState(false);

  const handleSave = () => {
    // Gate: EOAs cannot publish creator profiles — soft preview only
    const up = window.__fullsend_profile;
    if (up && up.isUP === false) {
      setShowUPGate(true);
      return;
    }
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
    syncCreatorToDB();
  };

  const copyToClipboard = (text) => {
    navigator.clipboard?.writeText?.(text);
  };

  const sections = [
    { id: "profile", label: "Profile", icon: "👤" },
    { id: "tipping", label: "Tipping", icon: "💰" },
    { id: "overlay-settings", label: "Overlay", icon: "📺" },
    { id: "sounds", label: "Sounds", icon: "🔊" },
    { id: "moderation", label: "Moderation", icon: "🛡️" },
    { id: "links", label: "Links & Embed", icon: "🔗" },
    { id: "webhook", label: "Webhook API", icon: "🔌" },
  ];

  const stockSounds = {
    micro: ["none", "typewriter", "puzzle", "mario", "whoosh"],
    supporter: ["none", "doorbell", "cash-register", "woah", "gameshow"],
    champion: ["none", "camera", "PA", "gong", "champion"],
    legend: ["none", "rizz", "wow", "Wilhelm", "hello-there"],
    whale: ["none", "royal-fanfare", "yeah-boi", "isnt-that-amazing", "whale"],
    follow: ["none", "noti", "8bit", "upgrade", "amp"],
  };

  const [playingSound, setPlayingSound] = useState(null);

  // TTS (Text-to-Speech) settings
  const [ttsEnabled, setTtsEnabled] = useState(() => window.__fullsend_tts?.enabled || false);
  const [ttsVoice, setTtsVoice] = useState(() => window.__fullsend_tts?.voice || "");
  const [ttsMinAmount, setTtsMinAmount] = useState(() => String(window.__fullsend_tts?.minAmount || "5"));
  const [ttsTiers, setTtsTiers] = useState(() => window.__fullsend_tts?.tiers || { micro: false, green: false, blue: true, purple: true, fire: true });
  const [ttsRate, setTtsRate] = useState(() => window.__fullsend_tts?.rate || 1);
  const [ttsPitch, setTtsPitch] = useState(() => window.__fullsend_tts?.pitch || 1);
  const [masterVolume, setMasterVolume] = useState(() => window.__fullsend_volume || 0.7);
  const [availableVoices, setAvailableVoices] = useState([]);

  // Load available voices
  useEffect(() => {
    const loadVoices = () => {
      const voices = window.speechSynthesis?.getVoices() || [];
      setAvailableVoices(voices);
      if (voices.length > 0 && !ttsVoice) setTtsVoice(voices[0].name);
    };
    loadVoices();
    window.speechSynthesis?.addEventListener("voiceschanged", loadVoices);
    return () => window.speechSynthesis?.removeEventListener("voiceschanged", loadVoices);
  }, []);

  // Store TTS settings globally so overlay can access them, persist to localStorage too
  useEffect(() => {
    window.__fullsend_tts = { enabled: ttsEnabled, voice: ttsVoice, minAmount: parseFloat(ttsMinAmount) || 5, rate: ttsRate, pitch: ttsPitch, tiers: ttsTiers };
    window.__fullsend_volume = masterVolume;
    // Persist to localStorage so it survives page reload
    try {
      const s = JSON.parse(localStorage.getItem("fullsend_settings") || "{}");
      s.tts = window.__fullsend_tts;
      s.masterVolume = masterVolume;
      s.soundToggles = window.__fullsend_sound_toggles || { tips: true, follows: true, polls: true };
      localStorage.setItem("fullsend_settings", JSON.stringify(s));
      if (window.__fullsend_settings_data) {
        window.__fullsend_settings_data.tts = window.__fullsend_tts;
        window.__fullsend_settings_data.masterVolume = masterVolume;
        window.__fullsend_settings_data.soundToggles = s.soundToggles;
      }
    } catch {}
  }, [ttsEnabled, ttsVoice, ttsMinAmount, ttsRate, ttsPitch, masterVolume, ttsTiers]);

  const previewSound = (tierKey, soundName) => {
    setPlayingSound(`${tierKey}-${soundName}`);
    // MP3 sounds: play via Audio element
    if (STOCK_MP3_SOUNDS[soundName]) {
      try {
        const audio = new Audio(STOCK_MP3_SOUNDS[soundName]);
        audio.volume = masterVolume;
        audio.play().catch(() => {});
        setTimeout(() => setPlayingSound(null), 2000);
      } catch {}
      return;
    }
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const master = ctx.createGain();
      master.gain.value = masterVolume;
      master.connect(ctx.destination);
      const now = ctx.currentTime;
      // All sounds route through master for volume control
      const dest = master;

      // Different synth patterns per sound type
      const patterns = {
        // Micro - short, subtle
        "coin-drop": () => { const o = ctx.createOscillator(); const g = ctx.createGain(); o.type = "sine"; o.frequency.setValueAtTime(1200, now); o.frequency.exponentialRampToValueAtTime(600, now + 0.15); g.gain.setValueAtTime(0.3, now); g.gain.exponentialRampToValueAtTime(0.01, now + 0.2); o.connect(g); g.connect(dest); o.start(now); o.stop(now + 0.2); },
        "soft-ping": () => { const o = ctx.createOscillator(); const g = ctx.createGain(); o.type = "sine"; o.frequency.value = 880; g.gain.setValueAtTime(0.2, now); g.gain.exponentialRampToValueAtTime(0.01, now + 0.3); o.connect(g); g.connect(dest); o.start(now); o.stop(now + 0.3); },
        "subtle-chime": () => { [1047, 1319, 1568].forEach((f, i) => { const o = ctx.createOscillator(); const g = ctx.createGain(); o.type = "sine"; o.frequency.value = f; g.gain.setValueAtTime(0.15, now + i * 0.08); g.gain.exponentialRampToValueAtTime(0.01, now + i * 0.08 + 0.25); o.connect(g); g.connect(dest); o.start(now + i * 0.08); o.stop(now + i * 0.08 + 0.25); }); },
        "pop": () => { const o = ctx.createOscillator(); const g = ctx.createGain(); o.type = "sine"; o.frequency.setValueAtTime(400, now); o.frequency.exponentialRampToValueAtTime(150, now + 0.1); g.gain.setValueAtTime(0.4, now); g.gain.exponentialRampToValueAtTime(0.01, now + 0.12); o.connect(g); g.connect(dest); o.start(now); o.stop(now + 0.12); },
        // Supporter - medium, satisfying
        "cash-register": () => { [800, 1000, 1200, 1400].forEach((f, i) => { const o = ctx.createOscillator(); const g = ctx.createGain(); o.type = "square"; o.frequency.value = f; g.gain.setValueAtTime(0.15, now + i * 0.05); g.gain.exponentialRampToValueAtTime(0.01, now + i * 0.05 + 0.1); o.connect(g); g.connect(dest); o.start(now + i * 0.05); o.stop(now + i * 0.05 + 0.15); }); },
        "level-up": () => { [523, 659, 784, 1047].forEach((f, i) => { const o = ctx.createOscillator(); const g = ctx.createGain(); o.type = "square"; o.frequency.value = f; g.gain.setValueAtTime(0.2, now + i * 0.1); g.gain.exponentialRampToValueAtTime(0.01, now + i * 0.1 + 0.2); o.connect(g); g.connect(dest); o.start(now + i * 0.1); o.stop(now + i * 0.1 + 0.25); }); },
        "notification-bell": () => { const o = ctx.createOscillator(); const g = ctx.createGain(); o.type = "sine"; o.frequency.value = 1500; g.gain.setValueAtTime(0.3, now); g.gain.setValueAtTime(0.3, now + 0.1); g.gain.exponentialRampToValueAtTime(0.01, now + 0.5); o.connect(g); g.connect(dest); o.start(now); o.stop(now + 0.5); },
        "success": () => { [523, 659, 784].forEach((f, i) => { const o = ctx.createOscillator(); const g = ctx.createGain(); o.type = "triangle"; o.frequency.value = f; g.gain.setValueAtTime(0.25, now + i * 0.12); g.gain.exponentialRampToValueAtTime(0.01, now + i * 0.12 + 0.3); o.connect(g); g.connect(dest); o.start(now + i * 0.12); o.stop(now + i * 0.12 + 0.35); }); },
        // Champion - bigger, fuller
        "fanfare": () => { [523, 659, 784, 1047, 784, 1047].forEach((f, i) => { const o = ctx.createOscillator(); const g = ctx.createGain(); o.type = "sawtooth"; o.frequency.value = f; g.gain.setValueAtTime(0.12, now + i * 0.1); g.gain.exponentialRampToValueAtTime(0.01, now + i * 0.1 + 0.25); o.connect(g); g.connect(dest); o.start(now + i * 0.1); o.stop(now + i * 0.1 + 0.3); }); },
        "power-up": () => { const o = ctx.createOscillator(); const g = ctx.createGain(); o.type = "sawtooth"; o.frequency.setValueAtTime(200, now); o.frequency.exponentialRampToValueAtTime(1200, now + 0.4); g.gain.setValueAtTime(0.2, now); g.gain.exponentialRampToValueAtTime(0.01, now + 0.5); o.connect(g); g.connect(dest); o.start(now); o.stop(now + 0.5); },
        "achievement": () => { [784, 988, 1175, 1568].forEach((f, i) => { const o = ctx.createOscillator(); const g = ctx.createGain(); o.type = "triangle"; o.frequency.value = f; g.gain.setValueAtTime(0.2, now + i * 0.08); g.gain.exponentialRampToValueAtTime(0.01, now + i * 0.08 + 0.4); o.connect(g); g.connect(dest); o.start(now + i * 0.08); o.stop(now + i * 0.08 + 0.45); }); },
        "trumpets": () => { [392, 494, 587, 784].forEach((f, i) => { const o = ctx.createOscillator(); const g = ctx.createGain(); o.type = "sawtooth"; o.frequency.value = f; g.gain.setValueAtTime(0.1, now + i * 0.15); g.gain.exponentialRampToValueAtTime(0.01, now + i * 0.15 + 0.35); o.connect(g); g.connect(dest); o.start(now + i * 0.15); o.stop(now + i * 0.15 + 0.4); }); },
        // Legend - epic, dramatic
        "epic-horn": () => { [262, 330, 392, 523].forEach((f, i) => { const o = ctx.createOscillator(); const o2 = ctx.createOscillator(); const g = ctx.createGain(); o.type = "sawtooth"; o2.type = "square"; o.frequency.value = f; o2.frequency.value = f * 1.005; g.gain.setValueAtTime(0.01, now + i * 0.15); g.gain.linearRampToValueAtTime(0.15, now + i * 0.15 + 0.1); g.gain.setValueAtTime(0.15, now + i * 0.15 + 0.3); g.gain.exponentialRampToValueAtTime(0.01, now + i * 0.15 + 0.8); o.connect(g); o2.connect(g); g.connect(dest); o.start(now + i * 0.15); o2.start(now + i * 0.15); o.stop(now + i * 0.15 + 0.85); o2.stop(now + i * 0.15 + 0.85); }); },
        "dramatic-reveal": () => { const o = ctx.createOscillator(); const o2 = ctx.createOscillator(); const g = ctx.createGain(); o.type = "sawtooth"; o2.type = "triangle"; o.frequency.setValueAtTime(80, now); o.frequency.exponentialRampToValueAtTime(600, now + 0.5); o.frequency.exponentialRampToValueAtTime(800, now + 0.8); o2.frequency.setValueAtTime(82, now); o2.frequency.exponentialRampToValueAtTime(602, now + 0.5); g.gain.setValueAtTime(0.01, now); g.gain.linearRampToValueAtTime(0.2, now + 0.3); g.gain.setValueAtTime(0.2, now + 0.5); g.gain.exponentialRampToValueAtTime(0.01, now + 1.0); o.connect(g); o2.connect(g); g.connect(dest); o.start(now); o2.start(now); o.stop(now + 1.0); o2.stop(now + 1.0); },
        "crowd-cheer": () => { for (let i = 0; i < 12; i++) { const o = ctx.createOscillator(); const g = ctx.createGain(); o.type = i % 2 === 0 ? "sawtooth" : "triangle"; o.frequency.value = 150 + Math.random() * 500; o.frequency.linearRampToValueAtTime(200 + Math.random() * 600, now + 0.4); g.gain.setValueAtTime(0.01, now); g.gain.linearRampToValueAtTime(0.05, now + 0.15); g.gain.setValueAtTime(0.05, now + 0.5); g.gain.exponentialRampToValueAtTime(0.01, now + 1.0); o.connect(g); g.connect(dest); o.start(now + Math.random() * 0.1); o.stop(now + 1.0); } },
        "orchestral": () => { [262, 330, 392, 523, 659].forEach((f, i) => { const o = ctx.createOscillator(); const o2 = ctx.createOscillator(); const g = ctx.createGain(); o.type = "sawtooth"; o2.type = "sine"; o.frequency.value = f; o2.frequency.value = f * 2; g.gain.setValueAtTime(0.01, now); g.gain.linearRampToValueAtTime(0.08, now + 0.15); g.gain.setValueAtTime(0.08, now + 0.5); g.gain.exponentialRampToValueAtTime(0.01, now + 1.2); o.connect(g); o2.connect(g); g.connect(dest); o.start(now + i * 0.05); o2.start(now + i * 0.05); o.stop(now + 1.2); o2.stop(now + 1.2); }); },
        // Whale - massive, earth-shaking
        "air-horn": () => { [110, 138, 165, 220].forEach((f, i) => { const o = ctx.createOscillator(); const o2 = ctx.createOscillator(); const g = ctx.createGain(); o.type = "sawtooth"; o2.type = "square"; o.frequency.value = f; o2.frequency.value = f * 1.01; o.frequency.linearRampToValueAtTime(f * 1.05, now + 0.5); o.frequency.linearRampToValueAtTime(f * 0.98, now + 1.0); g.gain.setValueAtTime(0.01, now); g.gain.linearRampToValueAtTime(0.18, now + 0.15); g.gain.setValueAtTime(0.18, now + 0.7); g.gain.exponentialRampToValueAtTime(0.01, now + 1.4); o.connect(g); o2.connect(g); g.connect(dest); o.start(now); o2.start(now); o.stop(now + 1.4); o2.stop(now + 1.4); }); },
        "explosion": () => { const bufferSize = ctx.sampleRate * 1.2; const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate); const data = buffer.getChannelData(0); for (let i = 0; i < bufferSize; i++) data[i] = (Math.random() * 2 - 1) * Math.exp(-i / (ctx.sampleRate * 0.2)); const src = ctx.createBufferSource(); const g = ctx.createGain(); const filter = ctx.createBiquadFilter(); filter.type = "lowpass"; filter.frequency.setValueAtTime(4000, now); filter.frequency.exponentialRampToValueAtTime(200, now + 1.0); src.buffer = buffer; g.gain.setValueAtTime(0.5, now); g.gain.exponentialRampToValueAtTime(0.01, now + 1.2); src.connect(filter); filter.connect(g); g.connect(dest); src.start(now); const boom = ctx.createOscillator(); const bg = ctx.createGain(); boom.type = "sine"; boom.frequency.setValueAtTime(80, now); boom.frequency.exponentialRampToValueAtTime(20, now + 0.8); bg.gain.setValueAtTime(0.4, now); bg.gain.exponentialRampToValueAtTime(0.01, now + 0.8); boom.connect(bg); bg.connect(dest); boom.start(now); boom.stop(now + 0.8); },
        "dubstep-drop": () => { const o = ctx.createOscillator(); const o2 = ctx.createOscillator(); const g = ctx.createGain(); const filter = ctx.createBiquadFilter(); filter.type = "lowpass"; filter.frequency.setValueAtTime(5000, now); o.type = "sawtooth"; o2.type = "square"; o.frequency.setValueAtTime(800, now); o.frequency.exponentialRampToValueAtTime(40, now + 0.25); o.frequency.setValueAtTime(800, now + 0.3); o.frequency.exponentialRampToValueAtTime(40, now + 0.55); o.frequency.setValueAtTime(600, now + 0.6); o.frequency.exponentialRampToValueAtTime(30, now + 0.85); o2.frequency.setValueAtTime(802, now); o2.frequency.exponentialRampToValueAtTime(41, now + 0.25); g.gain.setValueAtTime(0.3, now); g.gain.setValueAtTime(0.3, now + 0.5); g.gain.exponentialRampToValueAtTime(0.01, now + 1.0); o.connect(filter); o2.connect(filter); filter.connect(g); g.connect(dest); o.start(now); o2.start(now); o.stop(now + 1.0); o2.stop(now + 1.0); },
        "crowd-roar": () => { for (let i = 0; i < 16; i++) { const o = ctx.createOscillator(); const g = ctx.createGain(); o.type = i % 3 === 0 ? "sawtooth" : i % 3 === 1 ? "triangle" : "square"; const baseF = 80 + Math.random() * 500; o.frequency.value = baseF; o.frequency.linearRampToValueAtTime(baseF + Math.random() * 300, now + 0.3); o.frequency.linearRampToValueAtTime(baseF - 50, now + 0.8); g.gain.setValueAtTime(0.01, now + Math.random() * 0.1); g.gain.linearRampToValueAtTime(0.04, now + 0.2); g.gain.setValueAtTime(0.04, now + 0.6); g.gain.exponentialRampToValueAtTime(0.01, now + 1.3); o.connect(g); g.connect(dest); o.start(now + Math.random() * 0.08); o.stop(now + 1.3); } },
        // Follow
        "notification": () => { [880, 1100].forEach((f, i) => { const o = ctx.createOscillator(); const g = ctx.createGain(); o.type = "sine"; o.frequency.value = f; g.gain.setValueAtTime(0.2, now + i * 0.12); g.gain.exponentialRampToValueAtTime(0.01, now + i * 0.12 + 0.2); o.connect(g); g.connect(dest); o.start(now + i * 0.12); o.stop(now + i * 0.12 + 0.25); }); },
        "welcome-chime": () => { [659, 784, 988, 1319].forEach((f, i) => { const o = ctx.createOscillator(); const g = ctx.createGain(); o.type = "sine"; o.frequency.value = f; g.gain.setValueAtTime(0.15, now + i * 0.1); g.gain.exponentialRampToValueAtTime(0.01, now + i * 0.1 + 0.3); o.connect(g); g.connect(dest); o.start(now + i * 0.1); o.stop(now + i * 0.1 + 0.35); }); },
        "bell": () => { const o = ctx.createOscillator(); const g = ctx.createGain(); o.type = "sine"; o.frequency.value = 2000; g.gain.setValueAtTime(0.25, now); g.gain.exponentialRampToValueAtTime(0.01, now + 0.6); o.connect(g); g.connect(dest); o.start(now); o.stop(now + 0.6); },
        "whoosh": () => { const o = ctx.createOscillator(); const g = ctx.createGain(); o.type = "sine"; o.frequency.setValueAtTime(200, now); o.frequency.exponentialRampToValueAtTime(3000, now + 0.15); o.frequency.exponentialRampToValueAtTime(200, now + 0.3); g.gain.setValueAtTime(0.15, now); g.gain.exponentialRampToValueAtTime(0.01, now + 0.35); o.connect(g); g.connect(dest); o.start(now); o.stop(now + 0.35); },
      };
      if (patterns[soundName]) patterns[soundName]();
      setTimeout(() => ctx.close(), 1500);
    } catch(e) { /* audio context not available */ }
    setTimeout(() => setPlayingSound(null), 800);
  };

  // Expose previewSound globally for overlay
  useEffect(() => {
    window.__fullsend_previewSound = previewSound;
  });

  // Gate: require wallet connection to access settings
  if (!window.__fullsend_profile?.address) {
    return (
      <div style={{
        maxWidth: 720, margin: "0 auto", padding: "80px 24px",
        textAlign: "center",
        opacity: mounted ? 1 : 0,
        transform: mounted ? "translateY(0)" : "translateY(20px)",
        transition: "all 0.5s cubic-bezier(0.16, 1, 0.3, 1)",
      }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
        <div style={{
          fontFamily: "'Syne', sans-serif",
          fontSize: 20, fontWeight: 800, color: COLORS.text,
          marginBottom: 8,
        }}>Connect to Access Settings</div>
        <div style={{ fontSize: 12, color: COLORS.textMuted, lineHeight: 1.6, maxWidth: 360, margin: "0 auto" }}>
          Connect your Universal Profile to configure your channel, tipping preferences, and overlay settings.
        </div>
      </div>
    );
  }

  return (
    <div style={{
      maxWidth: 720, margin: "0 auto", padding: "28px 24px",
      opacity: mounted ? 1 : 0,
      transform: mounted ? "translateY(0)" : "translateY(20px)",
      transition: "all 0.5s cubic-bezier(0.16, 1, 0.3, 1)",
    }}>
      {/* UP Required Modal — shown when EOA tries to save/publish */}
      {showUPGate && (
        <div onClick={() => setShowUPGate(false)} style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(0,0,0,0.7)", backdropFilter: "blur(6px)",
          display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 1000, padding: 20,
          animation: "fadeIn 0.2s ease-out",
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            maxWidth: 460, width: "100%",
            background: `linear-gradient(135deg, ${COLORS.surface}, ${COLORS.cyan}08)`,
            border: `1px solid ${COLORS.cyan}30`,
            borderRadius: 16, padding: "28px 24px",
            position: "relative",
            animation: "scaleIn 0.25s cubic-bezier(0.16, 1, 0.3, 1)",
          }}>
            <button onClick={() => setShowUPGate(false)} style={{
              position: "absolute", top: 12, right: 12,
              background: "transparent", border: "none",
              color: COLORS.textMuted, fontSize: 18, cursor: "pointer",
              width: 28, height: 28, borderRadius: 6,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>×</button>
            <div style={{ fontSize: 36, marginBottom: 8, textAlign: "center" }}>🆔</div>
            <div style={{
              fontFamily: "'Syne', sans-serif",
              fontSize: 20, fontWeight: 800, textAlign: "center",
              background: `linear-gradient(135deg, ${COLORS.cyan}, ${COLORS.accent})`,
              WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
              marginBottom: 10,
            }}>One Last Step</div>
            <div style={{
              fontSize: 13, color: COLORS.textSecondary, lineHeight: 1.6,
              textAlign: "center", marginBottom: 16,
            }}>
              Publishing your creator page requires a <strong style={{ color: COLORS.text }}>LUKSO Universal Profile</strong>. It's free and takes about 2 minutes. <strong style={{ color: COLORS.accent }}>Everything you've configured here will carry over</strong> — don't worry.
            </div>
            <div style={{
              padding: "12px 14px", borderRadius: 10,
              background: `${COLORS.bg}60`, border: `1px solid ${COLORS.border}`,
              marginBottom: 16,
            }}>
              <div style={{
                fontSize: 9, fontWeight: 800, color: COLORS.accent,
                fontFamily: "'JetBrains Mono', monospace",
                letterSpacing: "1.2px", textTransform: "uppercase", marginBottom: 6,
              }}>What you unlock</div>
              <ul style={{ margin: 0, padding: "0 0 0 16px", fontSize: 11, color: COLORS.textSecondary, lineHeight: 1.7 }}>
                <li>Gasless follows and votes for your audience</li>
                <li>On-chain PFP + name on every alert</li>
                <li>Creator directory listing + leaderboard presence</li>
                <li>Future NFT milestones + rewards</li>
              </ul>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <a href={isMobileDevice() ? getUPMobileLink() : "https://my.universalprofile.cloud/"} target="_blank" rel="noopener noreferrer" style={{
                flex: 1,
                padding: "12px", borderRadius: 10,
                background: `linear-gradient(135deg, ${COLORS.cyan}, ${COLORS.accent})`,
                color: COLORS.bg,
                fontSize: 13, fontWeight: 700,
                fontFamily: "'Syne', sans-serif",
                textDecoration: "none", textAlign: "center",
                cursor: "pointer", transition: "transform 0.2s",
              }}
              onMouseEnter={e => e.currentTarget.style.transform = "translateY(-1px)"}
              onMouseLeave={e => e.currentTarget.style.transform = "translateY(0)"}>
                {isMobileDevice() ? "Get UP App" : "Create Universal Profile"} →
              </a>
              <button onClick={() => setShowUPGate(false)} style={{
                padding: "12px 16px", borderRadius: 10,
                background: "transparent",
                border: `1px solid ${COLORS.border}`,
                color: COLORS.textMuted,
                fontSize: 11, fontWeight: 700,
                fontFamily: "'JetBrains Mono', monospace",
                cursor: "pointer",
                letterSpacing: "0.5px", textTransform: "uppercase",
              }}>Keep Editing</button>
            </div>
          </div>
        </div>
      )}
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{
          fontFamily: "'Syne', sans-serif",
          fontSize: 26, fontWeight: 800,
          background: `linear-gradient(135deg, ${COLORS.textSecondary}, ${COLORS.text})`,
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          letterSpacing: "-0.5px",
        }}>Settings</div>
        <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 5 }}>
          Configure your channel, tipping preferences, and overlay
        </div>
      </div>

      {/* Section nav */}
      <div style={{
        display: "flex", gap: 4, marginBottom: 20,
        padding: 4, borderRadius: 10,
        background: COLORS.surface,
        border: `1px solid ${COLORS.border}`,
        overflowX: "auto",
      }}>
        {sections.map(s => (
          <button key={s.id} onClick={() => setActiveSection(s.id)} style={{
            flex: 1, padding: "8px 6px", borderRadius: 7,
            background: activeSection === s.id ? `${COLORS.accent}12` : "transparent",
            border: `1px solid ${activeSection === s.id ? COLORS.accent + "20" : "transparent"}`,
            color: activeSection === s.id ? COLORS.accent : COLORS.textMuted,
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 9, fontWeight: 600, cursor: "pointer",
            transition: "all 0.2s",
            textAlign: "center",
            whiteSpace: "nowrap",
          }}>
            <div style={{ fontSize: 14, marginBottom: 2 }}>{s.icon}</div>
            {s.label}
          </button>
        ))}
      </div>

      {/* -- PROFILE SECTION -- */}
      {activeSection === "profile" && (
        <div style={{ animation: "fadeInUp 0.3s ease-out" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {/* Profile Avatar */}
            <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 4 }}>
              <div style={{
                width: 64, height: 64, borderRadius: 16,
                background: `linear-gradient(135deg, ${COLORS.accent}20, ${COLORS.purple}20)`,
                border: `2px solid ${COLORS.accent}20`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 28, overflow: "hidden", flexShrink: 0,
              }}>
                {isImageAvatar(up.avatar) ? (
                  <img src={up.avatar} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                ) : (up.avatar || "👤")}
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.text }}>{channelName}</div>
                <div style={{ fontSize: 10, color: COLORS.textMuted, fontFamily: "'JetBrains Mono', monospace", marginTop: 2 }}>
                  {up.address ? `${up.address.slice(0, 10)}...${up.address.slice(-6)}` : "Not connected"}
                </div>
                <div style={{ fontSize: 9, color: COLORS.textMuted, marginTop: 4 }}>
                  Profile image is pulled from your Universal Profile
                </div>
              </div>
            </div>
            {/* Channel Name */}
            <div>
              <label style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "1.5px", display: "block", marginBottom: 6 }}>
                Channel Name
              </label>
              <input value={channelName} onChange={e => setChannelName(e.target.value)} style={{
                width: "100%", padding: "10px 14px",
                background: COLORS.surface, border: `1px solid ${COLORS.border}`,
                borderRadius: 8, color: COLORS.text,
                fontFamily: "'Outfit', sans-serif", fontSize: 14, fontWeight: 600,
                outline: "none", boxSizing: "border-box",
                transition: "border-color 0.2s",
              }}
              onFocus={e => e.target.style.borderColor = `${COLORS.accent}30`}
              onBlur={e => e.target.style.borderColor = COLORS.border} />
            </div>

            {/* Bio */}
            <div>
              <label style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "1.5px", display: "block", marginBottom: 6 }}>
                Channel Bio
              </label>
              <textarea value={channelBio} onChange={e => setChannelBio(e.target.value)} rows={3} style={{
                width: "100%", padding: "10px 14px",
                background: COLORS.surface, border: `1px solid ${COLORS.border}`,
                borderRadius: 8, color: COLORS.text,
                fontFamily: "'Outfit', sans-serif", fontSize: 12,
                outline: "none", boxSizing: "border-box", resize: "vertical",
                transition: "border-color 0.2s",
              }}
              onFocus={e => e.target.style.borderColor = `${COLORS.accent}30`}
              onBlur={e => e.target.style.borderColor = COLORS.border} />
            </div>

            {/* Channel Tags */}
            <div>
              <label style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "1.5px", display: "block", marginBottom: 6 }}>
                Channel Tags
              </label>
              <div style={{ fontSize: 9, color: COLORS.textMuted, marginBottom: 8 }}>
                Select up to 3 tags. These help viewers find you in the Creator Directory.
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {["crypto", "gaming", "music", "art", "tech", "education", "irl", "cooking", "fitness", "comedy", "news", "defi"].map(tag => {
                  const isSelected = channelTags.includes(tag);
                  return (
                    <button key={tag} onClick={() => {
                      if (isSelected) setChannelTags(prev => prev.filter(t2 => t2 !== tag));
                      else if (channelTags.length < 3) setChannelTags(prev => [...prev, tag]);
                    }} style={{
                      padding: "4px 10px", borderRadius: 4,
                      background: isSelected ? `${COLORS.accent}12` : COLORS.bgAlt,
                      border: `1px solid ${isSelected ? COLORS.accent + "25" : COLORS.border}`,
                      color: isSelected ? COLORS.accent : COLORS.textMuted,
                      fontSize: 9, fontWeight: 600, cursor: "pointer",
                      textTransform: "capitalize",
                      opacity: !isSelected && channelTags.length >= 3 ? 0.3 : 1,
                    }}>{tag}</button>
                  );
                })}
              </div>
            </div>

            {/* Social Links */}
            <div>
              <label style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "1.5px", display: "block", marginBottom: 8 }}>
                Social Links
              </label>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {[
                  { key: "youtube", label: "YouTube", icon: "▶", color: "#FF0000", placeholder: "https://youtube.com/@..." },
                  { key: "twitch", label: "Twitch", icon: "◆", color: "#9146FF", placeholder: "https://twitch.tv/..." },
                  { key: "kick", label: "Kick", icon: "●", color: "#53FC18", placeholder: "https://kick.com/..." },
                  { key: "x", label: "X", icon: "𝕏", color: "#fff", placeholder: "https://x.com/..." },
                  { key: "rumble", label: "Rumble", icon: "R", color: "#85c742", placeholder: "https://rumble.com/c/..." },
                  { key: "website", label: "Website", icon: "🌐", color: "#4a9eff", placeholder: "https://yoursite.com" },
                ].map(platform => {
                  const v = verification[platform.key] || { code: null, status: "none", verifiedAt: null };
                  return (
                  <div key={platform.key}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <label style={{
                        width: 32, height: 32, borderRadius: 6,
                        background: socialIcons[platform.key] ? COLORS.surface : `${platform.color}12`,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 12, color: platform.color, fontWeight: 700,
                        flexShrink: 0, position: "relative", cursor: "pointer",
                      }} title="Upload a custom icon">
                        <input type="file" accept="image/png,image/jpeg,image/svg+xml,.png,.jpg,.jpeg,.svg" style={{ display: "none" }} onChange={e => {
                          const f = e.target.files?.[0]; e.target.value = "";
                          readIconFile(f, (dataUrl) => setSocialIcons(prev => ({ ...prev, [platform.key]: dataUrl })));
                        }} />
                        {socialIcons[platform.key]
                          ? <img src={socialIcons[platform.key]} alt="" style={{ width: 22, height: 22, borderRadius: 4, objectFit: "cover", display: "block" }} />
                          : platform.icon}
                        {!socialIcons[platform.key] && (
                          <div style={{
                            position: "absolute", bottom: -3, left: -3,
                            width: 12, height: 12, borderRadius: "50%",
                            background: COLORS.purple, color: "#fff",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: 9, fontWeight: 900, lineHeight: 1,
                            border: `1.5px solid ${COLORS.bg}`,
                          }}>+</div>
                        )}
                        {socialIcons[platform.key] && (
                          <div onClick={e => { e.preventDefault(); e.stopPropagation(); setSocialIcons(prev => { const n = { ...prev }; delete n[platform.key]; return n; }); }}
                            title="Remove custom icon" style={{
                            position: "absolute", top: -5, right: -5,
                            width: 14, height: 14, borderRadius: "50%",
                            background: COLORS.red, color: "#fff",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: 10, fontWeight: 900, lineHeight: 1, cursor: "pointer",
                            border: `1.5px solid ${COLORS.bg}`,
                          }}>×</div>
                        )}
                        {v.status === "verified" && (
                          <div style={{
                            position: "absolute", bottom: -2, right: -2,
                            width: 10, height: 10, borderRadius: "50%",
                            background: COLORS.accent, display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: 6, color: COLORS.bg, fontWeight: 900,
                            border: `1.5px solid ${COLORS.surface}`,
                          }}>{"✓"}</div>
                        )}
                      </label>
                      <input
                        value={socialLinks[platform.key]}
                        onChange={e => {
                          setSocialLinks(prev => ({ ...prev, [platform.key]: e.target.value }));
                          if (v.status === "verified") {
                            setVerification(prev => ({ ...prev, [platform.key]: { ...prev[platform.key], status: "none", code: null, verifiedAt: null } }));
                          }
                        }}
                        placeholder={platform.placeholder}
                        style={{
                          flex: 1, padding: "8px 12px",
                          background: COLORS.surface, border: `1px solid ${COLORS.border}`,
                          borderRadius: 6, color: COLORS.text,
                          fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
                          outline: "none", boxSizing: "border-box",
                        }}
                      />
                      {(platform.key === "youtube" || platform.key === "twitch") && v.status === "none" && (
                        <button onClick={() => {
                          if (!socialLinks[platform.key]) return;
                          const upName = (window.__fullsend_profile?.name || "anon").replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
                          const addr = (window.__fullsend_profile?.address || "").slice(2, 6);
                          const code = `fullsend-verify-${upName}${addr}`;
                          setVerification(prev => ({ ...prev, [platform.key]: { ...prev[platform.key], code, status: "pending" } }));
                          setVerifyingPlatform(platform.key);
                        }} style={{
                          padding: "4px 8px", borderRadius: 4,
                          background: `${COLORS.cyan}10`, border: `1px solid ${COLORS.cyan}20`,
                          color: COLORS.cyan, fontSize: 8, fontWeight: 700,
                          cursor: socialLinks[platform.key] ? "pointer" : "default",
                          opacity: socialLinks[platform.key] ? 1 : 0.35,
                          fontFamily: "'JetBrains Mono', monospace", whiteSpace: "nowrap",
                        }}>Verify</button>
                      )}
                      {(platform.key === "youtube" || platform.key === "twitch") && socialLinks[platform.key] && v.status === "pending" && (
                        <button onClick={() => setVerifyingPlatform(verifyingPlatform === platform.key ? null : platform.key)} style={{
                          padding: "4px 8px", borderRadius: 4,
                          background: `${COLORS.orange}10`, border: `1px solid ${COLORS.orange}20`,
                          fontSize: 8, fontWeight: 700, color: COLORS.orange, whiteSpace: "nowrap",
                          cursor: "pointer",
                        }}>Pending</button>
                      )}
                      {socialLinks[platform.key] && v.status === "verified" && (
                        <div style={{
                          padding: "4px 8px", borderRadius: 4,
                          background: `${COLORS.accent}10`, border: `1px solid ${COLORS.accent}20`,
                          fontSize: 8, fontWeight: 700, color: COLORS.accent, whiteSpace: "nowrap",
                          display: "flex", alignItems: "center", gap: 3,
                        }}>{"✓"} Verified</div>
                      )}
                    </div>
                    {/* Verification instructions */}
                    {v.status === "pending" && verifyingPlatform === platform.key && (
                      <div style={{
                        marginTop: 6, marginLeft: 40, padding: "10px 12px", borderRadius: 6,
                        background: `${COLORS.orange}05`, border: `1px solid ${COLORS.orange}12`,
                        animation: "fadeInUp 0.2s ease-out",
                      }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.text, marginBottom: 6 }}>
                          Verify your {platform.label}
                        </div>
                        <div style={{ fontSize: 9, color: COLORS.textMuted, lineHeight: 1.6, marginBottom: 8 }}>
                          Paste this code anywhere in your {platform.label} {platform.key === "youtube" ? "channel description" : "bio"}:
                        </div>
                        <div style={{
                          display: "flex", alignItems: "center", gap: 6,
                          padding: "6px 10px", borderRadius: 4,
                          background: COLORS.bg, border: `1px solid ${COLORS.border}`,
                        }}>
                          <code style={{
                            flex: 1, fontSize: 10, color: COLORS.orange,
                            fontFamily: "'JetBrains Mono', monospace",
                          }}>{v.code}</code>
                          <button onClick={() => {
                            navigator.clipboard?.writeText?.(v.code);
                            setCopiedCode(platform.key);
                            setTimeout(() => setCopiedCode(null), 2000);
                          }} style={{
                            padding: "3px 10px", borderRadius: 3,
                            background: copiedCode === platform.key ? `${COLORS.accent}15` : `${COLORS.orange}12`,
                            border: `1px solid ${copiedCode === platform.key ? COLORS.accent + "30" : COLORS.orange + "20"}`,
                            color: copiedCode === platform.key ? COLORS.accent : COLORS.orange,
                            fontSize: 8, fontWeight: 700, cursor: "pointer",
                            fontFamily: "'JetBrains Mono', monospace",
                            transition: "all 0.2s",
                            minWidth: 50, textAlign: "center",
                          }}>{copiedCode === platform.key ? "✓ Copied!" : "📋 Copy"}</button>
                        </div>
                        <div style={{ display: "flex", gap: 6, marginTop: 8, alignItems: "center" }}>
                          {(platform.key === "youtube" || platform.key === "twitch") ? (
                            <button onClick={async () => {
                              setVerifyChecking(true);
                              setVerifyError(null);
                              try {
                                const res = await fetch("/api/verify-creator", {
                                  method: "POST",
                                  headers: { "Content-Type": "application/json" },
                                  body: JSON.stringify({
                                    platform: platform.key,
                                    url: socialLinks[platform.key],
                                    code: v.code,
                                    creator: window.__fullsend_profile?.address,
                                  }),
                                });
                                const result = await res.json();
                                if (result.verified) {
                                  setVerification(prev => ({ ...prev, [platform.key]: { ...prev[platform.key], status: "verified", verifiedAt: new Date().toISOString() } }));
                                  setVerifyingPlatform(null);
                                  setVerifyError(null);
                                } else {
                                  setVerifyError(result.error || "Code not found. Make sure you saved it.");
                                }
                              } catch (err) {
                                setVerifyError("Verification check failed. Try again.");
                              }
                              setVerifyChecking(false);
                            }} disabled={verifyChecking} style={{
                              padding: "6px 14px", borderRadius: 4,
                              background: verifyChecking ? COLORS.surface : `${COLORS.accent}12`,
                              border: `1px solid ${verifyChecking ? COLORS.border : COLORS.accent + "30"}`,
                              color: verifyChecking ? COLORS.textMuted : COLORS.accent,
                              fontSize: 9, fontWeight: 700, cursor: verifyChecking ? "wait" : "pointer",
                              fontFamily: "'JetBrains Mono', monospace",
                            }}>{verifyChecking ? "⏳ Checking..." : "✓ Check Now"}</button>
                          ) : (
                            <div style={{
                              flex: 1, fontSize: 9, color: COLORS.orange,
                              fontFamily: "'JetBrains Mono', monospace", fontWeight: 600,
                            }}>⏳ Verification coming soon for {platform.label}</div>
                          )}
                          <button onClick={() => {
                            setVerification(prev => ({ ...prev, [platform.key]: { code: null, status: "none", verifiedAt: null } }));
                            setVerifyingPlatform(null);
                            setVerifyError(null);
                          }} style={{
                            padding: "6px 10px", borderRadius: 4,
                            background: "transparent", border: `1px solid ${COLORS.border}`,
                            color: COLORS.textMuted, fontSize: 9, fontWeight: 600, cursor: "pointer",
                          }}>Cancel</button>
                        </div>
                        {verifyError && verifyingPlatform === platform.key && (
                          <div style={{ marginTop: 6, fontSize: 9, color: COLORS.red, fontFamily: "'JetBrains Mono', monospace", lineHeight: 1.5 }}>
                            🚫 {verifyError}
                          </div>
                        )}
                        <div style={{ fontSize: 8, color: COLORS.textMuted, marginTop: 6, lineHeight: 1.5 }}>
                          We check every 24 hours. If the code is removed, the badge will be revoked. Changing the URL resets verification.
                        </div>
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            </div>

            {/* Custom Links — creator-defined extra links, each with an uploadable icon */}
            <div style={{ marginTop: 18 }}>
              <label style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "1.5px", display: "block", marginBottom: 4 }}>
                Custom Links
              </label>
              <div style={{ fontSize: 9, color: COLORS.textMuted, marginBottom: 8, lineHeight: 1.4 }}>
                Add any extra links (Discord, store, OpenSea…). Tap the icon to upload a logo for each.
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {customLinks.map((link, idx) => (
                  <div key={link.id || idx} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <label style={{
                      width: 32, height: 32, borderRadius: 6,
                      background: link.icon ? COLORS.surface : `${COLORS.purple}12`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      flexShrink: 0, position: "relative", cursor: "pointer",
                    }} title="Upload an icon">
                      <input type="file" accept="image/png,image/jpeg,image/svg+xml,.png,.jpg,.jpeg,.svg" style={{ display: "none" }} onChange={e => {
                        const f = e.target.files?.[0]; e.target.value = "";
                        readIconFile(f, (dataUrl) => setCustomLinks(prev => prev.map((l, i) => i === idx ? { ...l, icon: dataUrl } : l)));
                      }} />
                      {link.icon
                        ? <img src={link.icon} alt="" style={{ width: 22, height: 22, borderRadius: 4, objectFit: "cover", display: "block" }} />
                        : <span style={{ fontSize: 14 }}>🌐</span>}
                      {!link.icon && (
                        <div style={{
                          position: "absolute", bottom: -3, left: -3,
                          width: 12, height: 12, borderRadius: "50%",
                          background: COLORS.purple, color: "#fff",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: 9, fontWeight: 900, lineHeight: 1,
                          border: `1.5px solid ${COLORS.bg}`,
                        }}>+</div>
                      )}
                    </label>
                    <input
                      value={link.url}
                      onChange={e => setCustomLinks(prev => prev.map((l, i) => i === idx ? { ...l, url: e.target.value } : l))}
                      placeholder="https://..."
                      style={{
                        flex: 1, padding: "8px 12px",
                        background: COLORS.surface, border: `1px solid ${COLORS.border}`,
                        borderRadius: 6, color: COLORS.text,
                        fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
                        outline: "none", boxSizing: "border-box",
                      }}
                    />
                    <button onClick={() => setCustomLinks(prev => prev.filter((_, i) => i !== idx))} title="Remove link" style={{
                      width: 28, height: 28, flexShrink: 0, borderRadius: 6,
                      background: `${COLORS.red}10`, border: `1px solid ${COLORS.red}20`,
                      color: COLORS.red, fontSize: 15, fontWeight: 700, cursor: "pointer",
                      display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1,
                    }}>×</button>
                  </div>
                ))}
                {customLinks.length < 6 ? (
                  <button onClick={() => setCustomLinks(prev => [...prev, { id: Date.now().toString(36) + Math.random().toString(36).slice(2, 6), url: "", icon: "" }])} style={{
                    marginTop: 2, padding: "8px 12px", borderRadius: 6,
                    background: `${COLORS.purple}10`, border: `1px dashed ${COLORS.purple}30`,
                    color: COLORS.purple, fontSize: 10, fontWeight: 700, cursor: "pointer",
                    fontFamily: "'JetBrains Mono', monospace",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                  }}>+ Add custom link</button>
                ) : (
                  <div style={{
                    marginTop: 2, padding: "8px 12px", textAlign: "center",
                    fontSize: 9, color: COLORS.textMuted, fontFamily: "'JetBrains Mono', monospace",
                  }}>Max 6 custom links reached</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* -- TIPPING SECTION -- */}
      {activeSection === "tipping" && (
        <div style={{ animation: "fadeInUp 0.3s ease-out" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            {/* Stream Status -- read-only, auto-detected in Phase 2 */}
            <div style={{
              padding: 14, borderRadius: 10,
              background: COLORS.surface,
              border: `1px solid ${COLORS.border}`,
            }}>
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
              }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.text, marginBottom: 2 }}>
                    Stream Status
                  </div>
                  <div style={{ fontSize: 10, color: COLORS.textMuted }}>
                    Auto-detected from your linked streaming platforms
                  </div>
                </div>
                <div style={{
                  padding: "5px 12px", borderRadius: 6,
                  background: `${COLORS.textMuted}10`,
                  border: `1px solid ${COLORS.textMuted}15`,
                  fontSize: 10, fontWeight: 700, color: COLORS.textMuted,
                  display: "flex", alignItems: "center", gap: 5,
                }}>
                  <div style={{
                    width: 6, height: 6, borderRadius: "50%",
                    background: COLORS.textMuted,
                  }} />
                  Offline
                </div>
              </div>
              <div style={{
                marginTop: 8, fontSize: 9, color: COLORS.textMuted, lineHeight: 1.5,
                padding: "6px 10px", borderRadius: 6,
                background: COLORS.bgAlt,
              }}>
                When your YouTube, Twitch, or Kick stream goes live, FullSend detects it automatically. The widget shows a live indicator and tip messages appear on stream via the overlay. When offline, tips and messages are still accepted — the message goes to the creator's control panel instead of the stream.
              </div>
            </div>

            {/* Share Your Tip Link */}
            <div style={{
              padding: 14, borderRadius: 10,
              background: COLORS.surface, border: `1px solid ${COLORS.accent}20`,
            }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.text, marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
                <span>🔗</span> Your Tip Link
              </div>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <div style={{
                  flex: 1, padding: "8px 10px", borderRadius: 6,
                  background: COLORS.bgAlt, border: `1px solid ${COLORS.border}`,
                  fontSize: 10, color: COLORS.cyan, fontFamily: "'JetBrains Mono', monospace",
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>fullsend.live/tip/{window.__fullsend_profile?.address || "0x..."}</div>
                <CopyButton text={`https://fullsend.live/tip/${window.__fullsend_profile?.address || ""}`} label="Copy" color={COLORS.accent} />
              </div>
              <div style={{ fontSize: 9, color: COLORS.textMuted, marginTop: 6 }}>
                Share this on stream, social media, or your bio. Viewers can tip you instantly.
              </div>
            </div>

            {/* Minimum Tip */}
            <div>
              <label style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "1.5px", display: "block", marginBottom: 6 }}>
                Minimum Tip Amount
              </label>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  type="number" value={minTip}
                  onChange={e => setMinTip(e.target.value)}
                  placeholder="No minimum"
                  style={{
                    width: 120, padding: "9px 12px",
                    background: COLORS.surface, border: `1px solid ${COLORS.border}`,
                    borderRadius: 6, color: COLORS.text,
                    fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 700,
                    outline: "none", textAlign: "center",
                  }}
                />
                <span style={{ fontSize: 11, color: COLORS.accent, fontWeight: 700 }}>LYX</span>
              </div>
              <div style={{ fontSize: 9, color: COLORS.textMuted, marginTop: 4 }}>
                Leave empty for no minimum. Tips below this amount will be blocked at the widget.
              </div>
            </div>

            {/* Auto-Show on Stream (per tier) */}
            <div>
              <label style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "1.5px", display: "block", marginBottom: 6 }}>
                Auto-Show on Stream
              </label>
              <div style={{ fontSize: 9, color: COLORS.textMuted, marginBottom: 10 }}>
                When toggled ON, tips in that tier auto-show on the overlay without approval. When OFF, they go to your Control Panel queue for manual review.
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {[
                  { key: "micro", label: "Micro", emoji: "⚡", color: COLORS.orange, desc: "Handle + amount" },
                  { key: "supporter", label: "Supporter", emoji: "💚", color: COLORS.accent, desc: "PFP + handle + message" },
                  { key: "champion", label: "Champion", emoji: "💪", color: COLORS.blue, desc: "PFP + handle + message" },
                  { key: "legend", label: "Legend", emoji: "💎", color: COLORS.purple, desc: "PFP + handle + message" },
                  { key: "whale", label: "Whale", emoji: "🐋", color: COLORS.gold, desc: "PFP + handle + message + Elite alert" },
                ].map(tier => {
                  const isOn = autoShowTiers[tier.key] || false;
                  return (
                    <div key={tier.key} style={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      padding: "8px 12px", borderRadius: 8,
                      background: isOn ? `${tier.color}06` : COLORS.surface,
                      border: `1px solid ${isOn ? tier.color + "20" : COLORS.border}`,
                      transition: "all 0.2s",
                    }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 14 }}>{tier.emoji}</span>
                        <div>
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <span style={{ fontSize: 11, fontWeight: 600, color: COLORS.text }}>{tier.label}</span>
                            <span style={{ fontSize: 9, color: isOn ? tier.color : COLORS.textMuted }}>
                              {isOn ? "Auto-show on stream" : "Needs approval"}
                            </span>
                          </div>
                          <div style={{ fontSize: 8, color: COLORS.textMuted, marginTop: 1 }}>{tier.desc}</div>
                        </div>
                      </div>
                      <div onClick={() => setAutoShowTiers(prev => ({ ...prev, [tier.key]: !isOn }))} style={{
                        width: 36, height: 20, borderRadius: 10, cursor: "pointer",
                        background: isOn ? `${tier.color}40` : `${COLORS.bg}80`,
                        border: `1px solid ${isOn ? tier.color + "50" : COLORS.border}`,
                        position: "relative", transition: "all 0.3s",
                      }}>
                        <div style={{
                          width: 14, height: 14, borderRadius: "50%",
                          background: isOn ? tier.color : COLORS.textMuted,
                          position: "absolute", top: 2,
                          left: isOn ? 19 : 2,
                          transition: "all 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55)",
                          boxShadow: isOn ? `0 0 8px ${tier.color}50` : "none",
                        }} />
                      </div>
                    </div>
                  );
                })}
              </div>
              <div style={{ fontSize: 9, color: COLORS.orange, marginTop: 6, display: "flex", alignItems: "center", gap: 4 }}>
                <span>⚠️</span> All tiers are OFF by default to protect against vulgar messages. Enable at your own risk.
              </div>
            </div>

            {/* Customizable Tiers */}
            <div>
              <label style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "1.5px", display: "block", marginBottom: 8 }}>
                Tip Tiers
              </label>
              <div style={{ fontSize: 9, color: COLORS.textMuted, marginBottom: 10 }}>
                Customize the LYX thresholds for each tier. These control alert styles, overlay animations, and NFT milestones.
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {[
                  { key: "micro", label: "Micro", emoji: "⚡", color: COLORS.orange, hasMin: false, shows: "Handle + amount (no msg)" },
                  { key: "supporter", label: "Supporter", emoji: "💚", color: COLORS.accent, shows: "PFP + handle + 80 char msg" },
                  { key: "champion", label: "Champion", emoji: "💪", color: COLORS.blue, shows: "120 char msg + TTS" },
                  { key: "legend", label: "Legend", emoji: "💎", color: COLORS.purple, shows: "150 char msg + extended TTS" },
                  { key: "whale", label: "WHALE", emoji: "🐋", color: COLORS.gold, hasMax: false, shows: "200 char msg + Elite alert + TTS" },
                ].map(tier => (
                  <div key={tier.key} style={{
                    display: "flex", alignItems: "center", gap: 10,
                    padding: "8px 12px", borderRadius: 8,
                    background: COLORS.surface, border: `1px solid ${COLORS.border}`,
                  }}>
                    <span style={{ fontSize: 16, width: 24, textAlign: "center" }}>{tier.emoji}</span>
                    <div style={{ width: 80 }}>
                      <span style={{ fontSize: 10, fontWeight: 700, color: tier.color, display: "block" }}>{tier.label}</span>
                      <span style={{ fontSize: 7, color: COLORS.textMuted, display: "block", marginTop: 1 }}>{tier.shows}</span>
                    </div>
                    {tier.hasMin === false ? (
                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <span style={{
                          width: 50, padding: "4px 6px", borderRadius: 4,
                          background: COLORS.bgAlt, border: `1px solid ${COLORS.border}`,
                          color: COLORS.textMuted, fontFamily: "'JetBrains Mono', monospace",
                          fontSize: 11, fontWeight: 700, textAlign: "center",
                          display: "inline-block", opacity: 0.5,
                        }}>{minTip || "0"}</span>
                        <span style={{ fontSize: 9, color: COLORS.textMuted }}>LYX</span>
                      </div>
                    ) : (
                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <input type="number" step="0.01"
                          value={tierEdit?.key === tier.key ? tierEdit.value : customTiers[tier.key].min}
                          onChange={e => setTierEdit({ key: tier.key, value: e.target.value })}
                          onKeyDown={e => { if (e.key === "Enter") e.currentTarget.blur(); }}
                          onBlur={() => {
                            if (!tierEdit || tierEdit.key !== tier.key) return;
                            const raw = parseFloat(tierEdit.value);
                            setTierEdit(null);
                            if (isNaN(raw) || raw < 0) return; // invalid → keep previous value
                            const keys = ["micro", "supporter", "champion", "legend", "whale"];
                            const idx = keys.indexOf(tier.key);
                            // Must sit strictly between the previous and next tier's min
                            const prevMin = idx > 0 ? parseFloat(customTiers[keys[idx-1]].min) || 0 : 0;
                            const minFloor = idx > 0 ? prevMin + 0.01 : 0.01;
                            const nextKey = keys[idx+1];
                            const nextMin = nextKey ? parseFloat(customTiers[nextKey].min) : Infinity;
                            const minCeil = nextMin - 0.01;
                            const r2 = n => Math.round(n * 100) / 100; // kill float drift (20.002 etc.)
                            const val = r2(Math.max(minFloor, Math.min(raw, minCeil)));
                            setCustomTiers(prev => {
                              const updated = { ...prev, [tier.key]: { ...prev[tier.key], min: val } };
                              // Keep ranges contiguous: previous tier's max = this min - 0.01
                              if (idx > 0) {
                                updated[keys[idx-1]] = { ...updated[keys[idx-1]], max: r2(Math.max(0, val - 0.01)) };
                              }
                              return updated;
                            });
                          }}
                          style={{
                          width: 50, padding: "4px 6px", borderRadius: 4,
                          background: COLORS.bgAlt, border: `1px solid ${COLORS.border}`,
                          color: COLORS.text, fontFamily: "'JetBrains Mono', monospace",
                          fontSize: 11, fontWeight: 700, textAlign: "center", outline: "none",
                        }} />
                        <span style={{ fontSize: 9, color: COLORS.textMuted }}>LYX</span>
                      </div>
                    )}
                    <span style={{ fontSize: 9, color: COLORS.textMuted }}>{tier.hasMax === false ? "+" : "-"}</span>
                    {tier.hasMax === false ? (
                      <span style={{ fontSize: 9, color: COLORS.textMuted, fontFamily: "'JetBrains Mono', monospace" }}>No limit</span>
                    ) : (
                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <input type="number" value={Math.round((parseFloat(customTiers[tier.key].max) || 0) * 100) / 100} readOnly style={{
                          width: 50, padding: "4px 6px", borderRadius: 4,
                          background: COLORS.bgAlt, border: `1px solid ${COLORS.border}`,
                          color: COLORS.textMuted, fontFamily: "'JetBrains Mono', monospace",
                          fontSize: 11, fontWeight: 700, textAlign: "center", outline: "none",
                          opacity: 0.5,
                        }} />
                        <span style={{ fontSize: 9, color: COLORS.textMuted }}>LYX</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div style={{ fontSize: 8, color: COLORS.textMuted, marginTop: 6, lineHeight: 1.5 }}>
                Each tier's max auto-adjusts when you change the next tier's minimum. Micro always starts at 0, Whale has no upper limit.
              </div>
            </div>

            {/* Top Tippers Leaderboard toggle */}
            <div style={{
              padding: 14, borderRadius: 10,
              background: showTopTippers ? `${COLORS.gold}06` : COLORS.surface,
              border: `1px solid ${showTopTippers ? COLORS.gold + "20" : COLORS.border}`,
            }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.text, marginBottom: 2, display: "flex", alignItems: "center", gap: 6 }}>
                    <span>🏆</span> Top Tippers Leaderboard
                  </div>
                  <div style={{ fontSize: 10, color: COLORS.textMuted }}>
                    {showTopTippers ? "Showing daily top tippers on your widget" : "Leaderboard hidden from widget"}
                  </div>
                </div>
                <div
                  onClick={() => setShowTopTippers(!showTopTippers)}
                  style={{
                    width: 44, height: 24, borderRadius: 12,
                    background: showTopTippers ? `${COLORS.gold}40` : `${COLORS.bg}80`,
                    border: `1px solid ${showTopTippers ? COLORS.gold + "50" : COLORS.border}`,
                    position: "relative", cursor: "pointer",
                    transition: "all 0.25s cubic-bezier(0.4,0,0.2,1)",
                  }}
                >
                  <div style={{
                    width: 18, height: 18, borderRadius: "50%",
                    background: showTopTippers ? COLORS.gold : COLORS.textMuted,
                    position: "absolute", top: 2,
                    left: showTopTippers ? 22 : 2,
                    transition: "all 0.25s cubic-bezier(0.4,0,0.2,1)",
                    boxShadow: showTopTippers ? `0 0 8px ${COLORS.gold}50` : "none",
                  }} />
                </div>
              </div>
            </div>

            {/* Thank You Card */}
            <div style={{ marginTop: 16 }}>
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                marginBottom: 10,
              }}>
                <div>
                  <label style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "1.5px" }}>
                    Thank You Card
                  </label>
                  <div style={{ fontSize: 9, color: COLORS.textMuted, marginTop: 2 }}>
                    Shown to tippers after a successful tip
                  </div>
                </div>
                <div onClick={() => setThankYouEnabled(!thankYouEnabled)} style={{
                  width: 34, height: 18, borderRadius: 9,
                  background: thankYouEnabled ? `${COLORS.accent}40` : `${COLORS.bg}80`,
                  border: `1px solid ${thankYouEnabled ? COLORS.accent + "50" : COLORS.border}`,
                  position: "relative", cursor: "pointer", transition: "all 0.25s",
                }}>
                  <div style={{
                    width: 12, height: 12, borderRadius: "50%",
                    background: thankYouEnabled ? COLORS.accent : COLORS.textMuted,
                    position: "absolute", top: 2, left: thankYouEnabled ? 18 : 2,
                    transition: "all 0.25s",
                  }} />
                </div>
              </div>

              {thankYouEnabled && (
                <div style={{
                  padding: 14, borderRadius: 10,
                  background: COLORS.bgAlt,
                  border: `1px solid ${COLORS.accent}15`,
                  display: "flex", flexDirection: "column", gap: 10,
                }}>
                  {/* Message */}
                  <div>
                    <label style={{ fontSize: 9, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "1px", display: "block", marginBottom: 4 }}>
                      Message
                    </label>
                    <textarea value={thankYouMessage} onChange={e => setThankYouMessage(e.target.value)} rows={2} style={{
                      width: "100%", padding: "8px 10px", background: COLORS.surface,
                      border: `1px solid ${COLORS.border}`, borderRadius: 6,
                      color: COLORS.text, fontFamily: "'Outfit', sans-serif", fontSize: 12,
                      outline: "none", resize: "vertical", boxSizing: "border-box",
                    }} />
                  </div>

                  {/* Image or Video */}
                  <div>
                    <label style={{ fontSize: 9, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "1px", display: "block", marginBottom: 4 }}>
                      Image, Video or GIF
                    </label>
                    <div style={{ display: "flex", gap: 6 }}>
                      <input value={thankYouImage} onChange={e => setThankYouImage(e.target.value)} placeholder="https://... image or video URL" style={{
                        flex: 1, padding: "8px 10px", background: COLORS.surface,
                        border: `1px solid ${COLORS.border}`, borderRadius: 6,
                        color: COLORS.text, fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
                        outline: "none", boxSizing: "border-box",
                      }} />
                      <button onClick={() => setThankYouGifSearch(prev => !prev)} style={{
                        padding: "8px 12px", borderRadius: 6,
                        background: thankYouGifSearch ? `${COLORS.cyan}12` : COLORS.surface,
                        border: `1px solid ${thankYouGifSearch ? COLORS.cyan + "30" : COLORS.border}`,
                        color: thankYouGifSearch ? COLORS.cyan : COLORS.textMuted,
                        fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
                        cursor: "pointer", display: "flex", alignItems: "center", gap: 4,
                      }}>🎞️ GIF</button>
                      <label style={{
                        padding: "8px 12px", borderRadius: 6,
                        background: COLORS.surface, border: `1px solid ${COLORS.border}`,
                        color: COLORS.textMuted, fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
                        cursor: "pointer", display: "flex", alignItems: "center", gap: 4,
                      }}>
                        📁 Upload
                        <input type="file" accept="image/*,video/*" style={{ display: "none" }} onChange={e => {
                          const file = e.target.files?.[0];
                          if (file) {
                            const reader = new FileReader();
                            reader.onload = (ev) => setThankYouImage(ev.target.result);
                            reader.readAsDataURL(file);
                          }
                        }} />
                      </label>
                    </div>

                    {/* Inline GIF search */}
                    {thankYouGifSearch && (
                      <div style={{
                        marginTop: 8, padding: 10, borderRadius: 8,
                        background: COLORS.surface, border: `1px solid ${COLORS.cyan}20`,
                        animation: "slideDown 0.2s ease-out",
                      }}>
                        <input
                          value={thankYouGifQuery}
                          onChange={e => {
                            setThankYouGifQuery(e.target.value);
                            clearTimeout(window.__tyGifTimer);
                            window.__tyGifTimer = setTimeout(async () => {
                              if (!e.target.value.trim()) { setThankYouGifResults([]); return; }
                              setThankYouGifResults(await klipyGifSearch(e.target.value, 9));
                            }, 400);
                          }}
                          placeholder="Search KLIPY"
                          style={{
                            width: "100%", padding: "8px 10px", background: COLORS.bgAlt,
                            border: `1px solid ${COLORS.border}`, borderRadius: 6,
                            color: COLORS.text, fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
                            outline: "none", boxSizing: "border-box", marginBottom: 8,
                          }}
                        />
                        {thankYouGifResults.length > 0 ? (
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 4 }}>
                            {thankYouGifResults.map(gif => (
                              <img key={gif.id} src={gif.preview} alt="" onClick={() => {
                                setThankYouImage(gif.full);
                                setThankYouGifSearch(false);
                                setThankYouGifQuery("");
                                setThankYouGifResults([]);
                              }} style={{
                                width: "100%", height: 90, objectFit: "cover", borderRadius: 6,
                                cursor: "pointer", border: "2px solid transparent",
                              }}
                              onMouseEnter={e => e.currentTarget.style.borderColor = COLORS.accent}
                              onMouseLeave={e => e.currentTarget.style.borderColor = "transparent"}
                              />
                            ))}
                          </div>
                        ) : (
                          <div style={{ textAlign: "center", fontSize: 9, color: COLORS.textMuted, padding: 10 }}>
                            Type to search GIFs
                          </div>
                        )}
                        <div style={{ textAlign: "center", fontSize: 7, color: COLORS.textMuted, opacity: 0.5, marginTop: 4 }}>Powered by KLIPY</div>
                      </div>
                    )}
                  </div>

                  {/* Preview */}
                  <div>
                    <label style={{ fontSize: 9, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "1px", display: "block", marginBottom: 6 }}>
                      Preview
                    </label>
                    <div style={{
                      padding: 16, borderRadius: 10,
                      background: `linear-gradient(135deg, ${COLORS.accent}08, ${COLORS.cyan}08)`,
                      border: `1px solid ${COLORS.accent}20`,
                      textAlign: "center",
                      position: "relative",
                    }}>
                      {thankYouImage && (() => {
                        // Detect YouTube
                        const ytMatch = thankYouImage.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
                        const isVideo = thankYouImage.includes("video") || thankYouImage.match(/\.(mp4|webm|mov)($|\?)/);
                        return (
                        <div style={{ position: "relative", display: "inline-block", marginBottom: 10 }}>
                          {ytMatch ? (
                            <iframe
                              src={`https://www.youtube.com/embed/${ytMatch[1]}`}
                              style={{ width: "100%", maxWidth: 320, height: 180, borderRadius: 8, border: "none" }}
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                            />
                          ) : isVideo ? (
                            <video src={thankYouImage} controls style={{
                              maxWidth: "100%", maxHeight: 140, borderRadius: 8,
                            }} />
                          ) : (
                            <img src={thankYouImage} alt="" style={{
                              maxWidth: "100%", maxHeight: 140, borderRadius: 8,
                              objectFit: "cover",
                            }} />
                          )}
                          <button onClick={() => setThankYouImage("")} style={{
                            position: "absolute", top: -6, right: -6,
                            width: 20, height: 20, borderRadius: "50%",
                            background: COLORS.red, border: "none",
                            color: "#fff", fontSize: 11, fontWeight: 700,
                            cursor: "pointer", display: "flex",
                            alignItems: "center", justifyContent: "center",
                            boxShadow: `0 2px 6px ${COLORS.red}40`,
                          }}>×</button>
                        </div>
                        );
                      })()}
                      <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.accent, marginBottom: 4 }}>
                        {thankYouMessage || "Thank you! 💚"}
                      </div>
                      <div style={{ fontSize: 10, color: COLORS.textMuted }}>
                        — {channelName}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* -- OVERLAY SECTION -- */}
      {activeSection === "overlay-settings" && (
        <div style={{ animation: "fadeInUp 0.3s ease-out" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {/* Overlay URL */}
            <div>
              <label style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "1.5px", display: "block", marginBottom: 6 }}>
                Overlay URL
              </label>
              <div style={{
                display: "flex", alignItems: "center", gap: 8,
                padding: "10px 14px", borderRadius: 8,
                background: COLORS.surface, border: `1px solid ${COLORS.border}`,
              }}>
                <span style={{ flex: 1, fontSize: 11, fontFamily: "'JetBrains Mono', monospace", color: COLORS.cyan }}>
                  {overlayUrl}
                </span>
                <CopyButton text={overlayUrl} />
              </div>
              <div style={{ fontSize: 9, color: COLORS.textMuted, marginTop: 4 }}>
                Add this as a Browser Source in OBS, Streamlabs, XSplit, or any streaming app. Set to 1920×1080.
              </div>
            </div>

            {/* Setup steps */}
            <div style={{
              padding: 14, borderRadius: 10,
              background: `${COLORS.cyan}05`,
              border: `1px solid ${COLORS.cyan}12`,
            }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.cyan, marginBottom: 8 }}>Quick Setup</div>
              {[
                "Open your streaming software (OBS, Streamlabs, etc.)",
                "Add a new Browser Source to your scene",
                "Paste the overlay URL above",
                "Set width to 1920 and height to 1080",
                "Done — alerts, polls, and chat will appear on stream",
              ].map((step, i) => (
                <div key={i} style={{
                  display: "flex", gap: 8, marginBottom: 4,
                  fontSize: 10, color: COLORS.textSecondary,
                }}>
                  <span style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 9, fontWeight: 700, color: COLORS.cyan,
                    width: 16, flexShrink: 0,
                  }}>{i + 1}.</span>
                  {step}
                </div>
              ))}
            </div>

            {/* Chat Name Color */}
            <div>
              <label style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "1.5px", display: "block", marginBottom: 6 }}>
                Chat Name Color
              </label>
              <div style={{ fontSize: 9, color: COLORS.textMuted, marginBottom: 8 }}>
                Chat overlay has no background — pick a name color visible on your stream.
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {[
                  { label: "Blue", color: "#3ea6ff" },
                  { label: "White", color: "#ffffff" },
                  { label: "Green", color: "#00ff88" },
                  { label: "Yellow", color: "#fbbf24" },
                  { label: "Pink", color: "#ff6b9d" },
                  { label: "Cyan", color: "#00e5ff" },
                ].map(c => {
                  const current = window.__fullsend_settings_data?.chatNameColor || "#3ea6ff";
                  const isActive = current === c.color;
                  return (
                  <button key={c.color} onClick={() => {
                    if (window.__fullsend_settings_data) window.__fullsend_settings_data.chatNameColor = c.color;
                    try { const s = JSON.parse(localStorage.getItem("fullsend_settings") || "{}"); s.chatNameColor = c.color; localStorage.setItem("fullsend_settings", JSON.stringify(s)); } catch {}
                  }} style={{
                    padding: "6px 12px", borderRadius: 6,
                    background: isActive ? `${c.color}15` : COLORS.surface,
                    border: `1px solid ${isActive ? c.color + "40" : COLORS.border}`,
                    color: c.color, fontSize: 10, fontWeight: 600, cursor: "pointer",
                    fontFamily: "'JetBrains Mono', monospace",
                    boxShadow: isActive ? `0 0 8px ${c.color}20` : "none",
                  }}>{c.label}</button>
                  );
                })}
              </div>
            </div>

            {/* Compatibility note */}
            <div style={{
              fontSize: 9, color: COLORS.textMuted, lineHeight: 1.6,
              padding: "8px 12px", borderRadius: 6,
              background: COLORS.surface, border: `1px solid ${COLORS.border}`,
            }}>
              <span style={{ fontWeight: 700, color: COLORS.textSecondary }}>Works with: </span>
              OBS Studio, Streamlabs, StreamElements, XSplit, Prism Live, Lightstream, Restream Studio, Ecamm Live, vMix — any app with browser source support
            </div>
          </div>
        </div>
      )}

      {/* -- SOUNDS SECTION -- */}
      {activeSection === "sounds" && (
        <div style={{ animation: "fadeInUp 0.3s ease-out" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {[
              { key: "micro", label: "⚡ Micro Tips", tier: "micro" },
              { key: "supporter", label: "💚 Supporter", tier: "supporter" },
              { key: "champion", label: "💪 Champion", tier: "champion" },
              { key: "legend", label: "💎 Legend", tier: "legend" },
              { key: "whale", label: "🐋 Whale", tier: "whale" },
              { key: "follow", label: "👤 Follow Alert", tier: "follow" },
            ].map(tier => (
              <div key={tier.key} style={{
                padding: "10px 14px", borderRadius: 8,
                background: COLORS.surface, border: `1px solid ${COLORS.border}`,
              }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.text, marginBottom: 6 }}>{tier.label}</div>
                <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                  {stockSounds[tier.key].map(sound => {
                    const isSelected = selectedSound[tier.key] === sound;
                    const isPlaying = playingSound === `${tier.key}-${sound}`;
                    return (
                      <button key={sound} onClick={() => { setSelectedSound(prev => ({ ...prev, [tier.key]: sound })); if (sound !== "none") previewSound(tier.key, sound); }} style={{
                        padding: "5px 10px", borderRadius: 5,
                        background: isPlaying ? `${COLORS.accent}22` : isSelected ? (sound === "none" ? `${COLORS.red}08` : `${COLORS.accent}12`) : COLORS.bgAlt,
                        border: `1px solid ${isPlaying ? COLORS.accent + "40" : isSelected ? (sound === "none" ? COLORS.red + "20" : COLORS.accent + "25") : COLORS.border}`,
                        color: isSelected ? (sound === "none" ? COLORS.red : COLORS.accent) : COLORS.textMuted,
                        fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 600,
                        cursor: "pointer", transition: "all 0.15s",
                        display: "flex", alignItems: "center", gap: 4,
                      }}>
                        {isPlaying && <span style={{ fontSize: 8, animation: "pulse 0.5s infinite" }}>🔊</span>}
                        {sound === "none" ? "🔇 off" : sound}
                      </button>
                    );
                  })}
                  {/* Custom sound option */}
                  {customSounds[tier.key] && (() => {
                    const isSelected = selectedSound[tier.key] === "custom";
                    const isPlaying = playingSound === `${tier.key}-custom`;
                    return (
                      <button onClick={() => {
                        setSelectedSound(prev => ({ ...prev, [tier.key]: "custom" }));
                        try { const audio = new Audio(customSounds[tier.key]); audio.volume = masterVolume; audio.play(); setPlayingSound(`${tier.key}-custom`); setTimeout(() => setPlayingSound(null), 2000); } catch {}
                      }} style={{
                        padding: "5px 10px", borderRadius: 5,
                        background: isPlaying ? `${COLORS.purple}22` : isSelected ? `${COLORS.purple}12` : COLORS.bgAlt,
                        border: `1px solid ${isPlaying ? COLORS.purple + "40" : isSelected ? COLORS.purple + "25" : COLORS.border}`,
                        color: isSelected ? COLORS.purple : COLORS.textMuted,
                        fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 600,
                        cursor: "pointer", transition: "all 0.15s",
                        display: "flex", alignItems: "center", gap: 4,
                      }}>
                        {isPlaying && <span style={{ fontSize: 8, animation: "pulse 0.5s infinite" }}>🔊</span>}
                        🎵 custom
                        <span onClick={(e) => { e.stopPropagation(); setCustomSounds(prev => { const n = { ...prev }; delete n[tier.key]; return n; }); if (selectedSound[tier.key] === "custom") setSelectedSound(prev => ({ ...prev, [tier.key]: "none" })); }} style={{ fontSize: 10, color: COLORS.red, cursor: "pointer", marginLeft: 2 }}>×</span>
                      </button>
                    );
                  })()}
                  {/* Upload button */}
                  <button onClick={() => {
                    const input = document.createElement("input");
                    input.type = "file";
                    input.accept = "audio/*";
                    input.onchange = (e) => {
                      const file = e.target.files?.[0];
                      if (!file || file.size > 2 * 1024 * 1024) { alert("Max 2MB audio file"); return; }
                      const reader = new FileReader();
                      reader.onload = () => {
                        setCustomSounds(prev => ({ ...prev, [tier.key]: reader.result }));
                        setSelectedSound(prev => ({ ...prev, [tier.key]: "custom" }));
                        // Preview it
                        try { const audio = new Audio(reader.result); audio.volume = masterVolume; audio.play(); } catch {}
                      };
                      reader.readAsDataURL(file);
                    };
                    input.click();
                  }} style={{
                    padding: "5px 10px", borderRadius: 5,
                    background: COLORS.bgAlt,
                    border: `1px dashed ${COLORS.border}`,
                    color: COLORS.textMuted,
                    fontFamily: "'JetBrains Mono', monospace", fontSize: 9,
                    cursor: "pointer",
                  }}>+ Upload</button>
                </div>
              </div>
            ))}
          </div>

          {/* Master Volume */}
          <div style={{
            marginTop: 16, padding: 16, borderRadius: 10,
            background: COLORS.bgAlt,
            border: `1px solid ${COLORS.border}`,
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text }}>🔊 Master Volume</div>
              <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.accent, fontFamily: "'JetBrains Mono', monospace" }}>
                {Math.round(masterVolume * 100)}%
              </div>
            </div>
            <input type="range" min="0" max="1" step="0.05" value={masterVolume} onChange={e => setMasterVolume(parseFloat(e.target.value))} style={{ width: "100%", accentColor: COLORS.accent }} />
            <div style={{ fontSize: 9, color: COLORS.textMuted, marginTop: 4 }}>
              Controls volume for all alert sounds and TTS
            </div>
          </div>

          {/* TTS - Text-to-Speech */}
          <div style={{
            marginTop: 16, padding: 16, borderRadius: 10,
            background: COLORS.bgAlt,
            border: `1px solid ${ttsEnabled ? COLORS.accent + "20" : COLORS.border}`,
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: ttsEnabled ? COLORS.accent : COLORS.text }}>🗣️ Text-to-Speech</div>
                <div style={{ fontSize: 10, color: COLORS.textMuted, marginTop: 2 }}>AI voice reads tip messages on stream</div>
              </div>
              <div onClick={() => setTtsEnabled(!ttsEnabled)} style={{
                width: 38, height: 20, borderRadius: 10,
                background: ttsEnabled ? `${COLORS.accent}40` : `${COLORS.bg}80`,
                border: `1px solid ${ttsEnabled ? COLORS.accent + "50" : COLORS.border}`,
                position: "relative", cursor: "pointer", transition: "all 0.25s",
              }}>
                <div style={{
                  width: 14, height: 14, borderRadius: "50%",
                  background: ttsEnabled ? COLORS.accent : COLORS.textMuted,
                  position: "absolute", top: 2, left: ttsEnabled ? 20 : 2,
                  transition: "all 0.25s",
                  boxShadow: ttsEnabled ? `0 0 6px ${COLORS.accent}50` : "none",
                }} />
              </div>
            </div>

            {ttsEnabled && (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {/* Voice selector */}
                <div>
                  <label style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "1.5px", display: "block", marginBottom: 4 }}>Voice</label>
                  <select value={ttsVoice} onChange={e => setTtsVoice(e.target.value)} style={{
                    width: "100%", padding: "8px 10px", background: COLORS.surface, border: `1px solid ${COLORS.border}`,
                    borderRadius: 6, color: COLORS.text, fontFamily: "'JetBrains Mono', monospace", fontSize: 11, outline: "none",
                  }}>
                    {availableVoices.map((v, i) => (
                      <option key={i} value={v.name}>{v.name} ({v.lang})</option>
                    ))}
                  </select>
                </div>

                {/* Per-tier TTS toggles */}
                <div>
                  <label style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "1.5px", display: "block", marginBottom: 6 }}>TTS Triggers By Tier</label>
                  <div style={{ display: "flex", gap: 4 }}>
                    {[
                      { key: "micro", label: "Micro", emoji: "⚡", color: COLORS.orange },
                      { key: "green", label: "Supporter", emoji: "💚", color: "#00cc66" },
                      { key: "blue", label: "Champion", emoji: "💪", color: "#4a9eff" },
                      { key: "purple", label: "Legend", emoji: "💎", color: "#b44aff" },
                      { key: "fire", label: "Whale", emoji: "🐋", color: "#ffd700" },
                    ].map(t => (
                      <button key={t.key} onClick={() => setTtsTiers(prev => ({ ...prev, [t.key]: !prev[t.key] }))} style={{
                        flex: 1, padding: "6px 4px", borderRadius: 6,
                        background: ttsTiers[t.key] ? `${t.color}15` : COLORS.surface,
                        border: `1px solid ${ttsTiers[t.key] ? t.color + "30" : COLORS.border}`,
                        color: ttsTiers[t.key] ? t.color : COLORS.textMuted,
                        fontFamily: "'JetBrains Mono', monospace", fontSize: 8, fontWeight: 600, cursor: "pointer",
                        display: "flex", flexDirection: "column", alignItems: "center", gap: 2,
                        opacity: ttsTiers[t.key] ? 1 : 0.5,
                        transition: "all 0.15s",
                      }}>
                        <span style={{ fontSize: 14 }}>{t.emoji}</span>
                        {t.label}
                      </button>
                    ))}
                  </div>
                  <div style={{ fontSize: 8, color: COLORS.textMuted, marginTop: 4, textAlign: "center" }}>
                    Tap to toggle — TTS only fires for enabled tiers
                  </div>
                </div>

                {/* Speed */}
                <div>
                  <label style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "1.5px", display: "block", marginBottom: 4 }}>
                    Speed: {ttsRate.toFixed(1)}x
                  </label>
                  <input type="range" min="0.5" max="2" step="0.1" value={ttsRate} onChange={e => setTtsRate(parseFloat(e.target.value))} style={{ width: "100%", accentColor: COLORS.accent }} />
                </div>

                {/* Pitch */}
                <div>
                  <label style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "1.5px", display: "block", marginBottom: 4 }}>
                    Pitch: {ttsPitch.toFixed(1)}
                  </label>
                  <input type="range" min="0.5" max="2" step="0.1" value={ttsPitch} onChange={e => setTtsPitch(parseFloat(e.target.value))} style={{ width: "100%", accentColor: COLORS.accent }} />
                </div>

                {/* Test button */}
                <button onClick={() => {
                  if (!window.speechSynthesis) return;
                  window.speechSynthesis.cancel();
                  const utterance = new SpeechSynthesisUtterance("Dracos tipped 25 LYX: LUKSO is the future!");
                  const voice = availableVoices.find(v => v.name === ttsVoice);
                  if (voice) utterance.voice = voice;
                  utterance.rate = ttsRate;
                  utterance.pitch = ttsPitch;
                  utterance.volume = masterVolume;
                  window.speechSynthesis.speak(utterance);
                }} style={{
                  padding: "10px", borderRadius: 8,
                  background: `${COLORS.accent}10`,
                  border: `1px solid ${COLORS.accent}20`,
                  color: COLORS.accent, fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 11, fontWeight: 700, cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                }}>
                  🔊 Test Voice
                </button>

                <div style={{ fontSize: 9, color: COLORS.textMuted, lineHeight: 1.5 }}>
                  When enabled, tips at or above the minimum amount will have their message read aloud on stream. Format: "[name] tipped [amount] LYX: [message]"
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {activeSection === "moderation" && (
        <div style={{ animation: "fadeInUp 0.3s ease-out" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {/* Blocked Profiles */}
            <div>
              <label style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "1.5px", display: "block", marginBottom: 6 }}>
                Blocked Profiles ({blockedProfiles.length})
              </label>
              <input
                value={blockSearch} onChange={e => setBlockSearch(e.target.value)}
                placeholder="Search by name or address..."
                style={{
                  width: "100%", padding: "8px 12px",
                  background: COLORS.surface, border: `1px solid ${COLORS.border}`,
                  borderRadius: 6, color: COLORS.text,
                  fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
                  outline: "none", boxSizing: "border-box", marginBottom: 8,
                }}
              />
              {blockedProfiles.length === 0 ? (
                <div style={{
                  textAlign: "center", padding: "20px 0",
                  fontSize: 11, color: COLORS.textMuted,
                }}>No blocked profiles</div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {blockedProfiles
                    .filter(p => !blockSearch || p.name.toLowerCase().includes(blockSearch.toLowerCase()) || p.address.includes(blockSearch))
                    .map((profile, i) => (
                    <div key={i} style={{
                      display: "flex", alignItems: "center", gap: 10,
                      padding: "8px 12px", borderRadius: 7,
                      background: COLORS.surface, border: `1px solid ${COLORS.border}`,
                    }}>
                      <span style={{ fontSize: 16 }}>{profile.avatar}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.text }}>{profile.name}</div>
                        <div style={{ fontSize: 8, color: COLORS.textMuted, fontFamily: "'JetBrains Mono', monospace" }}>{profile.address} · blocked {profile.blockedAt}</div>
                      </div>
                      <button onClick={() => setBlockedProfiles(prev => prev.filter((_, idx) => idx !== i))} style={{
                        padding: "5px 10px", borderRadius: 5,
                        background: `${COLORS.red}08`, border: `1px solid ${COLORS.red}15`,
                        color: COLORS.red, fontSize: 9, fontWeight: 700,
                        fontFamily: "'JetBrains Mono', monospace", cursor: "pointer",
                      }}>Unblock</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Word filter */}
            <div>
              <label style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "1.5px", display: "block", marginBottom: 6 }}>
                Word Filter
              </label>
              <div style={{ fontSize: 9, color: COLORS.textMuted, marginBottom: 6 }}>
                Tip messages containing these words will be auto-rejected
              </div>
              <textarea
                placeholder="Enter words separated by commas..."
                rows={2}
                style={{
                  width: "100%", padding: "8px 12px",
                  background: COLORS.surface, border: `1px solid ${COLORS.border}`,
                  borderRadius: 6, color: COLORS.text,
                  fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
                  outline: "none", boxSizing: "border-box", resize: "vertical",
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* -- LINKS & EMBED SECTION -- */}
      {activeSection === "links" && (
        <div style={{ animation: "fadeInUp 0.3s ease-out" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {/* Tip Page */}
            <div>
              <label style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "1.5px", display: "block", marginBottom: 6 }}>
                Tip Page URL
              </label>
              <div style={{
                display: "flex", alignItems: "center", gap: 8,
                padding: "10px 14px", borderRadius: 8,
                background: COLORS.surface, border: `1px solid ${COLORS.border}`,
              }}>
                <span style={{ flex: 1, fontSize: 11, fontFamily: "'JetBrains Mono', monospace", color: COLORS.accent }}>
                  {tipPageUrl}
                </span>
                <CopyButton text={tipPageUrl} color={COLORS.accent} />
              </div>
              <div style={{ fontSize: 9, color: COLORS.textMuted, marginTop: 4 }}>
                Share this link anywhere — YouTube description, Linktree, social bios
              </div>
            </div>

            {/* Embed Code */}
            <div>
              <label style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "1.5px", display: "block", marginBottom: 6 }}>
                Embed Code
              </label>
              <div style={{
                padding: "10px 14px", borderRadius: 8,
                background: COLORS.surface, border: `1px solid ${COLORS.border}`,
                fontFamily: "'JetBrains Mono', monospace", fontSize: 9,
                color: COLORS.purple, lineHeight: 1.6, wordBreak: "break-all",
              }}>
                {`<iframe src="${tipPageUrl}" width="400" height="600" frameborder="0"></iframe>`}
              </div>
              <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                <CopyButton text={`<iframe src="${tipPageUrl}" width="400" height="600" frameborder="0"></iframe>`} label="Copy Embed" color={COLORS.purple} />
              </div>
            </div>

            {/* QR Codes */}
            <div>
              <label style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "1.5px", display: "block", marginBottom: 10 }}>
                QR Codes
              </label>
              <div style={{ fontSize: 9, color: COLORS.textMuted, marginBottom: 10 }}>
                Toggle any of these on stream from the Control page. Download for merch, overlays, or IRL events.
              </div>

              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                {/* Tip Page QR */}
                <div style={{ width: 140 }}>
                  <div style={{ fontSize: 9, fontWeight: 700, color: COLORS.accent, marginBottom: 6 }}>💰 Tip Page</div>
                  {window.__fullsend_profile?.address ? (
                    <>
                      <img
                        src={`https://api.qrserver.com/v1/create-qr-code/?size=300x300&bgcolor=06060b&color=00ff88&data=${encodeURIComponent(`https://fullsend.live/tip/${window.__fullsend_profile.address}`)}`}
                        alt="Tip QR"
                        style={{ width: 120, height: 120, borderRadius: 8, border: `1px solid ${COLORS.accent}20`, display: "block" }}
                      />
                      <div style={{ display: "flex", gap: 4, marginTop: 4 }}>
                        <a href={`https://api.qrserver.com/v1/create-qr-code/?size=600x600&bgcolor=06060b&color=00ff88&format=png&data=${encodeURIComponent(`https://fullsend.live/tip/${window.__fullsend_profile.address}`)}`} download="fullsend-tip-qr.png" target="_blank" rel="noopener" style={{ padding: "3px 6px", borderRadius: 4, background: `${COLORS.accent}10`, border: `1px solid ${COLORS.accent}20`, color: COLORS.accent, fontSize: 8, fontWeight: 600, textDecoration: "none", fontFamily: "'JetBrains Mono', monospace" }}>⬇ PNG</a>
                        <a href={`https://api.qrserver.com/v1/create-qr-code/?size=600x600&bgcolor=ffffff&color=000000&format=png&data=${encodeURIComponent(`https://fullsend.live/tip/${window.__fullsend_profile.address}`)}`} download="fullsend-tip-qr-white.png" target="_blank" rel="noopener" style={{ padding: "3px 6px", borderRadius: 4, background: COLORS.surface, border: `1px solid ${COLORS.border}`, color: COLORS.textMuted, fontSize: 8, fontWeight: 600, textDecoration: "none", fontFamily: "'JetBrains Mono', monospace" }}>⬇ White</a>
                      </div>
                    </>
                  ) : (
                    <div style={{ width: 120, height: 120, borderRadius: 8, background: COLORS.surface, border: `1px solid ${COLORS.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 8, color: COLORS.textMuted, textAlign: "center", padding: 8 }}>Connect UP</div>
                  )}
                </div>

                {/* UP Profile QR */}
                <div style={{ width: 140 }}>
                  <div style={{ fontSize: 9, fontWeight: 700, color: COLORS.purple, marginBottom: 6 }}>👤 UP Profile</div>
                  {window.__fullsend_profile?.address ? (
                    <>
                      <img
                        src={`https://api.qrserver.com/v1/create-qr-code/?size=300x300&bgcolor=06060b&color=c084fc&data=${encodeURIComponent(`https://universalprofile.cloud/${window.__fullsend_profile.address}`)}`}
                        alt="UP QR"
                        style={{ width: 120, height: 120, borderRadius: 8, border: `1px solid ${COLORS.purple}20`, display: "block" }}
                      />
                      <div style={{ display: "flex", gap: 4, marginTop: 4 }}>
                        <a href={`https://api.qrserver.com/v1/create-qr-code/?size=600x600&bgcolor=06060b&color=c084fc&format=png&data=${encodeURIComponent(`https://universalprofile.cloud/${window.__fullsend_profile.address}`)}`} download="fullsend-up-qr.png" target="_blank" rel="noopener" style={{ padding: "3px 6px", borderRadius: 4, background: `${COLORS.purple}10`, border: `1px solid ${COLORS.purple}20`, color: COLORS.purple, fontSize: 8, fontWeight: 600, textDecoration: "none", fontFamily: "'JetBrains Mono', monospace" }}>⬇ PNG</a>
                        <a href={`https://api.qrserver.com/v1/create-qr-code/?size=600x600&bgcolor=ffffff&color=000000&format=png&data=${encodeURIComponent(`https://universalprofile.cloud/${window.__fullsend_profile.address}`)}`} download="fullsend-up-qr-white.png" target="_blank" rel="noopener" style={{ padding: "3px 6px", borderRadius: 4, background: COLORS.surface, border: `1px solid ${COLORS.border}`, color: COLORS.textMuted, fontSize: 8, fontWeight: 600, textDecoration: "none", fontFamily: "'JetBrains Mono', monospace" }}>⬇ White</a>
                      </div>
                    </>
                  ) : (
                    <div style={{ width: 120, height: 120, borderRadius: 8, background: COLORS.surface, border: `1px solid ${COLORS.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 8, color: COLORS.textMuted, textAlign: "center", padding: 8 }}>Connect UP</div>
                  )}
                </div>

                {/* Custom URL QR */}
                <div style={{ width: 140 }}>
                  <div style={{ fontSize: 9, fontWeight: 700, color: COLORS.cyan, marginBottom: 6 }}>🔗 Custom URL</div>
                  {customQrUrl ? (
                    <>
                      <img
                        src={`https://api.qrserver.com/v1/create-qr-code/?size=300x300&bgcolor=06060b&color=22d3ee&data=${encodeURIComponent(customQrUrl)}`}
                        alt="Custom QR"
                        style={{ width: 120, height: 120, borderRadius: 8, border: `1px solid ${COLORS.cyan}20`, display: "block" }}
                      />
                      <div style={{ display: "flex", gap: 4, marginTop: 4 }}>
                        <a href={`https://api.qrserver.com/v1/create-qr-code/?size=600x600&bgcolor=06060b&color=22d3ee&format=png&data=${encodeURIComponent(customQrUrl)}`} download="fullsend-custom-qr.png" target="_blank" rel="noopener" style={{ padding: "3px 6px", borderRadius: 4, background: `${COLORS.cyan}10`, border: `1px solid ${COLORS.cyan}20`, color: COLORS.cyan, fontSize: 8, fontWeight: 600, textDecoration: "none", fontFamily: "'JetBrains Mono', monospace" }}>⬇ PNG</a>
                        <button onClick={() => setCustomQrUrl("")} style={{ padding: "3px 6px", borderRadius: 4, background: `${COLORS.red}10`, border: `1px solid ${COLORS.red}20`, color: COLORS.red, fontSize: 8, fontWeight: 600, cursor: "pointer" }}>✕</button>
                      </div>
                    </>
                  ) : (
                    <div>
                      <input
                        value={customQrUrl}
                        onChange={e => setCustomQrUrl(e.target.value)}
                        placeholder="Enter any URL..."
                        style={{ width: 120, padding: "6px 8px", background: COLORS.bgAlt, border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.text, fontFamily: "'JetBrains Mono', monospace", fontSize: 8, outline: "none", boxSizing: "border-box" }}
                      />
                      <div style={{ fontSize: 7, color: COLORS.textMuted, marginTop: 3 }}>Paste a URL to generate</div>
                    </div>
                  )}
                </div>

                {/* Uploaded QR */}
                <div style={{ width: 140 }}>
                  <div style={{ fontSize: 9, fontWeight: 700, color: COLORS.gold, marginBottom: 6 }}>📁 Uploaded</div>
                  {uploadedQrImage ? (
                    <>
                      <img
                        src={uploadedQrImage}
                        alt="Uploaded QR"
                        style={{ width: 120, height: 120, borderRadius: 8, border: `1px solid ${COLORS.gold}20`, display: "block", objectFit: "contain", background: COLORS.surface }}
                      />
                      <div style={{ display: "flex", gap: 4, marginTop: 4 }}>
                        <button onClick={() => setUploadedQrImage("")} style={{ padding: "3px 6px", borderRadius: 4, background: `${COLORS.red}10`, border: `1px solid ${COLORS.red}20`, color: COLORS.red, fontSize: 8, fontWeight: 600, cursor: "pointer" }}>✕ Remove</button>
                      </div>
                    </>
                  ) : (
                    <label style={{
                      width: 120, height: 120, borderRadius: 8,
                      background: COLORS.surface, border: `1px dashed ${COLORS.gold}25`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 8, color: COLORS.textMuted, textAlign: "center", padding: 8,
                      cursor: "pointer",
                    }}>
                      <div>
                        <div style={{ fontSize: 20, marginBottom: 4 }}>📁</div>
                        Upload QR image
                      </div>
                      <input type="file" accept="image/*" style={{ display: "none" }} onChange={e => {
                        const file = e.target.files?.[0];
                        if (file) {
                          const reader = new FileReader();
                          reader.onload = () => setUploadedQrImage(reader.result);
                          reader.readAsDataURL(file);
                        }
                      }} />
                    </label>
                  )}
                </div>
              </div>
            </div>

            {/* Referral Link */}
            <div>
              <label style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "1.5px", display: "block", marginBottom: 6 }}>
                Referral Link
              </label>
              <div style={{
                display: "flex", alignItems: "center", gap: 8, padding: "10px 14px",
                background: COLORS.surface, border: `1px solid ${COLORS.purple}15`,
                borderRadius: 8,
              }}>
                <span style={{
                  flex: 1, fontSize: 12, fontFamily: "'JetBrains Mono', monospace",
                  color: COLORS.purple, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>https://fullsend.gg/ref/alts29d7</span>
                <button onClick={() => navigator.clipboard?.writeText?.("https://fullsend.gg/ref/alts29d7")} style={{
                  padding: "6px 14px", borderRadius: 5,
                  background: `${COLORS.purple}12`, border: `1px solid ${COLORS.purple}20`,
                  color: COLORS.purple, fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 10, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap",
                }}>Copy</button>
              </div>
              <div style={{ fontSize: 9, color: COLORS.textMuted, marginTop: 4 }}>
                Share with other creators. When they sign up through your link, both of you get highlighted on the platform.
              </div>
              {/* Referral stats — shows count, populates with actual referrals */}
              {(() => {
                const referrals = window.__fullsend_referrals || [];
                return (
                  <div style={{
                    marginTop: 10, padding: "10px 12px", borderRadius: 8,
                    background: `${COLORS.purple}05`, border: `1px solid ${COLORS.purple}10`,
                  }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: referrals.length > 0 ? 8 : 0 }}>
                      <span style={{ fontSize: 10, fontWeight: 700, color: COLORS.purple }}>Your Referrals</span>
                      <span style={{ fontSize: 10, fontWeight: 700, color: COLORS.purple, fontFamily: "'JetBrains Mono', monospace" }}>{referrals.length} creator{referrals.length !== 1 ? "s" : ""}</span>
                    </div>
                    {referrals.map((ref, i) => (
                      <div key={i} style={{
                        display: "flex", alignItems: "center", gap: 8,
                        padding: "4px 0",
                        borderTop: i > 0 ? `1px solid ${COLORS.border}` : "none",
                      }}>
                        <img src={ref.avatar || STOCK_AVATAR} alt="" style={{ width: 20, height: 20, borderRadius: 6 }} />
                        <span style={{ fontSize: 10, fontWeight: 600, flex: 1 }}>{ref.name}</span>
                        <span style={{ fontSize: 8, color: COLORS.textMuted }}>{ref.date}</span>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* -- WEBHOOK SECTION -- */}
      {activeSection === "webhook" && (
        <div style={{ animation: "fadeInUp 0.3s ease-out" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ fontSize: 11, color: COLORS.textMuted, lineHeight: 1.6 }}>
              FullSend fires an HTTP POST to your webhook URL on every event. Use it to build Discord bots, custom alerts, logging, Zapier integrations, and more.
            </div>

            {/* Webhook URL */}
            <div>
              <label style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "1.5px", display: "block", marginBottom: 6 }}>
                Webhook URL
              </label>
              <input value={webhookUrl} onChange={e => setWebhookUrl(e.target.value)} placeholder="https://your-server.com/webhook/fullsend" style={{
                width: "100%", padding: "10px 14px",
                background: COLORS.surface, border: `1px solid ${COLORS.border}`,
                borderRadius: 8, color: COLORS.text,
                fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
                outline: "none", boxSizing: "border-box",
              }} />
            </div>

            {/* Events */}
            <div>
              <label style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "1.5px", display: "block", marginBottom: 8 }}>
                Events
              </label>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {[
                  { event: "tip.received", desc: "When a viewer sends a tip", icon: "💰" },
                  { event: "follow.new", desc: "When a viewer follows your channel", icon: "👤" },
                  { event: "poll.created", desc: "When a new poll is launched", icon: "📊" },
                  { event: "poll.ended", desc: "When a poll closes with results", icon: "🏁" },
                  { event: "nft.minted", desc: "When a milestone NFT is minted for a tipper", icon: "💎" },
                  { event: "goal.reached", desc: "When a tip goal is met", icon: "🎯" },
                ].map((ev, i) => (
                  <div key={i} style={{
                    display: "flex", alignItems: "center", gap: 10,
                    padding: "8px 10px", borderRadius: 6,
                    background: COLORS.surface, border: `1px solid ${COLORS.border}`,
                  }}>
                    <span style={{ fontSize: 14 }}>{ev.icon}</span>
                    <div style={{ flex: 1 }}>
                      <code style={{ fontSize: 10, fontWeight: 700, color: COLORS.cyan }}>{ev.event}</code>
                      <div style={{ fontSize: 9, color: COLORS.textMuted }}>{ev.desc}</div>
                    </div>
                    <div style={{
                      width: 34, height: 18, borderRadius: 9,
                      background: `${COLORS.accent}40`, border: `1px solid ${COLORS.accent}50`,
                      position: "relative", cursor: "pointer",
                    }}>
                      <div style={{ width: 12, height: 12, borderRadius: "50%", background: COLORS.accent, position: "absolute", top: 2, left: 18 }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Test + Secret */}
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => {}} style={{
                padding: "8px 16px", borderRadius: 6,
                background: `${COLORS.cyan}10`, border: `1px solid ${COLORS.cyan}20`,
                color: COLORS.cyan, fontFamily: "'JetBrains Mono', monospace",
                fontSize: 10, fontWeight: 600, cursor: "pointer",
              }}>Send Test Payload</button>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 9, color: COLORS.textMuted, marginBottom: 2 }}>Webhook Secret</div>
                <code style={{ fontSize: 9, color: COLORS.textSecondary, fontFamily: "'JetBrains Mono', monospace" }}>whsec_alts29d7_xK9mP2...</code>
              </div>
            </div>

            {/* Sample payload */}
            <div>
              <label style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "1.5px", display: "block", marginBottom: 6 }}>
                Sample Payload
              </label>
              <div style={{
                padding: "12px", borderRadius: 8,
                background: COLORS.bg, border: `1px solid ${COLORS.border}`,
                fontFamily: "'JetBrains Mono', monospace", fontSize: 9,
                color: COLORS.textSecondary, lineHeight: 1.7, whiteSpace: "pre-wrap",
              }}>
{`{
  "event": "tip.received",
  "data": {
    "from": "0x29d7...b50e",
    "fromName": "Dracos",
    "amount": "25",
    "currency": "LYX",
    "message": "Great stream!",
    "tier": "legend",
    "timestamp": "2026-03-08T..."
  },
  "signature": "0x..."
}`}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Save Button - Sticky */}
      <div style={{
        position: "sticky", bottom: 0, left: 0, right: 0,
        padding: "16px 0 8px",
        background: `linear-gradient(to top, ${COLORS.bg} 60%, transparent)`,
        zIndex: 10,
      }}>
        <button onClick={() => {
          const isEOA = window.__fullsend_profile && !window.__fullsend_profile.isUP;
          if (isEOA) {
            setShowUpGate(true);
            return;
          }
          handleSave();
        }} style={{
          width: "100%", padding: "13px",
          background: saved
            ? `linear-gradient(135deg, ${COLORS.green}, ${COLORS.accent})`
            : `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.cyan})`,
          color: COLORS.bg, border: "none", borderRadius: 10,
          fontFamily: "'Syne', sans-serif",
          fontSize: 14, fontWeight: 700, cursor: "pointer",
          transition: "all 0.3s",
          boxShadow: `0 4px 20px ${COLORS.accentGlow}`,
        }}>
          {saved ? "✓ Saved" : "Save Changes"}
        </button>
      </div>
      {showUpGate && <UpRequiredModal onClose={() => setShowUpGate(false)} action="register as a creator" />}
    </div>
  );
}

// --- BUG REPORT FORM -----------------------------------------
function BugReportForm({ onClose, upProfile, connected }) {
  const [description, setDescription] = useState("");
  const [steps, setSteps] = useState("");
  const [section, setSection] = useState("");
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  const SECTIONS = [
    "Tip Widget", "Stream Overlay", "Polls", "Creator Settings",
    "Creator Analytics", "Control Panel", "Leaderboard", "NFT Gallery",
    "Chat", "Tipper Mode", "Creator Directory", "About Page",
    "Standalone Tip Page", "Onboarding", "Navigation", "Other"
  ];

  const handleFiles = (e) => {
    const newFiles = Array.from(e.target.files || []).slice(0, 3 - files.length);
    const processed = newFiles.map(f => ({
      name: f.name,
      size: (f.size / 1024 / 1024).toFixed(2) + " MB",
      type: f.type,
      file: f,
    }));
    setFiles(prev => [...prev, ...processed].slice(0, 3));
  };

  const removeFile = (idx) => setFiles(prev => prev.filter((_, i) => i !== idx));

  const handleSubmit = async () => {
    if (!description.trim()) return;
    setSending(true);
    setError("");

    try {
      const report = {
        description: description.trim(),
        steps: steps.trim(),
        section: section || "Not specified",
        reporter: connected && upProfile ? {
          name: upProfile.name || "Unknown",
          address: window.__fullsend_profile?.address || "Not connected",
        } : { name: "Anonymous", address: "Not connected" },
        timestamp: new Date().toISOString(),
        url: window.location.href,
        userAgent: navigator.userAgent,
      };

      // Convert files to base64 for sending
      const fileData = [];
      for (const f of files) {
        if (f.file && f.file.size < 8 * 1024 * 1024) {
          const base64 = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.readAsDataURL(f.file);
          });
          fileData.push({ name: f.name, type: f.type, data: base64 });
        }
      }
      report.files = fileData;

      // Save to database
      try {
        await fetch("/api/submissions", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            type: "bug",
            description: report.description,
            steps: report.steps,
            section: report.section,
            reporter_name: report.reporter?.name,
            reporter_address: report.reporter?.address,
            url: window.location.href,
            user_agent: navigator.userAgent,
            files: fileData.slice(0, 3),
          }),
        });
      } catch (_) { /* Silent fail */ }

      setSent(true);
      setTimeout(() => onClose(), 3000);
    } catch (err) {
      console.error("Bug report error:", err);
      setError("Failed to send report. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const inputStyle = {
    width: "100%", padding: "10px 12px",
    background: COLORS.bgAlt, border: `1px solid ${COLORS.border}`,
    borderRadius: 8, color: COLORS.text,
    fontFamily: "'Outfit', sans-serif", fontSize: 12,
    outline: "none", boxSizing: "border-box",
    transition: "border-color 0.2s",
  };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 10000,
      background: "rgba(0,0,0,0.7)",
      backdropFilter: "blur(8px)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 20,
      animation: "fadeIn 0.2s ease-out",
    }} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: COLORS.bg,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 16,
        maxWidth: 480, width: "100%",
        maxHeight: "90vh", overflowY: "auto",
        animation: "fadeInUp 0.3s ease-out",
      }}>
        {/* Header */}
        <div style={{
          padding: "20px 24px 16px",
          borderBottom: `1px solid ${COLORS.border}`,
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div>
            <div style={{
              fontFamily: "'Syne', sans-serif",
              fontSize: 16, fontWeight: 800, color: COLORS.text,
              display: "flex", alignItems: "center", gap: 8,
            }}>
              <span>🐛</span> Report a Bug
            </div>
            <div style={{ fontSize: 10, color: COLORS.textMuted, marginTop: 3 }}>
              Help us squash it — your report goes directly to the team
            </div>
          </div>
          <button onClick={onClose} style={{
            width: 32, height: 32, borderRadius: 8,
            background: COLORS.surface, border: `1px solid ${COLORS.border}`,
            color: COLORS.textMuted, fontSize: 14, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            transition: "all 0.2s",
          }}
          onMouseEnter={e => e.currentTarget.style.color = COLORS.text}
          onMouseLeave={e => e.currentTarget.style.color = COLORS.textMuted}
          >✕</button>
        </div>

        {sent ? (
          <div style={{
            padding: "40px 24px", textAlign: "center",
            animation: "fadeInUp 0.3s ease-out",
          }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>✅</div>
            <div style={{
              fontFamily: "'Syne', sans-serif",
              fontSize: 16, fontWeight: 700, color: COLORS.accent,
            }}>Bug Report Sent!</div>
            <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 6 }}>
              Thanks for helping improve FullSend. We'll look into it.
            </div>
          </div>
        ) : (
          <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 14 }}>
            {/* Reporter info */}
            {connected && upProfile && (
              <div style={{
                padding: "10px 14px", borderRadius: 8,
                background: `${COLORS.accent}06`,
                border: `1px solid ${COLORS.accent}10`,
                display: "flex", alignItems: "center", gap: 10,
              }}>
                <img
                  src={upProfile.avatar || STOCK_AVATAR}
                  alt=""
                  style={{ width: 28, height: 28, borderRadius: 7, objectFit: "cover" }}
                  onError={e => { e.target.src = STOCK_AVATAR; }}
                />
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.text }}>
                    {upProfile.name}
                  </div>
                  <div style={{
                    fontSize: 9, color: COLORS.textMuted,
                    fontFamily: "'JetBrains Mono', monospace",
                  }}>
                    {(window.__fullsend_profile?.address || "").slice(0, 8)}...{(window.__fullsend_profile?.address || "").slice(-6)}
                  </div>
                </div>
                <div style={{
                  marginLeft: "auto", fontSize: 8, color: COLORS.accent,
                  fontFamily: "'JetBrains Mono', monospace", fontWeight: 600,
                }}>REPORTING AS</div>
              </div>
            )}

            {/* Section selector */}
            <div>
              <label style={{ fontSize: 10, fontWeight: 600, color: COLORS.textSecondary, marginBottom: 5, display: "block" }}>
                Where did the bug happen?
              </label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                {SECTIONS.map(s => (
                  <button key={s} onClick={() => setSection(section === s ? "" : s)} style={{
                    padding: "5px 10px", borderRadius: 6,
                    background: section === s ? `${COLORS.red}15` : COLORS.surface,
                    border: `1px solid ${section === s ? COLORS.red + "30" : COLORS.border}`,
                    color: section === s ? COLORS.red : COLORS.textMuted,
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 9, fontWeight: 600, cursor: "pointer",
                    transition: "all 0.15s",
                  }}>{s}</button>
                ))}
              </div>
            </div>

            {/* Description */}
            <div>
              <label style={{ fontSize: 10, fontWeight: 600, color: COLORS.textSecondary, marginBottom: 5, display: "block" }}>
                What happened? <span style={{ color: COLORS.red }}>*</span>
              </label>
              <textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                rows={4}
                placeholder="Describe the bug — what did you expect vs what actually happened?"
                style={{ ...inputStyle, resize: "vertical" }}
                onFocus={e => e.target.style.borderColor = COLORS.red + "40"}
                onBlur={e => e.target.style.borderColor = COLORS.border}
              />
            </div>

            {/* Steps to reproduce */}
            <div>
              <label style={{ fontSize: 10, fontWeight: 600, color: COLORS.textSecondary, marginBottom: 5, display: "block" }}>
                Steps to reproduce <span style={{ color: COLORS.textMuted }}>(optional)</span>
              </label>
              <textarea
                value={steps}
                onChange={e => setSteps(e.target.value)}
                rows={3}
                placeholder={"1. Go to...\n2. Click on...\n3. Notice that..."}
                style={{ ...inputStyle, resize: "vertical" }}
                onFocus={e => e.target.style.borderColor = COLORS.red + "40"}
                onBlur={e => e.target.style.borderColor = COLORS.border}
              />
            </div>

            {/* File upload */}
            <div>
              <label style={{ fontSize: 10, fontWeight: 600, color: COLORS.textSecondary, marginBottom: 5, display: "block" }}>
                Screenshot or recording <span style={{ color: COLORS.textMuted }}>(optional)</span>
              </label>
              <input
                ref={fileRef}
                type="file"
                accept="image/*,video/*"
                multiple
                onChange={handleFiles}
                style={{ display: "none" }}
              />
              <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                {files.length < 3 && (
                  <button onClick={() => fileRef.current?.click()} style={{
                    padding: "8px 14px", borderRadius: 7,
                    background: COLORS.surface,
                    border: `1px dashed ${COLORS.textMuted}30`,
                    color: COLORS.textMuted, fontSize: 10, fontWeight: 600,
                    cursor: "pointer",
                    fontFamily: "'JetBrains Mono', monospace",
                    display: "flex", alignItems: "center", gap: 6,
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = COLORS.red + "40"}
                  onMouseLeave={e => e.currentTarget.style.borderColor = COLORS.textMuted + "30"}
                  >
                    <span style={{ fontSize: 13 }}>📎</span> Attach image or video
                  </button>
                )}
                {files.map((f, i) => (
                  <div key={i} style={{
                    padding: "5px 10px", borderRadius: 6,
                    background: `${COLORS.red}08`,
                    border: `1px solid ${COLORS.red}15`,
                    fontSize: 9, color: COLORS.textSecondary,
                    display: "flex", alignItems: "center", gap: 5,
                  }}>
                    <span>{f.type?.startsWith("video") ? "🎥" : "🖼️"}</span>
                    <span style={{ maxWidth: 100, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</span>
                    <span style={{ color: COLORS.textMuted, fontSize: 8 }}>{f.size}</span>
                    <span onClick={() => removeFile(i)} style={{
                      cursor: "pointer", color: COLORS.red, fontSize: 11,
                      marginLeft: 2, fontWeight: 700,
                    }}>×</span>
                  </div>
                ))}
              </div>
              {files.length > 0 && (
                <div style={{ fontSize: 8, color: COLORS.textMuted, marginTop: 4 }}>{files.length}/3 files · Images & video up to 8MB each</div>
              )}
            </div>

            {/* Error */}
            {error && (
              <div style={{
                padding: "8px 12px", borderRadius: 6,
                background: `${COLORS.red}10`, border: `1px solid ${COLORS.red}20`,
                fontSize: 10, color: COLORS.red,
              }}>{error}</div>
            )}

            {/* Submit */}
            <div style={{ display: "flex", gap: 10, alignItems: "center", paddingTop: 4, paddingBottom: 4 }}>
              <button onClick={handleSubmit} disabled={!description.trim() || sending} style={{
                padding: "12px 24px", borderRadius: 8, border: "none",
                background: description.trim() && !sending
                  ? `linear-gradient(135deg, ${COLORS.red}, ${COLORS.orange})`
                  : COLORS.surface,
                color: description.trim() && !sending ? "#fff" : COLORS.textMuted,
                fontFamily: "'Syne', sans-serif",
                fontSize: 13, fontWeight: 700,
                cursor: description.trim() && !sending ? "pointer" : "not-allowed",
                transition: "all 0.2s",
                display: "flex", alignItems: "center", gap: 6,
              }}>
                {sending ? (
                  <><span style={{ animation: "pulse 1s infinite" }}>⏳</span> Sending...</>
                ) : (
                  <><span>🐛</span> Send Bug Report</>
                )}
              </button>
              <button onClick={onClose} style={{
                padding: "12px 16px", borderRadius: 8,
                background: "transparent", border: `1px solid ${COLORS.border}`,
                color: COLORS.textMuted,
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 11, cursor: "pointer",
              }}>Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// --- ADMIN DASHBOARD -----------------------------------------
function AdminDashboard() {
  const [connected, setConnected] = useState(false);
  const [address, setAddress] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [provider, setProvider] = useState(null);

  // Dashboard data
  const [deployerBalance, setDeployerBalance] = useState(null);   // 0x3c34... contract admin EOA
  const [relayerBalance, setRelayerBalance] = useState(null);     // 0x3C23... pays gas, must stay funded
  const [creatorTreasuryBalance, setCreatorTreasuryBalance] = useState(null); // 0x29d7... assumed fee recipient
  const [contractTreasury, setContractTreasury] = useState(null);   // ground-truth: treasury() read off the live TipRouter
  const [contractTreasuryBalance, setContractTreasuryBalance] = useState(null); // balance of the address fees actually land in
  const [analytics, setAnalytics] = useState(null); // { volume, fees, count }
  const [feeConfig, setFeeConfig] = useState(null); // { percentage, cap, minTip, relayerEnabled, paused }
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);

  // Control panel state
  const [newFeePercentage, setNewFeePercentage] = useState("");
  const [newFeeCap, setNewFeeCap] = useState("");
  const [controlStatus, setControlStatus] = useState("");
  const [controlError, setControlError] = useState("");

  // Bug reports
  const [bugReports, setBugReports] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [adminTab, setAdminTab] = useState("dashboard"); // "dashboard" | "analytics" | "messages" | "polls" | "goals"
  const [siteAnalytics, setSiteAnalytics] = useState(null);
  const [messageTab, setMessageTab] = useState("bugs");
  const [showArchivedBugs, setShowArchivedBugs] = useState(false);
  const [showArchivedIdeas, setShowArchivedIdeas] = useState(false);
  const [showArchivedPartnerships, setShowArchivedPartnerships] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  // Notifications tab — real tip activity, archive state kept in localStorage
  // (no DB column needed) so individual/clear-all archiving persists per browser.
  const [notiTips, setNotiTips] = useState([]);
  const [notiLoading, setNotiLoading] = useState(false);
  const [showArchivedNotis, setShowArchivedNotis] = useState(false);
  const [notiArchived, setNotiArchived] = useState(() => {
    try { return JSON.parse(localStorage.getItem("fullsend_admin_noti_archived") || "[]"); } catch { return []; }
  });
  const persistNotiArchived = (arr) => {
    const uniq = [...new Set(arr)];
    setNotiArchived(uniq);
    try { localStorage.setItem("fullsend_admin_noti_archived", JSON.stringify(uniq)); } catch {}
  };
  const archiveNoti = (key) => persistNotiArchived([...notiArchived, key]);
  const unarchiveNoti = (key) => persistNotiArchived(notiArchived.filter(k => k !== key));
  const clearAllNotis = (keys) => persistNotiArchived([...notiArchived, ...keys]);

  // Platform metrics
  const [platformMetrics, setPlatformMetrics] = useState(null);
  
  // Poll analytics
  const [pollAnalytics, setPollAnalytics] = useState(null);

  // Goal analytics
  const [goalAnalytics, setGoalAnalytics] = useState(null);

  // Resolve creator profiles (PFP + handle) for poll/goal rows
  const adminCreatorAddrs = useMemo(() => {
    const set = new Set();
    (pollAnalytics?.recentPolls || []).forEach(p => { if (p.creator) set.add(p.creator); });
    (goalAnalytics?.goals || []).forEach(g => { if (g.creator) set.add(g.creator); });
    return [...set];
  }, [pollAnalytics, goalAnalytics]);
  const adminCreatorProfiles = useUPProfiles(adminCreatorAddrs);

  // DB-stored creator name/avatar (creator-register) — reliable fallback when the
  // live LSP3 lookup is empty or throttled. Every poll/goal creator is registered,
  // so the DB always has their handle + PFP (same source the Discover directory uses).
  const [adminCreatorDB, setAdminCreatorDB] = useState({});
  useEffect(() => {
    fetch("/api/creator-register?limit=500")
      .then(r => r.json())
      .then(data => {
        const map = {};
        (data.creators || []).forEach(c => {
          if (c.address) map[c.address.toLowerCase()] = { name: c.name || "", avatar: c.avatar || "" };
        });
        setAdminCreatorDB(map);
      })
      .catch(() => {});
  }, []);

  const renderCreatorTag = (addr) => {
    const key = addr ? addr.toLowerCase() : "";
    const live = key ? adminCreatorProfiles[key] : null;
    const db = key ? adminCreatorDB[key] : null;
    const short = addr ? `${addr.slice(0, 6)}...${addr.slice(-4)}` : "—";
    const isResolvedName = (n) => n && !/^0x[0-9a-fA-F]{4}\.\.\./.test(n);
    // Prefer a resolved live name, then the DB handle, then the short address
    const name = (live && isResolvedName(live.name) && live.name)
      || (db && isResolvedName(db.name) && db.name)
      || short;
    const liveAvatar = live && isImageAvatar(live.avatar) && live.avatar !== STOCK_AVATAR ? live.avatar : null;
    const dbAvatar = db && isImageAvatar(db.avatar) ? db.avatar : null;
    const avatar = liveAvatar || dbAvatar;
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 5, minWidth: 0 }}>
        {avatar && isImageAvatar(avatar)
          ? <img src={avatar} alt="" style={{ width: 16, height: 16, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
          : <div style={{ width: 16, height: 16, borderRadius: "50%", background: COLORS.border, flexShrink: 0 }} />}
        <span style={{ fontSize: 9, color: COLORS.textSecondary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</span>
      </div>
    );
  };

  // Fetch poll analytics when Polls tab is selected
  useEffect(() => {
    if (adminTab !== "polls") return;
    fetch("/api/data-polls?analytics=true")
      .then(r => r.json())
      .then(data => {
        if (data.success) setPollAnalytics(data.analytics);
      })
      .catch(() => {});
  }, [adminTab]);

  // Fetch site-wide analytics when Analytics tab is selected
  useEffect(() => {
    if (adminTab !== "analytics") return;
    const fetchAll = async () => {
      try {
        // Platform stats
        const statsRes = await fetch("/api/data-stats?action=platform");
        const statsData = await statsRes.json();

        // Top creators (by tip volume)
        const creatorsRes = await fetch("/api/creator-register?limit=50");
        const creatorsData = await creatorsRes.json();
        const creators = (creatorsData.creators || [])
          .filter(c => parseFloat(c.tip_volume || 0) > 0)
          .sort((a, b) => parseFloat(b.tip_volume || 0) - parseFloat(a.tip_volume || 0))
          .slice(0, 15);

        // Top tippers
        const tippersRes = await fetch("/api/data-tips?action=leaderboard&limit=15");
        const tippersData = await tippersRes.json();

        // Page views
        let pageViews = null;
        try {
          const viewsRes = await fetch("/api/data-stats?action=page-views");
          const viewsData = await viewsRes.json();
          if (viewsData.success) pageViews = viewsData;
        } catch {}

        setSiteAnalytics({
          stats: statsData,
          topCreators: creators,
          topTippers: tippersData.leaderboard || [],
          pageViews,
        });
      } catch (err) {
        console.log("Analytics fetch error:", err);
      }
    };
    fetchAll();
  }, [adminTab]);

  // Fetch goal analytics when Goals tab is selected
  useEffect(() => {
    if (adminTab !== "goals") return;
    fetch("/api/creator-register?limit=100")
      .then(r => r.json())
      .then(data => {
        // Handle both list and single creator responses
        let creators = [];
        if (data.creators && Array.isArray(data.creators)) creators = data.creators;
        else if (data.creator) creators = [data.creator];
        else if (Array.isArray(data)) creators = data;
        
        const goalsData = creators
          .map(c => {
            // Goal lives in platforms._activeGoal (DB-only since Session 20 refactor)
            const platforms = typeof c.platforms === "string" ? JSON.parse(c.platforms || "{}") : (c.platforms || {});
            const goal = platforms._activeGoal || {};
            
            const title = goal.title || "";
            const target = parseFloat(goal.target) || 0;
            const current = parseFloat(goal.current) || 0;
            
            return {
              creator: c.address || c.creator,
              name: c.name || "",
              title,
              target,
              current,
              active: goal.status === "active",
            };
          })
          .filter(g => g.target > 0)
          .filter((g, i, arr) => arr.findIndex(x => x.creator === g.creator && x.title === g.title) === i);
        
        const totalGoals = goalsData.length;
        const completedGoals = goalsData.filter(g => g.current >= g.target).length;
        const totalRaised = goalsData.reduce((sum, g) => sum + g.current, 0);
        const totalTarget = goalsData.reduce((sum, g) => sum + g.target, 0);
        
        setGoalAnalytics({
          totalGoals,
          completedGoals,
          activeGoals: totalGoals - completedGoals,
          totalRaised,
          totalTarget,
          goals: goalsData,
        });
      })
      .catch(() => {});
  }, [adminTab]);

  // Gas analytics
  const [gasAnalytics, setGasAnalytics] = useState(null); // from relay-gas-log?action=summary
  const [gasConsumers, setGasConsumers] = useState(null);  // from relay-gas-log?action=top-consumers
  const [gasAlerts, setGasAlerts] = useState(null);        // from relay-gas-log?action=alerts
  const [blockedAddrs, setBlockedAddrs] = useState(null);  // from relay-gas-log?action=blocked

  // Resolve UP profile (name + avatar) for every address shown on the dashboard —
  // top gas consumers + soft-blocked addresses. Cached across renders by useUPProfiles.
  const adminAddresses = useMemo(() => {
    const a = [];
    if (gasConsumers) a.push(...gasConsumers.map(c => c.upAddress).filter(Boolean));
    if (blockedAddrs) a.push(...blockedAddrs.map(b => b.address).filter(Boolean));
    return a;
  }, [gasConsumers, blockedAddrs]);
  const adminProfiles = useUPProfiles(adminAddresses);

  // Indexer state
  const [indexerStatus, setIndexerStatus] = useState(null);
  const [indexerSyncing, setIndexerSyncing] = useState(false);
  const [indexerResult, setIndexerResult] = useState(null);
  const [dbStats, setDbStats] = useState(null); // from data-stats?action=platform

  // Admin connect
  const connectAdmin = async () => {
    setConnecting(true);
    try {
      let prov = window.lukso || window.ethereum;
      if (!prov) {
        alert("No wallet found. Install the UP Browser Extension or MetaMask.");
        setConnecting(false);
        return;
      }
      const accounts = await prov.request({ method: "eth_requestAccounts" });
      if (!accounts || accounts.length === 0) { setConnecting(false); return; }
      const addr = accounts[0];
      setAddress(addr);
      setProvider(prov);
      setConnected(true);
      window.__fullsend_admin_provider = prov;

      // Check if admin
      const isAdm = ADMIN_WHITELIST.some(a => a.toLowerCase() === addr.toLowerCase());
      setIsAdmin(isAdm);

      if (isAdm) {
        await fetchDashboardData(prov);
        loadBugReports(); loadSubmissions(); loadNotiTips();
        fetchIndexerStatus();
        fetchDbStats();
        fetchGasAnalytics();
      }
    } catch (err) {
      console.error("Admin connect error:", err);
    }
    setConnecting(false);
  };

  // Fetch all dashboard data
  const fetchDashboardData = async (prov) => {
    const p = prov || provider;
    if (!p) return;
    setLoading(true);
    try {
      // Fetch all three wallet balances in parallel
      const [depHex, relHex, treasHex] = await Promise.all([
        p.request({ method: "eth_getBalance", params: [TREASURY_ADDRESS, "latest"] }),
        p.request({ method: "eth_getBalance", params: [FULLSEND_RELAYER, "latest"] }),
        p.request({ method: "eth_getBalance", params: [CREATOR_TREASURY_UP, "latest"] }),
      ]);
      const fmt = (h) => (h && h !== "0x") ? (Number(BigInt(h)) / 1e18).toFixed(4) : "0.0000";
      setDeployerBalance(fmt(depHex));
      setRelayerBalance(fmt(relHex));
      setCreatorTreasuryBalance(fmt(treasHex));

      // getAnalytics() from TipRouter
      const analyticsData = await p.request({
        method: "eth_call",
        params: [{ to: TIPROUTER_ADDRESS, data: "0x0ccb2ec8" }, "latest"],
      });
      if (analyticsData && analyticsData !== "0x" && analyticsData.length > 66) {
        const hex = analyticsData.slice(2);
        try {
          const volume = Number(BigInt("0x" + hex.slice(0, 64))) / 1e18;
          const fees = Number(BigInt("0x" + hex.slice(64, 128))) / 1e18;
          const count = Number(BigInt("0x" + hex.slice(128, 192)));
          setAnalytics({ volume, fees, count });
        } catch (e) { console.log("Analytics parse error:", e.message); }
      }

      // treasury() from TipRouter — the GROUND TRUTH for where the 0.50 fee lands.
      // The contract forwards each fee here on every tip (and reverts if it fails),
      // so this is the address actually receiving fees — not an assumption.
      try {
        const treasHex2 = await p.request({
          method: "eth_call",
          params: [{ to: TIPROUTER_ADDRESS, data: "0x61d027b3" }, "latest"], // treasury()
        });
        if (treasHex2 && treasHex2 !== "0x" && treasHex2.length >= 66) {
          const treasuryAddr = "0x" + treasHex2.slice(-40);
          setContractTreasury(treasuryAddr);
          const ctBal = await p.request({ method: "eth_getBalance", params: [treasuryAddr, "latest"] });
          setContractTreasuryBalance(fmt(ctBal));
        }
      } catch (e) { console.log("treasury() read error:", e.message); }

      // getFeeConfig() from TipRouter
      const feeData = await p.request({
        method: "eth_call",
        params: [{ to: TIPROUTER_ADDRESS, data: "0x5fbbc0d2" }, "latest"],
      });
      if (feeData && feeData !== "0x" && feeData.length > 66) {
        const hex = feeData.slice(2);
        try {
          const percentage = Number(BigInt("0x" + hex.slice(0, 64)));
          const cap = Number(BigInt("0x" + hex.slice(64, 128))) / 1e18;
          const minTip = Number(BigInt("0x" + hex.slice(128, 192))) / 1e18;
          const relayerEnabled = hex.length >= 320 ? BigInt("0x" + hex.slice(192, 256)) === 1n : false;
          const paused = hex.length >= 320 ? BigInt("0x" + hex.slice(256, 320)) === 1n : false;
          setFeeConfig({ percentage, cap, minTip, relayerEnabled, paused });
        } catch (e) { console.log("Fee config parse error:", e.message); }
      }

      setLastRefresh(new Date());

      // --- Platform Metrics (best-effort, silent fail) ---
      const metrics = { uniqueTippers: 0, uniqueCreators: 0, totalPolls: 0, totalFollows: 0 };
      
      try {
        // Get TipSent event logs to count unique tippers and creators
        // TipSent(address indexed from, address indexed to, uint256, uint256, bytes32, uint256)
        const tipLogs = await p.request({
          method: "eth_getLogs",
          params: [{
            fromBlock: "0x0",
            toBlock: "latest",
            address: TIPROUTER_ADDRESS,
            topics: ["0xa6ff4ac9c31172e3d6aeaf335e9527475d65beb807e58bff8421338e15a456c1"], // TipSent event topic
          }],
        });
        if (tipLogs && Array.isArray(tipLogs)) {
          const tippers = new Set();
          const creators = new Set();
          tipLogs.forEach(log => {
            if (log.topics && log.topics.length >= 3) {
              tippers.add(log.topics[1]); // from (indexed)
              creators.add(log.topics[2]); // to (indexed)
            }
          });
          metrics.uniqueTippers = tippers.size;
          metrics.uniqueCreators = creators.size;
        }
      } catch (e) { console.log("Tip logs fetch skipped:", e.message); }

      try {
        // Try nextPollId() on PollVoting contract
        const pollData = await p.request({
          method: "eth_call",
          params: [{ to: "0xA8A466Ab3096cbe88285d9B712d2562ef7297ACe", data: "0x2b786571" }, "latest"],
        });
        if (pollData && pollData !== "0x" && pollData.length > 2) {
          metrics.totalPolls = Number(BigInt(pollData));
        }
      } catch (e) { console.log("Poll count fetch skipped:", e.message); }

      try {
        // Get total follows from indexer DB (LSP26 doesn't have a global totalFollows)
        const followRes = await fetch("/api/data-stats?action=platform");
        const followJson = await followRes.json();
        if (followJson.follows?.total_follows) {
          metrics.totalFollows = parseInt(followJson.follows.total_follows);
        }
      } catch (e) { console.log("Follow count fetch skipped:", e.message); }

      setPlatformMetrics(metrics);

      // --- Poll Analytics (from DB — independent of RPC) ---
      try {
        const pollRes = await fetch("/api/data-polls?analytics=true");
        const pollJson = await pollRes.json();
        if (pollJson.success) {
          setPollAnalytics(pollJson.analytics);
        }
      } catch (e) { console.log("Poll analytics fetch skipped:", e.message); }

    } catch (err) {
      console.error("Dashboard fetch error:", err);
    }

    // Poll analytics retry if not loaded (in case outer try failed)
    if (!pollAnalytics) {
      try {
        const pollRes = await fetch("/api/data-polls?analytics=true");
        const pollJson = await pollRes.json();
        if (pollJson.success) {
          setPollAnalytics(pollJson.analytics);
        }
      } catch (e) {}
    }

    setLoading(false);
  };

  // Bug report helpers
  const loadBugReports = () => {
    fetch("/api/submissions")
      .then(r => r.json())
      .then(data => setBugReports(data.bugs || []))
      .catch(() => setBugReports([]));
  };

  // Notifications — platform-specific only: tips sent TO FullSend (the Tip
  // FullSend button + anything landing in the FS treasury UP), not every
  // creator's tips. Per-creator notifications live under My Profile.
  const loadNotiTips = () => {
    setNotiLoading(true);
    fetch(`/api/data-tips?action=activity&creator=${CREATOR_TREASURY_UP}&limit=50`)
      .then(r => r.json())
      .then(data => setNotiTips((data.activity || []).filter(t => t && t.tx_hash)))
      .catch(() => setNotiTips([]))
      .finally(() => setNotiLoading(false));
  };

  const toggleBugStatus = (id, newStatus) => {
    fetch("/api/submissions", {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ type: "bug", id, status: newStatus }),
    }).then(() => { loadBugReports(); loadSubmissions(); }).catch(() => {});
  };

  const archiveBug = (id) => toggleBugStatus(id, "archived");

  const toggleSubmissionStatus = (id, newStatus) => {
    fetch("/api/submissions", {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ type: "submission", id, status: newStatus }),
    }).then(() => loadSubmissions()).catch(() => {});
  };

  const archiveSubmission = (id) => toggleSubmissionStatus(id, "archived");

  const [submitterProfiles, setSubmitterProfiles] = useState({});

  const loadSubmissions = () => {
    fetch("/api/submissions")
      .then(r => r.json())
      .then(data => {
        const all = [...(data.ideas || []), ...(data.partnerships || [])];
        setSubmissions(all);
        // Resolve UP profiles for submitters
        const addresses = [...new Set([...all, ...(data.bugs || [])].map(s => s.reporter_address).filter(a => a && a !== "Not connected"))];
        addresses.forEach(addr => {
          if (!submitterProfiles[addr.toLowerCase()]) {
            fetchUPProfile(addr).then(p => {
              if (p) setSubmitterProfiles(prev => ({ ...prev, [addr.toLowerCase()]: p }));
            });
          }
        });
      })
      .catch(() => setSubmissions([]));
  };

  const deleteSubmission = (id) => archiveSubmission(id);

  // Indexer functions
  const fetchIndexerStatus = async () => {
    try {
      const res = await fetch("/api/data-stats?action=sync-status");
      const data = await res.json();
      setIndexerStatus(data);
    } catch (e) { console.log("Indexer status fetch failed:", e); }
  };

  const fetchDbStats = async () => {
    try {
      const res = await fetch("/api/data-stats?action=platform");
      const data = await res.json();
      setDbStats(data);
    } catch (e) { console.log("DB stats fetch failed:", e); }
  };

  // Gas analytics fetch
  const fetchGasAnalytics = async () => {
    try {
      const [summaryRes, consumersRes, alertsRes, blockedRes] = await Promise.all([
        fetch("/api/relay-gas-log?action=summary"),
        fetch("/api/relay-gas-log?action=top-consumers"),
        fetch("/api/relay-gas-log?action=alerts"),
        fetch("/api/relay-gas-log?action=blocked"),
      ]);
      const [summary, consumers, alerts, blocked] = await Promise.all([
        summaryRes.json(), consumersRes.json(), alertsRes.json(), blockedRes.json(),
      ]);
      if (summary.success) setGasAnalytics(summary);
      if (consumers.success) setGasConsumers(consumers.consumers);
      if (alerts.success) setGasAlerts(alerts.alerts);
      if (blocked.success) setBlockedAddrs(blocked.blocked);
    } catch (e) { console.log("Gas analytics fetch failed:", e); }
  };

  // Manual unblock — admin override
  const unblockAddress = async (addr) => {
    if (!confirm(`Unblock ${addr}?\n\nThis removes the soft-block immediately. They'll be subject to fresh rate limits going forward.`)) return;
    try {
      const res = await fetch(`/api/relay-gas-log?action=unblock&up=${addr}`);
      const data = await res.json();
      if (data.success) {
        await fetchGasAnalytics();
      } else {
        alert("Unblock failed: " + (data.error || "unknown"));
      }
    } catch (e) {
      alert("Unblock failed: " + e.message);
    }
  };

  const runIndexerSync = async () => {
    setIndexerSyncing(true);
    setIndexerResult(null);
    try {
      const res = await fetch("/api/indexer-sync");
      const data = await res.json();
      setIndexerResult(data);
      // Refresh status after sync
      await fetchIndexerStatus();
      await fetchDbStats();
      await fetchDashboardData();
    } catch (e) {
      setIndexerResult({ error: e.message });
    }
    setIndexerSyncing(false);
  };

  // Auto-refresh every 30s
  useEffect(() => {
    if (!isAdmin || !provider) return;
    const interval = setInterval(() => {
      fetchDashboardData();
      loadBugReports(); loadSubmissions();
      fetchIndexerStatus();
      fetchDbStats();
      fetchGasAnalytics();
    }, 30000);
    return () => clearInterval(interval);
  }, [isAdmin, provider]);

  // Contract control: setFeeParameters
  const updateFeeParams = async () => {
    if (!provider || !newFeePercentage || !newFeeCap) return;
    setControlStatus("Sending transaction...");
    setControlError("");
    try {
      const pct = parseInt(newFeePercentage);
      const capWei = BigInt(Math.round(parseFloat(newFeeCap) * 1e18));
      // setFeeParameters(uint256,uint256) selector
      const selector = "0x4d56022f"; // setFeeParameters(uint256,uint256)
      const data = selector +
        pct.toString(16).padStart(64, "0") +
        capWei.toString(16).padStart(64, "0");

      const tx = await provider.request({
        method: "eth_sendTransaction",
        params: [{
          from: address,
          to: TIPROUTER_ADDRESS,
          data: data,
          value: "0x0",
        }],
      });
      setControlStatus(`TX sent: ${tx.slice(0, 14)}...`);
      setTimeout(() => { fetchDashboardData(); setControlStatus(""); }, 5000);
    } catch (err) {
      setControlError(err.message || "Transaction failed");
      setControlStatus("");
    }
  };

  // Contract control: toggleRelayer
  const toggleRelayer = async () => {
    if (!provider) return;
    setControlStatus("Sending transaction...");
    setControlError("");
    try {
      const selector = "0x45ac6200"; // toggleRelayer()
      const tx = await provider.request({
        method: "eth_sendTransaction",
        params: [{
          from: address,
          to: TIPROUTER_ADDRESS,
          data: selector,
          value: "0x0",
        }],
      });
      setControlStatus(`TX sent: ${tx.slice(0, 14)}...`);
      setTimeout(() => { fetchDashboardData(); setControlStatus(""); }, 5000);
    } catch (err) {
      setControlError(err.message || "Transaction failed");
      setControlStatus("");
    }
  };

  // Contract control: togglePause
  const togglePause = async () => {
    if (!provider) return;
    setControlStatus("Sending transaction...");
    setControlError("");
    try {
      const selector = "0xc4ae3168"; // togglePause() precomputed — need to verify
      const tx = await provider.request({
        method: "eth_sendTransaction",
        params: [{
          from: address,
          to: TIPROUTER_ADDRESS,
          data: selector,
          value: "0x0",
        }],
      });
      setControlStatus(`TX sent: ${tx.slice(0, 14)}...`);
      setTimeout(() => { fetchDashboardData(); setControlStatus(""); }, 5000);
    } catch (err) {
      setControlError(err.message || "Transaction failed");
      setControlStatus("");
    }
  };

  // Stat card helper
  const AddrCopy = ({ addr, color }) => {
    const [c, setC] = useState(false);
    if (!addr) return null;
    return (
      <button
        onClick={(e) => { e.stopPropagation(); try { navigator.clipboard?.writeText?.(addr); setC(true); setTimeout(() => setC(false), 1500); } catch {} }}
        title={addr}
        style={{
          marginTop: 10, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6,
          padding: "5px 8px", borderRadius: 7, width: "100%",
          background: COLORS.bg, border: `1px solid ${COLORS.border}`,
          color: c ? COLORS.accent : COLORS.textMuted,
          fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 600,
          cursor: "pointer", transition: "all 0.15s", position: "relative",
        }}
        onMouseEnter={e => e.currentTarget.style.borderColor = `${color || COLORS.accent}40`}
        onMouseLeave={e => e.currentTarget.style.borderColor = COLORS.border}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {addr.slice(0, 10)}…{addr.slice(-8)}
        </span>
        <span style={{ flexShrink: 0, color: c ? COLORS.accent : (color || COLORS.textMuted), fontWeight: 700 }}>
          {c ? "✓ Copied" : "Copy"}
        </span>
      </button>
    );
  };

  const StatCard = ({ label, value, sub, color, icon, large, addr }) => (
    <div style={{
      padding: large ? "24px 28px" : "20px 22px", borderRadius: 14,
      background: COLORS.surface,
      border: `1px solid ${COLORS.border}`,
      flex: 1, minWidth: large ? 220 : 180,
      position: "relative",
      overflow: "hidden",
    }}>
      {/* Subtle glow accent */}
      <div style={{
        position: "absolute", top: -20, right: -20,
        width: 80, height: 80, borderRadius: "50%",
        background: `${color || COLORS.accent}08`,
        filter: "blur(20px)",
      }} />
      <div style={{
        fontSize: 11, fontWeight: 600, color: COLORS.textSecondary,
        fontFamily: "'Outfit', sans-serif",
        marginBottom: 10, display: "flex", alignItems: "center", gap: 8,
        position: "relative",
      }}>
        {icon && <span style={{ fontSize: 16 }}>{icon}</span>}
        {label}
      </div>
      <div style={{
        fontSize: large ? 32 : 24, fontWeight: 800, color: color || COLORS.accent,
        fontFamily: "'Syne', sans-serif",
        letterSpacing: "-0.5px",
        lineHeight: 1.1,
        position: "relative",
      }}>
        {value ?? <span style={{ animation: "pulse 1s infinite", opacity: 0.3 }}>—</span>}
      </div>
      {sub && (
        <div style={{ fontSize: 10, color: COLORS.textMuted, marginTop: 8, lineHeight: 1.4 }}>{sub}</div>
      )}
      <AddrCopy addr={addr} color={color} />
    </div>
  );

  // Section header helper
  const SectionHeader = ({ title, icon, right }) => (
    <div style={{
      fontFamily: "'Syne', sans-serif",
      fontSize: 15, fontWeight: 800, color: COLORS.text,
      marginBottom: 16, marginTop: 36,
      display: "flex", alignItems: "center", justifyContent: "space-between",
      gap: 8,
      paddingBottom: 12,
      borderBottom: `1px solid ${COLORS.border}`,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span style={{ fontSize: 18 }}>{icon}</span> {title}
      </div>
      {right && <div>{right}</div>}
    </div>
  );

  // Info row helper for key-value display
  const InfoRow = ({ label, value, color, mono }) => (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "12px 0",
      borderBottom: `1px solid ${COLORS.border}`,
    }}>
      <div style={{
        fontSize: 12, color: COLORS.textSecondary,
        fontFamily: "'Outfit', sans-serif",
      }}>{label}</div>
      <div style={{
        fontSize: 13, fontWeight: 600, color: color || COLORS.text,
        fontFamily: mono ? "'JetBrains Mono', monospace" : "'Outfit', sans-serif",
      }}>{value}</div>
    </div>
  );

  // Status pill helper
  const StatusPill = ({ active, activeLabel, inactiveLabel, activeColor, inactiveColor }) => (
    <div style={{
      padding: "6px 16px", borderRadius: 20,
      background: active ? `${activeColor || COLORS.accent}12` : `${inactiveColor || COLORS.red}12`,
      border: `1px solid ${active ? (activeColor || COLORS.accent) + "30" : (inactiveColor || COLORS.red) + "30"}`,
      fontSize: 11, fontWeight: 700,
      color: active ? (activeColor || COLORS.accent) : (inactiveColor || COLORS.red),
      fontFamily: "'JetBrains Mono', monospace",
      display: "inline-flex", alignItems: "center", gap: 8,
    }}>
      <div style={{
        width: 8, height: 8, borderRadius: "50%",
        background: active ? (activeColor || COLORS.accent) : (inactiveColor || COLORS.red),
        boxShadow: `0 0 8px ${active ? (activeColor || COLORS.accent) : (inactiveColor || COLORS.red)}60`,
      }} />
      {active ? activeLabel : inactiveLabel}
    </div>
  );

  return (
    <div style={{
      background: COLORS.bg,
      minHeight: "100vh",
      fontFamily: "'Outfit', 'JetBrains Mono', monospace",
      color: COLORS.text,
    }}>
      <style>{GLOBAL_STYLES}</style>
      <link href="https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@300;400;500;600;700;800&family=Outfit:wght@300;400;500;600;700;800;900&family=Syne:wght@400;500;600;700;800&display=swap" rel="stylesheet" />

      {/* Admin Nav */}
      <nav style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "14px 28px",
        borderBottom: `1px solid ${COLORS.border}`,
        background: `${COLORS.bg}f0`,
        backdropFilter: "blur(24px) saturate(1.5)",
        position: "sticky", top: 0, zIndex: 100,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div
            style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}
            onClick={() => window.location.href = "/"}
          >
            <div style={{
              width: 32, height: 32, borderRadius: 8,
              background: `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.cyan})`,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 15, fontWeight: 900, color: COLORS.bg,
              boxShadow: `0 0 16px ${COLORS.accentGlow}`,
            }}>F</div>
            <span style={{
              fontFamily: "'Syne', sans-serif",
              fontSize: 18, fontWeight: 800,
              background: `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.cyan})`,
              WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
            }}>FULLSEND</span>
          </div>
          <div style={{
            padding: "4px 12px", borderRadius: 6,
            background: `${COLORS.red}10`,
            border: `1px solid ${COLORS.red}20`,
            fontSize: 10, fontWeight: 700, color: COLORS.red,
            fontFamily: "'JetBrains Mono', monospace",
            letterSpacing: "2px",
          }}>ADMIN</div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {connected && isAdmin && lastRefresh && (
            <div style={{
              fontSize: 10, color: COLORS.textMuted,
              fontFamily: "'JetBrains Mono', monospace",
            }}>
              {lastRefresh.toLocaleTimeString()}
            </div>
          )}
          {connected && isAdmin && (
            <button onClick={async () => {
              const btn = document.getElementById("dash-refresh-btn");
              if (btn) { btn.style.color = COLORS.accent; btn.textContent = "⏳ Refreshing..."; }
              await Promise.all([
                fetchDashboardData(),
                loadBugReports(),
                loadSubmissions(),
                loadNotiTips(),
                fetchIndexerStatus(),
                fetchDbStats(),
                fetchGasAnalytics(),
              ]);
              setLastRefresh(new Date());
              if (btn) { btn.textContent = "✓ Updated"; btn.style.color = COLORS.accent; }
              setTimeout(() => { if (btn) { btn.textContent = "🔄 Refresh"; btn.style.color = COLORS.textSecondary; } }, 2000);
            }} id="dash-refresh-btn" style={{
              padding: "7px 16px", borderRadius: 7,
              background: COLORS.surface, border: `1px solid ${COLORS.border}`,
              color: COLORS.textSecondary, fontSize: 11, fontWeight: 600,
              cursor: "pointer", fontFamily: "'Outfit', sans-serif",
              transition: "all 0.2s",
              display: "flex", alignItems: "center", gap: 6,
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = COLORS.accent + "40"; e.currentTarget.style.color = COLORS.accent; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = COLORS.border; e.currentTarget.style.color = COLORS.textSecondary; }}
            >🔄 Refresh</button>
          )}
          <button onClick={() => window.location.href = "/"} style={{
            padding: "7px 16px", borderRadius: 7,
            background: "transparent", border: `1px solid ${COLORS.border}`,
            color: COLORS.textMuted, fontSize: 11, fontWeight: 600,
            cursor: "pointer", fontFamily: "'Outfit', sans-serif",
            transition: "all 0.2s",
          }}
          onMouseEnter={e => e.currentTarget.style.color = COLORS.text}
          onMouseLeave={e => e.currentTarget.style.color = COLORS.textMuted}
          >← Back to App</button>
        </div>
      </nav>

      <div style={{ maxWidth: 960, margin: "0 auto", padding: "0 28px 80px" }}>

        {/* Not connected state */}
        {!connected && (
          <div style={{
            textAlign: "center", padding: "100px 20px",
            animation: "fadeInUp 0.4s ease-out",
          }}>
            <div style={{ fontSize: 56, marginBottom: 20 }}>🔐</div>
            <div style={{
              fontFamily: "'Syne', sans-serif",
              fontSize: 28, fontWeight: 800, marginBottom: 10,
            }}>Admin Dashboard</div>
            <div style={{
              fontSize: 14, color: COLORS.textMuted, marginBottom: 36,
              maxWidth: 420, margin: "0 auto 36px",
              lineHeight: 1.6,
              fontFamily: "'Outfit', sans-serif",
            }}>
              Connect your Universal Profile to access platform admin controls. Only whitelisted addresses can view this dashboard.
            </div>
            <button onClick={connectAdmin} disabled={connecting} style={{
              padding: "16px 40px", borderRadius: 12, border: "none",
              background: `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.cyan})`,
              color: COLORS.bg,
              fontFamily: "'Syne', sans-serif",
              fontSize: 16, fontWeight: 700,
              cursor: connecting ? "wait" : "pointer",
              display: "inline-flex", alignItems: "center", gap: 10,
              transition: "all 0.2s",
              boxShadow: `0 4px 32px ${COLORS.accentGlow}`,
            }}
            onMouseEnter={e => e.currentTarget.style.transform = "translateY(-2px)"}
            onMouseLeave={e => e.currentTarget.style.transform = "translateY(0)"}
            >
              {connecting ? "Connecting..." : "Connect UP"}
            </button>
          </div>
        )}

        {/* Not authorized */}
        {connected && !isAdmin && (
          <div style={{
            textAlign: "center", padding: "100px 20px",
            animation: "fadeInUp 0.4s ease-out",
          }}>
            <div style={{ fontSize: 56, marginBottom: 20 }}>⛔</div>
            <div style={{
              fontFamily: "'Syne', sans-serif",
              fontSize: 28, fontWeight: 800, color: COLORS.red, marginBottom: 10,
            }}>Not Authorized</div>
            <div style={{
              fontSize: 14, color: COLORS.textMuted, marginBottom: 16,
              fontFamily: "'Outfit', sans-serif",
            }}>
              This address is not in the admin whitelist.
            </div>
            <div style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 12, color: COLORS.textMuted,
              padding: "10px 20px", borderRadius: 8,
              background: COLORS.surface, display: "inline-block",
              border: `1px solid ${COLORS.border}`,
            }}>
              {address.slice(0, 10)}...{address.slice(-8)}
            </div>
          </div>
        )}

        {/* ===== ADMIN DASHBOARD CONTENT ===== */}
        {connected && isAdmin && (
          <div style={{ animation: "fadeInUp 0.4s ease-out" }}>

            {/* Admin tabs */}
            <div style={{ display: "flex", gap: 6, marginTop: 24, marginBottom: 16 }}>
              {[
                { key: "dashboard", label: "Dashboard", icon: "📊" },
                { key: "analytics", label: "Analytics", icon: "📈" },
                { key: "polls", label: "Polls", icon: "📋" },
                { key: "goals", label: "Goals", icon: "🎯" },
                { key: "messages", label: "Messages", icon: "💬", count: bugReports.filter(r => r.status === "open").length + submissions.filter(s => s.status !== "archived").length },
              ].map(t => (
                <button key={t.key} onClick={() => setAdminTab(t.key)} style={{
                  padding: "8px 18px", borderRadius: 8,
                  background: adminTab === t.key ? `${COLORS.accent}12` : COLORS.surface,
                  border: `1px solid ${adminTab === t.key ? COLORS.accent + "25" : COLORS.border}`,
                  color: adminTab === t.key ? COLORS.accent : COLORS.textMuted,
                  fontFamily: "'Syne', sans-serif", fontSize: 13, fontWeight: 700,
                  cursor: "pointer", display: "flex", alignItems: "center", gap: 8,
                  transition: "all 0.2s",
                }}>
                  <span>{t.icon}</span> {t.label}
                  {t.count > 0 && (
                    <span style={{
                      fontSize: 9, padding: "1px 6px", borderRadius: 4,
                      background: COLORS.red + "20", color: COLORS.red, fontWeight: 700,
                      fontFamily: "'JetBrains Mono', monospace",
                    }}>{t.count}</span>
                  )}
                </button>
              ))}
            </div>

            {/* ===== DASHBOARD TAB ===== */}
            {adminTab === "dashboard" && (<>

            {/* Status bar at top */}
            <div style={{
              display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
              marginTop: 24, marginBottom: 8,
            }}>
              <StatusPill
                active={!feeConfig?.paused}
                activeLabel="CONTRACT ACTIVE"
                inactiveLabel="CONTRACT PAUSED"
              />
              <StatusPill
                active={feeConfig?.relayerEnabled}
                activeLabel="RELAYER ON"
                inactiveLabel="RELAYER OFF"
                activeColor={COLORS.cyan}
                inactiveColor={COLORS.orange}
              />
              <div style={{ marginLeft: "auto", fontSize: 10, color: COLORS.textMuted, fontFamily: "'JetBrains Mono', monospace" }}>
                LUKSO Mainnet · Chain 42
              </div>
            </div>

            {/* ---- WALLET BALANCES ---- */}
            <SectionHeader title="Wallet Balances" icon="🏦" />
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14 }}>
              <StatCard
                label="Relayer EOA"
                value={capBalance(relayerBalance)}
                sub="LYX · pays gas (critical)"
                color={
                  relayerBalance && parseFloat(relayerBalance) < 2 ? COLORS.red :
                  relayerBalance && parseFloat(relayerBalance) < 5 ? COLORS.orange :
                  COLORS.accent
                }
                icon="⚡"
                large
                addr={FULLSEND_RELAYER}
              />
              <StatCard
                label="Treasury"
                value={capBalance(contractTreasuryBalance ?? creatorTreasuryBalance)}
                sub={contractTreasury
                  ? `LYX · fees → ${contractTreasury.slice(0, 6)}…${contractTreasury.slice(-4)}`
                  : "LYX · tip fees collected"}
                color={COLORS.gold}
                icon="💰"
                large
                addr={contractTreasury ?? CREATOR_TREASURY_UP}
              />
              <StatCard
                label="Deployer EOA"
                value={capBalance(deployerBalance)}
                sub="LYX · contract admin ops"
                color={COLORS.blue}
                icon="🔧"
                large
                addr={TREASURY_ADDRESS}
              />
              <StatCard
                label="Fees Collected"
                value={analytics ? capBalance(analytics.fees.toFixed(4)) : null}
                sub="LYX · TipRouter total"
                color={COLORS.purple}
                icon="🏆"
                large
              />
            </div>

            {/* Treasury mismatch guard — the panel historically assumed fees land in
                CREATOR_TREASURY_UP, but the contract's treasury() is the real
                recipient. Surface it loudly if they differ so balances aren't misread. */}
            {contractTreasury && contractTreasury.toLowerCase() !== CREATOR_TREASURY_UP.toLowerCase() && (
              <div style={{
                marginTop: 12, padding: "10px 14px", borderRadius: 10,
                background: `${COLORS.orange}10`, border: `1px solid ${COLORS.orange}30`,
                fontSize: 11, color: COLORS.textSecondary, lineHeight: 1.6,
                fontFamily: "'JetBrains Mono', monospace",
              }}>
                <span style={{ color: COLORS.orange, fontWeight: 700 }}>⚠ Fee destination:</span>{" "}
                the TipRouter sends fees to <code style={{ color: COLORS.text }}>{contractTreasury}</code>, not the
                Treasury UP <code style={{ color: COLORS.textMuted }}>{CREATOR_TREASURY_UP}</code> this panel assumed.
                The Treasury card above now reads the real recipient. Update <code style={{ color: COLORS.text }}>CREATOR_TREASURY_UP</code> to
                match, or call <code style={{ color: COLORS.text }}>setTreasury()</code> if fees should go to the UP.
              </div>
            )}
            {/* Tiered alerts on RELAYER balance specifically — this is the wallet that pays gas */}
            {relayerBalance && parseFloat(relayerBalance) < 2 && (
              <div style={{
                margin: "16px 0", padding: "14px 16px", borderRadius: 10,
                background: `${COLORS.red}10`,
                border: `1px solid ${COLORS.red}40`,
                display: "flex", alignItems: "center", gap: 12,
                animation: "pulse 2s ease-in-out infinite",
              }}>
                <span style={{ fontSize: 24 }}>🚨</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: COLORS.red, fontFamily: "'Syne', sans-serif" }}>
                    CRITICAL — Relayer almost empty
                  </div>
                  <div style={{ fontSize: 11, color: COLORS.textSecondary, marginTop: 3, lineHeight: 1.5 }}>
                    Relayer EOA at <strong style={{ color: COLORS.red }}>{relayerBalance} LYX</strong>. Top up <code style={{ color: COLORS.red, fontFamily: "'JetBrains Mono', monospace", fontSize: 10 }}>{FULLSEND_RELAYER.slice(0, 10)}...{FULLSEND_RELAYER.slice(-6)}</code> NOW — gasless follows and votes will fail when this hits zero.
                  </div>
                </div>
              </div>
            )}
            {relayerBalance && parseFloat(relayerBalance) >= 2 && parseFloat(relayerBalance) < 5 && (
              <div style={{
                margin: "16px 0", padding: "12px 16px", borderRadius: 10,
                background: `${COLORS.orange}08`,
                border: `1px solid ${COLORS.orange}30`,
                display: "flex", alignItems: "center", gap: 10,
              }}>
                <span style={{ fontSize: 20 }}>⚠️</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.orange, fontFamily: "'Syne', sans-serif" }}>
                    Relayer balance low
                  </div>
                  <div style={{ fontSize: 10, color: COLORS.textMuted, marginTop: 2, lineHeight: 1.5 }}>
                    Relayer at {relayerBalance} LYX. Consider topping up <code style={{ fontFamily: "'JetBrains Mono', monospace", color: COLORS.textSecondary, fontSize: 9 }}>{FULLSEND_RELAYER.slice(0, 10)}...{FULLSEND_RELAYER.slice(-6)}</code> soon to avoid running out during peak traffic.
                  </div>
                </div>
              </div>
            )}
            {/* Deployer low balance — informational only (admin functions are rare) */}
            {deployerBalance && parseFloat(deployerBalance) < 0.5 && (
              <div style={{
                margin: "10px 0", padding: "10px 14px", borderRadius: 8,
                background: `${COLORS.blue}06`,
                border: `1px solid ${COLORS.blue}20`,
                display: "flex", alignItems: "center", gap: 10,
              }}>
                <span style={{ fontSize: 16 }}>ℹ️</span>
                <div style={{ fontSize: 10, color: COLORS.textMuted, lineHeight: 1.5 }}>
                  Deployer EOA at {deployerBalance} LYX — fine for now (only needed for occasional contract admin calls), but top up before next ownership transfer or fee change.
                </div>
              </div>
            )}

            {/* ---- RELAYER HEALTH ---- */}
            <SectionHeader title="Relayer Health" icon="🔋" />
            {(() => {
              const balance = parseFloat(relayerBalance || 0);
              const feesCollected = analytics?.fees || 0;
              const totalGasSpent = gasAnalytics?.totals?.totalGasLyx || 0;
              const netRevenue = feesCollected - totalGasSpent;
              const totalActions = gasAnalytics?.totals?.totalActions || 0;

              // Calculate burn rate from 7-day data
              const last7dGas = (gasAnalytics?.last7d || []).reduce((sum, r) => sum + (r.gasLyx || 0), 0);
              const dailyBurnRate = last7dGas > 0 ? last7dGas / 7 : 0;
              const runwayDays = dailyBurnRate > 0 ? Math.floor(balance / dailyBurnRate) : -1; // -1 = no data

              // Per-action costs (from all-time data)
              const actionCosts = {};
              for (const row of gasAnalytics?.allTime || []) {
                if (row.totalActions > 0) {
                  actionCosts[row.actionType] = {
                    avgCost: row.totalGasLyx / row.totalActions,
                    total: row.totalGasLyx,
                    count: row.totalActions,
                  };
                }
              }

              // Health status
              const healthColor = runwayDays < 0 ? COLORS.textMuted : runwayDays > 30 ? COLORS.accent : runwayDays > 7 ? COLORS.orange : COLORS.red;
              const healthLabel = runwayDays < 0 ? "No activity yet" : runwayDays > 30 ? "Healthy" : runwayDays > 7 ? "Monitor" : "Critical";
              const healthEmoji = runwayDays < 0 ? "⏳" : runwayDays > 30 ? "✅" : runwayDays > 7 ? "⚠️" : "🚨";

              return (<>
              <div style={{
                padding: "16px 18px", borderRadius: 12,
                background: COLORS.surface, border: `1px solid ${healthColor}20`,
                marginBottom: 14,
              }}>
                {/* Health status bar */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 16 }}>{healthEmoji}</span>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: healthColor, fontFamily: "'Syne', sans-serif" }}>
                        {healthLabel}
                      </div>
                      <div style={{ fontSize: 9, color: COLORS.textMuted }}>
                        Relayer status
                      </div>
                    </div>
                  </div>
                  {lastRefresh && (
                    <div style={{ fontSize: 8, color: COLORS.textMuted, fontFamily: "'JetBrains Mono', monospace" }}>
                      Updated {lastRefresh.toLocaleTimeString()}
                    </div>
                  )}
                </div>

                {/* Key metrics row */}
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
                  <div style={{ flex: "1 1 100px", padding: "10px 12px", borderRadius: 8, background: COLORS.bgAlt, border: `1px solid ${COLORS.border}`, textAlign: "center" }}>
                    <div style={{ fontSize: 9, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "1px", marginBottom: 4, fontFamily: "'JetBrains Mono', monospace" }}>Burn Rate</div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: COLORS.orange, fontFamily: "'Syne', sans-serif" }}>
                      {dailyBurnRate > 0 ? formatGas(dailyBurnRate) : "0"}
                    </div>
                    <div style={{ fontSize: 8, color: COLORS.textMuted }}>per day (7d avg)</div>
                  </div>
                  <div style={{ flex: "1 1 100px", padding: "10px 12px", borderRadius: 8, background: COLORS.bgAlt, border: `1px solid ${healthColor}15`, textAlign: "center" }}>
                    <div style={{ fontSize: 9, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "1px", marginBottom: 4, fontFamily: "'JetBrains Mono', monospace" }}>Runway</div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: healthColor, fontFamily: "'Syne', sans-serif" }}>
                      {runwayDays < 0 ? "—" : runwayDays >= 999 ? "∞" : `${runwayDays}d`}
                    </div>
                    <div style={{ fontSize: 8, color: COLORS.textMuted }}>{runwayDays < 0 ? "no gas data yet" : "at current burn rate"}</div>
                  </div>
                  <div style={{ flex: "1 1 100px", padding: "10px 12px", borderRadius: 8, background: COLORS.bgAlt, border: `1px solid ${COLORS.border}`, textAlign: "center" }}>
                    <div style={{ fontSize: 9, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "1px", marginBottom: 4, fontFamily: "'JetBrains Mono', monospace" }}>Avg Cost</div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: COLORS.text, fontFamily: "'Syne', sans-serif" }}>
                      {totalActions > 0 ? formatGas(totalGasSpent / totalActions) : "0"}
                    </div>
                    <div style={{ fontSize: 8, color: COLORS.textMuted }}>per action</div>
                  </div>
                  <div style={{ flex: "1 1 100px", padding: "10px 12px", borderRadius: 8, background: COLORS.bgAlt, border: `1px solid ${netRevenue >= 0 ? COLORS.accent : COLORS.red}15`, textAlign: "center" }}>
                    <div style={{ fontSize: 9, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "1px", marginBottom: 4, fontFamily: "'JetBrains Mono', monospace" }}>Profit</div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: netRevenue >= 0 ? COLORS.accent : COLORS.red, fontFamily: "'Syne', sans-serif" }}>
                      {netRevenue >= 0 ? "+" : ""}{formatGas(netRevenue)}
                    </div>
                    <div style={{ fontSize: 8, color: COLORS.textMuted }}>net (fees - gas)</div>
                  </div>
                </div>

                {/* Per-action profitability */}
                {Object.keys(actionCosts).length > 0 && (
                  <div>
                    <div style={{ fontSize: 9, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "1px", marginBottom: 8, fontFamily: "'JetBrains Mono', monospace" }}>
                      Cost per Action Type
                    </div>
                    {Object.entries(actionCosts).map(([type, data]) => {
                      const revenue = type === "tip" ? data.count * 0.50 : 0;
                      const profit = revenue - data.total;
                      const actionColor = type === "tip" ? COLORS.accent : type === "follow" ? COLORS.purple : COLORS.blue;
                      return (
                        <div key={type} style={{
                          display: "flex", alignItems: "center", justifyContent: "space-between",
                          padding: "6px 0",
                          borderBottom: `1px solid ${COLORS.border}`,
                        }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{
                              fontSize: 8, fontWeight: 700, textTransform: "uppercase",
                              padding: "2px 6px", borderRadius: 3,
                              background: `${actionColor}15`, color: actionColor,
                              fontFamily: "'JetBrains Mono', monospace",
                            }}>{type}</span>
                            <span style={{ fontSize: 10, color: COLORS.textSecondary }}>{data.count} actions</span>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                            <span style={{ fontSize: 9, color: COLORS.textMuted, fontFamily: "'JetBrains Mono', monospace" }}>
                              avg {formatGas(data.avgCost)}
                            </span>
                            {type === "tip" ? (
                              <span style={{ fontSize: 9, fontWeight: 700, color: profit >= 0 ? COLORS.accent : COLORS.red, fontFamily: "'JetBrains Mono', monospace" }}>
                                {profit >= 0 ? "+" : ""}{formatGas(profit)}
                              </span>
                            ) : (
                              <span style={{ fontSize: 9, fontWeight: 700, color: COLORS.red, fontFamily: "'JetBrains Mono', monospace" }}>
                                -{formatGas(data.total)}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    <div style={{ fontSize: 8, color: COLORS.textMuted, marginTop: 6 }}>
                      Tips generate 0.50 LYX fee each. Follows and polls are free (relayer pays gas).
                    </div>
                  </div>
                )}
              </div>
              </>);
            })()}

            {/* ---- GAS ALERTS ---- */}
            {gasAlerts && gasAlerts.length > 0 && (
              <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 6 }}>
                {gasAlerts.map((alert, i) => (
                  <div key={i} style={{
                    padding: "10px 14px", borderRadius: 8,
                    background: alert.severity === "critical" ? `${COLORS.red}08` : `${COLORS.orange}08`,
                    border: `1px solid ${alert.severity === "critical" ? COLORS.red + "20" : COLORS.orange + "20"}`,
                    display: "flex", alignItems: "center", gap: 8,
                  }}>
                    <span style={{ fontSize: 14 }}>{alert.severity === "critical" ? "🔴" : "🟡"}</span>
                    <div style={{ fontSize: 10, color: alert.severity === "critical" ? COLORS.red : COLORS.orange, fontFamily: "'JetBrains Mono', monospace" }}>
                      {alert.message}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* ---- GAS ANALYTICS ---- */}
            <SectionHeader title="Gas Spend Analytics" icon="⛽" />

            {/* Net revenue card */}
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 14 }}>
              <StatCard
                label="Net Revenue"
                value={analytics && gasAnalytics ? formatGas(analytics.fees - gasAnalytics.totals.totalGasLyx) : null}
                sub="Fees collected minus gas spent"
                color={analytics && gasAnalytics && (analytics.fees - gasAnalytics.totals.totalGasLyx) > 0 ? COLORS.accent : COLORS.red}
                icon="📈"
                large
              />
              <StatCard
                label="Total Gas Spent"
                value={gasAnalytics ? formatGas(gasAnalytics.totals.totalGasLyx) : null}
                sub="All relay transactions"
                color={COLORS.orange}
                icon="🔥"
                large
              />
            </div>

            {/* Gas by action type — 24h */}
            <div style={{
              padding: "14px 18px", borderRadius: 12,
              background: COLORS.surface,
              border: `1px solid ${COLORS.border}`,
              marginBottom: 14,
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.textSecondary, marginBottom: 12, fontFamily: "'Syne', sans-serif" }}>
                Last 24 Hours
              </div>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                {(gasAnalytics?.last24h || []).map((row, i) => (
                  <div key={i} style={{
                    flex: "1 1 120px", padding: "10px 14px", borderRadius: 8,
                    background: COLORS.bgAlt, border: `1px solid ${COLORS.border}`,
                    textAlign: "center",
                  }}>
                    <div style={{
                      fontSize: 9, fontWeight: 700, textTransform: "uppercase",
                      color: row.actionType === "tip" ? COLORS.accent : row.actionType === "follow" ? COLORS.purple : COLORS.blue,
                      fontFamily: "'JetBrains Mono', monospace", letterSpacing: "1px",
                      marginBottom: 6,
                    }}>{row.actionType}</div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: COLORS.text, fontFamily: "'Syne', sans-serif" }}>
                      {row.actions}
                    </div>
                    <div style={{ fontSize: 9, color: COLORS.textMuted, marginTop: 2, fontFamily: "'JetBrains Mono', monospace" }}>
                      {formatGas(row.gasLyx)} gas
                    </div>
                  </div>
                ))}
                {(!gasAnalytics?.last24h || gasAnalytics.last24h.length === 0) && (
                  <div style={{ fontSize: 11, color: COLORS.textMuted, padding: 10 }}>No relay activity in the last 24 hours</div>
                )}
              </div>
            </div>

            {/* All-time breakdown */}
            <div style={{
              padding: "14px 18px", borderRadius: 12,
              background: COLORS.surface,
              border: `1px solid ${COLORS.border}`,
              marginBottom: 14,
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.textSecondary, marginBottom: 10, fontFamily: "'Syne', sans-serif" }}>
                All-Time Breakdown
              </div>
              {(gasAnalytics?.allTime || []).map((row, i) => (
                <div key={i} style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "8px 0",
                  borderBottom: i < (gasAnalytics?.allTime?.length || 0) - 1 ? `1px solid ${COLORS.border}` : "none",
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{
                      fontSize: 8, fontWeight: 700, textTransform: "uppercase",
                      padding: "2px 6px", borderRadius: 3,
                      background: row.actionType === "tip" ? `${COLORS.accent}15` : row.actionType === "follow" ? `${COLORS.purple}15` : `${COLORS.blue}15`,
                      color: row.actionType === "tip" ? COLORS.accent : row.actionType === "follow" ? COLORS.purple : COLORS.blue,
                      fontFamily: "'JetBrains Mono', monospace",
                    }}>{row.actionType}</span>
                    <span style={{ fontSize: 11, color: COLORS.text, fontWeight: 600 }}>
                      {row.totalActions} actions
                    </span>
                    {row.failed > 0 && (
                      <span style={{ fontSize: 9, color: COLORS.red, fontFamily: "'JetBrains Mono', monospace" }}>
                        ({row.failed} failed)
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.orange, fontFamily: "'JetBrains Mono', monospace" }}>
                    {formatGas(row.totalGasLyx)}
                  </div>
                </div>
              ))}
              {(!gasAnalytics?.allTime || gasAnalytics.allTime.length === 0) && (
                <div style={{ fontSize: 11, color: COLORS.textMuted, padding: 10 }}>No relay data yet — gas logging starts on next relay action</div>
              )}
              {gasAnalytics?.totals && (
                <div style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  paddingTop: 10, marginTop: 6, borderTop: `1px solid ${COLORS.border}`,
                }}>
                  <span style={{ fontSize: 10, color: COLORS.textMuted }}>{gasAnalytics.totals.uniqueUPs} unique UPs · {gasAnalytics.totals.totalActions} total actions</span>
                  <span style={{ fontSize: 12, fontWeight: 800, color: COLORS.text, fontFamily: "'Syne', sans-serif" }}>
                    {formatGas(gasAnalytics.totals.totalGasLyx)} total
                  </span>
                </div>
              )}
            </div>

            {/* Top Gas Consumers (24h) */}
            {gasConsumers && gasConsumers.length > 0 && (
            <div style={{
              padding: "14px 18px", borderRadius: 12,
              background: COLORS.surface,
              border: `1px solid ${COLORS.border}`,
              marginBottom: 14,
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.textSecondary, marginBottom: 10, fontFamily: "'Syne', sans-serif" }}>
                Top Gas Consumers · 24h
              </div>
              {gasConsumers.slice(0, 10).map((c, i) => {
                const profile = adminProfiles[(c.upAddress || "").toLowerCase()] || {};
                const displayName = profile.name || `${c.upAddress.slice(0, 8)}...${c.upAddress.slice(-4)}`;
                const avatar = profile.avatar;
                return (
                <div key={i} style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "8px 0",
                  borderBottom: i < Math.min(gasConsumers.length, 10) - 1 ? `1px solid ${COLORS.border}` : "none",
                  gap: 10,
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0, flex: 1 }}>
                    <span style={{
                      fontSize: 10, fontWeight: 800, color: i < 3 ? COLORS.red : COLORS.textMuted,
                      fontFamily: "'JetBrains Mono', monospace", width: 16, flexShrink: 0,
                    }}>#{i + 1}</span>
                    {avatar ? (
                      <img
                        src={avatar}
                        alt=""
                        style={{ width: 24, height: 24, borderRadius: "50%", objectFit: "cover", flexShrink: 0, border: `1px solid ${COLORS.border}` }}
                        onError={e => { e.target.onerror = null; e.target.style.display = "none"; e.target.nextSibling && (e.target.nextSibling.style.display = "flex"); }}
                      />
                    ) : null}
                    <div style={{
                      width: 24, height: 24, borderRadius: "50%",
                      background: `${COLORS.accent}15`, border: `1px solid ${COLORS.accent}25`,
                      flexShrink: 0,
                      display: avatar ? "none" : "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 11,
                    }}>👤</div>
                    <div style={{ display: "flex", flexDirection: "column", minWidth: 0, flex: 1 }}>
                      <span style={{
                        fontSize: 11, fontWeight: 600, color: COLORS.text,
                        fontFamily: profile.name ? "'Outfit', sans-serif" : "'JetBrains Mono', monospace",
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                      }}>{displayName}</span>
                      {profile.name && (
                        <span style={{
                          fontSize: 9, color: COLORS.textMuted,
                          fontFamily: "'JetBrains Mono', monospace",
                          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        }}>{c.upAddress.slice(0, 8)}...{c.upAddress.slice(-4)}</span>
                      )}
                      <span style={{ fontSize: 9, color: COLORS.textMuted, marginTop: 1 }}>
                        {c.followCount}F · {c.tipCount}T · {c.pollCount}P
                      </span>
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                    <span style={{ fontSize: 10, color: COLORS.textMuted }}>{c.actionCount} actions</span>
                    <span style={{
                      fontSize: 11, fontWeight: 700,
                      color: c.totalGasLyx > 0.1 ? COLORS.red : COLORS.orange,
                      fontFamily: "'JetBrains Mono', monospace",
                    }}>{formatGas(c.totalGasLyx)}</span>
                  </div>
                </div>
                );
              })}
            </div>
            )}

            {/* Blocked Addresses (anomaly soft-blocks) */}
            {blockedAddrs && (
            <div style={{
              padding: "14px 18px", borderRadius: 12,
              background: COLORS.surface,
              border: `1px solid ${blockedAddrs.length > 0 ? COLORS.red + "40" : COLORS.border}`,
              marginBottom: 14,
            }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.textSecondary, fontFamily: "'Syne', sans-serif" }}>
                  🛑 Soft-Blocked Addresses {blockedAddrs.length > 0 && `· ${blockedAddrs.length}`}
                </div>
                <span style={{ fontSize: 9, color: COLORS.textMuted }}>Anomaly detection</span>
              </div>
              {blockedAddrs.length === 0 ? (
                <div style={{ fontSize: 11, color: COLORS.textMuted, padding: "4px 0" }}>
                  No active blocks — system clean
                </div>
              ) : (
                blockedAddrs.map((b, i) => {
                  const profile = adminProfiles[(b.address || "").toLowerCase()] || {};
                  const displayName = profile.name || `${b.address.slice(0, 10)}...${b.address.slice(-6)}`;
                  const avatar = profile.avatar;
                  return (
                  <div key={b.address} style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "8px 0",
                    borderBottom: i < blockedAddrs.length - 1 ? `1px solid ${COLORS.border}` : "none",
                    gap: 10,
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0, flex: 1 }}>
                      {avatar ? (
                        <img
                          src={avatar}
                          alt=""
                          style={{ width: 24, height: 24, borderRadius: "50%", objectFit: "cover", flexShrink: 0, border: `1px solid ${COLORS.red}40` }}
                          onError={e => { e.target.onerror = null; e.target.style.display = "none"; e.target.nextSibling && (e.target.nextSibling.style.display = "flex"); }}
                        />
                      ) : null}
                      <div style={{
                        width: 24, height: 24, borderRadius: "50%",
                        background: `${COLORS.red}15`, border: `1px solid ${COLORS.red}25`,
                        flexShrink: 0,
                        display: avatar ? "none" : "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 11,
                      }}>🛑</div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                        <span style={{
                          fontSize: 11, color: COLORS.text,
                          fontFamily: profile.name ? "'Outfit', sans-serif" : "'JetBrains Mono', monospace",
                          fontWeight: 600,
                          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        }}>{displayName}</span>
                        {profile.name && (
                          <span style={{
                            fontSize: 9, color: COLORS.textMuted,
                            fontFamily: "'JetBrains Mono', monospace",
                          }}>{b.address.slice(0, 10)}...{b.address.slice(-6)}</span>
                        )}
                        <span style={{ fontSize: 9, color: COLORS.textMuted }}>
                          Reason: <span style={{ color: COLORS.red, fontWeight: 600 }}>{b.reason}</span>
                        </span>
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
                      <span style={{ fontSize: 10, color: COLORS.orange, fontFamily: "'JetBrains Mono', monospace" }}>
                        {b.minutesRemaining}m left
                      </span>
                      <button
                        onClick={() => unblockAddress(b.address)}
                        style={{
                          fontSize: 10, padding: "4px 10px", borderRadius: 6,
                          background: "transparent", color: COLORS.accent,
                          border: `1px solid ${COLORS.accent}40`, cursor: "pointer",
                          fontFamily: "'Syne', sans-serif", fontWeight: 700,
                        }}
                        title="Manually remove this block"
                      >
                        Unblock
                      </button>
                    </div>
                  </div>
                  );
                })
              )}
            </div>
            )}

            {/* ---- TIP ANALYTICS ---- */}
            <SectionHeader title="Tip Analytics" icon="📊" />
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
              <StatCard
                label="Total Volume"
                value={analytics ? `${analytics.volume.toFixed(2)} LYX` : null}
                sub="All tips through contract"
                color={COLORS.accent}
                icon="💸"
              />
              <StatCard
                label="Total Tips"
                value={analytics ? analytics.count.toLocaleString() : null}
                sub="Transactions processed"
                color={COLORS.blue}
                icon="📝"
              />
              <StatCard
                label="Avg Tip Size"
                value={analytics && analytics.count > 0
                  ? `${(analytics.volume / analytics.count).toFixed(2)} LYX`
                  : analytics ? "0 LYX" : null}
                sub="Volume ÷ count"
                color={COLORS.purple}
                icon="📈"
              />
            </div>

            {/* ---- PLATFORM METRICS ---- */}
            <SectionHeader title="Platform Metrics" icon="👥" />
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
              <StatCard
                label="Unique Tippers"
                value={dbStats ? parseInt(dbStats.tips?.unique_tippers || 0).toLocaleString() : platformMetrics ? platformMetrics.uniqueTippers.toLocaleString() : null}
                sub="Distinct wallets that tipped"
                color={COLORS.accent}
                icon="🧑‍💻"
              />
              <StatCard
                label="Unique Creators"
                value={dbStats ? parseInt(dbStats.tips?.unique_creators || 0).toLocaleString() : platformMetrics ? platformMetrics.uniqueCreators.toLocaleString() : null}
                sub="Creators who received tips"
                color={COLORS.cyan}
                icon="💎"
              />
              <StatCard
                label="Total Follows"
                value={dbStats ? parseInt(dbStats.follows?.total_follows || 0).toLocaleString() : platformMetrics ? platformMetrics.totalFollows.toLocaleString() : null}
                sub="Indexed from FollowRegistry"
                color={COLORS.purple}
                icon="💜"
              />
              <StatCard
                label="Total Votes"
                value={dbStats ? parseInt(dbStats.votes?.total_votes || 0).toLocaleString() : "0"}
                sub="Indexed from PollVoting"
                color={COLORS.blue}
                icon="🗳️"
              />
            </div>

            {/* Today's activity */}
            {dbStats && (
              <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 14 }}>
                <StatCard
                  label="Tips Today"
                  value={parseInt(dbStats.today?.tips_today || 0).toLocaleString()}
                  sub={`${parseFloat(dbStats.today?.volume_today || 0).toFixed(2)} LYX volume`}
                  color={COLORS.gold}
                  icon="📅"
                />
                <StatCard
                  label="Tips This Week"
                  value={parseInt(dbStats.week?.tips_week || 0).toLocaleString()}
                  sub={`${parseFloat(dbStats.week?.volume_week || 0).toFixed(2)} LYX volume`}
                  color={COLORS.orange}
                  icon="📆"
                />
                <StatCard
                  label="Biggest Tip"
                  value={dbStats.tips?.biggest_tip ? `${parseFloat(dbStats.tips.biggest_tip).toFixed(2)} LYX` : "0 LYX"}
                  sub="All-time largest single tip"
                  color={COLORS.gold}
                  icon="🐋"
                />
              </div>
            )}

            {/* ---- EVENT INDEXER ---- */}
            <SectionHeader title="Event Indexer" icon="⛓️" right={
              <button onClick={runIndexerSync} disabled={indexerSyncing} style={{
                padding: "7px 18px", borderRadius: 7,
                background: indexerSyncing ? COLORS.surface : `${COLORS.cyan}10`,
                border: `1px solid ${indexerSyncing ? COLORS.border : COLORS.cyan + "25"}`,
                color: indexerSyncing ? COLORS.textMuted : COLORS.cyan,
                fontSize: 11, fontWeight: 700, cursor: indexerSyncing ? "wait" : "pointer",
                fontFamily: "'Syne', sans-serif",
                transition: "all 0.2s",
                display: "flex", alignItems: "center", gap: 6,
              }}
              onMouseEnter={e => { if (!indexerSyncing) e.currentTarget.style.background = `${COLORS.cyan}18`; }}
              onMouseLeave={e => { if (!indexerSyncing) e.currentTarget.style.background = `${COLORS.cyan}10`; }}
              >
                {indexerSyncing ? "⏳ Syncing..." : "⚡ Sync Now"}
              </button>
            } />
            <div style={{
              padding: "4px 20px 12px", borderRadius: 14,
              background: COLORS.surface,
              border: `1px solid ${COLORS.border}`,
            }}>
              {/* Sync state per contract */}
              {indexerStatus?.sync?.map((s, i) => (
                <div key={s.contract} style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "12px 0",
                  borderBottom: i < indexerStatus.sync.length - 1 ? `1px solid ${COLORS.border}` : "none",
                }}>
                  <div style={{
                    fontSize: 12, color: COLORS.textSecondary,
                    fontFamily: "'Outfit', sans-serif",
                    display: "flex", alignItems: "center", gap: 8,
                  }}>
                    <div style={{
                      width: 8, height: 8, borderRadius: "50%",
                      background: parseInt(s.last_block) > 0 ? COLORS.accent : COLORS.textMuted,
                      boxShadow: parseInt(s.last_block) > 0 ? `0 0 6px ${COLORS.accent}60` : "none",
                    }} />
                    {s.contract.replace("_", " ").replace(/\b\w/g, c => c.toUpperCase())}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                    <div style={{
                      fontSize: 11, color: COLORS.textMuted,
                      fontFamily: "'JetBrains Mono', monospace",
                    }}>
                      Block {parseInt(s.last_block).toLocaleString()}
                    </div>
                    <div style={{
                      fontSize: 10, color: COLORS.textMuted,
                    }}>
                      {s.last_synced ? new Date(s.last_synced).toLocaleTimeString() : "Never"}
                    </div>
                  </div>
                </div>
              ))}

              {/* DB record counts */}
              {indexerStatus?.counts && (
                <div style={{
                  display: "flex", gap: 16, paddingTop: 12, marginTop: 4,
                  borderTop: `1px solid ${COLORS.border}`,
                }}>
                  {[
                    { label: "Tips", count: indexerStatus.counts.tips, color: COLORS.accent },
                    { label: "Follows", count: indexerStatus.counts.follows, color: COLORS.purple },
                    { label: "Votes", count: indexerStatus.counts.votes, color: COLORS.blue },
                  ].map(({ label, count, color }) => (
                    <div key={label} style={{
                      fontSize: 11, color: COLORS.textSecondary,
                      fontFamily: "'Outfit', sans-serif",
                      display: "flex", alignItems: "center", gap: 6,
                    }}>
                      <span style={{ fontSize: 14, fontWeight: 800, color, fontFamily: "'Syne', sans-serif" }}>
                        {count}
                      </span>
                      {label} indexed
                    </div>
                  ))}
                </div>
              )}

              {/* Last sync result */}
              {indexerResult && (
                <div style={{
                  marginTop: 12, padding: "10px 14px", borderRadius: 8,
                  background: indexerResult.error ? `${COLORS.red}06` : `${COLORS.accent}06`,
                  border: `1px solid ${indexerResult.error ? COLORS.red + "15" : COLORS.accent + "15"}`,
                  fontSize: 11,
                  fontFamily: "'JetBrains Mono', monospace",
                  color: indexerResult.error ? COLORS.red : COLORS.textSecondary,
                }}>
                  {indexerResult.error
                    ? `Error: ${indexerResult.error}`
                    : `✓ Synced in ${indexerResult.runtime} — ${indexerResult.synced?.tips || 0} tips, ${indexerResult.synced?.follows || 0} follows, ${indexerResult.synced?.votes || 0} votes (${indexerResult.synced?.chunksProcessed || 0} chunks)`
                  }
                  {indexerResult.synced?.errors?.length > 0 && (
                    <div style={{ marginTop: 4, fontSize: 10, color: COLORS.orange }}>
                      Warnings: {indexerResult.synced.errors.join("; ")}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ---- FEE CONFIG (read-only display) ---- */}
            <SectionHeader title="Fee Configuration" icon="⚙️" />
            <div style={{
              padding: "4px 20px 12px", borderRadius: 14,
              background: COLORS.surface,
              border: `1px solid ${COLORS.border}`,
            }}>
              <InfoRow
                label="Fee Percentage"
                value={feeConfig ? `${(feeConfig.percentage / 100).toFixed(1)}% (${feeConfig.percentage} bps)` : "—"}
                color={COLORS.text}
              />
              <InfoRow
                label="Fee Cap"
                value={feeConfig ? `${feeConfig.cap} LYX` : "—"}
                color={COLORS.text}
              />
              <InfoRow
                label="Minimum Tip"
                value={feeConfig ? (feeConfig.minTip > 0 ? `${feeConfig.minTip} LYX` : "No minimum") : "—"}
                color={COLORS.text}
              />
              <InfoRow
                label="Relayer"
                value={feeConfig?.relayerEnabled ? "Enabled" : "Disabled"}
                color={feeConfig?.relayerEnabled ? COLORS.accent : COLORS.red}
              />
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "12px 0",
              }}>
                <div style={{ fontSize: 12, color: COLORS.textSecondary, fontFamily: "'Outfit', sans-serif" }}>Contract Status</div>
                <div style={{
                  fontSize: 13, fontWeight: 600,
                  color: feeConfig?.paused ? COLORS.red : COLORS.accent,
                  fontFamily: "'Outfit', sans-serif",
                }}>{feeConfig?.paused ? "Paused" : "Active"}</div>
              </div>
            </div>

            {/* ---- CONTROLS (collapsible) ---- */}
            <SectionHeader title="Contract Controls" icon="🎛️" />
            <div style={{
              padding: 24, borderRadius: 14,
              background: COLORS.surface,
              border: `1px solid ${COLORS.border}`,
              display: "flex", flexDirection: "column", gap: 20,
            }}>
              {/* Fee parameter update */}
              <div>
                <div style={{
                  fontSize: 12, fontWeight: 700, color: COLORS.text, marginBottom: 12,
                  fontFamily: "'Syne', sans-serif",
                }}>Update Fee Parameters</div>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
                  <div style={{ flex: 1, minWidth: 140 }}>
                    <label style={{
                      fontSize: 10, color: COLORS.textMuted, display: "block", marginBottom: 6,
                      fontFamily: "'Outfit', sans-serif",
                    }}>Basis Points (100 = 1%)</label>
                    <input
                      type="number"
                      value={newFeePercentage}
                      onChange={e => setNewFeePercentage(e.target.value)}
                      placeholder={feeConfig ? feeConfig.percentage.toString() : "100"}
                      style={{
                        width: "100%", padding: "10px 12px",
                        background: COLORS.bgAlt, border: `1px solid ${COLORS.border}`,
                        borderRadius: 8, color: COLORS.text,
                        fontFamily: "'JetBrains Mono', monospace", fontSize: 13,
                        outline: "none", boxSizing: "border-box",
                        transition: "border-color 0.2s",
                      }}
                      onFocus={e => e.target.style.borderColor = COLORS.accent + "40"}
                      onBlur={e => e.target.style.borderColor = COLORS.border}
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: 140 }}>
                    <label style={{
                      fontSize: 10, color: COLORS.textMuted, display: "block", marginBottom: 6,
                      fontFamily: "'Outfit', sans-serif",
                    }}>Fee Cap (LYX)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={newFeeCap}
                      onChange={e => setNewFeeCap(e.target.value)}
                      placeholder={feeConfig ? feeConfig.cap.toString() : "0.50"}
                      style={{
                        width: "100%", padding: "10px 12px",
                        background: COLORS.bgAlt, border: `1px solid ${COLORS.border}`,
                        borderRadius: 8, color: COLORS.text,
                        fontFamily: "'JetBrains Mono', monospace", fontSize: 13,
                        outline: "none", boxSizing: "border-box",
                        transition: "border-color 0.2s",
                      }}
                      onFocus={e => e.target.style.borderColor = COLORS.accent + "40"}
                      onBlur={e => e.target.style.borderColor = COLORS.border}
                    />
                  </div>
                  <button onClick={updateFeeParams} disabled={!newFeePercentage || !newFeeCap} style={{
                    padding: "10px 24px", borderRadius: 8, border: "none",
                    background: newFeePercentage && newFeeCap
                      ? `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.cyan})`
                      : COLORS.bgAlt,
                    color: newFeePercentage && newFeeCap ? COLORS.bg : COLORS.textMuted,
                    fontFamily: "'Syne', sans-serif", fontSize: 12, fontWeight: 700,
                    cursor: newFeePercentage && newFeeCap ? "pointer" : "not-allowed",
                    transition: "all 0.2s",
                    whiteSpace: "nowrap",
                  }}>Update Fees</button>
                </div>
              </div>

              {/* Divider */}
              <div style={{ borderTop: `1px solid ${COLORS.border}` }} />

              {/* Toggle buttons */}
              <div>
                <div style={{
                  fontSize: 12, fontWeight: 700, color: COLORS.text, marginBottom: 12,
                  fontFamily: "'Syne', sans-serif",
                }}>System Toggles</div>
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                  <button onClick={toggleRelayer} style={{
                    padding: "12px 24px", borderRadius: 10,
                    background: feeConfig?.relayerEnabled ? `${COLORS.orange}08` : `${COLORS.accent}08`,
                    border: `1px solid ${feeConfig?.relayerEnabled ? COLORS.orange + "25" : COLORS.accent + "25"}`,
                    color: feeConfig?.relayerEnabled ? COLORS.orange : COLORS.accent,
                    fontFamily: "'Syne', sans-serif", fontSize: 12, fontWeight: 700,
                    cursor: "pointer", transition: "all 0.2s",
                    display: "flex", alignItems: "center", gap: 8,
                  }}
                  onMouseEnter={e => e.currentTarget.style.transform = "translateY(-1px)"}
                  onMouseLeave={e => e.currentTarget.style.transform = "translateY(0)"}
                  >
                    {feeConfig?.relayerEnabled ? "⏸ Disable Relayer" : "▶ Enable Relayer"}
                  </button>

                  <button onClick={togglePause} style={{
                    padding: "12px 24px", borderRadius: 10,
                    background: feeConfig?.paused ? `${COLORS.accent}08` : `${COLORS.red}08`,
                    border: `1px solid ${feeConfig?.paused ? COLORS.accent + "25" : COLORS.red + "25"}`,
                    color: feeConfig?.paused ? COLORS.accent : COLORS.red,
                    fontFamily: "'Syne', sans-serif", fontSize: 12, fontWeight: 700,
                    cursor: "pointer", transition: "all 0.2s",
                    display: "flex", alignItems: "center", gap: 8,
                  }}
                  onMouseEnter={e => e.currentTarget.style.transform = "translateY(-1px)"}
                  onMouseLeave={e => e.currentTarget.style.transform = "translateY(0)"}
                  >
                    {feeConfig?.paused ? "▶ Unpause Contract" : "⏸ Pause Contract"}
                  </button>
                </div>
              </div>

              {/* Status / error messages */}
              {controlStatus && (
                <div style={{
                  padding: "10px 16px", borderRadius: 8,
                  background: `${COLORS.accent}06`, border: `1px solid ${COLORS.accent}15`,
                  fontSize: 11, color: COLORS.accent,
                  fontFamily: "'JetBrains Mono', monospace",
                }}>✓ {controlStatus}</div>
              )}
              {controlError && (
                <div style={{
                  padding: "10px 16px", borderRadius: 8,
                  background: `${COLORS.red}06`, border: `1px solid ${COLORS.red}15`,
                  fontSize: 11, color: COLORS.red,
                  fontFamily: "'JetBrains Mono', monospace",
                }}>✕ {controlError}</div>
              )}
            </div>

            {/* ---- CONTRACT ADDRESSES ---- */}
            <SectionHeader title="Contract Addresses" icon="📋" />
            <div style={{
              padding: "4px 20px 4px", borderRadius: 14,
              background: COLORS.surface,
              border: `1px solid ${COLORS.border}`,
            }}>
              {[
                { label: "TipRouter V2", addr: TIPROUTER_ADDRESS, color: COLORS.accent },
                { label: "PollVoting", addr: "0xA8A466Ab3096cbe88285d9B712d2562ef7297ACe", color: COLORS.blue },
                { label: "LSP26 Followers", addr: "0xf01103E5a9909Fc0DBe8166dA7085e0285daDDcA", color: COLORS.purple },
                { label: "Relayer EOA", addr: FULLSEND_RELAYER, color: COLORS.accent, note: "pays gas" },
                { label: "Treasury UP", addr: CREATOR_TREASURY_UP, color: COLORS.gold, note: "receives fees" },
                { label: "Deployer EOA", addr: TREASURY_ADDRESS, color: COLORS.textMuted, note: "contract owner" },
              ].map(({ label, addr: a, color, note }, idx, arr) => (
                <div key={label} style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "14px 0",
                  borderBottom: idx < arr.length - 1 ? `1px solid ${COLORS.border}` : "none",
                  gap: 12,
                }}>
                  <div style={{
                    minWidth: 130, flexShrink: 0,
                  }}>
                    <div style={{
                      fontSize: 12, fontWeight: 600, color: color,
                      fontFamily: "'Outfit', sans-serif",
                    }}>{label}</div>
                    {note && (
                      <div style={{
                        fontSize: 9, color: COLORS.textMuted,
                        fontFamily: "'JetBrains Mono', monospace",
                        letterSpacing: "0.3px", marginTop: 2,
                      }}>{note}</div>
                    )}
                  </div>
                  <div style={{
                    fontSize: 11, color: COLORS.textMuted,
                    fontFamily: "'JetBrains Mono', monospace",
                    flex: 1, textAlign: "right",
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}>{a}</div>
                  <button onClick={() => { navigator.clipboard.writeText(a); }} style={{
                    marginLeft: 4, padding: "5px 12px", borderRadius: 6,
                    background: `${color}08`, border: `1px solid ${color}15`,
                    color: color, fontSize: 10, fontWeight: 600, cursor: "pointer",
                    fontFamily: "'JetBrains Mono', monospace",
                    transition: "all 0.2s", flexShrink: 0,
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = `${color}15`}
                  onMouseLeave={e => e.currentTarget.style.background = `${color}08`}
                  >Copy</button>
                </div>
              ))}
            </div>

            </>)}

            {/* ===== ANALYTICS TAB ===== */}
            {adminTab === "analytics" && (<>
              <div style={{ fontFamily: "'Syne', sans-serif", fontSize: 18, fontWeight: 800, color: COLORS.text, marginBottom: 16, display: "flex", alignItems: "center", gap: 10, marginTop: 24 }}>
                📈 Site Analytics
              </div>

              {!siteAnalytics ? (
                <div style={{ textAlign: "center", padding: 40, color: COLORS.textMuted, fontSize: 12 }}>Loading analytics...</div>
              ) : (<>

                {/* Growth Overview */}
                <SectionHeader title="Growth Overview" icon="🌱" />
                <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
                  <StatCard
                    label="Connected UPs"
                    value={(siteAnalytics.stats?.creators?.connected || 0).toLocaleString()}
                    sub="Total profiles connected"
                    color={COLORS.accent}
                    icon="👤"
                  />
                  <StatCard
                    label="Created Widget"
                    value={(siteAnalytics.stats?.creators?.configured || 0).toLocaleString()}
                    sub="Set up their channel"
                    color={COLORS.cyan}
                    icon="🎬"
                  />
                  <StatCard
                    label="Active Streamers"
                    value={(siteAnalytics.stats?.creators?.active || 0).toLocaleString()}
                    sub="Received at least 1 tip"
                    color={COLORS.gold}
                    icon="📡"
                  />
                  <StatCard
                    label="Unique Tippers"
                    value={parseInt(siteAnalytics.stats?.tips?.unique_tippers || 0).toLocaleString()}
                    sub="Distinct wallets that tipped"
                    color={COLORS.purple}
                    icon="💸"
                  />
                  <StatCard
                    label="Total Follows"
                    value={parseInt(siteAnalytics.stats?.follows?.total_follows || 0).toLocaleString()}
                    sub="On-chain follows"
                    color={COLORS.blue}
                    icon="💜"
                  />
                  <StatCard
                    label="Total Votes"
                    value={parseInt(siteAnalytics.stats?.votes?.total_votes || 0).toLocaleString()}
                    sub="Poll votes cast"
                    color={COLORS.orange}
                    icon="🗳️"
                  />
                </div>

                {/* Activity */}
                {siteAnalytics.stats?.today && (
                  <>
                  <SectionHeader title="Activity" icon="⚡" />
                  <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
                    <StatCard label="Tips Today" value={parseInt(siteAnalytics.stats.today?.tips_today || 0).toLocaleString()} sub={`${parseFloat(siteAnalytics.stats.today?.volume_today || 0).toFixed(2)} LYX`} color={COLORS.gold} icon="📅" />
                    <StatCard label="Tips This Week" value={parseInt(siteAnalytics.stats.week?.tips_week || 0).toLocaleString()} sub={`${parseFloat(siteAnalytics.stats.week?.volume_week || 0).toFixed(2)} LYX`} color={COLORS.orange} icon="📆" />
                    <StatCard label="Biggest Tip" value={siteAnalytics.stats.tips?.biggest_tip ? `${parseFloat(siteAnalytics.stats.tips.biggest_tip).toFixed(2)} LYX` : "0 LYX"} sub="All-time" color={COLORS.gold} icon="🐋" />
                    <StatCard label="Avg Tip" value={parseInt(siteAnalytics.stats.tips?.total_tips || 0) > 0 ? `${(parseFloat(siteAnalytics.stats.tips?.total_volume || 0) / parseInt(siteAnalytics.stats.tips?.total_tips || 1)).toFixed(2)} LYX` : "0 LYX"} sub="Volume ÷ count" color={COLORS.purple} icon="📊" />
                  </div>
                  </>
                )}

                {/* Page Views */}
                {siteAnalytics.pageViews && siteAnalytics.pageViews.views && (
                  <>
                  <SectionHeader title="Tip Page Views" icon="👁️" />
                  <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
                    <StatCard label="Total Views" value={parseInt(siteAnalytics.pageViews.views.total || 0).toLocaleString()} sub="All tip pages" color={COLORS.accent} icon="👀" />
                    <StatCard label="Today" value={parseInt(siteAnalytics.pageViews.views.today || 0).toLocaleString()} sub="Views today" color={COLORS.cyan} icon="📅" />
                    <StatCard label="This Week" value={parseInt(siteAnalytics.pageViews.views.week || 0).toLocaleString()} sub="Views this week" color={COLORS.blue} icon="📆" />
                  </div>
                  {siteAnalytics.pageViews.topPages && siteAnalytics.pageViews.topPages.length > 0 && (
                    <div style={{ marginTop: 14, background: COLORS.surface, borderRadius: 10, border: `1px solid ${COLORS.border}`, overflow: "hidden" }}>
                      <div style={{ padding: "10px 14px", borderBottom: `1px solid ${COLORS.border}`, fontSize: 11, fontWeight: 700, color: COLORS.textMuted }}>Top Tip Pages</div>
                      {siteAnalytics.pageViews.topPages.slice(0, 10).map((p, i) => (
                        <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 14px", borderBottom: i < 9 ? `1px solid ${COLORS.border}` : "none" }}>
                          <span style={{ fontSize: 10, color: COLORS.textSecondary, fontFamily: "'JetBrains Mono', monospace" }}>{p.address ? `${p.address.slice(0, 8)}...${p.address.slice(-6)}` : "unknown"}</span>
                          <span style={{ fontSize: 11, fontWeight: 700, color: COLORS.accent, fontFamily: "'JetBrains Mono', monospace" }}>{parseInt(p.views || 0).toLocaleString()} views</span>
                        </div>
                      ))}
                    </div>
                  )}
                  </>
                )}

                {/* Top Creators */}
                <SectionHeader title="Top Creators by Volume" icon="🏆" />
                {siteAnalytics.topCreators.length > 0 ? (
                  <div style={{ background: COLORS.surface, borderRadius: 10, border: `1px solid ${COLORS.border}`, overflow: "hidden" }}>
                    {siteAnalytics.topCreators.map((c, i) => (
                      <div key={c.address} style={{
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                        padding: "10px 14px",
                        borderBottom: i < siteAnalytics.topCreators.length - 1 ? `1px solid ${COLORS.border}` : "none",
                        background: i < 3 ? `${COLORS.gold}04` : "transparent",
                      }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <span style={{ fontSize: 10, fontWeight: 800, color: i < 3 ? COLORS.gold : COLORS.textMuted, fontFamily: "'Syne', sans-serif", width: 20 }}>#{i + 1}</span>
                          <div>
                            <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.text }}>{c.name || `${c.address.slice(0, 8)}...`}</div>
                            <div style={{ fontSize: 9, color: COLORS.textMuted, fontFamily: "'JetBrains Mono', monospace" }}>
                              {parseInt(c.total_tips || 0)} tips · {parseInt(c.unique_tippers || 0)} tippers · {parseInt(c.follower_count || 0)} followers
                            </div>
                          </div>
                        </div>
                        <div style={{ fontSize: 12, fontWeight: 800, color: COLORS.accent, fontFamily: "'JetBrains Mono', monospace" }}>
                          {parseFloat(c.tip_volume || 0).toFixed(2)} LYX
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ textAlign: "center", padding: 20, color: COLORS.textMuted, fontSize: 11 }}>No creator data yet</div>
                )}

                {/* Top Tippers */}
                <SectionHeader title="Top Tippers" icon="💰" />
                {siteAnalytics.topTippers.length > 0 ? (
                  <div style={{ background: COLORS.surface, borderRadius: 10, border: `1px solid ${COLORS.border}`, overflow: "hidden" }}>
                    {siteAnalytics.topTippers.slice(0, 15).map((t, i) => (
                      <div key={t.tipper || i} style={{
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                        padding: "10px 14px",
                        borderBottom: i < Math.min(siteAnalytics.topTippers.length, 15) - 1 ? `1px solid ${COLORS.border}` : "none",
                        background: i < 3 ? `${COLORS.gold}04` : "transparent",
                      }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <span style={{ fontSize: 10, fontWeight: 800, color: i < 3 ? COLORS.gold : COLORS.textMuted, fontFamily: "'Syne', sans-serif", width: 20 }}>#{i + 1}</span>
                          <span style={{ fontSize: 10, color: COLORS.textSecondary, fontFamily: "'JetBrains Mono', monospace" }}>
                            {t.tipper ? `${t.tipper.slice(0, 8)}...${t.tipper.slice(-6)}` : "unknown"}
                          </span>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                          <span style={{ fontSize: 9, color: COLORS.textMuted }}>{parseInt(t.tip_count || 0)} tips</span>
                          <span style={{ fontSize: 12, fontWeight: 800, color: COLORS.accent, fontFamily: "'JetBrains Mono', monospace" }}>
                            {parseFloat(t.total_amount || 0).toFixed(2)} LYX
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ textAlign: "center", padding: 20, color: COLORS.textMuted, fontSize: 11 }}>No tipper data yet</div>
                )}

              </>)}
            </>)}

            {/* ===== POLLS TAB ===== */}
            {adminTab === "polls" && (<>
              <div style={{ fontFamily: "'Syne', sans-serif", fontSize: 18, fontWeight: 800, color: COLORS.text, marginBottom: 16, display: "flex", alignItems: "center", gap: 10 }}>
                📋 Poll Analytics
              </div>

              {/* Poll stats cards */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12, marginBottom: 20 }}>
                {[
                  { label: "Total Polls", value: pollAnalytics?.totalPolls ?? "—", sub: `${pollAnalytics?.freePolls ?? 0} free · ${pollAnalytics?.lyxPolls ?? 0} LYX · ${pollAnalytics?.tokenPolls ?? 0} token`, color: COLORS.cyan },
                  { label: "Total Votes", value: pollAnalytics?.totalVotes ?? "—", sub: "All polls", color: COLORS.accent },
                  { label: "LYX Collected", value: pollAnalytics ? pollAnalytics.lyxCollected.toFixed(2) : "—", sub: `${pollAnalytics?.paidVoteCount ?? 0} paid votes`, color: COLORS.gold },
                  { label: "Active Now", value: pollAnalytics?.activePolls ?? "—", sub: `${pollAnalytics?.endedPolls ?? 0} ended · ${pollAnalytics?.archivedPolls ?? 0} archived`, color: COLORS.purple },
                ].map((card, ci) => (
                  <div key={ci} style={{
                    padding: "16px", borderRadius: 10,
                    background: `${card.color}06`, border: `1px solid ${card.color}15`,
                  }}>
                    <div style={{ fontSize: 9, color: COLORS.textMuted, fontWeight: 600, textTransform: "uppercase", letterSpacing: "1px", marginBottom: 8 }}>{card.label}</div>
                    <div style={{ fontSize: 24, fontWeight: 800, color: card.color, fontFamily: "'Outfit', sans-serif" }}>{card.value}</div>
                    <div style={{ fontSize: 8, color: COLORS.textMuted, marginTop: 4 }}>{card.sub}</div>
                  </div>
                ))}
              </div>

              {/* Recent polls table */}
              {pollAnalytics?.recentPolls?.length > 0 && (
                <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, overflow: "hidden" }}>
                  <div style={{ padding: "12px 16px", borderBottom: `1px solid ${COLORS.border}`, fontSize: 12, fontWeight: 700, color: COLORS.text }}>
                    Recent Polls
                  </div>
                  <div style={{ maxHeight: 300, overflowY: "auto" }}>
                    {pollAnalytics.recentPolls.map((p, pi) => (
                      <div key={pi} style={{
                        display: "flex", alignItems: "center", gap: 12, padding: "10px 16px",
                        borderBottom: pi < pollAnalytics.recentPolls.length - 1 ? `1px solid ${COLORS.border}` : "none",
                      }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.question}</div>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 3 }}>
                            {renderCreatorTag(p.creator)}
                            <div style={{ fontSize: 8, color: COLORS.textMuted }}>
                              {p.gateType === 0 ? "🟢 Free" : p.gateType === 1 ? "🪙 LYX" : "🔒 Token"}
                              {p.onchainId && <span> · ⛓ #{p.onchainId}</span>}
                              {" · "}{new Date(p.createdAt).toLocaleDateString()}
                            </div>
                          </div>
                        </div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.accent, fontFamily: "'JetBrains Mono', monospace", minWidth: 50, textAlign: "right" }}>
                          {p.totalVotes} vote{p.totalVotes !== 1 ? "s" : ""}
                        </div>
                        <div style={{
                          fontSize: 8, padding: "2px 6px", borderRadius: 3, fontWeight: 600,
                          background: p.status === "active" ? `${COLORS.accent}12` : p.status === "ended" ? `${COLORS.orange}10` : `${COLORS.textMuted}10`,
                          color: p.status === "active" ? COLORS.accent : p.status === "ended" ? COLORS.orange : COLORS.textMuted,
                        }}>
                          {p.status.toUpperCase()}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {!pollAnalytics && (
                <div style={{ padding: "24px", borderRadius: 10, background: COLORS.surface, border: `1px solid ${COLORS.border}`, textAlign: "center" }}>
                  <div style={{ fontSize: 12, color: COLORS.textMuted }}>Loading poll analytics...</div>
                </div>
              )}
            </>)}

            {/* ===== GOALS TAB ===== */}
            {adminTab === "goals" && (<>
              <div style={{ fontFamily: "'Syne', sans-serif", fontSize: 18, fontWeight: 800, color: COLORS.text, marginBottom: 16, display: "flex", alignItems: "center", gap: 10 }}>
                🎯 Goal Analytics
              </div>

              {/* Goal stats cards */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 20 }}>
                {[
                  { label: "Goals Created", value: goalAnalytics?.totalGoals ?? "—", sub: `${goalAnalytics?.activeGoals ?? 0} active · ${goalAnalytics?.completedGoals ?? 0} completed`, color: COLORS.cyan },
                  { label: "Goals Completed", value: goalAnalytics?.completedGoals ?? "—", sub: goalAnalytics?.totalGoals ? `${Math.round((goalAnalytics.completedGoals / goalAnalytics.totalGoals) * 100)}% completion rate` : "No goals yet", color: COLORS.accent },
                  { label: "LYX Raised", value: goalAnalytics ? goalAnalytics.totalRaised.toFixed(1) : "—", sub: goalAnalytics?.totalTarget ? `of ${goalAnalytics.totalTarget.toFixed(0)} LYX target` : "Total across goals", color: COLORS.gold },
                ].map((card, ci) => (
                  <div key={ci} style={{
                    padding: "16px", borderRadius: 10,
                    background: `${card.color}06`, border: `1px solid ${card.color}15`,
                  }}>
                    <div style={{ fontSize: 9, color: COLORS.textMuted, fontWeight: 600, textTransform: "uppercase", letterSpacing: "1px", marginBottom: 8 }}>{card.label}</div>
                    <div style={{ fontSize: 24, fontWeight: 800, color: card.color, fontFamily: "'Outfit', sans-serif" }}>{card.value}</div>
                    <div style={{ fontSize: 8, color: COLORS.textMuted, marginTop: 4 }}>{card.sub}</div>
                  </div>
                ))}
              </div>

              {/* Active goals list */}
              {goalAnalytics?.goals?.length > 0 && (
                <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, overflow: "hidden" }}>
                  <div style={{ padding: "12px 16px", borderBottom: `1px solid ${COLORS.border}`, fontSize: 12, fontWeight: 700, color: COLORS.text }}>
                    All Goals
                  </div>
                  <div style={{ maxHeight: 300, overflowY: "auto" }}>
                    {goalAnalytics.goals.map((g, gi) => {
                      const pct = g.target > 0 ? Math.min((g.current / g.target) * 100, 100) : 0;
                      const completed = g.current >= g.target;
                      return (
                        <div key={gi} style={{
                          padding: "12px 16px",
                          borderBottom: gi < goalAnalytics.goals.length - 1 ? `1px solid ${COLORS.border}` : "none",
                        }}>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                            <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.text }}>{g.title || "Untitled Goal"}</div>
                            <div style={{ fontSize: 10, fontWeight: 700, color: completed ? COLORS.accent : COLORS.orange, fontFamily: "'JetBrains Mono', monospace" }}>
                              {g.current.toFixed(1)} / {g.target} LYX {completed && "✅"}
                            </div>
                          </div>
                          <div style={{ height: 4, borderRadius: 2, background: `${COLORS.border}`, overflow: "hidden" }}>
                            <div style={{ height: "100%", borderRadius: 2, background: completed ? COLORS.accent : COLORS.cyan, width: `${pct}%`, transition: "width 0.3s" }} />
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
                            {renderCreatorTag(g.creator)}
                            <span style={{ fontSize: 8, color: COLORS.textMuted }}>{pct.toFixed(0)}%</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {goalAnalytics && goalAnalytics.goals?.length === 0 && (
                <div style={{ padding: "24px", borderRadius: 10, background: COLORS.surface, border: `1px solid ${COLORS.border}`, textAlign: "center" }}>
                  <div style={{ fontSize: 12, color: COLORS.textMuted }}>No goals created yet</div>
                </div>
              )}

              {!goalAnalytics && (
                <div style={{ padding: "24px", borderRadius: 10, background: COLORS.surface, border: `1px solid ${COLORS.border}`, textAlign: "center" }}>
                  <div style={{ fontSize: 12, color: COLORS.textMuted }}>Loading goal analytics...</div>
                </div>
              )}
            </>)}

            {/* ===== MESSAGES TAB ===== */}
            {adminTab === "messages" && (<>

            {/* Sub-tabs */}
            <div style={{ display: "flex", gap: 4, marginBottom: 20 }}>
              {[
                { key: "bugs", label: "Bug Reports", icon: "🐛", count: bugReports.filter(r => r.status === "open").length },
                { key: "ideas", label: "Ideas", icon: "💡", count: submissions.filter(s => s.type === "idea" && s.status !== "archived").length },
                { key: "partnerships", label: "Partnerships", icon: "🤝", count: submissions.filter(s => s.type === "partnership" && s.status !== "archived").length },
                { key: "notifications", label: "Notifications", icon: "🔔", count: notiTips.filter(t => !notiArchived.includes(t.tx_hash)).length },
              ].map(t => (
                <button key={t.key} onClick={() => setMessageTab(t.key)} style={{
                  padding: "7px 16px", borderRadius: 7,
                  background: messageTab === t.key ? `${COLORS.purple}12` : COLORS.surface,
                  border: `1px solid ${messageTab === t.key ? COLORS.purple + "25" : COLORS.border}`,
                  color: messageTab === t.key ? COLORS.purple : COLORS.textMuted,
                  fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 600,
                  cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
                  transition: "all 0.2s",
                }}>
                  <span>{t.icon}</span> {t.label}
                  {t.count > 0 && (
                    <span style={{
                      fontSize: 9, padding: "1px 5px", borderRadius: 4,
                      background: COLORS.red + "20", color: COLORS.red, fontWeight: 700,
                    }}>{t.count}</span>
                  )}
                </button>
              ))}
            </div>

            {/* ---- BUG REPORTS ---- */}
            {messageTab === "bugs" && (<>
            <SectionHeader title={`Bug Reports${bugReports.length > 0 ? ` (${bugReports.filter(r => r.status === "open").length} open)` : ""}`} icon="🐛" />
            <div style={{
              padding: 20, borderRadius: 14,
              background: COLORS.surface,
              border: `1px solid ${COLORS.border}`,
            }}>
              {bugReports.filter(r => r.status !== "archived").length === 0 ? (
                <div style={{
                  textAlign: "center", padding: "32px 16px",
                  color: COLORS.textMuted, fontSize: 13,
                  fontFamily: "'Outfit', sans-serif",
                }}>
                  <div style={{ fontSize: 32, marginBottom: 10 }}>🎉</div>
                  No bug reports yet — looking clean!
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {bugReports.filter(r => r.status !== "archived").map(report => (
                    <div key={report.id} style={{
                      padding: "14px 16px", borderRadius: 10,
                      background: COLORS.bgAlt,
                      border: `1px solid ${report.status === "resolved" ? COLORS.accent + "15" : COLORS.red + "15"}`,
                      opacity: report.status === "resolved" ? 0.6 : 1,
                      transition: "all 0.2s",
                    }}>
                      {/* Header row */}
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <div style={{
                            padding: "2px 8px", borderRadius: 4,
                            background: report.status === "resolved" ? `${COLORS.accent}12` : `${COLORS.red}12`,
                            border: `1px solid ${report.status === "resolved" ? COLORS.accent + "25" : COLORS.red + "25"}`,
                            fontSize: 8, fontWeight: 700,
                            color: report.status === "resolved" ? COLORS.accent : COLORS.red,
                            fontFamily: "'JetBrains Mono', monospace",
                            letterSpacing: "1px", textTransform: "uppercase",
                          }}>
                            {report.status === "resolved" ? "✓ RESOLVED" : "● OPEN"}
                          </div>
                          {report.section && report.section !== "Not specified" && (
                            <div style={{
                              padding: "2px 8px", borderRadius: 4,
                              background: `${COLORS.blue}10`,
                              border: `1px solid ${COLORS.blue}15`,
                              fontSize: 8, fontWeight: 600, color: COLORS.blue,
                              fontFamily: "'JetBrains Mono', monospace",
                            }}>{report.section}</div>
                          )}
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <button onClick={() => toggleBugStatus(report.id, report.status === "resolved" ? "open" : "resolved")} style={{
                            padding: "3px 10px", borderRadius: 5,
                            background: report.status === "resolved" ? `${COLORS.orange}10` : `${COLORS.accent}10`,
                            border: `1px solid ${report.status === "resolved" ? COLORS.orange + "20" : COLORS.accent + "20"}`,
                            color: report.status === "resolved" ? COLORS.orange : COLORS.accent,
                            fontSize: 9, fontWeight: 600, cursor: "pointer",
                            fontFamily: "'JetBrains Mono', monospace",
                          }}>
                            {report.status === "resolved" ? "Reopen" : "✓ Resolve"}
                          </button>
                          <button onClick={() => archiveBug(report.id)} style={{
                            padding: "3px 10px", borderRadius: 5,
                            background: `${COLORS.textMuted}08`,
                            border: `1px solid ${COLORS.border}`,
                            color: COLORS.textMuted, fontSize: 9, fontWeight: 600, cursor: "pointer",
                            fontFamily: "'JetBrains Mono', monospace",
                          }}>📦 Archive</button>
                        </div>
                      </div>

                      {/* Reporter */}
                      {(() => {
                        const addr = report.reporter_address || report.reporter?.address;
                        const hasAddr = addr && addr !== "Not connected";
                        const profile = hasAddr ? submitterProfiles[addr.toLowerCase()] : null;
                        return (
                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                          {hasAddr && (
                            <a href={`https://universaleverything.io/${addr}`} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
                              <div style={{ width: 22, height: 22, borderRadius: 5, overflow: "hidden", background: `${COLORS.red}15`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, cursor: "pointer" }}>
                                {profile?.avatar && isImageAvatar(profile.avatar) ? <img src={profile.avatar} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : "👤"}
                              </div>
                            </a>
                          )}
                          {hasAddr ? (
                            <a href={`https://universaleverything.io/${addr}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: 10, fontWeight: 600, color: COLORS.text, textDecoration: "none" }}
                              onMouseEnter={e => e.currentTarget.style.color = COLORS.accent} onMouseLeave={e => e.currentTarget.style.color = COLORS.text}>
                              {profile?.name || report.reporter_name || report.reporter?.name || "Anonymous"}
                            </a>
                          ) : (
                            <div style={{ fontSize: 10, fontWeight: 600, color: COLORS.text }}>{report.reporter_name || "Anonymous"}</div>
                          )}
                          {hasAddr && <div style={{ fontSize: 8, color: COLORS.textMuted, fontFamily: "'JetBrains Mono', monospace" }}>{addr.slice(0, 8)}...{addr.slice(-4)}</div>}
                          {profile?.links?.x && <a href={profile.links.x} target="_blank" rel="noopener noreferrer" style={{ fontSize: 8, color: "#fff", fontFamily: "'JetBrains Mono', monospace", textDecoration: "none" }}>𝕏</a>}
                          <div style={{ marginLeft: "auto", fontSize: 8, color: COLORS.textMuted }}>
                            {(report.created_at || report.timestamp) ? new Date(report.created_at || report.timestamp).toLocaleDateString() + " " + new Date(report.created_at || report.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}
                          </div>
                        </div>
                        );
                      })()}

                      {/* Description */}
                      <div style={{
                        fontSize: 11, color: COLORS.textSecondary, lineHeight: 1.6,
                        marginBottom: report.steps ? 8 : 0,
                      }}>
                        {report.description}
                      </div>

                      {/* Steps */}
                      {report.steps && (
                        <div style={{
                          padding: "8px 12px", borderRadius: 6,
                          background: `${COLORS.text}04`,
                          border: `1px solid ${COLORS.border}`,
                          fontSize: 10, color: COLORS.textMuted, lineHeight: 1.6,
                          whiteSpace: "pre-wrap",
                          fontFamily: "'JetBrains Mono', monospace",
                        }}>
                          {report.steps}
                        </div>
                      )}

                      {/* File attachments */}
                      {(() => {
                        const fileList = typeof report.files === "string" ? JSON.parse(report.files || "[]") : (report.files || []);
                        if (fileList.length === 0) return null;
                        return (
                          <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                            {fileList.map((f, fi) => (
                              f.data && f.type?.startsWith("image") ? (
                                <div key={fi} onClick={() => setLightboxImage(f.data)} style={{ cursor: "pointer" }}>
                                  <img src={f.data} alt={f.name} style={{
                                    maxWidth: 120, maxHeight: 80, borderRadius: 6,
                                    border: `1px solid ${COLORS.border}`, cursor: "pointer",
                                    objectFit: "cover",
                                  }} />
                                </div>
                              ) : (
                                <div key={fi} style={{
                                  padding: "4px 10px", borderRadius: 4,
                                  background: `${COLORS.purple}08`, border: `1px solid ${COLORS.purple}12`,
                                  fontSize: 8, color: COLORS.purple, display: "flex", alignItems: "center", gap: 4,
                                }}>
                                  <span>{f.type?.startsWith("video") ? "🎥" : "📄"}</span> {f.name}
                                </div>
                              )
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Archived Bugs */}
            {bugReports.filter(r => r.status === "archived").length > 0 && (
              <div style={{ marginTop: 16 }}>
                <div onClick={() => setShowArchivedBugs(!showArchivedBugs)} style={{
                  fontSize: 10, fontWeight: 700, color: COLORS.textMuted, cursor: "pointer",
                  display: "flex", alignItems: "center", gap: 6, marginBottom: 8,
                  fontFamily: "'JetBrains Mono', monospace", letterSpacing: "1px", textTransform: "uppercase",
                }}>
                  <span style={{ transition: "transform 0.2s", transform: showArchivedBugs ? "rotate(90deg)" : "rotate(0)" }}>▸</span>
                  📦 Archived ({bugReports.filter(r => r.status === "archived").length})
                </div>
                {showArchivedBugs && (
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {bugReports.filter(r => r.status === "archived").map(report => (
                    <div key={report.id} style={{
                      padding: "10px 14px", borderRadius: 8, background: COLORS.bgAlt,
                      border: `1px solid ${COLORS.border}`, opacity: 0.5, fontSize: 11,
                    }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ color: COLORS.textSecondary }}>{report.description?.slice(0, 80)}{report.description?.length > 80 ? "..." : ""}</span>
                        <button onClick={() => toggleBugStatus(report.id, "open")} style={{
                          padding: "2px 8px", borderRadius: 4, background: `${COLORS.cyan}08`,
                          border: `1px solid ${COLORS.cyan}15`, color: COLORS.cyan, fontSize: 8,
                          fontWeight: 600, cursor: "pointer", fontFamily: "'JetBrains Mono', monospace",
                        }}>Restore</button>
                      </div>
                    </div>
                  ))}
                </div>
                )}
              </div>
            )}
            </>)}
            {messageTab === "ideas" && (<>
            <SectionHeader title={`Ideas${submissions.filter(s => s.type === "idea").length > 0 ? ` (${submissions.filter(s => s.type === "idea").length})` : ""}`} icon="💡" />
            <div style={{
              padding: 20, borderRadius: 14,
              background: COLORS.surface,
              border: `1px solid ${COLORS.border}`,
            }}>
              {submissions.filter(s => s.type === "idea" && s.status !== "archived").length === 0 ? (
                <div style={{
                  textAlign: "center", padding: "32px 16px",
                  color: COLORS.textMuted, fontSize: 13,
                  fontFamily: "'Outfit', sans-serif",
                }}>
                  <div style={{ fontSize: 32, marginBottom: 10 }}>💡</div>
                  No ideas submitted yet
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {submissions.filter(s => s.type === "idea" && s.status !== "archived").map(sub => {
                    const addr = sub.reporter_address || sub.reporter?.address;
                    const hasAddr = addr && addr !== "Not connected";
                    const profile = hasAddr ? submitterProfiles[addr.toLowerCase()] : null;
                    return (
                    <div key={sub.id} style={{
                      padding: "14px 16px", borderRadius: 10,
                      background: COLORS.bgAlt,
                      border: `1px solid ${COLORS.accent}15`,
                    }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          {hasAddr && (
                            <a href={`https://universaleverything.io/${addr}`} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
                              <div style={{ width: 28, height: 28, borderRadius: 6, overflow: "hidden", background: `${COLORS.accent}15`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, cursor: "pointer" }}>
                                {profile?.avatar && isImageAvatar(profile.avatar) ? <img src={profile.avatar} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : "👤"}
                              </div>
                            </a>
                          )}
                          <div>
                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                              {hasAddr ? (
                                <a href={`https://universaleverything.io/${addr}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: 10, fontWeight: 600, color: COLORS.text, textDecoration: "none" }}
                                  onMouseEnter={e => e.currentTarget.style.color = COLORS.accent} onMouseLeave={e => e.currentTarget.style.color = COLORS.text}>
                                  {profile?.name || sub.reporter_name || sub.name || "Anonymous"}
                                </a>
                              ) : (
                                <div style={{ fontSize: 10, fontWeight: 600, color: COLORS.text }}>{sub.name || "Anonymous"}</div>
                              )}
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 2, flexWrap: "wrap" }}>
                              {hasAddr && <div style={{ fontSize: 8, color: COLORS.textMuted, fontFamily: "'JetBrains Mono', monospace" }}>{addr.slice(0, 8)}...{addr.slice(-4)}</div>}
                              {sub.email && <a href={`mailto:${sub.email}`} style={{ fontSize: 8, color: COLORS.cyan, fontFamily: "'JetBrains Mono', monospace", textDecoration: "none" }}>📧 {sub.email}</a>}
                              {sub.twitter && <a href={`https://x.com/${sub.twitter.replace("@", "")}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: 8, color: "#fff", fontFamily: "'JetBrains Mono', monospace", textDecoration: "none" }}>𝕏 {sub.twitter}</a>}
                              {profile?.links?.x && !sub.twitter && <a href={profile.links.x} target="_blank" rel="noopener noreferrer" style={{ fontSize: 8, color: "#fff", fontFamily: "'JetBrains Mono', monospace", textDecoration: "none" }}>𝕏 UP</a>}
                              {profile?.links?.youtube && <a href={profile.links.youtube} target="_blank" rel="noopener noreferrer" style={{ fontSize: 8, color: "#FF0000", fontFamily: "'JetBrains Mono', monospace", textDecoration: "none" }}>▶ YT</a>}
                            </div>
                          </div>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <div style={{ fontSize: 8, color: COLORS.textMuted }}>{(sub.created_at || sub.timestamp) ? new Date(sub.created_at || sub.timestamp).toLocaleDateString() : ""}</div>
                          <button onClick={() => toggleSubmissionStatus(sub.id, sub.status === "replied" ? "new" : "replied")} style={{ padding: "3px 10px", borderRadius: 5, background: sub.status === "replied" ? `${COLORS.accent}10` : `${COLORS.cyan}08`, border: `1px solid ${sub.status === "replied" ? COLORS.accent + "20" : COLORS.cyan + "20"}`, color: sub.status === "replied" ? COLORS.accent : COLORS.cyan, fontSize: 9, fontWeight: 600, cursor: "pointer", fontFamily: "'JetBrains Mono', monospace" }}>{sub.status === "replied" ? "✓ Replied" : "Mark Replied"}</button>
                          <button onClick={() => archiveSubmission(sub.id)} style={{ padding: "3px 10px", borderRadius: 5, background: `${COLORS.textMuted}08`, border: `1px solid ${COLORS.border}`, color: COLORS.textMuted, fontSize: 9, fontWeight: 600, cursor: "pointer", fontFamily: "'JetBrains Mono', monospace" }}>📦</button>
                        </div>
                      </div>
                      <div style={{ fontSize: 11, color: COLORS.textSecondary, lineHeight: 1.6 }}>{sub.message}</div>
                      {(() => {
                        const fileList = typeof sub.files === "string" ? JSON.parse(sub.files || "[]") : (sub.files || []);
                        if (fileList.length === 0) return null;
                        return (
                          <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                            {fileList.map((f, fi) => (
                              f.data && f.type?.startsWith("image") ? (
                                <div key={fi} onClick={() => setLightboxImage(f.data)} style={{ cursor: "pointer" }}>
                                  <img src={f.data} alt={f.name} style={{ maxWidth: 120, maxHeight: 80, borderRadius: 6, border: `1px solid ${COLORS.border}`, cursor: "pointer", objectFit: "cover" }} />
                                </div>
                              ) : (
                                <div key={fi} style={{ padding: "4px 10px", borderRadius: 4, background: `${COLORS.purple}08`, border: `1px solid ${COLORS.purple}12`, fontSize: 8, color: COLORS.purple, display: "flex", alignItems: "center", gap: 4 }}>
                                  <span>{f.type?.startsWith("video") ? "🎥" : "📄"}</span> {f.name}
                                </div>
                              )
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Archived Ideas */}
            {submissions.filter(s => s.type === "idea" && s.status === "archived").length > 0 && (
              <div style={{ marginTop: 16 }}>
                <div onClick={() => setShowArchivedIdeas(!showArchivedIdeas)} style={{
                  fontSize: 10, fontWeight: 700, color: COLORS.textMuted, cursor: "pointer",
                  display: "flex", alignItems: "center", gap: 6, marginBottom: 8,
                  fontFamily: "'JetBrains Mono', monospace", letterSpacing: "1px", textTransform: "uppercase",
                }}>
                  <span style={{ transition: "transform 0.2s", transform: showArchivedIdeas ? "rotate(90deg)" : "rotate(0)" }}>▸</span>
                  📦 Archived ({submissions.filter(s => s.type === "idea" && s.status === "archived").length})
                </div>
                {showArchivedIdeas && (
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {submissions.filter(s => s.type === "idea" && s.status === "archived").map(sub => (
                    <div key={sub.id} style={{
                      padding: "10px 14px", borderRadius: 8, background: COLORS.bgAlt,
                      border: `1px solid ${COLORS.border}`, opacity: 0.5, fontSize: 11,
                    }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ color: COLORS.textSecondary }}>{sub.message?.slice(0, 80)}{sub.message?.length > 80 ? "..." : ""}</span>
                        <button onClick={() => toggleSubmissionStatus(sub.id, "new")} style={{
                          padding: "2px 8px", borderRadius: 4, background: `${COLORS.cyan}08`,
                          border: `1px solid ${COLORS.cyan}15`, color: COLORS.cyan, fontSize: 8,
                          fontWeight: 600, cursor: "pointer", fontFamily: "'JetBrains Mono', monospace",
                        }}>Restore</button>
                      </div>
                    </div>
                  ))}
                </div>
                )}
              </div>
            )}
            </>)}

            {/* ---- PARTNERSHIPS ---- */}
            {messageTab === "partnerships" && (<>
            <SectionHeader title={`Partnerships${submissions.filter(s => s.type === "partnership").length > 0 ? ` (${submissions.filter(s => s.type === "partnership").length})` : ""}`} icon="🤝" />
            <div style={{
              padding: 20, borderRadius: 14,
              background: COLORS.surface,
              border: `1px solid ${COLORS.border}`,
            }}>
              {submissions.filter(s => s.type === "partnership" && s.status !== "archived").length === 0 ? (
                <div style={{
                  textAlign: "center", padding: "32px 16px",
                  color: COLORS.textMuted, fontSize: 13,
                  fontFamily: "'Outfit', sans-serif",
                }}>
                  <div style={{ fontSize: 32, marginBottom: 10 }}>🤝</div>
                  No partnership inquiries yet
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {submissions.filter(s => s.type === "partnership" && s.status !== "archived").map(sub => {
                    const addr = sub.reporter_address || sub.reporter?.address;
                    const hasAddr = addr && addr !== "Not connected";
                    const profile = hasAddr ? submitterProfiles[addr.toLowerCase()] : null;
                    return (
                    <div key={sub.id} style={{
                      padding: "14px 16px", borderRadius: 10,
                      background: COLORS.bgAlt,
                      border: `1px solid ${COLORS.purple}15`,
                    }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          {hasAddr && (
                            <a href={`https://universaleverything.io/${addr}`} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
                              <div style={{ width: 28, height: 28, borderRadius: 6, overflow: "hidden", background: `${COLORS.purple}15`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, cursor: "pointer" }}>
                                {profile?.avatar && isImageAvatar(profile.avatar) ? <img src={profile.avatar} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : "👤"}
                              </div>
                            </a>
                          )}
                          <div>
                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                              {hasAddr ? (
                                <a href={`https://universaleverything.io/${addr}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: 10, fontWeight: 600, color: COLORS.text, textDecoration: "none" }}
                                  onMouseEnter={e => e.currentTarget.style.color = COLORS.purple} onMouseLeave={e => e.currentTarget.style.color = COLORS.text}>
                                  {profile?.name || sub.reporter_name || sub.name || "Anonymous"}
                                </a>
                              ) : (
                                <div style={{ fontSize: 10, fontWeight: 600, color: COLORS.text }}>{sub.name || "Anonymous"}</div>
                              )}
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 2, flexWrap: "wrap" }}>
                              {hasAddr && <div style={{ fontSize: 8, color: COLORS.textMuted, fontFamily: "'JetBrains Mono', monospace" }}>{addr.slice(0, 8)}...{addr.slice(-4)}</div>}
                              {sub.email && <a href={`mailto:${sub.email}`} style={{ fontSize: 8, color: COLORS.cyan, fontFamily: "'JetBrains Mono', monospace", textDecoration: "none" }}>📧 {sub.email}</a>}
                              {sub.twitter && <a href={`https://x.com/${sub.twitter.replace("@", "")}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: 8, color: "#fff", fontFamily: "'JetBrains Mono', monospace", textDecoration: "none" }}>𝕏 {sub.twitter}</a>}
                              {profile?.links?.x && !sub.twitter && <a href={profile.links.x} target="_blank" rel="noopener noreferrer" style={{ fontSize: 8, color: "#fff", fontFamily: "'JetBrains Mono', monospace", textDecoration: "none" }}>𝕏 UP</a>}
                              {profile?.links?.youtube && <a href={profile.links.youtube} target="_blank" rel="noopener noreferrer" style={{ fontSize: 8, color: "#FF0000", fontFamily: "'JetBrains Mono', monospace", textDecoration: "none" }}>▶ YT</a>}
                            </div>
                          </div>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <div style={{ fontSize: 8, color: COLORS.textMuted }}>{(sub.created_at || sub.timestamp) ? new Date(sub.created_at || sub.timestamp).toLocaleDateString() : ""}</div>
                          <button onClick={() => toggleSubmissionStatus(sub.id, sub.status === "replied" ? "new" : "replied")} style={{ padding: "3px 10px", borderRadius: 5, background: sub.status === "replied" ? `${COLORS.accent}10` : `${COLORS.cyan}08`, border: `1px solid ${sub.status === "replied" ? COLORS.accent + "20" : COLORS.cyan + "20"}`, color: sub.status === "replied" ? COLORS.accent : COLORS.cyan, fontSize: 9, fontWeight: 600, cursor: "pointer", fontFamily: "'JetBrains Mono', monospace" }}>{sub.status === "replied" ? "✓ Replied" : "Mark Replied"}</button>
                          <button onClick={() => archiveSubmission(sub.id)} style={{ padding: "3px 10px", borderRadius: 5, background: `${COLORS.textMuted}08`, border: `1px solid ${COLORS.border}`, color: COLORS.textMuted, fontSize: 9, fontWeight: 600, cursor: "pointer", fontFamily: "'JetBrains Mono', monospace" }}>📦</button>
                        </div>
                      </div>
                      <div style={{ fontSize: 11, color: COLORS.textSecondary, lineHeight: 1.6 }}>{sub.message}</div>
                      {(() => {
                        const fileList = typeof sub.files === "string" ? JSON.parse(sub.files || "[]") : (sub.files || []);
                        if (fileList.length === 0) return null;
                        return (
                          <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                            {fileList.map((f, fi) => (
                              f.data && f.type?.startsWith("image") ? (
                                <div key={fi} onClick={() => setLightboxImage(f.data)} style={{ cursor: "pointer" }}>
                                  <img src={f.data} alt={f.name} style={{ maxWidth: 120, maxHeight: 80, borderRadius: 6, border: `1px solid ${COLORS.border}`, cursor: "pointer", objectFit: "cover" }} />
                                </div>
                              ) : (
                                <div key={fi} style={{ padding: "4px 10px", borderRadius: 4, background: `${COLORS.purple}08`, border: `1px solid ${COLORS.purple}12`, fontSize: 8, color: COLORS.purple, display: "flex", alignItems: "center", gap: 4 }}>
                                  <span>{f.type?.startsWith("video") ? "🎥" : "📄"}</span> {f.name}
                                </div>
                              )
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Archived Partnerships */}
            {submissions.filter(s => s.type === "partnership" && s.status === "archived").length > 0 && (
              <div style={{ marginTop: 16 }}>
                <div onClick={() => setShowArchivedPartnerships(!showArchivedPartnerships)} style={{
                  fontSize: 10, fontWeight: 700, color: COLORS.textMuted, cursor: "pointer",
                  display: "flex", alignItems: "center", gap: 6, marginBottom: 8,
                  fontFamily: "'JetBrains Mono', monospace", letterSpacing: "1px", textTransform: "uppercase",
                }}>
                  <span style={{ transition: "transform 0.2s", transform: showArchivedPartnerships ? "rotate(90deg)" : "rotate(0)" }}>▸</span>
                  📦 Archived ({submissions.filter(s => s.type === "partnership" && s.status === "archived").length})
                </div>
                {showArchivedPartnerships && (
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {submissions.filter(s => s.type === "partnership" && s.status === "archived").map(sub => (
                    <div key={sub.id} style={{
                      padding: "10px 14px", borderRadius: 8, background: COLORS.bgAlt,
                      border: `1px solid ${COLORS.border}`, opacity: 0.5, fontSize: 11,
                    }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ color: COLORS.textSecondary }}>{sub.message?.slice(0, 80)}{sub.message?.length > 80 ? "..." : ""}</span>
                        <button onClick={() => toggleSubmissionStatus(sub.id, "new")} style={{
                          padding: "2px 8px", borderRadius: 4, background: `${COLORS.cyan}08`,
                          border: `1px solid ${COLORS.cyan}15`, color: COLORS.cyan, fontSize: 8,
                          fontWeight: 600, cursor: "pointer", fontFamily: "'JetBrains Mono', monospace",
                        }}>Restore</button>
                      </div>
                    </div>
                  ))}
                </div>
                )}
              </div>
            )}
            </>)}

            {/* ---- NOTIFICATIONS ---- */}
            {messageTab === "notifications" && (() => {
              const parseTs = (ts) => {
                if (!ts) return null;
                if (typeof ts === "string" && isNaN(Number(ts))) { const d = new Date(ts); return isNaN(d.getTime()) ? null : d; }
                const n = Number(ts); return new Date(n < 1e12 ? n * 1000 : n);
              };
              const activeNotis = notiTips.filter(t => !notiArchived.includes(t.tx_hash));
              const archivedNotis = notiTips.filter(t => notiArchived.includes(t.tx_hash));
              const row = (t, archived) => {
                const d = parseTs(t.timestamp);
                const isPlatform = (t.message || "") === PLATFORM_TIP_TAG;
                return (
                  <div key={t.tx_hash} style={{
                    padding: "12px 14px", borderRadius: 8,
                    background: archived ? COLORS.bgAlt : COLORS.bgAlt,
                    border: `1px solid ${COLORS.border}`, opacity: archived ? 0.55 : 1,
                    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
                  }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: 12, color: COLORS.text, fontWeight: 600, fontFamily: "'JetBrains Mono', monospace" }}>
                        💸 Received {parseFloat(t.amount || 0)} LYX{isPlatform ? " → FullSend" : ""}
                      </div>
                      <div style={{ fontSize: 10, color: COLORS.textMuted, marginTop: 3, fontFamily: "'JetBrains Mono', monospace" }}>
                        from {t.tipper ? `${t.tipper.slice(0, 6)}…${t.tipper.slice(-4)}` : "Anonymous"}{d ? ` · ${d.toLocaleString()}` : ""}
                      </div>
                      {t.message && !isPlatform && (
                        <div style={{ fontSize: 11, color: COLORS.textSecondary, marginTop: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          “{t.message.slice(0, 80)}{t.message.length > 80 ? "…" : ""}”
                        </div>
                      )}
                    </div>
                    <button onClick={() => archived ? unarchiveNoti(t.tx_hash) : archiveNoti(t.tx_hash)} style={{
                      padding: "4px 10px", borderRadius: 5, flexShrink: 0,
                      background: archived ? `${COLORS.cyan}08` : `${COLORS.textSecondary}08`,
                      border: `1px solid ${archived ? COLORS.cyan + "15" : COLORS.textSecondary + "15"}`,
                      color: archived ? COLORS.cyan : COLORS.textSecondary, fontSize: 9, fontWeight: 600,
                      cursor: "pointer", fontFamily: "'JetBrains Mono', monospace",
                    }}>{archived ? "Restore" : "Archive"}</button>
                  </div>
                );
              };
              return (<>
              <SectionHeader title={`Platform Notifications${activeNotis.length > 0 ? ` (${activeNotis.length})` : ""}`} icon="🔔" right={
                activeNotis.length > 0 ? (
                  <button onClick={() => clearAllNotis(activeNotis.map(t => t.tx_hash))} style={{
                    padding: "6px 14px", borderRadius: 7, background: `${COLORS.red}10`,
                    border: `1px solid ${COLORS.red}25`, color: COLORS.red, fontSize: 10, fontWeight: 700,
                    cursor: "pointer", fontFamily: "'Syne', sans-serif",
                  }}>Clear all</button>
                ) : null
              } />
              <div style={{ padding: 20, borderRadius: 14, background: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
                {notiLoading ? (
                  <div style={{ textAlign: "center", padding: "30px 0", color: COLORS.textMuted, fontSize: 12 }}>Loading…</div>
                ) : activeNotis.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "30px 0" }}>
                    <div style={{ fontSize: 28 }}>🔔</div>
                    <div style={{ color: COLORS.textMuted, fontSize: 12, marginTop: 8 }}>No new notifications</div>
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {activeNotis.map(t => row(t, false))}
                  </div>
                )}
              </div>
              {archivedNotis.length > 0 && (
                <div style={{ marginTop: 18 }}>
                  <button onClick={() => setShowArchivedNotis(!showArchivedNotis)} style={{
                    display: "flex", alignItems: "center", gap: 8, background: "transparent", border: "none",
                    color: COLORS.textMuted, fontSize: 12, fontWeight: 600, cursor: "pointer",
                    fontFamily: "'JetBrains Mono', monospace", marginBottom: 10,
                  }}>
                    📦 Archived ({archivedNotis.length}) <span style={{ fontSize: 9, transform: showArchivedNotis ? "rotate(180deg)" : "rotate(0)", transition: "transform 0.2s" }}>▼</span>
                  </button>
                  {showArchivedNotis && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {archivedNotis.map(t => row(t, true))}
                    </div>
                  )}
                </div>
              )}
              </>);
            })()}

            </>)}

          </div>
        )}
      </div>

      {/* Image Lightbox */}
      {lightboxImage && (
        <div onClick={() => setLightboxImage(null)} style={{
          position: "fixed", inset: 0, zIndex: 9999,
          background: "rgba(0,0,0,0.85)",
          display: "flex", alignItems: "center", justifyContent: "center",
          cursor: "pointer",
          animation: "fadeInUp 0.2s ease-out",
        }}>
          <div style={{ position: "relative", maxWidth: "90vw", maxHeight: "90vh" }}>
            <img src={lightboxImage} alt="" style={{
              maxWidth: "90vw", maxHeight: "90vh", borderRadius: 12,
              border: `2px solid ${COLORS.border}`,
              boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
            }} />
            <div style={{
              position: "absolute", top: -12, right: -12,
              width: 28, height: 28, borderRadius: "50%",
              background: COLORS.surface, border: `1px solid ${COLORS.border}`,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 14, color: COLORS.textMuted, cursor: "pointer",
            }}>✕</div>
          </div>
        </div>
      )}
    </div>
  );
}

// Marker written into the tip message for tips made via the footer "Tip FullSend"
// button, so the supporters board can show ONLY deliberate platform supporters
// (not every tip that happens to land in FullSend's creator UP).
const PLATFORM_TIP_TAG = "Tip to FullSend 💚";

// Shared tip sender — sends a real LYX tip to `creatorAddress` through TipRouter,
// reusing the exact wallet branching validated in TipWidget (UP direct call +
// self-pay fallback, desktop manual UP.execute wrap, EOA direct). Reads the
// connected wallet from window globals, records the tip to the DB, returns txHash.
async function sendTipTransaction({ creatorAddress, amount, message, direct = false }) {
  let provider = window.__fullsend_provider;
  let senderProfile = window.__fullsend_profile;
  // No wallet session (e.g. mobile, where the nav Connect is hidden) → trigger
  // the app's connect flow first, which opens WalletConnect on mobile, then re-read.
  if ((!provider || !senderProfile) && typeof window.__fullsend_connect === "function") {
    try { await window.__fullsend_connect(); } catch {}
    provider = window.__fullsend_provider;
    senderProfile = window.__fullsend_profile;
  }
  if (!provider || !senderProfile) throw new Error("Wallet not connected");
  if (!amount || parseFloat(amount) <= 0) throw new Error("Enter an amount");
  if (senderProfile.address && creatorAddress.toLowerCase() === senderProfile.address.toLowerCase()) {
    throw new Error("You can't tip yourself");
  }

  const tipWei = lyxToWei(String(amount));
  const feeWei = lyxToWei("0.5");
  // Direct tips to FullSend itself bypass TipRouter entirely → no 0.5 fee.
  // (The fee would only route FullSend→FullSend anyway.) Plain LYX transfer
  // straight to the treasury UP; all other tips still go through TipRouter.
  const totalPaymentWei = direct ? tipWei : (BigInt(tipWei) + BigInt(feeWei)).toString();
  const callTarget = direct ? creatorAddress : TIPROUTER_ADDRESS;
  const messageHash = hashMessage(message || "");
  const callData = direct ? "0x" : encodeTipCall(creatorAddress, messageHash);

  let txh;
  if (senderProfile.isUP) {
    const pt = senderProfile.providerType;
    if (isAutoWrapProvider(pt)) {
      const directParams = {
        from: senderProfile.address,
        to: callTarget,
        value: "0x" + BigInt(totalPaymentWei).toString(16),
        data: callData,
      };
      try {
        txh = await provider.request({ method: "eth_sendTransaction", params: [directParams] });
      } catch (wcErr) {
        const wm = (wcErr && wcErr.message) || "";
        const valueDropped = /Must send LYX|estimat(e|ing) gas|gas estimation|execution reverted|reverted by the EVM|insufficient/i.test(wm);
        const codeRetry = wcErr && (wcErr.code === -32603 || wcErr.code === -32000);
        if (pt === "walletconnect" && (valueDropped || codeRetry) && wcErr?.code !== 4001) {
          txh = await provider.request({
            method: "eth_sendTransaction",
            params: [{ from: senderProfile.address, to: senderProfile.address, data: encodeUPExecute(callTarget, totalPaymentWei, callData), value: "0x0" }],
          });
        } else { throw wcErr; }
      }
    } else {
      txh = await provider.request({
        method: "eth_sendTransaction",
        params: [{ from: senderProfile.address, to: senderProfile.address, data: encodeUPExecute(callTarget, totalPaymentWei, callData), value: "0x0" }],
      });
    }
  } else {
    txh = await provider.request({
      method: "eth_sendTransaction",
      params: [{ from: senderProfile.address, to: callTarget, value: "0x" + BigInt(totalPaymentWei).toString(16), data: callData }],
    });
  }

  // Record to DB so the supporters board / analytics pick it up (tips are never
  // recorded by the relayer — every tip path must record itself).
  try {
    await fetch("/api/data-tips?action=record", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        creator: creatorAddress.toLowerCase(),
        tipper: senderProfile.address.toLowerCase(),
        amount: parseFloat(amount),
        message: message || "",
        fee: direct ? 0 : 0.5,
        txHash: txh,
      }),
    });
  } catch (e) { console.warn("record-tip (platform) failed:", e); }

  window.dispatchEvent(new CustomEvent("fullsend-tip", { detail: { amount: parseFloat(amount), txHash: txh, outgoing: true } }));
  return txh;
}

// Thank-you board — the Universal Profiles who have tipped FullSend through the
// "Tip FullSend" button (filtered by PLATFORM_TIP_TAG so it shows real platform
// supporters only). Aggregates by supporter and ranks by total tipped.
function FullSendSupporters() {
  const [supporters, setSupporters] = useState([]);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        // All-time supporters, server-aggregated across every platform tip (top 100)
        // — so a supporter never drops off as the treasury's tip volume grows.
        const res = await fetch(`/api/data-tips?action=platform-supporters&limit=100`);
        const data = await res.json();
        const list = (data.supporters || []).map(s => ({
          address: s.tipper,
          total: parseFloat(s.total_amount || 0),
          count: parseInt(s.tip_count || 0),
        })).sort((a, b) => b.total - a.total);
        await Promise.all(list.map(async s => {
          try { const p = await fetchUPProfile(s.address); if (p) { s.name = p.name; s.avatar = p.avatar; } } catch {}
        }));
        if (!cancelled) { setSupporters(list); setLoaded(true); }
      } catch { if (!cancelled) setLoaded(true); }
    };
    load();
    const onTip = () => load();
    window.addEventListener("fullsend-tip", onTip);
    const id = setInterval(load, 60000);
    return () => { cancelled = true; window.removeEventListener("fullsend-tip", onTip); clearInterval(id); };
  }, []);

  return (
    <div style={{ marginTop: 48, paddingTop: 32, borderTop: `1px solid ${COLORS.border}` }}>
      <div style={{ textAlign: "center", marginBottom: 6 }}>
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 8,
          fontFamily: "'Syne', sans-serif", fontSize: 22, fontWeight: 800,
          letterSpacing: "-0.5px", color: COLORS.text,
        }}>
          <span>💚</span> Our Supporters
        </div>
      </div>
      <div style={{
        textAlign: "center", fontSize: 12, color: COLORS.textSecondary,
        marginBottom: 24, fontFamily: "'JetBrains Mono', monospace",
      }}>
        The Universal Profiles keeping FullSend running
      </div>

      {!loaded ? (
        <div style={{ textAlign: "center", color: COLORS.textMuted, fontSize: 12, padding: "20px 0" }}>
          <span style={{ animation: "pulse 1s infinite" }}>Loading supporters…</span>
        </div>
      ) : supporters.length === 0 ? (
        <div style={{
          textAlign: "center", padding: "28px 20px", borderRadius: 14,
          background: COLORS.surface, border: `1px dashed ${COLORS.accent}25`,
          color: COLORS.textSecondary, fontSize: 13, lineHeight: 1.6,
        }}>
          No supporters yet — <span style={{ color: COLORS.accent, fontWeight: 700 }}>be the first</span> to back FullSend
          <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 4 }}>Use the “Tip FullSend” button in the footer 💚</div>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 12 }}>
          {supporters.map((s, i) => {
            const top = false; // tiers disabled for now — uniform cards, sorted by amount
            const name = s.name || `${s.address.slice(0, 6)}…${s.address.slice(-4)}`;
            return (
              <a key={s.address}
                href={`https://universalprofile.cloud/${s.address}`}
                target="_blank" rel="noopener noreferrer"
                title={`View ${name} on Universal Profile`}
                style={{
                position: "relative",
                display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
                padding: "18px 12px 14px", borderRadius: 14,
                background: top ? `linear-gradient(160deg, ${COLORS.accent}12, ${COLORS.surface})` : COLORS.surface,
                border: `1px solid ${top ? COLORS.accent + "40" : COLORS.border}`,
                boxShadow: top ? `0 0 24px ${COLORS.accent}12` : "none",
                textDecoration: "none", cursor: "pointer", transition: "all 0.2s",
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = `${COLORS.accent}40`; e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = `0 0 20px ${COLORS.accent}12`; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = top ? COLORS.accent + "40" : COLORS.border; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = top ? `0 0 24px ${COLORS.accent}12` : "none"; }}>
                {top && (
                  <div style={{
                    position: "absolute", top: 8, right: 8,
                    fontSize: 8, fontWeight: 800, letterSpacing: "0.5px",
                    color: COLORS.gold, background: `${COLORS.gold}15`,
                    border: `1px solid ${COLORS.gold}30`, borderRadius: 5,
                    padding: "2px 6px", fontFamily: "'JetBrains Mono', monospace",
                  }}>★ TOP</div>
                )}
                <div style={{
                  width: 52, height: 52, borderRadius: "50%", overflow: "hidden",
                  background: `${COLORS.accent}15`, display: "flex", alignItems: "center", justifyContent: "center",
                  border: `2px solid ${top ? COLORS.accent + "50" : COLORS.border}`,
                }}>
                  {isImageAvatar(s.avatar)
                    ? <img src={s.avatar} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    : <span style={{ fontSize: 22 }}>{s.avatar || "👤"}</span>}
                </div>
                <div style={{
                  fontSize: 12, fontWeight: 700, color: COLORS.text, textAlign: "center",
                  maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>{name}</div>
                <div style={{
                  fontSize: 11, fontWeight: 700, color: COLORS.accent,
                  fontFamily: "'JetBrains Mono', monospace",
                }}>{s.total.toFixed(s.total >= 100 ? 0 : 2)} LYX</div>
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SiteFooter({ onNavigate } = {}) {
  const [tipOpen, setTipOpen] = useState(false);
  const [tipAmount, setTipAmount] = useState("");
  const [tipSending, setTipSending] = useState(false);
  const [tipSent, setTipSent] = useState(false);
  const [tipError, setTipError] = useState(null);

  const sendPlatformTip = async () => {
    if (!tipAmount || parseFloat(tipAmount) <= 0) return;
    setTipError(null);
    setTipSending(true);
    try {
      await sendTipTransaction({ creatorAddress: CREATOR_TREASURY_UP, amount: tipAmount, message: PLATFORM_TIP_TAG, direct: true });
      setTipSending(false);
      setTipSent(true);
      setTimeout(() => { setTipSent(false); setTipAmount(""); setTipOpen(false); }, 2500);
    } catch (e) {
      setTipSending(false);
      const m = (e && e.message) || "";
      setTipError(
        e?.code === 4001 ? "Transaction rejected"
        : /not connected/i.test(m) ? "Connect your wallet first (top-right Connect)"
        : /yourself/i.test(m) ? "That's your own profile 🙂"
        : /Must send LYX/i.test(m) ? "Wallet didn't attach the LYX — try again"
        : /insufficient|exceeds balance/i.test(m) ? "Not enough LYX"
        : "Tip failed — please try again"
      );
      setTimeout(() => setTipError(null), 6000);
    }
  };

  return (
    <div style={{
      maxWidth: 720, margin: "0 auto",
      padding: "24px 24px 20px",
    }}>
      <div style={{
        borderTop: `1px solid ${COLORS.border}`,
        paddingTop: 20,
        display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
      }}>
        <div style={{
          fontFamily: "'Syne', sans-serif",
          fontSize: 12, fontWeight: 700, color: COLORS.textSecondary,
        }}>Metaheads Studios</div>
        <div style={{
          fontSize: 10, color: COLORS.textMuted,
        }}>
          Created by <span style={{ fontWeight: 600, color: COLORS.textSecondary }}>Alts Anonymous</span>
        </div>

        {/* Legal links */}
        <div style={{ display: "flex", gap: 16, marginTop: 2 }}>
          <a href="/terms" style={{
            fontSize: 9, color: COLORS.textMuted,
            fontFamily: "'JetBrains Mono', monospace",
            textDecoration: "none",
            letterSpacing: "0.8px", textTransform: "uppercase",
            transition: "color 0.2s",
          }}
          onMouseEnter={e => e.currentTarget.style.color = COLORS.accent}
          onMouseLeave={e => e.currentTarget.style.color = COLORS.textMuted}>Terms</a>
          <span style={{ fontSize: 9, color: COLORS.textMuted, opacity: 0.5 }}>·</span>
          <a href="/privacy" style={{
            fontSize: 9, color: COLORS.textMuted,
            fontFamily: "'JetBrains Mono', monospace",
            textDecoration: "none",
            letterSpacing: "0.8px", textTransform: "uppercase",
            transition: "color 0.2s",
          }}
          onMouseEnter={e => e.currentTarget.style.color = COLORS.cyan}
          onMouseLeave={e => e.currentTarget.style.color = COLORS.textMuted}>Privacy</a>
        </div>

        {/* Links + Tip Button */}
        <div style={{
          display: "flex", alignItems: "center", gap: 8,
          marginTop: 4,
        }}>
          <a href="https://profile.link/alts@29d7" target="_blank" rel="noopener noreferrer" style={{
            fontSize: 9, color: COLORS.accent,
            fontFamily: "'JetBrains Mono', monospace",
            textDecoration: "none",
            padding: "3px 8px", borderRadius: 4,
            background: `${COLORS.accent}08`,
            border: `1px solid ${COLORS.accent}12`,
            transition: "all 0.2s",
            display: "flex", alignItems: "center", gap: 4,
          }}
          onMouseEnter={e => e.currentTarget.style.background = `${COLORS.accent}15`}
          onMouseLeave={e => e.currentTarget.style.background = `${COLORS.accent}08`}>
            <svg width="9" height="9" viewBox="0 0 24 24" fill={COLORS.accent} style={{ display: "block" }}>
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
            </svg>
            UP Profile
          </a>
          <a href="https://x.com/Alts_Anonymous" target="_blank" rel="noopener noreferrer" style={{
            fontSize: 9, color: COLORS.text,
            fontFamily: "'JetBrains Mono', monospace",
            textDecoration: "none",
            padding: "3px 8px", borderRadius: 4,
            background: `${COLORS.text}06`,
            border: `1px solid ${COLORS.text}08`,
            transition: "all 0.2s",
            display: "flex", alignItems: "center", gap: 4,
          }}
          onMouseEnter={e => e.currentTarget.style.background = `${COLORS.text}12`}
          onMouseLeave={e => e.currentTarget.style.background = `${COLORS.text}06`}>
            <svg width="9" height="9" viewBox="0 0 24 24" fill="#fff" style={{ display: "block" }}>
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
            </svg>
            @Alts_Anonymous
          </a>
          <button onClick={() => setTipOpen(!tipOpen)} style={{
            fontSize: 9,
            fontFamily: "'JetBrains Mono', monospace",
            fontWeight: 700,
            textDecoration: "none",
            padding: "3px 10px", borderRadius: 4,
            background: tipOpen ? `${COLORS.accent}15` : `linear-gradient(135deg, ${COLORS.accent}15, ${COLORS.cyan}15)`,
            border: `1px solid ${tipOpen ? COLORS.accent + "30" : COLORS.accent + "20"}`,
            color: COLORS.accent,
            cursor: "pointer",
            transition: "all 0.2s",
            display: "flex", alignItems: "center", gap: 4,
          }}
          onMouseEnter={e => e.currentTarget.style.transform = "translateY(-1px)"}
          onMouseLeave={e => e.currentTarget.style.transform = "translateY(0)"}>
            💚 Tip FullSend
          </button>
        </div>

        {/* Tip FullSend Dropdown */}
        {tipOpen && (
          <div style={{
            marginTop: 4, padding: 12, borderRadius: 10,
            background: COLORS.surface,
            border: `1px solid ${COLORS.accent}15`,
            width: "100%", maxWidth: 320,
            animation: "slideDown 0.25s ease-out",
          }}>
            <div style={{ fontSize: 10, color: COLORS.textMuted, marginBottom: 8, textAlign: "center", lineHeight: 1.5 }}>
              Help keep FullSend running — tips go directly to the platform's UP
            </div>
            <div style={{ display: "flex", gap: 4, marginBottom: 8 }}>
              {[1, 5, 10, 25].map(val => (
                <button key={val} onClick={() => setTipAmount(val.toString())} style={{
                  flex: 1, padding: "6px 0", borderRadius: 5,
                  background: parseFloat(tipAmount) === val ? `${COLORS.accent}18` : COLORS.bgAlt,
                  border: `1px solid ${parseFloat(tipAmount) === val ? COLORS.accent + "30" : COLORS.border}`,
                  color: parseFloat(tipAmount) === val ? COLORS.accent : COLORS.textSecondary,
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 11, fontWeight: 600, cursor: "pointer",
                  transition: "all 0.15s",
                }}>{val}</button>
              ))}
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <input
                type="number"
                value={tipAmount}
                onChange={e => setTipAmount(e.target.value)}
                placeholder="Custom"
                style={{
                  flex: 1, padding: "7px 10px",
                  background: COLORS.bgAlt,
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: 6, color: COLORS.text,
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 11, outline: "none", boxSizing: "border-box",
                }}
              />
              <button onClick={sendPlatformTip} disabled={!tipAmount || parseFloat(tipAmount) <= 0 || tipSending} style={{
                padding: "7px 16px", borderRadius: 6,
                background: tipSent
                  ? COLORS.accent
                  : tipSending
                    ? COLORS.surface
                    : `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.cyan})`,
                color: tipSending ? COLORS.textMuted : COLORS.bg,
                border: tipSending ? `1px solid ${COLORS.border}` : "none",
                fontFamily: "'Syne', sans-serif",
                fontSize: 11, fontWeight: 700, cursor: "pointer",
                opacity: (!tipAmount || parseFloat(tipAmount) <= 0) ? 0.4 : 1,
                transition: "all 0.2s",
                whiteSpace: "nowrap",
              }}>
                {tipSent ? "✓ Sent!" : tipSending ? "..." : `Send ${tipAmount || "0"} LYX`}
              </button>
            </div>
            {tipError && (
              <div style={{
                marginTop: 8, fontSize: 10, color: COLORS.red, textAlign: "center",
                fontFamily: "'JetBrains Mono', monospace",
              }}>{tipError}</div>
            )}
            <button
              onClick={() => {
                setTipOpen(false);
                if (onNavigate) onNavigate("partners");
                // navigate() scrolls to top after a 200ms transition, so wait
                // for the Partners page to mount before scrolling to the board
                setTimeout(() => {
                  document.getElementById("fullsend-supporters")?.scrollIntoView({ behavior: "smooth", block: "start" });
                }, 500);
              }}
              style={{
                width: "100%", marginTop: 10, padding: "7px 0", borderRadius: 6,
                background: "transparent",
                border: `1px solid ${COLORS.accent}25`,
                color: COLORS.accent,
                fontFamily: "'Syne', sans-serif",
                fontSize: 10, fontWeight: 700, cursor: "pointer",
                transition: "all 0.15s",
              }}
              onMouseEnter={e => { e.currentTarget.style.background = `${COLORS.accent}12`; }}
              onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
            >
              🏆 View Supporters Board →
            </button>
          </div>
        )}

        <div style={{
          fontSize: 8, color: COLORS.textMuted, marginTop: 4,
          display: "flex", alignItems: "center", gap: 8,
        }}>
          <span>Built with 💚 on LUKSO</span>

        </div>
      </div>
    </div>
  );
}

// --- IDEA / PARTNERSHIP FORM ---------------------------------
function IdeaForm({ type }) {
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [twitter, setTwitter] = useState("");
  const [website, setWebsite] = useState("");
  const [emailError, setEmailError] = useState("");
  const [sent, setSent] = useState(false);
  const [files, setFiles] = useState([]);
  const fileRef = useRef(null);

  const color = type === "idea" ? COLORS.accent : COLORS.purple;

  const validateEmail = (v) => {
    if (!v) return false; // required
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
  };

  const handleFiles = (e) => {
    const newFiles = Array.from(e.target.files || []).slice(0, 3 - files.length);
    setFiles(prev => [...prev, ...newFiles.map(f => ({ name: f.name, size: (f.size / 1024).toFixed(1) + " KB", type: f.type, file: f }))].slice(0, 3));
  };

  const removeFile = (idx) => setFiles(prev => prev.filter((_, i) => i !== idx));

  const handleSubmit = async () => {
    if (!message.trim()) return;
    if (!email.trim()) {
      setEmailError("Email is required");
      return;
    }
    if (!validateEmail(email)) {
      setEmailError("Please enter a valid email");
      return;
    }
    setEmailError("");

    // Convert files to base64
    const fileData = [];
    for (const f of files) {
      if (f.file && f.file.size < 5 * 1024 * 1024) {
        const base64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.readAsDataURL(f.file);
        });
        fileData.push({ name: f.name, type: f.type, data: base64 });
      }
    }

    // Save to database
    fetch("/api/submissions", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        type, // "idea" or "partnership"
        name: name.trim() || "Anonymous",
        email: email.trim(),
        twitter: twitter.trim() || "",
        website: website.trim() || "",
        message: message.trim(),
        reporter_name: window.__fullsend_profile?.name || "Anonymous",
        reporter_address: window.__fullsend_profile?.address || "Not connected",
        files: fileData.slice(0, 3),
      }),
    }).catch(() => {});

    setSent(true);
    setTimeout(() => setSent(false), 4000);
    setName(""); setMessage(""); setEmail(""); setTwitter(""); setWebsite(""); setFiles([]);
  };

  if (sent) {
    return (
      <div style={{
        padding: "16px", borderRadius: 8, textAlign: "center",
        background: `${color}08`, border: `1px solid ${color}15`,
        animation: "fadeInUp 0.3s ease-out",
      }}>
        <div style={{ fontSize: 22, marginBottom: 6 }}>{type === "idea" ? "🎉" : "🤝"}</div>
        <div style={{ fontSize: 12, fontWeight: 700, color }}>
          {type === "idea" ? "Thanks! We'll review your idea." : "Thanks! We'll be in touch."}
        </div>
        <div style={{ fontSize: 10, color: COLORS.textMuted, marginTop: 4 }}>
          {type === "idea" ? "Every suggestion helps shape FullSend." : "Expect a response within 48 hours."}
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ display: "flex", gap: 8 }}>
        <input value={name} onChange={e => setName(e.target.value)} placeholder="Name (optional)" style={{
          flex: 1, padding: "8px 12px", background: COLORS.bgAlt, border: `1px solid ${COLORS.border}`,
          borderRadius: 6, color: COLORS.text, fontFamily: "'Outfit', sans-serif", fontSize: 11, outline: "none", boxSizing: "border-box",
        }} />
        {type === "partnership" && (
          <input value={email} onChange={e => { setEmail(e.target.value); setEmailError(""); }} placeholder="Email *" style={{
            flex: 1, padding: "8px 12px", background: COLORS.bgAlt,
            border: `1px solid ${emailError ? COLORS.red + "50" : COLORS.border}`,
            borderRadius: 6, color: COLORS.text, fontFamily: "'Outfit', sans-serif", fontSize: 11, outline: "none", boxSizing: "border-box",
          }} />
        )}
      </div>
      {emailError && <div style={{ fontSize: 9, color: COLORS.red, marginTop: -4 }}>{emailError}</div>}
      <input value={twitter} onChange={e => setTwitter(e.target.value)} placeholder="𝕏 handle (optional, e.g. @username)" style={{
        width: "100%", padding: "8px 12px", background: COLORS.bgAlt, border: `1px solid ${COLORS.border}`,
        borderRadius: 6, color: COLORS.text, fontFamily: "'Outfit', sans-serif", fontSize: 11, outline: "none", boxSizing: "border-box",
      }} />
      <input value={website} onChange={e => setWebsite(e.target.value)} placeholder="Website URL (optional)" style={{
        width: "100%", padding: "8px 12px", background: COLORS.bgAlt, border: `1px solid ${COLORS.border}`,
        borderRadius: 6, color: COLORS.text, fontFamily: "'Outfit', sans-serif", fontSize: 11, outline: "none", boxSizing: "border-box",
      }} />
      <textarea value={message} onChange={e => setMessage(e.target.value)} rows={3}
        placeholder={type === "idea" ? "Share your idea or feedback..." : "Tell us about your partnership idea..."}
        style={{
          width: "100%", padding: "10px 12px", background: COLORS.bgAlt, border: `1px solid ${COLORS.border}`,
          borderRadius: 6, color: COLORS.text, fontFamily: "'Outfit', sans-serif", fontSize: 11,
          outline: "none", boxSizing: "border-box", resize: "vertical",
        }}
      />
      {/* File upload */}
      <div>
        <input ref={fileRef} type="file" accept="image/*,.pdf,.doc,.docx,.txt" multiple onChange={handleFiles} style={{ display: "none" }} />
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          {files.length < 3 && (
            <button onClick={() => fileRef.current?.click()} style={{
              padding: "6px 12px", borderRadius: 5,
              background: COLORS.bgAlt, border: `1px dashed ${COLORS.textMuted}25`,
              color: COLORS.textMuted, fontSize: 9, fontWeight: 600, cursor: "pointer",
              fontFamily: "'JetBrains Mono', monospace",
              display: "flex", alignItems: "center", gap: 5,
            }}>
              <span style={{ fontSize: 12 }}>📎</span> Attach file
            </button>
          )}
          {files.map((f, i) => (
            <div key={i} style={{
              padding: "4px 8px", borderRadius: 4,
              background: `${color}08`, border: `1px solid ${color}12`,
              fontSize: 9, color: COLORS.textSecondary,
              display: "flex", alignItems: "center", gap: 5,
            }}>
              <span>{f.type?.startsWith("image") ? "🖼️" : "📄"}</span>
              <span style={{ maxWidth: 100, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</span>
              <span style={{ color: COLORS.textMuted, fontSize: 8 }}>{f.size}</span>
              <span onClick={() => removeFile(i)} style={{ cursor: "pointer", color: COLORS.red, fontSize: 10, marginLeft: 2 }}>x</span>
            </div>
          ))}
        </div>
        {files.length > 0 && (
          <div style={{ fontSize: 8, color: COLORS.textMuted, marginTop: 3 }}>{files.length}/3 files attached</div>
        )}
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <button onClick={handleSubmit} style={{
          padding: "10px 20px", borderRadius: 6, border: "none",
          background: `linear-gradient(135deg, ${color}, ${type === "idea" ? COLORS.cyan : COLORS.blue})`,
          color: COLORS.bg, fontFamily: "'Syne', sans-serif", fontSize: 12, fontWeight: 700,
          cursor: message.trim() ? "pointer" : "not-allowed",
          opacity: message.trim() ? 1 : 0.5,
        }}>
          {type === "idea" ? "Send Feedback" : "Send Inquiry"}
        </button>
        <span style={{ fontSize: 8, color: COLORS.textMuted }}>Images, PDFs, docs up to 5MB</span>
      </div>
    </div>
  );
}

// --- PARTNERS PAGE -------------------------------------------
function PartnersPage() {
  const [mounted, setMounted] = useState(false);
  useEffect(() => { requestAnimationFrame(() => setMounted(true)); }, []);

  // Partner data — add new partners here
  const partners = [
    { name: "Metaheads Studios", logo: "/Metaheads_logo.png", url: "https://profile.link/metaheads@bDFE", description: "The team behind FullSend. Building decentralized creator tools on LUKSO.", tier: "platinum" },
  ];

  const tiers = [
    { id: "platinum", label: "Platinum", color: "#e5e4e2", perks: "Logo featured at top of Partners page, homepage visibility, social shoutouts, creator onboarding placement" },
    { id: "gold", label: "Gold", color: COLORS.gold, perks: "Logo on Partners page with link, footer mention, social features" },
    { id: "silver", label: "Silver", color: "#c0c0c0", perks: "Logo on Partners page with link" },
  ];

  return (
    <div style={{
      maxWidth: 720, margin: "0 auto", padding: "28px 24px",
      opacity: mounted ? 1 : 0,
      transform: mounted ? "translateY(0)" : "translateY(15px)",
      transition: "all 0.5s cubic-bezier(0.16, 1, 0.3, 1)",
    }}>
      {/* Hero */}
      <div style={{ textAlign: "center", marginBottom: 40 }}>
        <div style={{
          fontSize: 48, fontWeight: 900,
          fontFamily: "'Syne', sans-serif",
          letterSpacing: "-2px",
          lineHeight: 1,
          marginBottom: 12,
          background: `linear-gradient(135deg, ${COLORS.gold}, ${COLORS.orange}, ${COLORS.accent})`,
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
        }}>Partners</div>
        <div style={{
          fontSize: 14, color: COLORS.textSecondary,
          fontFamily: "'Outfit', sans-serif",
          fontWeight: 300,
          maxWidth: 480, margin: "0 auto",
          lineHeight: 1.7,
        }}>
          FullSend is powered by LUKSO and built alongside its ecosystem. Our partners help drive the platform forward and support creators everywhere.
        </div>
      </div>

      {/* Partners Grid */}
      <div style={{
        display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
        gap: 16, marginBottom: 40,
      }}>
        {partners.map((p, i) => (
          <a key={i} href={p.url} target="_blank" rel="noopener noreferrer" style={{
            textDecoration: "none",
            padding: "24px 16px", borderRadius: 14,
            background: COLORS.surface,
            border: `1px solid ${p.tier === "platinum" ? "#e5e4e2" : p.tier === "gold" ? COLORS.gold : "#c0c0c0"}25`,
            display: "flex", flexDirection: "column", alignItems: "center", gap: 12,
            transition: "all 0.2s",
            cursor: "pointer",
          }}
          onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.borderColor = `${p.tier === "platinum" ? "#e5e4e2" : p.tier === "gold" ? COLORS.gold : "#c0c0c0"}50`; }}
          onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.borderColor = `${p.tier === "platinum" ? "#e5e4e2" : p.tier === "gold" ? COLORS.gold : "#c0c0c0"}25`; }}
          >
            {p.logo && <img src={p.logo} alt={p.name} style={{ height: 100, maxWidth: 200, objectFit: "contain" }} />}
            <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.text, textAlign: "center", fontFamily: "'Syne', sans-serif" }}>{p.name}</div>
            {p.description && <div style={{ fontSize: 10, color: COLORS.textMuted, textAlign: "center", lineHeight: 1.5 }}>{p.description}</div>}
            <span style={{
              fontSize: 8, fontWeight: 700, textTransform: "uppercase", letterSpacing: "1px",
              padding: "2px 8px", borderRadius: 4,
              background: `${p.tier === "platinum" ? "#e5e4e2" : p.tier === "gold" ? COLORS.gold : "#c0c0c0"}15`,
              color: p.tier === "platinum" ? "#e5e4e2" : p.tier === "gold" ? COLORS.gold : "#c0c0c0",
            }}>{p.tier}</span>
          </a>
        ))}
      </div>

      {/* Become a Partner */}
      <div style={{
        background: COLORS.surface, border: `1px solid ${COLORS.border}`,
        borderRadius: 16, padding: "24px 20px", marginBottom: 30,
      }}>
        <div style={{
          fontFamily: "'Syne', sans-serif", fontSize: 17, fontWeight: 800,
          marginBottom: 6, display: "flex", alignItems: "center", gap: 8,
        }}>
          <span style={{ fontSize: 20 }}>🤝</span>
          <span style={{
            background: `linear-gradient(135deg, ${COLORS.gold}, ${COLORS.orange})`,
            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
          }}>Become a Partner</span>
        </div>
        <div style={{ fontSize: 12, color: COLORS.textSecondary, lineHeight: 1.7, marginBottom: 16 }}>
          Interested in integrating with FullSend, collaborating on content, or exploring a partnership? We'd love to hear from you.
        </div>
        <IdeaForm type="partnership" />
      </div>

      {/* Partnership Tiers */}
      <div style={{
        background: COLORS.surface, border: `1px solid ${COLORS.border}`,
        borderRadius: 16, padding: "24px 20px", marginBottom: 30,
      }}>
        <div style={{
          fontFamily: "'Syne', sans-serif", fontSize: 17, fontWeight: 800,
          marginBottom: 16, display: "flex", alignItems: "center", gap: 8,
        }}>
          <span style={{ fontSize: 20 }}>💎</span>
          <span style={{ color: COLORS.text }}>Partnership Tiers</span>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {tiers.map(tier => (
            <div key={tier.id} style={{
              padding: "16px 18px", borderRadius: 12,
              background: COLORS.bgAlt,
              border: `1px solid ${tier.color}20`,
              display: "flex", alignItems: "flex-start", gap: 14,
            }}>
              <div style={{
                width: 36, height: 36, borderRadius: 10,
                background: `${tier.color}15`,
                border: `1.5px solid ${tier.color}30`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 16, flexShrink: 0,
              }}>
                {tier.id === "platinum" ? "💠" : tier.id === "gold" ? "🥇" : "🥈"}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: tier.color, fontFamily: "'Syne', sans-serif", marginBottom: 4 }}>{tier.label}</div>
                <div style={{ fontSize: 11, color: COLORS.textSecondary, lineHeight: 1.6 }}>{tier.perks}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Why Partner */}
      <div style={{
        background: COLORS.surface, border: `1px solid ${COLORS.cyan}12`,
        borderRadius: 16, padding: "24px 20px",
      }}>
        <div style={{
          fontFamily: "'Syne', sans-serif", fontSize: 17, fontWeight: 800,
          marginBottom: 16, display: "flex", alignItems: "center", gap: 8,
        }}>
          <span style={{ fontSize: 20 }}>🎯</span>
          <span style={{ color: COLORS.text }}>Why Partner with FullSend?</span>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {[
            { icon: "👀", title: "Visibility", desc: "Your brand in front of every creator and tipper on the platform" },
            { icon: "🔗", title: "LUKSO Ecosystem", desc: "Align with the leading creator monetization platform on LUKSO" },
            { icon: "📊", title: "Analytics", desc: "Monthly reports on impressions, clicks, and platform growth" },
            { icon: "🎨", title: "Co-Creation", desc: "Collaborate on features, integrations, and community events" },
          ].map((item, i) => (
            <div key={i} style={{
              display: "flex", alignItems: "flex-start", gap: 10,
              padding: "8px 0",
            }}>
              <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>{item.icon}</span>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.text, marginBottom: 2 }}>{item.title}</div>
                <div style={{ fontSize: 11, color: COLORS.textMuted, lineHeight: 1.5 }}>{item.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Supporters board — UPs who tipped FullSend via the Tip FullSend button */}
      <div id="fullsend-supporters"><FullSendSupporters /></div>
    </div>
  );
}

// --- ABOUT & FAQ ---------------------------------------------
function AboutPage() {
  const [mounted, setMounted] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  useEffect(() => { requestAnimationFrame(() => setMounted(true)); }, []);

  const faqs = [
    {
      q: "Does 100% of my tip really go to the creator?",
      a: "Yes — 100% of the tip amount reaches the creator's Universal Profile. FullSend adds a flat 0.50 LYX fee on top of every tip — this covers the platform and gas (via LUKSO's relay service), so you never see a gas popup. Tip 5 LYX → creator gets 5, you pay 5.50. Tip 50 LYX → creator gets 50, you pay 50.50. Compare that to Twitch (50% cut) or YouTube Super Chat (30% cut) — and on top of those platform cuts, credit card processors and payment networks can take an additional 2-4% in fees that creators never see."
    },
    {
      q: "How are gas fees handled?",
      a: "FullSend is built so gas never gets in your way. Free poll votes, token-gated votes, and claim drops are fully covered by FullSend — they happen with zero wallet popups, zero gas, and nothing to set up. Tips, LYX-gated votes, and follows take a quick one-tap wallet confirmation: you sign and submit those yourself, so you stay in full control of every LYX flow — and LUKSO sponsors the gas on Universal Profile transactions, so you won't pay a network fee there either. Bottom line: nobody ever pays gas on FullSend."
    },
    {
      q: "What does FullSend cover the gas for?",
      a: "FullSend's relayer covers the gas for three things automatically — free poll votes, token-gated poll votes, and claim drops — with no setup, no wallet popups, and nothing to enable. For votes the relayer submits to the poll contract on your behalf; for claims it mints the drop straight to you. Neither requires any permission on your Universal Profile. Tips, LYX-gated votes, and follows are the exception: those always confirm in your wallet (one tap, no gas — LUKSO covers it), because any LYX flow must be authorized by you directly. The relayer can never move LYX or touch your funds under any configuration."
    },
    {
      q: "Why build on LUKSO instead of Ethereum or Solana?",
      a: "LUKSO was purpose-built for social and creative use cases. Universal Profiles give every user a rich on-chain identity (avatar, name, bio, links) — not just a hex address. Gas fees are consistently low and fast (~3 second finality). LSP8 NFTs are natively tied to profiles. And the follow system is on-chain, which powers our live follower alerts. No other chain gives us all of this out of the box."
    },
    {
      q: "Do I need a Universal Profile to tip?",
      a: "To send a tip, yes — you need a Universal Profile with some LYX. This is what makes FullSend different from traditional tipping: your identity, your tip history, and your earned NFTs are all tied to your UP and portable across the LUKSO ecosystem. To vote in free polls, you don't need a wallet at all."
    },
    {
      q: "How do token-gated polls work?",
      a: "Creators can set up polls with three access levels: free (anyone can vote, no wallet needed), LYX-gated (costs a set amount of LYX per vote — 100% goes to the creator, no platform fee), or token-gated (voters must hold a specific NFT collection via LSP8, or a minimum balance of an LSP7 token). FullSend covers gas on all poll transactions. This lets creators give their top supporters a stronger voice in community decisions, or use paid voting as a fundraising mechanic."
    },
    {
      q: "What are milestone NFTs?",
      a: "When your total tips to a channel cross certain thresholds (50, 100, 200, 300 LYX), you automatically receive an LSP8 NFT minted to your Universal Profile. These are Bronze, Silver, Gold, and Diamond tiers. They show up in your UP grid, unlock perks like custom chat badges and exclusive poll access, and serve as on-chain proof of your support history."
    },
    {
      q: "How does the stream overlay work?",
      a: "The overlay is a browser source URL you add to your streaming software (OBS, Streamlabs, XSplit, or any app with browser source support). It displays tip alerts, follow notifications, micro tip acknowledgments, live poll results, and an optional chat feed — all positioned wherever you want on screen via the layout editor. Tips $5+ go through your moderation queue before showing on stream. Under $5 auto-displays with just the tipper's handle and amount."
    },
    {
      q: "Can I stream on multiple platforms?",
      a: "Absolutely. FullSend is platform-agnostic — it runs as a stream overlay and an embeddable widget. You can stream to YouTube, Kick, Twitch, or anywhere simultaneously. The tip widget lives on its own URL that you share with viewers or embed in your LUKSO Universal Profile grid. YouTube chat integration posts tip messages directly into your YouTube live chat via the Data API."
    },
    {
      q: "What happens to my tips if I'm not live?",
      a: "Tips are on-chain transactions — they go through regardless of whether you're streaming. The tip widget is active 24/7. Viewers can tip and send messages anytime — when you're live, the message shows on stream. When you're offline, the tip and message go to your control panel so you can read them later. Alerts only fire on the overlay when your stream is live."
    },
    {
      q: "Is my tip message stored on-chain?",
      a: "No — messages are ephemeral by design. They appear in the YouTube live chat and the stream overlay, then they're gone. Only the transaction itself (who tipped, how much, when) is recorded on-chain. This keeps costs low and avoids storing potentially sensitive content permanently on a public ledger."
    },
    {
      q: "How do I get started as a creator?",
      a: "Connect your Universal Profile, and your FullSend channel is created automatically. You get a tip widget URL to share with viewers (or embed in your UP grid), a control panel for moderating tips, an overlay URL to add as a browser source in any streaming app, and access to polls, leaderboard, and NFT settings. No approval process, no fees, no setup cost."
    },
  ];

  return (
    <div style={{
      maxWidth: 720, margin: "0 auto", padding: isMobileDevice() ? "16px 14px" : "28px 24px",
      opacity: mounted ? 1 : 0,
      transform: mounted ? "translateY(0)" : "translateY(15px)",
      transition: "all 0.5s cubic-bezier(0.16, 1, 0.3, 1)",
    }}>
      {/* Hero */}
      <div style={{ textAlign: "center", marginBottom: isMobileDevice() ? 24 : 40 }}>
        <div style={{
          fontSize: isMobileDevice() ? 32 : 48, fontWeight: 900,
          fontFamily: "'Syne', sans-serif",
          letterSpacing: "-2px",
          lineHeight: 1,
          marginBottom: 12,
          background: `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.cyan}, ${COLORS.blue})`,
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
        }}>About FullSend</div>
        <div style={{
          fontSize: 14, color: COLORS.textSecondary,
          fontFamily: "'Outfit', sans-serif",
          fontWeight: 300,
          maxWidth: 500, margin: "0 auto",
          lineHeight: 1.7,
        }}>
          Decentralized tipping for live streamers, built on LUKSO.
          No middleman. No cut from your tip. 100% reaches the creator.
        </div>
      </div>

      {/* Key Points — top of page */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 40 }}>
        {[
          {
            icon: "💸",
            title: "100% to Creator",
            desc: "Every LYX you tip goes straight to the creator. A flat 0.50 LYX fee is added on top — covers the platform and gas. You never approve a gas transaction.",
            color: COLORS.accent,
          },
          {
            icon: "⛽",
            title: "Gas? Covered.",
            desc: "Free votes, token-gated votes, and claim drops are fully on us — no gas, no popups, nothing to set up. Tips, LYX votes, and follows take one tap to confirm, and LUKSO sponsors that gas too, so nobody ever pays a network fee.",
            color: COLORS.cyan,
          },
          {
            icon: "🆔",
            title: "Portable Identity",
            desc: "Your tips, follows, NFTs, unlocks, perks, and milestone achievements all live on your Universal Profile. Portable across all of LUKSO, not locked in one platform.",
            color: COLORS.purple,
          },
        ].map((item, i) => (
          <div key={i} style={{
            background: COLORS.surface,
            border: `1px solid ${COLORS.border}`,
            borderRadius: 12,
            padding: "20px 16px",
            textAlign: "center",
            transition: "all 0.3s",
            animation: `fadeInUp 0.5s ease-out ${0.1 + i * 0.08}s both`,
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = `${item.color}25`; e.currentTarget.style.transform = "translateY(-2px)"; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = COLORS.border; e.currentTarget.style.transform = "translateY(0)"; }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>{item.icon}</div>
            <div style={{
              fontFamily: "'Syne', sans-serif",
              fontSize: 13, fontWeight: 700, color: item.color,
              marginBottom: 6,
            }}>{item.title}</div>
            <div style={{ fontSize: 11, color: COLORS.textMuted, lineHeight: 1.6 }}>{item.desc}</div>
          </div>
        ))}
      </div>

      {/* No gatekeeping */}
      <div style={{
        background: `linear-gradient(135deg, ${COLORS.surface}, ${COLORS.bgAlt})`,
        border: `1px solid ${COLORS.accent}15`,
        borderRadius: 16,
        padding: "24px 20px",
        marginBottom: 40,
        position: "relative",
        overflow: "hidden",
      }}>
        <div style={{
          fontFamily: "'Syne', sans-serif",
          fontSize: 17, fontWeight: 800, color: COLORS.accent,
          marginBottom: 10,
        }}>🔓 No Gatekeeping</div>
        <div style={{ fontSize: 13, color: COLORS.textSecondary, lineHeight: 1.7 }}>
          On most platforms you can't accept tips until you qualify. YouTube Super Chats, for example, have to be unlocked and manually switched on in YouTube Studio — and only after you've been accepted into the YouTube Partner Program, hit the subscriber threshold (500+ for fan funding), live in an eligible country, and aren't streaming age-restricted or "made for kids" content. Twitch, Facebook, Instagram, and Kick all gate their tipping behind similar affiliate or monetization programs.
          <br /><br />
          <span style={{ color: COLORS.text, fontWeight: 700 }}>FullSend has none of that.</span> No partner program, no follower minimum, no approval queue, no country restrictions. Connect your Universal Profile and you can accept tips on your very first stream — <span style={{ color: COLORS.accent, fontWeight: 700 }}>permissionless by design.</span>
        </div>
      </div>

      {/* Seamless UX */}
      <div style={{
        background: `linear-gradient(135deg, ${COLORS.surface}, ${COLORS.bgAlt})`,
        border: `1px solid ${COLORS.accent}15`,
        borderRadius: 16,
        padding: "24px 20px",
        marginBottom: 40,
        position: "relative",
        overflow: "hidden",
      }}>
        <div style={{
          position: "absolute", top: -30, right: -30,
          width: 120, height: 120, borderRadius: "50%",
          background: `radial-gradient(circle, ${COLORS.accent}06 0%, transparent 70%)`,
        }} />
        <div style={{
          fontFamily: "'Syne', sans-serif",
          fontSize: 17, fontWeight: 800, color: COLORS.accent,
          marginBottom: 12, display: "flex", alignItems: "center", gap: 8,
          position: "relative",
        }}>
          <span style={{ fontSize: 22 }}>✨</span> Effortless UX
        </div>
        <div style={{ fontSize: 12, color: COLORS.textSecondary, lineHeight: 1.8, marginBottom: 16, position: "relative" }}>
          FullSend is designed to feel like a Web2 app built on Web3 rails — no confusing token approvals, no friction. We cover the gas on the actions your audience does most: voting in polls (free or token-gated) and claiming token drops happen instantly — no popups, no gas, nothing to set up. Follows and tips are a single tap to confirm, and LUKSO sponsors that gas too, so nobody ever pays a network fee.
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, position: "relative" }}>
          {[
            { step: "1", label: "Vote", desc: "Vote in live polls instantly — every free vote is completely gasless. No popup, nothing to set up.", icon: "📊", color: COLORS.cyan },
            { step: "2", label: "Claim", desc: "Grab token drops during streams with a single tap. Gas-free — viewers pay nothing.", icon: "🎁", color: COLORS.blue },
            { step: "3", label: "Token Gated", desc: "NFT- and token-gated votes verify on-chain and run gasless too — hold the asset, act in one tap.", icon: "🔒", color: COLORS.accent },
          ].map((item, i) => (
            <div key={i} style={{
              padding: "14px 12px",
              background: `${COLORS.bg}60`,
              borderRadius: 10,
              border: `1px solid ${COLORS.border}`,
              textAlign: "center",
            }}>
              <div style={{ fontSize: 22, marginBottom: 6 }}>{item.icon}</div>
              <div style={{
                fontSize: 9, fontWeight: 800, color: COLORS.textMuted,
                background: `${COLORS.bg}80`, padding: "1px 5px", borderRadius: 3,
                fontFamily: "'JetBrains Mono', monospace",
                display: "inline-block", marginBottom: 4,
              }}>{item.step}</div>
              <div style={{ fontSize: 12, fontWeight: 700, color: item.color, fontFamily: "'Syne', sans-serif", marginBottom: 4 }}>{item.label}</div>
              <div style={{ fontSize: 10, color: COLORS.textMuted, lineHeight: 1.5 }}>{item.desc}</div>
            </div>
          ))}
        </div>

        <div style={{
          marginTop: 14, padding: "10px 14px", borderRadius: 8,
          background: `${COLORS.bg}40`,
          border: `1px solid ${COLORS.border}`,
          display: "flex", alignItems: "center", gap: 10,
          position: "relative",
        }}>
          <div style={{ fontSize: 18 }}>🧠</div>
          <div style={{ fontSize: 11, color: COLORS.textMuted, lineHeight: 1.6 }}>
            <span style={{ fontWeight: 700, color: COLORS.textSecondary }}>Why this matters: </span>
            The #1 killer of Web3 adoption is UX friction. Gas approvals, confusing transaction flows, and constant wallet popups scare off mainstream users. FullSend strips that away: voting and claiming drops happen with no popups and no gas at all, and follows and tips are a single tap — never a network fee. The blockchain part stays invisible, but the benefits (on-chain identity, portable reputation, milestone NFTs) are very real.
          </div>
        </div>

        {/* Gasless UX */}
        <div style={{
          marginTop: 14, padding: "14px 16px", borderRadius: 10,
          background: `${COLORS.bg}40`,
          border: `1px solid ${COLORS.accent}15`,
          position: "relative",
        }}>
          <div style={{
            display: "flex", alignItems: "center", gap: 8, marginBottom: 8,
          }}>
            <span style={{ fontSize: 16 }}>⚡</span>
            <span style={{
              fontFamily: "'Syne', sans-serif", fontSize: 13, fontWeight: 700, color: COLORS.accent,
            }}>Gas, on us</span>
          </div>
          <div style={{ fontSize: 11, color: COLORS.textMuted, lineHeight: 1.7 }}>
            FullSend's relayer quietly covers the gas for free poll votes, token-gated votes, and claim drops — your viewers do all three with zero popups, zero gas, and nothing to enable or grant. It just works. Tips, LYX votes, and follows confirm in your wallet (one tap, no gas — LUKSO sponsors it), so you always authorize every LYX flow yourself. More on how that stays safe below.
          </div>
        </div>

        {/* Safety */}
        <div style={{
          marginTop: 14, padding: "14px 16px", borderRadius: 10,
          background: `${COLORS.bg}40`,
          border: `1px solid ${COLORS.accent}15`,
          position: "relative",
        }}>
          <div style={{
            display: "flex", alignItems: "center", gap: 8, marginBottom: 8,
          }}>
            <span style={{ fontSize: 16 }}>🔒</span>
            <span style={{
              fontFamily: "'Syne', sans-serif", fontSize: 13, fontWeight: 700, color: COLORS.accent,
            }}>Safety — your funds can never be drained</span>
          </div>
          <div style={{ fontSize: 11, color: COLORS.textMuted, lineHeight: 1.7 }}>
            FullSend's relayer can never move LYX from your Universal Profile — by contract, not by promise.
            <br /><br />
            <span style={{ color: COLORS.textSecondary, fontWeight: 600 }}>Tips and paid poll votes</span> always require your wallet confirmation. The LYX flow is authorized by you, signed by your key, and submitted to the contract directly. FullSend's relayer is never in the path of any LYX movement.
            <br /><br />
            <span style={{ color: COLORS.textSecondary, fontWeight: 600 }}>The relayer is never a controller on your Universal Profile</span> — it holds no permission on your account at all. It only does two things: submit <code style={{ color: COLORS.accent, fontFamily: "'JetBrains Mono', monospace" }}>vote</code> on free or token-gated polls (on the poll contract) and <code style={{ color: COLORS.accent, fontFamily: "'JetBrains Mono', monospace" }}>mint</code> claim drops (on the creator's own token). Neither touches your UP. It cannot call TipRouter or transfer value from your account under any configuration — enforced on-chain, not by any code FullSend controls.
            <br /><br />
            <span style={{ color: COLORS.textSecondary, fontWeight: 600 }}>If FullSend's relayer key were ever compromised</span>, the worst an attacker could do is spam free or token-gated poll votes and claim mints. They could not access, move, or transfer any LYX from any user, and they hold no permission on anyone's Universal Profile. Your funds are simply not in scope of what the relayer can touch.
          </div>
        </div>
      </div>

      {/* Stream Overlay Compatibility */}
      <div style={{
        background: COLORS.surface,
        border: `1px solid ${COLORS.cyan}12`,
        borderRadius: 16, padding: "24px 20px", marginBottom: 40,
      }}>
        <div style={{
          fontFamily: "'Syne', sans-serif", fontSize: 17, fontWeight: 800, color: COLORS.cyan,
          marginBottom: 6, display: "flex", alignItems: "center", gap: 8,
        }}>
          <span style={{ fontSize: 22 }}>📺</span> Stream Overlay Setup
        </div>
        <div style={{ fontSize: 12, color: COLORS.textSecondary, lineHeight: 1.7, marginBottom: 16 }}>
          Add FullSend's overlay to your stream in under a minute. Tips, follows, polls, goals, and chat all appear live on your broadcast.
        </div>

        <div style={{
          padding: "16px 18px", borderRadius: 10,
          background: COLORS.bgAlt, border: `1px solid ${COLORS.border}`,
          marginBottom: 14,
        }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.text, marginBottom: 10 }}>Quick Setup</div>
          {[
            "Open your streaming software (OBS, Streamlabs, etc.)",
            "Add a new Browser Source to your scene",
            "Paste your overlay URL (found on the Overlay tab)",
            "Set width to 1920 and height to 1080",
            "Done — alerts, polls, and chat will appear on stream",
          ].map((step, i) => (
            <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 6 }}>
              <div style={{
                width: 20, height: 20, borderRadius: 5,
                background: `${COLORS.cyan}12`, border: `1px solid ${COLORS.cyan}20`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 10, fontWeight: 800, color: COLORS.cyan, flexShrink: 0,
                fontFamily: "'JetBrains Mono', monospace",
              }}>{i + 1}</div>
              <div style={{ fontSize: 11, color: COLORS.textSecondary, lineHeight: 1.6, paddingTop: 2 }}>{step}</div>
            </div>
          ))}
        </div>

        <div style={{
          padding: "12px 16px", borderRadius: 8,
          background: `${COLORS.accent}06`, border: `1px solid ${COLORS.accent}12`,
        }}>
          <div style={{ fontSize: 10, color: COLORS.textMuted, lineHeight: 1.7 }}>
            <span style={{ fontWeight: 700, color: COLORS.accent }}>Works with:</span> OBS Studio, Streamlabs, StreamElements, XSplit, Prism Live, Lightstream, Restream Studio, Ecamm Live, vMix — any app with browser source support
          </div>
        </div>
      </div>

      {/* Works on platforms without native tipping */}
      <div style={{
        background: COLORS.surface,
        border: `1px solid ${COLORS.purple}12`,
        borderRadius: 16, padding: "24px 20px", marginBottom: 40,
      }}>
        <div style={{
          fontFamily: "'Syne', sans-serif", fontSize: 17, fontWeight: 800, color: COLORS.purple,
          marginBottom: 6, display: "flex", alignItems: "center", gap: 8,
        }}>
          <span style={{ fontSize: 22 }}>🎙️</span> Works on platforms that don't have tipping
        </div>
        <div style={{ fontSize: 12, color: COLORS.textSecondary, lineHeight: 1.7, marginBottom: 16 }}>
          Streaming on X Spaces? Hosting a Facebook Live? Going live on TikTok or running a Discord Stage? None of these platforms have a built-in super chat. FullSend gives you a monetization layer that works anywhere you have an audience.
        </div>

        <div style={{
          padding: "14px 16px", borderRadius: 10,
          background: COLORS.bgAlt, border: `1px solid ${COLORS.border}`,
          marginBottom: 14,
        }}>
          <div style={{ fontSize: 11, color: COLORS.textSecondary, lineHeight: 1.7, marginBottom: 10 }}>
            Share your FullSend tip page URL with viewers, drop it in your bio, or pin it in your chat. Tips flow directly to your Universal Profile while you're live — no platform middleman, no commission cut, no eligibility requirements, no waiting for a payout.
          </div>
          <div style={{
            display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10,
          }}>
            {["X Spaces", "Facebook Live", "TikTok Live", "Discord Stages", "Instagram Live", "Clubhouse", "LinkedIn Live", "Spotify Live"].map((platform, i) => (
              <span key={i} style={{
                fontSize: 10, fontWeight: 600,
                padding: "4px 9px", borderRadius: 5,
                background: `${COLORS.purple}10`,
                border: `1px solid ${COLORS.purple}20`,
                color: COLORS.purple,
                fontFamily: "'JetBrains Mono', monospace",
                letterSpacing: "0.3px",
              }}>{platform}</span>
            ))}
          </div>
        </div>

        <div style={{
          padding: "12px 16px", borderRadius: 8,
          background: `${COLORS.purple}06`, border: `1px solid ${COLORS.purple}12`,
        }}>
          <div style={{ fontSize: 10, color: COLORS.textMuted, lineHeight: 1.7 }}>
            <span style={{ fontWeight: 700, color: COLORS.purple }}>The bottom line:</span> if you have an audience anywhere — even on platforms with no native monetization — FullSend can be your tip layer. No application, no minimum subscribers, no platform approval. Connect a UP and you're live.
          </div>
        </div>
      </div>

      {/* Getting Started -- UP Onboarding */}

      {/* Now Live — Claim Drops */}
      <div style={{
        background: `linear-gradient(135deg, ${COLORS.cyan}10, ${COLORS.accent}08)`,
        border: `1px solid ${COLORS.cyan}30`,
        borderRadius: 16, padding: "24px 20px", marginBottom: 20,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
          <span style={{ fontSize: 22 }}>🎁</span>
          <span style={{ fontFamily: "'Syne', sans-serif", fontSize: 17, fontWeight: 800, color: COLORS.text }}>Live Claim Drops</span>
          <span style={{ fontSize: 9, fontWeight: 800, color: COLORS.bg, background: COLORS.green, padding: "2px 8px", borderRadius: 20, letterSpacing: "0.5px" }}>NOW LIVE</span>
        </div>
        <div style={{ fontSize: 13, color: COLORS.textSecondary, lineHeight: 1.7, marginBottom: 16 }}>
          Reward your audience during your livestream. Create a token and FullSend mints it straight to viewers when they claim — gas-free for them, no wallet popup. Use it as on-chain reputation, voting power, or community status.
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {[
            ["⏱️", "Launch Timer", "Launch a time-boxed drop (up to 10 min), optionally passphrase-gated so only your live audience can claim."],
            ["⚡", "Mint-on-claim", "Viewers tap Claim on your tip page and the token lands in their Universal Profile — the relayer covers the gas, no signature needed."],
            ["🏆", "Live leaderboards", "Per-token leaderboards rank your top claimers, with a leaderboard overlay you can place anywhere in OBS and that auto-cycles through your top supporters on stream."],
          ].map(([icon, title, desc], i) => (
            <div key={i} style={{ display: "flex", gap: 14, alignItems: "flex-start", padding: "14px 16px", background: COLORS.bgAlt, border: `1px solid ${COLORS.cyan}15`, borderRadius: 10 }}>
              <div style={{ width: 36, height: 36, borderRadius: 8, background: `${COLORS.cyan}12`, border: `1px solid ${COLORS.cyan}20`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>{icon}</div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.text, marginBottom: 3 }}>{title}</div>
                <div style={{ fontSize: 12, color: COLORS.textMuted, lineHeight: 1.6 }}>{desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Coming Soon */}
      <div style={{
        background: COLORS.surface, border: `1px solid ${COLORS.orange}12`,
        borderRadius: 16, padding: "24px 20px", marginBottom: 40,
      }}>
        <div style={{
          fontFamily: "'Syne', sans-serif", fontSize: 17, fontWeight: 800,
          marginBottom: 16, display: "flex", alignItems: "center", gap: 8,
        }}>
          <span style={{ fontSize: 22 }}>🚀</span>
          <span style={{
            background: `linear-gradient(135deg, ${COLORS.orange}, ${COLORS.gold})`,
            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
          }}>Coming Soon</span>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {[
            { icon: "💎", title: "NFT Collection System", desc: "Leaderboard reward NFTs for top tippers. Creator-designed custom NFTs for loyalty perks and token-gated content. Milestone NFTs auto-mint when tippers hit thresholds. All minted as LSP8 tokens on LUKSO — portable across the ecosystem.", color: COLORS.purple },
            { icon: "⭐", title: "Featured Creators", desc: "One featured creator per category in the Discover directory, rotated weekly. Adds visibility for new and smaller creators and incentivizes category diversity across the platform.", color: COLORS.gold },
            { icon: "🪂", title: "Community Airdrops", desc: "Airdrop tokens to anyone who has tipped, voted, followed, or met custom criteria. Set parameters like minimum tip amount, poll participation, or follower status — then batch-send tokens directly to qualifying Universal Profiles. Widget check-in feature lets viewers register for upcoming airdrops during live streams.", color: COLORS.cyan },
            { icon: "📢", title: "Sponsorship & Creator Ads", desc: "Sponsor logo toggle on your tip widget and overlay. OBS banner rotations, branded tip notifications, and sponsor QR codes. Creators monetize their stream real estate while keeping full control of what appears.", color: COLORS.orange },
            { icon: "🏅", title: "Channel Memberships", desc: "Viewers sponsor a creator's channel with recurring LYX payments. Members get perks like exclusive polls, a membership badge on tip alerts and the stream overlay, priority in chat, early access to NFT drops, and member-only airdrops. Creators set their own tier pricing and perks. All managed on-chain through Universal Profiles.", color: COLORS.gold },
            { icon: "🏦", title: "Custom Payout Address", desc: "Route tips and poll revenue to a different wallet, multisig, or split between multiple addresses. Creators choose where their earnings go — personal UP, team treasury, or custom split. Leaderboards and analytics still track everything the same way.", color: COLORS.blue },
            { icon: "🔌", title: "Public API & Webhooks", desc: "Already using Streamlabs, StreamElements, or a custom overlay package like Nerd or Die? Keep your existing setup and just decentralize the tipping layer. A public read-only API exposes goal progress, recent tips, active polls, and follower events — pulled into any third-party overlay. Bonus: tippers come with verified Universal Profile data (real name, on-chain PFP, bio, linked socials), so your existing alerts get richer than they've ever been. Webhooks fire on every tip, follow, and vote so anything that listens for events plugs in cleanly. In the meantime, you can simply add the FullSend tip widget as a second browser source in OBS — runs side-by-side with your existing tipping setup, no integration needed.", color: COLORS.cyan },
          ].map((item, i) => (
            <div key={i} style={{ display: "flex", gap: 14, alignItems: "flex-start", padding: "14px 16px", background: COLORS.bgAlt, border: `1px solid ${item.color}12`, borderRadius: 10 }}>
              <div style={{ width: 36, height: 36, borderRadius: 8, background: `${item.color}12`, border: `1px solid ${item.color}18`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>{item.icon}</div>
              <div>
                <div style={{ fontFamily: "'Syne', sans-serif", fontSize: 13, fontWeight: 700, color: item.color, marginBottom: 3 }}>{item.title}</div>
                <div style={{ fontSize: 11, color: COLORS.textMuted, lineHeight: 1.6 }}>{item.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Getting Started -- UP Onboarding (continued) */}
      <div style={{
        background: `linear-gradient(135deg, ${COLORS.surface}, ${COLORS.bgAlt})`,
        border: `1px solid ${COLORS.purple}12`,
        borderRadius: 16, padding: "24px 20px", marginBottom: 40,
      }}>
        <div style={{
          fontFamily: "'Syne', sans-serif", fontSize: 17, fontWeight: 800, color: COLORS.purple,
          marginBottom: 6, display: "flex", alignItems: "center", gap: 8,
        }}>
          <span style={{ fontSize: 22 }}>🆔</span> Don't have a Universal Profile?
        </div>
        <div style={{ fontSize: 12, color: COLORS.textSecondary, lineHeight: 1.7, marginBottom: 16 }}>
          A Universal Profile is your identity on LUKSO — think of it as a smart wallet with a name, avatar, bio, and the ability to own NFTs, tokens, and reputation. It's free to create, and it's the only thing you need to start tipping on FullSend.
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 16 }}>
          {[
            { step: "1", title: "Create Your UP", desc: "Head to my.universalprofile.cloud and set up your profile in under 2 minutes. Pick a name, avatar, and you're done.", icon: "🌐", color: COLORS.accent, links: [{ url: "https://my.universalprofile.cloud/", label: "Create your UP" }] },
            { step: "2", title: "Get Some $LYX", desc: "Buy $LYX directly with card or Apple Pay via Transak — delivered to any LUKSO-compatible wallet address.", icon: "💰", color: COLORS.cyan, links: [
              { url: "https://transak.com/buy/lyx", label: "Buy with Transak" },
            ] },
            { step: "3", title: "Connect & Tip", desc: "Come back to FullSend, click Connect UP, and you're ready to tip creators, vote in polls, and earn milestone NFTs.", icon: "🚀", color: COLORS.purple, links: [] },
          ].map((item, i) => (
            <div key={i} style={{
              padding: "14px 12px", background: `${COLORS.bg}60`, borderRadius: 10,
              border: `1px solid ${COLORS.border}`, textAlign: "center",
            }}>
              <div style={{ fontSize: 22, marginBottom: 6 }}>{item.icon}</div>
              <div style={{
                fontSize: 9, fontWeight: 800, color: COLORS.textMuted,
                background: `${COLORS.bg}80`, padding: "1px 5px", borderRadius: 3,
                fontFamily: "'JetBrains Mono', monospace",
                display: "inline-block", marginBottom: 4,
              }}>Step {item.step}</div>
              <div style={{ fontSize: 12, fontWeight: 700, color: item.color, fontFamily: "'Syne', sans-serif", marginBottom: 4 }}>{item.title}</div>
              <div style={{ fontSize: 10, color: COLORS.textMuted, lineHeight: 1.5 }}>{item.desc}</div>
              {item.links && item.links.length > 0 && (
                <div style={{ display: "flex", gap: 6, justifyContent: "center", flexWrap: "wrap", marginTop: 8 }}>
                  {item.links.map((lk, li) => (
                    <a key={li} href={lk.url} target="_blank" rel="noopener noreferrer" style={{
                      display: "inline-block", padding: "5px 12px", borderRadius: 5,
                      background: `${item.color}12`, border: `1px solid ${item.color}20`,
                      color: item.color, fontSize: 9, fontWeight: 700, textDecoration: "none",
                      fontFamily: "'JetBrains Mono', monospace",
                    }}>{lk.label}</a>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        <div style={{
          padding: "12px 14px", borderRadius: 8,
          background: `${COLORS.bg}40`, border: `1px solid ${COLORS.border}`,
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.text, marginBottom: 6 }}>Why a Universal Profile?</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {[
              { icon: "🖼️", text: "Your avatar and name show on every tip — not a random hex address" },
              { icon: "💎", text: "Milestone NFTs mint directly to your profile and are visible everywhere on LUKSO" },
              { icon: "🏆", text: "Your tipping reputation follows you across channels and dApps" },
              { icon: "👤", text: "Follows are on-chain — creators see real, verified supporters" },
              { icon: "🔐", text: "You own your identity — no platform can delete it or lock you out" },
            ].map((item, i) => (
              <div key={i} style={{ display: "flex", gap: 8, fontSize: 10, color: COLORS.textSecondary }}>
                <span>{item.icon}</span>
                <span>{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Where to Buy LYX */}
      <div style={{
        marginBottom: 40,
        padding: 20,
        background: `linear-gradient(135deg, ${COLORS.surface}, ${COLORS.bgAlt})`,
        border: `1px solid ${COLORS.cyan}20`,
        borderRadius: 14,
      }}>
        <div style={{
          fontFamily: "'Syne', sans-serif",
          fontSize: 18, fontWeight: 700, color: COLORS.cyan,
          marginBottom: 8, display: "flex", alignItems: "center", gap: 8,
        }}>
          <span style={{ fontSize: 22 }}>💰</span> Where to Buy $LYX
        </div>
        <div style={{ fontSize: 12, color: COLORS.textSecondary, lineHeight: 1.6, marginBottom: 16 }}>
          LYX is the native token of LUKSO. The fastest path for newcomers is to buy directly with a credit card or Apple Pay through Transak — funds go straight to your Universal Profile on the LUKSO network, no exchange account or network switching needed.
        </div>

        {/* Primary: fiat on-ramp */}
        <div style={{
          fontSize: 10, fontWeight: 800, color: COLORS.accent,
          fontFamily: "'JetBrains Mono', monospace", textTransform: "uppercase",
          letterSpacing: "1.5px", marginBottom: 8,
        }}>⚡ Fastest — Buy with Card</div>
        <div style={{ marginBottom: 18 }}>
          {[
            { name: "Transak", tag: "Credit Card · Apple Pay · Bank Transfer · 155+ countries", url: "https://transak.com/buy/lyx", color: COLORS.cyan },
          ].map((source, i) => (
            <a key={i} href={source.url} target="_blank" rel="noopener noreferrer" style={{
              padding: "14px 16px", borderRadius: 8,
              background: `${COLORS.bg}60`, border: `1px solid ${source.color}25`,
              textDecoration: "none",
              display: "flex", flexDirection: "column", gap: 4,
              transition: "all 0.2s",
            }}
            onMouseEnter={e => { e.currentTarget.style.background = `${source.color}10`; e.currentTarget.style.borderColor = `${source.color}60`; }}
            onMouseLeave={e => { e.currentTarget.style.background = `${COLORS.bg}60`; e.currentTarget.style.borderColor = `${source.color}25`; }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: source.color, fontFamily: "'Syne', sans-serif" }}>
                {source.name} →
              </div>
              <div style={{ fontSize: 9, color: COLORS.textMuted, fontFamily: "'JetBrains Mono', monospace", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                {source.tag}
              </div>
            </a>
          ))}
        </div>

        {/* Secondary: exchanges */}
        <div style={{
          fontSize: 10, fontWeight: 800, color: COLORS.textMuted,
          fontFamily: "'JetBrains Mono', monospace", textTransform: "uppercase",
          letterSpacing: "1.5px", marginBottom: 8,
        }}>Already have crypto? Use an exchange</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
          {[
            { name: "CoinGecko", tag: "Full Exchange List", url: "https://www.coingecko.com/en/coins/lukso-token", color: COLORS.accent },
            { name: "CoinMarketCap", tag: "Full Exchange List", url: "https://coinmarketcap.com/currencies/lukso-network/", color: COLORS.blue },
          ].map((source, i) => (
            <a key={i} href={source.url} target="_blank" rel="noopener noreferrer" style={{
              padding: "14px 16px", borderRadius: 8,
              background: `${COLORS.bg}60`, border: `1px solid ${source.color}25`,
              textDecoration: "none",
              display: "flex", flexDirection: "column", gap: 4,
              transition: "all 0.2s",
            }}
            onMouseEnter={e => { e.currentTarget.style.background = `${source.color}10`; e.currentTarget.style.borderColor = `${source.color}60`; }}
            onMouseLeave={e => { e.currentTarget.style.background = `${COLORS.bg}60`; e.currentTarget.style.borderColor = `${source.color}25`; }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: source.color, fontFamily: "'Syne', sans-serif" }}>
                {source.name} →
              </div>
              <div style={{ fontSize: 9, color: COLORS.textMuted, fontFamily: "'JetBrains Mono', monospace", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                {source.tag}
              </div>
            </a>
          ))}
        </div>

        <div style={{
          padding: "10px 14px", borderRadius: 6,
          background: `${COLORS.orange}08`, border: `1px solid ${COLORS.orange}18`,
          fontSize: 10, color: COLORS.textSecondary, lineHeight: 1.6,
        }}>
          <span style={{ color: COLORS.orange, fontWeight: 700 }}>⚠️ Important:</span> When withdrawing, select the <strong style={{ color: COLORS.text }}>LUKSO network</strong> and send to your Universal Profile address (starts with 0x). Sending on the wrong chain will lose your funds. FullSend does not endorse any specific exchange — always do your own research.
        </div>
      </div>

      {/* How it Works */}
      <div style={{ marginBottom: 40 }}>
        <div style={{
          fontFamily: "'Syne', sans-serif",
          fontSize: 11, fontWeight: 700, color: COLORS.textMuted,
          letterSpacing: "3px", textTransform: "uppercase",
          marginBottom: 16,
        }}>How It Works</div>

      {/* Gas & Relayer Explainer */}
      <div style={{
        background: `linear-gradient(135deg, ${COLORS.surface}, ${COLORS.bgAlt})`,
        border: `1px solid ${COLORS.orange}15`,
        borderRadius: 14,
        padding: 20,
        marginBottom: 40,
      }}>
        <div style={{
          fontFamily: "'Syne', sans-serif",
          fontSize: 15, fontWeight: 700, color: COLORS.orange,
          marginBottom: 10, display: "flex", alignItems: "center", gap: 8,
        }}>
          <span style={{ fontSize: 20 }}>⛽</span> Fees & Gas
        </div>
        <div style={{ fontSize: 12, color: COLORS.textSecondary, lineHeight: 1.8, marginBottom: 14 }}>
          FullSend charges one transparent fee that covers everything — the platform and gas. No gas popups, no hidden costs, no surprises.
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8, marginBottom: 14 }}>
          {[
            { label: "To Creator", value: "100%", sub: "of tip amount" },
            { label: "Platform Fee", value: "0.50 LYX", sub: "flat fee on top" },
            { label: "Fee Cap", value: "0.50", sub: "LYX maximum" },
            { label: "Gas", value: "Free", sub: "covered by fee" },
          ].map((stat, i) => (
            <div key={i} style={{
              textAlign: "center", padding: "10px 8px",
              background: `${COLORS.bg}60`, borderRadius: 8,
              border: `1px solid ${COLORS.border}`,
            }}>
              <div style={{ fontFamily: "'Syne', sans-serif", fontSize: 18, fontWeight: 800, color: i === 0 ? COLORS.accent : i === 3 ? COLORS.green : COLORS.orange }}>{stat.value}</div>
              <div style={{ fontSize: 9, color: COLORS.textMuted, marginTop: 2, textTransform: "uppercase", letterSpacing: "0.5px" }}>{stat.sub}</div>
            </div>
          ))}
        </div>
        <div style={{ fontSize: 12, color: COLORS.textSecondary, lineHeight: 1.8 }}>
          <span style={{ fontWeight: 700, color: COLORS.text }}>How it works:</span> A flat 0.50 LYX fee is added on top of every tip. Tip 5 LYX → creator gets 5 LYX, you pay 5.50. Tip 50 LYX → creator gets 50 LYX, you pay 50.50. This fee funds the platform and covers gas via LUKSO's Transaction Relay Service — so you never see a gas approval popup.
        </div>
        <div style={{ fontSize: 12, color: COLORS.textSecondary, lineHeight: 1.8, marginTop: 6 }}>
          <span style={{ fontWeight: 700, color: COLORS.text }}>Why this is better:</span> Twitch takes 50% of subscriptions. YouTube Super Chat takes 30%. And on top of those platform cuts, credit card processors and payment networks can take an additional 2-4% — fees creators never see but ultimately pay for. FullSend takes 0% from the creator and just 0.50 LYX from the tipper — with gas included, no card processors, no hidden cuts.
        </div>
        <div style={{ fontSize: 12, color: COLORS.textSecondary, lineHeight: 1.8, marginTop: 6 }}>
          <span style={{ fontWeight: 700, color: COLORS.text }}>Future-proofed:</span> The fee percentage, cap, and relay settings are configurable parameters — not hardcoded. If network conditions change, FullSend can adjust without disrupting the user experience. Transparency is baked in: the exact fee is always shown before you confirm.
        </div>
      </div>

      {/* Creator Verification */}
      <div style={{
        marginBottom: 40, padding: "28px 24px",
        background: COLORS.surface,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 14,
        animation: "fadeInUp 0.5s ease-out 0.3s both",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
          <div style={{
            width: 40, height: 40, borderRadius: 10,
            background: `${COLORS.accent}12`,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 20,
          }}>✓</div>
          <div>
            <div style={{
              fontFamily: "'Syne', sans-serif",
              fontSize: 16, fontWeight: 700, color: COLORS.accent,
            }}>Creator Verification</div>
            <span style={{
              fontSize: 8, padding: "2px 8px", borderRadius: 4,
              background: `${COLORS.accent}15`, color: COLORS.accent,
              fontWeight: 700, fontFamily: "'JetBrains Mono', monospace",
              marginLeft: 8,
            }}>LIVE</span>
            <div style={{ fontSize: 11, color: COLORS.textMuted }}>Prove you own your channels. Build trust with your audience.</div>
          </div>
        </div>

        <div style={{ fontSize: 12, color: COLORS.textSecondary, lineHeight: 1.8, marginBottom: 16 }}>
          FullSend lets creators verify ownership of their YouTube and Twitch accounts directly on-chain — Kick and X support is coming soon. A green checkmark on your tip widget tells viewers they're tipping the real creator, not an impersonator.
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
          {[
            { step: "1", text: "Go to Settings > Profile and click Verify next to a social link", color: COLORS.accent },
            { step: "2", text: "FullSend generates a unique code for that platform", color: COLORS.cyan },
            { step: "3", text: "Paste the code in your channel bio or description", color: COLORS.blue },
            { step: "4", text: "FullSend checks for the code and marks it verified on-chain", color: COLORS.purple },
          ].map((item, i) => (
            <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
              <div style={{
                width: 22, height: 22, borderRadius: 6,
                background: `${item.color}15`,
                border: `1px solid ${item.color}25`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 10, fontWeight: 800, color: item.color,
                fontFamily: "'JetBrains Mono', monospace",
                flexShrink: 0,
              }}>{item.step}</div>
              <div style={{ fontSize: 11, color: COLORS.textSecondary, lineHeight: 1.6, paddingTop: 2 }}>{item.text}</div>
            </div>
          ))}
        </div>

        <div style={{ fontSize: 11, color: COLORS.textMuted, lineHeight: 1.7 }}>
          <span style={{ fontWeight: 700, color: COLORS.text }}>Why it matters:</span> Verification is stored on your Universal Profile — it's a portable credential that follows you across the LUKSO ecosystem. Auto-rechecked every 24 hours. If a URL changes, verification resets so it's always current.
        </div>
      </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {[
            { step: "1", title: "Creator connects UP", desc: "Your Universal Profile becomes your channel. Share your tip widget URL or embed it in your UP grid.", icon: "📡", color: COLORS.accent },
            { step: "2", title: "Viewer sends tip", desc: "Connect a UP, pick an amount, add a message ($5+). The LYX transfers directly — no intermediary holds funds.", icon: "💰", color: COLORS.green },
            { step: "3", title: "One small fee, gas included", desc: "A flat 0.50 LYX fee is added on top of the tip. This covers the platform and gas — you never see a gas popup. The creator always gets 100% of the tip amount.", icon: "⛽", color: COLORS.orange },
            { step: "4", title: "Creator moderates & broadcasts", desc: "Tips $5+ enter the moderation queue. Approve to show on stream via the overlay, or skip. Under $5 auto-shows handle + amount.", icon: "🎮", color: COLORS.blue },
            { step: "5", title: "On-chain leaderboard & NFTs", desc: "Total tips per viewer are tracked on-chain. Cross a milestone and an LSP8 NFT auto-mints to the tipper's UP.", icon: "🏆", color: COLORS.gold },
          ].map((item, i) => (
            <div key={i} style={{
              display: "flex", gap: 14, alignItems: "flex-start",
              padding: "14px 16px",
              background: COLORS.surface,
              border: `1px solid ${COLORS.border}`,
              borderRadius: 10,
              animation: `fadeInUp 0.5s ease-out ${0.3 + i * 0.06}s both`,
              transition: "all 0.3s",
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = `${item.color}20`; e.currentTarget.style.background = COLORS.surfaceAlt; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = COLORS.border; e.currentTarget.style.background = COLORS.surface; }}>
              <div style={{
                width: 36, height: 36, borderRadius: 8,
                background: `${item.color}12`,
                border: `1px solid ${item.color}18`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 18, flexShrink: 0,
              }}>{item.icon}</div>
              <div>
                <div style={{
                  fontFamily: "'Syne', sans-serif",
                  fontSize: 13, fontWeight: 700, color: item.color,
                  marginBottom: 3,
                  display: "flex", alignItems: "center", gap: 6,
                }}>
                  <span style={{
                    fontSize: 9, fontWeight: 800, color: COLORS.textMuted,
                    background: `${COLORS.bg}80`, padding: "1px 5px", borderRadius: 3,
                    fontFamily: "'JetBrains Mono', monospace",
                  }}>{item.step}</span>
                  {item.title}
                </div>
                <div style={{ fontSize: 11, color: COLORS.textMuted, lineHeight: 1.6 }}>{item.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Ideas & Feedback */}
      <div style={{
        background: COLORS.surface, border: `1px solid ${COLORS.accent}12`,
        borderRadius: 16, padding: "24px 20px", marginBottom: 24,
      }}>
        <div style={{
          fontFamily: "'Syne', sans-serif", fontSize: 17, fontWeight: 800, color: COLORS.accent,
          marginBottom: 6, display: "flex", alignItems: "center", gap: 8,
        }}>
          <span style={{ fontSize: 22 }}>💡</span> Got an Idea?
        </div>
        <div style={{ fontSize: 12, color: COLORS.textSecondary, lineHeight: 1.7, marginBottom: 14 }}>
          We're building FullSend for creators and their communities. If you have a feature idea, feedback, or something you'd love to see — tell us. Every message goes directly to the team.
        </div>
        <IdeaForm type="idea" />
      </div>

      {/* Partnership Inquiries */}
      <div style={{
        background: COLORS.surface, border: `1px solid ${COLORS.purple}12`,
        borderRadius: 16, padding: "24px 20px", marginBottom: 24,
      }}>
        <div style={{
          fontFamily: "'Syne', sans-serif", fontSize: 17, fontWeight: 800, color: COLORS.purple,
          marginBottom: 6, display: "flex", alignItems: "center", gap: 8,
        }}>
          <span style={{ fontSize: 22 }}>🤝</span> Partnership Inquiries
        </div>
        <div style={{ fontSize: 12, color: COLORS.textSecondary, lineHeight: 1.7, marginBottom: 14 }}>
          Interested in integrating with FullSend, collaborating on content, or exploring a business partnership? We'd love to hear from you.
        </div>
        <IdeaForm type="partnership" />
      </div>

      {/* FAQ */}
      <div style={{ marginBottom: 40 }}>
        <div style={{
          fontFamily: "'Syne', sans-serif",
          fontSize: 11, fontWeight: 700, color: COLORS.textMuted,
          letterSpacing: "3px", textTransform: "uppercase",
          marginBottom: 16,
        }}>Frequently Asked Questions</div>

        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {faqs.map((faq, i) => {
            const isOpen = openFaq === i;
            return (
              <div key={i}
                onClick={() => setOpenFaq(isOpen ? null : i)}
                style={{
                  background: COLORS.surface,
                  border: `1px solid ${isOpen ? COLORS.accent + "20" : COLORS.border}`,
                  borderRadius: 10,
                  overflow: "hidden",
                  cursor: "pointer",
                  transition: "all 0.3s cubic-bezier(0.4,0,0.2,1)",
                }}
                onMouseEnter={e => { if (!isOpen) e.currentTarget.style.borderColor = `${COLORS.text}10`; }}
                onMouseLeave={e => { if (!isOpen) e.currentTarget.style.borderColor = COLORS.border; }}
              >
                <div style={{
                  padding: "13px 16px",
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  gap: 12,
                }}>
                  <span style={{
                    fontSize: 12, fontWeight: 600,
                    color: isOpen ? COLORS.accent : COLORS.text,
                    lineHeight: 1.4,
                    transition: "color 0.2s",
                  }}>{faq.q}</span>
                  <span style={{
                    fontSize: 14, color: COLORS.textMuted,
                    transform: isOpen ? "rotate(45deg)" : "rotate(0deg)",
                    transition: "transform 0.3s cubic-bezier(0.4,0,0.2,1)",
                    flexShrink: 0,
                    width: 20, textAlign: "center",
                  }}>+</span>
                </div>
                {isOpen && (
                  <div style={{
                    padding: "0 16px 14px",
                    fontSize: 11, color: COLORS.textMuted,
                    lineHeight: 1.8,
                    animation: "slideDown 0.25s ease-out",
                    borderTop: `1px solid ${COLORS.border}`,
                    paddingTop: 12,
                    marginTop: -2,
                  }}>
                    {faq.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Tech Stack */}
      <div style={{
        padding: 16, borderRadius: 12,
        background: COLORS.surface,
        border: `1px solid ${COLORS.border}`,
        marginBottom: 20,
      }}>
        <div style={{
          fontFamily: "'Syne', sans-serif",
          fontSize: 11, fontWeight: 700, color: COLORS.textMuted,
          letterSpacing: "3px", textTransform: "uppercase",
          marginBottom: 12,
        }}>Tech Stack</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {[
            { label: "LUKSO", desc: "L1 Blockchain" },
            { label: "Universal Profiles", desc: "On-chain identity" },
            { label: "LSP1", desc: "Universal Receiver" },
            { label: "LSP7", desc: "LYX token standard" },
            { label: "LSP8", desc: "NFT standard" },
            { label: "LSP25", desc: "Relay Calls" },
            { label: "LSP26", desc: "Follower System" },
            { label: "IPFS", desc: "Metadata storage" },
            { label: "Vercel", desc: "Hosting & serverless" },
            { label: "Solidity", desc: "Smart contracts" },
          ].map((tech, i) => (
            <div key={i} style={{
              padding: "5px 10px", borderRadius: 6,
              background: COLORS.bgAlt,
              border: `1px solid ${COLORS.border}`,
              fontSize: 10, color: COLORS.textSecondary,
              display: "flex", alignItems: "center", gap: 5,
            }}>
              <span style={{ fontWeight: 700 }}>{tech.label}</span>
              <span style={{ color: COLORS.textMuted }}>· {tech.desc}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Terms & Privacy */}
      <div style={{
        padding: 20, borderRadius: 12,
        background: COLORS.surface,
        border: `1px solid ${COLORS.border}`,
        marginBottom: 20,
      }}>
        <div style={{
          fontFamily: "'Syne', sans-serif",
          fontSize: 11, fontWeight: 700, color: COLORS.textMuted,
          letterSpacing: "3px", textTransform: "uppercase",
          marginBottom: 16,
        }}>Terms & Privacy</div>

        <div style={{ fontSize: 12, color: COLORS.textSecondary, lineHeight: 1.7, fontFamily: "'Outfit', sans-serif" }}>
          <div style={{ fontWeight: 700, color: COLORS.text, marginBottom: 6 }}>Platform Terms</div>
          <p style={{ margin: "0 0 12px" }}>
            FullSend is a decentralized tipping platform built on the LUKSO blockchain. By using FullSend, you agree to the following:
          </p>
          <p style={{ margin: "0 0 8px" }}>
            <span style={{ color: COLORS.accent, fontWeight: 600 }}>Non-custodial.</span> FullSend never holds, controls, or has access to your funds. All tips are sent directly from the tipper's Universal Profile to the creator's Universal Profile via on-chain smart contracts. FullSend cannot reverse, refund, or modify any transaction.
          </p>
          <p style={{ margin: "0 0 8px" }}>
            <span style={{ color: COLORS.accent, fontWeight: 600 }}>Platform fee.</span> A flat 0.50 LYX fee is added on top of every tip. This fee funds the platform and covers gas for gasless actions. The fee is never deducted from the creator's tip — 100% of the tip amount reaches the creator.
          </p>
          <p style={{ margin: "0 0 8px" }}>
            <span style={{ color: COLORS.accent, fontWeight: 600 }}>Gasless actions.</span> FullSend's relayer covers the network gas for free poll votes, token-gated poll votes, and claim drops by submitting them on the relevant contracts (PollVoting and the creator's token). The relayer is never granted controller permission on your Universal Profile and cannot transfer LYX, call TipRouter, modify your profile, or take any action on your account beyond those specific submissions. Tips, LYX-gated votes, and follows are always authorized by you directly via your wallet — FullSend's relayer is never involved in moving LYX.
          </p>
          <p style={{ margin: "0 0 8px" }}>
            <span style={{ color: COLORS.accent, fontWeight: 600 }}>No guarantees.</span> FullSend is provided as-is. We make no guarantees about uptime, transaction success, or the behavior of third-party services (LUKSO network, wallets, RPC providers). Use at your own risk.
          </p>
          <p style={{ margin: "0 0 8px" }}>
            <span style={{ color: COLORS.accent, fontWeight: 600 }}>Smart contract risk.</span> FullSend's smart contracts have not been formally audited. While we've tested extensively, smart contracts carry inherent risk. Never tip more than you can afford to lose.
          </p>

          <div style={{ height: 1, background: COLORS.border, margin: "16px 0" }} />

          <div style={{ fontWeight: 700, color: COLORS.text, marginBottom: 6 }}>Privacy Policy</div>
          <p style={{ margin: "0 0 8px" }}>
            <span style={{ color: COLORS.accent, fontWeight: 600 }}>What we collect.</span> FullSend stores Universal Profile addresses, tip amounts, poll votes, follow events, and page view counts. All of this data is either publicly visible on the LUKSO blockchain or generated through your use of the platform.
          </p>
          <p style={{ margin: "0 0 8px" }}>
            <span style={{ color: COLORS.accent, fontWeight: 600 }}>What we don't collect.</span> FullSend does not collect personal information such as names, emails, phone numbers, or IP addresses. We do not use cookies for tracking. We do not sell or share any data with third parties.
          </p>
          <p style={{ margin: "0 0 8px" }}>
            <span style={{ color: COLORS.accent, fontWeight: 600 }}>On-chain data.</span> All tips, follows, and poll votes are recorded on the LUKSO blockchain and are publicly visible. This data cannot be deleted or modified by FullSend or anyone else.
          </p>
          <p style={{ margin: "0 0 8px" }}>
            <span style={{ color: COLORS.accent, fontWeight: 600 }}>Creator verification.</span> If a creator links a YouTube or Twitch account for verification, FullSend checks the channel description for a verification code. We do not store or access any platform credentials or account data beyond the public channel description.
          </p>
          <p style={{ margin: "0 0 8px" }}>
            <span style={{ color: COLORS.accent, fontWeight: 600 }}>Email.</span> If you submit an idea or partnership inquiry with your email address, that email is stored solely for the purpose of responding to your submission. It is never used for marketing or shared with third parties.
          </p>
          <p style={{ margin: "0 0 0", color: COLORS.textMuted, fontSize: 10 }}>
            Last updated: April 2026 · Questions? Reach out on <a href="https://x.com/Alts_Anonymous" target="_blank" rel="noopener noreferrer" style={{ color: COLORS.accent, textDecoration: "none" }}>X @Alts_Anonymous</a>
          </p>
        </div>
      </div>

    </div>
  );
}
